import React, { useState, useEffect, useRef } from 'react';
import { useFonts, Arvo_700Bold } from '@expo-google-fonts/arvo';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  FlatList,
  StyleSheet,
  Alert,
  ScrollView,
  Platform,
  Modal,
  ActivityIndicator,
  KeyboardAvoidingView,
  AppState,
  Image,
  Linking,
  Share,
  PanResponder,
} from 'react-native';
import { NavigationContainer, useFocusEffect, createNavigationContainerRef } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { SafeAreaProvider, SafeAreaView, initialWindowMetrics } from 'react-native-safe-area-context';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { StatusBar } from 'expo-status-bar';
import DateTimePicker from './src/components/DateTimePicker';
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';
import * as DocumentPicker from 'expo-document-picker';
import { subscribeToAuthChanges, signOut, deleteAccount, handleWebRedirectResult } from './src/firebase/auth';
import { auth, firebaseInitError } from './src/firebase/config';
import {
  subscribeToHorses,
  subscribeToHorse,
  subscribeToDeletedHorses,
  fetchUserHorses,
  addHorse,
  updateHorse,
  deleteHorse,
  softDeleteHorse,
  restoreHorse,
  markHorseSold,
  unmarkHorseSold,
  importHorses,
  shareHorse,
  revokeShare,
  transferHorse,
  grantCompanyTrainer,
  addHorseDocument,
  removeHorseDocument,
  requestTrainer,
  approveTrainer,
  denyTrainer,
} from './src/firebase/horses';
import LoginScreen from './src/screens/LoginScreen';
import BiometricLockScreen from './src/screens/BiometricLockScreen';
import { isBiometricAvailable } from './src/utils/biometrics';
import { ensureNotificationPermission, scheduleReminder, cancelReminder, getPushToken, sendPushNotification } from './src/utils/notifications';
import { saveServiceChips, subscribeToUserSettings, addCustomCategory, removeCustomCategory, addCustomNoteTag, removeCustomNoteTag, hidePresetCategory, unhidePresetCategory, hidePresetNoteTag, unhidePresetNoteTag, addCustomLocation, removeCustomLocation, addCustomIncidentTag, removeCustomIncidentTag, addCustomDocType, saveBusinessProfile, getNextInvoiceNumber, setHorseSectionLayout } from './src/firebase/settings';
import { subscribeToMyCompany, saveCompanyProfile, addCompanyMember, removeCompanyMember, reconcileInvoiceCounter, fetchCompanyOfUser, subscribeToCompanyById } from './src/firebase/company';
import { isPhotoPickingAvailable, isDocPickingAvailable, pickPhotosAsync, pickPhotosWebAsync, pickDocumentAsync, uploadHorseFileAsync, uploadBusinessLogoAsync } from './src/firebase/media';
import { isInvoiceAvailable, generateAndShareInvoice, generateAndShareStatement, buildInvoiceHtml } from './src/utils/invoice';
import { shareOrDownloadFile } from './src/utils/files';
import { upsertUserProfile, fetchAllUsers, fetchAllHorses, findUserByEmail, findUidByEmail, saveShareEmail, isRelayEmail, savePushToken, saveUserTimezone, checkIsNewUser, writeAdminAlert, subscribeToAdminAlerts, markAdminAlertRead, deleteUserProfile, fetchUserById, markActive, fetchAllActivity } from './src/firebase/users';
import { callSendPushToUser, callStripeStatus, callStripeConnect, callLedgerPayLink, callStripeSetFeePolicy, callStripeRefundPayment, callStripeRestoreRefundedCharges, callSendPaymentReminder, callStripeTestDisputeOutcome, callStripeAcceptFees, callStripePaymentFees, callAnnounceOnlinePayments, callAdminClearSandboxPayments, callAdminListFeeRates, callAdminSetFeeRate } from './src/firebase/functions';
import {
  inviteHerdPartner, acceptHerdPartnership, declineHerdPartnership, endHerdPartnership,
  markPartnershipApplied, markPartnershipCleaned, removePartnership,
  subscribeToHerdPartnerships, partnerOf, activePartners, isOwnHerdHorse, partnershipCovers,
} from './src/firebase/herdPartners';
import { subscribeToLedger, subscribeToLedgerForHorses, addCharge, addPayment, updateLedgerEntry, deleteLedgerEntry, restoreLedgerEntry, addChargeToInvoice, removeChargeFromInvoice, invoiceListOf, firstInvoiceOf } from './src/firebase/trainingLedger';
import { subscribeToIncidents, subscribeToIncident, subscribeToHerdIncidents, addIncident, updateIncident, markIncidentSound, reopenIncident, deleteIncident, addIncidentLog, setIncidentLogs, addIncidentPhoto, removeIncidentPhoto } from './src/firebase/incidents';
import { PHOTO_UPLOADS_ENABLED, uploadFileAsync, deleteFileByUrl } from './src/firebase/storage';
import { subscribeToBreedingPlans, subscribeToBreedingPlan, addBreedingPlan, updateBreedingPlan, deleteBreedingPlan, subscribeToStuds, addStud, updateStud, deleteStud, buildChecks, RECIP_CHECK, canEditPlan, shareBreedingPlan, revokeBreedingPlanShare, fetchMyBreedingPlans } from './src/firebase/breeding';
import {
  inviteSeasonShare, acceptSeasonShare, declineSeasonShare, endSeasonShare, markSeasonShareApplied, removeSeasonShare,
  subscribeToSeasonShares, seasonShareCovers, shareEntryFor, seasonLabel,
} from './src/firebase/planShares';

const BIOMETRIC_LOCK_KEY = 'biometricLockEnabled';
const ADMIN_EMAIL = 'gingerbreadranch.life@gmail.com';

// Breeding Planning is live for everyone (released 2026-08-30). Setting this
// back to false hides the Dashboard card again, leaving it visible only to the
// accounts listed below — plans already created stay in the database untouched.
const BREEDING_PLANNING_ENABLED = true;
const BREEDING_PREVIEW_EMAILS = [
  ADMIN_EMAIL,
];
const canSeeBreedingPlanning = () =>
  BREEDING_PLANNING_ENABLED
  || BREEDING_PREVIEW_EMAILS.some(
    (e) => e.toLowerCase() === (auth.currentUser?.email || '').toLowerCase()
  );

// Online invoice payments (Stripe). Built 2026-10-10, testing in the Stripe
// sandbox: only the accounts below see "Set up online payments" until this is
// flipped on. Invoices only get a Pay link once a provider has finished Stripe
// setup, so nobody else is affected while it's off.
const ONLINE_PAYMENTS_ENABLED = true; // live for everyone 2026-10-13
// Email invoices straight to the client (server sends via Resend).
const INVOICE_EMAIL_ENABLED = true; // Resend connected 2026-10-31
const ONLINE_PAYMENTS_PREVIEW_EMAILS = [ADMIN_EMAIL];
const canSeeOnlinePayments = () =>
  ONLINE_PAYMENTS_ENABLED
  || ONLINE_PAYMENTS_PREVIEW_EMAILS.some((e) => e.toLowerCase() === (auth.currentUser?.email || '').toLowerCase());

// One invoice covering several horses for the same client. Live for everyone
// (released 2026-09-05, after the create/carry-forward/void cycle was tested
// against real data). Setting this back to false limits it to the accounts
// listed below again; invoices already created stay exactly as they are.
const MULTI_HORSE_INVOICE_ENABLED = true;
// Its own list rather than the breeding one — the two features are unrelated,
// and shipping one should not quietly change who can see the other.
const MULTI_HORSE_PREVIEW_EMAILS = [
  ADMIN_EMAIL,
  'tcw965@gmail.com',
];
const canUseMultiHorseInvoice = () =>
  MULTI_HORSE_INVOICE_ENABLED
  || MULTI_HORSE_PREVIEW_EMAILS.some(
    (e) => e.toLowerCase() === (auth.currentUser?.email || '').toLowerCase()
  );

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const Stack = createStackNavigator();
const navigationRef = createNavigationContainerRef();

const RED = '#4A0E0E';
const GOLD = '#D4AF37';
const CREAM = '#F5EDD8';
const GREEN = '#4A7C59';
const BG = '#FAF8F4';
const CARD = '#FFFFFF';
const TEXT = '#1A1A1A';
const MUTED = '#757575';
const BORDER = '#EAE4DA';

// Rustic, earthy accents per profile section — meaningful per category but all
// muted and warm so the horse profile reads as one cohesive screen. Each has an
// accent (icon/left-edge), a soft tint (header strip), and a dark text color.
const SECTION_STYLES = {
  details:       { accent: '#4A7C59', tint: '#E9F0EA', text: '#2E4A37', emoji: '📋' },
  pedigree:      { accent: '#6E5773', tint: '#EEE8EF', text: '#463A4A', emoji: '🌿' },
  family:        { accent: '#55697A', tint: '#E9EDF1', text: '#37444F', emoji: '🐴' },
  tasks:         { accent: '#B08322', tint: '#F5EDD6', text: '#6E5210', emoji: '✅' },
  earnings:      { accent: '#9A7B2E', tint: '#F4EEDA', text: '#5E4A16', emoji: '🏆' },
  incentive:     { accent: '#9A7B2E', tint: '#F4EEDA', text: '#5E4A16', emoji: '⭐' },
  contact:       { accent: '#55697A', tint: '#E9EDF1', text: '#37444F', emoji: '📇' },
  health:        { accent: '#8B3A3A', tint: '#F2E5E3', text: '#5C2020', emoji: '🩺' },
  trainingNotes: { accent: '#8B6B4A', tint: '#F0E9DF', text: '#5C4530', emoji: '📝' },
  ledger:        { accent: '#3E6B5A', tint: '#E5EDE9', text: '#26443A', emoji: '💵' },
  planShort:     { accent: '#55697A', tint: '#E9EDF1', text: '#37444F', emoji: '📋' },
  planConf:      { accent: '#4A7C59', tint: '#E9F0EA', text: '#2E4A37', emoji: '✅' },
  planSigned:    { accent: '#9A7B2E', tint: '#F4EEDA', text: '#5E4A16', emoji: '📜' },
  planArchive:   { accent: '#7A7267', tint: '#EDEAE5', text: '#4A443C', emoji: '🗄️' },
  notes:         { accent: '#8B6B4A', tint: '#F0E9DF', text: '#5C4530', emoji: '🗒️' },
  trainerReq:    { accent: '#8B3A3A', tint: '#F2E5E3', text: '#5C2020', emoji: '🔔' },
  docs:          { accent: '#4E6FA3', tint: '#E8EDF5', text: '#2C4368', emoji: '📎' },
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────

const addDays = (dateStr, days) => {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

// Custom milestones store an absolute `date`; the original embryo checklist
// items store a `daysOffset` relative to the horse's breed date.
// A picked calendar date as YYYY-MM-DD in the user's own time zone. Never go
// through toISOString() for this: it converts to UTC first, and an evening
// entry in the US would be stamped with tomorrow's date.
const localYmd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const getMilestoneDate = (horse, m) =>
  m.date ? m.date : addDays(horse.breedDate, m.daysOffset);

// 'YYYY-MM' key for a Date, used to track recurring-charge generation by month.
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

// A horse can have one or more trainers. New horses store a `trainers` array;
// older horses may still have a single `trainerInfo` object — support both.
const getHorseTrainers = (horse) => {
  const arr = Array.isArray(horse?.trainers) ? horse.trainers : [];
  // Merge the legacy single-trainer field: a legacy trainer must not lose
  // access just because a second trainer was later added via the array.
  if (horse?.trainerInfo?.uid && !arr.some((t) => t.uid === horse.trainerInfo.uid)) {
    return [...arr, horse.trainerInfo];
  }
  return arr;
};
const isHorseTrainer = (horse, uid) => !!uid && getHorseTrainers(horse).some((t) => t.uid === uid);
const horseHasTrainer = (horse) => getHorseTrainers(horse).length > 0;
const getOwnerContact = (horse) => horse?.ownerContact || horse?.trainerInfo?.ownerContact || null;

// A sharing email is set by its owner and never verified, so a match through
// one is a claim, not a fact: anyone could list your address as theirs. When a
// lookup matches that way, show the sharer who the account actually is and let
// them decide. A match on the sign-in email needs no confirmation — that
// address was verified by the sign-in provider.
const confirmSharingMatch = (found, typed) => new Promise((resolve) => {
  const needle = (typed || '').toLowerCase().trim();
  if (!found || (found.email || '').toLowerCase() === needle) { resolve(true); return; }
  const who = found.displayName ? `"${found.displayName}"` : 'an account with no name set';
  const signIn = isRelayEmail(found.email)
    ? 'signs in with a hidden Apple ID'
    : `signs in as ${found.email || 'an unknown address'}`;
  Alert.alert(
    'Is this the right person?',
    `${needle} is listed as the sharing email of ${who}, which ${signIn}. Sharing emails aren't verified, so make sure this is who you mean before you share.`,
    [
      { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
      { text: 'Yes, share', onPress: () => resolve(true) },
    ]
  );
});

// Why a share by email can come back empty, and what to do about it. Someone
// who signed in with Apple and hid their email has an address nobody knows;
// the fix is on THEIR side, so the person sharing has to be told what to ask for.
const USER_NOT_FOUND_MSG =
  'No Horse Math account uses that email.\n\n'
  + 'If they signed in with Apple and chose "Hide My Email", their account has a hidden address. '
  + 'Ask them to open Data & Account → Sharing Email in Horse Math and add the email you know them by — then try again.\n\n'
  + 'Otherwise they need to sign up first.';

// "INV-0001", "INV-0001 and INV-0002", "INV-0001, INV-0002 and INV-0003".
const invoiceListLabel = (nums) =>
  nums.length <= 1
    ? (nums[0] || '')
    : nums.slice(0, -1).join(', ') + ' and ' + nums[nums.length - 1];

// Parse a currency amount string strictly. Returns NaN for malformed input
// (multiple decimal points, more than 2 decimal places, non-numeric).
const parseAmount = (raw) => {
  const cleaned = String(raw == null ? '' : raw).replace(/[^0-9.]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return NaN;
  return parseFloat(cleaned);
};

// Built-in task/milestone categories. Users can add their own via Task Categories settings.
const PRESET_CATEGORIES = ['Deworming', 'Farrier', 'Vaccination', 'Vet Check'];

// Built-in note tags for the health log. Users can add their own via Note Tags settings.
const PRESET_NOTE_TAGS = ['Vet Visit', 'Vaccination', 'Medication', 'Injury', 'Weight'];

// Filter chips shown above the Notes & Health Log list
const NOTE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'general', label: 'General' },
  { key: 'health', label: 'Health' },
];

// Chip selector for a milestone/task category or note tag. `value` is the selected chip
// ('Other' shows a free-text field via otherText/onOtherTextChange).
function CategoryPicker({ presetOptions = PRESET_CATEGORIES, hiddenPresets = [], customCategories, value, onSelect, otherText, onOtherTextChange }) {
  const options = [...presetOptions.filter((c) => !hiddenPresets.includes(c)), ...customCategories, 'Other'];
  return (
    <View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {options.map((c) => (
          <TouchableOpacity
            key={c}
            style={[styles.categoryChip, value === c && styles.categoryChipActive]}
            onPress={() => onSelect(value === c ? '' : c)}
            activeOpacity={0.75}
          >
            <Text style={[styles.categoryChipTxt, value === c && styles.categoryChipTxtActive]}>{c}</Text>
          </TouchableOpacity>
        ))}
      </View>
      {value === 'Other' && (
        <TextInput
          style={[styles.input, { marginTop: 8 }]}
          placeholder="Enter category name"
          placeholderTextColor="#BDBDBD"
          value={otherText}
          onChangeText={onOtherTextChange}
        />
      )}
    </View>
  );
}

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getDaysLabel = (dateStr) => {
  if (!dateStr) return '';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr + 'T12:00:00');
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target - today) / 86400000);
  if (diff === 0) return 'Today';
  if (diff > 0) return `In ${diff} day${diff === 1 ? '' : 's'}`;
  return `${-diff} day${-diff === 1 ? '' : 's'} ago`;
};

// Returns { years, months, days, totalDays } from a date string to today.
// The month/day remainder is measured against the true calendar anchor — the
// same day-of-month N months later, clamped to that month's length — so it is
// exact at month boundaries. (The earlier version added the previous month's
// length and clamped a still-negative remainder to 0, which under-reported by
// a day or two for dates on the 29th-31st: born Jan 31, viewed Mar 1, read
// "1 mo, 0 days" instead of "1 mo, 1 day".)
const getAge = (dateStr) => {
  const birth = new Date(dateStr + 'T12:00:00');
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  if (Number.isNaN(birth.getTime()) || today <= birth) {
    return { years: 0, months: 0, days: 0, totalDays: 0 };
  }

  const totalDays = Math.max(0, Math.floor((today - birth) / 86400000));

  // Birth day-of-month, m months on, clamped (Jan 31 + 1 month = Feb 28).
  const anchorAfter = (m) => {
    const d = new Date(birth.getFullYear(), birth.getMonth() + m, 1, 12, 0, 0, 0);
    const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(birth.getDate(), lastDay));
    return d;
  };

  let totalMonths =
    (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
  if (anchorAfter(totalMonths) > today) totalMonths -= 1;
  const days = Math.max(0, Math.floor((today - anchorAfter(totalMonths)) / 86400000));

  return {
    years: Math.floor(totalMonths / 12),
    months: totalMonths % 12,
    days,
    totalDays,
  };
};

// Quarter Horse age convention: every horse turns a year older on January 1,
// regardless of actual birthdate. Within the foal's first calendar year,
// actual age is shown; from the following January 1 onward, age in years is
// (currentYear - birthYear) and months/days count from that January 1.
const getQuarterHorseAge = (dateStr) => {
  const birth = new Date(dateStr + 'T12:00:00');
  const today = new Date();
  today.setHours(12, 0, 0, 0);

  const years = today.getFullYear() - birth.getFullYear();
  if (years < 1) return getAge(dateStr);

  const totalDays = Math.max(0, Math.floor((today - birth) / 86400000));
  const months = today.getMonth();
  const days = today.getDate() - 1;

  return {
    years: Math.max(0, years),
    months: Math.max(0, months),
    days: Math.max(0, days),
    totalDays,
  };
};

const getDaysInFoal = (breedDateStr) => {
  const breedDate = new Date(breedDateStr + 'T12:00:00');
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  return Math.max(0, Math.floor((today - breedDate) / 86400000));
};

// Gestation the way breeders say it: calendar months + days, shown alongside the
// raw day count. Years fold into months so a bad breed date reads "14 mo"
// rather than "1 yr, 2 mo".
const formatMonthsInFoal = (breedDateStr) => {
  if (!breedDateStr) return '';
  if (Number.isNaN(new Date(breedDateStr + 'T12:00:00').getTime())) return '';
  const { years, months, days } = getAge(breedDateStr);
  const mo = years * 12 + months;
  const parts = [];
  if (mo > 0) parts.push(`${mo} mo`);
  if (days > 0 || mo === 0) parts.push(`${days} day${days === 1 ? '' : 's'}`);
  return parts.join(', ');
};

const formatAge = ({ years, months, days }) => {
  const parts = [];
  if (years > 0) parts.push(`${years} yr${years === 1 ? '' : 's'}`);
  if (months > 0) parts.push(`${months} mo`);
  if (days > 0 || parts.length === 0) parts.push(`${days} day${days === 1 ? '' : 's'}`);
  return parts.join(', ');
};

// Short age label for cards: years only once 1+, otherwise months (or days if under a month)
const formatAgeShort = ({ years, months, days }) => {
  if (years > 0) return `${years} yr${years === 1 ? '' : 's'}`;
  if (months > 0) return `${months} mo`;
  return `${days} day${days === 1 ? '' : 's'}`;
};

const createEmbryoHorse = (name, breedDate, isRecip = false) => ({
  name: name.trim(),
  type: 'embryo',
  isRecip,
  breedDate,
  notes: '',
  milestones: [
    { id: 'm0', name: 'Breed Date',   daysOffset: 0,   completed: false },
    { id: 'm1', name: '14 Day Check', daysOffset: 14,  completed: false },
    { id: 'm2', name: '30 Day Check', daysOffset: 30,  completed: false },
    { id: 'm3', name: '45 Day Check', daysOffset: 45,  completed: false },
    ...(isRecip ? [{ id: 'm4', name: '120 Day Check', daysOffset: 120, completed: false }] : []),
  ],
});

const createBornHorse = (name, dateOfBirth) => ({
  name: name.trim(),
  type: 'born',
  dateOfBirth,
  notes: '',
  milestones: [],
});

// A batch of frozen embryos (ICSI or Flush) awaiting implant. Identified by its
// sire/dam pairing; `frozenCount` drops by one each time an embryo is implanted.
const createFrozenEmbryo = ({ method, location, count, firstFrozen, lastFrozen }) => ({
  type: 'frozen_embryo',
  method: method === 'Flush' ? 'Flush' : 'ICSI',
  frozenLocation: (location || '').trim(),
  frozenCount: Math.max(0, parseInt(count, 10) || 0),
  frozenTotal: Math.max(0, parseInt(count, 10) || 0),
  firstFrozen: firstFrozen || '',
  lastFrozen: lastFrozen || '',
  depleted: false,
  notes: '',
  milestones: [],
});

// A frozen batch's display label is its sire × dam pairing.
const frozenLabel = (h) => {
  const s = (h.sire || '').trim();
  const d = (h.dam || '').trim();
  if (s && d) return `${s} × ${d}`;
  return s || d || h.registeredName || h.name || 'Frozen embryo';
};

// Batches with the same sire+dam (case/space-insensitive) group into one profile.
const frozenKey = (h) => `${(h.sire || '').trim().toLowerCase().replace(/\s+/g, ' ')}|${(h.dam || '').trim().toLowerCase().replace(/\s+/g, ' ')}`;

// Group frozen-embryo batches by sire/dam pairing.
const groupFrozen = (batches) =>
  Object.values(batches.reduce((acc, h) => {
    const k = frozenKey(h);
    (acc[k] = acc[k] || { key: k, sire: h.sire || '', dam: h.dam || '', batches: [] }).batches.push(h);
    return acc;
  }, {})).sort((a, b) => frozenLabel(a).localeCompare(frozenLabel(b)));

const frozenGroupRemaining = (g) => g.batches.reduce((s, h) => s + (h.frozenCount || 0), 0);

// ─── CSV BULK IMPORT ──────────────────────────────────────────────────────────

const CSV_TEMPLATE_HEADERS = [
  'name', 'type', 'dateOfBirth', 'breedDate', 'isRecip', 'recipMare',
  'registeredName', 'registryNumber', 'breed', 'color', 'height',
  'status', 'sire', 'dam', 'sireSire', 'sireDam', 'damSire', 'damDam',
];

const CSV_TEMPLATE = [
  CSV_TEMPLATE_HEADERS.join(','),
  'Bella,born,2022-05-14,,,,Bella Reg Name,12345,Quarter Horse,Bay,15.2,Mare,Big Sire,Big Dam,,,,',
  'Daisy,born,2018-04-02,,,,Daisy Reg Name,54321,Quarter Horse,Sorrel,15.0,Mare;Recip,,,,,,',
  'Shadow,embryo,,2026-01-10,true,Daisy,,,,,,,Stallion Name,Dam Name,,,,',
].join('\n');

// Minimal CSV parser supporting quoted fields with embedded commas/quotes/newlines
const parseCSV = (text) => {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else if (c !== '\r') {
      field += c;
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  const headers = rows[0]?.map((h) => h.trim()) ?? [];
  return rows
    .slice(1)
    .filter((r) => r.some((c) => c.trim() !== ''))
    .map((r) => {
      const obj = {};
      headers.forEach((h, idx) => { obj[h] = (r[idx] ?? '').trim(); });
      return obj;
    });
};

// Converts a date string to YYYY-MM-DD. Accepts ISO (YYYY-MM-DD) as-is, and
// converts M/D/YYYY or MM/DD/YYYY (Excel's default format) to ISO.
const normalizeDateString = (value, rowNum, fieldName) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const m = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const [, month, day, year] = m;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  throw new Error(`Row ${rowNum}: "${fieldName}" must be in YYYY-MM-DD or M/D/YYYY format (got "${value}")`);
};

// Converts a parsed CSV row into a horse object, or throws if required fields are missing/invalid
const csvRowToHorse = (row, rowNum) => {
  const name = row.name;
  if (!name) throw new Error(`Row ${rowNum}: missing "name"`);

  const isEmbryo = (row.type || '').toLowerCase() === 'embryo' || (!row.type && row.breedDate);
  let horse;

  if (isEmbryo) {
    if (!row.breedDate) throw new Error(`Row ${rowNum} (${name}): "embryo" type requires a breedDate`);
    const isRecip = /^(true|yes|y|1)$/i.test(row.isRecip || '');
    horse = createEmbryoHorse(name, normalizeDateString(row.breedDate, rowNum, 'breedDate'), isRecip);
    if (isRecip && row.recipMare) horse.recipMare = row.recipMare;
  } else {
    const dob = row.dateOfBirth || row.breedDate;
    if (!dob) throw new Error(`Row ${rowNum} (${name}): "born" type requires a dateOfBirth`);
    horse = createBornHorse(name, normalizeDateString(dob, rowNum, 'dateOfBirth'));
  }

  if (row.registeredName) horse.registeredName = row.registeredName;
  if (row.registryNumber) horse.registryNumber = row.registryNumber;
  if (row.breed) horse.breed = row.breed;
  if (row.color) horse.color = row.color;
  if (row.height) horse.height = row.height;
  if (row.stall) horse.stall = String(row.stall).trim();
  if (row.status) horse.status = row.status.split(';').map((s) => s.trim()).filter(Boolean);
  if (row.sire) horse.sire = row.sire;
  if (row.dam) horse.dam = row.dam;
  if (row.sireSire) horse.sireSire = row.sireSire;
  if (row.sireDam) horse.sireDam = row.sireDam;
  if (row.damSire) horse.damSire = row.damSire;
  if (row.damDam) horse.damDam = row.damDam;

  return horse;
};

const STATUS_OPTIONS = ['Stallion', 'Gelding', 'Mare', 'Foal/Yearling', 'Embryo'];

// Order in which status groups are shown when sorting by status
const STATUS_GROUP_ORDER = ['Stallion', 'Gelding', 'Mare', 'Broodmare', 'Foal/Yearling', 'Embryo'];

// Toggling 'Mare' off should also clear the 'Broodmare' tag
const toggleStatusOption = (status, option) => {
  let next = status.includes(option)
    ? status.filter((s) => s !== option)
    : [...status, option];
  if (option === 'Mare' && !next.includes('Mare')) {
    next = next.filter((s) => s !== 'Broodmare');
  }
  return next;
};

const toggleBroodmare = (status) =>
  status.includes('Broodmare')
    ? status.filter((s) => s !== 'Broodmare')
    : [...status, 'Broodmare'];

const toggleRecipStatus = (status) =>
  status.includes('Recip')
    ? status.filter((s) => s !== 'Recip')
    : [...status, 'Recip'];

// Filly/Colt are mutually exclusive sex tags for Foal/Yearling horses
const toggleFoalSex = (status, sex) => {
  const other = sex === 'Filly' ? 'Colt' : 'Filly';
  const next = status.filter((s) => s !== other);
  return next.includes(sex) ? next.filter((s) => s !== sex) : [...next, sex];
};

// Display name preferred for pedigree links: registered name, falling back to barn name
const stableDisplayName = (h) => h.registeredName || h.name;

// Primary display name: barn name, falling back to registered name if no barn name given
const displayName = (h) => h.name || h.registeredName || 'Unnamed Horse';

// ── Progeny (offspring) earnings ────────────────────────────────────────────
// A horse's offspring earnings come from two places:
//   1. Offspring currently in the stable — derived live from what they earned,
//      so editing a foal's earnings updates its dam/sire immediately.
//   2. Manual entries — offspring that were never in the stable, plus snapshots
//      written when an offspring leaves (see snapshotProgenyOnDelete). A horse
//      that produced a winner keeps that credit after the foal is sold.
// Derived rows are never written to Firestore; only snapshots are.

const horseEarningsTotal = (h) =>
  (h.earnings || []).filter((e) => e.type === 'horse').reduce((s, e) => s + (e.amount || 0), 0);

// A foal is this horse's offspring if it was linked by id (dam or sire picked
// from the stable) OR, failing that, if the dam or sire NAME on the foal is
// this horse's name. Typing a parent's name instead of picking it is common,
// and it used to leave the foal invisible to progeny earnings entirely.
const parentNameMatches = (typed, horse) => {
  const k = normalizeHorseName(typed);
  return !!k && (k === normalizeHorseName(horse.name) || k === normalizeHorseName(horse.registeredName));
};
const isOffspringOf = (h, horse) => {
  if (!horse?.id || h.id === horse.id) return false;
  if (h.sireId === horse.id || h.damId === horse.id) return true;
  return (!h.sireId && parentNameMatches(h.sire, horse)) || (!h.damId && parentNameMatches(h.dam, horse));
};
const offspringInStable = (horse, allHorses) => allHorses.filter((h) => isOffspringOf(h, horse));

// One offspring row per earning event of each in-stable offspring.
const derivedOffspringEntries = (horse, allHorses) =>
  offspringInStable(horse, allHorses).flatMap((child) =>
    (child.earnings || [])
      .filter((e) => e.type === 'horse')
      .map((e) => ({
        ...e,
        id: `derived_${child.id}_${e.id}`,
        type: 'offspring',
        offspringName: displayName(child),
        fromHorseId: child.id,
        sourceEntryId: e.id,
        derived: true,
      }))
  );

// Manual + snapshot entries stored on the horse itself.
const manualOffspringEntries = (horse) => (horse.earnings || []).filter((e) => e.type === 'offspring');

// Everything that counts toward this horse's offspring earnings, newest first.
// A snapshot is suppressed while its offspring is back in the stable (restored
// from the trash) so the same event is never counted twice.
const allOffspringEntries = (horse, allHorses = []) => {
  const liveIds = new Set(allHorses.map((h) => h.id));
  const manual = manualOffspringEntries(horse).filter((e) => !(e.fromHorseId && liveIds.has(e.fromHorseId)));
  return [...manual, ...derivedOffspringEntries(horse, allHorses)].sort((a, b) =>
    String(b.date || '').localeCompare(String(a.date || ''))
  );
};

// Every offspring this horse already knows by name — foals in the stable, and
// every name that has ever been typed on one of its offspring earnings. Offered
// when adding a new one, so the same foal is never entered twice under two
// spellings (which would split its earnings into two horses). One entry per
// name, however it was capitalised, keeping the first spelling seen.
const knownOffspringNames = (horse, allHorses = []) => {
  const seen = new Map();
  const add = (n) => { const k = normalizeHorseName(n); if (k && !seen.has(k)) seen.set(k, String(n).trim()); };
  // Only born horses can have earnings — an embryo or a frozen batch has
  // nothing to enter, and offering them would just clutter the list.
  offspringInStable(horse || {}, allHorses).filter((c) => c.type === 'born').forEach((c) => add(displayName(c)));
  manualOffspringEntries(horse || {}).forEach((e) => add(e.offspringName));
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
};

// Snap a typed name onto a known one that differs only in case or spacing, so
// "gingerbread cookie" and "Gingerbread Cookie" stay one horse.
const canonicalOffspringName = (typed, known) => {
  const k = normalizeHorseName(typed);
  return known.find((n) => normalizeHorseName(n) === k) || String(typed || '').trim();
};

const offspringEarningsTotal = (horse, allHorses = []) =>
  allOffspringEntries(horse, allHorses).reduce((s, e) => s + (e.amount || 0), 0);

// Herd-wide progeny total. Each earning event is counted ONCE no matter how
// many of its parents are in the stable — summing every horse's own offspring
// total would double-count a foal whose sire and dam are both here.
const herdOffspringTotal = (allHorses) => {
  const liveIds = new Set(allHorses.map((h) => h.id));
  const seen = new Set();
  let total = 0;
  allHorses.forEach((h) => {
    // Offspring that left the stable: snapshots exist on each parent, so key by source event.
    manualOffspringEntries(h).forEach((e) => {
      if (e.fromHorseId && liveIds.has(e.fromHorseId)) return; // counted below, via the offspring itself
      const key = e.fromHorseId ? `src_${e.fromHorseId}_${e.sourceEntryId || e.id}` : `own_${h.id}_${e.id}`;
      if (seen.has(key)) return;
      seen.add(key);
      total += e.amount || 0;
    });
    // Offspring still in the stable: count the offspring's own earnings once.
    if ((h.sireId && liveIds.has(h.sireId)) || (h.damId && liveIds.has(h.damId))) {
      total += horseEarningsTotal(h);
    }
  });
  return total;
};

// Mirrors the firestore rule for who may write to a horse doc. A parent can be
// a horse shared with you view-only, which you must not try to write to.
const canWriteHorse = (h, uid) =>
  !!uid && !!h && (
    h.ownerId === uid
    || (h.editors || []).includes(uid)
    || (!h.ownerId && (h.members || [])[0] === uid)
  );

// Snapshot a departing horse's earnings onto each parent still in the stable, so
// their progeny earnings survive the sale. Returns the pending write promises.
// Skips parents the caller can't write to — the write would be denied, and a
// rejected promise here must never be allowed to block the delete itself.
const snapshotProgenyOnDelete = (horse, allHorses, updateFn, uid) => {
  const events = (horse.earnings || []).filter((e) => e.type === 'horse' && (e.amount || 0) > 0);
  if (events.length === 0) return [];
  const name = displayName(horse);
  const parents = allHorses.filter(
    (h) => (h.id === horse.sireId || h.id === horse.damId) && canWriteHorse(h, uid)
  );
  return parents.map((parent) => {
    // Drop any earlier snapshot of this same horse (it may have been restored
    // and its earnings corrected since) and write a fresh set.
    const existing = (parent.earnings || []).filter((e) => e.fromHorseId !== horse.id);
    const snapshots = events.map((e) => ({
      id: `earn_snap_${horse.id}_${e.id}`,
      type: 'offspring',
      date: e.date,
      eventName: e.eventName,
      amount: e.amount || 0,
      notes: e.notes || '',
      offspringName: name,
      fromHorseId: horse.id,
      sourceEntryId: e.id,
    }));
    return updateFn(parent.id, { earnings: [...snapshots, ...existing] });
  });
};

// True when some offspring has BOTH its sire and dam in the stable. That is the
// only case where the per-horse offspring totals won't add up to the herd total:
// each parent is credited in full, but the foal's purse counts once for the herd.
const hasSharedProgenyCredit = (allHorses) => {
  const liveIds = new Set(allHorses.map((h) => h.id));
  return allHorses.some(
    (h) => h.sireId && h.damId && liveIds.has(h.sireId) && liveIds.has(h.damId) && horseEarningsTotal(h) > 0
  );
};

const normalizeHorseName = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');

// Conservative duplicate check for hand-typed offspring entries. Before progeny
// earnings were derived, the only way to credit a dam was to type the event in
// on her page — those entries now sit alongside the automatic one. Matches on
// offspring name AND exact amount, so it only flags near-certain duplicates.
// Returns the offspring it matched, or null. It never removes anything: the
// entry still counts toward the total until the user deletes it themselves.
const duplicateOffspringSource = (entry, horse, allHorses = []) => {
  if (!entry || entry.derived || entry.fromHorseId) return null;   // not a typed entry
  if (entry.type !== 'offspring' || !((entry.amount || 0) > 0)) return null;
  const typed = normalizeHorseName(entry.offspringName);
  if (!typed) return null;
  return offspringInStable(horse, allHorses).find((child) =>
    [displayName(child), child.name, child.registeredName]
      .some((n) => normalizeHorseName(n) === typed)
    && (child.earnings || []).some((e) => e.type === 'horse' && (e.amount || 0) === entry.amount)
  ) || null;
};

// Filters the stable list for the sire/dam picker based on sex
const filterByPedigreeRole = (list, role) => {
  if (role === 'dam') return list.filter((h) => (h.status || []).includes('Mare'));
  if (role === 'recip') return list.filter((h) => (h.status || []).includes('Recip'));
  if (role === 'sire') return list.filter((h) => (h.status || []).some((s) => s === 'Stallion' || s === 'Gelding'));
  return list;
};

const emptyHorseDetails = () => ({
  registeredName: '',
  registryNumber: '',
  breed: 'Quarter Horse',
  color: '',
  height: '',
  location: '',
  stall: '',
  imageUrl: '',
  status: [],
  sire: '',
  sireId: null,
  dam: '',
  damId: null,
  sireSire: '',
  sireDam: '',
  damSire: '',
  damDam: '',
  recipMare: '',
  recipMareId: null,
  recipLeased: false,
  recipLeasedFrom: '',
});

const GRANDPARENT_FIELDS = [
  { key: 'sireSire', label: "Sire's Sire" },
  { key: 'sireDam', label: "Sire's Dam" },
  { key: 'damSire', label: "Dam's Sire" },
  { key: 'damDam', label: "Dam's Dam" },
];

// "Shared by Jane · Can Edit" for a horse someone else owns; nothing for your
// own. Providers see it too — for them it reads as the owner's name.
const sharedByLine = (horse, uid) => {
  if (!uid || !horse?.ownerId || horse.ownerId === uid) return '';
  const who = horse.ownerName || horse.ownerEmail || 'owner';
  const mine = (horse.shares || []).find((s) => s.uid === uid);
  const level = mine?.permission === 'edit' ? 'Can Edit'
    : mine?.permission === 'trainer' ? 'Provider'
    : mine?.permission === 'view' ? 'View Only'
    : '';
  return `🤝 Shared by ${who}${level ? ` · ${level}` : ''}`;
};

// A provider's service chips. Pinned ones (edited by hand) come first in
// their own order; the rest are learned from their own recent charges,
// grouped by description-and-amount and ranked by how often and how recently
// they used each pair. Nobody configures anything — it is their ledger read
// back to them.
const CHIP_WINDOW_DAYS = 180;
const CHIP_COUNT = 3;
const chipKey = (description, amount) => `${String(description || '').trim().toLowerCase()}|${Number(amount) || 0}`;
const serviceChips = (entries, pinned, todayYmd, hidden = []) => {
  const out = (pinned || [])
    .filter((c) => c && c.description)
    .map((c) => ({ description: c.description, amount: Number(c.amount) || 0, pinned: true }));
  const seen = new Set(out.map((c) => chipKey(c.description, c.amount)));
  // A learned chip they deleted stays gone; otherwise it would reappear on the
  // next charge. A pinned one they delete is simply unpinned.
  (hidden || []).forEach((k) => seen.add(k));
  const cutoff = new Date(todayYmd + 'T12:00:00');
  cutoff.setDate(cutoff.getDate() - CHIP_WINDOW_DAYS);
  const cutoffYmd = cutoff.toISOString().split('T')[0];
  const scored = {};
  (entries || []).forEach((e) => {
    if (e.type !== 'charge' || e.deleted || !e.description) return;
    if (!e.date || e.date < cutoffYmd) return;
    const key = chipKey(e.description, e.amount);
    if (seen.has(key)) return;
    const s = scored[key] || (scored[key] = { description: String(e.description).trim(), amount: Number(e.amount) || 0, uses: 0, last: '' });
    s.uses += 1;
    if (e.date > s.last) s.last = e.date;
  });
  // Used often, and used lately. A price he has moved on from fades out as the
  // new one overtakes it rather than needing to be deleted.
  const rank = Object.values(scored).sort((a, b) => (b.uses - a.uses) || b.last.localeCompare(a.last));
  return out.concat(rank.slice(0, Math.max(0, CHIP_COUNT - out.length))).slice(0, CHIP_COUNT);
};

// Who a task belongs to. A client horse can carry several providers, and a
// farrier has no business seeing the vet's round — but the owner keeps the
// whole picture of their own horse, and so does anyone they have shared it
// with as a partner rather than a provider.
const iOwnHorse = (horse, uid) => !!uid && (horse?.ownerId ? horse.ownerId === uid : horse?.members?.[0] === uid);
const taskVisibleTo = (horse, m, uid) => {
  if (!isHorseTrainer(horse, uid)) return true; // owner, or shared with me as a partner
  if (iOwnHorse(horse, uid)) return true;       // my own horse that I also service
  // Tasks from before createdByUid existed (and checklist milestones) carry no
  // record of who made them; hiding those emptied providers' task lists, so
  // they stay visible to everyone as they always were.
  if (!m.assignedToUid && !m.createdByUid) return true;
  return m.assignedToUid === uid || m.createdByUid === uid;
};

const nextAppointment = (horse) => {
  const today = localYmd(new Date());
  const open = (horse?.milestones || [])
    .filter((m) => !m.completed)
    .map((m) => ({ m, date: getMilestoneDate(horse, m) }))
    .filter((x) => x.date && x.date >= today)
    .sort((a, b) => (a.date === b.date ? (b.m.isAppointment ? 1 : 0) - (a.m.isAppointment ? 1 : 0) : a.date.localeCompare(b.date)));
  return open[0] || null;
};

// ─── CARDS / LIST TOGGLE ──────────────────────────────────────────────────────
// Cards are pleasant for a dozen horses and exhausting for a hundred. Every
// list that can get long offers the same two-button switch, and each screen
// remembers the choice.
function DensityToggle({ compact, onChange }) {
  return (
    <View style={{ flexDirection: 'row', gap: 6, marginBottom: 10 }}>
      {[[false, 'Cards'], [true, 'List']].map(([val, label]) => (
        <TouchableOpacity
          key={label}
          style={[styles.densityBtn, compact === val && styles.densityBtnOn]}
          onPress={() => onChange(val)}
          activeOpacity={0.75}
        >
          <Text style={[styles.densityTxt, compact === val && styles.densityTxtOn]}>{label}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );
}

// ─── WEEKS / MONTHS OFFSET ────────────────────────────────────────────────────
// Two little number boxes: "N weeks" and "N months". Typing in one clears the
// other, so only one offset is ever in force. A farrier books "6 weeks out";
// a vet books "3 months out" — either way it is a number they know, not a chip.
function OffsetInputs({ weeks, months, onWeeks, onMonths, caption }) {
  const box = (val, set, unit) => (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
      <TextInput
        style={[styles.input, { width: 58, marginBottom: 0, textAlign: 'center', paddingVertical: 8 }]}
        keyboardType="number-pad"
        placeholder="#"
        placeholderTextColor="#BDBDBD"
        value={val}
        onChangeText={(v) => set(v.replace(/[^0-9]/g, ''))}
        maxLength={3}
      />
      <Text style={{ fontSize: 14, color: TEXT }}>{unit}</Text>
    </View>
  );
  return (
    <View style={{ marginTop: 8 }}>
      {!!caption && <Text style={{ fontSize: 12, color: MUTED, marginBottom: 6 }}>{caption}</Text>}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {box(weeks, onWeeks, 'weeks')}
        <Text style={{ fontSize: 13, color: MUTED, fontWeight: '600' }}>or</Text>
        {box(months, onMonths, 'months')}
      </View>
    </View>
  );
}

// Whole months, clamped to the end of a shorter month (Jan 31 + 1 → Feb 28).
const addMonthsClamped = (d, n) => {
  const x = new Date(d);
  const day = x.getDate();
  x.setDate(1);
  x.setMonth(x.getMonth() + n);
  const last = new Date(x.getFullYear(), x.getMonth() + 1, 0).getDate();
  x.setDate(Math.min(day, last));
  return x;
};

// ─── OWNER-GROUPED HORSE PICKER ───────────────────────────────────────────────
// Client horses grouped by owner. Tapping an owner selects (or clears) every
// horse of theirs; tapping a horse toggles just that one, and the owner row
// shows full / partial / none. A provider books and bills by client, so this
// is the picker for anything done to several client horses at once.
function OwnerGroupedPicker({ horses, selected, onChange, ownerNameOf }) {
  const groups = [];
  horses.forEach((h) => {
    const key = ownerNameOf(h);
    const g = groups.find((x) => x.key === key);
    if (g) g.rows.push(h); else groups.push({ key, rows: [h] });
  });
  groups.sort((a, b) => a.key.localeCompare(b.key));
  const count = horses.filter((h) => selected[h.id]).length;
  const allOn = horses.length > 0 && horses.every((h) => selected[h.id]);
  return (
    <View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, marginBottom: 4 }}>
        <Text style={styles.fieldLabel}>Horses ({count} of {horses.length})</Text>
        <TouchableOpacity onPress={() => onChange(allOn ? {} : Object.fromEntries(horses.map((h) => [h.id, true])))} activeOpacity={0.7}>
          <Text style={{ fontSize: 12, color: GREEN, fontWeight: '600' }}>{allOn ? 'Clear all' : 'Select all'}</Text>
        </TouchableOpacity>
      </View>
      {groups.map((g) => {
        const on = g.rows.filter((h) => selected[h.id]).length;
        const state = on === 0 ? '⬜️' : on === g.rows.length ? '☑️' : '◽️';
        return (
          <View key={g.key} style={{ marginBottom: 4 }}>
            <TouchableOpacity
              onPress={() => onChange((prev) => {
                const next = { ...prev };
                const full = on === g.rows.length;
                g.rows.forEach((h) => { if (full) delete next[h.id]; else next[h.id] = true; });
                return next;
              })}
              activeOpacity={0.7}
              style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, marginTop: 6 }}
            >
              <Text style={{ fontSize: 18, marginRight: 10 }}>{state}</Text>
              <Text style={{ flex: 1, fontSize: 14, fontWeight: '700', color: TEXT }}>{g.key}</Text>
              <Text style={{ fontSize: 12, color: MUTED }}>{on}/{g.rows.length}</Text>
            </TouchableOpacity>
            {g.rows.map((h) => (
              <TouchableOpacity
                key={h.id}
                onPress={() => onChange((prev) => ({ ...prev, [h.id]: !prev[h.id] }))}
                activeOpacity={0.7}
                style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 7, paddingLeft: 30, borderBottomWidth: 0.5, borderBottomColor: BORDER }}
              >
                <Text style={{ fontSize: 16, marginRight: 10 }}>{selected[h.id] ? '☑️' : '⬜️'}</Text>
                <Text style={{ flex: 1, fontSize: 15, color: TEXT }}>{displayName(h)}</Text>
              </TouchableOpacity>
            ))}
          </View>
        );
      })}
    </View>
  );
}

// ─── PLAN SHARES LIST ─────────────────────────────────────────────────────────
// Who a plan is shared with, shown on the plan itself so the owner does not
// have to open the share sheet to find out. A share that came from a season
// share says so — those are managed from Share All Plans, not one by one.
function PlanSharesList({ plan }) {
  const shares = plan?.shares || [];
  if (shares.length === 0) return null;
  return (
    <View style={{ marginBottom: 10 }}>
      {shares.map((s) => (
        <View key={s.uid} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6, borderBottomWidth: 0.5, borderBottomColor: BORDER }}>
          <Text style={{ fontSize: 14, marginRight: 8 }}>🤝</Text>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 14, color: TEXT }}>{s.name || s.email}</Text>
            {!!s.email && s.email !== s.name && <Text style={{ fontSize: 11, color: MUTED }}>{s.email}</Text>}
          </View>
          <Text style={{ fontSize: 12, color: MUTED }}>
            {s.permission === 'full' ? 'Full Access' : 'View Only'}{s.viaSeasonShare ? ' · season share' : ''}
          </Text>
        </View>
      ))}
    </View>
  );
}

// ─── SHARE NOTIFICATIONS ──────────────────────────────────────────────────────
// A push to someone who has just been given access. Looked up by uid, since
// the syncs only know uids. Best effort — a missing token or a failed send
// never fails the share itself. One message per person however many horses:
// nobody wants thirty notifications for one herd.
const notifyAccess = async (uid, title, body, data = {}) => {
  if (!uid) return;
  try { await callSendPushToUser(uid, title, body, data); } catch (e) { /* best effort */ }
};
const myDisplayName = () => auth.currentUser?.displayName || auth.currentUser?.email || 'Someone';
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// ─── SHARE SYNCS ──────────────────────────────────────────────────────────────
// Three background jobs keep who-is-on-which-horse in step with something else:
// a herd partnership, my company's roster (for my client horses), and the
// roster of any provider team an owner has shared a horse with. They share two
// rules that matter:
//
//   1. A horse's share list is rewritten whole on every change, so two writes
//      to the same horse in one run must each start from the list as the LAST
//      write left it — not from the snapshot the run began with. Otherwise the
//      second write silently drops the first person's entry: they keep access
//      (members/editors are append-only) but disappear from every list and can
//      never be removed as part of a team. `useShareLedger` carries the lists
//      forward within a run.
//   2. One run at a time. A snapshot that arrives mid-run is not lost: it sets
//      a flag, and the run re-triggers itself when it finishes.
const useRunOnce = () => {
  const busy = useRef(false);
  const pending = useRef(false);
  const [tick, setTick] = useState(0);
  const run = (fn) => {
    if (busy.current) { pending.current = true; return; }
    busy.current = true;
    Promise.resolve().then(fn).catch(() => {}).finally(() => {
      busy.current = false;
      if (pending.current) { pending.current = false; setTick((n) => n + 1); }
    });
  };
  return [run, tick];
};

// The share/trainer lists as they stand after each write this run has made.
const shareLedger = (horses) => {
  const shares = new Map(horses.map((h) => [h.id, h.shares || []]));
  const trainers = new Map(horses.map((h) => [h.id, h.trainers || []]));
  return {
    shares: (id) => shares.get(id) || [],
    trainers: (id) => trainers.get(id) || [],
    added: (id, entry) => shares.set(id, [...(shares.get(id) || []).filter((s) => s.uid !== entry.uid), entry]),
    removed: (id, uid) => {
      shares.set(id, (shares.get(id) || []).filter((s) => s.uid !== uid));
      trainers.set(id, (trainers.get(id) || []).filter((tr) => tr?.uid !== uid));
    },
  };
};

// ─── HERD PARTNER SYNC ────────────────────────────────────────────────────────
// Only a horse's owner can change who is on it, so each side of a partnership
// has to share — and later un-share — its own horses itself. This runs wherever
// the herd is already loaded (the Dashboard), so it catches up even if the
// person never opens the Sharing screen.
//
//   active  and I have not applied  → share every horse I own with them
//   ended   and I have not cleaned  → take them off every horse I own
//   ended   and both have cleaned   → the record has done its job; remove it
const useHerdPartnerSync = (uid, horses, partnerships) => {
  const [run, tick] = useRunOnce();
  useEffect(() => {
    if (!uid || !partnerships?.length) return;
    const work = partnerships.filter((p) =>
      (p.status === 'active' && !(p.appliedBy || []).includes(uid)) || p.status === 'ended'
    );
    if (work.length === 0) return;
    run(async () => {
      const ledger = shareLedger(horses);
      for (const p of work) {
        const other = partnerOf(p, uid);
        if (!other) continue;
        // A horse where they are already the trainer is theirs for a different
        // reason: sharing it again would downgrade that to view/edit, and taking
        // it back later would strip access they still need. Leave those alone.
        const mine = horses.filter((h) => partnershipCovers(h, uid, other.includeClients) && !(h.trainers || []).some((tr) => tr?.uid === other.uid));

        if (p.status === 'active') {
          for (const h of mine) {
            try {
              await shareHorse(h.id, ledger.shares(h.id), other.uid, other.email, other.name, other.permission);
              ledger.added(h.id, { uid: other.uid, email: other.email, name: other.name || other.email, permission: other.permission });
            } catch (e) { /* retried on the next snapshot */ }
          }
          try { await markPartnershipApplied(p.id, uid); } catch (e) { /* retried on the next snapshot */ }
          // The server (applyHerdPartnership) sends the push; this path is the
          // silent fallback, so nobody is told twice.
        } else {
          const cleaned = p.cleanedBy || [];
          if (!cleaned.includes(uid)) {
            for (const h of mine) {
              try {
                await revokeShare(h.id, ledger.shares(h.id), other.uid);
                ledger.removed(h.id, other.uid);
              } catch (e) { /* retried on the next snapshot */ }
            }
            try { await markPartnershipCleaned(p.id, uid); } catch (e) { /* retried on the next snapshot */ }
          } else if ((p.uids || []).every((u) => cleaned.includes(u))) {
            try { await removePartnership(p.id); } catch (e) { /* either side may do this */ }
          }
        }
      }
    });
  }, [uid, horses, partnerships, tick]);
};

// ─── COMPANY CLIENT-HORSE SYNC ────────────────────────────────────────────────
// Everyone linked under one company works the same client horses, so each of
// my client horses should carry every colleague as a Provider — the same access
// I have. Covers the client horses I OWN (the ones I created); one an owner
// shared to me as their trainer is theirs to share on, and a colleague still
// goes through that owner's approval as before.
//
//   colleague not yet on one of my client horses → add them as Provider
//   someone on it via the company who has since left → take them off
//   I leave the company → take every colleague the company added off
//
// Shares the company made are tagged `viaCompany`, so a Provider someone added
// by hand is never touched.
const useCompanyClientSync = (uid, horses, company) => {
  const [run, tick] = useRunOnce();
  const lastCompanyId = useRef(null);
  useEffect(() => {
    if (!uid) return;
    const myClientHorses = horses.filter((h) => h.ownerId === uid && isHorseTrainer(h, uid) && !h.deleted);
    const plan = []; // { h, adds: [member], removes: [uid], cid, cname }

    if (company?.id) {
      lastCompanyId.current = company.id;
      const colleagues = (company.members || []).filter((m) => m?.uid && m.uid !== uid);
      const memberIds = new Set((company.memberUids || []).filter(Boolean));
      myClientHorses.forEach((h) => {
        const onIt = new Set((h.trainers || []).map((tr) => tr?.uid));
        const adds = colleagues.filter((m) => !onIt.has(m.uid));
        const removes = (h.shares || []).filter((s) => s.viaCompany === company.id && s.uid !== uid && !memberIds.has(s.uid)).map((s) => s.uid);
        if (adds.length || removes.length) plan.push({ h, adds, removes, cid: company.id, cname: company.name || '' });
      });
    } else if (lastCompanyId.current) {
      // I have left (or the company is gone): colleagues it added lose my client horses.
      const gone = lastCompanyId.current;
      myClientHorses.forEach((h) => {
        const removes = (h.shares || []).filter((s) => s.viaCompany === gone && s.uid !== uid).map((s) => s.uid);
        if (removes.length) plan.push({ h, adds: [], removes, cid: gone, cname: '' });
      });
      if (plan.length === 0) lastCompanyId.current = null;
    }

    if (plan.length === 0) return;
    run(async () => {
      const ledger = shareLedger(horses);
      const added = new Map(); // colleague uid → how many client horses this run gave them
      for (const { h, adds, removes, cid, cname } of plan) {
        for (const m of adds) {
          try {
            await grantCompanyTrainer(h.id, ledger.shares(h.id), m, cid, cname);
            ledger.added(h.id, { uid: m.uid, email: m.email || '', name: m.name || m.email || '', permission: 'trainer', viaCompany: cid, viaCompanyName: cname });
            added.set(m.uid, (added.get(m.uid) || 0) + 1);
          } catch (e) { /* the next snapshot retries */ }
        }
        for (const u of removes) {
          try {
            await revokeShare(h.id, ledger.shares(h.id), u, ledger.trainers(h.id));
            ledger.removed(h.id, u);
          } catch (e) { /* the next snapshot retries */ }
        }
      }
      added.forEach((n, who) => notifyAccess(who, '🎓 Client Horses Shared',
        `${myDisplayName()} added you to ${plural(n, 'client horse')} at ${company?.name || 'your company'}.`));
    });
  }, [uid, horses, company, tick]);
};

// ─── TEAM SHARE SYNC (owner side) ─────────────────────────────────────────────
// When an owner shares a horse with a provider and chooses "the whole team",
// every member of that provider's company goes on as a Provider, tagged with
// the company. From then on the horse follows the roster: someone who joins
// the company is added, someone who leaves is removed — without the owner
// having to know or do anything. Only shares tagged `viaCompany` are touched;
// the provider the owner chose by hand keeps their access regardless.
//
// Covers horses I own that I am NOT the trainer on — my own client horses are
// kept in step with MY company by useCompanyClientSync instead.
const useTeamShareSync = (uid, horses) => {
  const [teams, setTeams] = useState({});
  const subs = useRef({});
  const [run, tick] = useRunOnce();
  const owned = horses.filter((h) => h.ownerId === uid && !isHorseTrainer(h, uid) && !h.deleted);
  const teamIds = [...new Set(owned.flatMap((h) => (h.shares || []).map((s) => s.viaCompany).filter(Boolean)))].sort();
  const teamKey = teamIds.join(',');

  // One live subscription per company any of my horses is shared with.
  useEffect(() => {
    teamIds.forEach((id) => {
      if (!subs.current[id]) subs.current[id] = subscribeToCompanyById(id, (c) => setTeams((prev) => ({ ...prev, [id]: c })));
    });
    Object.keys(subs.current).forEach((id) => {
      if (!teamIds.includes(id)) { subs.current[id](); delete subs.current[id]; setTeams((prev) => { const n = { ...prev }; delete n[id]; return n; }); }
    });
  }, [teamKey]);
  useEffect(() => () => Object.values(subs.current).forEach((u) => u()), []);

  useEffect(() => {
    if (!uid) return;
    const plan = []; // { h, adds: [member], removes: [uid], cid, cname }
    owned.forEach((h) => {
      const byCompany = {};
      (h.shares || []).forEach((s) => { if (s.viaCompany) { if (!byCompany[s.viaCompany]) byCompany[s.viaCompany] = []; byCompany[s.viaCompany].push(s); } });
      Object.keys(byCompany).forEach((cid) => {
        const c = teams[cid];
        if (!c) return; // roster not loaded (or the company is gone) — leave it be
        const memberIds = new Set((c.memberUids || []).filter(Boolean));
        const onIt = new Set((h.trainers || []).map((tr) => tr?.uid));
        const adds = (c.members || []).filter((m) => m?.uid && m.uid !== uid && !onIt.has(m.uid));
        const removes = byCompany[cid].filter((s) => s.uid !== uid && !memberIds.has(s.uid)).map((s) => s.uid);
        if (adds.length || removes.length) plan.push({ h, adds, removes, cid, cname: c.name || '' });
      });
    });
    if (plan.length === 0) return;
    run(async () => {
      const ledger = shareLedger(horses);
      const added = new Map(); // new team member uid → { company, n }
      for (const { h, adds, removes, cid, cname } of plan) {
        for (const m of adds) {
          try {
            await grantCompanyTrainer(h.id, ledger.shares(h.id), m, cid, cname);
            ledger.added(h.id, { uid: m.uid, email: m.email || '', name: m.name || m.email || '', permission: 'trainer', viaCompany: cid, viaCompanyName: cname });
            if (!added.has(m.uid)) added.set(m.uid, { company: cname || 'your team', n: 0 });
            added.get(m.uid).n += 1;
          } catch (e) { /* the next snapshot retries */ }
        }
        for (const u of removes) {
          try {
            await revokeShare(h.id, ledger.shares(h.id), u, ledger.trainers(h.id));
            ledger.removed(h.id, u);
          } catch (e) { /* the next snapshot retries */ }
        }
      }
      added.forEach(({ company, n }, who) => notifyAccess(who, '🐴 Horses Shared With Your Team',
        `${myDisplayName()} shares ${plural(n, 'horse')} with your team at ${company} — you now have Provider access.`));
    });
  }, [uid, horses, teams, tick]);
};

// ─── SEASON SHARE SYNC (owner side) ───────────────────────────────────────────
// Only a plan's owner can change who is on it, so the owner's device does the
// work. It runs on the Dashboard so it catches up whenever the app is opened,
// without a standing subscription to every plan: when there is something to do
// it fetches the owner's plans once.
//
//   active and not yet applied → share every plan the season covers, then push
//   ended  and not yet cleaned → take back what THIS share gave (entries tagged
//                                 with it), never a plan shared by hand
const useSeasonShareSync = (uid, shares) => {
  const [run, tick] = useRunOnce();
  useEffect(() => {
    if (!uid) return;
    const mine = (shares || []).filter((s) => s.ownerId === uid);
    const toApply = mine.filter((s) => s.status === 'active' && !s.applied);
    const toClean = mine.filter((s) => s.status === 'ended');
    if (toApply.length === 0 && toClean.length === 0) return;
    run(async () => {
      let plans;
      try { plans = await fetchMyBreedingPlans(uid); } catch (e) { return; }
      const sharesOf = new Map(plans.map((p) => [p.id, p.shares || []]));
      for (const s of toApply) {
        const entry = shareEntryFor(s);
        let n = 0;
        for (const p of plans) {
          if (!seasonShareCovers(s, p)) continue;
          const cur = sharesOf.get(p.id) || [];
          if (cur.some((x) => x.uid === s.toUid && x.permission === s.permission)) continue;
          try {
            await shareBreedingPlan(p.id, cur, s.toUid, entry.email, entry.name, s.permission, { viaSeasonShare: s.id });
            sharesOf.set(p.id, [...cur.filter((x) => x.uid !== s.toUid), entry]);
            n += 1;
          } catch (e) { /* retried on the next open */ }
        }
        try { await markSeasonShareApplied(s.id); } catch (e) { /* retried on the next open */ }
        // The server (applySeasonShare) sends the push; this is the silent fallback.
      }
      for (const s of toClean) {
        for (const p of plans) {
          const cur = sharesOf.get(p.id) || [];
          const gave = cur.find((x) => x.uid === s.toUid && x.viaSeasonShare === s.id);
          if (!gave) continue;
          try {
            await revokeBreedingPlanShare(p.id, cur, s.toUid);
            sharesOf.set(p.id, cur.filter((x) => x.uid !== s.toUid));
          } catch (e) { /* retried on the next open */ }
        }
        try { await removeSeasonShare(s.id); } catch (e) { /* retried on the next open */ }
      }
    });
  }, [uid, shares, tick]);
};

// ─── DASHBOARD SCREEN ─────────────────────────────────────────────────────────

function DashboardScreen({ navigation }) {
  const [horses, setHorses] = useState([]);
  const [soldHorses, setSoldHorses] = useState([]); // only for the backup export
  const [showDataAccount, setShowDataAccount] = useState(false);
  // The email people share with me at — set by me, on top of my sign-in email.
  const [shareEmail, setShareEmail] = useState('');
  const [showShareEmail, setShowShareEmail] = useState(false);
  const [shareEmailDraft, setShareEmailDraft] = useState('');
  const [shareEmailBusy, setShareEmailBusy] = useState(false);
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    fetchUserById(uid).then((u) => setShareEmail(u?.shareEmail || '')).catch(() => {});
  }, []);

  const handleSaveShareEmail = async () => {
    const email = shareEmailDraft.trim().toLowerCase();
    const me = auth.currentUser;
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { Alert.alert('Check the email', 'That does not look like an email address.'); return; }
    setShareEmailBusy(true);
    try {
      if (email) {
        // One address must point at one account, or a share could land on the
        // wrong person.
        const taken = await findUserByEmail(email);
        if (taken && taken.uid !== me?.uid) {
          Alert.alert('Already in use', 'Another Horse Math account uses that email. Pick one only you use.');
          return;
        }
      }
      await saveShareEmail(me.uid, email);
      setShareEmail(email);
      setShowShareEmail(false);
    } catch (e) {
      Alert.alert('Could not save', e?.message || 'Please try again.');
    } finally {
      setShareEmailBusy(false);
    }
  };
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [newSharedHorses, setNewSharedHorses] = useState([]);
  const [seenSharedIds, setSeenSharedIds] = useState(null);
  const [horsesLoaded, setHorsesLoaded] = useState(false);
  const [clientHorseCount, setClientHorseCount] = useState(0);
  const [dueExpanded, setDueExpanded] = useState(false);
  const [partnerships, setPartnerships] = useState([]);
  useEffect(() => subscribeToHerdPartnerships(auth.currentUser?.uid, setPartnerships), []);
  useHerdPartnerSync(auth.currentUser?.uid, horses, partnerships);
  const [dashCompany, setDashCompany] = useState(null);
  useEffect(() => subscribeToMyCompany(auth.currentUser?.uid, setDashCompany), []);
  useCompanyClientSync(auth.currentUser?.uid, horses, dashCompany);
  useTeamShareSync(auth.currentUser?.uid, horses);
  const [seasonShares, setSeasonShares] = useState([]);
  useEffect(() => subscribeToSeasonShares(auth.currentUser?.uid, setSeasonShares), []);
  useSeasonShareSync(auth.currentUser?.uid, seasonShares);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    return subscribeToHorses(uid, (withSold) => {
      const all = withSold.filter((h) => !h.sold);
      setHorses(all);
      setSoldHorses(withSold.filter((h) => h.sold));
      setHorsesLoaded(true);
      setClientHorseCount(all.filter((h) => isHorseTrainer(h, uid)).length);
    }, { includeSold: true });
  }, []);

  // First-horse walkthrough: start it for a new user with no horses, end it
  // for anyone who already has some.
  const guide = useGuide();
  const dashScrollRef = useRef(null);
  const guideScrolled = useRef(false);
  useEffect(() => {
    if (!horsesLoaded || !guide.loaded || guide.uid !== auth.currentUser?.uid) return;
    if (guide.stage == null) setGuideStage(horses.length === 0 ? 'add' : 'done');
    else if ((guide.stage === 'add' || guide.stage === 'switch') && horses.length > 0) setGuideStage('done');
  }, [horsesLoaded, guide.loaded, guide.uid, guide.stage, horses.length]);

  // Which shared horses this USER has already been told about. Keyed by user:
  // two accounts on one phone must not dismiss each other's banners. The old
  // un-keyed value seeds the first read so nobody sees every old share again.
  const seenKey = 'sharedHorsesSeen:' + (auth.currentUser?.uid || 'anon');
  useEffect(() => {
    AsyncStorage.getItem(seenKey).then(async (v) => {
      if (v) { setSeenSharedIds(JSON.parse(v)); return; }
      const legacy = await AsyncStorage.getItem('sharedHorsesSeen');
      setSeenSharedIds(legacy ? JSON.parse(legacy) : []);
    }).catch(() => setSeenSharedIds([]));
  }, [seenKey]);

  useEffect(() => {
    if (seenSharedIds === null) return;
    const uid = auth.currentUser?.uid;
    // "Seen" means "told about this share", not "told about this horse, ever".
    // A horse that has left the herd is forgotten, so if it is shared again the
    // banner comes back. Only once the herd has loaded: before that, an empty
    // list would look like everything had been revoked.
    if (horsesLoaded) {
      const present = new Set(horses.map((h) => h.id));
      const stillHere = seenSharedIds.filter((id) => present.has(id));
      if (stillHere.length !== seenSharedIds.length) {
        setSeenSharedIds(stillHere);
        AsyncStorage.setItem(seenKey, JSON.stringify(stillHere)).catch(() => {});
        return; // re-runs with the pruned list
      }
    }
    const shared = horses.filter((h) => h.ownerId && h.ownerId !== uid && !seenSharedIds.includes(h.id));
    setNewSharedHorses(shared);
  }, [horses, seenSharedIds, horsesLoaded]);

  const dismissSharedBanner = async () => {
    const newSeen = [...(seenSharedIds || []), ...newSharedHorses.map((h) => h.id)];
    setSeenSharedIds(newSeen);
    setNewSharedHorses([]);
    await AsyncStorage.setItem(seenKey, JSON.stringify(newSeen));
  };

  useEffect(() => {
    isBiometricAvailable().then(setBiometricSupported);
    AsyncStorage.getItem(BIOMETRIC_LOCK_KEY).then((v) => setBiometricEnabled(v === 'true'));
  }, []);

  const toggleBiometricLock = async () => {
    const next = !biometricEnabled;
    setBiometricEnabled(next);
    await AsyncStorage.setItem(BIOMETRIC_LOCK_KEY, next ? 'true' : 'false');
  };

  const embryos = horses.filter((h) => h.type === 'embryo');
  const born = horses.filter((h) => h.type === 'born');
  // Frozen embryos are tracked separately — not part of the herd horse count.
  const frozenBatchesDash = horses.filter((h) => h.type === 'frozen_embryo');
  const frozenEmbryoCount = frozenBatchesDash.reduce((s, h) => s + (h.frozenCount || 0), 0);
  const herdCount = horses.filter((h) => h.type !== 'frozen_embryo').length;
  const ownHerdCount = horses.filter((h) => h.type !== 'frozen_embryo' && !isHorseTrainer(h, auth.currentUser?.uid)).length;
  const upcomingCount = horses.reduce((count, h) => {
    const next = h.milestones?.find((m) => !m.completed);
    return next ? count + 1 : count;
  }, 0);

  // Horses I own that have trainer requests awaiting my approval.
  const myUid = auth.currentUser?.uid;
  const pendingReqHorses = horses.filter(
    (h) => (h.ownerId ? h.ownerId === myUid : h.members?.[0] === myUid) && (h.pendingTrainers || []).length > 0
  );
  const pendingReqCount = pendingReqHorses.reduce((n, h) => n + (h.pendingTrainers || []).length, 0);

  // Incomplete tasks due today or overdue, assigned to me (or unassigned on a
  // horse I own). Drives the in-app "task due" banner — works on web too.
  const todayStr = new Date().toLocaleDateString('en-CA'); // local YYYY-MM-DD
  const dueTasks = [];
  horses.forEach((h) => {
    (h.milestones || []).forEach((m) => {
      if (m.completed || !m.date || m.date > todayStr) return;
      const mine = m.assignedToUid
        ? m.assignedToUid === myUid
        : (h.ownerId ? h.ownerId === myUid : h.members?.[0] === myUid);
      if (!mine) return;
      dueTasks.push({ horse: h, m, overdue: m.date < todayStr });
    });
  });
  dueTasks.sort((a, b) => (a.m.date || '').localeCompare(b.m.date || ''));
  const isMyClientWork = (x) => isHorseTrainer(x.horse, myUid) && !iOwnHorse(x.horse, myUid) && taskVisibleTo(x.horse, x.m, myUid);
  const apptsToday = dueTasks.filter((x) => !x.overdue && isMyClientWork(x));
  const apptsLate = dueTasks.filter((x) => x.overdue && isMyClientWork(x));
  const apptHorseCount = new Set(apptsToday.map((x) => x.horse.id)).size;
  const lateHorseCount = new Set(apptsLate.map((x) => x.horse.id)).size;
  const otherDue = dueTasks.filter((x) => !apptsToday.includes(x) && !apptsLate.includes(x));

  const handleExport = async () => {
    try {
      // Other people's private provider notes must not leave the app in a file.
      // Matched by author uid — tighter than the in-app company rule, which would
      // also show a colleague's private notes. A backup missing one of those beats
      // leaking it. Note this only closes the file path: the raw horse document is
      // still readable by any member through the API, so "private" remains a UI
      // convention rather than a real boundary.
      const uid = auth.currentUser?.uid;
      // Sold horses are archived, not gone — a backup must include them.
      const exportHorses = [...horses, ...soldHorses].map((h) =>
        (h.trainingNotes || []).some((n) => n.private && n.createdByUid !== uid)
          ? { ...h, trainingNotes: h.trainingNotes.filter((n) => !n.private || n.createdByUid === uid) }
          : h
      );
      const payload = JSON.stringify({ exportedAt: new Date().toISOString(), version: 1, horses: exportHorses }, null, 2);
      const filename = `horses_${new Date().toISOString().split('T')[0]}.json`;
      if (Platform.OS === 'web') {
        const blob = new Blob([payload], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
        return;
      }
      const fileUri = FileSystem.cacheDirectory + filename;
      await FileSystem.writeAsStringAsync(fileUri, payload, { encoding: FileSystem.EncodingType.UTF8 });
      await Sharing.shareAsync(fileUri, {
        mimeType: 'application/json',
        dialogTitle: 'Save Horse Data',
        UTI: 'public.json',
      });
    } catch (e) {
      Alert.alert('Export Failed', e.message);
    }
  };

  const handleImport = async () => {
    if (Platform.OS === 'web') {
      const input = document.createElement('input');
      input.type = 'file'; input.accept = '.json,.csv';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const content = await file.text();
        const trimmed = content.trim();
        const isCSV = /\.csv$/i.test(file.name || '')
          || (!/\.json$/i.test(file.name || '') && !trimmed.startsWith('{') && !trimmed.startsWith('['));

        let imported;
        if (isCSV) {
          const rows = parseCSV(content);
          if (rows.length === 0) { Alert.alert('Empty File', 'No horse rows were found in this CSV file.'); return; }
          try {
            imported = rows.map((row, idx) => csvRowToHorse(row, idx + 2));
          } catch (err) {
            Alert.alert('Invalid CSV', err.message);
            return;
          }
        } else {
          let parsed;
          try {
            parsed = JSON.parse(content);
          } catch {
            Alert.alert('Invalid File', 'The selected file is not valid JSON or CSV.');
            return;
          }
          imported = parsed.horses ?? (Array.isArray(parsed) ? parsed : null);
          if (!imported) { Alert.alert('Invalid File', 'This file does not appear to be a valid horse data export.'); return; }
        }

        Alert.alert('Import Data', `Add ${imported.length} horse(s) from this file to your herd?`, [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Import', onPress: async () => { await importHorses(auth.currentUser.uid, imported); Alert.alert('Imported', `Successfully imported ${imported.length} horse(s).`); } },
        ]);
      };
      input.click();
      return;
    }
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/json', 'text/plain', 'text/csv', 'text/comma-separated-values', 'application/vnd.ms-excel', '*/*'],
        copyToCacheDirectory: true,
      });
      if (result.canceled) return;
      const file = result.assets[0];
      const content = await FileSystem.readAsStringAsync(file.uri);
      const isCSV = /\.csv$/i.test(file.name || '') || (!/\.json$/i.test(file.name || '') && !content.trim().startsWith('{') && !content.trim().startsWith('['));

      let imported;
      if (isCSV) {
        const rows = parseCSV(content);
        if (rows.length === 0) {
          Alert.alert('Empty File', 'No horse rows were found in this CSV file.');
          return;
        }
        try {
          imported = rows.map((row, idx) => csvRowToHorse(row, idx + 2));
        } catch (e) {
          Alert.alert('Invalid CSV', e.message);
          return;
        }
      } else {
        let parsed;
        try {
          parsed = JSON.parse(content);
        } catch {
          Alert.alert('Invalid File', 'The selected file is not valid JSON or CSV.');
          return;
        }
        imported = parsed.horses ?? (Array.isArray(parsed) ? parsed : null);
        if (!imported) {
          Alert.alert('Invalid File', 'This file does not appear to be a valid horse data export.');
          return;
        }
      }

      Alert.alert(
        'Import Data',
        `Add ${imported.length} horse(s) from this file to your herd?`,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Import',
            onPress: async () => {
              await importHorses(auth.currentUser.uid, imported);
              Alert.alert('Imported', `Successfully imported ${imported.length} horse(s).`);
            },
          },
        ]
      );
    } catch (e) {
      Alert.alert('Import Failed', e.message);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      if (Platform.OS === 'web') {
        const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'horse_import_template.csv'; a.click();
        URL.revokeObjectURL(url);
        return;
      }
      const fileUri = FileSystem.cacheDirectory + 'horse_import_template.csv';
      await FileSystem.writeAsStringAsync(fileUri, CSV_TEMPLATE, { encoding: FileSystem.EncodingType.UTF8 });
      await Sharing.shareAsync(fileUri, {
        mimeType: 'text/csv',
        dialogTitle: 'Horse Import Template',
        UTI: 'public.comma-separated-values-text',
      });
    } catch (e) {
      Alert.alert('Download Failed', e.message);
    }
  };

  const handleSignOut = () => {
    if (Platform.OS === 'web') {
      if (window.confirm('Are you sure you want to sign out?')) signOut();
    } else {
      Alert.alert('Sign Out', 'Are you sure you want to sign out?', [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Sign Out', style: 'destructive', onPress: () => signOut() },
      ]);
    }
  };

  const [deletingAccount, setDeletingAccount] = useState(false);
  const handleDeleteAccount = () => {
    // Say exactly what goes — especially horses other people can see.
    const uid = auth.currentUser?.uid;
    const owned = [...horses, ...soldHorses].filter((h) => (h.ownerId ? h.ownerId === uid : h.members?.[0] === uid));
    const sharedOwned = owned.filter((h) => (h.members || []).some((m) => m !== uid));
    const lines = [
      `This permanently deletes your account and ${owned.length} horse${owned.length === 1 ? '' : 's'} you own, with their health logs, billing records, photos and documents. Also your breeding plans, stud library and settings.`,
      sharedOwned.length
        ? `${sharedOwned.length} of your horses ${sharedOwned.length === 1 ? 'is' : 'are'} shared with other people — ${sharedOwned.length === 1 ? 'it' : 'they'} will be deleted for them too. Transfer ${sharedOwned.length === 1 ? 'it' : 'them'} first if they should keep ${sharedOwned.length === 1 ? 'it' : 'them'}.`
        : '',
      "Charges and payments you recorded on other people's horses stay on their records.",
      'This cannot be undone.',
    ].filter(Boolean);
    const confirm = async () => {
      setDeletingAccount(true);
      try {
        await deleteAccount(); // signs out on success → login screen
      } catch (e) {
        setDeletingAccount(false);
        if (e.code === 'apple-cancelled') {
          Alert.alert('Not deleted', 'Apple confirmation was cancelled, so your account was not deleted.');
        } else {
          Alert.alert('Could not delete account', 'Nothing was lost — please check your connection and try again.');
        }
      }
    };
    if (Platform.OS === 'web') {
      if (window.confirm('Delete account?\n\n' + lines.join('\n\n'))) confirm();
    } else {
      Alert.alert('Delete Account', lines.join('\n\n'), [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete Everything', style: 'destructive', onPress: confirm },
      ]);
    }
  };

  // Which card the walkthrough is pointing at right now (others dim).
  const guideOn = horsesLoaded && guide.loaded && guide.uid === auth.currentUser?.uid;
  const skipGuide = () => setGuideStage('done');
  let guideFocus = null;
  if (guideOn && (guide.stage === 'add' || guide.stage === 'switch') && horses.length === 0) {
    guideFocus = {
      title: 'Add Horse', above: true, step: 'Step 1 of 3:',
      text: "Add your first horse here. You'll find this button on your Home screen any time.",
      onPress: () => setGuideStage('switch'),
    };
  } else if (guideOn && guide.stage === 'placed' && guide.kind === 'client' && clientHorseCount > 0) {
    guideFocus = {
      title: 'Client Horses', step: 'Step 3 of 3:',
      text: `${guide.horseName || 'Your horse'} is in Client Horses. This section shows on Home once you have a client horse. Tap it to open.`,
      onPress: () => setGuideStage('payments'),
    };
  } else if (guideOn && guide.stage === 'placed' && guide.kind !== 'client' && horses.length > 0) {
    guideFocus = {
      title: 'My Herd', step: 'Step 3 of 3:', gotIt: true,
      text: 'Your horses live in My Herd. Tap a horse to add breed dates, health records, photos and documents. You can also add horses from the button at the bottom of My Herd.',
      onPress: () => setGuideStage('done'),
    };
  }

  const SectionCard = ({ icon, title, subtitle, onPress }) => {
    const focus = !!guideFocus && guideFocus.title === title;
    const card = (
      <TouchableOpacity
        style={[styles.sectionCard, focus && GUIDE_HIGHLIGHT, !!guideFocus && !focus && { opacity: 0.35 }]}
        onPress={() => { if (focus) guideFocus.onPress(); onPress(); }}
        activeOpacity={0.75}
      >
        <Text style={styles.sectionCardIcon}>{icon}</Text>
        <View style={{ flex: 1 }}>
          <Text style={styles.sectionCardTitle}>{title}</Text>
          <Text style={styles.sectionCardSub}>{subtitle}</Text>
        </View>
        <Text style={styles.sectionCardChevron}>›</Text>
      </TouchableOpacity>
    );
    if (!focus) return card;
    const tip = (
      <GuideTip step={guideFocus.step} text={guideFocus.text} arrow={guideFocus.above ? 'down' : 'up'} onSkip={guideFocus.gotIt ? null : skipGuide}>
        {guideFocus.gotIt && (
          <TouchableOpacity onPress={skipGuide} style={{ alignSelf: 'flex-end', marginTop: 8 }} activeOpacity={0.7}>
            <Text style={{ color: GOLD, fontWeight: '700', fontSize: 13.5 }}>Got it</Text>
          </TouchableOpacity>
        )}
      </GuideTip>
    );
    return (
      <View
        onLayout={(e) => {
          // Bring the card into view once (Add Horse sits low on Home).
          if (guideScrolled.current || !guideFocus.above) return;
          guideScrolled.current = true;
          // Wait for the rest of Home to lay out, or the scroll gets clamped short.
          const y = Math.max(0, e.nativeEvent.layout.y - 140);
          setTimeout(() => dashScrollRef.current?.scrollTo({ y, animated: true }), 500);
        }}
      >
        {guideFocus.above ? <>{tip}{card}</> : <>{card}{tip}</>}
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.screen} edges={['bottom']}>

      {/* ── Branded hero banner — fixed, does not scroll ── */}
      <View style={styles.dashHero}>
        <View style={[styles.dashCorner, styles.dashCornerTL]} />
        <View style={[styles.dashCorner, styles.dashCornerTR]} />
        <View style={[styles.dashCorner, styles.dashCornerBL]} />
        <View style={[styles.dashCorner, styles.dashCornerBR]} />
        <Image
          source={require('./assets/horse-silhouette.png')}
          style={styles.dashHorseSilhouette}
        />
        <Text style={styles.dashHeroStar}>★</Text>
        <Text style={styles.dashHeroAppName}>Horse <Text style={styles.dashHeroAppNameAccent}>Math</Text></Text>
        <Text style={styles.dashHeroTitle}>Herd & Breeding{'\n'}Management</Text>
        <View style={styles.dashHeroPoweredRow}>
          <View style={styles.dashHeroPoweredLine} />
          <Text style={styles.dashHeroPoweredText}>POWERED BY GINGERBREAD RANCH</Text>
          <View style={styles.dashHeroPoweredLine} />
        </View>
      </View>

      <ScrollView ref={dashScrollRef} contentContainerStyle={{ paddingBottom: 40 }}>
        <View style={{ padding: 16 }}>
        <Text style={styles.dashSub}>Here's what's happening with your herd.</Text>

        {/* ── Pending trainer requests banner (owner) ── */}
        {pendingReqCount > 0 && (
          <View style={styles.requestBanner}>
            <Text style={styles.requestBannerTitle}>
              ⚠️ {pendingReqCount === 1 ? '1 trainer request needs' : `${pendingReqCount} trainer requests need`} your approval
            </Text>
            {pendingReqHorses.map((h) =>
              (h.pendingTrainers || []).map((req) => (
                <TouchableOpacity
                  key={`${h.id}-${req.uid}`}
                  style={styles.requestBannerRow}
                  onPress={() => navigation.navigate('Horse', { horseId: h.id })}
                  activeOpacity={0.75}
                >
                  <Text style={styles.requestBannerText}>
                    <Text style={{ fontWeight: '700' }}>{req.requestedByName || 'A trainer'}</Text>
                    {' wants to add '}
                    <Text style={{ fontWeight: '700' }}>{req.name || req.email}</Text>
                    {' to '}
                    <Text style={{ fontWeight: '700' }}>{displayName(h)}</Text>
                  </Text>
                  <Text style={styles.requestBannerChevron}>Review ›</Text>
                </TouchableOpacity>
              ))
            )}
          </View>
        )}

        {/* ── Tasks due banner (assignee / owner) ── */}
        {dueTasks.length > 0 && (
          <View style={styles.dueBanner}>
            <Text style={styles.dueBannerTitle}>
              🔔 {(() => {
                const parts = [];
                if (lateHorseCount > 0) parts.push(`${lateHorseCount} missed`);
                if (apptHorseCount > 0) parts.push(`appointments on ${apptHorseCount} horse${apptHorseCount === 1 ? '' : 's'}`);
                if (otherDue.length > 0) parts.push(`${otherDue.length} task${otherDue.length === 1 ? '' : 's'}`);
                return `${parts.join(' and ')} due`;
              })()}
            </Text>
            {lateHorseCount > 0 && (
              <TouchableOpacity
                style={styles.dueBannerRow}
                onPress={() => navigation.navigate('Milestones', { calScope: 'today', clientOnly: true })}
                activeOpacity={0.75}
              >
                <Text style={styles.dueBannerText}>
                  <Text style={{ fontWeight: '700' }}>Missed appointments</Text>
                  {` — ${lateHorseCount} horse${lateHorseCount === 1 ? '' : 's'}`}
                  {apptsLate.length > lateHorseCount && <Text style={styles.dueBannerDate}>{'  '}{apptsLate.length} jobs</Text>}
                </Text>
                <Text style={[styles.dueBannerTag, styles.dueBannerTagOverdue]}>Overdue ›</Text>
              </TouchableOpacity>
            )}
            {apptHorseCount > 0 && (
              <TouchableOpacity
                style={styles.dueBannerRow}
                onPress={() => navigation.navigate('Milestones', { calScope: 'today', clientOnly: true })}
                activeOpacity={0.75}
              >
                <Text style={styles.dueBannerText}>
                  <Text style={{ fontWeight: '700' }}>Appointments today</Text>
                  {` — ${apptHorseCount} horse${apptHorseCount === 1 ? '' : 's'}`}
                  {apptsToday.length > apptHorseCount && <Text style={styles.dueBannerDate}>{'  '}{apptsToday.length} jobs</Text>}
                </Text>
                <Text style={styles.dueBannerTag}>Day sheet ›</Text>
              </TouchableOpacity>
            )}
            {(dueExpanded ? otherDue : otherDue.slice(0, 5)).map(({ horse: h, m, overdue }) => (
              <TouchableOpacity
                key={`${h.id}-${m.id}`}
                style={styles.dueBannerRow}
                onPress={() => navigation.navigate('Horse', { horseId: h.id })}
                activeOpacity={0.75}
              >
                <Text style={styles.dueBannerText}>
                  <Text style={{ fontWeight: '700' }}>{m.name}</Text>
                  {' — '}{displayName(h)}
                  {!!m.date && <Text style={styles.dueBannerDate}>{'  '}{formatDate(m.date)}</Text>}
                </Text>
                <Text style={[styles.dueBannerTag, overdue && styles.dueBannerTagOverdue]}>
                  {overdue ? 'Overdue ›' : 'Today ›'}
                </Text>
              </TouchableOpacity>
            ))}
            {otherDue.length > 5 && (
              <TouchableOpacity onPress={() => setDueExpanded((v) => !v)} activeOpacity={0.7}>
                <Text style={styles.dueBannerMore}>
                  {dueExpanded ? 'Show less ▲' : `Show all ${otherDue.length} ▼`}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* ── Hidden Apple email: nobody can share with you until this is set ── */}
        {isRelayEmail(auth.currentUser?.email) && !shareEmail && (
          <TouchableOpacity
            style={[styles.sharedBanner, { borderColor: '#B8860B' }]}
            onPress={() => { setShareEmailDraft(''); setShowShareEmail(true); }}
            activeOpacity={0.8}
          >
            <Text style={styles.sharedBannerTitle}>⚠️ Nobody can share horses with you yet</Text>
            <Text style={{ fontSize: 13, color: TEXT, lineHeight: 19, marginTop: 4 }}>
              You signed in with Apple and hid your email, so your account has an address nobody knows. Add the email people know you by so they can share horses with you.
            </Text>
            <Text style={{ fontSize: 13, color: GREEN, fontWeight: '700', marginTop: 8 }}>Add a sharing email ›</Text>
          </TouchableOpacity>
        )}

        {/* ── Shared horse banner ── */}
        {newSharedHorses.length > 0 && (
          <View style={styles.sharedBanner}>
            <Text style={styles.sharedBannerTitle}>
              🐴 {newSharedHorses.length === 1 ? 'A horse was' : `${newSharedHorses.length} horses were`} shared with you!
            </Text>
            {newSharedHorses.map((h) => {
              const perm = h.shares?.find((s) => s.uid === auth.currentUser?.uid)?.permission;
              return (
                <TouchableOpacity
                  key={h.id}
                  style={styles.sharedBannerRow}
                  onPress={() => { dismissSharedBanner(); navigation.navigate('Horse', { horseId: h.id }); }}
                  activeOpacity={0.75}
                >
                  <Text style={styles.sharedBannerHorseName}>{displayName(h)}</Text>
                  <Text style={styles.sharedBannerPerm}>{perm === 'trainer' ? 'Provider' : perm === 'edit' ? 'Can Edit' : 'View Only'} ›</Text>
                </TouchableOpacity>
              );
            })}
            <TouchableOpacity onPress={dismissSharedBanner} style={styles.sharedBannerDismiss} activeOpacity={0.75}>
              <Text style={styles.sharedBannerDismissTxt}>Got it</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* ── Stats row ── */}
        <View style={[styles.statsRow, !!guideFocus && { opacity: 0.35 }]}>
          <View style={styles.statCard}>
            <Text style={styles.statNumber}>{herdCount}</Text>
            <Text style={styles.statLabel}>Total Horses</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={[styles.statNumber, { color: RED }]}>{embryos.length}</Text>
            <Text style={styles.statLabel}>Embryo / In Foal</Text>
          </View>
          {frozenEmbryoCount > 0 && (
            <View style={styles.statCard}>
              <Text style={[styles.statNumber, { color: '#2E6DA4' }]}>{frozenEmbryoCount}</Text>
              <Text style={styles.statLabel}>Frozen Embryos</Text>
            </View>
          )}
          <View style={styles.statCard}>
            <Text style={[styles.statNumber, { color: GREEN }]}>{born.length}</Text>
            <Text style={styles.statLabel}>Born</Text>
          </View>
        </View>

        {upcomingCount > 0 && (
          <View style={styles.upcomingBanner}>
            <Text style={styles.upcomingBannerTxt}>
              {upcomingCount} horse{upcomingCount === 1 ? '' : 's'} with upcoming milestones
            </Text>
          </View>
        )}

        {/* ── Sections ── */}
        <Text style={styles.sectionHeading}>Sections</Text>
        <SectionCard
          icon="🐴"
          title="My Herd"
          subtitle={`${ownHerdCount} horse${ownHerdCount === 1 ? '' : 's'}${clientHorseCount > 0 ? ' (plus client horses)' : ''} · view, sort & track`}
          onPress={() => navigation.navigate('Herd')}
        />
        {clientHorseCount > 0 && (
          <SectionCard
            icon="🎓"
            title="Client Horses"
            subtitle={`${clientHorseCount} horse${clientHorseCount === 1 ? '' : 's'} you bill · ledger & invoices`}
            onPress={() => navigation.navigate('ClientHorses')}
          />
        )}

        {/* ── Herd Earnings Summary ── */}
        {horses.some((h) => (h.earnings || []).length > 0) && (() => {
          const fmt = (n) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const horseTotal = horses.reduce((s, h) => s + horseEarningsTotal(h), 0);
          const offspringTotal = herdOffspringTotal(horses);
          const hasOffspring = offspringTotal > 0;
          return (
            <TouchableOpacity style={styles.sectionCard} onPress={() => navigation.navigate('HerdEarnings')} activeOpacity={0.75}>
              <Text style={styles.sectionCardIcon}>🏆</Text>
              <View style={{ flex: 1 }}>
                <Text style={styles.sectionCardTitle}>Herd Earnings</Text>
                <View style={{ flexDirection: 'row', gap: 16, marginTop: 6 }}>
                  <View>
                    <Text style={styles.earningsTotalLabel}>Horse</Text>
                    <Text style={[styles.earningsTotalValue, { color: GREEN, fontSize: 15 }]}>{fmt(horseTotal)}</Text>
                  </View>
                  {hasOffspring && (
                    <View>
                      <Text style={styles.earningsTotalLabel}>Offspring</Text>
                      <Text style={[styles.earningsTotalValue, { color: RED, fontSize: 15 }]}>{fmt(offspringTotal)}</Text>
                    </View>
                  )}
                </View>
              </View>
              <Text style={styles.sectionCardChevron}>›</Text>
            </TouchableOpacity>
          );
        })()}
        {canSeeBreedingPlanning() && (
          <SectionCard
            icon="📋"
            title="Breeding Planning"
            subtitle={(() => {
              const waiting = seasonShares.filter((s) => s.status === 'pending' && s.toUid === auth.currentUser?.uid).length;
              return waiting > 0
                ? `${waiting} breeding plan share${waiting === 1 ? '' : 's'} waiting for you`
                : "Plan next season's crosses";
            })()}
            onPress={() => navigation.navigate('BreedingPlanning')}
          />
        )}
        <SectionCard
          icon="📅"
          title="Milestones & Tasks"
          subtitle={
            upcomingCount > 0
              ? `${upcomingCount} horse${upcomingCount === 1 ? '' : 's'} with upcoming milestones`
              : 'Tasks and a timeline of what is due'
          }
          onPress={() => navigation.navigate('Milestones')}
        />
        <SectionCard
          icon="🩺"
          title="Herd Health Calendar"
          subtitle="All horses' injuries & lameness on one calendar, by type"
          onPress={() => navigation.navigate('HerdHealth')}
        />
        {(() => {
          const uid = auth.currentUser?.uid;
          const waiting = partnerships.filter((p) => p.status === 'pending' && p.invitedBy !== uid).length;
          const active = activePartners(partnerships, uid);
          const subtitle = waiting > 0
            ? `${waiting} herd invite${waiting === 1 ? '' : 's'} waiting for you`
            : active.length > 0
              ? `Herd shared with ${active.map((a) => a.name || a.email).join(', ')}`
              : 'Share your whole herd, or several horses at once';
          return (
            <SectionCard
              icon="🤝"
              title="Sharing"
              subtitle={subtitle}
              onPress={() => navigation.navigate('Sharing')}
            />
          );
        })()}
        <SectionCard
          icon="➕"
          title="Add Horse"
          subtitle="Add a new embryo or already-born horse"
          onPress={() => navigation.navigate('Herd', { openAdd: true })}
        />

        <Modal visible={showShareEmail} animationType="slide" transparent onRequestClose={() => setShowShareEmail(false)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.sheet}>
            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={styles.sheetTitle}>Sharing Email</Text>
              <Text style={{ fontSize: 13, color: MUTED, lineHeight: 19, marginBottom: 10 }}>
                When someone shares a horse with you, they type your email. Your sign-in email is {auth.currentUser?.email || 'unknown'}
                {isRelayEmail(auth.currentUser?.email) ? ' — an Apple relay address nobody would know to use. Add the email you actually give people.' : '. If that is not the one people know you by, add the one they do.'}
              </Text>
              <Text style={styles.fieldLabel}>Email people can share with you at</Text>
              <TextInput
                style={styles.input}
                placeholder="you@example.com"
                placeholderTextColor="#BDBDBD"
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                value={shareEmailDraft}
                onChangeText={setShareEmailDraft}
              />
              <Text style={{ fontSize: 11, color: MUTED, marginTop: -8, marginBottom: 6 }}>Your sign-in email keeps working too. Leave blank to remove.</Text>
              <View style={styles.sheetBtns}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowShareEmail(false)} activeOpacity={0.85}>
                  <Text style={styles.cancelBtnText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={handleSaveShareEmail} disabled={shareEmailBusy} activeOpacity={0.85}>
                  {shareEmailBusy ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>Save</Text>}
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── Data & Account ── */}
        <TouchableOpacity
          style={styles.collapsibleHeader}
          onPress={() => setShowDataAccount((v) => !v)}
          activeOpacity={0.75}
        >
          <Text style={styles.sectionHeading}>Data & Account</Text>
          <Text style={styles.sectionCardChevron}>{showDataAccount ? '⌄' : '›'}</Text>
        </TouchableOpacity>
        {showDataAccount && (
          <>
            <Text style={styles.loggedInAsTxt}>Logged in as {auth.currentUser?.email}</Text>
            {(() => {
              const login = auth.currentUser?.email || '';
              const relay = isRelayEmail(login);
              const subtitle = shareEmail
                ? `People can share with you at ${shareEmail}`
                : relay
                  ? 'Your Apple ID is hidden — nobody can share horses with you until you add one'
                  : `People can share with you at ${login}`;
              return (
                <SectionCard
                  icon={relay && !shareEmail ? '⚠️' : '📧'}
                  title="Sharing Email"
                  subtitle={subtitle}
                  onPress={() => { setShareEmailDraft(shareEmail); setShowShareEmail(true); }}
                />
              );
            })()}
            <SectionCard icon="⬆️" title="Export Data" subtitle="Save your herd data to a file" onPress={handleExport} />
            <SectionCard icon="⬇️" title="Import Data" subtitle="Load horses from a JSON or CSV file" onPress={handleImport} />
            <SectionCard icon="📊" title="Download Excel Template" subtitle="Fillable spreadsheet with dropdowns for bulk-adding horses" onPress={() => Linking.openURL('https://horsemath.app/Horse_Import_Template.xlsx')} />
            <SectionCard icon="📋" title="Download CSV Template" subtitle="Plain CSV template for bulk-adding horses" onPress={handleDownloadTemplate} />
            <SectionCard icon="🗑️" title="Recently Deleted" subtitle="Restore horses removed in the last 30 days" onPress={() => navigation.navigate('RecentlyDeleted')} />
            {canSeeBreedingPlanning() && (
              <SectionCard icon="🐎" title="Stud Library" subtitle="Saved stallions reused across mares and seasons" onPress={() => navigation.navigate('StudLibrary')} />
            )}
            <SectionCard icon="🏷️" title="Task Categories" subtitle="Add custom categories like Teeth Floated" onPress={() => navigation.navigate('TaskCategories')} />
            <SectionCard icon="🩺" title="Note Tags" subtitle="Add custom health log tags like Dental Exam" onPress={() => navigation.navigate('NoteTags')} />
            {auth.currentUser?.email === ADMIN_EMAIL && (
              <SectionCard icon="🛠️" title="Admin: All Users" subtitle="View every registered user and their horses" onPress={() => navigation.navigate('Admin')} />
            )}
            {biometricSupported && (
              <TouchableOpacity
                style={styles.recipToggle}
                onPress={toggleBiometricLock}
                activeOpacity={0.75}
              >
                <View style={[styles.recipToggleTrack, biometricEnabled && styles.recipToggleTrackOn]}>
                  <View style={[styles.recipToggleThumb, biometricEnabled && styles.recipToggleThumbOn]} />
                </View>
                <Text style={styles.recipToggleLabel}>
                  {biometricEnabled ? 'App Lock (Face ID / Fingerprint) — On' : 'App Lock (Face ID / Fingerprint) — Off'}
                </Text>
              </TouchableOpacity>
            )}
            <SectionCard icon="💬" title="Contact Support" subtitle="gingerbreadranch.life@gmail.com" onPress={() => Linking.openURL('mailto:gingerbreadranch.life@gmail.com')} />
            <SectionCard icon="🔒" title="Privacy Policy" subtitle="How we handle your data" onPress={() => Linking.openURL('https://horsemath.app/privacy')} />
            <SectionCard icon="📄" title="Terms of Service" subtitle="Terms governing your use of Horse Math" onPress={() => Linking.openURL('https://horsemath.app/terms')} />
            <SectionCard icon="🚪" title="Sign Out" subtitle="Sign out of your account" onPress={handleSignOut} />
            <SectionCard icon="⚠️" title="Delete Account" subtitle={deletingAccount ? 'Deleting your account and data…' : 'Permanently delete your account and all data'} onPress={deletingAccount ? () => {} : handleDeleteAccount} />
          </>
        )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

// ─── HERD EARNINGS SCREEN ────────────────────────────────────────────────────

function HerdEarningsScreen({ navigation }) {
  const [horses, setHorses] = useState([]);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const unsub = subscribeToHorses(uid, setHorses);
    return unsub;
  }, []);

  // Totalled once per horse — offspring earnings scan the herd, so don't compute
  // them again inside the render. Includes mares/stallions whose only earnings
  // are their offspring's, derived live.
  const rows = horses
    .map((h) => ({ h, horseTotal: horseEarningsTotal(h), offspringTotal: offspringEarningsTotal(h, horses) }))
    .filter((r) => r.horseTotal > 0 || r.offspringTotal > 0 || (r.h.earnings || []).length > 0);
  const fmt = (n) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <SafeAreaView style={styles.screen} edges={['bottom']}>
      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
        {rows.length === 0 ? (
          <Text style={[styles.noNotesTxt, { marginTop: 20 }]}>No horses with earnings recorded yet.</Text>
        ) : (
          rows.map(({ h, horseTotal, offspringTotal }) => {
            return (
              <TouchableOpacity
                key={h.id}
                style={[styles.sectionCard, { flexDirection: 'column', alignItems: 'flex-start' }]}
                onPress={() => navigation.navigate('Horse', { horseId: h.id })}
                activeOpacity={0.75}
              >
                <Text style={[styles.sectionCardTitle, { marginBottom: 8 }]}>{h.registeredName || h.name}</Text>
                <View style={{ flexDirection: 'row', gap: 16 }}>
                  <View>
                    <Text style={[styles.earningsTotalLabel]}>Horse</Text>
                    <Text style={[styles.earningsTotalValue, { color: GREEN, fontSize: 15 }]}>{fmt(horseTotal)}</Text>
                  </View>
                  <View>
                    <Text style={[styles.earningsTotalLabel]}>Offspring</Text>
                    <Text style={[styles.earningsTotalValue, { color: RED, fontSize: 15 }]}>{fmt(offspringTotal)}</Text>
                  </View>
                </View>
              </TouchableOpacity>
            );
          })
        )}
        {hasSharedProgenyCredit(horses) && (
          <Text style={styles.herdEarningsNote}>
            Some offspring have both their sire and dam in your stable. Each parent is credited in full
            above. The herd total on the Dashboard counts each foal's earnings once, so a single purse
            isn't counted twice — which is why these figures add up to more than the herd total.
          </Text>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

// ─── RECENTLY DELETED SCREEN ─────────────────────────────────────────────────

const TRASH_RETENTION_DAYS = 30;

function RecentlyDeletedScreen() {
  const [horses, setHorses] = useState([]);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const unsub = subscribeToDeletedHorses(uid, setHorses);
    return unsub;
  }, []);

  useEffect(() => {
    horses.forEach((h) => {
      const deletedDate = h.deletedAt?.toDate ? h.deletedAt.toDate() : null;
      if (!deletedDate) return;
      const daysSince = (Date.now() - deletedDate.getTime()) / 86400000;
      if (daysSince > TRASH_RETENTION_DAYS) deleteHorse(h.id).catch(() => {});
    });
  }, [horses]);

  const handleRestore = (h) => restoreHorse(h.id, auth.currentUser?.uid, auth.currentUser?.email);

  const handlePermanentDelete = (h) => {
    Alert.alert(
      'Delete Forever',
      `Permanently delete ${displayName(h)} and its billing records, health log, photos and documents — for everyone it was shared with? This cannot be undone.`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete Forever', style: 'destructive', onPress: () => deleteHorse(h.id) },
      ]
    );
  };

  return (
    <SafeAreaView style={styles.screen} edges={['bottom']}>
      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
        {horses.length === 0 && (
          <Text style={styles.noNotesTxt}>Nothing in Recently Deleted.</Text>
        )}
        {horses.map((h) => {
          const deletedDate = h.deletedAt?.toDate ? h.deletedAt.toDate() : null;
          const daysLeft = deletedDate
            ? Math.max(0, TRASH_RETENTION_DAYS - Math.floor((Date.now() - deletedDate.getTime()) / 86400000))
            : TRASH_RETENTION_DAYS;
          const uid = auth.currentUser?.uid;
          const canManage = h.ownerId === uid || (!h.ownerId && h.members?.[0] === uid);
          return (
            <View key={h.id} style={styles.notesCard}>
              <Text style={styles.noteText}>{displayName(h)}</Text>
              <Text style={styles.notePreview}>
                {daysLeft} day{daysLeft === 1 ? '' : 's'} left before permanent deletion
              </Text>
              {!!h.deletedBy?.email && (
                <Text style={styles.notePreview}>Deleted by {h.deletedBy.email}</Text>
              )}
              {canManage && (
                <View style={{ flexDirection: 'row', gap: 20, marginTop: 10 }}>
                  <TouchableOpacity onPress={() => handleRestore(h)} activeOpacity={0.75}>
                    <Text style={[styles.noteDeleteTxt, { color: GREEN }]}>Restore</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => handlePermanentDelete(h)} activeOpacity={0.75}>
                    <Text style={styles.noteDeleteTxt}>Delete Forever</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          );
        })}
      </ScrollView>
    </SafeAreaView>
  );
}

// ─── TASK CATEGORIES SCREEN ───────────────────────────────────────────────────

function TaskCategoriesScreen() {
  const [customCategories, setCustomCategories] = useState([]);
  const [hiddenCategories, setHiddenCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const unsub = subscribeToUserSettings(uid, (s) => {
      setCustomCategories(s.customCategories || []);
      setHiddenCategories(s.hiddenCategories || []);
    });
    return unsub;
  }, []);

  const handleAdd = () => {
    const trimmed = newCategory.trim();
    if (!trimmed) return;
    const all = [...PRESET_CATEGORIES, ...customCategories];
    if (all.some((c) => c.toLowerCase() === trimmed.toLowerCase())) {
      Alert.alert('Already exists', `"${trimmed}" is already a category.`);
      return;
    }
    const uid = auth.currentUser?.uid;
    addCustomCategory(uid, trimmed);
    setNewCategory('');
  };

  const handleRemove = (category) => {
    const uid = auth.currentUser?.uid;
    removeCustomCategory(uid, category);
  };

  const handleHide = (category) => {
    const uid = auth.currentUser?.uid;
    hidePresetCategory(uid, category);
  };

  const handleRestore = (category) => {
    const uid = auth.currentUser?.uid;
    unhidePresetCategory(uid, category);
  };

  const visiblePresets = PRESET_CATEGORIES.filter((c) => !hiddenCategories.includes(c));

  return (
    <SafeAreaView style={styles.screen} edges={['bottom']}>
      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
        <Text style={styles.sectionHeading}>Built-in Categories</Text>
        {visiblePresets.length === 0 && (
          <Text style={styles.noNotesTxt}>No built-in categories shown.</Text>
        )}
        {visiblePresets.map((c) => (
          <View key={c} style={styles.notesCard}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text style={styles.noteText}>{c}</Text>
              <TouchableOpacity onPress={() => handleHide(c)} activeOpacity={0.75}>
                <Text style={styles.noteDeleteTxt}>Remove</Text>
              </TouchableOpacity>
            </View>
          </View>
        ))}

        {hiddenCategories.length > 0 && (
          <>
            <Text style={[styles.sectionHeading, { marginTop: 18 }]}>Hidden Built-in Categories</Text>
            {hiddenCategories.map((c) => (
              <View key={c} style={styles.notesCard}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text style={styles.noteText}>{c}</Text>
                  <TouchableOpacity onPress={() => handleRestore(c)} activeOpacity={0.75}>
                    <Text style={styles.addTagCategoryTxt}>Restore</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ))}
          </>
        )}

        <Text style={[styles.sectionHeading, { marginTop: 18 }]}>Your Categories</Text>
        {customCategories.length === 0 && (
          <Text style={styles.noNotesTxt}>No custom categories yet.</Text>
        )}
        {customCategories.map((c) => (
          <View key={c} style={styles.notesCard}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text style={styles.noteText}>{c}</Text>
              <TouchableOpacity onPress={() => handleRemove(c)} activeOpacity={0.75}>
                <Text style={styles.noteDeleteTxt}>Remove</Text>
              </TouchableOpacity>
            </View>
          </View>
        ))}

        <Text style={[styles.sectionHeading, { marginTop: 18 }]}>Add a Category</Text>
        <TextInput
          style={styles.input}
          placeholder="e.g. Teeth Floated"
          placeholderTextColor="#BDBDBD"
          value={newCategory}
          onChangeText={setNewCategory}
        />
        <TouchableOpacity style={[styles.saveBtn, { marginTop: 12 }]} onPress={handleAdd} activeOpacity={0.75}>
          <Text style={styles.saveBtnText}>Add Category</Text>
        </TouchableOpacity>
      </ScrollView>
    </SafeAreaView>
  );
}

// ─── NOTE TAGS SCREEN ─────────────────────────────────────────────────────────

function NoteTagsScreen() {
  const [customNoteTags, setCustomNoteTags] = useState([]);
  const [hiddenNoteTags, setHiddenNoteTags] = useState([]);
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const unsub = subscribeToUserSettings(uid, (s) => {
      setCustomNoteTags(s.customNoteTags || []);
      setHiddenNoteTags(s.hiddenNoteTags || []);
    });
    return unsub;
  }, []);

  const handleAdd = () => {
    const trimmed = newTag.trim();
    if (!trimmed) return;
    const all = [...PRESET_NOTE_TAGS, ...customNoteTags];
    if (all.some((t) => t.toLowerCase() === trimmed.toLowerCase())) {
      Alert.alert('Already exists', `"${trimmed}" is already a tag.`);
      return;
    }
    const uid = auth.currentUser?.uid;
    addCustomNoteTag(uid, trimmed);
    setNewTag('');
  };

  const handleRemove = (tag) => {
    const uid = auth.currentUser?.uid;
    removeCustomNoteTag(uid, tag);
  };

  const handleHide = (tag) => {
    const uid = auth.currentUser?.uid;
    hidePresetNoteTag(uid, tag);
  };

  const handleRestore = (tag) => {
    const uid = auth.currentUser?.uid;
    unhidePresetNoteTag(uid, tag);
  };

  const visiblePresets = PRESET_NOTE_TAGS.filter((t) => !hiddenNoteTags.includes(t));

  return (
    <SafeAreaView style={styles.screen} edges={['bottom']}>
      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
        <Text style={styles.sectionHeading}>Built-in Tags</Text>
        {visiblePresets.length === 0 && (
          <Text style={styles.noNotesTxt}>No built-in tags shown.</Text>
        )}
        {visiblePresets.map((t) => (
          <View key={t} style={styles.notesCard}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text style={styles.noteText}>{t}</Text>
              <TouchableOpacity onPress={() => handleHide(t)} activeOpacity={0.75}>
                <Text style={styles.noteDeleteTxt}>Remove</Text>
              </TouchableOpacity>
            </View>
          </View>
        ))}

        {hiddenNoteTags.length > 0 && (
          <>
            <Text style={[styles.sectionHeading, { marginTop: 18 }]}>Hidden Built-in Tags</Text>
            {hiddenNoteTags.map((t) => (
              <View key={t} style={styles.notesCard}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text style={styles.noteText}>{t}</Text>
                  <TouchableOpacity onPress={() => handleRestore(t)} activeOpacity={0.75}>
                    <Text style={styles.addTagCategoryTxt}>Restore</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ))}
          </>
        )}

        <Text style={[styles.sectionHeading, { marginTop: 18 }]}>Your Tags</Text>
        {customNoteTags.length === 0 && (
          <Text style={styles.noNotesTxt}>No custom tags yet.</Text>
        )}
        {customNoteTags.map((t) => (
          <View key={t} style={styles.notesCard}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text style={styles.noteText}>{t}</Text>
              <TouchableOpacity onPress={() => handleRemove(t)} activeOpacity={0.75}>
                <Text style={styles.noteDeleteTxt}>Remove</Text>
              </TouchableOpacity>
            </View>
          </View>
        ))}

        <Text style={[styles.sectionHeading, { marginTop: 18 }]}>Add a Tag</Text>
        <TextInput
          style={styles.input}
          placeholder="e.g. Dental Exam"
          placeholderTextColor="#BDBDBD"
          value={newTag}
          onChangeText={setNewTag}
        />
        <TouchableOpacity style={[styles.saveBtn, { marginTop: 12 }]} onPress={handleAdd} activeOpacity={0.75}>
          <Text style={styles.saveBtnText}>Add Tag</Text>
        </TouchableOpacity>
      </ScrollView>
    </SafeAreaView>
  );
}

// ─── ADMIN SCREENS ────────────────────────────────────────────────────────────

const ADMIN_DISMISSED_SIGNUPS_KEY = 'adminDismissedSignups';

function AdminScreen({ navigation }) {
  const [showFeeRates, setShowFeeRates] = useState(false);
  const [users, setUsers] = useState([]);
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissedIds, setDismissedIds] = useState([]);
  // Find accounts: search by name/email, filter by herd size, sort.
  const [userSearch, setUserSearch] = useState('');
  const [herdSize, setHerdSize] = useState('all'); // 'all' | 'none' | 'small' | 'mid' | 'large'
  const [userSort, setUserSort] = useState('name'); // 'name' | 'most' | 'fewest' | 'newest' | 'active'
  const [activity, setActivity] = useState({}); // uid → { lastActive, activeDays }

  const loadData = () => {
    setLoading(true);
    Promise.allSettled([
      fetchAllUsers(),
      fetchAllHorses(),
      AsyncStorage.getItem(ADMIN_DISMISSED_SIGNUPS_KEY),
      fetchAllActivity(),
    ]).then(([uResult, hResult, dimResult, aResult]) => {
      if (aResult.status === 'fulfilled') setActivity(aResult.value);
      if (uResult.status === 'fulfilled') setUsers(uResult.value);
      else Alert.alert('Could not load users', uResult.reason?.message || 'Unknown error');
      if (hResult.status === 'fulfilled') setHorses(hResult.value);
      else Alert.alert('Could not load horses', hResult.reason?.message || 'Unknown error');
      setDismissedIds(dimResult.status === 'fulfilled' && dimResult.value ? JSON.parse(dimResult.value) : []);
    }).finally(() => setLoading(false));
  };

  const dismissSignup = (uid) => {
    const updated = [...dismissedIds, uid];
    setDismissedIds(updated);
    AsyncStorage.setItem(ADMIN_DISMISSED_SIGNUPS_KEY, JSON.stringify(updated));
  };

  useFocusEffect(React.useCallback(() => { loadData(); }, []));

  if (loading) {
    return (
      <SafeAreaView style={styles.screen} edges={['bottom']}>
        <ActivityIndicator size="large" color={RED} style={{ marginTop: 40 }} />
      </SafeAreaView>
    );
  }

  const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000);
  const recentSignups = [...users]
    .filter((u) => {
      if (!u.createdAt || dismissedIds.includes(u.id)) return false;
      const created = u.createdAt?.toDate ? u.createdAt.toDate() : new Date(u.createdAt);
      return created > cutoff;
    })
    .sort((a, b) => {
      const ta = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const tb = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return tb - ta;
    })
    .slice(0, 5);

  // Accounts list after search / herd-size filter / sort.
  const horseCount = {};
  users.forEach((u) => { horseCount[u.id] = horses.filter((h) => (h.members || []).includes(u.id)).length; });
  const userLabel = (u) => (u.displayName || u.email || u.id || '').toLowerCase();
  const createdMs = (u) => (u.createdAt?.toDate ? u.createdAt.toDate().getTime() : (u.createdAt ? new Date(u.createdAt).getTime() : 0));
  // Activity: last opened (falls back to last sign-in from before tracking) and
  // how many of the last 30 days they used the app.
  const tsMs = (t) => (t?.toDate ? t.toDate().getTime() : (t ? new Date(t).getTime() : 0));
  const lastMs = (u) => tsMs(activity[u.id]?.lastActive) || tsMs(u.lastLogin);
  const ymdLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const day30 = ymdLocal(new Date(Date.now() - 29 * 86400000));
  const days30 = (u) => (activity[u.id]?.activeDays || []).filter((d) => d >= day30).length;
  const nowMs = Date.now();
  const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
  const activeToday = users.filter((u) => tsMs(activity[u.id]?.lastActive) >= startOfToday.getTime()).length;
  const activeWeek = users.filter((u) => nowMs - tsMs(activity[u.id]?.lastActive) < 7 * 86400000).length;
  const activeMonth = users.filter((u) => nowMs - tsMs(activity[u.id]?.lastActive) < 30 * 86400000).length;
  const ago = (ms) => {
    if (!ms) return 'never';
    const m = Math.floor((nowMs - ms) / 60000);
    if (m < 2) return 'just now';
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
    const dd = Math.floor(h / 24);
    if (dd < 60) return `${dd} day${dd === 1 ? '' : 's'} ago`;
    return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const userQuery = userSearch.trim().toLowerCase();
  const inHerdSize = (n) => herdSize === 'all' || (herdSize === 'none' && n === 0) || (herdSize === 'small' && n >= 1 && n <= 5)
    || (herdSize === 'mid' && n >= 6 && n <= 20) || (herdSize === 'large' && n >= 21);
  const shownUsers = users
    .filter((u) => inHerdSize(horseCount[u.id]) && (!userQuery || [u.displayName, u.email, u.shareEmail].some((f) => f && String(f).toLowerCase().includes(userQuery))))
    .sort((a, b) => {
      const byName = userLabel(a).localeCompare(userLabel(b));
      if (userSort === 'most') return horseCount[b.id] - horseCount[a.id] || byName;
      if (userSort === 'fewest') return horseCount[a.id] - horseCount[b.id] || byName;
      if (userSort === 'newest') return createdMs(b) - createdMs(a);
      if (userSort === 'active') return lastMs(b) - lastMs(a) || byName;
      return byName;
    });

  const formatSignupDate = (ts) => {
    if (!ts) return '';
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <SafeAreaView style={styles.screen} edges={['bottom']}>
      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
        {canSeeOnlinePayments() && (
          <TouchableOpacity
            onPress={() => Alert.alert('Clear sandbox test payments?', 'Removes the Stripe sandbox test records: test provider connections, test pay links, and the test "Paid online", refund and dispute entries in ledgers. Charges that were paid by those test payments are moved to Recently deleted (restorable). Real (live) payments are never touched. Any other test charges stay — delete those by hand if you want.', [
              { text: 'Cancel', style: 'cancel' },
              { text: 'Clear', style: 'destructive', onPress: () => callAdminClearSandboxPayments()
                .then((r) => Alert.alert('Cleared', `${r.accounts} test connection${r.accounts === 1 ? '' : 's'}, ${r.links} pay link${r.links === 1 ? '' : 's'}, ${r.ledgerEntries} test payment entr${r.ledgerEntries === 1 ? 'y' : 'ies'} and ${r.charges || 0} test charge${r.charges === 1 ? '' : 's'} cleared.`))
                .catch((e) => Alert.alert('Not cleared', e?.message || 'Try again.')) },
            ])}
            activeOpacity={0.75}
            style={[styles.sectionCard, { marginBottom: 12 }]}
          >
            <Text style={styles.sectionCardIcon}>🧹</Text>
            <View style={{ flex: 1 }}>
              <Text style={styles.sectionCardTitle}>Clear sandbox test payments</Text>
              <Text style={styles.sectionCardSub}>One-time clean-up after switching Stripe to live</Text>
            </View>
            <Text style={styles.sectionCardChevron}>›</Text>
          </TouchableOpacity>
        )}
        {canSeeOnlinePayments() && (
          <TouchableOpacity
            onPress={() => Alert.alert('Announce online payments?', 'Sends a one-time push to every provider who hasn\'t set up online payments yet. Each provider only ever gets it once.', [
              { text: 'Cancel', style: 'cancel' },
              { text: 'Send', onPress: () => callAnnounceOnlinePayments()
                .then((r) => Alert.alert('Sent', `${r.sent} provider${r.sent === 1 ? '' : 's'} notified (${r.skipped} skipped: already told, already set up, or notifications off).`))
                .catch((e) => Alert.alert('Not sent', e?.message || 'Try again.')) },
            ])}
            activeOpacity={0.75}
            style={[styles.sectionCard, { marginBottom: 12 }]}
          >
            <Text style={styles.sectionCardIcon}>📣</Text>
            <View style={{ flex: 1 }}>
              <Text style={styles.sectionCardTitle}>Announce online payments</Text>
              <Text style={styles.sectionCardSub}>One-time push to providers (after launch)</Text>
            </View>
            <Text style={styles.sectionCardChevron}>›</Text>
          </TouchableOpacity>
        )}
        {canSeeOnlinePayments() && (
          <TouchableOpacity onPress={() => setShowFeeRates(true)} activeOpacity={0.75} style={[styles.sectionCard, { marginBottom: 12 }]}>
            <Text style={styles.sectionCardIcon}>💳</Text>
            <View style={{ flex: 1 }}>
              <Text style={styles.sectionCardTitle}>Provider fee rates</Text>
              <Text style={styles.sectionCardSub}>Lower the Horse Math fee for early adopters</Text>
            </View>
            <Text style={styles.sectionCardChevron}>›</Text>
          </TouchableOpacity>
        )}
        <FeeRatesModal visible={showFeeRates} onClose={() => setShowFeeRates(false)} />
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text style={styles.sectionHeading}>Users ({users.length})</Text>
          <TouchableOpacity onPress={loadData} activeOpacity={0.75}>
            <Text style={styles.addTagCategoryTxt}>↻ Refresh</Text>
          </TouchableOpacity>
        </View>

        {recentSignups.length > 0 && (
          <>
            <Text style={[styles.sectionHeading, { marginTop: 4, marginBottom: 8 }]}>Recent Signups</Text>
            {recentSignups.map((u) => (
              <View key={u.id} style={[styles.notesCard, { marginBottom: 8 }]}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  <Text style={{ fontSize: 20 }}>🆕</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.noteText, { marginTop: 0, fontWeight: '700' }]}>{u.displayName || u.email || u.id}</Text>
                    <Text style={styles.noteDate}>{formatSignupDate(u.createdAt)}</Text>
                  </View>
                  <TouchableOpacity
                    onPress={() => Alert.alert('Dismiss', `What would you like to do with ${u.email || u.id}?`, [
                      { text: 'Cancel', style: 'cancel' },
                      { text: 'Dismiss', onPress: () => dismissSignup(u.id) },
                      { text: 'Delete User', style: 'destructive', onPress: () => deleteUserProfile(u.id).then(loadData) },
                    ])}
                    activeOpacity={0.75}
                  >
                    <Text style={{ color: MUTED, fontSize: 13, fontWeight: '600' }}>Dismiss</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ))}
            <View style={styles.divider} />
          </>
        )}

        {users.length === 0 && <Text style={styles.noNotesTxt}>No users found.</Text>}
        {users.length > 0 && (() => {
          const chip = (active, label, onPress) => (
            <TouchableOpacity key={label} onPress={onPress} activeOpacity={0.75} style={[styles.categoryChip, active && styles.categoryChipActive]}>
              <Text style={[styles.categoryChipTxt, active && styles.categoryChipTxtActive]}>{label}</Text>
            </TouchableOpacity>
          );
          return (
            <View style={{ marginBottom: 10 }}>
              <TextInput
                style={[styles.input, { marginBottom: 8 }]}
                placeholder="Search by name or email"
                placeholderTextColor="#BDBDBD"
                value={userSearch}
                onChangeText={setUserSearch}
                autoCapitalize="none"
              />
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                <Text style={{ fontSize: 12, color: MUTED }}>Horses:</Text>
                {[['all', 'All'], ['none', '0'], ['small', '1–5'], ['mid', '6–20'], ['large', '21+']].map(([k, l]) => chip(herdSize === k, l, () => setHerdSize(k)))}
              </View>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                <Text style={{ fontSize: 12, color: MUTED }}>Sort:</Text>
                {[['name', 'Name'], ['active', 'Recently active'], ['most', 'Most horses'], ['fewest', 'Fewest'], ['newest', 'Newest']].map(([k, l]) => chip(userSort === k, l, () => setUserSort(k)))}
              </View>
            </View>
          );
        })()}
        {users.length > 0 && (
          <View style={{ flexDirection: 'row', gap: 8, marginBottom: 10 }}>
            {[['Active today', activeToday], ['This week', activeWeek], ['This month', activeMonth]].map(([label, n]) => (
              <View key={label} style={[styles.statCard, { flex: 1 }]}>
                <Text style={[styles.statNumber, { color: GREEN }]}>{n}</Text>
                <Text style={styles.statLabel}>{label}</Text>
              </View>
            ))}
          </View>
        )}
        {(userQuery || herdSize !== 'all') && (
          <Text style={{ fontSize: 12, color: MUTED, marginBottom: 6 }}>Showing {shownUsers.length} of {users.length}</Text>
        )}
        {shownUsers.length === 0 && users.length > 0 && <Text style={styles.noNotesTxt}>No accounts match.</Text>}
        {shownUsers
          .map((u) => {
          const userHorses = horses.filter((h) => (h.members || []).includes(u.id));
          return (
            <TouchableOpacity
              key={u.id}
              style={styles.sectionCard}
              onPress={() => navigation.navigate('AdminUserHorses', { user: u, horses: userHorses })}
              activeOpacity={0.75}
            >
              <Text style={styles.sectionCardIcon}>👤</Text>
              <View style={{ flex: 1 }}>
                <Text style={styles.sectionCardTitle}>{u.displayName || u.email || u.id}</Text>
                <Text style={styles.sectionCardSub}>
                  {u.email ? `${u.email} · ` : ''}
                  {userHorses.length} horse{userHorses.length === 1 ? '' : 's'}
                </Text>
                <Text style={[styles.sectionCardSub, { marginTop: 2 }]}>
                  Last opened {ago(lastMs(u))}{activity[u.id] ? ` · active ${days30(u)} of last 30 days` : ''}
                </Text>
              </View>
              <Text style={styles.sectionCardChevron}>›</Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </SafeAreaView>
  );
}

function AdminUserHorsesScreen({ route, navigation }) {
  const { user, horses } = route.params;

  return (
    <SafeAreaView style={styles.screen} edges={['bottom']}>
      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
        <Text style={styles.dashSub}>{user.email || user.id}</Text>
        {horses.length === 0 && <Text style={styles.noNotesTxt}>No horses.</Text>}
        {horses.map((h) => (
          <TouchableOpacity
            key={h.id}
            style={styles.notesCard}
            onPress={() => navigation.navigate('Horse', { horseId: h.id })}
            activeOpacity={0.75}
          >
            <Text style={styles.noteText}>{displayName(h)}</Text>
            <Text style={styles.notePreview}>{(h.status || []).join(', ') || 'No status'}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </SafeAreaView>
  );
}

// ─── HOME SCREEN ──────────────────────────────────────────────────────────────

// sort options: 'name_asc' | 'name_desc' | 'date_asc' | 'date_desc'
const SORT_OPTIONS = [
  { key: 'name_asc',  label: 'Name A→Z' },
  { key: 'name_desc', label: 'Name Z→A' },
  { key: 'date_desc', label: 'Date (Newest)' },
  { key: 'date_asc',  label: 'Date (Oldest)' },
  { key: 'status',    label: 'Status' },
  { key: 'location',  label: 'Location' },
];

const sortHorses = (list, sortBy) => {
  return [...list].sort((a, b) => {
    if (sortBy === 'name_asc' || sortBy === 'status' || sortBy === 'location')
      return displayName(a).localeCompare(displayName(b));
    if (sortBy === 'name_desc') return displayName(b).localeCompare(displayName(a));
    const dateA = new Date((a.breedDate || a.dateOfBirth) + 'T12:00:00');
    const dateB = new Date((b.breedDate || b.dateOfBirth) + 'T12:00:00');
    if (sortBy === 'date_desc') return dateB - dateA;
    if (sortBy === 'date_asc')  return dateA - dateB;
    return 0;
  });
};

// Secondary sort used within each status group: 'age' (oldest first) or 'name'
const STATUS_SUBSORT_OPTIONS = [
  { key: 'name', label: 'Name' },
  { key: 'age',  label: 'Age' },
];

const sortByAgeOrName = (list, subSort, subSortDir) => {
  const dir = subSortDir === 'desc' ? -1 : 1;
  return [...list].sort((a, b) => {
    if (subSort === 'name') return displayName(a).localeCompare(displayName(b)) * dir;
    const dateA = new Date((a.dateOfBirth || a.breedDate) + 'T12:00:00');
    const dateB = new Date((b.dateOfBirth || b.breedDate) + 'T12:00:00');
    return (dateA - dateB) * dir;
  });
};

const ADD_HORSE_DRAFT_KEY = 'addHorseDraft';
const ADD_HORSE_DRAFT_TTL = 30 * 60 * 1000; // 30 minutes
const ADD_TASK_DRAFT_KEY = 'addTaskDraft';
const ADD_EVENT_DRAFT_KEY = 'addEventDraft';

// Preset incident tags for the Health & Lameness section (users can add more).
const INCIDENT_TAG_PRESETS = ['Injury', 'Lameness', 'Colic', 'Sickness'];
const INCIDENT_LOG_TYPES = ['Note', 'Vet Visit', 'Treatment'];

// Fixed colors for preset types; custom types get a stable auto-assigned color.
const INCIDENT_TYPE_COLORS = {
  Lameness: { fill: '#EF9F27', bg: '#FAEEDA', txt: '#854F0B' },
  Injury: { fill: '#E24B4A', bg: '#FCEBEB', txt: '#A32D2D' },
  Colic: { fill: '#7F77DD', bg: '#EEEDFE', txt: '#3C3489' },
  Sickness: { fill: '#378ADD', bg: '#E6F1FB', txt: '#0C447C' },
};
const INCIDENT_CUSTOM_PALETTE = [
  { fill: '#1D9E75', bg: '#E1F5EE', txt: '#0F6E56' }, // teal
  { fill: '#D4537E', bg: '#FBEAF0', txt: '#993556' }, // pink
  { fill: '#D85A30', bg: '#FAECE7', txt: '#993C1D' }, // coral
  { fill: '#639922', bg: '#EAF3DE', txt: '#3B6D11' }, // green
  { fill: '#888780', bg: '#F1EFE8', txt: '#444441' }, // gray
];
// Default collapsed/expanded layout for a horse profile's sections (reference /
// heavy sections start folded). Used until the user's saved per-horse layout loads.
const DEFAULT_SECTION_LAYOUT = {
  pedigree: true, family: true, earnings: true, incentive: true, contact: true, trainingNotes: true, ledger: true, docs: true,
};

const NEUTRAL_TYPE_COLOR = { fill: '#9E9E9E', bg: '#F1EFE8', txt: '#444441' };
const incidentTypeColor = (type) => {
  if (!type) return NEUTRAL_TYPE_COLOR;
  if (INCIDENT_TYPE_COLORS[type]) return INCIDENT_TYPE_COLORS[type];
  let h = 0;
  for (let i = 0; i < type.length; i++) h = (h * 31 + type.charCodeAt(i)) >>> 0;
  return INCIDENT_CUSTOM_PALETTE[h % INCIDENT_CUSTOM_PALETTE.length];
};
// The color used to represent an incident (by its first/primary tag).
const incidentColor = (incident) => incidentTypeColor((incident.tags || [])[0]);

function HerdScreen({ navigation, route }) {
  const guide = useGuide();
  const addScrollRef = useRef(null);
  const clientToggleY = useRef(0);
  const [horses, setHorses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('born'); // 'embryo' | 'born' | 'frozen_embryo'
  // Starts empty: the user must pick the date of birth / breed date
  // themselves — a pre-filled "today" was being saved unnoticed.
  const [newDate, setNewDate] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [newIsRecip, setNewIsRecip] = useState(false);
  // Frozen-embryo batch fields (only used when newType === 'frozen_embryo')
  const [newFrozenMethod, setNewFrozenMethod] = useState('ICSI'); // 'ICSI' | 'Flush'
  const [newFrozenLocation, setNewFrozenLocation] = useState('');
  const [newFrozenCount, setNewFrozenCount] = useState('');
  const [newFirstFrozen, setNewFirstFrozen] = useState(new Date());
  const [newLastFrozen, setNewLastFrozen] = useState(new Date());
  const [showFirstFrozenPicker, setShowFirstFrozenPicker] = useState(false);
  const [showLastFrozenPicker, setShowLastFrozenPicker] = useState(false);
  const [newDetails, setNewDetails] = useState(emptyHorseDetails());
  const [newIsClientHorse, setNewIsClientHorse] = useState(false);
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [clientPickerSearch, setClientPickerSearch] = useState('');
  const [newProfilePhoto, setNewProfilePhoto] = useState(null); // picked-but-unsaved profile photo
  const [newProfileFocus, setNewProfileFocus] = useState({ x: 0.5, y: 0.5, zoom: 1 });
  const [newClientOwnerName, setNewClientOwnerName] = useState('');
  const [newClientOwnerEmail, setNewClientOwnerEmail] = useState('');
  const [newClientOwnerPhone, setNewClientOwnerPhone] = useState('');
  const [pickerFor, setPickerFor] = useState(null); // 'sire' | 'dam' | null
  // Saved stallions, offered when picking a sire.
  const [studs, setStuds] = useState([]);
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    return subscribeToStuds(uid, setStuds);
  }, []);
  const [pickerSearch, setPickerSearch] = useState('');
  const [sortBy, setSortBy] = useState('name_asc');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showFrozenSection, setShowFrozenSection] = useState(false); // pin frozen batches to the top of the herd
  const [searchQuery, setSearchQuery] = useState('');
  const [compact, setCompact] = useState(false); // one line per horse instead of a card
  const densityLoaded = useRef(false);
  useEffect(() => {
    AsyncStorage.getItem('herdCompact').then((v) => { if (v === '1') setCompact(true); }).catch(() => {}).finally(() => { densityLoaded.current = true; });
  }, []);
  useEffect(() => { if (densityLoaded.current) AsyncStorage.setItem('herdCompact', compact ? '1' : '0').catch(() => {}); }, [compact]);
  // Keep personal and client horses apart: client horses are hidden from the
  // herd unless switched on (remembered). They always have their own section.
  const [showClients, setShowClients] = useState(false);
  const showClientsLoaded = useRef(false);
  useEffect(() => {
    AsyncStorage.getItem('herdShowClients').then((v) => { if (v === '1') setShowClients(true); }).catch(() => {}).finally(() => { showClientsLoaded.current = true; });
  }, []);
  useEffect(() => { if (showClientsLoaded.current) AsyncStorage.setItem('herdShowClients', showClients ? '1' : '0').catch(() => {}); }, [showClients]);
  const [statusSubSort, setStatusSubSort] = useState('name');
  const [statusSubSortDir, setStatusSubSortDir] = useState('asc');
  const [customLocations, setCustomLocations] = useState([]);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [newLocationText, setNewLocationText] = useState('');

  // Load the last-used sort option, then persist changes
  useEffect(() => {
    AsyncStorage.getItem('herdSortBy').then((v) => { if (v) setSortBy(v); });
    AsyncStorage.getItem('herdStatusSubSort').then((v) => { if (v) setStatusSubSort(v); });
    AsyncStorage.getItem('herdStatusSubSortDir').then((v) => { if (v) setStatusSubSortDir(v); });
  }, []);
  useEffect(() => {
    AsyncStorage.setItem('herdSortBy', sortBy);
  }, [sortBy]);
  useEffect(() => {
    AsyncStorage.setItem('herdStatusSubSort', statusSubSort);
  }, [statusSubSort]);
  useEffect(() => {
    AsyncStorage.setItem('herdStatusSubSortDir', statusSubSortDir);
  }, [statusSubSortDir]);

  const handleStatusSubSortPress = (key) => {
    if (statusSubSort === key) {
      setStatusSubSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setStatusSubSort(key);
      setStatusSubSortDir('asc');
    }
  };

  // Live subscription to this user's horses. Sold horses come in the same
  // snapshot but live apart, in the Sold Horses archive at the bottom.
  const [soldHorses, setSoldHorses] = useState([]);
  const [showSold, setShowSold] = useState(false);
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const unsub = subscribeToHorses(uid, (all) => {
      setHorses(all.filter((h) => !h.sold));
      setSoldHorses(all.filter((h) => h.sold).sort((a, b) => (b.soldDate || '').localeCompare(a.soldDate || '')));
    }, { includeSold: true });
    return unsub;
  }, []);

  // Live subscription to user locations
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const unsub = subscribeToUserSettings(uid, (s) => {
      setCustomLocations(s.customLocations || []);
    });
    return unsub;
  }, []);

  // Restore draft form if user left and came back within 30 minutes
  useEffect(() => {
    AsyncStorage.getItem(ADD_HORSE_DRAFT_KEY).then((raw) => {
      if (!raw) return;
      try {
        const draft = JSON.parse(raw);
        if (Date.now() - draft.savedAt > ADD_HORSE_DRAFT_TTL) {
          AsyncStorage.removeItem(ADD_HORSE_DRAFT_KEY);
          return;
        }
        setNewName(draft.newName || '');
        setNewType(draft.newType || 'born');
        setNewDate(draft.newDate ? new Date(draft.newDate) : null);
        setNewIsRecip(draft.newIsRecip || false);
        setNewDetails(draft.newDetails || emptyHorseDetails());
        setShowModal(true);
      } catch {
        AsyncStorage.removeItem(ADD_HORSE_DRAFT_KEY);
      }
    });
  }, []);

  // Save draft to AsyncStorage whenever modal is open and form has content
  useEffect(() => {
    if (!showModal) return;
    const hasContent = newName.trim() || newDetails.registeredName?.trim() ||
      newDetails.sire?.trim() || newDetails.dam?.trim() || newDetails.color?.trim();
    if (!hasContent) return;
    AsyncStorage.setItem(ADD_HORSE_DRAFT_KEY, JSON.stringify({
      savedAt: Date.now(),
      newName, newType, newDate: newDate ? newDate.toISOString() : null, newIsRecip, newDetails,
    }));
  }, [showModal, newName, newType, newDate, newIsRecip, newDetails]);

  // Auto-open the Add Horse modal when navigated here from the Dashboard
  useEffect(() => {
    if (route.params?.openAdd) {
      openModal(route.params?.clientHorse);
      navigation.setParams({ openAdd: undefined, clientHorse: undefined });
    }
  }, [route.params?.openAdd]);

  const clearDraft = () => AsyncStorage.removeItem(ADD_HORSE_DRAFT_KEY);

  const openModal = (asClientHorse = false) => {
    setNewName('');
    setNewType('born');
    setNewDate(null);
    setShowPicker(false);
    setNewIsRecip(false);
    setNewFrozenMethod('ICSI');
    setNewFrozenLocation('');
    setNewFrozenCount('');
    setNewFirstFrozen(new Date());
    setNewLastFrozen(new Date());
    setShowFirstFrozenPicker(false);
    setShowLastFrozenPicker(false);
    setNewDetails(emptyHorseDetails());
    setNewProfilePhoto(null);
    setNewProfileFocus({ x: 0.5, y: 0.5, zoom: 1 });
    // Strictly true, never truthy: a press event handed in as the first
    // argument must not switch this on.
    // Walkthrough: opening Add Horse is step 2; people who said they care for
    // clients' horses get the client-horse switch already on.
    const guiding = guideState.stage === 'add' || guideState.stage === 'switch';
    if (guideState.stage === 'add') setGuideStage('switch');
    setNewIsClientHorse(asClientHorse === true || (guiding && guideState.use === 'clients'));
    setNewClientOwnerName('');
    setNewClientOwnerEmail('');
    setNewClientOwnerPhone('');
    setShowModal(true);
  };

  // Clients already on file, taken from the horses this user trains. Picking
  // one fills the contact fields instead of retyping them — and, just as
  // important, keeps the name spelled identically, since the name is what
  // groups a client's horses together and drives billing them as one.
  const knownClients = (() => {
    const uid = auth.currentUser?.uid;
    const seen = new Map();
    horses.filter((h) => isHorseTrainer(h, uid)).forEach((h) => {
      const c = getOwnerContact(h);
      const name = (c?.name || h.ownerName || '').trim();
      if (!name) return;
      if (seen.has(name)) { seen.get(name).horseCount += 1; return; }
      seen.set(name, {
        name,
        email: (c?.email || h.ownerEmail || '').trim(),
        phone: (c?.phone || '').trim(),
        horseCount: 1,
      });
    });
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  })();

  const handleAdd = async () => {
    const isFrozen = newType === 'frozen_embryo';
    if (isFrozen) {
      if (!newDetails.sire.trim() && !newDetails.dam.trim()) {
        Alert.alert('Pedigree needed', 'Enter at least a sire or dam so this batch can be identified.');
        return;
      }
      if (!(parseInt(newFrozenCount, 10) >= 1)) {
        Alert.alert('Count needed', 'Enter how many embryos are frozen (1 or more).');
        return;
      }
    } else if (!newName.trim() && !newDetails.registeredName.trim()) {
      Alert.alert('Name required', 'Please enter a horse name or registered name.');
      return;
    }
    if (!isFrozen && !newDate) {
      Alert.alert(
        newType === 'born' ? 'Date of birth required' : 'Breed date required',
        newType === 'born' ? "Please select the horse's date of birth." : 'Please select the breed date.',
      );
      return;
    }
    const dateStr = isFrozen ? '' : localYmd(newDate);
    const horseBase = isFrozen
      ? {
          ...createFrozenEmbryo({
            method: newFrozenMethod,
            location: newFrozenLocation,
            count: newFrozenCount,
            firstFrozen: newFirstFrozen.toISOString().split('T')[0],
            lastFrozen: newLastFrozen.toISOString().split('T')[0],
          }),
          name: frozenLabel({ sire: newDetails.sire, dam: newDetails.dam }),
        }
      : newType === 'born'
        ? createBornHorse(newName, dateStr)
        : createEmbryoHorse(newName, dateStr, newIsRecip);

    // If tagged as a client horse, the creator (trainer) also acts as the
    // horse's trainer, so it appears in Client Horses with full trainer features.
    let clientFields = {};
    if (newIsClientHorse) {
      const u = auth.currentUser;
      clientFields = {
        trainers: [{ uid: u.uid, email: u.email || '', name: u.displayName || u.email || '' }],
        ownerContact: {
          name: newClientOwnerName.trim(),
          email: newClientOwnerEmail.trim(),
          phone: newClientOwnerPhone.trim(),
        },
      };
    }

    // Walkthrough: mark the horse placed BEFORE saving, so Home (which hears
    // about the new horse instantly) shows step 3 rather than ending the tips.
    const wasGuiding = guideState.stage === 'switch' || guideState.stage === 'add';
    if (wasGuiding) setGuideStage('placed', { kind: newIsClientHorse ? 'client' : 'own', horseName: (newName.trim() || newDetails.registeredName.trim() || '') });

    try {
      const owner = auth.currentUser;
      const horseId = await addHorse(owner.uid, {
        ...horseBase, ...newDetails, ...clientFields,
        ownerEmail: owner.email || '', ownerName: owner.displayName || '',
      });
      // Upload the profile photo after the horse exists (non-fatal if it fails).
      if (newProfilePhoto) {
        try {
          const meta = await uploadHorseFileAsync(horseId, newProfilePhoto.payload, { kind: 'profile', name: newProfilePhoto.name, mime: newProfilePhoto.mime });
          await updateHorse(horseId, { imageUrl: meta.url, imagePath: meta.path, imageFocus: newProfileFocus });
        } catch {
          Alert.alert('Photo upload failed', 'The horse was saved, but the photo didn’t upload. You can add it later from Edit Details.');
        }
      }
      setNewProfilePhoto(null);
      clearDraft();
      setShowModal(false);
      if (wasGuiding) navigation.navigate('Home'); // step 3: where the horse went
      else if (isFrozen) setShowFrozenSection(true); // reveal the frozen section at the top of the herd
      else navigation.navigate('Horse', { horseId });
    } catch (e) {
      if (wasGuiding) setGuideStage('switch');
      Alert.alert('Save Failed', 'Could not save the horse. Please check your connection and try again.');
    }
  };

  const toggleNewStatus = (option) => {
    setNewDetails((d) => ({ ...d, status: toggleStatusOption(d.status, option) }));
  };

  const toggleNewBroodmare = () => {
    setNewDetails((d) => ({ ...d, status: toggleBroodmare(d.status) }));
  };

  const toggleNewRecip = () => {
    setNewDetails((d) => ({ ...d, status: toggleRecipStatus(d.status) }));
  };

  const toggleNewFoalSex = (sex) => {
    setNewDetails((d) => ({ ...d, status: toggleFoalSex(d.status, sex) }));
  };

  const openPicker = (forField) => {
    setPickerSearch('');
    setPickerFor(forField);
  };

  // A saved stud is not a horse in your stable, so there is no sireId to link —
  // just his name and the two generations already stored against him.
  const pickStudAsSire = (st) => {
    setNewDetails((d) => ({
      ...d,
      sire: st.name || '',
      sireId: null,
      sireSire: st.sire || '',
      sireDam: st.dam || '',
    }));
    setPickerFor(null);
  };

  const pickFromStable = (picked) => {
    if (pickerFor === 'sire') {
      setNewDetails((d) => ({
        ...d,
        sire: stableDisplayName(picked),
        sireId: picked.id,
        sireSire: picked.sire || '',
        sireDam: picked.dam || '',
      }));
    } else if (pickerFor === 'dam') {
      setNewDetails((d) => ({
        ...d,
        dam: stableDisplayName(picked),
        damId: picked.id,
        damSire: picked.sire || '',
        damDam: picked.dam || '',
      }));
    } else if (pickerFor === 'recip') {
      setNewDetails((d) => ({
        ...d,
        recipMare: stableDisplayName(picked),
        recipMareId: picked.id,
      }));
    }
    setPickerFor(null);
  };

  const renderHorseCard = (item) => {
    // ── One-line row ──
    // The name, and the one fact that screen is for: how old a born horse is,
    // or when an embryo's foal is due. Tags shrink to a single trailing chip.
    if (compact) {
      const born = item.type === 'born';
      const dueDate = !born && item.breedDate ? addDays(item.breedDate, 340) : null;
      const right = born
        ? formatAgeShort(getQuarterHorseAge(item.dateOfBirth))
        : dueDate ? `Due ${formatDate(dueDate)}` : '—';
      // In Training and In Recip are facts about where the horse IS, so they
      // outrank a status label for the single chip a row has room for.
      const chips = [
        ...(horseHasTrainer(item) ? ['In Training'] : []),
        ...(item.isRecip ? ['In Recip'] : []),
        ...(item.status || []),
      ];
      const tag = chips[0] || null;
      const more = Math.max(0, chips.length - 1);
      return (
        <TouchableOpacity
          key={item.id}
          style={styles.denseRow}
          onPress={() => navigation.navigate('Horse', { horseId: item.id })}
          activeOpacity={0.7}
        >
          <View style={{ flex: 1 }}>
            <Text style={styles.denseName} numberOfLines={1}>{displayName(item)}</Text>
            {!born && !!dueDate && <Text style={styles.denseSub} numberOfLines={1}>{getDaysLabel(dueDate)}</Text>}
            {born && ((item.openHealthCount || 0) > 0 || !!item.stall) && (
              <Text style={styles.denseSub} numberOfLines={1}>
                {[item.stall ? `Stall ${item.stall}` : '', (item.openHealthCount || 0) > 0 ? `🩺 ${item.openHealthCount} ongoing` : ''].filter(Boolean).join('  ·  ')}
              </Text>
            )}
          </View>
          {!!tag && (
            <View style={styles.denseTag}>
              <Text style={styles.denseTagTxt} numberOfLines={1}>{tag}{more > 0 ? ' +' + more : ''}</Text>
            </View>
          )}
          <Text style={[styles.denseRight, born && { color: GREEN }]} numberOfLines={1}>{right}</Text>
          <Text style={styles.denseChevron}>›</Text>
        </TouchableOpacity>
      );
    }

    // ── Already Born card ──
    if (item.type === 'born') {
      const age = getQuarterHorseAge(item.dateOfBirth);
      return (
        <TouchableOpacity
          key={item.id}
          style={styles.horseCard}
          onPress={() => navigation.navigate('Horse', { horseId: item.id })}
          activeOpacity={0.75}
        >
          <View style={styles.horseCardTop}>
            <View style={{ flex: 1 }}>
              <Text style={styles.horseName}>{displayName(item)}</Text>
              {(item.openHealthCount || 0) > 0 && (
                <Text style={styles.horseHealthBadge}>🩺 {item.openHealthCount} ongoing health</Text>
              )}
              {item.name && item.registeredName && item.registeredName !== item.name && (
                <Text style={styles.horseRegisteredName}>{item.registeredName}</Text>
              )}
              {!!sharedByLine(item, auth.currentUser?.uid) && (
                <Text style={styles.horseSharedTag}>{sharedByLine(item, auth.currentUser?.uid)}</Text>
              )}
              <Text style={styles.horseSubtext}>Born {formatDate(item.dateOfBirth)}</Text>
              <Text style={[styles.horseNext, { color: GREEN }]}>Age: {formatAgeShort(age)}</Text>
              {(item.location || item.stall) ? <Text style={styles.horseSubtext}>📍 {[item.location, item.stall ? `Stall ${item.stall}` : ''].filter(Boolean).join(' · ')}</Text> : null}
            </View>
            {((item.status && item.status.length > 0) || horseHasTrainer(item)) && (
              <View style={[styles.cardTagRow, styles.cardTagRowBadge]}>
                {item.status && item.status.map((s) => (
                  <View key={s} style={styles.cardTag}>
                    <Text style={styles.cardTagTxt}>{s}</Text>
                  </View>
                ))}
                {horseHasTrainer(item) && (
                  <View style={[styles.cardTag, { backgroundColor: '#1D9E7518', borderColor: '#1D9E75' }]}>
                    <Text style={[styles.cardTagTxt, { color: '#0F6E56' }]}>In Training</Text>
                  </View>
                )}
              </View>
            )}
          </View>
        </TouchableOpacity>
      );
    }

    // ── Embryo / In Foal card ──
    const milestones = item.milestones || [];
    const done = milestones.filter((m) => m.completed).length;
    const total = milestones.length;
    const allDone = done === total;
    const next = milestones.find((m) => !m.completed);
    // Must go through getMilestoneDate: a custom task carries an absolute
    // `date` and no `daysOffset`, and addDays(breedDate, undefined) throws
    // RangeError, which takes the whole herd list down with it.
    const nextDate = next ? getMilestoneDate(item, next) : null;

    return (
      <TouchableOpacity
        key={item.id}
        style={styles.horseCard}
        onPress={() => navigation.navigate('Horse', { horseId: item.id })}
        activeOpacity={0.75}
      >
        {((item.status && item.status.length > 0) || item.isRecip || horseHasTrainer(item)) && (
          <View style={styles.cardTagRow}>
            {item.status && item.status.map((s) => (
              <View key={s} style={styles.cardTag}>
                <Text style={styles.cardTagTxt}>{s}</Text>
              </View>
            ))}
            {item.isRecip && (
              <View style={[styles.cardTag, { backgroundColor: '#7B4FBF18', borderColor: '#7B4FBF' }]}>
                <Text style={[styles.cardTagTxt, { color: '#7B4FBF' }]}>In Recip</Text>
              </View>
            )}
            {horseHasTrainer(item) && (
              <View style={[styles.cardTag, { backgroundColor: '#1D9E7518', borderColor: '#1D9E75' }]}>
                <Text style={[styles.cardTagTxt, { color: '#0F6E56' }]}>In Training</Text>
              </View>
            )}
          </View>
        )}
        <View style={styles.horseCardTop}>
          <View style={{ flex: 1 }}>
            <Text style={styles.horseName}>{displayName(item)}</Text>
            {item.name && item.registeredName && item.registeredName !== item.name && (
              <Text style={styles.horseRegisteredName}>{item.registeredName}</Text>
            )}
            {!!sharedByLine(item, auth.currentUser?.uid) && (
              <Text style={styles.horseSharedTag}>{sharedByLine(item, auth.currentUser?.uid)}</Text>
            )}
            <Text style={styles.horseSubtext}>
              Bred {formatDate(item.breedDate)}
              {item.breedDate ? ` · Due ${formatDate(addDays(item.breedDate, 340))}` : ''}
            </Text>
            {(item.location || item.stall) ? <Text style={styles.horseSubtext}>📍 {[item.location, item.stall ? `Stall ${item.stall}` : ''].filter(Boolean).join(' · ')}</Text> : null}
            {allDone ? (
              <Text style={styles.horseAllDone}>All milestones complete ✓</Text>
            ) : (
              <Text style={styles.horseNext}>Next: {next.name} · {getDaysLabel(nextDate)}</Text>
            )}
          </View>
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{done}/{total}</Text>
          </View>
        </View>
        <View style={styles.barBg}>
          <View style={[styles.barFill, { width: `${(done / total) * 100}%` }]} />
        </View>
      </TouchableOpacity>
    );
  };

  // Apply search filter across name, registered name, sire, dam, color, and location
  const q = searchQuery.trim().toLowerCase();
  const matchesSearch = (h) => {
    if (!q) return true;
    const fields = [
      displayName(h), h.name, h.registeredName, h.registryNumber,
      h.sire, h.dam, h.color, h.location, h.breed, h.stall ? `stall ${h.stall}` : '', h.stall,
    ];
    return fields.some((f) => f && String(f).toLowerCase().includes(q));
  };
  // Frozen-embryo batches are excluded from the main herd groups and shown in
  // their own section (grouped by sire/dam), pinnable to the top of the herd.
  const frozenBatches = horses.filter((h) => h.type === 'frozen_embryo');
  const frozenGroups = groupFrozen(frozenBatches);
  const frozenTotal = frozenBatches.reduce((s, h) => s + (h.frozenCount || 0), 0);
  const frozenSectionEl = frozenBatches.length > 0 ? (
    <View style={{ marginBottom: 8 }}>
      <View style={styles.groupHeader}>
        <Text style={[styles.groupHeaderTxt, { color: '#2E6DA4' }]}>🧊 Frozen Embryos</Text>
        <View style={[styles.groupCount, { backgroundColor: '#2E6DA418' }]}>
          <Text style={[styles.groupCountTxt, { color: '#2E6DA4' }]}>{frozenTotal}</Text>
        </View>
      </View>
      {frozenGroups.map((g) => {
        const remaining = frozenGroupRemaining(g);
        if (compact) {
          return (
            <TouchableOpacity key={g.key} style={styles.denseRow} onPress={() => navigation.navigate('FrozenEmbryo', { sire: g.sire, dam: g.dam })} activeOpacity={0.7}>
              <View style={{ flex: 1 }}>
                <Text style={styles.denseName} numberOfLines={1}>{frozenLabel(g)}</Text>
                {g.batches.length > 1 && <Text style={styles.denseSub}>{g.batches.length} batches</Text>}
              </View>
              <Text style={[styles.denseRight, { color: remaining > 0 ? '#2E6DA4' : MUTED }]}>
                {remaining > 0 ? `${remaining} frozen` : 'Depleted'}
              </Text>
              <Text style={styles.denseChevron}>›</Text>
            </TouchableOpacity>
          );
        }
        return (
          <TouchableOpacity key={g.key} style={styles.horseCard} onPress={() => navigation.navigate('FrozenEmbryo', { sire: g.sire, dam: g.dam })} activeOpacity={0.75}>
            <View style={styles.horseCardTop}>
              <View style={{ flex: 1 }}>
                <Text style={styles.horseName}>{frozenLabel(g)}</Text>
                <Text style={styles.horseRegisteredName}>{g.batches.length > 1 ? `${g.batches.length} batches` : `${g.batches[0].method || 'ICSI'}${g.batches[0].frozenLocation ? ` · ${g.batches[0].frozenLocation}` : ''}`}</Text>
              </View>
              <View style={{ alignItems: 'flex-end' }}>
                <Text style={{ fontSize: 18, fontWeight: '800', color: remaining > 0 ? '#2E6DA4' : MUTED }}>{remaining}</Text>
                <Text style={{ fontSize: 11, color: MUTED }}>{remaining > 0 ? 'frozen' : 'depleted'}</Text>
              </View>
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  ) : null;
  // Search always looks through client horses too, so nothing seems lost.
  const herdUid = auth.currentUser?.uid;
  const clientHorseTotal = horses.filter((h) => h.type !== 'frozen_embryo' && isHorseTrainer(h, herdUid)).length;
  const hideClients = !showClients && !q;
  const filteredHorses = horses.filter((h) => h.type !== 'frozen_embryo' && matchesSearch(h) && !(hideClients && isHorseTrainer(h, herdUid)));

  const embryos = sortHorses(filteredHorses.filter((h) => h.type === 'embryo'), sortBy);
  const born    = sortHorses(filteredHorses.filter((h) => h.type === 'born'),    sortBy);
  const currentSortLabel = SORT_OPTIONS.find((o) => o.key === sortBy)?.label ?? 'Sort';

  // Merge saved locations with any location already set on a horse
  const allLocations = [...new Set([...customLocations, ...horses.map((h) => h.location).filter(Boolean)])];

  // Group by location when sorting by location
  const locationGroups = sortBy === 'location'
    ? [
        ...allLocations.map((loc) => ({
          location: loc,
          horses: sortHorses(filteredHorses.filter((h) => h.location === loc), 'name_asc'),
        })),
        {
          location: 'No Location',
          horses: sortHorses(filteredHorses.filter((h) => !h.location || !allLocations.includes(h.location)), 'name_asc'),
        },
      ].filter((g) => g.horses.length > 0)
    : [];

  // Group by status when sorting by status; horses with multiple statuses appear in each group.
  // Broodmares are shown only in the Broodmare group, not also under Mare.
  const statusGroups = sortBy === 'status'
    ? [
        ...STATUS_GROUP_ORDER.map((status) => ({
          status,
          horses: sortByAgeOrName(
            filteredHorses.filter((h) => {
              const s = h.status || [];
              if (!s.includes(status)) return false;
              if (status === 'Mare' && s.includes('Broodmare')) return false;
              return true;
            }),
            statusSubSort,
            statusSubSortDir
          ),
        })),
        {
          status: 'No Status',
          horses: sortByAgeOrName(filteredHorses.filter((h) => !(h.status || []).length), statusSubSort, statusSubSortDir),
        },
      ].filter((g) => g.horses.length > 0)
    : [];

  return (
    <SafeAreaView style={styles.screen} edges={['bottom']}>
      {horses.length === 0 && soldHorses.length === 0 ? (
        <View style={[styles.emptyWrap, { paddingBottom: 160 }]}>
          <Text style={styles.emptyIcon}>🐎</Text>
          <Text style={styles.emptyTitle}>No horses yet</Text>
          <Text style={styles.emptySub}>
            Tap the button below to add your first horse and start tracking milestones.
          </Text>
        </View>
      ) : (
        <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 160 }} keyboardShouldPersistTaps="handled">

          {/* ── Search bar ── */}
          <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: CARD, borderWidth: 1, borderColor: BORDER, borderRadius: 10, paddingHorizontal: 12, marginBottom: 10 }}>
            <Text style={{ fontSize: 15, color: MUTED, marginRight: 6 }}>🔍</Text>
            <TextInput
              style={{ flex: 1, paddingVertical: 10, fontSize: 15, color: TEXT }}
              placeholder="Search by name, sire, dam, color..."
              placeholderTextColor="#BDBDBD"
              value={searchQuery}
              onChangeText={setSearchQuery}
              autoCapitalize="none"
              autoCorrect={false}
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery('')} activeOpacity={0.7} style={{ padding: 4 }}>
                <Text style={{ fontSize: 16, color: MUTED }}>✕</Text>
              </TouchableOpacity>
            )}
          </View>

          <DensityToggle compact={compact} onChange={setCompact} />

          {clientHorseTotal > 0 && (
            <TouchableOpacity
              style={[styles.recipToggle, { marginBottom: 10 }]}
              onPress={() => setShowClients((v) => !v)}
              activeOpacity={0.75}
            >
              <View style={[styles.recipToggleTrack, showClients && styles.recipToggleTrackOn]}>
                <View style={[styles.recipToggleThumb, showClients && styles.recipToggleThumbOn]} />
              </View>
              <Text style={styles.recipToggleLabel}>
                Show client horses ({clientHorseTotal})
              </Text>
            </TouchableOpacity>
          )}
          {hideClients && clientHorseTotal > 0 && filteredHorses.length === 0 && (
            <Text style={[styles.noNotesTxt, { marginTop: 12 }]}>
              Your client horses are hidden here. Turn on “Show client horses”, or open Client Horses from Home.
            </Text>
          )}

          {/* ── Sort bar ── */}
          <TouchableOpacity
            style={styles.sortBar}
            onPress={() => setShowSortMenu((v) => !v)}
            activeOpacity={0.75}
          >
            <Text style={styles.sortBarLabel}>Sort: <Text style={styles.sortBarValue}>{currentSortLabel}</Text></Text>
            <Text style={styles.sortBarChevron}>{showSortMenu ? '▲' : '▼'}</Text>
          </TouchableOpacity>

          {q.length > 0 && filteredHorses.length === 0 && (
            <Text style={[styles.noNotesTxt, { marginTop: 20 }]}>No horses match "{searchQuery}".</Text>
          )}

          {showSortMenu && (
            <View style={styles.sortMenu}>
              {SORT_OPTIONS.map((opt) => (
                <TouchableOpacity
                  key={opt.key}
                  style={[styles.sortMenuItem, sortBy === opt.key && styles.sortMenuItemActive]}
                  onPress={() => { setSortBy(opt.key); setShowSortMenu(false); }}
                  activeOpacity={0.75}
                >
                  <Text style={[styles.sortMenuItemTxt, sortBy === opt.key && styles.sortMenuItemTxtActive]}>
                    {opt.label}
                  </Text>
                  {sortBy === opt.key && <Text style={styles.sortMenuItemCheck}>✓</Text>}
                </TouchableOpacity>
              ))}
            </View>
          )}

          {sortBy === 'status' && (
            /* ── Sub-sort within each status group ── */
            <View style={styles.subSortRow}>
              <Text style={styles.subSortLabel}>Sort within status:</Text>
              {STATUS_SUBSORT_OPTIONS.map((opt) => {
                const active = statusSubSort === opt.key;
                return (
                  <TouchableOpacity
                    key={opt.key}
                    style={[styles.subSortChip, active && styles.subSortChipActive]}
                    onPress={() => handleStatusSubSortPress(opt.key)}
                    activeOpacity={0.75}
                  >
                    <Text style={[styles.subSortChipTxt, active && styles.subSortChipTxtActive]}>
                      {opt.label}{active ? (statusSubSortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          )}

          {frozenBatches.length > 0 && (
            <TouchableOpacity onPress={() => setShowFrozenSection((v) => !v)} activeOpacity={0.75} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12, paddingVertical: 4 }}>
              <Text style={{ fontSize: 18 }}>{showFrozenSection ? '☑️' : '⬜️'}</Text>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 14, fontWeight: '600', color: TEXT }}>🧊 View Frozen Embryos ({frozenTotal})</Text>
                <Text style={{ fontSize: 12, color: MUTED }}>{showFrozenSection ? 'Pinned to the top — uncheck to move to the bottom.' : 'Shown at the bottom — check to bring to the top.'}</Text>
              </View>
            </TouchableOpacity>
          )}

          {showFrozenSection && frozenSectionEl}

          {sortBy === 'status' ? (
            /* ── Status groups ── */
            statusGroups.map((group, idx) => (
              <View key={group.status}>
                <View style={[styles.groupHeader, idx > 0 && { marginTop: 8 }]}>
                  <Text style={styles.groupHeaderTxt}>{group.status}</Text>
                  <View style={styles.groupCount}>
                    <Text style={styles.groupCountTxt}>{group.horses.length}</Text>
                  </View>
                </View>
                {group.horses.map(renderHorseCard)}
              </View>
            ))
          ) : sortBy === 'location' ? (
            /* ── Location groups ── */
            locationGroups.map((group, idx) => (
              <View key={group.location}>
                <View style={[styles.groupHeader, idx > 0 && { marginTop: 8 }]}>
                  <Text style={styles.groupHeaderTxt}>{group.location}</Text>
                  <View style={styles.groupCount}>
                    <Text style={styles.groupCountTxt}>{group.horses.length}</Text>
                  </View>
                </View>
                {group.horses.map(renderHorseCard)}
              </View>
            ))
          ) : (
            <>
              {/* ── Horses (Born) group ── */}
              {born.length > 0 && (
                <>
                  <View style={styles.groupHeader}>
                    <Text style={[styles.groupHeaderTxt, { color: GREEN }]}>Horses</Text>
                    <View style={[styles.groupCount, { backgroundColor: GREEN + '18' }]}>
                      <Text style={[styles.groupCountTxt, { color: GREEN }]}>{born.length}</Text>
                    </View>
                  </View>
                  {born.map(renderHorseCard)}
                </>
              )}

              {/* ── Embryo / In Foal group ── */}
              {embryos.length > 0 && (
                <>
                  <View style={[styles.groupHeader, born.length > 0 && { marginTop: 8 }]}>
                    <Text style={styles.groupHeaderTxt}>Embryo / In Foal</Text>
                    <View style={styles.groupCount}>
                      <Text style={styles.groupCountTxt}>{embryos.length}</Text>
                    </View>
                  </View>
                  {embryos.map(renderHorseCard)}
                </>
              )}
            </>
          )}

          {!showFrozenSection && frozenBatches.length > 0 && (
            <View style={{ marginTop: 18 }}>{frozenSectionEl}</View>
          )}

          {soldHorses.length > 0 && (
            <View style={{ marginTop: 24 }}>
              {/* Kept out of the way: a plain link at the very bottom of the herd. */}
              <TouchableOpacity
                style={{ alignSelf: 'center', paddingVertical: 8, paddingHorizontal: 14, borderRadius: 8, borderWidth: 0.5, borderColor: BORDER, backgroundColor: CARD }}
                onPress={() => setShowSold((v) => !v)}
                activeOpacity={0.7}
              >
                <Text style={{ fontSize: 13, color: MUTED, fontWeight: '600' }}>
                  {showSold ? 'Hide Sold Horses' : `View Sold Horses (${soldHorses.length})`}
                </Text>
              </TouchableOpacity>
              {showSold && <View style={{ height: 10 }} />}
              {showSold && soldHorses.map((h) => (
                <TouchableOpacity
                  key={h.id}
                  style={styles.denseRow}
                  onPress={() => navigation.navigate('Horse', { horseId: h.id })}
                  activeOpacity={0.7}
                >
                  <View style={{ flex: 1 }}>
                    <Text style={styles.denseName} numberOfLines={1}>{displayName(h)}</Text>
                    {!!h.soldTo && <Text style={styles.denseSub} numberOfLines={1}>To {h.soldTo}</Text>}
                  </View>
                  <Text style={styles.denseRight} numberOfLines={1}>Sold {formatDate(h.soldDate)}</Text>
                  <Text style={styles.denseChevron}>›</Text>
                </TouchableOpacity>
              ))}
            </View>
          )}

        </ScrollView>
      )}

      <View style={styles.bottomBar}>
        <TouchableOpacity style={styles.addBtn} onPress={() => openModal()} activeOpacity={0.85}>
          <Text style={styles.addBtnText}>+ Add Horse</Text>
        </TouchableOpacity>
      </View>

      {/* ── Add Horse Modal ── */}
      <Modal visible={showModal} animationType="slide" transparent>
        <KeyboardAvoidingView
          style={styles.overlay}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <View style={[styles.sheet, { maxHeight: '85%' }]}>
            <Text style={styles.sheetTitle}>Add Horse</Text>
            {guide.stage === 'switch' && (
              <GuideTip
                step="Step 2 of 3:"
                text="Fill in your horse. Training, boarding or shoeing it for someone else? Turn on “This is a client horse” near the bottom. Leave it off for your own horses."
                onSkip={() => setGuideStage('done')}
              >
                <TouchableOpacity
                  onPress={() => addScrollRef.current?.scrollTo({ y: Math.max(0, clientToggleY.current - 80), animated: true })}
                  style={{ alignSelf: 'flex-start', marginTop: 8 }}
                  activeOpacity={0.7}
                >
                  <Text style={{ color: GOLD, fontWeight: '700', fontSize: 13.5 }}>Show me ↓</Text>
                </TouchableOpacity>
              </GuideTip>
            )}

            <ScrollView ref={addScrollRef} showsVerticalScrollIndicator={false}>
              {newType !== 'frozen_embryo' && (
                <>
                  <Text style={styles.fieldLabel}>Horse Name</Text>
                  <TextInput
                    style={styles.input}
                    placeholder="e.g. Bella"
                    placeholderTextColor="#BDBDBD"
                    value={newName}
                    onChangeText={setNewName}
                    autoFocus
                    returnKeyType="done"
                  />
                </>
              )}

              <Text style={styles.fieldLabel}>Type</Text>
              <View style={styles.typeToggle}>
                <TouchableOpacity
                  style={[styles.typeBtn, newType === 'born' && styles.typeBtnActive]}
                  onPress={() => { setNewType('born'); setShowPicker(false); }}
                  activeOpacity={0.75}
                >
                  <Text style={[styles.typeBtnTxt, newType === 'born' && styles.typeBtnTxtActive]}>
                    Already Born
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.typeBtn, newType === 'embryo' && styles.typeBtnActive]}
                  onPress={() => { setNewType('embryo'); setShowPicker(false); }}
                  activeOpacity={0.75}
                >
                  <Text style={[styles.typeBtnTxt, newType === 'embryo' && styles.typeBtnTxtActive]}>
                    Embryo / In Foal
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.typeBtn, newType === 'frozen_embryo' && styles.typeBtnActive]}
                  onPress={() => { setNewType('frozen_embryo'); setShowPicker(false); }}
                  activeOpacity={0.75}
                >
                  <Text style={[styles.typeBtnTxt, newType === 'frozen_embryo' && styles.typeBtnTxtActive]}>
                    Frozen Embryo
                  </Text>
                </TouchableOpacity>
              </View>

              {newType !== 'frozen_embryo' && (
                <>
                  <Text style={styles.fieldLabel}>
                    {newType === 'born' ? 'Date of Birth' : 'Breed Date'}
                  </Text>
                  <TouchableOpacity
                    style={styles.datePicker}
                    onPress={() => {
                      // The iOS spinner only reports a date once it's scrolled,
                      // so opening it counts as choosing the date it shows.
                      if (!newDate && Platform.OS === 'ios') setNewDate(new Date());
                      setShowPicker(true);
                    }}
                  >
                    {newDate ? (
                      <Text style={styles.datePickerText}>
                        {newDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </Text>
                    ) : (
                      <Text style={[styles.datePickerText, { color: '#BDBDBD' }]}>
                        {newType === 'born' ? 'Select date of birth *' : 'Select breed date *'}
                      </Text>
                    )}
                    <Text>📅</Text>
                  </TouchableOpacity>
                  {showPicker && (
                    <DateTimePicker
                      // Web renders an empty input for null; native needs a Date.
                      value={Platform.OS === 'web' ? newDate : (newDate || new Date())}
                      mode="date"
                      // Spinner on both platforms: a date of birth can be many
                      // years back, and the calendar has no way to jump a year.
                      display="spinner"
                      onChange={(e, d) => {
                        if (Platform.OS === 'android') setShowPicker(false);
                        if (d) setNewDate(d);
                      }}
                    />
                  )}
                </>
              )}

              {newType === 'frozen_embryo' && (
                <>
                  <Text style={styles.fieldLabel}>Made via</Text>
                  <View style={styles.typeToggle}>
                    {['ICSI', 'Flush'].map((m) => (
                      <TouchableOpacity key={m} style={[styles.typeBtn, newFrozenMethod === m && styles.typeBtnActive]} onPress={() => setNewFrozenMethod(m)} activeOpacity={0.75}>
                        <Text style={[styles.typeBtnTxt, newFrozenMethod === m && styles.typeBtnTxtActive]}>{m}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>

                  <Text style={[styles.fieldLabel, { marginTop: 14 }]}>Frozen at (location)</Text>
                  <TextInput style={styles.input} placeholder="e.g. Vet-Repro Center" placeholderTextColor="#BDBDBD" value={newFrozenLocation} onChangeText={setNewFrozenLocation} />

                  <Text style={styles.fieldLabel}>Number of embryos frozen</Text>
                  <TextInput style={styles.input} placeholder="e.g. 3" placeholderTextColor="#BDBDBD" keyboardType="number-pad" value={newFrozenCount} onChangeText={setNewFrozenCount} />

                  <Text style={styles.fieldLabel}>First frozen</Text>
                  <TouchableOpacity style={styles.datePicker} onPress={() => setShowFirstFrozenPicker(true)}>
                    <Text style={styles.datePickerText}>{newFirstFrozen.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Text><Text>📅</Text>
                  </TouchableOpacity>
                  {showFirstFrozenPicker && (
                    <DateTimePicker value={newFirstFrozen} mode="date" display={Platform.OS === 'ios' ? 'spinner' : 'default'} onChange={(e, d) => { if (Platform.OS === 'android') setShowFirstFrozenPicker(false); if (d) setNewFirstFrozen(d); }} />
                  )}

                  <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Last frozen</Text>
                  <TouchableOpacity style={styles.datePicker} onPress={() => setShowLastFrozenPicker(true)}>
                    <Text style={styles.datePickerText}>{newLastFrozen.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Text><Text>📅</Text>
                  </TouchableOpacity>
                  {showLastFrozenPicker && (
                    <DateTimePicker value={newLastFrozen} mode="date" display={Platform.OS === 'ios' ? 'spinner' : 'default'} onChange={(e, d) => { if (Platform.OS === 'android') setShowLastFrozenPicker(false); if (d) setNewLastFrozen(d); }} />
                  )}
                  <Text style={{ fontSize: 12, color: MUTED, marginTop: 10 }}>Enter the sire, dam, and grandparents in the Pedigree section below — that's how each batch is identified.</Text>
                </>
              )}

              {newType !== 'frozen_embryo' && (
              <>
              <Text style={[styles.fieldLabel, { marginTop: 18 }]}>Registered Name</Text>
              <TextInput
                style={styles.input}
                placeholder="e.g. Moonin inthe Darkk"
                placeholderTextColor="#BDBDBD"
                value={newDetails.registeredName}
                onChangeText={(v) => setNewDetails((d) => ({ ...d, registeredName: v }))}
              />

              <Text style={styles.fieldLabel}>Registry Number</Text>
              <TextInput
                style={styles.input}
                placeholder="e.g. 6497896"
                placeholderTextColor="#BDBDBD"
                value={newDetails.registryNumber}
                onChangeText={(v) => setNewDetails((d) => ({ ...d, registryNumber: v }))}
              />

              <Text style={styles.fieldLabel}>Breed</Text>
              <TextInput
                style={styles.input}
                placeholder="e.g. Quarter Horse"
                placeholderTextColor="#BDBDBD"
                value={newDetails.breed}
                onChangeText={(v) => setNewDetails((d) => ({ ...d, breed: v }))}
              />

              <Text style={styles.fieldLabel}>Color</Text>
              <TextInput
                style={styles.input}
                placeholder="e.g. Red Roan"
                placeholderTextColor="#BDBDBD"
                value={newDetails.color}
                onChangeText={(v) => setNewDetails((d) => ({ ...d, color: v }))}
              />

              <Text style={styles.fieldLabel}>Height</Text>
              <TextInput
                style={styles.input}
                placeholder="e.g. 15.2 hands"
                placeholderTextColor="#BDBDBD"
                value={newDetails.height}
                onChangeText={(v) => setNewDetails((d) => ({ ...d, height: v }))}
              />

              <Text style={styles.fieldLabel}>Location</Text>
              <View style={styles.statusChipRow}>
                {allLocations.map((loc) => {
                  const selected = newDetails.location === loc;
                  return (
                    <TouchableOpacity
                      key={loc}
                      style={[styles.statusChip, selected && styles.statusChipSelected]}
                      onPress={() => setNewDetails((d) => ({ ...d, location: selected ? '' : loc }))}
                      activeOpacity={0.75}
                    >
                      <Text style={[styles.statusChipTxt, selected && styles.statusChipTxtSelected]}>{loc}</Text>
                    </TouchableOpacity>
                  );
                })}
                <TouchableOpacity
                  style={styles.statusChip}
                  onPress={() => { setShowAddLocation(true); setNewLocationText(''); }}
                  activeOpacity={0.75}
                >
                  <Text style={styles.statusChipTxt}>+ Add Location</Text>
                </TouchableOpacity>
              </View>
              {showAddLocation && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  <TextInput
                    style={[styles.input, { flex: 1, marginBottom: 0 }]}
                    placeholder="Location name"
                    placeholderTextColor="#BDBDBD"
                    value={newLocationText}
                    onChangeText={setNewLocationText}
                    autoFocus
                  />
                  <TouchableOpacity
                    style={[styles.primaryButton, { paddingHorizontal: 14, paddingVertical: 10, marginBottom: 0 }]}
                    onPress={async () => {
                      const loc = newLocationText.trim();
                      if (loc && !allLocations.includes(loc)) {
                        await addCustomLocation(auth.currentUser.uid, loc);
                      }
                      if (loc) setNewDetails((d) => ({ ...d, location: loc }));
                      setShowAddLocation(false);
                      setNewLocationText('');
                    }}
                  >
                    <Text style={styles.primaryButtonTxt}>Add</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setShowAddLocation(false)}>
                    <Text style={{ color: '#999', fontSize: 15 }}>Cancel</Text>
                  </TouchableOpacity>
                </View>
              )}

              <Text style={[styles.fieldLabel, { marginTop: 14 }]}>Stall # (optional)</Text>
              <TextInput
                style={styles.input}
                placeholder="e.g. 12 or B-4"
                placeholderTextColor="#BDBDBD"
                value={newDetails.stall || ''}
                onChangeText={(v) => setNewDetails((d) => ({ ...d, stall: v }))}
              />

              <Text style={[styles.fieldLabel, { marginTop: 18 }]}>Status (select all that apply)</Text>
              <View style={styles.statusChipRow}>
                {STATUS_OPTIONS.map((option) => {
                  const selected = newDetails.status.includes(option);
                  return (
                    <TouchableOpacity
                      key={option}
                      style={[styles.statusChip, selected && styles.statusChipSelected]}
                      onPress={() => toggleNewStatus(option)}
                      activeOpacity={0.75}
                    >
                      <Text style={[styles.statusChipTxt, selected && styles.statusChipTxtSelected]}>
                        {option}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>

              {newDetails.status.includes('Foal/Yearling') && (
                <>
                  <Text style={styles.fieldLabel}>Sex</Text>
                  <View style={styles.statusChipRow}>
                    {['Filly', 'Colt'].map((sex) => {
                      const selected = newDetails.status.includes(sex);
                      return (
                        <TouchableOpacity
                          key={sex}
                          style={[styles.statusChip, selected && styles.statusChipSelected]}
                          onPress={() => toggleNewFoalSex(sex)}
                          activeOpacity={0.75}
                        >
                          <Text style={[styles.statusChipTxt, selected && styles.statusChipTxtSelected]}>
                            {sex}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </>
              )}

              {newDetails.status.includes('Mare') && (
                <>
                  <Text style={styles.fieldLabel}>Is she a broodmare?</Text>
                  <TouchableOpacity
                    style={styles.recipToggle}
                    onPress={toggleNewBroodmare}
                    activeOpacity={0.75}
                  >
                    <View style={[styles.recipToggleTrack, newDetails.status.includes('Broodmare') && styles.recipToggleTrackOn]}>
                      <View style={[styles.recipToggleThumb, newDetails.status.includes('Broodmare') && styles.recipToggleThumbOn]} />
                    </View>
                    <Text style={styles.recipToggleLabel}>
                      {newDetails.status.includes('Broodmare') ? 'Yes — tagged as Broodmare' : 'No'}
                    </Text>
                  </TouchableOpacity>

                  <Text style={styles.fieldLabel}>Is she a recip?</Text>
                  <TouchableOpacity
                    style={styles.recipToggle}
                    onPress={toggleNewRecip}
                    activeOpacity={0.75}
                  >
                    <View style={[styles.recipToggleTrack, newDetails.status.includes('Recip') && styles.recipToggleTrackOn]}>
                      <View style={[styles.recipToggleThumb, newDetails.status.includes('Recip') && styles.recipToggleThumbOn]} />
                    </View>
                    <Text style={styles.recipToggleLabel}>
                      {newDetails.status.includes('Recip') ? 'Yes — tagged as Recip' : 'No'}
                    </Text>
                  </TouchableOpacity>
                </>
              )}

              </>
              )}

              {newType === 'embryo' && (
                <>
                  <Text style={[styles.fieldLabel, { marginTop: 18 }]}>Recipient Mare?</Text>
                  <TouchableOpacity
                    style={styles.recipToggle}
                    onPress={() => setNewIsRecip((v) => !v)}
                    activeOpacity={0.75}
                  >
                    <View style={[styles.recipToggleTrack, newIsRecip && styles.recipToggleTrackOn]}>
                      <View style={[styles.recipToggleThumb, newIsRecip && styles.recipToggleThumbOn]} />
                    </View>
                    <Text style={styles.recipToggleLabel}>
                      {newIsRecip ? 'Yes — adds 120-day check' : 'No'}
                    </Text>
                  </TouchableOpacity>

                  {newIsRecip && (
                    <>
                      <Text style={styles.fieldLabel}>Recip Mare</Text>
                      <View style={styles.pedigreeInputRow}>
                        <TextInput
                          style={[styles.input, { flex: 1, marginBottom: 0 }]}
                          placeholder="Recip mare name or source"
                          placeholderTextColor="#BDBDBD"
                          value={newDetails.recipMare}
                          onChangeText={(v) => setNewDetails((d) => ({ ...d, recipMare: v, recipMareId: null }))}
                        />
                        <TouchableOpacity style={styles.fromStableBtn} onPress={() => openPicker('recip')} activeOpacity={0.75}>
                          <Text style={styles.fromStableBtnTxt}>From Stable</Text>
                        </TouchableOpacity>
                      </View>
                      {newDetails.recipMareId && (
                        <Text style={styles.linkedNote}>✓ Linked to {newDetails.recipMare}</Text>
                      )}
                      {(newDetails.recipMare.trim() || newDetails.recipMareId) && (
                        <>
                          <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Is the recip leased?</Text>
                          <TouchableOpacity
                            style={styles.recipToggle}
                            onPress={() => setNewDetails((d) => ({ ...d, recipLeased: !d.recipLeased, recipLeasedFrom: !d.recipLeased ? d.recipLeasedFrom : '' }))}
                            activeOpacity={0.75}
                          >
                            <View style={[styles.recipToggleTrack, newDetails.recipLeased && styles.recipToggleTrackOn]}>
                              <View style={[styles.recipToggleThumb, newDetails.recipLeased && styles.recipToggleThumbOn]} />
                            </View>
                            <Text style={styles.recipToggleLabel}>{newDetails.recipLeased ? 'Yes — leased' : 'No'}</Text>
                          </TouchableOpacity>
                          {newDetails.recipLeased && (
                            <>
                              <Text style={styles.fieldLabel}>Leased from</Text>
                              <TextInput
                                style={styles.input}
                                placeholder="e.g. Smith Ranch"
                                placeholderTextColor="#BDBDBD"
                                value={newDetails.recipLeasedFrom}
                                onChangeText={(v) => setNewDetails((d) => ({ ...d, recipLeasedFrom: v }))}
                              />
                            </>
                          )}
                        </>
                      )}
                      <View style={{ marginBottom: 18 }} />
                    </>
                  )}
                </>
              )}

              <Text style={[styles.fieldLabel, { marginTop: 18 }]}>Pedigree</Text>
              <Text style={styles.fieldLabel}>Sire</Text>
              <View style={styles.pedigreeInputRow}>
                <TextInput
                  style={[styles.input, { flex: 1, marginBottom: 0 }]}
                  placeholder="Sire's name"
                  placeholderTextColor="#BDBDBD"
                  value={newDetails.sire}
                  onChangeText={(v) => setNewDetails((d) => ({ ...d, sire: v, sireId: null }))}
                />
                <TouchableOpacity style={styles.fromStableBtn} onPress={() => openPicker('sire')} activeOpacity={0.75}>
                  <Text style={styles.fromStableBtnTxt}>From Stable</Text>
                </TouchableOpacity>
              </View>
              {newDetails.sireId && (
                <Text style={styles.linkedNote}>✓ Linked to {newDetails.sire}</Text>
              )}
              <View style={{ marginBottom: 18 }} />

              <Text style={styles.fieldLabel}>Dam</Text>
              <View style={styles.pedigreeInputRow}>
                <TextInput
                  style={[styles.input, { flex: 1, marginBottom: 0 }]}
                  placeholder="Dam's name"
                  placeholderTextColor="#BDBDBD"
                  value={newDetails.dam}
                  onChangeText={(v) => setNewDetails((d) => ({ ...d, dam: v, damId: null }))}
                />
                <TouchableOpacity style={styles.fromStableBtn} onPress={() => openPicker('dam')} activeOpacity={0.75}>
                  <Text style={styles.fromStableBtnTxt}>From Stable</Text>
                </TouchableOpacity>
              </View>
              {newDetails.damId && (
                <Text style={styles.linkedNote}>✓ Linked to {newDetails.dam}</Text>
              )}
              <View style={{ marginBottom: 18 }} />

              <Text style={[styles.fieldLabel, { marginTop: 6 }]}>Grandparents</Text>
              {GRANDPARENT_FIELDS.map((f) => (
                <React.Fragment key={f.key}>
                  <Text style={styles.fieldLabel}>{f.label}</Text>
                  <TextInput
                    style={styles.input}
                    placeholder="Enter manually"
                    placeholderTextColor="#BDBDBD"
                    value={newDetails[f.key]}
                    onChangeText={(v) => setNewDetails((d) => ({ ...d, [f.key]: v }))}
                  />
                </React.Fragment>
              ))}

              <Text style={styles.fieldLabel}>Image URL</Text>
              <TextInput
                style={styles.input}
                placeholder="https://example.com/horse-photo.jpg"
                placeholderTextColor="#BDBDBD"
                value={newDetails.imageUrl}
                onChangeText={(v) => setNewDetails((d) => ({ ...d, imageUrl: v }))}
                autoCapitalize="none"
              />

              {/* ── Client horse (trainer manages for a client) ── */}
              <View style={styles.divider} />
              <TouchableOpacity
                style={[styles.recipToggle, guide.stage === 'switch' && { ...GUIDE_HIGHLIGHT, borderRadius: 10, padding: 8 }]}
                onPress={() => setNewIsClientHorse((v) => !v)}
                onLayout={(e) => { clientToggleY.current = e.nativeEvent.layout.y; }}
                activeOpacity={0.75}
              >
                <View style={[styles.recipToggleTrack, newIsClientHorse && styles.recipToggleTrackOn]}>
                  <View style={[styles.recipToggleThumb, newIsClientHorse && styles.recipToggleThumbOn]} />
                </View>
                <Text style={styles.recipToggleLabel}>
                  {newIsClientHorse ? '🎓 Client horse — you train it' : 'This is a client horse'}
                </Text>
              </TouchableOpacity>
              {newIsClientHorse && (
                <>
                  <Text style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>
                    Adds this horse to your Client Horses with training notes and a billing ledger. Enter the owner's contact info so you have it handy.
                  </Text>
                  {knownClients.length > 0 && (
                    <View style={{ width: '100%', marginBottom: 4 }}>
                      <Text style={styles.fieldLabel}>Existing client</Text>
                      <TouchableOpacity
                        style={styles.datePicker}
                        onPress={() => { setClientPickerSearch(''); setShowClientPicker(true); }}
                        activeOpacity={0.75}
                      >
                        <Text style={[styles.datePickerText, !newClientOwnerName.trim() && { color: '#BDBDBD' }]} numberOfLines={1}>
                          {newClientOwnerName.trim() || `Choose from ${knownClients.length} client${knownClients.length === 1 ? '' : 's'}…`}
                        </Text>
                        <Text style={{ color: MUTED }}>▾</Text>
                      </TouchableOpacity>
                      <Text style={{ fontSize: 11, color: MUTED, marginTop: 4, marginBottom: 2 }}>
                        Pick a client to fill in their details, or just type a new one below.
                      </Text>
                    </View>
                  )}
                  <View style={{ width: '100%' }}>
                    <Text style={styles.fieldLabel}>Owner Name</Text>
                    <TextInput style={styles.input} placeholder="Owner name" placeholderTextColor="#BDBDBD" value={newClientOwnerName} onChangeText={setNewClientOwnerName} />
                  </View>
                  <View style={{ width: '100%' }}>
                    <Text style={styles.fieldLabel}>Owner Email</Text>
                    <TextInput style={styles.input} placeholder="owner@email.com" placeholderTextColor="#BDBDBD" keyboardType="email-address" autoCapitalize="none" value={newClientOwnerEmail} onChangeText={setNewClientOwnerEmail} />
                  </View>
                  <View style={{ width: '100%' }}>
                    <Text style={styles.fieldLabel}>Owner Phone</Text>
                    <TextInput style={styles.input} placeholder="(555) 555-5555" placeholderTextColor="#BDBDBD" keyboardType="phone-pad" value={newClientOwnerPhone} onChangeText={setNewClientOwnerPhone} />
                  </View>
                </>
              )}

              {PHOTO_UPLOADS_ENABLED && isPhotoPickingAvailable() && (
                <View style={{ width: '100%' }}>
                  <Text style={styles.fieldLabel}>Profile Photo (optional)</Text>
                  {newProfilePhoto ? (
                    <View>
                      <AdjustableImage
                        uri={newProfilePhoto.uri}
                        focus={newProfileFocus}
                        onChange={setNewProfileFocus}
                        maxWidth={520}
                      />
                      <Text style={{ fontSize: 11, color: MUTED, textAlign: 'center', marginTop: 4 }}>
                        Drag to adjust framing · pinch or +/− to zoom
                      </Text>
                      <View style={{ flexDirection: 'row', gap: 20, marginTop: 6, justifyContent: 'center' }}>
                        <TouchableOpacity onPress={() => choosePhotoSource((items) => { setNewProfilePhoto(items[0]); setNewProfileFocus({ x: 0.5, y: 0.5, zoom: 1 }); })} activeOpacity={0.7}>
                          <Text style={styles.editNameBtnTxt}>Change Photo</Text>
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => setNewProfilePhoto(null)} activeOpacity={0.7}>
                          <Text style={{ color: RED, fontWeight: '600', fontSize: 13 }}>Remove</Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  ) : (
                    <TouchableOpacity style={styles.addMilestoneBtn} onPress={() => choosePhotoSource((items) => setNewProfilePhoto(items[0]))} activeOpacity={0.75}>
                      <Text style={styles.addMilestoneBtnTxt}>+ Add Profile Photo</Text>
                    </TouchableOpacity>
                  )}
                </View>
              )}
            </ScrollView>

            <View style={styles.sheetBtns}>
              <TouchableOpacity
                style={styles.cancelBtn}
                onPress={() => { clearDraft(); setShowModal(false); }}
              >
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleAdd}>
                <Text style={styles.saveBtnText}>Add Horse</Text>
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── Existing Client Picker (Add Horse) ── */}
      <Modal visible={showClientPicker} animationType="slide" transparent onRequestClose={() => setShowClientPicker(false)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '85%' }]}>
            <Text style={styles.sheetTitle}>Choose a Client</Text>
            <TextInput
              style={styles.input}
              placeholder="Search clients..."
              placeholderTextColor="#BDBDBD"
              value={clientPickerSearch}
              onChangeText={setClientPickerSearch}
              autoFocus
            />
            <ScrollView showsVerticalScrollIndicator={false} style={{ maxHeight: 380 }} keyboardShouldPersistTaps="handled">
              {(() => {
                const q = clientPickerSearch.trim().toLowerCase();
                const filtered = q
                  ? knownClients.filter((c) => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q))
                  : knownClients;
                if (filtered.length === 0) {
                  return <Text style={styles.pickerEmpty}>No client matches that search.</Text>;
                }
                return filtered.map((c) => (
                  <TouchableOpacity
                    key={c.name}
                    style={styles.pickerRow}
                    onPress={() => {
                      setNewClientOwnerName(c.name);
                      setNewClientOwnerEmail(c.email);
                      setNewClientOwnerPhone(c.phone);
                      setShowClientPicker(false);
                    }}
                    activeOpacity={0.75}
                  >
                    <Text style={styles.pickerRowName}>{c.name}</Text>
                    {!!c.email && <Text style={styles.pickerRowSub}>{c.email}</Text>}
                    {!!c.phone && <Text style={styles.pickerRowSub}>{c.phone}</Text>}
                    <Text style={styles.pickerRowSub}>{c.horseCount} horse{c.horseCount === 1 ? '' : 's'} with you</Text>
                  </TouchableOpacity>
                ));
              })()}
            </ScrollView>
            <View style={styles.sheetBtns}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowClientPicker(false)} activeOpacity={0.85}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              {!!newClientOwnerName.trim() && (
                <TouchableOpacity
                  style={styles.saveBtn}
                  onPress={() => {
                    setNewClientOwnerName('');
                    setNewClientOwnerEmail('');
                    setNewClientOwnerPhone('');
                    setShowClientPicker(false);
                  }}
                  activeOpacity={0.85}
                >
                  <Text style={styles.saveBtnText}>Clear</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── From Stable Picker Modal (Add Horse) ── */}
      <Modal visible={!!pickerFor} animationType="slide" transparent>
        <View style={styles.overlay}>
          <View style={[styles.sheet, { maxHeight: '85%' }]}>
            <Text style={styles.sheetTitle}>
              Select {pickerFor === 'dam' ? 'Dam' : pickerFor === 'recip' ? 'Recip Mare' : 'Sire'} from Your Stable
            </Text>
            <TextInput
              style={styles.input}
              placeholder="Search horses..."
              placeholderTextColor="#BDBDBD"
              value={pickerSearch}
              onChangeText={setPickerSearch}
              autoFocus
            />
            <ScrollView showsVerticalScrollIndicator={false} style={{ maxHeight: 360 }}>
              {(() => {
                const eligible = filterByPedigreeRole(horses, pickerFor);
                const q = pickerSearch.trim().toLowerCase();
                const filtered = eligible.filter(
                  (h) => stableDisplayName(h).toLowerCase().includes(q) || h.name.toLowerCase().includes(q)
                );
                return (
                  <>
                    {filtered.map((h) => (
                      <TouchableOpacity
                        key={h.id}
                        style={styles.pickerRow}
                        onPress={() => pickFromStable(h)}
                        activeOpacity={0.75}
                      >
                        <Text style={styles.pickerRowName}>{stableDisplayName(h)}</Text>
                        {h.registeredName && h.name !== h.registeredName ? (
                          <Text style={styles.pickerRowSub}>Barn name: {h.name}</Text>
                        ) : null}
                        {h.status && h.status.length > 0 ? (
                          <Text style={styles.pickerRowSub}>{h.status.join(', ')}</Text>
                        ) : null}
                      </TouchableOpacity>
                    ))}
                    {eligible.length === 0 && (
                      <Text style={styles.pickerEmpty}>
                        No {pickerFor === 'sire' ? 'stallions or geldings' : 'mares'} in your stable yet.
                      </Text>
                    )}
                    {pickerFor === 'sire' && (
                      <StudPickerRows studs={studs} search={pickerSearch} onPick={pickStudAsSire} />
                    )}
                  </>
                );
              })()}
            </ScrollView>
            <View style={styles.sheetBtns}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setPickerFor(null)}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

// ─── HORSE DETAIL SCREEN ──────────────────────────────────────────────────────

// A profile section with a rustic colored header bar (icon + title + optional
// badge) that collapses/expands its content. `sec` keys into SECTION_STYLES.
function CollapsibleSection({ sec, title, badge, collapsed, onToggle, children }) {
  const m = SECTION_STYLES[sec] || SECTION_STYLES.details;
  return (
    <View style={[styles.sectionWrap, { borderLeftColor: m.accent }]}>
      <TouchableOpacity style={[styles.sectionHeaderBar, { backgroundColor: m.tint }]} onPress={onToggle} activeOpacity={0.7}>
        <Text style={styles.sectionEmoji}>{m.emoji}</Text>
        <Text style={[styles.sectionTitle, { color: m.text }]}>{title}</Text>
        {badge != null && badge !== '' && (
          <View style={[styles.sectionBadge, { backgroundColor: m.accent }]}>
            <Text style={styles.sectionBadgeTxt}>{badge}</Text>
          </View>
        )}
        <Text style={[styles.sectionChevron, { color: m.text }]}>{collapsed ? '▾' : '▴'}</Text>
      </TouchableOpacity>
      {!collapsed && <View style={styles.sectionBody}>{children}</View>}
    </View>
  );
}

// ── Pictures & Documents helpers ──────────────────────────────────────────────
const DOC_TYPE_PRESETS = ['Picture', 'Invoice', 'Health Certificate', 'Registration Papers', 'Coggins', 'Vet Record', 'Receipt'];
const PROVIDER_TYPE_PRESETS = ['Trainer', 'Boarding', 'Farrier', 'Vet', 'Other'];
// Distinct accent color per provider type for grouped billing/notes.
const providerTypeColor = (type) => {
  const map = { Trainer: '#1D9E75', Boarding: '#378ADD', Farrier: '#BA7517', Vet: '#7F77DD' };
  if (map[type]) return map[type];
  let h = 0; for (let i = 0; i < (type || '').length; i++) h = (h * 31 + type.charCodeAt(i)) >>> 0;
  return ['#D4537E', '#D85A30', '#639922', '#888780'][h % 4];
};

// One ledger entry row (charge/payment) with optional edit/delete.
// Online payments whose fee details have been requested this session, so a
// payment is looked up once rather than on every render.
const feeLookups = new Map(); // entryId -> error message ('' while pending/ok)

function LedgerRow({ e, fmt, paidTowardCharge, canEdit, onEdit, onDelete }) {
  // Provider-only (canEdit = it's my entry as the provider): what an online
  // payment cost me and what I received. Never shown to the owner/client.
  const pf = canEdit && e.type === 'payment' ? e.providerFees : null;
  // Online-payment entries mirror Stripe; they change only through Refund, so
  // hand edits can't put the ledger out of step with what really happened.
  const canModify = canEdit && e.method !== 'stripe' && !e.refunded;
  const isOnline = canEdit && e.type === 'payment' && e.method === 'stripe' && !!e.stripePaymentIntent;
  const [feeErr, setFeeErr] = useState(feeLookups.get(e.id) || '');
  const [refundAsk, setRefundAsk] = useState(false); // the refund question, same on every platform
  useEffect(() => {
    // Missing or not-yet-settled fee details: ask Stripe once; the ledger
    // subscription shows the result as soon as it's saved.
    if (!isOnline || (pf && pf.stripe != null) || feeLookups.has(e.id)) return;
    feeLookups.set(e.id, '');
    callStripePaymentFees(e.id).catch((err) => {
      const m = err?.message || 'Fee details unavailable.';
      feeLookups.set(e.id, m);
      setFeeErr(m);
    });
  }, [e.id, isOnline, pf && pf.stripe]);
  const paid = e.type === 'charge' ? paidTowardCharge(e.id) : 0;
  const remaining = (e.amount || 0) - paid;
  return (
    <View style={[styles.notesCard, { flexDirection: 'row', alignItems: 'center' }]}>
      <View style={{ flex: 1 }}>
        <Text style={[styles.noteText, e.refunded && { textDecorationLine: 'line-through', color: MUTED }]}>{e.description}</Text>
        {!!e.note && <Text style={{ fontSize: 12, color: MUTED, marginTop: 2, lineHeight: 17 }}>{e.note}</Text>}
        {!!pf && (
          <Text style={{ fontSize: 11.5, color: '#5C4A2E', marginTop: 3, lineHeight: 16 }}>
            {[
              `Client paid ${fmt(pf.clientPaid || 0)}`,
              pf.stripe != null ? `Stripe fee ${fmt(pf.stripe)}` : 'Stripe fee pending',
              `Horse Math fee ${fmt(pf.horseMath || 0)}`,
              pf.received != null ? `You received ${fmt(pf.received)}` : null,
            ].filter(Boolean).join(' · ')}
          </Text>
        )}
        {isOnline && !pf && !!feeErr && (
          <Text style={{ fontSize: 11, color: MUTED, marginTop: 3 }}>Fee details: {feeErr}</Text>
        )}
        {e.type === 'payment' && e.disputeStatus === 'open' && (
          <Text style={{ fontSize: 12, color: '#B00020', fontWeight: '700', marginTop: 3 }}>⚠️ Disputed by the client{canEdit ? ' — respond in your Stripe dashboard' : ''}</Text>
        )}
        {e.type === 'payment' && (e.disputeStatus === 'won' || e.disputeStatus === 'lost') && (
          <Text style={{ fontSize: 12, color: e.disputeStatus === 'won' ? GREEN : '#B00020', fontWeight: '600', marginTop: 3 }}>Dispute {e.disputeStatus}</Text>
        )}
        {canEdit && e.disputeStatus === 'open' && e.disputeLivemode === false && auth.currentUser?.email === ADMIN_EMAIL && (
          <View style={{ flexDirection: 'row', gap: 14, marginTop: 4 }}>
            {['won', 'lost'].map((o) => (
              <TouchableOpacity key={o} activeOpacity={0.7} onPress={() => callStripeTestDisputeOutcome(e.id, o).then(() => Alert.alert('Sent', `Stripe will close the test dispute as ${o} shortly.`)).catch((err) => Alert.alert('Failed', err?.message || 'Try again.'))}>
                <Text style={{ fontSize: 12, color: MUTED, fontWeight: '700' }}>Test: {o === 'won' ? 'win' : 'lose'} dispute</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
        {e.refunded && (
          <Text style={{ fontSize: 12, color: '#B00020', fontWeight: '700', marginTop: 3 }}>
            Refunded{e.refundCancelledCharges ? ' · charges cancelled' : ' · charges unpaid again'}
          </Text>
        )}
        {e.refunded && e.refundCancelledCharges && canEdit && isOnline && (
          <TouchableOpacity
            activeOpacity={0.7}
            style={{ alignSelf: 'flex-start', marginTop: 4 }}
            onPress={() => {
              const go = () => callStripeRestoreRefundedCharges(e.id)
                .then((r) => Alert.alert('Charges owed again', r.skipped
                  ? `${r.restored} charge${r.restored === 1 ? '' : 's'} put back as owed. ${r.skipped} older charge${r.skipped === 1 ? ' was' : 's were'} reduced before this option existed — edit ${r.skipped === 1 ? 'its' : 'their'} amount by hand.`
                  : `${r.restored} charge${r.restored === 1 ? '' : 's'} put back as owed. They show under Outstanding.`))
                .catch((err) => Alert.alert('Not changed', err?.message || 'Please try again.'));
              const msg = 'The client still owes for the charges this payment covered? They come back under Outstanding.';
              if (Platform.OS === 'web') { if (window.confirm(msg)) go(); }
              else Alert.alert('Charges still owed?', msg, [{ text: 'Cancel', style: 'cancel' }, { text: 'Yes, still owed', onPress: go }]);
            }}
          >
            <Text style={{ fontSize: 12, color: RED, fontWeight: '700' }}>Client still owes these charges? Put them back</Text>
          </TouchableOpacity>
        )}
        {canEdit && !e.refunded && e.type === 'payment' && e.method === 'stripe' && !!e.stripePaymentIntent && e.disputeStatus !== 'open' && (
            <TouchableOpacity
              activeOpacity={0.7}
              style={{ alignSelf: 'flex-start', marginTop: 4 }}
              onPress={() => setRefundAsk(true)}
            >
              <Text style={{ fontSize: 12, color: RED, fontWeight: '700' }}>Refund this payment</Text>
            </TouchableOpacity>
        )}
        <Modal visible={refundAsk} animationType="fade" transparent onRequestClose={() => setRefundAsk(false)}>
          <View style={[styles.overlay, { justifyContent: 'center', padding: 20 }]}>
            <View style={{ backgroundColor: CARD, borderRadius: 14, padding: 18 }}>
              <Text style={styles.sheetTitle}>Refund {fmt((pf && pf.clientPaid) || e.amount)}?</Text>
              <Text style={{ fontSize: 14, color: TEXT, lineHeight: 20, marginBottom: 14 }}>
                The full payment goes back to your client’s card or bank. Does the client still owe for the charges it paid?
              </Text>
              {[
                [false, 'Refund — client still owes', 'The charges go back under Outstanding, unpaid.', RED],
                [true, 'Refund and cancel the charges', 'The charges are removed (restorable from Recently deleted).', '#555'],
              ].map(([cancel, label, sub, color]) => (
                <TouchableOpacity
                  key={label}
                  activeOpacity={0.85}
                  onPress={() => {
                    setRefundAsk(false);
                    callStripeRefundPayment(e.id, cancel)
                      .then((r) => Alert.alert('Refund sent', `${fmt(r.amount)} is being returned to your client. The ledger updates in a moment.`))
                      .catch((err) => Alert.alert('Refund failed', err?.message || 'Please try again.'));
                  }}
                  style={{ borderWidth: 1.5, borderColor: color, borderRadius: 10, padding: 12, marginBottom: 10 }}
                >
                  <Text style={{ fontSize: 15, fontWeight: '700', color }}>{label}</Text>
                  <Text style={{ fontSize: 12.5, color: MUTED, marginTop: 2 }}>{sub}</Text>
                </TouchableOpacity>
              ))}
              <TouchableOpacity onPress={() => setRefundAsk(false)} style={{ alignItems: 'center', paddingTop: 6 }} activeOpacity={0.7}>
                <Text style={{ color: MUTED, fontSize: 15 }}>Don’t refund</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>
        <Text style={styles.noteDate}>{formatDate(e.date)}</Text>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 2 }}>
          {e.type === 'charge' && paid > 0.005 && (
            remaining <= 0.005
              ? <Text style={{ fontSize: 11, color: GREEN, fontWeight: '700' }}>✓ Paid</Text>
              : <Text style={{ fontSize: 11, color: '#B8860B', fontWeight: '700' }}>Partial · {fmt(remaining)} left</Text>
          )}
          {e.type === 'charge' && !!e.invoiceNumber && (
            <View style={{ backgroundColor: '#F1ECE0', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 1 }}>
              <Text style={{ fontSize: 10, color: '#8A6D2F', fontWeight: '700' }}>Invoiced · {e.invoiceNumber}</Text>
            </View>
          )}
          {e.type === 'charge' && !!e.emailProblem ? (
            <View style={{ backgroundColor: '#FCEBEB', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 1 }}>
              <Text style={{ fontSize: 10, color: '#791F1F', fontWeight: '700' }}>{e.emailProblem === 'complained' ? 'Email marked spam' : 'Email bounced'}</Text>
            </View>
          ) : e.type === 'charge' && !!e.invoiceEmailedAt && (
            <View style={{ backgroundColor: '#EAF3DE', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 1 }}>
              <Text style={{ fontSize: 10, color: '#27500A', fontWeight: '700' }}>Emailed {formatDate(e.invoiceEmailedAt)}</Text>
            </View>
          )}
          {e.type === 'charge' && !!e.lastRemindedAt && remaining > 0.005 && (
            <View style={{ backgroundColor: '#FAEEDA', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 1 }}>
              <Text style={{ fontSize: 10, color: '#633806', fontWeight: '700' }}>Reminded {formatDate(e.lastRemindedAt)}</Text>
            </View>
          )}
        </View>
      </View>
      <Text style={[{ fontWeight: '700', color: e.type === 'payment' ? GREEN : RED, fontSize: 15 }, e.refunded && { textDecorationLine: 'line-through', color: MUTED }]}>
        {e.type === 'payment' ? '-' : '+'}{fmt(e.amount)}
      </Text>
      {canModify && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginLeft: 12 }}>
          <TouchableOpacity onPress={() => onEdit(e)} activeOpacity={0.7}><Text style={{ fontSize: 14 }}>✏️</Text></TouchableOpacity>
          <TouchableOpacity onPress={() => onDelete(e)} activeOpacity={0.7}><Text style={styles.incDeleteTxt}>✕</Text></TouchableOpacity>
        </View>
      )}
    </View>
  );
}

// A collapsible per-provider notes section (owner's grouped view).
function NotesProviderGroup({ name, notes, canEditNote, onEdit, onDelete, defaultCollapsed }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const color = providerTypeColor(name || 'Provider');
  return (
    <View style={{ borderWidth: 0.5, borderColor: BORDER, borderLeftWidth: 3, borderLeftColor: color, borderRadius: 10, overflow: 'hidden', marginBottom: 10, backgroundColor: CARD }}>
      <TouchableOpacity onPress={() => setCollapsed((c) => !c)} activeOpacity={0.75} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 10 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1 }}>
          <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: color }} />
          <Text style={{ fontSize: 14, fontWeight: '600', color: TEXT }} numberOfLines={1}>{name}</Text>
        </View>
        <Text style={{ fontSize: 12, color: MUTED }}>{notes.length} · {collapsed ? '▾' : '▴'}</Text>
      </TouchableOpacity>
      {!collapsed && (
        <View style={{ paddingHorizontal: 8, paddingBottom: 6 }}>
          {notes.slice().reverse().map((n) => (
            <View key={n.id} style={styles.notesCard}>
              <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 6 }}>
                {n.private && <Text style={{ fontSize: 13, color: MUTED }}>🔒</Text>}
                <Text style={[styles.noteText, { flex: 1 }]}>{n.text}</Text>
                {canEditNote(n) && (
                  <View style={{ flexDirection: 'row', gap: 12 }}>
                    <TouchableOpacity onPress={() => onEdit(n)} activeOpacity={0.7}><Text style={{ fontSize: 13, color: MUTED }}>✏️</Text></TouchableOpacity>
                    <TouchableOpacity onPress={() => onDelete(n.id)} activeOpacity={0.7}><Text style={{ fontSize: 13, color: RED }}>🗑</Text></TouchableOpacity>
                  </View>
                )}
              </View>
              <Text style={styles.noteDate}>{formatDate(n.date)}</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

// Group ledger entries / notes by COMPANY (business name), so colleagues who
// share a business name fold into one provider section instead of splitting.
// The match key is normalized (case/spacing-insensitive) to tolerate small
// differences in how the name is typed; the displayed name uses the raw text.
const normalizeCompany = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
const entryCompany = (e) => normalizeCompany(e.providerName) || ('u:' + (e.trainerUid || e.createdByUid || 'unknown'));
const fmtMoney = (n) => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// A user's effective billing identity. If they belong to a company (their own or
// a colleague's they were linked into), the SHARED company profile wins over
// their personal one — so linked colleagues bill under one name everywhere.
const effectiveBusinessProfile = (own, company) => {
  if (!company) return own;
  return {
    ...(own || {}),
    name: company.name || own?.name || '',
    email: company.email || own?.email || '',
    phone: company.phone || own?.phone || '',
    address: company.address || own?.address || '',
    terms: company.terms || own?.terms || '',
    logoUrl: company.logoUrl || '',
    logoPath: company.logoPath || '',
    companyId: company.id,
  };
};

// A collapsible per-provider billing section (owner's grouped view).
// Online payment from the Billing Ledger, for one provider's balance on one
// horse. The owner sees "Pay online" (opens the pay page); the provider sees
// "Send pay link" (share it with the client). Shows nothing when that provider
// hasn't set up online payments.
function LedgerPayButton({ horseId, group, isProvider }) {
  const [url, setUrl] = useState(undefined); // undefined = loading, null = not available
  useEffect(() => {
    if (!canSeeOnlinePayments() || !(group.balance > 0.004)) { setUrl(null); return; }
    let live = true;
    callLedgerPayLink(horseId, group.key).then((r) => { if (live) setUrl(r?.url || null); }).catch(() => { if (live) setUrl(null); });
    return () => { live = false; };
  }, [horseId, group.key, group.balance > 0.004]);
  if (!url) return null;
  const onPress = async () => {
    if (!isProvider) {
      if (Platform.OS === 'web') window.open(url, '_blank'); else Linking.openURL(url);
      return;
    }
    const message = `Pay your balance with ${group.name || 'us'} online: ${url}`;
    if (Platform.OS === 'web') {
      try { await navigator.clipboard.writeText(url); Alert.alert('Pay link copied', 'Paste it into a text or email to your client.'); }
      catch { window.prompt('Copy this pay link:', url); }
    } else {
      Share.share({ message });
    }
  };
  return (
    <TouchableOpacity onPress={onPress} activeOpacity={0.85} style={{ alignSelf: 'flex-start', backgroundColor: RED, borderRadius: 8, paddingVertical: 8, paddingHorizontal: 14, marginTop: 6, marginBottom: 4 }}>
      <Text style={{ color: '#FFF', fontWeight: '700', fontSize: 13 }}>{isProvider ? 'Send pay link' : `Pay ${'$' + group.balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} online`}</Text>
      <Text style={{ color: '#F3E7E7', fontSize: 10.5, marginTop: 1 }}>Card or bank transfer · processed by Stripe</Text>
    </TouchableOpacity>
  );
}

// A long ledger scrolls inside its own section instead of stretching the
// whole horse page. Short lists render as before.
const LEDGER_SCROLL_AFTER = 8;
function LedgerList({ children, count }) {
  // Phones only flash their scroll bar while moving, so a long ledger didn't
  // look scrollable. This draws our own always-visible bar showing where you are.
  const [viewH, setViewH] = useState(0);
  const [contentH, setContentH] = useState(0);
  const [offset, setOffset] = useState(0);
  if (count <= LEDGER_SCROLL_AFTER) return <>{children}</>;
  const scrollable = contentH > viewH + 1 && viewH > 0;
  const thumbH = scrollable ? Math.max(28, (viewH * viewH) / contentH) : 0;
  const thumbTop = scrollable ? (Math.min(Math.max(offset, 0), contentH - viewH) / (contentH - viewH)) * (viewH - thumbH) : 0;
  return (
    <>
      <View style={{ flexDirection: 'row' }}>
        <ScrollView
          style={{ maxHeight: 460, flex: 1 }}
          nestedScrollEnabled
          showsVerticalScrollIndicator={false}
          scrollEventThrottle={16}
          onLayout={(ev) => setViewH(ev.nativeEvent.layout.height)}
          onContentSizeChange={(w, h) => setContentH(h)}
          onScroll={(ev) => setOffset(ev.nativeEvent.contentOffset.y)}
        >
          {children}
        </ScrollView>
        {scrollable && (
          <View style={{ width: 5, marginLeft: 4, borderRadius: 3, backgroundColor: '#EDE7DD' }}>
            <View style={{ position: 'absolute', left: 0, right: 0, top: thumbTop, height: thumbH, borderRadius: 3, backgroundColor: '#A8998A' }} />
          </View>
        )}
      </View>
      <Text style={{ fontSize: 11, color: MUTED, textAlign: 'center', marginTop: 4 }}>{count} entries · scroll for more</Text>
    </>
  );
}

function LedgerProviderGroup({ group, fmt, filterEntries, paidTowardCharge, canEditEntry, onEdit, onDelete, defaultCollapsed, horseId, isProvider }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const color = providerTypeColor(group.name || 'Provider');
  const entries = group.displayEntries || filterEntries(group.entries);
  return (
    <View style={{ borderWidth: 0.5, borderColor: BORDER, borderLeftWidth: 3, borderLeftColor: color, borderRadius: 10, overflow: 'hidden', marginBottom: 10, backgroundColor: CARD }}>
      <TouchableOpacity onPress={() => setCollapsed((c) => !c)} activeOpacity={0.75} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 10 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1 }}>
          <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: color }} />
          <Text style={{ fontSize: 14, fontWeight: '600', color: TEXT }} numberOfLines={1}>{group.name}</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Text style={{ fontSize: 12, color: group.balance > 0 ? RED : GREEN, fontWeight: '600' }}>{group.balance > 0 ? fmt(group.balance) + ' owed' : 'Paid up'}</Text>
          <Text style={{ fontSize: 12, color: MUTED }}>{collapsed ? '▾' : '▴'}</Text>
        </View>
      </TouchableOpacity>
      {!collapsed && (
        <View style={{ paddingHorizontal: 8, paddingBottom: 6 }}>
          {!!horseId && <LedgerPayButton horseId={horseId} group={group} isProvider={isProvider} />}
          {entries.length === 0
            ? <Text style={styles.noNotesTxt}>Nothing outstanding.</Text>
            : <LedgerList count={entries.length}>{entries.map((e) => <LedgerRow key={e.id} e={e} fmt={fmt} paidTowardCharge={paidTowardCharge} canEdit={canEditEntry(e)} onEdit={onEdit} onDelete={onDelete} />)}</LedgerList>}
        </View>
      )}
    </View>
  );
}

// Uploading is allowed for the horse's owner and editors (matches storage.rules).
const canUploadForHorse = (horse, uid) =>
  !!uid && !!horse && (
    horse.ownerId === uid
    || (!horse.ownerId && horse.members?.[0] === uid)
    || (horse.editors || []).includes(uid)
  );

const uploadErrMsg = (e) =>
  e && (e.code === 'storage/unauthorized' || String(e.message || '').includes('storage/unauthorized'))
    ? 'Only the owner or an editor of this horse can upload or remove files.'
    : 'Could not upload. Please check your connection and try again.';

// Normalize picker results (native URI strings or web File objects) into
// { uri (displayable), payload (uploadable), name, mime } items.
const normalizePickedPhotos = (arr) =>
  arr.map((x) =>
    typeof x === 'string'
      ? { uri: x, payload: x, name: '', mime: 'image/jpeg' }
      : { uri: URL.createObjectURL(x), payload: x, name: x.name || '', mime: x.type || 'image/jpeg' }
  );

// Ask camera-or-library on native; straight to the file dialog on web.
// Any picker failure is surfaced to the user instead of silently doing nothing.
const runPicker = (source, onPicked) => {
  pickPhotosAsync(source)
    .then((u) => { if (u.length) onPicked(normalizePickedPhotos(u)); })
    .catch((e) => Alert.alert('Couldn’t add photo', e?.message || 'Something went wrong opening the photo picker. Please try again.'));
};

const choosePhotoSource = (onPicked) => {
  if (Platform.OS === 'web') {
    pickPhotosWebAsync().then((files) => { if (files.length) onPicked(normalizePickedPhotos(files)); });
    return;
  }
  Alert.alert('Add Photo', 'Where from?', [
    { text: 'Take Photo', onPress: () => runPicker('camera', onPicked) },
    { text: 'Choose from Library', onPress: () => runPicker('library', onPicked) },
    { text: 'Cancel', style: 'cancel' },
  ]);
};

// An image cropped to a fixed-height box, showing the part chosen by a stored
// focal point ({x,y} in 0..1; 0.5/0.5 = center). With `onChange` set, the user
// can drag the image inside the box to adjust the framing; with `onPress` it
// acts as a plain tappable image. The photo itself is never cropped on disk.
function AdjustableImage({ uri, focus, onChange, onPress, aspect = 1.6, maxWidth, borderRadius = 10, style }) {
  const [box, setBox] = useState(null); // measured { w, h }
  const [dims, setDims] = useState(null);
  const f = focus && typeof focus.x === 'number' ? focus : { x: 0.5, y: 0.5 };
  const zoom = Math.min(3, Math.max(1, f.zoom || 1));
  const geoRef = useRef(null);
  const focusRef = useRef(f);
  focusRef.current = f;
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const pinchRef = useRef(null); // { d0, z0 } while two fingers are down
  const pinchedRef = useRef(false); // suppress drag for the rest of a gesture after a pinch

  useEffect(() => {
    let live = true;
    Image.getSize(uri, (w, h) => { if (live) setDims({ w, h }); }, () => {});
    return () => { live = false; };
  }, [uri]);

  let inner = null;
  if (box && dims) {
    const scale = Math.max(box.w / dims.w, box.h / dims.h) * zoom;
    const sw = dims.w * scale;
    const sh = dims.h * scale;
    const maxX = Math.max(0, sw - box.w);
    const maxY = Math.max(0, sh - box.h);
    geoRef.current = { maxX, maxY };
    inner = (
      <Image
        source={{ uri }}
        style={{ position: 'absolute', width: sw, height: sh, left: -f.x * maxX, top: -f.y * maxY }}
      />
    );
  }

  const panRef = useRef(null);
  if (onChange && !panRef.current) {
    let start = null;
    panRef.current = PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: (e, g) => Math.abs(g.dx) > 2 || Math.abs(g.dy) > 2,
      onPanResponderGrant: () => { start = { x: focusRef.current.x, y: focusRef.current.y }; pinchRef.current = null; pinchedRef.current = false; },
      onPanResponderMove: (e, g) => {
        const geo = geoRef.current;
        if (!geo) return;
        const touches = e.nativeEvent.touches || [];
        if (touches.length >= 2) {
          // Pinch: scale zoom by the change in finger distance.
          const dx = touches[0].pageX - touches[1].pageX;
          const dy = touches[0].pageY - touches[1].pageY;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (!pinchRef.current) { pinchRef.current = { d0: d, z0: zoomRef.current }; pinchedRef.current = true; return; }
          const nz = Math.min(3, Math.max(1, pinchRef.current.z0 * (d / pinchRef.current.d0)));
          onChangeRef.current?.({ x: focusRef.current.x, y: focusRef.current.y, zoom: nz });
          return;
        }
        pinchRef.current = null;
        if (pinchedRef.current || !start) return;
        const nx = geo.maxX ? Math.min(1, Math.max(0, start.x - g.dx / geo.maxX)) : 0.5;
        const ny = geo.maxY ? Math.min(1, Math.max(0, start.y - g.dy / geo.maxY)) : 0.5;
        onChangeRef.current?.({ x: nx, y: ny, zoom: zoomRef.current });
      },
      onPanResponderRelease: () => { pinchRef.current = null; pinchedRef.current = false; },
    });
  }

  const stepZoom = (delta) => {
    const nz = Math.min(3, Math.max(1, Math.round((zoomRef.current + delta) * 4) / 4));
    onChangeRef.current?.({ x: focusRef.current.x, y: focusRef.current.y, zoom: nz });
  };

  const zoomBtn = { width: 30, height: 30, borderRadius: 15, backgroundColor: 'rgba(0,0,0,0.55)', alignItems: 'center', justifyContent: 'center' };
  const boxView = (
    <View style={[{ width: '100%', aspectRatio: aspect }, maxWidth ? { maxWidth, alignSelf: 'center' } : null, style]}>
      <View
        onLayout={(e) => setBox({ w: e.nativeEvent.layout.width, h: e.nativeEvent.layout.height })}
        style={{ width: '100%', height: '100%', borderRadius, overflow: 'hidden', backgroundColor: '#EBE7DF' }}
        {...(onChange && panRef.current ? panRef.current.panHandlers : {})}
      >
        {inner || (box ? <Image source={{ uri }} style={{ width: '100%', height: '100%' }} resizeMode="cover" /> : null)}
      </View>
      {!!onChange && (
        <View style={{ position: 'absolute', right: 8, bottom: 8, flexDirection: 'row', gap: 6 }}>
          <TouchableOpacity onPress={() => stepZoom(-0.25)} style={zoomBtn} activeOpacity={0.8}>
            <Text style={{ color: '#FFF', fontSize: 18, fontWeight: '700', lineHeight: 20 }}>−</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => stepZoom(0.25)} style={zoomBtn} activeOpacity={0.8}>
            <Text style={{ color: '#FFF', fontSize: 18, fontWeight: '700', lineHeight: 20 }}>+</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );

  if (onPress && !onChange) {
    return (
      <TouchableOpacity onPress={onPress} activeOpacity={0.9} style={{ width: '100%' }}>
        {boxView}
      </TouchableOpacity>
    );
  }
  return boxView;
}

// Shared Business Info editor for invoices (used from the ledger and the
// Client Horses screen). Account-wide — one profile per user, applied to every
// invoice. Includes an optional logo. Generic wording so it fits trainers,
// boarding facilities, or anyone billing a horse owner.
// Online payments (Stripe). The provider sets up their own Stripe account once;
// after that every new invoice carries a "Pay online" link whose amount is the
// client's live balance. Clients pay the provider directly.
// Plain statement of what a provider pays per online payment. The rate comes
// from the server, so changing PLATFORM_FEE_RATE there updates this text.
function OnlinePaymentFees({ status }) {
  const card = status?.platformFeeCard;
  const bank = status?.platformFeeBank;
  if (card == null) return null;
  const pct = (r) => `${+(r * 100).toFixed(2)}%`;
  const clientPays = status?.clientPaysCardFee !== false;
  return (
    <View style={{ marginTop: 14, padding: 10, borderRadius: 8, backgroundColor: '#F7F4EE' }}>
      <Text style={{ fontSize: 12.5, fontWeight: '700', color: TEXT, marginBottom: 4 }}>Fees per online payment</Text>
      <FeeDiscountNote status={status} />
      <Text style={{ fontSize: 12, color: MUTED, lineHeight: 17 }}>Horse Math service fee: {pct(card)} on card payments, {pct(bank)} on bank transfers — on the amount your client pays toward their bill. Stripe also charges its standard processing fee (stripe.com/pricing).</Text>
      {clientPays ? (
        <>
          <Text style={{ fontSize: 12, color: MUTED, lineHeight: 17, marginTop: 4 }}>• Credit card: your client pays a card processing fee; you pay the Horse Math {pct(card)} and any Stripe fee it doesn’t cover.</Text>
          <Text style={{ fontSize: 12, color: MUTED, lineHeight: 17 }}>• Debit card: card rules don’t allow a fee to the client, so you pay Stripe’s fee and the Horse Math {pct(card)}.</Text>
          <Text style={{ fontSize: 12, color: MUTED, lineHeight: 17 }}>• Bank transfer: your client pays a processing fee covering Stripe and Horse Math — you receive the full amount owed.</Text>
        </>
      ) : (
        <Text style={{ fontSize: 12, color: MUTED, lineHeight: 17, marginTop: 4 }}>You’ve chosen to cover fees: clients pay exactly what they owe, and Stripe’s fee and the Horse Math fee come out of what you receive.</Text>
      )}
      <Text style={{ fontSize: 12, color: MUTED, lineHeight: 17, marginTop: 4 }}>Each online payment in your ledger shows exactly what you paid and received. Horse Math fees are returned in proportion to any refund.</Text>
    </View>
  );
}

// A provider on a discounted Horse Math rate (set by admin) sees it spelled out.
function FeeDiscountNote({ status }) {
  if (!status?.feeDiscounted) return null;
  const pct = (r) => `${+((r || 0) * 100).toFixed(2)}%`;
  const until = status.feeDiscountUntil
    ? new Date(status.feeDiscountUntil + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : '';
  return (
    <Text style={{ fontSize: 12, color: GREEN, fontWeight: '700', lineHeight: 17, marginBottom: 4 }}>
      🎉 Early-adopter rate: {pct(status.platformFeeCard)} on cards, {pct(status.platformFeeBank)} on bank transfers
      {until ? ` until ${until}, then the standard ${pct(status.standardFeeCard)} / ${pct(status.standardFeeBank)}.` : ' (standard is ' + pct(status.standardFeeCard) + ' / ' + pct(status.standardFeeBank) + ').'}
    </Text>
  );
}

// Admin: set a lower Horse Math fee for particular providers (early adopters).
function FeeRatesModal({ visible, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // { ownerKey?, email?, name }
  const [card, setCard] = useState('');
  const [bank, setBank] = useState('');
  const [until, setUntil] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const pct = (r) => `${+((r || 0) * 100).toFixed(2)}%`;
  const load = () => { setError(''); callAdminListFeeRates().then(setData).catch((e) => setError(e?.message || 'Could not load.')); };
  useEffect(() => { if (visible) { setEditing(null); setEmail(''); load(); } }, [visible]);
  const startEdit = (p) => {
    const o = p.override;
    setEditing(p);
    setCard(o ? String(+(o.card * 100).toFixed(4)) : '0');
    setBank(o ? String(+(o.bank * 100).toFixed(4)) : '0');
    setUntil(o?.until || '');
  };
  const save = (clear) => {
    setBusy(true);
    const target = editing.ownerKey ? { ownerKey: editing.ownerKey, name: editing.name } : { email: editing.email };
    const args = clear ? { ...target, clear: true } : { ...target, card: Number(card) / 100, bank: Number(bank) / 100, until: until.trim() };
    callAdminSetFeeRate(args)
      .then(() => { setEditing(null); setEmail(''); load(); })
      .catch((e) => Alert.alert('Not saved', e?.message || 'Try again.'))
      .finally(() => setBusy(false));
  };
  const std = data?.standard;
  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <View style={[styles.sheet, { maxHeight: '90%' }]}>
          <ScrollView keyboardShouldPersistTaps="handled">
            <Text style={styles.sheetTitle}>Provider fee rates</Text>
            {!editing ? (
              <>
                <Text style={{ fontSize: 13, color: MUTED, lineHeight: 19, marginBottom: 10 }}>
                  Standard Horse Math fee: {std ? `${pct(std.card)} cards, ${pct(std.bank)} bank transfers` : '…'}. Give a provider a lower rate, with an optional end date — after it they go back to standard automatically. Rates can only be lowered here.
                </Text>
                {!!error && <Text style={{ color: '#B00020', marginBottom: 8 }}>{error}</Text>}
                {!data && !error && <ActivityIndicator color={RED} style={{ marginVertical: 16 }} />}
                {(data?.providers || []).map((p) => (
                  <TouchableOpacity key={p.ownerKey} onPress={() => startEdit(p)} activeOpacity={0.75}
                    style={{ borderTopWidth: 0.5, borderTopColor: BORDER, paddingVertical: 10, flexDirection: 'row', alignItems: 'center' }}>
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: 14, fontWeight: '700', color: TEXT }}>{p.name || p.ownerKey}</Text>
                      <Text style={{ fontSize: 12, color: MUTED }}>
                        {p.connected ? (p.ready ? 'Taking payments' : 'Setup unfinished') : 'Not set up yet'}
                        {' · '}
                        {p.override
                          ? `${pct(p.override.card)} / ${pct(p.override.bank)}${p.override.until ? ` until ${p.override.until}` : ''}${p.active ? '' : ' (ended)'}`
                          : 'Standard rate'}
                      </Text>
                    </View>
                    <Text style={styles.sectionCardChevron}>›</Text>
                  </TouchableOpacity>
                ))}
                {data && data.providers.length === 0 && <Text style={{ fontSize: 13, color: MUTED }}>No providers have set up online payments yet.</Text>}
                <Text style={[styles.fieldLabel, { marginTop: 16 }]}>Someone not set up yet? Their sign-in email</Text>
                <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                  <TextInput style={[styles.input, { flex: 1, marginBottom: 0 }]} value={email} onChangeText={setEmail}
                    placeholder="provider@example.com" placeholderTextColor="#BDBDBD" autoCapitalize="none" keyboardType="email-address" />
                  <TouchableOpacity onPress={() => email.trim() && startEdit({ email: email.trim(), name: email.trim() })} activeOpacity={0.8}
                    style={{ backgroundColor: RED, borderRadius: 8, paddingVertical: 10, paddingHorizontal: 14 }}>
                    <Text style={{ color: CREAM, fontWeight: '700' }}>Set rate</Text>
                  </TouchableOpacity>
                </View>
                <TouchableOpacity onPress={onClose} style={{ alignItems: 'center', marginTop: 18 }} activeOpacity={0.7}>
                  <Text style={{ color: MUTED, fontSize: 15 }}>Close</Text>
                </TouchableOpacity>
              </>
            ) : (
              <>
                <Text style={{ fontSize: 15, fontWeight: '700', color: TEXT, marginBottom: 8 }}>{editing.name || editing.email}</Text>
                <Text style={styles.fieldLabel}>Card payments (%) — standard {std ? pct(std.card) : ''}</Text>
                <TextInput style={styles.input} value={card} onChangeText={setCard} keyboardType="decimal-pad" placeholder="0.25" placeholderTextColor="#BDBDBD" />
                <Text style={styles.fieldLabel}>Bank transfers (%) — standard {std ? pct(std.bank) : ''}</Text>
                <TextInput style={styles.input} value={bank} onChangeText={setBank} keyboardType="decimal-pad" placeholder="0.5" placeholderTextColor="#BDBDBD" />
                <Text style={styles.fieldLabel}>Ends on (optional, YYYY-MM-DD)</Text>
                <TextInput style={styles.input} value={until} onChangeText={setUntil} placeholder="2027-01-01" placeholderTextColor="#BDBDBD" autoCapitalize="none" />
                <Text style={{ fontSize: 12, color: MUTED, lineHeight: 17, marginBottom: 12 }}>
                  Applies to new payments right away. Leave the end date blank to keep this rate until you change it.
                </Text>
                <TouchableOpacity onPress={() => save(false)} disabled={busy} activeOpacity={0.85}
                  style={{ backgroundColor: RED, borderRadius: 10, paddingVertical: 13, alignItems: 'center', opacity: busy ? 0.6 : 1 }}>
                  <Text style={{ color: CREAM, fontWeight: '700', fontSize: 15 }}>{busy ? 'Saving…' : 'Save rate'}</Text>
                </TouchableOpacity>
                {!!editing.override && (
                  <TouchableOpacity onPress={() => save(true)} disabled={busy} style={{ alignItems: 'center', marginTop: 14 }} activeOpacity={0.7}>
                    <Text style={{ color: '#B00020', fontWeight: '600' }}>Back to standard rate</Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity onPress={() => setEditing(null)} style={{ alignItems: 'center', marginTop: 14 }} activeOpacity={0.7}>
                  <Text style={{ color: MUTED, fontSize: 15 }}>Back</Text>
                </TouchableOpacity>
              </>
            )}
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

// The fees a provider must read and accept before taking online payments (and
// again whenever the terms change). Rates come from the server.
function FeeAcknowledgementModal({ visible, status, onCancel, onAgree, busy }) {
  const [checked, setChecked] = useState(false);
  useEffect(() => { if (visible) setChecked(false); }, [visible]);
  const pct = (r) => `${+((r || 0) * 100).toFixed(2)}%`;
  const card = status?.platformFeeCard;
  const bank = status?.platformFeeBank;
  const Row = ({ label, client, you }) => (
    <View style={{ borderTopWidth: 0.5, borderTopColor: BORDER, paddingVertical: 8 }}>
      <Text style={{ fontSize: 13, fontWeight: '700', color: TEXT }}>{label}</Text>
      <Text style={{ fontSize: 12.5, color: MUTED, lineHeight: 18 }}>Your client pays: {client}</Text>
      <Text style={{ fontSize: 12.5, color: MUTED, lineHeight: 18 }}>You pay: {you}</Text>
    </View>
  );
  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onCancel}>
      <View style={styles.overlay}>
        <View style={[styles.sheet, { maxHeight: '90%' }]}>
          <ScrollView>
            <Text style={styles.sheetTitle}>Online payment fees</Text>
            <FeeDiscountNote status={status} />
            <Text style={{ fontSize: 13, color: TEXT, lineHeight: 19, marginBottom: 10 }}>
              Horse Math charges a service fee on each online payment: <Text style={{ fontWeight: '700' }}>{pct(card)} on card payments</Text> and <Text style={{ fontWeight: '700' }}>{pct(bank)} on bank transfers</Text>, on the amount your client pays toward their bill. It's deducted automatically through Stripe. Stripe also charges its own processing fee.
            </Text>
            <Text style={{ fontSize: 13, fontWeight: '700', color: TEXT, marginTop: 4, marginBottom: 2 }}>If clients pay processing fees (the default):</Text>
            <Row label="Credit card" client={`the amount owed + a card processing fee (up to 3%), shown before they pay`} you={`the Horse Math ${pct(card)} and any part of Stripe's fee the card processing fee doesn't cover`} />
            <Row label="Debit card" client="exactly the amount owed (card rules don't allow a fee)" you={`Stripe's fee + the Horse Math ${pct(card)}`} />
            <Row label="Bank transfer" client={`the amount owed + a processing fee covering Stripe and the Horse Math ${pct(bank)}`} you="nothing — you receive the full amount owed" />
            <Text style={{ fontSize: 13, fontWeight: '700', color: TEXT, marginTop: 12, marginBottom: 2 }}>If you choose to cover fees yourself:</Text>
            <Text style={{ fontSize: 12.5, color: MUTED, lineHeight: 18 }}>Your client pays exactly what they owe. You pay Stripe's fee plus the Horse Math fee ({pct(card)} cards, {pct(bank)} bank transfers). You can switch any time in Online payments.</Text>
            <Text style={{ fontSize: 12.5, color: MUTED, lineHeight: 18, marginTop: 10 }}>
              The Horse Math fee is returned in proportion to any refund. We'll give at least 30 days' notice before raising it, and it will never be more than 1.5%. You're responsible for making sure passing fees to clients is allowed where you do business. Full details are in the Terms of Service (horsemath.app/terms), section 6.
            </Text>
            <TouchableOpacity onPress={() => setChecked((v) => !v)} activeOpacity={0.75} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginTop: 14 }}>
              <Text style={{ fontSize: 20 }}>{checked ? '☑️' : '⬜️'}</Text>
              <Text style={{ flex: 1, fontSize: 13, color: TEXT, lineHeight: 19 }}>I understand these fees, when I pay them and when my clients pay them, and I agree to them and to the Horse Math Terms of Service.</Text>
            </TouchableOpacity>
          </ScrollView>
          <View style={styles.sheetBtns}>
            <TouchableOpacity style={styles.cancelBtn} onPress={onCancel}><Text style={styles.cancelBtnText}>Cancel</Text></TouchableOpacity>
            <TouchableOpacity style={[styles.saveBtn, (!checked || busy) && { opacity: 0.5 }]} disabled={!checked || busy} onPress={onAgree}>
              {busy ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>Agree</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function OnlinePaymentsSection({ visible, hasBusinessName }) {
  const [status, setStatus] = useState(null); // null = loading
  const [busy, setBusy] = useState(false);
  const [showFees, setShowFees] = useState(null); // null | 'connect' | 'accept'
  const load = () => {
    setStatus(null);
    callStripeStatus().then(setStatus).catch(() => setStatus({ error: true }));
  };
  useEffect(() => { if (visible && hasBusinessName && canSeeOnlinePayments()) load(); }, [visible, hasBusinessName]);
  const start = async (restart = false, acceptFees = false) => {
    // Fees must be accepted (once, and again if the terms change) before setup.
    if (!acceptFees && !status?.feeTermsAccepted) { setShowFees(restart ? 'restart' : 'connect'); return; }
    setBusy(true);
    try {
      const { url } = await callStripeConnect(restart, acceptFees);
      setShowFees(null);
      if (Platform.OS === 'web') window.open(url, '_blank'); else await Linking.openURL(url);
    } catch (e) {
      Alert.alert('Could not open Stripe', e?.message || 'Please check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };
  if (!hasBusinessName || !canSeeOnlinePayments()) return null;
  const ready = status?.ready;
  const acceptOnly = async () => {
    setBusy(true);
    try { await callStripeAcceptFees(); setShowFees(null); load(); }
    catch (e) { Alert.alert('Could not save', e?.message || 'Please try again.'); }
    finally { setBusy(false); }
  };
  return (
    <View style={{ marginTop: 20, padding: 14, borderRadius: 10, borderWidth: 0.5, borderColor: BORDER, backgroundColor: CARD }}>
      <FeeAcknowledgementModal
        visible={!!showFees}
        status={status}
        busy={busy}
        onCancel={() => setShowFees(null)}
        onAgree={() => (showFees === 'accept' ? acceptOnly() : start(showFees === 'restart', true))}
      />
      <Text style={{ fontSize: 15, fontWeight: '700', color: TEXT, marginBottom: 4 }}>💳 Online payments</Text>
      {status && status.connected && !status.feeTermsAccepted && (
        <TouchableOpacity onPress={() => setShowFees('accept')} activeOpacity={0.8} style={{ backgroundColor: '#FBF3E2', borderRadius: 8, borderWidth: 0.5, borderColor: '#E4CF9C', padding: 10, marginBottom: 8 }}>
          <Text style={{ fontSize: 13, fontWeight: '700', color: '#8A6D2F' }}>Please review and accept the online payment fees</Text>
          <Text style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>New invoices won't include a Pay online link until you do. Tap to review.</Text>
        </TouchableOpacity>
      )}
      {status === null ? (
        <ActivityIndicator style={{ alignSelf: 'flex-start', marginVertical: 6 }} color={RED} />
      ) : ready ? (
        <>
          <Text style={{ fontSize: 13, color: GREEN, fontWeight: '700' }}>Ready — new invoices include a Pay online link.</Text>
          <Text style={{ fontSize: 12, color: MUTED, marginTop: 4, lineHeight: 17 }}>Clients pay you directly by card or bank transfer; payments are added to the ledger automatically. Refunds and disputes you handle in your Stripe dashboard update the ledger too.</Text>

          <Text style={{ fontSize: 13, fontWeight: '700', color: TEXT, marginTop: 14, marginBottom: 6 }}>Who pays processing fees?</Text>
          {status.cardFeeBanned ? (
            <Text style={{ fontSize: 12, color: MUTED, lineHeight: 17 }}>Card fees can't be passed to clients in your state ({status.providerState}), so you cover them.</Text>
          ) : (
            <>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                {[{ v: true, l: 'Client' }, { v: false, l: 'Me' }].map((o) => (
                  <TouchableOpacity
                    key={o.l}
                    onPress={() => {
                      if (status.clientPaysCardFee === o.v) return;
                      setStatus({ ...status, clientPaysCardFee: o.v });
                      callStripeSetFeePolicy(o.v).then(load).catch(() => { Alert.alert('Could not save', 'Please try again.'); load(); });
                    }}
                    activeOpacity={0.75}
                    style={[styles.categoryChip, status.clientPaysCardFee === o.v && styles.categoryChipActive]}
                  >
                    <Text style={[styles.categoryChipTxt, status.clientPaysCardFee === o.v && styles.categoryChipTxtActive]}>{o.l}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              <Text style={{ fontSize: 12, color: MUTED, marginTop: 6, lineHeight: 17 }}>
                {status.clientPaysCardFee
                  ? (status.cardFeeRate > 0
                    ? `Clients see the fee before they pay: ${+(status.cardFeeRate * 100).toFixed(2)}% on credit cards, about ${+((status.bankClientFeeRate || 0) * 100).toFixed(2)}% on bank transfers, none on debit cards.`
                    : `Your business state isn’t confirmed with Stripe yet, so no credit card fee is added for now. Bank transfers include about ${+((status.bankClientFeeRate || 0) * 100).toFixed(2)}%.`)
                  : 'You cover all fees — clients pay exactly what they owe.'}
              </Text>
            </>
          )}
          <OnlinePaymentFees status={status} />
          {!!status.accountId && <Text selectable style={{ fontSize: 11, color: MUTED, marginTop: 8 }}>Stripe account: {status.accountId}</Text>}
        </>
      ) : (
        <>
          <Text style={{ fontSize: 12.5, color: MUTED, lineHeight: 18 }}>
            {status?.connected
              ? (status.needsUser > 0
                ? 'Stripe needs a bit more information before you can take payments. Continue setup to see what’s missing.'
                : status.awaitingStripe > 0
                  ? 'Stripe is reviewing your details. This is usually quick — check again in a few minutes.'
                  : 'Your Stripe setup isn’t finished yet. Continue where you left off.')
              : 'Let clients pay invoices online by card or bank transfer. Money goes straight to your bank through Stripe.'}
          </Text>
          {!status?.connected && status !== null && <OnlinePaymentFees status={status} />}
          {(status?.errors || []).map((m, i) => (
            <Text key={i} style={{ fontSize: 12, color: '#B00020', marginTop: 6, lineHeight: 17 }}>• {m}</Text>
          ))}
          <TouchableOpacity style={[styles.saveBtn, { marginTop: 10, opacity: busy ? 0.6 : 1 }]} onPress={() => start(false)} disabled={busy} activeOpacity={0.85}>
            {busy ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>{status?.connected ? 'Continue Stripe setup' : 'Set up online payments'}</Text>}
          </TouchableOpacity>
          {status?.connected && (
            <>
              <TouchableOpacity onPress={load} activeOpacity={0.7} style={{ marginTop: 8 }}>
                <Text style={{ fontSize: 12.5, color: GREEN, fontWeight: '600' }}>I finished — check again</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => Alert.alert('Start over?', 'This abandons the unfinished Stripe setup so you can begin again — for example with a different email.', [
                  { text: 'Cancel', style: 'cancel' },
                  { text: 'Start Over', onPress: () => start(true) },
                ])}
                activeOpacity={0.7}
                style={{ marginTop: 8 }}
              >
                <Text style={{ fontSize: 12.5, color: MUTED, fontWeight: '600' }}>Start over with a different email</Text>
              </TouchableOpacity>
            </>
          )}
        </>
      )}
    </View>
  );
}

function BusinessProfileModal({ visible, onClose, uid, initialProfile, company, onSaved }) {
  const b = initialProfile || {};
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [terms, setTerms] = useState('');
  const [providerType, setProviderType] = useState('Trainer');
  const [providerTypeCustom, setProviderTypeCustom] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoPath, setLogoPath] = useState('');
  const [logoPending, setLogoPending] = useState(null); // freshly picked, not yet uploaded
  const [saving, setSaving] = useState(false);
  // Team (shared company): link colleagues so they bill under one name
  const [teamEmail, setTeamEmail] = useState('');
  const [teamBusy, setTeamBusy] = useState(false);
  const myUid = auth.currentUser?.uid;
  const myMember = { uid: myUid, name: auth.currentUser?.displayName || auth.currentUser?.email || '', email: auth.currentUser?.email || '' };
  const currentFields = () => ({ name: name.trim(), email: email.trim(), phone: phone.trim(), address: address.trim(), terms: terms.trim(), logoUrl: logoUrl || '', logoPath: logoPath || '' });
  // Make sure a company exists (create from the current form on first use), so a
  // colleague can be linked even before the profile has been formally saved.
  const ensureCompanyId = async () => {
    if (company?.id) return company.id;
    if (!name.trim()) { Alert.alert('Name required', 'Enter your business name first, then add team members.'); return null; }
    const cid = await saveCompanyProfile(uid, null, myMember, currentFields());
    await saveBusinessProfile(uid, { ...currentFields(), companyId: cid });
    return cid;
  };
  const addTeamMember = async () => {
    const em = teamEmail.trim().toLowerCase();
    if (!em) return;
    if (em === auth.currentUser?.email?.toLowerCase()) { Alert.alert("That's you", "You're already part of your company."); return; }
    setTeamBusy(true);
    try {
      const found = await findUserByEmail(em);
      if (!found) { Alert.alert('User not found', USER_NOT_FOUND_MSG); return; }
      if (!(await confirmSharingMatch(found, em))) return;
      if ((company?.memberUids || []).includes(found.uid)) { Alert.alert('Already linked', 'That person is already part of your company.'); return; }
      const cid = await ensureCompanyId();
      if (!cid) return;
      await addCompanyMember(cid, { uid: found.uid, name: found.displayName || found.email, email: found.email });
      setTeamEmail('');
    } catch {
      Alert.alert('Error', 'Could not link that person. Please try again.');
    } finally {
      setTeamBusy(false);
    }
  };
  const removeTeamMember = (m) => {
    if (!company?.id) return;
    Alert.alert('Remove from company', `Remove ${m.name || m.email} from ${company.name || 'your company'}? Their past charges & notes stay; new ones will no longer group with yours.`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Remove', style: 'destructive', onPress: () => removeCompanyMember(company, m.uid).catch(() => Alert.alert('Error', 'Could not remove. Please try again.')) },
    ]);
  };

  useEffect(() => {
    if (!visible) return;
    setName(b.name || '');
    setEmail(b.email || auth.currentUser?.email || '');
    setPhone(b.phone || '');
    setAddress(b.address || '');
    setTerms(b.terms || '');
    const pt = b.providerType || 'Trainer';
    const preset = PROVIDER_TYPE_PRESETS.includes(pt);
    setProviderType(preset ? pt : 'Other');
    setProviderTypeCustom(preset ? '' : pt);
    setLogoUrl(b.logoUrl || '');
    setLogoPath(b.logoPath || '');
    setLogoPending(null);
  }, [visible]);

  const pickLogo = () => choosePhotoSource((items) => setLogoPending(items[0]));

  const save = async () => {
    if (!name.trim()) { Alert.alert('Name required', 'Enter a business name to show on invoices.'); return; }
    setSaving(true);
    try {
      let url = logoUrl, path = logoPath;
      if (logoPending) {
        const meta = await uploadBusinessLogoAsync(uid, logoPending.payload, logoPending.mime);
        if (logoPath && logoPath !== meta.path) deleteFileByUrl(logoPath);
        url = meta.url; path = meta.path;
      } else if (!logoUrl && b.logoPath) {
        deleteFileByUrl(b.logoPath); // logo was removed
        path = '';
      }
      const profile = { name: name.trim(), email: email.trim(), phone: phone.trim(), address: address.trim(), terms: terms.trim(), logoUrl: url || '', logoPath: path || '' };
      // Write the shared company profile (any member may edit it), then keep the
      // user's own copy + a pointer to their company.
      const cid = await saveCompanyProfile(uid, company, myMember, profile);
      await saveBusinessProfile(uid, { ...profile, companyId: cid });
      onSaved && onSaved(profile);
      onClose();
    } catch (e) {
      Alert.alert('Error', 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const shownLogo = logoPending ? logoPending.uri : logoUrl;
  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <View style={[styles.sheet, { maxHeight: '90%' }]}>
          <ScrollView keyboardShouldPersistTaps="handled">
            <Text style={styles.sheetTitle}>Business Info & Payments</Text>
            <Text style={[styles.fieldLabel, { marginBottom: 8 }]}>Shown at the top of every invoice you send. Saved to your account and used for all your client horses. Works for a training business, boarding facility, or anyone billing an owner.</Text>

            {isPhotoPickingAvailable() && (
              <>
                <Text style={styles.fieldLabel}>Logo (optional)</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14, marginBottom: 8 }}>
                  {shownLogo ? (
                    <Image source={{ uri: shownLogo }} style={{ width: 64, height: 64, borderRadius: 8, backgroundColor: '#EBE7DF' }} />
                  ) : (
                    <View style={{ width: 64, height: 64, borderRadius: 8, backgroundColor: '#F1EFE8', alignItems: 'center', justifyContent: 'center' }}><Text style={{ fontSize: 22 }}>🏷️</Text></View>
                  )}
                  <View style={{ gap: 10 }}>
                    <TouchableOpacity onPress={pickLogo} activeOpacity={0.7}><Text style={styles.editNameBtnTxt}>{shownLogo ? 'Change Logo' : 'Add Logo'}</Text></TouchableOpacity>
                    {!!shownLogo && <TouchableOpacity onPress={() => { setLogoPending(null); setLogoUrl(''); }} activeOpacity={0.7}><Text style={{ color: RED, fontWeight: '600', fontSize: 13 }}>Remove</Text></TouchableOpacity>}
                  </View>
                </View>
              </>
            )}

            <Text style={styles.fieldLabel}>Business Name</Text>
            <TextInput style={styles.input} placeholder="e.g. Anderson Horse Training" placeholderTextColor="#BDBDBD" value={name} onChangeText={setName} />
            <Text style={{ fontSize: 11, color: MUTED, marginBottom: 4 }}>Shown on your invoices and as your section when a horse has more than one provider.</Text>
            <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Email</Text>
            <TextInput style={styles.input} placeholder="you@business.com" placeholderTextColor="#BDBDBD" keyboardType="email-address" autoCapitalize="none" value={email} onChangeText={setEmail} />
            <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Phone</Text>
            <TextInput style={styles.input} placeholder="(555) 555-5555" placeholderTextColor="#BDBDBD" keyboardType="phone-pad" value={phone} onChangeText={setPhone} />
            <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Address (optional)</Text>
            <TextInput style={styles.input} placeholder="Street, City, State" placeholderTextColor="#BDBDBD" value={address} onChangeText={setAddress} />
            <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Default Payment Terms (optional)</Text>
            <TextInput style={[styles.input, styles.inputMultiline]} placeholder="e.g. Payment due within 15 days. Venmo @anderson-horses" placeholderTextColor="#BDBDBD" multiline value={terms} onChangeText={setTerms} />
            <Text style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>Pre-fills the notes on each invoice — you can still edit or add notes per invoice.</Text>

            {/* ── Online payments: above the team, so it's easy to find ── */}
            <OnlinePaymentsSection visible={visible} hasBusinessName={!!(initialProfile?.name || company?.name)} />

            {/* ── Team: colleagues who share this company identity ── */}
            <View style={{ marginTop: 22, paddingTop: 16, borderTopWidth: 0.5, borderTopColor: BORDER }}>
              <Text style={[styles.fieldLabel, { fontSize: 14, fontWeight: '700', color: TEXT }]}>Team</Text>
              <Text style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>
                Link colleagues to {name.trim() || 'your company'}. Everyone linked bills under this one profile — their charges & notes group together on every horse, now and in the future. You still share each client's horse separately.
              </Text>

              {Object.values((company?.members || [{ ...myMember }]).reduce((acc, m) => { if (m?.uid && !acc[m.uid]) acc[m.uid] = m; return acc; }, {})).map((m) => {
                const isFounder = company ? m.uid === company.founderUid : m.uid === myUid;
                const isMe = m.uid === myUid;
                return (
                  <View key={m.uid} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 0.5, borderBottomColor: BORDER }}>
                    <View style={{ width: 30, height: 30, borderRadius: 15, backgroundColor: '#EDE7DA', alignItems: 'center', justifyContent: 'center', marginRight: 10 }}>
                      <Text style={{ fontSize: 13, fontWeight: '700', color: '#7A6A4F' }}>{(m.name || m.email || '?').trim().charAt(0).toUpperCase()}</Text>
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: 14, fontWeight: '600', color: TEXT }}>{m.name || m.email}{isMe ? ' (you)' : ''}</Text>
                      {!!m.email && m.email !== m.name && <Text style={{ fontSize: 12, color: MUTED }}>{m.email}</Text>}
                    </View>
                    {isFounder ? (
                      <Text style={{ fontSize: 11, color: MUTED, fontWeight: '600' }}>Owner</Text>
                    ) : (
                      <TouchableOpacity onPress={() => removeTeamMember(m)} activeOpacity={0.7} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                        <Text style={{ fontSize: 13, color: RED, fontWeight: '600' }}>Remove</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                );
              })}

              <Text style={[styles.fieldLabel, { marginTop: 14 }]}>Add a colleague by email</Text>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <TextInput
                  style={[styles.input, { flex: 1, marginBottom: 0 }]}
                  placeholder="colleague@email.com"
                  placeholderTextColor="#BDBDBD"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  value={teamEmail}
                  onChangeText={setTeamEmail}
                />
                <TouchableOpacity style={[styles.saveBtn, { paddingHorizontal: 18, opacity: teamBusy ? 0.6 : 1 }]} onPress={addTeamMember} disabled={teamBusy} activeOpacity={0.85}>
                  {teamBusy ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>Link</Text>}
                </TouchableOpacity>
              </View>
            </View>

            <View style={[styles.sheetBtns, { marginTop: 24, borderTopWidth: 0.5, borderTopColor: BORDER, paddingTop: 16 }]}>
              <TouchableOpacity style={styles.cancelBtn} onPress={onClose} activeOpacity={0.85}><Text style={styles.cancelBtnText}>Cancel</Text></TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={save} disabled={saving} activeOpacity={0.85}>
                {saving ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>Save</Text>}
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

// Full-screen photo viewer (tap anywhere to close).
function PhotoViewerModal({ url, onClose, note, onEditNote }) {
  const [busy, setBusy] = useState(false);
  const share = async () => {
    if (!url) return;
    setBusy(true);
    try { await shareOrDownloadFile({ url, mime: 'image/jpeg' }); }
    catch { Alert.alert('Error', 'Could not share this photo. Please try again.'); }
    finally { setBusy(false); }
  };
  return (
    <Modal visible={!!url} transparent animationType="fade" onRequestClose={onClose}>
      <TouchableOpacity style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.92)', justifyContent: 'center' }} activeOpacity={1} onPress={onClose}>
        {!!url && <Image source={{ uri: url }} style={{ width: '100%', height: onEditNote || note ? '72%' : '85%' }} resizeMode="contain" />}
        {!!note && (
          <Text style={{ color: '#E8E2D2', fontSize: 14, lineHeight: 20, paddingHorizontal: 24, marginTop: 12 }}>{note}</Text>
        )}
        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 20, marginTop: 14 }}>
          {!!onEditNote && (
            <TouchableOpacity onPress={onEditNote} activeOpacity={0.7} style={{ borderWidth: 1, borderColor: '#666', borderRadius: 8, paddingVertical: 8, paddingHorizontal: 18 }}>
              <Text style={{ color: '#F5EDD8', fontSize: 14, fontWeight: '600' }}>{note ? 'Edit note' : 'Add note'}</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity onPress={share} disabled={busy} activeOpacity={0.7} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, borderWidth: 1, borderColor: '#666', borderRadius: 8, paddingVertical: 8, paddingHorizontal: 18 }}>
            {busy ? <ActivityIndicator color="#EEE" /> : <Text style={{ color: '#F5EDD8', fontSize: 14, fontWeight: '600' }}>{Platform.OS === 'web' ? '⬇  Download' : '⤴  Share'}</Text>}
          </TouchableOpacity>
          <TouchableOpacity onPress={onClose} activeOpacity={0.7} style={{ paddingVertical: 8, paddingHorizontal: 14 }}>
            <Text style={{ color: '#BBB', fontSize: 14 }}>Close</Text>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    </Modal>
  );
}

// Horizontal strip of photo thumbnails. `photos` items may be stored metadata
// ({ url }) or pending local items ({ uri }). Optional add tile and per-photo ✕.
function PhotoStrip({ photos, onOpen, onAdd, onRemove, size = 76 }) {
  if ((!photos || photos.length === 0) && !onAdd) return null;
  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 8 }}>
      {(photos || []).map((p, i) => {
        const uri = p.url || p.uri;
        return (
          <View key={p.path || uri || i} style={{ marginRight: 8 }}>
            <TouchableOpacity onPress={() => onOpen && onOpen(uri)} activeOpacity={0.8}>
              <Image source={{ uri }} style={{ width: size, height: size, borderRadius: 8, backgroundColor: '#EBE7DF' }} />
            </TouchableOpacity>
            {!!onRemove && (
              <TouchableOpacity
                onPress={() => onRemove(p, i)}
                style={{ position: 'absolute', top: -6, right: -6, backgroundColor: '#FFF', borderRadius: 10, width: 20, height: 20, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: '#DDD' }}
                activeOpacity={0.8}
              >
                <Text style={{ fontSize: 11, color: RED, fontWeight: '700' }}>✕</Text>
              </TouchableOpacity>
            )}
          </View>
        );
      })}
      {!!onAdd && (
        <TouchableOpacity
          onPress={onAdd}
          style={{ width: size, height: size, borderRadius: 8, borderWidth: 1.5, borderStyle: 'dashed', borderColor: '#B9B2A6', alignItems: 'center', justifyContent: 'center', backgroundColor: '#FAF8F3' }}
          activeOpacity={0.75}
        >
          <Text style={{ fontSize: 22, color: MUTED }}>＋</Text>
          <Text style={{ fontSize: 10, color: MUTED }}>Photo</Text>
        </TouchableOpacity>
      )}
    </ScrollView>
  );
}

function HorseScreen({ route, navigation }) {
  const { horseId } = route.params;
  const [horse, setHorse] = useState(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameInput, setRenameInput] = useState('');
  const [noteEntries, setNoteEntries] = useState([]);
  const [notesLoaded, setNotesLoaded] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [newNoteTag, setNewNoteTag] = useState('');
  const [newNoteTagOther, setNewNoteTagOther] = useState('');
  const [noteFilter, setNoteFilter] = useState('all'); // 'all' | 'general' | 'health'
  const [customNoteTags, setCustomNoteTags] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [details, setDetails] = useState(null);
  const [detailsPhotoPending, setDetailsPhotoPending] = useState(null); // picked-but-unsaved profile photo
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsDate, setDetailsDate] = useState(new Date());
  const [showDetailsDatePicker, setShowDetailsDatePicker] = useState(false);
  const [allHorses, setAllHorses] = useState([]);
  const [pickerFor, setPickerFor] = useState(null); // 'sire' | 'dam' | null
  // Saved stallions, offered when picking a sire.
  const [studs, setStuds] = useState([]);
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    return subscribeToStuds(uid, setStuds);
  }, []);
  const [pickerSearch, setPickerSearch] = useState('');
  const [showAddMilestoneModal, setShowAddMilestoneModal] = useState(false);
  const [milestoneName, setMilestoneName] = useState('');
  const [milestoneDate, setMilestoneDate] = useState(new Date());
  const [showMilestoneDatePicker, setShowMilestoneDatePicker] = useState(false);
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderMode, setReminderMode] = useState('specified'); // 'specified' | 'custom'
  const [reminderDayBefore, setReminderDayBefore] = useState(false); // a push the day before, at 8am local
  const [reminderRepeat, setReminderRepeat] = useState(false); // repeat daily until completed
  const [reminderDate, setReminderDate] = useState(new Date());
  const [showReminderDatePicker, setShowReminderDatePicker] = useState(false);
  const [showReminderTimePicker, setShowReminderTimePicker] = useState(false);
  const [milestoneHorseIds, setMilestoneHorseIds] = useState([]);
  const [milestoneCategory, setMilestoneCategory] = useState('');
  const [milestoneCategoryOther, setMilestoneCategoryOther] = useState('');
  const [milestoneNotes, setMilestoneNotes] = useState('');
  const [milestoneAddAsComplete, setMilestoneAddAsComplete] = useState(false); // log a task that's already done
  const [editingMilestoneId, setEditingMilestoneId] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [showHistory, setShowHistory] = useState(true);
  const [showAllCompleted, setShowAllCompleted] = useState(false);
  const [customCategories, setCustomCategories] = useState([]);
  const [hiddenCategories, setHiddenCategories] = useState([]);
  const [hiddenNoteTags, setHiddenNoteTags] = useState([]);
  const [customLocations, setCustomLocations] = useState([]);
  const [showAddLocationEdit, setShowAddLocationEdit] = useState(false);
  const [newLocationTextEdit, setNewLocationTextEdit] = useState('');
  const [showMarkBornModal, setShowMarkBornModal] = useState(false);
  // Mark as Sold (a sale where the horse is NOT transferred to the buyer's account)
  // Client horse on/off from Edit Details (for when it wasn't ticked on adding).
  const [detailsIsClient, setDetailsIsClient] = useState(false);
  const [convOwnerName, setConvOwnerName] = useState('');
  const [convOwnerEmail, setConvOwnerEmail] = useState('');
  const [convOwnerPhone, setConvOwnerPhone] = useState('');
  const [showSoldModal, setShowSoldModal] = useState(false);
  const [soldDate, setSoldDate] = useState(null); // must be picked — no default
  const [showSoldDatePicker, setShowSoldDatePicker] = useState(false);
  const [soldTo, setSoldTo] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [soldNotes, setSoldNotes] = useState('');
  const [soldSaving, setSoldSaving] = useState(false);
  const [bornDate, setBornDate] = useState(new Date());
  const [showBornDatePicker, setShowBornDatePicker] = useState(false);
  const [showAddEarningsModal, setShowAddEarningsModal] = useState(false);
  const [earningsType, setEarningsType] = useState('horse');
  const [earningsDate, setEarningsDate] = useState(new Date());
  const [showEarningsDatePicker, setShowEarningsDatePicker] = useState(false);
  const [earningsEventName, setEarningsEventName] = useState('');
  const [earningsAmount, setEarningsAmount] = useState('');
  const [earningsNotes, setEarningsNotes] = useState('');
  const [earningsOffspringName, setEarningsOffspringName] = useState('');
  const [showOffspringPicker, setShowOffspringPicker] = useState(false);
  const [breakdownName, setBreakdownName] = useState(null); // offspring whose earnings are open
  const [offspringPickerSearch, setOffspringPickerSearch] = useState('');
  const offspringChoices = knownOffspringNames(horse, allHorses);
  const [showAddIncentiveModal, setShowAddIncentiveModal] = useState(false);
  const [incentiveName, setIncentiveName] = useState('');
  const [incentiveDate, setIncentiveDate] = useState(new Date());
  const [showIncentiveDatePicker, setShowIncentiveDatePicker] = useState(false);
  const [incentiveCost, setIncentiveCost] = useState('');
  const [incentiveNotes, setIncentiveNotes] = useState('');
  const [incentiveFrequency, setIncentiveFrequency] = useState('one-time'); // 'one-time' | 'annual' | 'other'
  const [editingIncentiveId, setEditingIncentiveId] = useState(null);
  const [ownerLabel, setOwnerLabel] = useState('');
  const [milestoneAssignee, setMilestoneAssignee] = useState(null); // { uid, label }
  const [showAssigneeMenu, setShowAssigneeMenu] = useState(false);
  const [viewEarningsEntry, setViewEarningsEntry] = useState(null);
  const [showViewEarningsModal, setShowViewEarningsModal] = useState(false);
  const [editEarningsEntry, setEditEarningsEntry] = useState(null);
  const [showEditEarningsModal, setShowEditEarningsModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [sharePermission, setSharePermission] = useState('view');
  const [shareCanSeeBilling, setShareCanSeeBilling] = useState(false);
  const [shareSameCompany, setShareSameCompany] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [sharesExpanded, setSharesExpanded] = useState(true);
  const [ownerContactName, setOwnerContactName] = useState('');
  const [ownerContactEmail, setOwnerContactEmail] = useState('');
  const [ownerContactPhone, setOwnerContactPhone] = useState('');
  const [trainingNoteText, setTrainingNoteText] = useState('');
  const [trainingNotePrivate, setTrainingNotePrivate] = useState(false);
  const [showAddTrainingNoteModal, setShowAddTrainingNoteModal] = useState(false);
  const [editingTrainingNote, setEditingTrainingNote] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [showAddChargeModal, setShowAddChargeModal] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [chargeDescription, setChargeDescription] = useState('');
  const [chargeNote, setChargeNote] = useState(''); // the detail under the description
  const [chargeAmount, setChargeAmount] = useState('');
  const [chargeDate, setChargeDate] = useState(new Date());
  const [showChargeDatePicker, setShowChargeDatePicker] = useState(false);
  const [chargeRecurring, setChargeRecurring] = useState(false);
  const [chargeRecurDay, setChargeRecurDay] = useState('1'); // day of month for recurring
  const recurringRanRef = useRef(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [showPaymentDatePicker, setShowPaymentDatePicker] = useState(false);
  const [paymentNote, setPaymentNote] = useState('');
  const [paymentAllocations, setPaymentAllocations] = useState({}); // chargeId -> amount string applied

  // Health & Lameness incidents
  const [incidents, setIncidents] = useState([]);
  const [showAllIncidents, setShowAllIncidents] = useState(false); // false = ongoing only
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [incidentName, setIncidentName] = useState('');
  const [incidentTags, setIncidentTags] = useState([]);
  const [incidentDate, setIncidentDate] = useState(new Date());
  const [showIncidentDatePicker, setShowIncidentDatePicker] = useState(false);
  const [incidentSymptoms, setIncidentSymptoms] = useState('');
  const [incidentVetNotes, setIncidentVetNotes] = useState('');
  const [incidentTreatment, setIncidentTreatment] = useState('');
  const [incidentSaving, setIncidentSaving] = useState(false);
  const [customIncidentTags, setCustomIncidentTags] = useState([]);
  const [incidentTagFilter, setIncidentTagFilter] = useState(null); // null = all types
  const [incidentCustomTagInput, setIncidentCustomTagInput] = useState('');
  const [healthView, setHealthView] = useState('list'); // 'list' | 'calendar'

  // Pictures & Documents section
  const [customDocTypes, setCustomDocTypes] = useState([]);
  const [docFilter, setDocFilter] = useState('All');
  // Pictures & Documents: newest first by default (remembered), plus search.
  const [docSort, setDocSort] = useState('newest'); // 'newest' | 'oldest' | 'name'
  const [docSearch, setDocSearch] = useState('');
  useEffect(() => { AsyncStorage.getItem('docSort').then((v) => { if (v === 'newest' || v === 'oldest' || v === 'name') setDocSort(v); }).catch(() => {}); }, []);
  const chooseDocSort = (v) => { setDocSort(v); AsyncStorage.setItem('docSort', v).catch(() => {}); };
  const [sharingDocKey, setSharingDocKey] = useState(null); // path/url of the file being shared

  const handleShareDocument = async (d) => {
    const key = d.path || d.url;
    setSharingDocKey(key);
    try { await shareOrDownloadFile({ url: d.url, fileName: d.name, mime: d.mime }); }
    catch { Alert.alert('Error', 'Could not share this file. Please try again.'); }
    finally { setSharingDocKey(null); }
  };
  const [docUploading, setDocUploading] = useState(false);
  const [pendingDocFiles, setPendingDocFiles] = useState(null); // picked files awaiting a doc type
  const [docTypeChoice, setDocTypeChoice] = useState('Picture');
  const [customDocTypeInput, setCustomDocTypeInput] = useState('');
  const [docNameInput, setDocNameInput] = useState(''); // optional custom name for a single upload
  const [docNoteInput, setDocNoteInput] = useState(''); // what this file is about, beyond its name
  const [showDocPhotos, setShowDocPhotos] = useState(true); // collapse the photo grid when it gets long
  const [docViewerDoc, setDocViewerDoc] = useState(null); // the picture being viewed
  const [editingDocNote, setEditingDocNote] = useState(null); // the file whose note is being edited
  const [docNoteEdit, setDocNoteEdit] = useState('');
  const [docNoteSaving, setDocNoteSaving] = useState(false);
  // Photos attached while creating a new incident (uploaded on save)
  const [incidentPhotosPending, setIncidentPhotosPending] = useState([]);

  // Invoicing (Training Ledger). `businessProfile` is the EFFECTIVE identity:
  // the shared company profile (own or a colleague's) layered over the user's own.
  const [ownProfile, setOwnProfile] = useState(null);
  const [company, setCompany] = useState(null);
  const businessProfile = effectiveBusinessProfile(ownProfile, company);
  const [showBusinessModal, setShowBusinessModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [invoiceSelectedIds, setInvoiceSelectedIds] = useState({}); // which NEW charges to bill this invoice
  const [invoiceChooseNew, setInvoiceChooseNew] = useState(false); // let user pick which new charges to include
  const [invoiceIncludePrevious, setInvoiceIncludePrevious] = useState(true); // carry forward previously-invoiced balance
  const [invoiceExtraIds, setInvoiceExtraIds] = useState({}); // other horses billed on this same invoice
  const [otherLedger, setOtherLedger] = useState([]); // their ledger entries, loaded only while the sheet is open
  const [pendingInvoiceOwner, setPendingInvoiceOwner] = useState(null); // client to pre-tick, arriving from Client Horses
  const [showOwnerEdit, setShowOwnerEdit] = useState(false);
  const [invoiceListOpen, setInvoiceListOpen] = useState(null); // null = decide by length
  const [oeName, setOeName] = useState('');
  const [oeEmail, setOeEmail] = useState('');
  const [oePhone, setOePhone] = useState('');
  const [oeSaving, setOeSaving] = useState(false);
  const [showApplyCreditModal, setShowApplyCreditModal] = useState(false);
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [invoiceEmail, setInvoiceEmail] = useState(true); // email it straight to the client's email on file
  // Account Statement (date-range debit/credit report)
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [statementRange, setStatementRange] = useState('this_year'); // this_month|last_month|this_year|last_year|all|custom
  const [statementFrom, setStatementFrom] = useState(new Date(new Date().getFullYear(), 0, 1));
  const [statementTo, setStatementTo] = useState(new Date());
  const [showStmtFromPicker, setShowStmtFromPicker] = useState(false);
  const [showStmtToPicker, setShowStmtToPicker] = useState(false);
  const [statementBusy, setStatementBusy] = useState(false);
  const [ledgerShowOutstanding, setLedgerShowOutstanding] = useState(true); // ledger filter: defaults to outstanding
  const [showDeletedLedger, setShowDeletedLedger] = useState(false); // "Recently deleted" starts collapsed
  // Edit a ledger entry
  const [editingLedgerEntry, setEditingLedgerEntry] = useState(null);
  const [showEditLedgerModal, setShowEditLedgerModal] = useState(false);
  const [editLedgerDesc, setEditLedgerDesc] = useState('');
  const [editLedgerNote, setEditLedgerNote] = useState('');
  const [editLedgerAmount, setEditLedgerAmount] = useState('');
  const [editLedgerDate, setEditLedgerDate] = useState(new Date());
  const [showEditLedgerDatePicker, setShowEditLedgerDatePicker] = useState(false);
  const [editLedgerRecurring, setEditLedgerRecurring] = useState(false);
  const [editLedgerRecurDay, setEditLedgerRecurDay] = useState('1');

  // Which profile sections are collapsed. Per-horse and synced to the user's
  // account (loaded from userSettings.sectionLayout below); reference/heavy
  // sections start folded until a saved layout arrives.
  const [collapsedSections, setCollapsedSections] = useState(DEFAULT_SECTION_LAYOUT);
  const toggleSection = (key) => {
    setCollapsedSections((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      const uid = auth.currentUser?.uid;
      if (uid) setHorseSectionLayout(uid, horseId, next).catch(() => {});
      return next;
    });
  };

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const unsub = subscribeToUserSettings(uid, (s) => {
      setCustomCategories(s.customCategories || []);
      setCustomNoteTags(s.customNoteTags || []);
      setHiddenCategories(s.hiddenCategories || []);
      setHiddenNoteTags(s.hiddenNoteTags || []);
      setCustomIncidentTags(s.customIncidentTags || []);
      setCustomDocTypes(s.customDocTypes || []);
      setOwnProfile(s.businessProfile || null);
      const saved = s.sectionLayout && s.sectionLayout[horseId];
      setCollapsedSections(saved ? { ...DEFAULT_SECTION_LAYOUT, ...saved } : DEFAULT_SECTION_LAYOUT);
    });
    return unsub;
  }, [horseId]);

  // The shared company (provider identity) this user belongs to, if any.
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    // Catch the company's invoice sequence up to this member's own, so joining
    // can never cause a number to be issued twice.
    return subscribeToMyCompany(uid, (c) => {
      setCompany(c);
      if (c?.id) reconcileInvoiceCounter(uid, c.id);
    });
  }, []);

  useEffect(() => {
    const unsub = subscribeToHorse(horseId, (data) => {
      setHorse(data);
      if (data) {
        navigation.setOptions({ title: displayName(data) });
        if (!notesLoaded) {
          if (data.noteEntries) {
            setNoteEntries(data.noteEntries);
          } else if (data.notes) {
            // Migrate legacy single-string notes into the entry list
            setNoteEntries([{ id: `note_${Date.now()}`, text: data.notes, createdAt: new Date().toISOString() }]);
          }
          setNotesLoaded(true);
        }
      }
    });
    return unsub;
  }, [horseId]);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const unsub = subscribeToHorses(uid, setAllHorses);
    return unsub;
  }, []);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const unsub = subscribeToUserSettings(uid, (s) => {
      setCustomLocations(s.customLocations || []);
    });
    return unsub;
  }, []);

  // Restore add-task draft if user left and came back within 30 min
  useEffect(() => {
    let mounted = true;
    AsyncStorage.getItem(ADD_TASK_DRAFT_KEY).then((raw) => {
      if (!mounted || !raw) return;
      try {
        const draft = JSON.parse(raw);
        if (Date.now() - draft.savedAt > ADD_HORSE_DRAFT_TTL) {
          AsyncStorage.removeItem(ADD_TASK_DRAFT_KEY);
          return;
        }
        setMilestoneName(draft.milestoneName || '');
        setMilestoneCategory(draft.milestoneCategory || '');
        setMilestoneCategoryOther(draft.milestoneCategoryOther || '');
        setShowAddMilestoneModal(true);
      } catch {
        AsyncStorage.removeItem(ADD_TASK_DRAFT_KEY);
      }
    });
    return () => { mounted = false; };
  }, []);

  // Save add-task draft whenever modal is open and has content
  useEffect(() => {
    if (!showAddMilestoneModal) return;
    if (!milestoneName.trim() && !milestoneCategory) return;
    try {
      AsyncStorage.setItem(ADD_TASK_DRAFT_KEY, JSON.stringify({
        savedAt: Date.now(),
        milestoneName, milestoneCategory, milestoneCategoryOther,
      }));
    } catch {}
  }, [showAddMilestoneModal, milestoneName, milestoneCategory, milestoneCategoryOther]);

  // Restore add-event draft if user left and came back within 30 min
  useEffect(() => {
    let mounted = true;
    AsyncStorage.getItem(ADD_EVENT_DRAFT_KEY).then((raw) => {
      if (!mounted || !raw) return;
      try {
        const draft = JSON.parse(raw);
        if (Date.now() - draft.savedAt > ADD_HORSE_DRAFT_TTL) {
          AsyncStorage.removeItem(ADD_EVENT_DRAFT_KEY);
          return;
        }
        setEarningsType(draft.earningsType || 'horse');
        setEarningsEventName(draft.earningsEventName || '');
        setEarningsAmount(draft.earningsAmount || '');
        setEarningsNotes(draft.earningsNotes || '');
        setEarningsOffspringName(draft.earningsOffspringName || '');
        setShowAddEarningsModal(true);
      } catch {
        AsyncStorage.removeItem(ADD_EVENT_DRAFT_KEY);
      }
    });
    return () => { mounted = false; };
  }, []);

  // Save add-event draft whenever modal is open and has content
  useEffect(() => {
    if (!showAddEarningsModal) return;
    if (!earningsEventName.trim() && !earningsNotes.trim()) return;
    try {
      AsyncStorage.setItem(ADD_EVENT_DRAFT_KEY, JSON.stringify({
        savedAt: Date.now(),
        earningsType, earningsEventName, earningsAmount, earningsNotes, earningsOffspringName,
      }));
    } catch {}
  }, [showAddEarningsModal, earningsType, earningsEventName, earningsAmount, earningsNotes, earningsOffspringName]);

  const clearTaskDraft = () => AsyncStorage.removeItem(ADD_TASK_DRAFT_KEY);
  const clearEventDraft = () => AsyncStorage.removeItem(ADD_EVENT_DRAFT_KEY);

  // Subscribe to training ledger for this horse
  useEffect(() => {
    return subscribeToLedger(horseId, setLedgerEntries);
  }, [horseId]);

  // Every other horse's ledger, so one invoice can bill several at once. Held
  // only while the Create Invoice sheet is open — it is a lot of data to keep
  // live otherwise, and useless until then. Keyed on the id list rather than the
  // array, which is a new object on every herd snapshot.
  const otherHorseIdKey = allHorses.map((h) => h.id).filter((id) => id && id !== horseId).sort().join(',');
  useEffect(() => {
    if (!showInvoiceModal || !canUseMultiHorseInvoice()) { setOtherLedger([]); setInvoiceExtraIds({}); setPendingInvoiceOwner(null); return undefined; }
    return subscribeToLedgerForHorses(otherHorseIdKey ? otherHorseIdKey.split(',') : [], setOtherLedger);
  }, [showInvoiceModal, otherHorseIdKey]);

  // Navigated here from Client Horses to bill one client: open the invoice sheet.
  useEffect(() => {
    if (!route.params?.invoiceOwner) return;
    setPendingInvoiceOwner(route.params.invoiceOwner);
    setInvoiceChooseNew(false);
    setInvoiceIncludePrevious(true);
    setInvoiceSelectedIds({});
    setShowInvoiceModal(true);
    navigation.setParams({ invoiceOwner: undefined });
  }, [route.params?.invoiceOwner]);

  // …then tick that client's other horses once their ledgers arrive. It runs on
  // each snapshot until it finds them, because the sheet opens before the data
  // does. The billing terms are filled in here too, for the same reason.
  useEffect(() => {
    if (!pendingInvoiceOwner || !showInvoiceModal) return;
    const rows = invoiceBillableHorses().filter(
      (b) => (getOwnerContact(b.horse)?.name || b.horse.ownerName || 'Owner not set') === pendingInvoiceOwner
    );
    if (rows.length === 0) return;
    setInvoiceExtraIds(Object.fromEntries(rows.map((b) => [b.horse.id, true])));
    setInvoiceNotes(businessProfile?.terms || '');
    setPendingInvoiceOwner(null);
  }, [pendingInvoiceOwner, showInvoiceModal, otherLedger, businessProfile?.terms]);

  // Subscribe to health incidents for this horse
  useEffect(() => {
    return subscribeToIncidents(horseId, setIncidents);
  }, [horseId]);

  // Keep the horse's denormalized ongoing-incident count in sync so the herd
  // list and profile badge can show it cheaply without their own subscription.
  useEffect(() => {
    if (!horse) return;
    const ongoing = incidents.filter((i) => i.status !== 'resolved').length;
    if ((horse.openHealthCount || 0) !== ongoing) {
      updateHorse(horseId, { openHealthCount: ongoing }).catch(() => {});
    }
  }, [incidents, horse?.openHealthCount, horseId]);

  const openCreateIncident = () => {
    setIncidentName('');
    setIncidentTags([]);
    setIncidentCustomTagInput('');
    setIncidentDate(new Date());
    setShowIncidentDatePicker(false);
    setIncidentSymptoms('');
    setIncidentVetNotes('');
    setIncidentTreatment('');
    setIncidentPhotosPending([]);
    setShowIncidentModal(true);
  };

  // ── Pictures & Documents handlers ──
  const startAddPictures = () => {
    choosePhotoSource((items) => {
      setDocTypeChoice('Picture');
      setCustomDocTypeInput('');
      setDocNameInput('');
      setDocNoteInput('');
      setPendingDocFiles(items);
    });
  };

  const startAddDocument = async () => {
    const d = await pickDocumentAsync();
    if (!d) return;
    if (d.size && d.size > 15 * 1024 * 1024) {
      Alert.alert('File too large', 'Documents must be under 15 MB.');
      return;
    }
    setDocTypeChoice('Health Certificate');
    setCustomDocTypeInput('');
    // Pre-fill the name field with the original filename minus its extension.
    const orig = d.name || '';
    const dot = orig.lastIndexOf('.');
    setDocNameInput(dot > 0 ? orig.slice(0, dot) : orig);
    setDocNoteInput('');
    setPendingDocFiles([{ uri: d.uri || '', payload: d.file || d.uri, name: d.name || '', mime: d.mime || 'application/pdf' }]);
  };

  const confirmUploadDocs = async () => {
    const files = pendingDocFiles || [];
    const note = docNoteInput.trim();
    let docType = docTypeChoice;
    const custom = customDocTypeInput.trim();
    if (custom) {
      docType = custom;
      const uid = auth.currentUser?.uid;
      if (uid && !DOC_TYPE_PRESETS.includes(custom) && !customDocTypes.includes(custom)) {
        addCustomDocType(uid, custom).catch(() => {});
      }
    }
    // For a single file, honor a custom name (keeping the original extension).
    let filesToUpload = files;
    const customName = docNameInput.trim();
    if (files.length === 1 && customName) {
      const orig = files[0].name || '';
      const dot = orig.lastIndexOf('.');
      const ext = dot > 0 ? orig.slice(dot) : '';
      const named = ext && !customName.toLowerCase().endsWith(ext.toLowerCase()) ? customName + ext : customName;
      filesToUpload = [{ ...files[0], name: named }];
    }
    setPendingDocFiles(null);
    setDocUploading(true);
    try {
      for (const f of filesToUpload) {
        const meta = await uploadHorseFileAsync(horseId, f.payload, { kind: 'documents', name: f.name, mime: f.mime });
        // A note is only written when there is one — an empty string on every
        // document would be noise in the stored record.
        await addHorseDocument(horseId, { ...meta, docType, ...(note ? { note } : {}) });
      }
    } catch (e) {
      Alert.alert('Upload failed', uploadErrMsg(e));
    } finally {
      setDocUploading(false);
    }
  };

  const openOwnerEdit = () => {
    const c = getOwnerContact(horse) || {};
    setOeName(c.name || '');
    setOeEmail(c.email || '');
    setOePhone(c.phone || '');
    setShowOwnerEdit(true);
  };

  // Write the same contact card to each of these horses.
  const applyOwnerContact = async (targets, contact) => {
    setOeSaving(true);
    const results = await Promise.allSettled(
      targets.map((h) => updateHorse(h.id, { ownerContact: contact }))
    );
    setOeSaving(false);
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      Alert.alert(
        'Partly saved',
        `Updated ${results.length - failed} of ${results.length} horses. ${failed} could not be changed — you may not have edit access to ${failed === 1 ? 'it' : 'them'}.`
      );
      return;
    }
    setShowOwnerEdit(false);
  };

  // A client is held together by their name — it is the key everything groups
  // and bills by. So changing it on one horse would quietly split them in two.
  // Both readings are legitimate (a correction, or a horse that changed hands),
  // so ask rather than guess. With no other horses on file there is nothing to
  // ask about.
  const saveOwnerContact = async () => {
    const name = oeName.trim();
    if (!name) { Alert.alert('Name required', "Enter the owner's name."); return; }
    const contact = { name, email: oeEmail.trim(), phone: oePhone.trim() };
    const uid = auth.currentUser?.uid;
    const currentName = (getOwnerContact(horse)?.name || '').trim();
    const siblings = currentName
      ? allHorses.filter(
          (h) => h.id !== horseId
            && isHorseTrainer(h, uid)
            && (getOwnerContact(h)?.name || '').trim() === currentName
        )
      : [];
    if (siblings.length === 0) { applyOwnerContact([horse], contact); return; }
    const n = siblings.length;
    Alert.alert(
      'Their other horses',
      `${currentName} has ${n} other horse${n === 1 ? '' : 's'} with you. Update ${n === 1 ? 'it' : 'them'} to match, so they stay together as one client?`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'This horse only', onPress: () => applyOwnerContact([horse], contact) },
        { text: `Update all ${n + 1}`, onPress: () => applyOwnerContact([horse, ...siblings], contact) },
      ]
    );
  };

  // ── Invoicing ──
  const openBusinessModal = () => setShowBusinessModal(true);

  // Resolve the [from,to] YYYY-MM-DD window + a human label for the chosen preset.
  // Split my company's OUTSTANDING charges into what's already been invoiced
  // (carried forward, tagged with its invoice number) and what's new (billed for
  // the first time here). `credit` = unapplied prepayment that reduces the total.
  // The same split, computed from ANY ledger list. getInvoiceParts is this
  // applied to the horse on screen; a multi-horse invoice runs it again for each
  // extra horse against that horse's own entries, so every horse's carry-over,
  // part-payments and prepayment credit are its own.
  const partsFor = (entries, { includePrevious = true, selectedIds = null } = {}) => {
    // One pass over the payments builds both what has been paid toward each
    // charge and the unapplied credit. Asking per charge instead meant
    // re-scanning every payment for every charge — and this runs for each
    // billable horse on every render of the invoice sheet, so a provider with
    // many client horses and long ledgers would feel it.
    const paidBy = new Map();
    let credit = 0;
    entries.forEach((e) => {
      if (e.type !== 'payment' || e.deleted) return;
      let alloc = 0;
      (e.allocations || []).forEach((a) => {
        const amt = a.amount || 0;
        alloc += amt;
        paidBy.set(a.chargeId, (paidBy.get(a.chargeId) || 0) + amt);
      });
      if (entryCompany(e) === myCompanyKey) credit += Math.max(0, (e.amount || 0) - alloc);
    });
    const outstanding = entries
      .filter((e) => !e.deleted && e.type === 'charge' && entryCompany(e) === myCompanyKey)
      .map((c) => ({ ...c, remaining: (c.amount || 0) - (paidBy.get(c.id) || 0) }))
      .filter((c) => c.remaining > 0.005);
    const label = (c) => (c.remaining < (c.amount || 0) - 0.005 ? `${c.description} (balance)` : c.description);
    const prevAll = outstanding
      .filter((c) => invoiceListOf(c).length > 0)
      .map((c) => ({ id: c.id, date: c.date, description: label(c), note: c.note || '', amount: c.remaining, invoiceNumber: firstInvoiceOf(c) }));
    const newAll = outstanding.filter((c) => invoiceListOf(c).length === 0);
    const current = (selectedIds ? newAll.filter((c) => selectedIds[c.id]) : newAll)
      .map((c) => ({ id: c.id, date: c.date, description: label(c), note: c.note || '', amount: c.remaining }));
    return { previously: includePrevious ? prevAll : [], current, credit, newCount: newAll.length, hasPrevious: prevAll.length > 0 };
  };

  const getInvoiceParts = () => partsFor(ledgerEntries, {
    includePrevious: invoiceIncludePrevious,
    selectedIds: invoiceChooseNew ? invoiceSelectedIds : null,
  });

  // Other horses this invoice could also bill: ones I'm a trainer on that have
  // outstanding charges from my company. Grouped by owner, because billing a
  // client for all of their horses at once is the whole point.
  const invoiceBillableHorses = () => {
    if (!canUseMultiHorseInvoice()) return [];
    const byHorse = new Map();
    otherLedger.forEach((e) => {
      if (!byHorse.has(e.horseId)) byHorse.set(e.horseId, []);
      byHorse.get(e.horseId).push(e);
    });
    const uid = auth.currentUser?.uid;
    return allHorses
      .filter((h) => h.id && h.id !== horseId && isHorseTrainer(h, uid))
      .map((h) => ({ horse: h, parts: partsFor(byHorse.get(h.id) || [], { includePrevious: invoiceIncludePrevious }) }))
      .filter((x) => x.parts.current.length > 0 || x.parts.previously.length > 0)
      .sort((a, b) => {
        const oa = (getOwnerContact(a.horse)?.name || a.horse.ownerName || 'Owner');
        const ob = (getOwnerContact(b.horse)?.name || b.horse.ownerName || 'Owner');
        return oa.localeCompare(ob) || displayName(a.horse).localeCompare(displayName(b.horse));
      });
  };

  const handleCreateInvoice = async () => {
    if (invoiceBusy) return; // guard against a double-tap creating two invoices
    if (!businessProfile?.name) { setShowInvoiceModal(false); openBusinessModal(); return; }
    const { previously, current, credit } = getInvoiceParts();
    // Every horse on this invoice, this one first. One number, one PDF, filed
    // under each of them; each horse's charges keep their own section.
    const extras = invoiceBillableHorses().filter((b) => invoiceExtraIds[b.horse.id]);
    // A horse with nothing outstanding is dropped rather than printed as an
    // empty section — which happens when you bill a client from Client Horses
    // and the horse you happened to land on is already paid up. Its prepayment
    // credit goes with it: credit cannot reduce a bill the horse isn't on.
    const groups = [
      { horse, previously, current, credit },
      ...extras.map((x) => ({ horse: x.horse, previously: x.parts.previously, current: x.parts.current, credit: x.parts.credit })),
    ].filter((g) => g.previously.length > 0 || g.current.length > 0);
    if (groups.length === 0) {
      Alert.alert('Nothing to invoice', invoiceChooseNew ? 'Pick at least one new charge to include, or there is nothing outstanding.' : 'There are no outstanding charges to invoice.');
      return;
    }
    const totalCredit = groups.reduce((s, g) => s + (g.credit || 0), 0);
    const primaryHorse = groups[0].horse;
    const clientContact = getOwnerContact(primaryHorse) || { name: primaryHorse.ownerName, email: primaryHorse.ownerEmail };
    const emailTo = INVOICE_EMAIL_ENABLED && invoiceEmail ? (clientContact?.email || '').trim() : '';
    const invoiceTotal = Math.max(0, groups.reduce((s, g) => s + [...g.previously, ...g.current].reduce((t, c) => t + (c.amount || 0), 0), 0) - totalCredit);
    setInvoiceBusy(true);
    try {
      // Unique, sequential per-business invoice number (INV-0001, INV-0002…).
      // If the counter cannot be reached we stop: a made-up number could repeat
      // one already given to a client, and a duplicate is worse than a retry.
      let num;
      try {
        num = await getNextInvoiceNumber(auth.currentUser.uid, company?.id || null);
      } catch (e) {
        throw new Error('Could not reserve an invoice number. Check your connection and try again — nothing has been billed.');
      }
      const horseLabel = (primaryHorse.name || primaryHorse.registeredName || 'Horse').replace(/[^\w -]+/g, '').trim() || 'Horse';
      const fileName = groups.length > 1 ? `Invoice ${num}.pdf` : `${horseLabel} Invoice ${num}.pdf`;
      // The server renders the PDF and files it in the horse's Documents (type
      // "Invoice"), then we present it — same on every platform.
      const result = await generateAndShareInvoice({
        horseId: primaryHorse.id || horseId,
        horseIds: groups.map((g) => g.horse.id).filter(Boolean),
        fileName,
        groups,
        credit: totalCredit,
        business: businessProfile,
        ownerContact: clientContact,
        horse: primaryHorse,
        invoiceNumber: num,
        notes: invoiceNotes.trim(),
        emailTo,
        emailTotal: Math.round(invoiceTotal * 100) / 100,
      });
      // Stamp the newly-billed charges so they become "previously invoiced" next
      // time — this is what makes double-billing impossible. Done only after the
      // PDF was generated successfully.
      const unstamped = [];
      for (const g of groups) {
        for (const c of [...g.current, ...g.previously]) {
          if (!c.id) continue;
          try {
            await addChargeToInvoice(c.id, num);
            if (result === 'emailed') await updateLedgerEntry(c.id, { invoiceEmailedAt: localYmd(new Date()), emailProblem: null });
          } catch (err) {
            unstamped.push(displayName(g.horse));
          }
        }
      }
      if (unstamped.length > 0) {
        // Say so rather than swallowing it: these charges still look new, so
        // they would be billed again on the next invoice unless they are dealt
        // with. The invoice itself is fine — it is already filed.
        const names = [...new Set(unstamped)];
        Alert.alert(
          'Invoice created, but check ' + (names.length === 1 ? names[0] : 'these horses'),
          `${num} was created and saved, but some charges on ${names.join(', ')} could not be marked as invoiced. They will still look like new charges, so leave them off your next invoice or they will be billed twice.`
        );
      }
      setShowInvoiceModal(false);
      if (result === 'emailed') {
        Alert.alert('Invoice emailed', `${num} was emailed to ${emailTo}${groups.length > 1 ? '' : ''}. It's also saved under “Invoice” in Pictures & Documents.`);
      } else if (typeof result === 'string' && result.startsWith('email_failed')) {
        Alert.alert('Invoice saved, but not emailed', `${num} was created and saved under “Invoice” in Pictures & Documents, but the email to ${emailTo} didn't go through${result.slice(13) ? ` (${result.slice(13)})` : ''}. Open it from Documents to share it another way.`);
      }
      // On web, a confirmation is safe. On native we skip it — the OS share
      // sheet is about to present, and a JS alert would collide with it. The
      // invoice is already filed under "Invoice" in Pictures & Documents.
      if (result === 'opened') {
        Alert.alert(
          'Invoice created',
          groups.length > 1
            ? `Saved under “Invoice” in the Pictures & Documents of all ${groups.length} horses. It also opened in a new tab to view, print, or email (allow pop-ups if it didn’t).`
            : 'Saved under “Invoice” in this horse’s Pictures & Documents. It also opened in a new tab to view, print, or email (allow pop-ups if it didn’t) — or reopen it anytime from Documents.'
        );
      }
    } catch (e) {
      // Every failure on this path is safe to retry: charges are stamped only
      // after the PDF is generated, so a failed attempt bills nothing. Say so
      // plainly, whatever the underlying error was — otherwise the user is left
      // looking at a raw Firebase message with no idea what to do next.
      const detail = e?.message || 'Something went wrong.';
      Alert.alert(
        'Could not create invoice',
        /try again/i.test(detail) ? detail : `${detail}\n\nNothing has been billed — you can safely try again.`
      );
    } finally {
      setInvoiceBusy(false);
    }
  };

  // Void an invoice: release its charges back to "new" (so they can be
  // re-invoiced) and remove the saved PDF from Documents. Charges are NOT
  // deleted — only the invoice stamp and the PDF are removed.
  const handleVoidInvoice = (num) => {
    Alert.alert('Void invoice', `Void ${num}? Its charges are released so you can re-invoice them — on every horse it billed — and the saved PDF is removed. The charges themselves are not deleted.`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Void', style: 'destructive', onPress: async () => {
        try {
          const charges = ledgerEntries.filter((e) => e.type === 'charge' && !e.deleted && invoiceListOf(e).includes(num));
          for (const c of charges) { try { await removeChargeFromInvoice(c.id, num); } catch {} }
          const docs = horse.documents || [];
          // Match the PDF by "<num>.pdf" so INV-0001 can't also match INV-00019.
          const isThisInvoice = (d) => d.docType === 'Invoice' && (d.name || '').includes(num + '.pdf');
          const meta = docs.find(isThisInvoice);
          // A multi-horse invoice records who else it billed. Their charges must
          // be released too, or they stay stamped with a number that no longer
          // exists and can never be re-invoiced.
          const others = (meta?.billedHorses || '').split(',').filter((id) => id && id !== horseId);
          if (!meta) {
            Alert.alert(
              'Released this horse',
              `${num}'s charges on this horse are released, but its saved PDF isn't filed here, so there's no record of which other horses it covered. If it billed more than this one, void it from one of them too.`
            );
          }
          if (others.length) {
            const theirEntries = await new Promise((resolve) => {
              let done = false;
              const stop = subscribeToLedgerForHorses(others, (rows) => {
                if (done) return;
                done = true;
                resolve(rows);
                setTimeout(() => { try { stop(); } catch {} }, 0);
              });
              setTimeout(() => { if (!done) { done = true; try { stop(); } catch {} resolve([]); } }, 8000);
            });
            for (const e of theirEntries) {
              if (e.type === 'charge' && !e.deleted && invoiceListOf(e).includes(num)) {
                try { await removeChargeFromInvoice(e.id, num); } catch {}
              }
            }
            // The same document object sits on each horse, so arrayRemove matches.
            for (const id of others) { try { await removeHorseDocument(id, meta); } catch {} }
          }
          const keep = docs.filter((d) => !isThisInvoice(d));
          if (keep.length !== docs.length) {
            await updateHorse(horseId, { documents: keep });
            docs.filter((d) => isThisInvoice(d) && d.path).forEach((d) => { try { deleteFileByUrl(d.path); } catch {} });
          }
        } catch { Alert.alert('Error', 'Could not fully void the invoice. Please try again.'); }
      } },
    ]);
  };

  // Link a user's older, unstamped charges/payments to their current business so
  // they group correctly and can be edited & invoiced. Fixes entries created
  // before Provider info existed.
  const handleAssignFloating = (entries) => {
    const name = businessProfile?.name;
    if (!name) { setShowInvoiceModal(false); openBusinessModal(); return; }
    Alert.alert(
      `Link to ${name}`,
      `Link ${entries.length} earlier ${entries.length === 1 ? 'entry' : 'entries'} to ${name}? They'll group under your business and can be invoiced. This doesn't change any amounts.`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Link', onPress: async () => {
          for (const e of entries) {
            try { await updateLedgerEntry(e.id, { providerName: name, providerType: businessProfile?.providerType || 'Trainer' }); } catch {}
          }
        } },
      ]
    );
  };

  const resolveStatementRange = () => {
    const iso = (d) => d.toISOString().split('T')[0];
    const now = new Date();
    const y = now.getFullYear();
    if (statementRange === 'all') return { from: '0000-01-01', to: '9999-12-31', label: 'All time' };
    if (statementRange === 'this_year') return { from: `${y}-01-01`, to: iso(now), label: `${y}` };
    if (statementRange === 'last_year') return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31`, label: `${y - 1}` };
    if (statementRange === 'this_month') { const f = new Date(y, now.getMonth(), 1); return { from: iso(f), to: iso(now), label: now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) }; }
    if (statementRange === 'last_month') { const f = new Date(y, now.getMonth() - 1, 1); const t = new Date(y, now.getMonth(), 0); return { from: iso(f), to: iso(t), label: f.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) }; }
    const f = new Date(Math.min(statementFrom, statementTo));
    const t = new Date(Math.max(statementFrom, statementTo));
    return { from: iso(f), to: iso(t), label: `${f.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – ${t.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` };
  };

  const handleCreateStatement = async () => {
    if (statementBusy) return;
    if (!businessProfile?.name) { setShowStatementModal(false); openBusinessModal(); return; }
    const active = ledgerEntries.filter((e) => !e.deleted && entryCompany(e) === myCompanyKey && (e.type === 'charge' || e.type === 'payment'));
    const r = resolveStatementRange();
    const rows = active.filter((e) => e.date >= r.from && e.date <= r.to);
    // Opening balance = everything on the account BEFORE the range (charges − payments).
    const opening = r.from > '0000-01-01'
      ? active.filter((e) => e.date < r.from).reduce((s, e) => s + (e.type === 'charge' ? (e.amount || 0) : -(e.amount || 0)), 0)
      : 0;
    if (rows.length === 0 && Math.abs(opening) < 0.005) {
      Alert.alert('Nothing to report', 'There is no account activity in that date range.');
      return;
    }
    setStatementBusy(true);
    try {
      const horseLabel = (horse.name || horse.registeredName || 'Horse').replace(/[^\w -]+/g, '').trim() || 'Horse';
      const safeLabel = r.label.replace(/[^\w -]+/g, '').trim() || 'statement';
      const result = await generateAndShareStatement({
        horseId,
        fileName: `${horseLabel} Statement ${safeLabel}.pdf`,
        rows,
        opening,
        business: businessProfile,
        ownerContact: getOwnerContact(horse) || { name: horse.ownerName, email: horse.ownerEmail },
        horse,
        periodLabel: r.label,
        notes: invoiceNotes.trim(),
      });
      setShowStatementModal(false);
      if (result === 'opened') {
        Alert.alert('Statement created', 'Saved under “Statement” in this horse’s Pictures & Documents. It also opened in a new tab to view, print, or email (allow pop-ups if it didn’t).');
      }
    } catch (e) {
      Alert.alert('Could not create statement', e?.message || 'Please try again.');
    } finally {
      setStatementBusy(false);
    }
  };

  // ── Edit / delete a ledger entry (any trainer, any entry) ──
  const openEditLedger = (e) => {
    setEditingLedgerEntry(e);
    setEditLedgerDesc(e.description || '');
    setEditLedgerNote(e.note || '');
    setEditLedgerAmount(String(e.amount ?? ''));
    setEditLedgerDate(e.date ? new Date(e.date + 'T12:00:00') : new Date());
    setShowEditLedgerDatePicker(false);
    setEditLedgerRecurring(false);
    setEditLedgerRecurDay('1');
    setShowEditLedgerModal(true);
  };

  const handleSaveEditLedger = async () => {
    const amt = parseAmount(editLedgerAmount);
    if (isNaN(amt) || amt <= 0) { Alert.alert('Invalid amount', 'Enter a valid amount greater than 0.'); return; }
    if (!editLedgerDesc.trim()) { Alert.alert('Description required', 'Enter a description for this entry.'); return; }
    // Warn if this charge was already billed — the sent PDFs won't change. A
    // charge carried forward is on EVERY invoice it appeared on, and the client
    // holds them all, so name them all rather than just the most recent.
    const invoicedOn = editingLedgerEntry.type === 'charge' ? invoiceListOf(editingLedgerEntry) : [];
    if (invoicedOn.length > 0) {
      const label = invoiceListLabel(invoicedOn);
      const many = invoicedOn.length > 1;
      Alert.alert(
        'Already invoiced',
        `This charge is on ${label}. Editing it won't change the ${many ? 'invoices' : 'invoice'} you already sent — only the ledger and future invoices/statements. To correct ${many ? 'them' : 'the sent invoice'}, void ${label} and re-issue. Edit anyway?`,
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Edit anyway', onPress: () => doSaveEditLedger(amt) },
        ]
      );
      return;
    }
    await doSaveEditLedger(amt);
  };

  const doSaveEditLedger = async (amt) => {
    try {
      // If a payment is reduced below what it's already applied to, clear its
      // allocations so charges aren't left wrongly marked paid.
      const extra = {};
      if (editingLedgerEntry.type === 'payment' && Array.isArray(editingLedgerEntry.allocations) && editingLedgerEntry.allocations.length) {
        const allocTotal = editingLedgerEntry.allocations.reduce((s, a) => s + (a.amount || 0), 0);
        if (amt < allocTotal - 0.005) extra.allocations = [];
      }
      await updateLedgerEntry(editingLedgerEntry.id, {
        description: editLedgerDesc.trim(),
        ...(editingLedgerEntry.type === 'payment' ? {} : { note: editLedgerNote.trim() }),
        amount: amt,
        date: localYmd(editLedgerDate),
        ...extra,
      });
      // Optionally set this charge up to repeat monthly from now on.
      if (editLedgerRecurring && editingLedgerEntry.type === 'charge') {
        const template = {
          id: `rc_${Date.now()}`,
          description: editLedgerDesc.trim(),
          amount: amt,
          lastGenerated: monthKey(new Date()),
          dayOfMonth: Math.min(28, Math.max(1, parseInt(editLedgerRecurDay, 10) || 1)),
          trainerUid: editingLedgerEntry.trainerUid || auth.currentUser?.uid,
          providerName: editingLedgerEntry.providerName || '',
          providerType: editingLedgerEntry.providerType || '',
        };
        await updateHorse(horseId, { recurringCharges: [...(horse.recurringCharges || []), template] });
      }
      setShowEditLedgerModal(false);
      setEditingLedgerEntry(null);
    } catch {
      Alert.alert('Error', 'Could not save the change. Please try again.');
    }
  };

  const handleDeleteLedgerEntry = (e) => {
    const me = auth.currentUser;
    const invoicedOn = e.type === 'charge' ? invoiceListOf(e) : [];
    const msg = `Remove "${e.description}" (${e.type === 'payment' ? 'payment' : 'charge'})? You can restore it from “Recently deleted” at the bottom of the ledger.`
      + (invoicedOn.length > 0
        ? `\n\nHeads up: this charge is on ${invoiceListLabel(invoicedOn)}. Deleting it won't change the ${invoicedOn.length > 1 ? 'invoices' : 'invoice'} already sent.`
        : '');
    Alert.alert('Delete entry', msg, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => deleteLedgerEntry(e.id, me?.uid, me?.email).catch(() => Alert.alert('Error', 'Could not delete. Please try again.')) },
    ]);
  };

  const handleRestoreLedgerEntry = (e) => restoreLedgerEntry(e.id).catch(() => Alert.alert('Error', 'Could not restore. Please try again.'));

  const openDocNote = (d) => {
    setDocNoteEdit(d.note || '');
    setEditingDocNote(d);
  };

  // Documents live in an array on the horse, so the note is changed by writing
  // the array back with that one entry replaced. Files are matched on their
  // storage path, which is unique and stable; an emptied note is removed
  // outright rather than stored as an empty string.
  const saveDocNote = async () => {
    const target = editingDocNote;
    if (!target) return;
    const note = docNoteEdit.trim();
    const key = (d) => d.path || d.url;
    setDocNoteSaving(true);
    try {
      const next = (horse.documents || []).map((d) => {
        if (key(d) !== key(target)) return d;
        const { note: _drop, ...rest } = d;
        return note ? { ...rest, note } : rest;
      });
      await updateHorse(horseId, { documents: next });
      setEditingDocNote(null);
      setDocViewerDoc((cur) => (cur && key(cur) === key(target) ? { ...cur, note } : cur));
    } catch (e) {
      Alert.alert('Could not save', e?.message || 'Please try again.');
    } finally {
      setDocNoteSaving(false);
    }
  };

  const handleRemoveDocument = (d) => {
    Alert.alert('Remove', `Remove ${d.docType === 'Picture' ? 'this picture' : `"${d.name || 'this document'}"`}? This cannot be undone.`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Remove',
        style: 'destructive',
        onPress: async () => {
          try {
            await removeHorseDocument(horseId, d);
            deleteFileByUrl(d.path || d.url);
          } catch {
            Alert.alert('Error', 'Could not remove it. Please try again.');
          }
        },
      },
    ]);
  };

  const toggleIncidentTag = (tag) => {
    setIncidentTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const addCustomIncidentType = () => {
    const t = incidentCustomTagInput.trim();
    if (!t) return;
    if (!incidentTags.includes(t)) setIncidentTags((prev) => [...prev, t]);
    const uid = auth.currentUser?.uid;
    if (uid && !INCIDENT_TAG_PRESETS.includes(t) && !customIncidentTags.includes(t)) {
      addCustomIncidentTag(uid, t).catch(() => {});
    }
    setIncidentCustomTagInput('');
  };

  const handleSaveIncident = async () => {
    if (!incidentName.trim()) {
      Alert.alert('Name required', 'Please enter what is going on (e.g. "Left front lameness").');
      return;
    }
    setIncidentSaving(true);
    const picked = new Date(incidentDate);
    picked.setHours(12, 0, 0, 0);
    const dateStr = picked.toISOString().split('T')[0];
    try {
      const ref = await addIncident(horseId, horse.ownerId || auth.currentUser?.uid || null, {
        name: incidentName,
        tags: incidentTags,
        dateOccurred: dateStr,
        symptoms: incidentSymptoms,
        vetNotes: incidentVetNotes,
        prescribedTreatment: incidentTreatment,
      });
      // Upload any attached photos after the incident exists, then link them.
      // A photo failure must not read as "incident not saved" — it was.
      try {
        for (const f of incidentPhotosPending) {
          const meta = await uploadHorseFileAsync(horseId, f.payload, { kind: 'incidents', name: f.name, mime: f.mime });
          await addIncidentPhoto(ref.id, meta);
        }
      } catch (photoErr) {
        Alert.alert('Incident saved, photos failed', uploadErrMsg(photoErr));
      }
      setIncidentPhotosPending([]);
      setShowIncidentModal(false);
    } catch (e) {
      Alert.alert('Error', 'Could not save the incident. Please try again.');
    } finally {
      setIncidentSaving(false);
    }
  };

  // Resolve who owns this horse record (for the "Record Owner" row).
  useEffect(() => {
    if (!horse) return;
    const uid = auth.currentUser?.uid;
    // Legacy horses may have no ownerId — the first member is the owner.
    const ownerUid = horse.ownerId || horse.members?.[0] || null;
    if (ownerUid && ownerUid === uid) { setOwnerLabel('You'); return; }
    if (horse.ownerEmail) { setOwnerLabel(horse.ownerEmail); return; }
    if (ownerUid) {
      fetchUserById(ownerUid)
        .then((u) => setOwnerLabel(u?.email || u?.displayName || 'Unknown'))
        .catch(() => setOwnerLabel(''));
    } else {
      setOwnerLabel('');
    }
  }, [horse?.ownerId, horse?.ownerEmail, horse?.members]);

  // Generate any missing monthly recurring charges (trainer only, once per mount)
  useEffect(() => {
    if (!horse || recurringRanRef.current) return;
    const uid = auth.currentUser?.uid;
    const templates = horse.recurringCharges || [];
    if (!isHorseTrainer(horse, uid) || templates.length === 0) return;
    recurringRanRef.current = true;
    (async () => {
      const curKey = monthKey(new Date());
      let changed = false;
      const updated = templates.map((t) => ({ ...t }));
      for (const t of updated) {
        let last = t.lastGenerated || curKey;
        while (last < curKey) {
          const [y, m] = last.split('-').map(Number);
          const nextKey = monthKey(new Date(y, m, 1)); // m (1-based) as 0-based index = next month
          if (nextKey > curKey) break;
          const dd = String(Math.min(28, Math.max(1, t.dayOfMonth || 1))).padStart(2, '0');
          try {
            // Attribute the generated charge to the provider who set up the template.
            await addCharge(horseId, t.trainerUid || uid, horse.ownerId, t.description, t.amount, `${nextKey}-${dd}`, { name: t.providerName || '', type: t.providerType || '' });
            last = nextKey;
            changed = true;
          } catch {
            break;
          }
        }
        t.lastGenerated = last;
      }
      if (changed) updateHorse(horseId, { recurringCharges: updated });
    })();
  }, [horse]);

  const toggle = (milestoneId) => {
    const today = new Date().toISOString().split('T')[0];
    const milestones = (horse.milestones || []).map((m) => {
      if (m.id !== milestoneId) return m;
      const nowComplete = !m.completed;
      const updated = { ...m, completed: nowComplete };
      // Record WHEN it was completed (today) so history shows the real date.
      if (nowComplete) updated.completedDate = today;
      else delete updated.completedDate;
      return updated;
    });
    const target = (horse.milestones || []).find((m) => m.id === milestoneId);
    if (target && !target.completed && target.notificationId) {
      cancelReminder(target.notificationId);
    }
    updateHorse(horseId, { milestones });
  };

  // Everyone this horse is shared with (owner + shared users) — for task assignment.
  const getAssignableMembers = () => {
    const uid = auth.currentUser?.uid;
    const list = [];
    if (horse?.ownerId) {
      const label = horse.ownerId === uid ? 'You' : (horse.ownerName || horse.ownerEmail || 'Owner');
      list.push({ uid: horse.ownerId, label });
    }
    (horse?.shares || []).forEach((s) => {
      list.push({ uid: s.uid, label: s.uid === uid ? 'You' : (s.name || s.email) });
    });
    const seen = new Set();
    return list.filter((m) => (seen.has(m.uid) ? false : (seen.add(m.uid), true)));
  };

  const meAsAssignee = () => {
    const me = auth.currentUser;
    return { uid: me?.uid, label: 'You' };
  };

  const openAddMilestoneModal = () => {
    setEditingMilestoneId(null);
    setMilestoneName('');
    setMilestoneDate(new Date());
    setReminderEnabled(true); // reminders on by default; user can turn off
    setReminderMode('specified');
    setReminderDayBefore(false);
    setReminderRepeat(false); // one reminder unless the user opts into repeats
    setReminderDate(new Date());
    setShowReminderDatePicker(false);
    setShowReminderTimePicker(false);
    setMilestoneHorseIds([horseId]);
    setMilestoneCategory('');
    setMilestoneCategoryOther('');
    setMilestoneNotes('');
    setMilestoneAddAsComplete(false);
    setMilestoneAssignee(meAsAssignee());
    setShowAssigneeMenu(false);
    setShowAddMilestoneModal(true);
  };

  // Open the task modal pre-filled to edit an existing task (current horse only)
  const openEditMilestoneModal = (m) => {
    setEditingMilestoneId(m.id);
    setMilestoneAddAsComplete(false);
    setMilestoneName(m.name || '');
    setMilestoneDate(new Date(getMilestoneDate(horse, m) + 'T12:00:00'));
    setReminderEnabled(!!m.remindOnDue || !!m.reminderAt);
    setReminderMode(m.reminderAt && !m.remindOnDue ? 'custom' : 'specified');
    setReminderDayBefore(!!m.remindDayBefore);
    setReminderRepeat(!!m.repeatUntilDone);
    setReminderDate(m.reminderAt ? new Date(m.reminderAt) : new Date());
    setShowReminderDatePicker(false);
    setShowReminderTimePicker(false);
    setMilestoneHorseIds([horseId]);
    setMilestoneCategory(m.category || '');
    setMilestoneCategoryOther('');
    setMilestoneNotes(m.notes || '');
    setMilestoneAssignee(
      m.assignedToUid
        ? { uid: m.assignedToUid, label: m.assignedToUid === auth.currentUser?.uid ? 'You' : (m.assignedToName || 'Assigned') }
        : meAsAssignee()
    );
    setShowAssigneeMenu(false);
    setShowAddMilestoneModal(true);
  };

  const toggleMilestoneHorse = (id) => {
    setMilestoneHorseIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAddMilestone = async () => {
    if (!milestoneName.trim()) {
      Alert.alert('Name required', 'Please enter a task name.');
      return;
    }
    const picked = new Date(milestoneDate);
    picked.setHours(12, 0, 0, 0);
    const dateStr = picked.toISOString().split('T')[0];

    // A "due date" reminder is delivered server-side (push to the assignee on
    // the due date). A "custom" reminder still schedules a local notification
    // on this device. This avoids double-notifying and reaches the assignee.
    const remindOnDue = reminderEnabled && reminderMode === 'specified';
    let fireDate = null;
    if (reminderEnabled && reminderMode === 'custom') {
      fireDate = reminderDate;
    }
    // Logging a task that's already done — no reminders, and stamp the date it
    // was completed (the date field doubles as "completed on").
    const completingNow = milestoneAddAsComplete && !editingMilestoneId;
    if (completingNow) fireDate = null;
    const effRemindOnDue = completingNow ? false : remindOnDue;
    const assignerName = auth.currentUser?.displayName || auth.currentUser?.email || '';

    const category = milestoneCategory === 'Other' ? milestoneCategoryOther.trim() : milestoneCategory;
    const notes = milestoneNotes.trim();

    // ── Edit existing task (current horse only) ──
    if (editingMilestoneId) {
      const existing = (horse.milestones || []).find((m) => m.id === editingMilestoneId);
      if (existing?.notificationId) await cancelReminder(existing.notificationId);
      let newNotifId = null;
      if (fireDate) {
        newNotifId = await scheduleReminder(milestoneName.trim(), `${displayName(horse)} — reminder`, fireDate);
      }
      const milestones = (horse.milestones || []).map((m) => {
        if (m.id !== editingMilestoneId) return m;
        const updated = { ...m, name: milestoneName.trim(), date: dateStr };
        if (category) updated.category = category; else delete updated.category;
        if (notes) updated.notes = notes; else delete updated.notes;
        if (milestoneAssignee?.uid) {
          const reassigned = m.assignedToUid !== milestoneAssignee.uid;
          updated.assignedToUid = milestoneAssignee.uid;
          updated.assignedToName = milestoneAssignee.label;
          if (reassigned) {
            updated.assignedBy = auth.currentUser?.uid || '';
            updated.assignedByName = assignerName;
            delete updated.assignNotified; // let the server send a fresh "assigned to you" push
          }
        }
        if (remindOnDue) updated.remindOnDue = true; else delete updated.remindOnDue;
        // Repeats are server-driven, so they only apply to the due-date reminder.
        if (remindOnDue && reminderRepeat) updated.repeatUntilDone = true; else delete updated.repeatUntilDone;
        if (remindOnDue && reminderDayBefore) updated.remindDayBefore = true; else delete updated.remindDayBefore;
        delete updated.dayBeforeNotifiedOn; // edited, so it may be due again
        delete updated.ownerDueNotifiedOn; delete updated.ownerDayBeforeNotifiedOn;
        // Changing the task means it may be due again — allow a new due-date push.
        delete updated.dueNotifiedOn;
        if (newNotifId) { updated.notificationId = newNotifId; updated.reminderAt = fireDate.toISOString(); }
        else { delete updated.notificationId; delete updated.reminderAt; }
        return updated;
      }).sort((a, b) => getMilestoneDate(horse, a).localeCompare(getMilestoneDate(horse, b)));
      updateHorse(horseId, { milestones });
      clearTaskDraft();
      setEditingMilestoneId(null);
      setShowAddMilestoneModal(false);
      return;
    }

    const targetIds = milestoneHorseIds.length ? milestoneHorseIds : [horseId];
    for (const id of targetIds) {
      const target = id === horseId ? horse : allHorses.find((h) => h.id === id);
      if (!target) continue;
      const newMilestone = {
        id: `m_custom_${Date.now()}_${id}`,
        name: milestoneName.trim(),
        date: dateStr,
        completed: completingNow,
        ...(completingNow && { completedDate: dateStr }),
        custom: true,
        ...(category && { category }),
        ...(notes && { notes }),
        ...(milestoneAssignee?.uid && {
          assignedToUid: milestoneAssignee.uid,
          assignedToName: milestoneAssignee.label,
          assignedBy: auth.currentUser?.uid || '',
          assignedByName: assignerName,
        }),
        ...(effRemindOnDue && { remindOnDue: true }),
        ...(effRemindOnDue && reminderRepeat && { repeatUntilDone: true }),
        ...(effRemindOnDue && reminderDayBefore && { remindDayBefore: true }),
        createdByUid: auth.currentUser?.uid || '',
      };
      if (fireDate) {
        const notificationId = await scheduleReminder(
          newMilestone.name,
          `${displayName(target)} — reminder`,
          fireDate
        );
        if (notificationId) {
          newMilestone.notificationId = notificationId;
          newMilestone.reminderAt = fireDate.toISOString();
        }
      }
      const milestones = [...(target.milestones || []), newMilestone]
        .sort((a, b) => getMilestoneDate(target, a).localeCompare(getMilestoneDate(target, b)));
      updateHorse(id, { milestones });
    }
    clearTaskDraft();
    setShowAddMilestoneModal(false);
  };

  const handleDeleteMilestone = (milestoneId) => {
    const target = (horse.milestones || []).find((m) => m.id === milestoneId);
    if (target?.notificationId) cancelReminder(target.notificationId);
    const milestones = (horse.milestones || []).filter((m) => m.id !== milestoneId);
    updateHorse(horseId, { milestones });
  };

  const openMarkBornModal = () => {
    setBornDate(new Date());
    setShowMarkBornModal(true);
  };

  const handleMarkBorn = async () => {
    // Keep the owner's own OPEN tasks; drop everything breeding-related.
    // The breeding work is exactly the built-in checklist (Breed Date, 14/30/45/
    // 120 day checks) — those are generated from the breed date, carry no
    // `custom` flag, and mean nothing once the foal is on the ground. Custom
    // tasks are the owner's own (farrier, vet, registration) and outlive the
    // pregnancy, so open ones carry over with their reminders intact. Completed
    // ones are dropped along with the checklist. Wiping the whole array used to
    // delete open tasks silently and orphan their scheduled reminders.
    const keptMilestones = (horse?.milestones || []).filter((m) => m.custom && !m.completed);
    await updateHorse(horseId, {
      type: 'born',
      dateOfBirth: localYmd(bornDate),
      milestones: keptMilestones,
    });
    setShowMarkBornModal(false);
  };

  const openSoldModal = () => {
    setSoldDate(null);
    setShowSoldDatePicker(false);
    setSoldTo('');
    setSalePrice('');
    setSoldNotes('');
    setShowSoldModal(true);
  };

  const handleMarkSold = async () => {
    if (!soldDate) {
      Alert.alert('Sale date required', 'Please select the date the horse was sold.');
      return;
    }
    const priceNum = salePrice.trim() ? parseFloat(salePrice.replace(/[^0-9.]/g, '')) : null;
    if (salePrice.trim() && !(priceNum >= 0)) {
      Alert.alert('Check the price', 'Enter the sale price as a number, or leave it blank.');
      return;
    }
    setSoldSaving(true);
    try {
      // Same as leaving the stable: the sire/dam keep credit for what it won.
      if (horse) {
        try {
          await Promise.allSettled(snapshotProgenyOnDelete(horse, allHorses, updateHorse, auth.currentUser?.uid));
        } catch {}
      }
      // Its reminders stop — a sold horse's farrier date is no longer yours.
      (horse?.milestones || []).forEach((m) => { if (!m.completed && m.notificationId) cancelReminder(m.notificationId); });
      await markHorseSold(horseId, {
        soldDate: localYmd(soldDate),
        soldTo: soldTo.trim(),
        salePrice: priceNum,
        soldNotes: soldNotes.trim(),
      }, auth.currentUser?.uid, auth.currentUser?.email);
      setShowSoldModal(false);
    } catch (e) {
      Alert.alert('Could not mark as sold', 'Please check your connection and try again.');
    } finally {
      setSoldSaving(false);
    }
  };

  const handleUndoSold = () => {
    Alert.alert(
      'Undo sale?',
      `Move ${displayName(horse)} back into your herd? Its tasks and reminders resume.`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Undo Sale', onPress: () => unmarkHorseSold(horseId).catch(() => Alert.alert('Could not undo', 'Please check your connection and try again.')) },
      ]
    );
  };

  const handleDelete = () => {
    Alert.alert(
      'Delete Horse',
      `Move ${horse ? displayName(horse) : horse?.name} to Recently Deleted? You can restore it within 30 days from Data & Account on the Dashboard.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              // Preserve what this horse earned as progeny credit on its sire/dam
              // before it leaves the stable — they produced a winner either way.
              // Best-effort: a parent write must never stop the horse being deleted.
              if (horse) {
                try {
                  await Promise.allSettled(
                    snapshotProgenyOnDelete(horse, allHorses, updateHorse, auth.currentUser?.uid)
                  );
                } catch {}
              }
              await softDeleteHorse(horseId, auth.currentUser?.uid, auth.currentUser?.email);
              navigation.goBack();
            } catch (e) {
              Alert.alert('Delete Failed', 'Could not delete the horse. Please check your connection and try again.');
            }
          },
        },
      ]
    );
  };

  const handleRename = async () => {
    if (!renameInput.trim()) {
      Alert.alert('Name required', 'Please enter a horse name.');
      return;
    }
    await updateHorse(horseId, { name: renameInput.trim() });
    setShowRenameModal(false);
  };

  // Link a colleague into my company (creating it from my profile if needed) so
  // they share the provider identity for all current and future horses.
  const linkToMyCompany = async (found) => {
    const me = auth.currentUser;
    if ((company?.memberUids || []).includes(found.uid)) return; // already linked
    let cid = company?.id;
    if (!cid) {
      if (!businessProfile?.name) return; // nothing to link to yet
      cid = await saveCompanyProfile(me.uid, null, { uid: me.uid, name: me.displayName || me.email || '', email: me.email || '' }, businessProfile);
      await saveBusinessProfile(me.uid, { ...businessProfile, companyId: cid });
    }
    await addCompanyMember(cid, { uid: found.uid, name: found.displayName || found.email, email: found.email || '' });
  };

  const handleShare = async () => {
    const email = shareEmail.trim().toLowerCase();
    if (!email) { Alert.alert('Email required', 'Enter the email address to share with.'); return; }
    if (email === auth.currentUser?.email?.toLowerCase()) {
      Alert.alert('Invalid', 'You cannot share a horse with yourself.');
      return;
    }
    setShareLoading(true);
    try {
      const found = await findUserByEmail(email);
      if (!found) {
        Alert.alert('User not found', USER_NOT_FOUND_MSG);
        setShareLoading(false);
        return;
      }
      if (!(await confirmSharingMatch(found, email))) { setShareLoading(false); return; }
      const currentShares = horse?.shares || [];
      if (currentShares.some((s) => s.uid === found.uid)) {
        Alert.alert('Already shared', 'This horse is already shared with that user.');
        setShareLoading(false);
        return;
      }
      // A trainer (not the owner) can only request to add another trainer — the owner must approve.
      if (!isOwner) {
        if ((horse?.pendingTrainers || []).some((p) => p.uid === found.uid)) {
          Alert.alert('Already requested', 'A request to add this trainer is already awaiting owner approval.');
          setShareLoading(false);
          return;
        }
        const me = auth.currentUser;
        await requestTrainer(horseId, found.uid, found.email, found.displayName || '', me?.displayName || me?.email || '');
        if (shareSameCompany) { try { await linkToMyCompany(found); } catch {} }
        Alert.alert('Request sent', shareSameCompany
          ? 'They were linked to your company. The horse owner will be asked to approve their access to this horse.'
          : 'The horse owner will be asked to approve this provider before they get access.');
        setShowShareModal(false);
        setShareEmail('');
        setShareSameCompany(false);
        setShareLoading(false);
        return;
      }
      if (sharePermission === 'transfer') {
        const me = auth.currentUser;
        const iAmTrainer = isHorseTrainer(horse, me.uid);
        const fromInfo = getHorseTrainers(horse).find((t) => t.uid === me.uid) || { uid: me.uid, email: me.email || '', name: me.displayName || me.email || '' };
        Alert.alert(
          'Transfer Ownership',
          `Transfer ${displayName(horse)} to ${found.displayName || email}? They become the owner${iAmTrainer ? ', and you stay on as a trainer with billing access' : ' and you lose access'}.`,
          [
            { text: 'Cancel', style: 'cancel', onPress: () => setShareLoading(false) },
            {
              text: 'Transfer',
              style: 'destructive',
              onPress: async () => {
                try {
                  await transferHorse(horseId, found.uid, me.uid, currentShares, {
                    keepAsTrainer: iAmTrainer,
                    currentTrainers: getHorseTrainers(horse),
                    fromInfo,
                  });
                  setShowShareModal(false);
                  setShareEmail('');
                  setSharePermission('view');
                } catch {
                  Alert.alert('Error', 'Transfer failed. Please try again.');
                } finally {
                  setShareLoading(false);
                }
              },
            },
          ]
        );
      } else {
        const ownerContact = sharePermission === 'trainer'
          ? { name: ownerContactName.trim(), email: ownerContactEmail.trim(), phone: ownerContactPhone.trim() }
          : null;
        await shareHorse(horseId, currentShares, found.uid, found.email, found.displayName || '', sharePermission, ownerContact, (sharePermission === 'view' || sharePermission === 'edit') && shareCanSeeBilling);
        if (sharePermission === 'trainer' && shareSameCompany) { try { await linkToMyCompany(found); } catch {} }
        if (sharePermission === 'trainer' && !shareSameCompany) {
          const sharesNow = [...currentShares.filter((s) => s.uid !== found.uid), { uid: found.uid, email: found.email, name: found.displayName || found.email, permission: 'trainer' }];
          offerTeamShare(found, sharesNow);
        }
        {
          const permLabel = sharePermission === 'edit' ? 'Can Edit' : sharePermission === 'trainer' ? 'Trainer' : 'View Only';
          notifyAccess(found.uid, '🐴 Horse Shared With You', `${displayName(horse)} was shared with you (${permLabel}) in Horse Math`, { horseId });
        }
        setShowShareModal(false);
        setShareEmail('');
        setSharePermission('view');
        setShareSameCompany(false);
        setOwnerContactName('');
        setOwnerContactEmail('');
        setOwnerContactPhone('');
        setShareLoading(false);
      }
    } catch (err) {
      Alert.alert('Share failed', err.message);
      setShareLoading(false);
    }
  };

  // After a provider is put on this horse: if they belong to a company with
  // other people in it, offer to share with the whole team. `sharesNow` is the
  // shares array AS IT STANDS after that provider was added — the horse in
  // state may not have caught up yet, and rewriting from a stale copy would
  // drop the share that was just made.
  const offerTeamShare = async (person, sharesNow) => {
    let company = null;
    try { company = await fetchCompanyOfUser(person.uid); } catch (e) { return; }
    if (!company) return;
    const onIt = new Set([...(horse?.trainers || []).map((t) => t?.uid), person.uid, auth.currentUser?.uid]);
    const others = (company.members || []).filter((m) => m?.uid && !onIt.has(m.uid));
    if (others.length === 0) return;
    const who = person.name || person.displayName || person.email;
    Alert.alert(
      `Share with ${company.name || 'their team'}?`,
      `${who} is part of ${company.name || 'a team'} with ${others.length} other ${others.length === 1 ? 'person' : 'people'}. Share this horse with the whole team as Providers too? Anyone who joins or leaves that team later is added or removed automatically.`,
      [
        { text: `Just ${who}`, style: 'cancel' },
        { text: 'Whole team', onPress: async () => {
          let shares = sharesNow;
          let failed = 0;
          for (const m of others) {
            try {
              await grantCompanyTrainer(horseId, shares, m, company.id, company.name || '');
              shares = [...shares.filter((s) => s.uid !== m.uid), { uid: m.uid, email: m.email || '', name: m.name || m.email || '', permission: 'trainer', viaCompany: company.id, viaCompanyName: company.name || '' }];
              notifyAccess(m.uid, '🐴 Horse Shared With Your Team',
                `${myDisplayName()} shared ${displayName(horse)} with your team at ${company.name || 'your company'} — you have Provider access.`, { horseId });
            } catch (e) { failed += 1; }
          }
          if (failed > 0) Alert.alert('Partly shared', `${others.length - failed} of ${others.length} team members were added. Try again for the rest from Sharing on this horse.`);
        } },
      ]
    );
  };

  const handleApproveTrainer = async (req) => {
    try {
      await approveTrainer(horseId, horse?.shares || [], horse?.pendingTrainers || [], req);
      notifyAccess(req.uid, '✅ Provider Access Approved', `You now have Provider access to ${displayName(horse)} in Horse Math.`, { horseId });
      const sharesNow = [...(horse?.shares || []).filter((s) => s.uid !== req.uid), { uid: req.uid, email: req.email, name: req.name, permission: 'trainer' }];
      offerTeamShare(req, sharesNow);
    } catch {
      Alert.alert('Error', 'Could not approve. Please try again.');
    }
  };

  const handleDenyTrainer = (reqUid) => {
    denyTrainer(horseId, horse?.pendingTrainers || [], reqUid).catch(() => {});
  };

  // Net balance still owed to the provider group the given user bills under, on
  // this horse (their charges minus payments, colleagues in the same company
  // included). Used to stop an owner cutting off a provider they still owe.
  const providerOutstanding = (uid) => {
    const theirs = ledgerEntries.filter((e) => !e.deleted && e.trainerUid === uid);
    if (theirs.length === 0) return 0;
    const keys = new Set(theirs.map(entryCompany));
    const grp = ledgerEntries.filter((e) => !e.deleted && keys.has(entryCompany(e)));
    const charges = grp.filter((e) => e.type === 'charge').reduce((s, e) => s + (e.amount || 0), 0);
    const payments = grp.filter((e) => e.type === 'payment').reduce((s, e) => s + (e.amount || 0), 0);
    return charges - payments;
  };

  // Take a whole provider team off this horse at once. Removing one member
  // would be undone — the owner-side sync would put them straight back — so
  // the team comes off together, which also drops the company from the sync.
  // Notes are kept: a team is several people, and one prompt per person is
  // not a decision anyone wants to make six times.
  const handleRemoveTeam = (cid, members) => {
    const owed = members.map((m) => ({ m, bal: providerOutstanding(m.uid) })).filter((x) => x.bal > 0.005);
    if (owed.length > 0) {
      Alert.alert(
        'Balance still owed',
        `${owed.map((x) => `${x.m.name || x.m.email} (${fmtMoney(x.bal)})`).join(', ')} ${owed.length === 1 ? 'is' : 'are'} still owed money on ${displayName(horse)}. Close that out first, then the team can be removed.`
      );
      return;
    }
    const label = members[0]?.viaCompanyName || 'their team';
    Alert.alert(
      'Remove team',
      `Remove ${label} — ${members.length} ${members.length === 1 ? 'person' : 'people'} — from ${displayName(horse)}? Their notes stay. The provider you originally chose keeps access unless you remove them separately.`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Remove team', style: 'destructive', onPress: async () => {
          let shares = horse?.shares || [];
          let trainers = getHorseTrainers(horse);
          let failed = 0;
          for (const m of members) {
            try {
              await revokeShare(horseId, shares, m.uid, trainers);
              shares = shares.filter((s) => s.uid !== m.uid);
              trainers = trainers.filter((t) => t.uid !== m.uid);
            } catch (e) { failed += 1; }
          }
          if (failed > 0) Alert.alert('Partly removed', `${members.length - failed} of ${members.length} removed. Try again for the rest.`);
        } },
      ]
    );
  };

  const handleRevokeShare = (share) => {
    // A provider can't be removed while they're still owed money on this horse.
    if (share.permission === 'trainer') {
      const bal = providerOutstanding(share.uid);
      if (bal > 0.005) {
        Alert.alert(
          'Balance still owed',
          `${share.name || share.email} is still owed ${fmtMoney(bal)} on ${displayName(horse)}. The balance has to be closed out first — once they record the payment (or clear the charges), the ledger will show a zero balance and you'll be able to remove their access.`,
          [{ text: 'OK' }]
        );
        return;
      }
    }
    // Access is revoked either way; the choice is only what happens to the notes
    // this person wrote. Revoke FIRST so a failed note write can never leave the
    // notes deleted but the access intact. Notes are matched by author uid, not
    // by company, so removing one person never touches a colleague's notes.
    const theirNotes = (horse?.trainingNotes || []).filter((n) => n.createdByUid === share.uid);
    const finish = async (deleteNotes) => {
      try {
        await revokeShare(horseId, horse?.shares || [], share.uid, share.permission === 'trainer' ? getHorseTrainers(horse) : null);
        if (deleteNotes && theirNotes.length > 0) {
          const kept = (horse?.trainingNotes || []).filter((n) => n.createdByUid !== share.uid);
          await updateHorse(horseId, { trainingNotes: kept });
        }
      } catch (e) {
        Alert.alert('Could not remove access', 'Please check your connection and try again.');
      }
    };

    const who = share.name || share.email;
    if (theirNotes.length === 0) {
      Alert.alert('Remove Access', `Remove ${who}'s access to ${displayName(horse)}?`, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Remove', style: 'destructive', onPress: () => finish(false) },
      ]);
      return;
    }

    const n = theirNotes.length;
    Alert.alert(
      'Remove Access',
      `Remove ${who}'s access to ${displayName(horse)}?

They've written ${n} note${n === 1 ? '' : 's'} on this horse. Keeping them preserves the history — any they marked private stay private and remain hidden from you. Deleting them cannot be undone.`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Keep notes', onPress: () => finish(false) },
        { text: 'Delete notes', style: 'destructive', onPress: () => finish(true) },
      ]
    );
  };

  const handleDeleteTrainingNote = (noteId) => {
    Alert.alert('Delete Note', 'Delete this training note?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => {
        if (!horse) return;
        const updated = (horse.trainingNotes || []).filter((n) => n.id !== noteId);
        updateHorse(horseId, { trainingNotes: updated });
      }},
    ]);
  };

  const handleSaveEditTrainingNote = () => {
    if (!horse || !editingTrainingNote || !trainingNoteText.trim()) return;
    const updated = (horse.trainingNotes || []).map((n) =>
      n.id === editingTrainingNote.id
        ? { ...n, text: trainingNoteText.trim(), private: trainingNotePrivate }
        : n
    );
    updateHorse(horseId, { trainingNotes: updated });
    setEditingTrainingNote(null);
    setShowAddTrainingNoteModal(false);
    setTrainingNoteText('');
    setTrainingNotePrivate(false);
  };

  const handleAddTrainingNote = () => {
    if (!horse || !trainingNoteText.trim()) return;
    const user = auth.currentUser;
    const note = {
      id: `tn_${Date.now()}`,
      text: trainingNoteText.trim(),
      date: new Date().toISOString().split('T')[0],
      createdByUid: user?.uid || '',
      createdByName: user?.displayName || user?.email || 'Unknown',
      providerName: businessProfile?.name || '', // label the note's provider group by business
      createdAt: new Date().toISOString(),
      private: trainingNotePrivate,
    };
    updateHorse(horseId, { trainingNotes: [...(horse.trainingNotes || []), note] });
    setShowAddTrainingNoteModal(false);
    setTrainingNoteText('');
    setTrainingNotePrivate(false);
  };

  const handleAddCharge = async () => {
    const amt = parseAmount(chargeAmount);
    if (!chargeDescription.trim() || isNaN(amt) || amt <= 0) {
      Alert.alert('Missing info', 'Please enter a description and a valid amount.');
      return;
    }
    const uid = auth.currentUser?.uid;
    if (!isHorseTrainer(horse, uid)) {
      Alert.alert('Error', 'Only a trainer of this horse can add charges.');
      return;
    }
    const myProvider = { name: businessProfile?.name || auth.currentUser?.displayName || auth.currentUser?.email || 'Provider', type: businessProfile?.providerType || 'Trainer' };
    try {
      await addCharge(horseId, uid, horse.ownerId, chargeDescription, amt, localYmd(chargeDate), myProvider, chargeNote);
      if (chargeRecurring) {
        const template = {
          id: `rc_${Date.now()}`,
          description: chargeDescription.trim(),
          amount: amt,
          lastGenerated: monthKey(new Date()),
          dayOfMonth: Math.min(28, Math.max(1, parseInt(chargeRecurDay, 10) || 1)),
          trainerUid: uid,
          providerName: myProvider.name,
          providerType: myProvider.type,
        };
        await updateHorse(horseId, { recurringCharges: [...(horse.recurringCharges || []), template] });
      }
      setShowAddChargeModal(false);
    } catch (e) {
      Alert.alert('Error', 'Failed to add charge. Please try again.');
    }
  };

  // Remove a recurring-charge template (stops future auto-charges; past entries stay)
  const handleRemoveRecurring = (templateId) => {
    if (!horse) return;
    Alert.alert('Stop Recurring Charge', 'Stop auto-adding this charge each month? Past entries stay in the ledger.', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Stop', style: 'destructive', onPress: () => {
        const updated = (horse.recurringCharges || []).filter((t) => t.id !== templateId);
        updateHorse(horseId, { recurringCharges: updated });
      }},
    ]);
  };

  // How much has been paid toward a given charge across all payments' allocations.
  const paidTowardCharge = (chargeId) =>
    ledgerEntries
      .filter((e) => e.type === 'payment' && !e.deleted)
      .reduce((s, p) => s + (p.allocations || []).filter((a) => a.chargeId === chargeId).reduce((t, a) => t + (a.amount || 0), 0), 0);

  // My company's key, for scoping billing to colleagues who share a business name.
  const myCompanyKey = normalizeCompany(businessProfile?.name) || ('u:' + (auth.currentUser?.uid || 'unknown'));

  // Unapplied prepayment credit held on the account for my company = payment
  // money not yet applied to any charge. It reduces the balance due until it's
  // allocated to specific charges.
  const creditAvailable = () =>
    ledgerEntries
      .filter((e) => e.type === 'payment' && !e.deleted && entryCompany(e) === myCompanyKey)
      .reduce((s, p) => {
        const alloc = (p.allocations || []).reduce((t, a) => t + (a.amount || 0), 0);
        return s + Math.max(0, (p.amount || 0) - alloc);
      }, 0);

  // Apply available prepayment credit to one charge (oldest prepayments first),
  // by appending allocations onto the prepayment entries.
  const applyCreditToCharge = async (charge) => {
    let need = Math.min(creditAvailable(), (charge.amount || 0) - paidTowardCharge(charge.id));
    if (need <= 0.005) return;
    const prepayments = ledgerEntries
      .filter((e) => e.type === 'payment' && !e.deleted && entryCompany(e) === myCompanyKey)
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    for (const p of prepayments) {
      if (need <= 0.005) break;
      const alloc = p.allocations || [];
      const avail = (p.amount || 0) - alloc.reduce((t, a) => t + (a.amount || 0), 0);
      if (avail <= 0.005) continue;
      const give = Math.min(avail, need);
      try { await updateLedgerEntry(p.id, { allocations: [...alloc, { chargeId: charge.id, amount: give }] }); }
      catch { Alert.alert('Error', 'Could not apply credit. Please try again.'); return; }
      need -= give;
    }
  };

  const handleAddPayment = async () => {
    const amt = parseAmount(paymentAmount);
    if (isNaN(amt) || amt <= 0) {
      Alert.alert('Missing info', 'Please enter a valid payment amount.');
      return;
    }
    const uid = auth.currentUser?.uid;
    if (!isHorseTrainer(horse, uid)) {
      Alert.alert('Error', 'Only a trainer of this horse can record payments.');
      return;
    }
    // Build allocations from the picker. Every payment should land on the charge
    // it pays; any leftover becomes a flagged prepayment credit on the account.
    const allocations = Object.entries(paymentAllocations)
      .map(([chargeId, v]) => ({ chargeId, amount: parseAmount(v) }))
      .filter((a) => !isNaN(a.amount) && a.amount > 0);
    const allocTotal = allocations.reduce((s, a) => s + a.amount, 0);
    if (allocTotal > amt + 0.005) {
      Alert.alert('Over-applied', `You've applied ${fmtMoney(allocTotal)} but the payment is only ${fmtMoney(amt)}. Reduce the amounts applied.`);
      return;
    }
    const remainder = amt - allocTotal;
    const myProvider = { name: businessProfile?.name || auth.currentUser?.displayName || auth.currentUser?.email || 'Provider', type: businessProfile?.providerType || 'Trainer' };
    const doSave = async () => {
      try {
        await addPayment(horseId, uid, horse.ownerId, amt, localYmd(paymentDate), paymentNote, allocations, myProvider);
        setPaymentAllocations({});
        setShowAddPaymentModal(false);
      } catch (e) {
        Alert.alert('Error', 'Failed to record payment. Please try again.');
      }
    };
    // Leftover (or a payment with nothing applied) → confirm holding it as credit.
    if (remainder > 0.005) {
      Alert.alert(
        'Hold as prepayment credit?',
        (allocTotal > 0.005
          ? `${fmtMoney(allocTotal)} will be applied to charges and ${fmtMoney(remainder)} is left over. `
          : `Nothing is applied to a charge. `) +
        `Hold ${fmtMoney(remainder)} as a prepayment credit on the account? It stays flagged until you apply it to a charge.`,
        [
          { text: 'Back', style: 'cancel' },
          { text: `Hold ${fmtMoney(remainder)} credit`, onPress: doSave },
        ]
      );
      return;
    }
    await doSave();
  };

  const openAddNoteModal = () => {
    setEditingNoteId(null);
    setNewNoteText('');
    setNewNoteTag('');
    setNewNoteTagOther('');
    setShowAddNoteModal(true);
  };

  const openEditNoteModal = (entry) => {
    setEditingNoteId(entry.id);
    setNewNoteText(entry.text || '');
    setNewNoteTag(entry.tag || '');
    setNewNoteTagOther('');
    setShowAddNoteModal(true);
  };

  const handleAddNote = () => {
    if (!newNoteText.trim()) {
      Alert.alert('Note required', 'Please enter some text for the note.');
      return;
    }
    const tag = newNoteTag === 'Other' ? newNoteTagOther.trim() : newNoteTag;
    let entries;
    if (editingNoteId) {
      entries = noteEntries.map((n) =>
        n.id === editingNoteId
          ? { ...n, text: newNoteText.trim(), ...(tag ? { tag } : { tag: undefined }) }
          : n
      ).map((n) => { if (n.tag === undefined) { const { tag: _t, ...rest } = n; return rest; } return n; });
    } else {
      const entry = {
        id: `note_${Date.now()}`,
        text: newNoteText.trim(),
        createdAt: new Date().toISOString(),
        ...(tag && { tag }),
      };
      entries = [entry, ...noteEntries];
    }
    setNoteEntries(entries);
    updateHorse(horseId, { noteEntries: entries, notes: '' });
    setEditingNoteId(null);
    setShowAddNoteModal(false);
  };

  const handleDeleteNote = (noteId) => {
    Alert.alert('Delete Note', 'Remove this note?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: () => {
          const entries = noteEntries.filter((n) => n.id !== noteId);
          setNoteEntries(entries);
          updateHorse(horseId, { noteEntries: entries });
        },
      },
    ]);
  };

  const toggleNoteExpanded = (noteId) => {
    setExpandedNotes((prev) => ({ ...prev, [noteId]: !prev[noteId] }));
  };

  const openAddEarningsModal = () => {
    setEarningsType('horse');
    setEarningsDate(new Date());
    setShowEarningsDatePicker(false);
    setEarningsEventName('');
    setEarningsAmount('');
    setEarningsNotes('');
    setEarningsOffspringName('');
    setShowAddEarningsModal(true);
  };

  const handleAddEarnings = () => {
    if (!earningsEventName.trim()) {
      Alert.alert('Event name required', 'Please enter the name of the event.');
      return;
    }
    const amountNum = parseFloat(earningsAmount.replace(/[^0-9.]/g, '')) || 0;
    if (earningsType === 'offspring' && !earningsOffspringName.trim()) {
      Alert.alert('Offspring name required', 'Please enter the name of the offspring.');
      return;
    }
    const entry = {
      id: `earn_${Date.now()}`,
      type: earningsType,
      date: localYmd(earningsDate),
      eventName: earningsEventName.trim(),
      amount: amountNum,
      notes: earningsNotes.trim(),
      ...(earningsType === 'offspring' && { offspringName: canonicalOffspringName(earningsOffspringName, offspringChoices) }),
    };

    // An offspring earning for a foal that is IN the stable is recorded on the
    // foal, as one of its own earnings. From there it counts toward this horse
    // — and toward the other parent if they are here too — the same way an
    // earning entered on the foal directly does. One record, every profile,
    // and nothing to keep in step. Only a foal that is not in the stable gets
    // a manual entry here.
    if (earningsType === 'offspring') {
      const uid = auth.currentUser?.uid;
      const wanted = normalizeHorseName(entry.offspringName);
      const foal = offspringInStable(horse, allHorses)
        .filter((c) => c.type === 'born')
        .find((c) => normalizeHorseName(displayName(c)) === wanted);
      if (foal) {
        if (!canWriteHorse(foal, uid)) {
          Alert.alert(
            `You can't edit ${displayName(foal)}`,
            `${displayName(foal)} is in your stable but you only have view access, so this earning can't be recorded on them. Ask their owner to add it on ${displayName(foal)} — it will count toward ${displayName(horse)} automatically.`
          );
          return;
        }
        const foalEntry = { ...entry, type: 'horse' };
        delete foalEntry.offspringName;
        updateHorse(foal.id, { earnings: [foalEntry, ...(foal.earnings || [])] })
          .then(() => {
            const otherParentHere = allHorses.some((h) => h.id !== horse.id && (h.id === foal.sireId || h.id === foal.damId));
            Alert.alert(
              `Recorded on ${displayName(foal)}`,
              `It's on ${displayName(foal)}'s own earnings and counts toward ${displayName(horse)}${otherParentHere ? ' and the other parent' : ''} automatically.`
            );
          })
          .catch(() => Alert.alert('Could not save', 'Please check your connection and try again.'));
        clearEventDraft();
        setShowAddEarningsModal(false);
        return;
      }
    }

    const updated = [entry, ...(horse.earnings || [])];
    updateHorse(horseId, { earnings: updated });
    clearEventDraft();
    setShowAddEarningsModal(false);
  };

  const openAddIncentiveModal = () => {
    setEditingIncentiveId(null);
    setIncentiveName('');
    setIncentiveDate(new Date());
    setShowIncentiveDatePicker(false);
    setIncentiveCost('');
    setIncentiveNotes('');
    setIncentiveFrequency('one-time');
    setShowAddIncentiveModal(true);
  };

  const openEditIncentiveModal = (inc) => {
    setEditingIncentiveId(inc.id);
    setIncentiveName(inc.name || '');
    setIncentiveDate(inc.datePaid ? new Date(inc.datePaid + 'T12:00:00') : new Date());
    setShowIncentiveDatePicker(false);
    setIncentiveCost(inc.cost != null ? String(inc.cost) : '');
    setIncentiveNotes(inc.notes || '');
    setIncentiveFrequency(inc.frequency || 'one-time');
    setShowAddIncentiveModal(true);
  };

  const handleSaveIncentive = () => {
    if (!horse) return;
    if (!incentiveName.trim()) {
      Alert.alert('Name required', 'Please enter the incentive name.');
      return;
    }
    const costNum = parseAmount(incentiveCost);
    const entry = {
      id: editingIncentiveId || `inc_${Date.now()}`,
      name: incentiveName.trim(),
      datePaid: localYmd(incentiveDate),
      cost: isNaN(costNum) ? 0 : costNum,
      notes: incentiveNotes.trim(),
      frequency: incentiveFrequency,
    };
    const existing = horse.incentives || [];
    const updated = editingIncentiveId
      ? existing.map((i) => (i.id === editingIncentiveId ? { ...i, ...entry } : i))
      : [entry, ...existing];
    updateHorse(horseId, { incentives: updated });
    setShowAddIncentiveModal(false);
    setEditingIncentiveId(null);
  };

  const handleDeleteIncentive = (incId) => {
    Alert.alert('Delete Nomination', 'Remove this incentive nomination?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => {
        if (!horse) return;
        const updated = (horse.incentives || []).filter((i) => i.id !== incId);
        updateHorse(horseId, { incentives: updated });
      }},
    ]);
  };

  const handleDeleteEarnings = (entryId) => {
    Alert.alert('Delete Entry', 'Remove this earnings entry?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: () => {
          const updated = (horse.earnings || []).filter((e) => e.id !== entryId);
          updateHorse(horseId, { earnings: updated });
        },
      },
    ]);
  };

  const openViewEarnings = (entry) => {
    setViewEarningsEntry(entry);
    setShowViewEarningsModal(true);
  };

  const openEditEarnings = (entry) => {
    setEditEarningsEntry({ ...entry });
    setEarningsType(entry.type);
    setEarningsDate(new Date(entry.date + 'T12:00:00'));
    setShowEarningsDatePicker(false);
    setEarningsEventName(entry.eventName);
    setEarningsAmount(entry.amount > 0 ? String(entry.amount) : '');
    setEarningsNotes(entry.notes || '');
    setEarningsOffspringName(entry.offspringName || '');
    setShowEditEarningsModal(true);
  };

  const handleSaveEditEarnings = async () => {
    if (!earningsEventName.trim()) {
      Alert.alert('Event name required', 'Please enter the name of the event.');
      return;
    }
    if (earningsType === 'offspring' && !earningsOffspringName.trim()) {
      Alert.alert('Offspring name required', 'Please enter the name of the offspring.');
      return;
    }
    if (!editEarningsEntry) return;
    // Snapshot everything before any state changes
    const entryId = editEarningsEntry.id;
    const currentEarnings = horse?.earnings || [];
    const amountNum = parseFloat(String(earningsAmount).replace(/[^0-9.]/g, '')) || 0;
    const updatedEntry = {
      id: entryId,
      type: earningsType,
      date: localYmd(earningsDate),
      eventName: earningsEventName.trim(),
      amount: amountNum,
      notes: earningsNotes.trim(),
      offspringName: earningsType === 'offspring' ? canonicalOffspringName(earningsOffspringName, offspringChoices) : null,
    };
    const updated = currentEarnings.map((e) => e.id === entryId ? updatedEntry : e);
    // Close modal first so user isn't staring at a frozen sheet
    setShowEditEarningsModal(false);
    setEditEarningsEntry(null);
    try {
      await updateHorse(horseId, { earnings: updated });
    } catch (err) {
      Alert.alert('Save failed', err.message);
    }
  };

  const handleDeleteFromEdit = () => {
    if (!editEarningsEntry) return;
    const entryId = editEarningsEntry.id;
    const currentEarnings = horse?.earnings || [];
    Alert.alert('Delete Entry', 'Remove this earnings entry?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          setShowEditEarningsModal(false);
          setEditEarningsEntry(null);
          try {
            await updateHorse(horseId, { earnings: currentEarnings.filter((e) => e.id !== entryId) });
          } catch (err) {
            Alert.alert('Delete failed', err.message);
          }
        },
      },
    ]);
  };

  const formatNoteDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const openDetailsModal = () => {
    setDetailsPhotoPending(null);
    setDetailsIsClient(isHorseTrainer(horse, auth.currentUser?.uid));
    const oc = getOwnerContact(horse) || {};
    setConvOwnerName(oc.name || '');
    setConvOwnerEmail(oc.email || '');
    setConvOwnerPhone(oc.phone || '');
    setDetails({
      registeredName: horse.registeredName || '',
      registryNumber: horse.registryNumber || '',
      breed: horse.breed || '',
      color: horse.color || '',
      height: horse.height || '',
      location: horse.location || '',
      stall: horse.stall || '',
      imageUrl: horse.imageUrl || '',
      imageFocus: horse.imageFocus || { x: 0.5, y: 0.5, zoom: 1 },
      status: horse.status || [],
      sire: horse.sire || '',
      sireId: horse.sireId || null,
      dam: horse.dam || '',
      damId: horse.damId || null,
      sireSire: horse.sireSire || '',
      sireDam: horse.sireDam || '',
      damSire: horse.damSire || '',
      damDam: horse.damDam || '',
      recipMare: horse.recipMare || '',
      recipMareId: horse.recipMareId || null,
      recipLeased: horse.recipLeased || false,
      recipLeasedFrom: horse.recipLeasedFrom || '',
    });
    const existingDate = horse.type === 'born' ? horse.dateOfBirth : horse.breedDate;
    setDetailsDate(existingDate ? new Date(existingDate + 'T12:00:00') : new Date());
    setShowDetailsModal(true);
  };

  const openPicker = (forField) => {
    setPickerSearch('');
    setPickerFor(forField);
  };

  const pickStudAsSire = (st) => {
    setDetails((d) => ({
      ...d,
      sire: st.name || '',
      sireId: null,
      sireSire: st.sire || '',
      sireDam: st.dam || '',
    }));
    setPickerFor(null);
  };

  const pickFromStable = (picked) => {
    if (pickerFor === 'sire') {
      setDetails((d) => ({
        ...d,
        sire: stableDisplayName(picked),
        sireId: picked.id,
        sireSire: picked.sire || '',
        sireDam: picked.dam || '',
      }));
    } else if (pickerFor === 'dam') {
      setDetails((d) => ({
        ...d,
        dam: stableDisplayName(picked),
        damId: picked.id,
        damSire: picked.sire || '',
        damDam: picked.dam || '',
      }));
    } else if (pickerFor === 'recip') {
      setDetails((d) => ({
        ...d,
        recipMare: stableDisplayName(picked),
        recipMareId: picked.id,
      }));
    }
    setPickerFor(null);
  };

  const toggleDetailsStatus = (option) => {
    setDetails((d) => ({ ...d, status: toggleStatusOption(d.status, option) }));
  };

  const toggleDetailsBroodmare = () => {
    setDetails((d) => ({ ...d, status: toggleBroodmare(d.status) }));
  };

  const toggleDetailsRecip = () => {
    setDetails((d) => ({ ...d, status: toggleRecipStatus(d.status) }));
  };

  const toggleDetailsFoalSex = (sex) => {
    setDetails((d) => ({ ...d, status: toggleFoalSex(d.status, sex) }));
  };

  const handleSaveDetails = async () => {
    if (detailsSaving) return;
    // Client horse switched on/off here — same shape as ticking it when adding:
    // I'm its trainer and the owner's contact card is filled in. Turning it off
    // keeps the billing records and contact; it just leaves Client Horses.
    const me = auth.currentUser;
    const ownsIt = horse.ownerId === me?.uid || (!horse.ownerId && horse.members?.[0] === me?.uid);
    const wasClient = isHorseTrainer(horse, me?.uid);
    let clientUpdate = {};
    if (ownsIt && detailsIsClient && !wasClient) {
      if (!convOwnerName.trim()) {
        Alert.alert('Owner name required', "Enter the client's name — it groups their horses together for billing.");
        return;
      }
      clientUpdate = {
        trainers: [...(horse.trainers || []).filter((t) => t?.uid !== me.uid), { uid: me.uid, email: me.email || '', name: me.displayName || me.email || '' }],
        ownerContact: { name: convOwnerName.trim(), email: convOwnerEmail.trim(), phone: convOwnerPhone.trim() },
      };
    } else if (ownsIt && !detailsIsClient && wasClient) {
      clientUpdate = { trainers: (horse.trainers || []).filter((t) => t?.uid !== me.uid) };
    }
    setDetailsSaving(true);
    try {
      const dateField = horse.type === 'born' ? 'dateOfBirth' : 'breedDate';
      const payload = { ...details };
      if (detailsPhotoPending) {
        const meta = await uploadHorseFileAsync(horseId, detailsPhotoPending.payload, { kind: 'profile', name: detailsPhotoPending.name, mime: detailsPhotoPending.mime });
        payload.imageUrl = meta.url;
        payload.imagePath = meta.path;
      }
      if (!payload.imageUrl) payload.imagePath = '';
      // Clean up the previous stored photo when it was replaced or removed.
      if (horse.imagePath && payload.imageUrl !== horse.imageUrl) deleteFileByUrl(horse.imagePath);
      await updateHorse(horseId, { ...payload, ...clientUpdate, [dateField]: localYmd(detailsDate) });
      setDetailsPhotoPending(null);
      setShowDetailsModal(false);
    } catch (e) {
      Alert.alert('Error', detailsPhotoPending ? uploadErrMsg(e) : 'Failed to save details. Please try again.');
    } finally {
      setDetailsSaving(false);
    }
  };

  if (!horse) return <View style={styles.screen}><ActivityIndicator style={{ marginTop: 60 }} color={RED} /></View>;

  // On a client horse this is my work only; on my own horse it is everything.
  const myMilestones = (horse.milestones || []).filter((m) => taskVisibleTo(horse, m, auth.currentUser?.uid));

  const isBorn = horse.type === 'born';
  const aqhaAge = isBorn ? (new Date().getFullYear() - new Date(horse.dateOfBirth + 'T12:00:00').getFullYear()) : null;
  const actualAge = isBorn ? getAge(horse.dateOfBirth) : null;
  const age = isBorn ? getQuarterHorseAge(horse.dateOfBirth) : null;
  const foalDate = !isBorn ? addDays(horse.breedDate, 340) : null;
  const daysInFoal = !isBorn ? getDaysInFoal(horse.breedDate) : null;
  const monthsInFoal = !isBorn ? formatMonthsInFoal(horse.breedDate) : '';
  const uid = auth.currentUser?.uid;
  const isOwner = horse.ownerId === uid || (!horse.ownerId && horse.members?.[0] === uid);
  const canUploadMedia = canUploadForHorse(horse, uid);
  // View-only shares get no edit affordances anywhere on this screen.
  const canEdit = canUploadMedia;

  return (
    <>
      <ScrollView
        style={styles.screen}
        contentContainerStyle={{ padding: 16, paddingBottom: 48 }}
      >
        {horse.sold && (
          <View style={styles.soldBanner}>
            <View style={{ flex: 1 }}>
              <Text style={styles.soldBannerTitle}>Sold {formatDate(horse.soldDate)}</Text>
              {(!!horse.soldTo || horse.salePrice != null) && (
                <Text style={styles.soldBannerText}>
                  {[horse.soldTo ? `To ${horse.soldTo}` : '', horse.salePrice != null ? '$' + Number(horse.salePrice).toLocaleString('en-US') : ''].filter(Boolean).join(' · ')}
                </Text>
              )}
              {!!horse.soldNotes && <Text style={styles.soldBannerText}>{horse.soldNotes}</Text>}
              <Text style={styles.soldBannerHint}>Archived — kept for your records, not shown in your herd, tasks or reminders.</Text>
            </View>
            {isOwner && (
              <TouchableOpacity onPress={handleUndoSold} activeOpacity={0.75} style={styles.soldUndoBtn}>
                <Text style={styles.soldUndoTxt}>Undo</Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* ── Summary card ── */}
        <View style={styles.summaryCard}>
          {!!horse.imageUrl && (
            <AdjustableImage
              uri={horse.imageUrl}
              focus={horse.imageFocus}
              onPress={() => setDocViewerDoc({ url: horse.imageUrl })}
              maxWidth={520}
              style={{ marginBottom: 12 }}
            />
          )}
          <View style={styles.summaryRow}>
            <View>
              <Text style={styles.summaryLabel}>
                {isBorn ? 'Date of Birth' : 'Breed Date'}
              </Text>
              <Text style={styles.summaryValue}>
                {isBorn ? formatDate(horse.dateOfBirth) : formatDate(horse.breedDate)}
              </Text>
            </View>
            <View style={styles.cardActions}>
              {canEdit && (
                <TouchableOpacity
                  style={styles.editNameBtn}
                  onPress={() => { setRenameInput(horse.name); setShowRenameModal(true); }}
                  activeOpacity={0.75}
                >
                  <Text style={styles.editNameBtnTxt}>Edit Name</Text>
                </TouchableOpacity>
              )}
              {canEdit && (
                <TouchableOpacity style={styles.editNameBtn} onPress={openDetailsModal} activeOpacity={0.75}>
                  <Text style={styles.editNameBtnTxt}>Edit Details</Text>
                </TouchableOpacity>
              )}
              {canEdit && !isBorn && (
                <TouchableOpacity style={styles.bornBtn} onPress={openMarkBornModal} activeOpacity={0.75}>
                  <Text style={styles.bornBtnTxt}>Mark as Born</Text>
                </TouchableOpacity>
              )}
              {(isOwner || isHorseTrainer(horse, auth.currentUser?.uid)) && (
                <TouchableOpacity style={styles.shareBtn} onPress={() => { setShareEmail(''); setSharePermission(isOwner ? 'view' : 'trainer'); setShareCanSeeBilling(false); const oc = getOwnerContact(horse); setOwnerContactName(oc?.name || ''); setOwnerContactEmail(oc?.email || ''); setOwnerContactPhone(oc?.phone || ''); setShowShareModal(true); }} activeOpacity={0.75}>
                  <Text style={styles.shareBtnTxt}>{isOwner ? 'Share' : 'Add Provider'}</Text>
                </TouchableOpacity>
              )}
              {isOwner && !horse.sold && (
                <TouchableOpacity style={styles.editNameBtn} onPress={openSoldModal} activeOpacity={0.75}>
                  <Text style={styles.editNameBtnTxt}>Mark as Sold</Text>
                </TouchableOpacity>
              )}
              {isOwner && (
                <TouchableOpacity style={styles.deleteBtn} onPress={handleDelete}>
                  <Text style={styles.deleteBtnTxt}>Delete</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>

          {isBorn ? (
            <>
              <View style={styles.divider} />
              <Text style={styles.summaryLabel}>Current AQHA Age</Text>
              <Text style={[styles.summaryValue, { color: GREEN, marginBottom: 14 }]}>
                {aqhaAge} year{aqhaAge === 1 ? '' : 's'}
              </Text>
              <Text style={[styles.summaryLabel, { marginBottom: 8 }]}>Actual Age</Text>
              <View style={styles.ageGrid}>
                <View style={styles.ageCell}>
                  <Text style={styles.ageCellValue}>{actualAge.years}</Text>
                  <Text style={styles.ageCellLabel}>Years</Text>
                </View>
                <View style={styles.ageGridDivider} />
                <View style={styles.ageCell}>
                  <Text style={styles.ageCellValue}>{actualAge.months}</Text>
                  <Text style={styles.ageCellLabel}>Months</Text>
                </View>
                <View style={styles.ageGridDivider} />
                <View style={styles.ageCell}>
                  <Text style={styles.ageCellValue}>{actualAge.days}</Text>
                  <Text style={styles.ageCellLabel}>Days</Text>
                </View>
              </View>
            </>
          ) : (
            <>
              {horse.isRecip && (
                <View style={styles.recipBadgeWrap}>
                  <View style={styles.recipBadge}>
                    <Text style={styles.recipBadgeTxt}>Recipient Mare</Text>
                  </View>
                  {horse.recipMare && (
                    <Text style={styles.summaryNote}>{horse.recipMare}</Text>
                  )}
                  {horse.recipLeased && (
                    <Text style={styles.summaryNote}>
                      Leased{horse.recipLeasedFrom ? ` from ${horse.recipLeasedFrom}` : ''}
                    </Text>
                  )}
                </View>
              )}
              <View style={styles.divider} />
              <Text style={styles.summaryLabel}>Days in Foal</Text>
              <Text style={[styles.summaryValue, { color: RED }]}>
                {daysInFoal} day{daysInFoal === 1 ? '' : 's'}
                {monthsInFoal ? `  ·  ${monthsInFoal}` : ''}
              </Text>
              <View style={styles.divider} />
              <Text style={styles.summaryLabel}>Est. Foal Date</Text>
              <Text style={[styles.summaryValue, { color: RED }]}>
                {formatDate(foalDate)}
              </Text>
              <Text style={styles.summaryNote}>
                {getDaysLabel(foalDate)} · based on ~340 day gestation
              </Text>
            </>
          )}
        </View>

        {/* ── Shared With section ── */}
        {((horse.shares || []).length > 0 || !isOwner) && (
          <>
            <TouchableOpacity
              style={styles.collapsibleHeader}
              onPress={() => setSharesExpanded((v) => !v)}
              activeOpacity={0.75}
            >
              <Text style={styles.sectionHeading}>
                {isOwner ? `Shared With (${(horse.shares || []).length})` : 'Shared Horse'}
              </Text>
              <Text style={styles.sectionCardChevron}>{sharesExpanded ? '⌄' : '›'}</Text>
            </TouchableOpacity>
            {sharesExpanded && (
              <View style={styles.sharesCard}>
                {!isOwner && (
                  <Text style={styles.sharedByTxt}>
                    Shared with you by {horse.shares?.find((s) => s.uid === horse.ownerId)?.email || 'the owner'}
                  </Text>
                )}
                {isOwner && (() => {
                  // A provider's team is one line, not one per person: "Willow
                  // Creek Farrier · team (3)". People shared by hand list as before.
                  const teams = {};
                  (horse.shares || []).forEach((s) => { if (s.viaCompany) { if (!teams[s.viaCompany]) teams[s.viaCompany] = []; teams[s.viaCompany].push(s); } });
                  const myClientHorse = isHorseTrainer(horse, auth.currentUser?.uid);
                  return Object.keys(teams).map((cid) => {
                    const members = teams[cid];
                    const name = members.find((m) => m.viaCompanyName)?.viaCompanyName || 'Provider team';
                    return (
                      <View key={'team:' + cid} style={styles.shareRow}>
                        <View style={{ flex: 1 }}>
                          <Text style={styles.shareRowName}>🏢 {name} · team ({members.length})</Text>
                          <Text style={styles.shareRowEmail} numberOfLines={2}>{members.map((m) => m.name || m.email).join(', ')}</Text>
                          <Text style={[styles.shareRowEmail, { marginTop: 2 }]}>Follows their roster automatically.</Text>
                        </View>
                        <View style={[styles.permBadge, styles.permBadgeEdit]}>
                          <Text style={[styles.permBadgeTxt, styles.permBadgeTxtEdit]}>Providers</Text>
                        </View>
                        {!myClientHorse && (
                          <TouchableOpacity onPress={() => handleRemoveTeam(cid, members)} style={styles.revokeBtn} activeOpacity={0.75}>
                            <Text style={styles.revokeBtnTxt}>Remove</Text>
                          </TouchableOpacity>
                        )}
                      </View>
                    );
                  });
                })()}
                {isOwner && (horse.shares || []).filter((s) => !s.viaCompany).map((s) => (
                  <View key={s.uid} style={styles.shareRow}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.shareRowName}>{s.name || s.email}</Text>
                      <Text style={styles.shareRowEmail}>{s.email}</Text>
                    </View>
                    <View style={[styles.permBadge, (s.permission === 'edit' || s.permission === 'trainer') && styles.permBadgeEdit]}>
                      <Text style={[styles.permBadgeTxt, (s.permission === 'edit' || s.permission === 'trainer') && styles.permBadgeTxtEdit]}>
                        {s.permission === 'trainer' ? 'With Trainer' : s.permission === 'edit' ? 'Can Edit' : 'View Only'}
                      </Text>
                    </View>
                    <TouchableOpacity onPress={() => handleRevokeShare(s)} style={styles.revokeBtn} activeOpacity={0.75}>
                      <Text style={styles.revokeBtnTxt}>Revoke</Text>
                    </TouchableOpacity>
                  </View>
                ))}
                {isOwner && (horse.shares || []).length === 0 && (
                  <Text style={styles.sharesEmpty}>Not shared with anyone yet.</Text>
                )}
              </View>
            )}
          </>
        )}

        {/* ── Pending Trainer Requests (owner only) ── */}
        {isOwner && (horse.pendingTrainers || []).length > 0 && (
          <CollapsibleSection sec="trainerReq" title="Provider Requests" badge={(horse.pendingTrainers || []).length || ''} collapsed={collapsedSections.trainerReq} onToggle={() => toggleSection('trainerReq')}>
              {(horse.pendingTrainers || []).map((req) => (
                <View key={req.uid} style={styles.shareRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.shareRowName}>{req.name || req.email}</Text>
                    <Text style={styles.shareRowEmail}>{req.email}</Text>
                    {!!req.requestedByName && (
                      <Text style={[styles.shareRowEmail, { fontStyle: 'italic' }]}>Requested by {req.requestedByName}</Text>
                    )}
                  </View>
                  <TouchableOpacity onPress={() => handleApproveTrainer(req)} style={[styles.revokeBtn, { backgroundColor: '#1D9E7518', borderColor: '#1D9E75' }]} activeOpacity={0.75}>
                    <Text style={[styles.revokeBtnTxt, { color: '#0F6E56' }]}>Approve</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => handleDenyTrainer(req.uid)} style={styles.revokeBtn} activeOpacity={0.75}>
                    <Text style={styles.revokeBtnTxt}>Deny</Text>
                  </TouchableOpacity>
                </View>
              ))}
          </CollapsibleSection>
        )}

        {/* ── Details card ── */}
        {(horse.registeredName || horse.registryNumber || horse.breed || horse.color || horse.height || horse.location || horse.stall || (horse.status && horse.status.length > 0) || !!ownerLabel) && (
          <CollapsibleSection sec="details" title="Details" collapsed={collapsedSections.details} onToggle={() => toggleSection('details')}>
              {!!ownerLabel && (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Record Owner</Text>
                  <Text style={styles.detailValue}>{ownerLabel}</Text>
                </View>
              )}
              {horse.registeredName && (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Registered Name</Text>
                  <Text style={styles.detailValue}>{horse.registeredName}</Text>
                </View>
              )}
              {horse.registryNumber && (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Registry Number</Text>
                  <Text style={styles.detailValue}>{horse.registryNumber}</Text>
                </View>
              )}
              {horse.breed && (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Breed</Text>
                  <Text style={styles.detailValue}>{horse.breed}</Text>
                </View>
              )}
              {horse.color && (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Color</Text>
                  <Text style={styles.detailValue}>{horse.color}</Text>
                </View>
              )}
              {horse.height && (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Height</Text>
                  <Text style={styles.detailValue}>{horse.height}</Text>
                </View>
              )}
              {horse.location && (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Location</Text>
                  <Text style={styles.detailValue}>{horse.location}</Text>
                </View>
              )}
              {!!horse.stall && (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Stall #</Text>
                  <Text style={styles.detailValue}>{horse.stall}</Text>
                </View>
              )}
              {horse.status && horse.status.length > 0 && (
                <View style={styles.statusChipRow}>
                  {horse.status.map((s) => (
                    <View key={s} style={styles.statusChip}>
                      <Text style={styles.statusChipTxt}>{s}</Text>
                    </View>
                  ))}
                </View>
              )}
          </CollapsibleSection>
        )}

        {/* ── Pedigree card ── */}
        {(horse.sire || horse.dam || GRANDPARENT_FIELDS.some((f) => horse[f.key])) && (
          <CollapsibleSection sec="pedigree" title="Pedigree" collapsed={collapsedSections.pedigree} onToggle={() => toggleSection('pedigree')}>
              <View style={styles.pedigreeRow}>
                <View style={styles.pedigreeCell}>
                  <Text style={styles.detailLabel}>Sire</Text>
                  <Text style={styles.detailValue}>{horse.sire || '—'}</Text>
                </View>
                <View style={styles.pedigreeCell}>
                  <Text style={styles.detailLabel}>Dam</Text>
                  <Text style={styles.detailValue}>{horse.dam || '—'}</Text>
                </View>
              </View>
              {GRANDPARENT_FIELDS.some((f) => horse[f.key]) && (
                <>
                  <View style={styles.divider} />
                  <View style={styles.pedigreeRow}>
                    {GRANDPARENT_FIELDS.slice(0, 2).map((f) => (
                      <View key={f.key} style={styles.pedigreeCell}>
                        <Text style={styles.detailLabel}>{f.label}</Text>
                        <Text style={styles.detailValue}>{horse[f.key] || '—'}</Text>
                      </View>
                    ))}
                  </View>
                  <View style={styles.pedigreeRow}>
                    {GRANDPARENT_FIELDS.slice(2, 4).map((f) => (
                      <View key={f.key} style={styles.pedigreeCell}>
                        <Text style={styles.detailLabel}>{f.label}</Text>
                        <Text style={styles.detailValue}>{horse[f.key] || '—'}</Text>
                      </View>
                    ))}
                  </View>
                </>
              )}
          </CollapsibleSection>
        )}

        {/* ── Family card ── */}
        {(() => {
          const parents = [
            horse.sireId ? { label: 'Sire', name: horse.sire, id: horse.sireId } : null,
            horse.damId ? { label: 'Dam', name: horse.dam, id: horse.damId } : null,
          ].filter(Boolean);

          const siblings = allHorses.filter(
            (h) =>
              h.id !== horse.id &&
              ((horse.sireId && h.sireId === horse.sireId) ||
                (horse.damId && h.damId === horse.damId))
          );

          const offspring = allHorses.filter(
            (h) => h.sireId === horse.id || h.damId === horse.id
          );

          // A signed breeding, mirrored here from the plan. Shown to anyone who
          // can see the horse; only the plan's owner can open it, since the plan
          // itself is owner-only.
          const comingSoon = horse.comingSoon || [];

          if (parents.length === 0 && siblings.length === 0 && offspring.length === 0 && comingSoon.length === 0) {
            return null;
          }

          const FamilyRow = ({ label, name, id }) => (
            <TouchableOpacity
              key={`${label}-${name}`}
              style={styles.familyRow}
              activeOpacity={id ? 0.6 : 1}
              disabled={!id}
              onPress={() => id && navigation.navigate('Horse', { horseId: id })}
            >
              <Text style={styles.familyRowLabel}>{label}</Text>
              <Text style={[styles.familyRowName, id && styles.familyRowNameLinked]}>
                {name}
              </Text>
            </TouchableOpacity>
          );

          return (
            <CollapsibleSection sec="family" title="Family" collapsed={collapsedSections.family} onToggle={() => toggleSection('family')}>
                {parents.map((p) => (
                  <FamilyRow key={p.label} label={p.label} name={p.name} id={p.id} />
                ))}
                {siblings.map((h) => (
                  <FamilyRow key={h.id} label="Sibling" name={displayName(h)} id={h.id} />
                ))}
                {offspring.map((h) => (
                  <FamilyRow key={h.id} label="Offspring" name={displayName(h)} id={h.id} />
                ))}
                {comingSoon.map((c) => {
                  const mine = c.ownerId === auth.currentUser?.uid;
                  return (
                    <TouchableOpacity
                      key={c.planId}
                      style={styles.familyRow}
                      activeOpacity={mine ? 0.6 : 1}
                      disabled={!mine}
                      onPress={() => mine && navigation.navigate('BreedingContract', { planId: c.planId })}
                    >
                      <Text style={styles.familyRowLabel}>Coming Soon</Text>
                      <Text style={[styles.familyRowName, mine && styles.familyRowNameLinked]}>
                        {comingSoonText(c)}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
            </CollapsibleSection>
          );
        })()}

        {/* ── Milestones & Tasks ── */}
        <CollapsibleSection sec="tasks" title="Milestones & Tasks" badge={myMilestones.filter((m) => !m.completed).length || ''} collapsed={collapsedSections.tasks} onToggle={() => toggleSection('tasks')}>
            {myMilestones.filter((m) => !m.completed).map((m) => {
              const mDate = getMilestoneDate(horse, m);
              return (
                <View
                  key={m.id}
                  style={[styles.milestone, m.completed && styles.milestoneDone]}
                >
                  <View style={[styles.circle, m.completed && styles.circleDone]}>
                    {m.completed && <Text style={styles.tick}>✓</Text>}
                  </View>
                  <View style={{ flex: 1, marginHorizontal: 12 }}>
                    {!!m.category && (
                      <View style={styles.categoryBadge}>
                        <Text style={styles.categoryBadgeTxt}>{m.category}</Text>
                      </View>
                    )}
                    <Text style={[styles.mName, m.completed && styles.mNameDone]}>
                      {m.name}
                    </Text>
                    <Text style={styles.mDate}>{formatDate(mDate)}</Text>
                    {!m.completed && (
                      <Text style={styles.mDays}>{getDaysLabel(mDate)}</Text>
                    )}
                    {!!m.assignedToName && (
                      <Text style={styles.mAssignee}>👤 {m.assignedToUid === auth.currentUser?.uid ? 'You' : m.assignedToName}</Text>
                    )}
                    {!!m.notes && (
                      <Text style={styles.mNotes}>{m.notes}</Text>
                    )}
                  </View>
                  {canEdit && m.custom && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                      <TouchableOpacity onPress={() => openEditMilestoneModal(m)} activeOpacity={0.7}>
                        <Text style={{ fontSize: 14 }}>✏️</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.deleteMilestoneBtn}
                        onPress={() => handleDeleteMilestone(m.id)}
                        activeOpacity={0.75}
                      >
                        <Text style={styles.deleteMilestoneBtnTxt}>✕</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                  {canEdit && (
                    <TouchableOpacity
                      style={[styles.markBtn, m.completed && styles.markBtnDone]}
                      onPress={() => toggle(m.id)}
                      activeOpacity={0.75}
                    >
                      <Text style={[styles.markBtnTxt, m.completed && styles.markBtnTxtDone]}>
                        {m.completed ? 'Undo' : 'Mark\nComplete'}
                      </Text>
                    </TouchableOpacity>
                  )}
                </View>
              );
            })}
            {canEdit && (
            <TouchableOpacity
              style={styles.addMilestoneBtn}
              onPress={openAddMilestoneModal}
              activeOpacity={0.75}
            >
              <Text style={styles.addMilestoneBtnTxt}>+ Add Task</Text>
            </TouchableOpacity>
            )}

            {myMilestones.some((m) => m.completed) && (
              <TouchableOpacity
                style={styles.recipToggle}
                onPress={() => setShowHistory((v) => !v)}
                activeOpacity={0.75}
              >
                <View style={[styles.recipToggleTrack, showHistory && styles.recipToggleTrackOn]}>
                  <View style={[styles.recipToggleThumb, showHistory && styles.recipToggleThumbOn]} />
                </View>
                <Text style={styles.recipToggleLabel}>
                  Show last completed per category
                </Text>
              </TouchableOpacity>
            )}

            {myMilestones.some((m) => m.completed) && (
              <TouchableOpacity
                style={styles.recipToggle}
                onPress={() => setShowAllCompleted((v) => !v)}
                activeOpacity={0.75}
              >
                <View style={[styles.recipToggleTrack, showAllCompleted && styles.recipToggleTrackOn]}>
                  <View style={[styles.recipToggleThumb, showAllCompleted && styles.recipToggleThumbOn]} />
                </View>
                <Text style={styles.recipToggleLabel}>
                  Show all completed
                </Text>
              </TouchableOpacity>
            )}

            {showAllCompleted && (horse.milestones || [])
              .filter((m) => m.completed)
              .slice()
              .sort((a, b) => getMilestoneDate(horse, b).localeCompare(getMilestoneDate(horse, a)))
              .map((m) => {
                const mDate = getMilestoneDate(horse, m);
                return (
                  <View key={m.id} style={[styles.milestone, styles.milestoneDone]}>
                    <View style={[styles.circle, styles.circleDone]}>
                      <Text style={styles.tick}>✓</Text>
                    </View>
                    <TouchableOpacity
                      style={{ flex: 1, marginHorizontal: 12 }}
                      onPress={() => m.custom ? openEditMilestoneModal(m) : null}
                      activeOpacity={m.custom ? 0.6 : 1}
                    >
                      {!!m.category && (
                        <View style={styles.categoryBadge}>
                          <Text style={styles.categoryBadgeTxt}>{m.category}</Text>
                        </View>
                      )}
                      <Text style={[styles.mName, styles.mNameDone]}>{m.name}</Text>
                      <Text style={styles.mDate}>✓ Completed {formatDate(m.completedDate || mDate)}</Text>
                      {!!m.notes && (
                        <Text style={styles.mNotes}>{m.notes}</Text>
                      )}
                      {m.custom && (
                        <Text style={{ fontSize: 12, color: GREEN, marginTop: 4 }}>Tap to view / edit ✏️</Text>
                      )}
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.markBtn, styles.markBtnDone]}
                      onPress={() => toggle(m.id)}
                      activeOpacity={0.75}
                    >
                      <Text style={[styles.markBtnTxt, styles.markBtnTxtDone]}>Undo</Text>
                    </TouchableOpacity>
                  </View>
                );
              })}

            {showHistory && (() => {
              const completed = (horse.milestones || []).filter((m) => m.completed);
              const latestByKey = {};
              completed.forEach((m) => {
                const key = m.category || m.name;
                const mDate = getMilestoneDate(horse, m);
                if (!latestByKey[key] || mDate > getMilestoneDate(horse, latestByKey[key])) {
                  latestByKey[key] = m;
                }
              });
              return Object.entries(latestByKey).map(([key, m]) => {
                const mDate = getMilestoneDate(horse, m);
                return (
                  <View key={m.id} style={[styles.milestone, styles.milestoneDone]}>
                    <View style={[styles.circle, styles.circleDone]}>
                      <Text style={styles.tick}>✓</Text>
                    </View>
                    <TouchableOpacity
                      style={{ flex: 1, marginHorizontal: 12 }}
                      onPress={() => m.custom ? openEditMilestoneModal(m) : null}
                      activeOpacity={m.custom ? 0.6 : 1}
                    >
                      <Text style={styles.mName}>{key}</Text>
                      <Text style={styles.mDate}>Last: {formatDate(mDate)}</Text>
                      {!!m.notes && (
                        <Text style={styles.mNotes}>{m.notes}</Text>
                      )}
                      {m.custom && (
                        <Text style={{ fontSize: 12, color: GREEN, marginTop: 4 }}>Tap to view / edit ✏️</Text>
                      )}
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.markBtn, styles.markBtnDone]}
                      onPress={() => toggle(m.id)}
                      activeOpacity={0.75}
                    >
                      <Text style={[styles.markBtnTxt, styles.markBtnTxtDone]}>Undo</Text>
                    </TouchableOpacity>
                  </View>
                );
              });
            })()}
          </CollapsibleSection>

        {/* ── Earnings & Accomplishments ── */}
        {horse.type !== 'embryo' && (() => {
          const horseEntries = (horse.earnings || []).filter((e) => e.type === 'horse');
          // Offspring rows include foals currently in the stable, derived live.
          const offspringEntries = allOffspringEntries(horse, allHorses);
          const allEarnings = [...horseEntries, ...offspringEntries].sort((a, b) =>
            String(b.date || '').localeCompare(String(a.date || ''))
          );
          const horseTotal = horseEntries.reduce((s, e) => s + (e.amount || 0), 0);
          const offspringTotal = offspringEntries.reduce((s, e) => s + (e.amount || 0), 0);
          const hasOffspring = offspringEntries.length > 0;
          const fmt = (n) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          // group offspring by name for per-offspring totals
          const offspringByName = offspringEntries.reduce((acc, e) => {
            const name = e.offspringName || 'Unknown';
            acc[name] = (acc[name] || 0) + (e.amount || 0);
            return acc;
          }, {});
          return (
            <CollapsibleSection sec="earnings" title="Earnings & Accomplishments" collapsed={collapsedSections.earnings} onToggle={() => toggleSection('earnings')}>
              {/* Totals row */}
              <View style={styles.earningsTotalsRow}>
                <View style={styles.earningsTotalCell}>
                  <Text style={styles.earningsTotalLabel}>Horse Earnings</Text>
                  <Text style={[styles.earningsTotalValue, { color: GREEN }]}>{fmt(horseTotal)}</Text>
                </View>
                {hasOffspring && (
                  <>
                    <View style={styles.earningsTotalDivider} />
                    <View style={styles.earningsTotalCell}>
                      <Text style={styles.earningsTotalLabel}>Offspring Earnings</Text>
                      <Text style={[styles.earningsTotalValue, { color: RED }]}>{fmt(offspringTotal)}</Text>
                    </View>
                  </>
                )}
              </View>

              {canEdit && (
                <TouchableOpacity
                  style={styles.addMilestoneBtn}
                  onPress={openAddEarningsModal}
                  activeOpacity={0.75}
                >
                  <Text style={styles.addMilestoneBtnTxt}>+ Add Event</Text>
                </TouchableOpacity>
              )}

              {(
                <>
                  {/* Per-offspring breakdown */}
                  {hasOffspring && Object.keys(offspringByName).length > 0 && (
                    <View style={styles.offspringBreakdownCard}>
                      <Text style={styles.offspringBreakdownTitle}>Offspring Breakdown</Text>
                      {Object.entries(offspringByName).sort((a, b) => b[1] - a[1]).map(([name, total]) => {
                        const inStable = offspringInStable(horse, allHorses).find((c) => c.type === 'born' && normalizeHorseName(displayName(c)) === normalizeHorseName(name));
                        return (
                          <TouchableOpacity
                            key={name}
                            style={styles.offspringBreakdownRow}
                            onPress={() => (inStable ? navigation.navigate('Horse', { horseId: inStable.id }) : setBreakdownName(name))}
                            activeOpacity={0.7}
                          >
                            <View style={{ flex: 1 }}>
                              <Text style={styles.offspringBreakdownName}>{name}</Text>
                              <Text style={{ fontSize: 11, color: MUTED }}>{inStable ? 'In your stable · open profile' : 'Tap for their earnings'}</Text>
                            </View>
                            <Text style={styles.offspringBreakdownAmt}>{fmt(total)}</Text>
                            <Text style={{ fontSize: 14, color: MUTED, marginLeft: 8 }}>›</Text>
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  )}

                  {allEarnings.length === 0 ? (
                    <Text style={styles.noNotesTxt}>No events recorded yet.</Text>
                  ) : (
                    allEarnings.map((entry) => (
                      <TouchableOpacity
                        key={entry.id}
                        style={styles.earningsCard}
                        onPress={() => entry.derived
                          ? navigation.push('Horse', { horseId: entry.fromHorseId })
                          : openViewEarnings(entry)}
                        activeOpacity={0.75}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
                          <View style={[styles.earningsTypeBadge, entry.type === 'offspring' && styles.earningsTypeBadgeOffspring]}>
                            <Text style={[styles.earningsTypeBadgeTxt, entry.type === 'offspring' && { color: RED }]}>
                              {entry.type === 'offspring' ? (entry.offspringName || 'Offspring') : 'Horse'}
                            </Text>
                          </View>
                          <Text style={styles.earningsEventDate}>{formatDate(entry.date)}</Text>
                        </View>
                        <Text style={styles.earningsEventName}>{entry.eventName}</Text>
                        {entry.amount > 0 && (
                          <Text style={styles.earningsEventAmount}>{fmt(entry.amount)}</Text>
                        )}
                        {!!entry.notes && (
                          <Text style={styles.earningsEventNotes} numberOfLines={2}>{entry.notes}</Text>
                        )}
                        {entry.derived && (
                          <Text style={styles.earningsEventNotes}>From this horse's record · tap to open ›</Text>
                        )}
                        {(() => {
                          const dupOf = duplicateOffspringSource(entry, horse, allHorses);
                          if (!dupOf) return null;
                          return (
                            <View style={styles.dupWarnBox}>
                              <Text style={styles.dupWarnTxt}>
                                ⚠️ Possible duplicate — {displayName(dupOf)} is in your stable, so this event is
                                already counted automatically. Both are being added to the total.
                              </Text>
                              {canEdit && (
                                <TouchableOpacity onPress={() => handleDeleteEarnings(entry.id)} activeOpacity={0.7}>
                                  <Text style={styles.dupWarnDelete}>Delete this typed entry</Text>
                                </TouchableOpacity>
                              )}
                            </View>
                          );
                        })()}
                      </TouchableOpacity>
                    ))
                  )}
                </>
              )}
            </CollapsibleSection>
          );
        })()}

        {/* ── Incentive Nominations ── */}
        {(() => {
          const incentives = horse.incentives || [];
          const totalCost = incentives.reduce((s, i) => s + (i.cost || 0), 0);
          const fmt = (n) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const freqLabel = (f) => f === 'annual' ? 'Annual' : f === 'other' ? 'Other' : 'One-time';
          return (
            <CollapsibleSection sec="incentive" title="Incentive Nominations" badge={incentives.length > 0 ? fmt(totalCost) : ''} collapsed={collapsedSections.incentive} onToggle={() => toggleSection('incentive')}>
              {canEdit && (
                <TouchableOpacity style={styles.addMilestoneBtn} onPress={openAddIncentiveModal} activeOpacity={0.75}>
                  <Text style={styles.addMilestoneBtnTxt}>+ Add Nomination</Text>
                </TouchableOpacity>
              )}

              {(
                incentives.length === 0 ? (
                  <Text style={styles.noNotesTxt}>No nominations yet.</Text>
                ) : (
                  incentives.map((inc) => (
                    <TouchableOpacity key={inc.id} style={styles.earningsCard} onPress={() => openEditIncentiveModal(inc)} activeOpacity={0.75}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
                        <View style={styles.categoryBadge}>
                          <Text style={styles.categoryBadgeTxt}>{freqLabel(inc.frequency)}</Text>
                        </View>
                        {!!inc.datePaid && (
                          <Text style={styles.earningsEventDate}>Last paid {inc.datePaid.slice(0, 4)}</Text>
                        )}
                      </View>
                      <Text style={styles.earningsEventName}>{inc.name}</Text>
                      <View style={{ flexDirection: 'row', gap: 18, marginTop: 6 }}>
                        <Text style={{ fontSize: 12, color: GREEN }}>Tap to edit ✏️</Text>
                        <TouchableOpacity onPress={() => handleDeleteIncentive(inc.id)} activeOpacity={0.7}>
                          <Text style={{ fontSize: 12, color: RED }}>Delete</Text>
                        </TouchableOpacity>
                      </View>
                    </TouchableOpacity>
                  ))
                )
              )}
            </CollapsibleSection>
          );
        })()}

        {/* ── Trainer Contact Info (trainer's view only) ── */}
        {isHorseTrainer(horse, auth.currentUser?.uid) && (() => {
          const oc = getOwnerContact(horse) || {};
          return (
          <CollapsibleSection sec="contact" title="Owner Contact" collapsed={collapsedSections.contact} onToggle={() => toggleSection('contact')}>
              {oc.name ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                  <Text style={{ fontSize: 15, marginRight: 8 }}>👤</Text>
                  <Text style={[styles.noteText, { flex: 1 }]}>{oc.name}</Text>
                </View>
              ) : null}
              {oc.email ? (
                <TouchableOpacity
                  style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}
                  onPress={() => Linking.openURL(`mailto:${oc.email}`)}
                  activeOpacity={0.7}
                >
                  <Text style={{ fontSize: 15, marginRight: 8 }}>✉️</Text>
                  <Text style={[styles.noteText, { flex: 1, color: GREEN }]}>{oc.email}</Text>
                </TouchableOpacity>
              ) : null}
              {oc.phone ? (
                <TouchableOpacity
                  style={{ flexDirection: 'row', alignItems: 'center' }}
                  onPress={() => Linking.openURL(`tel:${oc.phone}`)}
                  activeOpacity={0.7}
                >
                  <Text style={{ fontSize: 15, marginRight: 8 }}>📞</Text>
                  <Text style={[styles.noteText, { flex: 1, color: GREEN }]}>{oc.phone}</Text>
                </TouchableOpacity>
              ) : null}
              {!oc.name && !oc.email && !oc.phone && (
                <Text style={{ fontSize: 13, color: MUTED }}>No owner contact saved for this horse yet.</Text>
              )}
              <TouchableOpacity onPress={openOwnerEdit} activeOpacity={0.7} style={{ paddingTop: 12 }}>
                <Text style={{ fontSize: 13, color: GREEN, fontWeight: '700' }}>
                  {oc.name || oc.email || oc.phone ? 'Edit owner contact' : 'Add owner contact'}
                </Text>
              </TouchableOpacity>
          </CollapsibleSection>
          );
        })()}

        {/* ── Health & Lameness ── */}
        <CollapsibleSection sec="health" title="Health & Lameness" badge={incidents.filter((i) => i.status !== 'resolved').length || ''} collapsed={collapsedSections.health} onToggle={() => toggleSection('health')}>
            <View style={styles.healthToggleRow}>
              <TouchableOpacity
                style={[styles.healthToggleBtn, healthView === 'list' && styles.healthToggleBtnActive]}
                onPress={() => setHealthView('list')}
                activeOpacity={0.75}
              >
                <Text style={[styles.healthToggleTxt, healthView === 'list' && styles.healthToggleTxtActive]}>List</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.healthToggleBtn, healthView === 'calendar' && styles.healthToggleBtnActive]}
                onPress={() => setHealthView('calendar')}
                activeOpacity={0.75}
              >
                <Text style={[styles.healthToggleTxt, healthView === 'calendar' && styles.healthToggleTxtActive]}>Calendar</Text>
              </TouchableOpacity>
            </View>
            {healthView === 'list' && (
              <View style={styles.healthToggleRow}>
                <TouchableOpacity
                  style={[styles.healthToggleBtn, !showAllIncidents && styles.healthToggleBtnActive]}
                  onPress={() => setShowAllIncidents(false)}
                  activeOpacity={0.75}
                >
                  <Text style={[styles.healthToggleTxt, !showAllIncidents && styles.healthToggleTxtActive]}>Ongoing</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.healthToggleBtn, showAllIncidents && styles.healthToggleBtnActive]}
                  onPress={() => setShowAllIncidents(true)}
                  activeOpacity={0.75}
                >
                  <Text style={[styles.healthToggleTxt, showAllIncidents && styles.healthToggleTxtActive]}>All</Text>
                </TouchableOpacity>
              </View>
            )}

            {(() => {
              // Distinct tags present across this horse's incidents (for the filter row).
              const presentTags = Array.from(new Set(incidents.flatMap((i) => i.tags || []))).sort();
              return presentTags.length > 0 ? (
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, paddingBottom: 10 }}>
                  <TouchableOpacity
                    style={[styles.categoryChip, !incidentTagFilter && styles.categoryChipActive]}
                    onPress={() => setIncidentTagFilter(null)}
                    activeOpacity={0.75}
                  >
                    <Text style={[styles.categoryChipTxt, !incidentTagFilter && styles.categoryChipTxtActive]}>All types</Text>
                  </TouchableOpacity>
                  {presentTags.map((tag) => (
                    <TouchableOpacity
                      key={tag}
                      style={[styles.categoryChip, incidentTagFilter === tag && styles.categoryChipActive]}
                      onPress={() => setIncidentTagFilter((prev) => (prev === tag ? null : tag))}
                      activeOpacity={0.75}
                    >
                      <Text style={[styles.categoryChipTxt, incidentTagFilter === tag && styles.categoryChipTxtActive]}>{tag}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              ) : null;
            })()}

            {healthView === 'calendar' && (
              <IncidentCalendar
                incidents={incidents.filter((i) => !incidentTagFilter || (i.tags || []).includes(incidentTagFilter))}
                onIncidentPress={(i) => navigation.navigate('Incident', { horseId, incidentId: i.id })}
              />
            )}

            {healthView === 'list' && (() => {
              const shown = incidents.filter((i) =>
                (showAllIncidents || i.status !== 'resolved') &&
                (!incidentTagFilter || (i.tags || []).includes(incidentTagFilter))
              );
              if (shown.length === 0) {
                return (
                  <Text style={styles.healthEmpty}>
                    {incidentTagFilter ? `No ${incidentTagFilter} incidents${showAllIncidents ? '' : ' ongoing'}.`
                      : showAllIncidents ? 'No incidents recorded.' : 'No ongoing incidents. 🎉'}
                  </Text>
                );
              }
              return shown.map((i) => {
                const resolved = i.status === 'resolved';
                return (
                  <TouchableOpacity
                    key={i.id}
                    style={styles.incidentRow}
                    onPress={() => navigation.navigate('Incident', { horseId, incidentId: i.id })}
                    activeOpacity={0.7}
                  >
                    <View style={{ flex: 1 }}>
                      <Text style={styles.incidentName}>{i.name}</Text>
                      <Text style={styles.incidentMeta}>
                        {formatDate(i.dateOccurred)}
                        {resolved && i.dateResolved ? ` → Sound ${formatDate(i.dateResolved)}` : ''}
                      </Text>
                      {(i.tags || []).length > 0 && (
                        <View style={styles.incidentTagWrap}>
                          {i.tags.map((t) => (
                            <View key={t} style={styles.incidentTagChip}>
                              <Text style={styles.incidentTagChipTxt}>{t}</Text>
                            </View>
                          ))}
                        </View>
                      )}
                    </View>
                    <View style={[styles.incidentStatus, resolved ? styles.incidentStatusSound : styles.incidentStatusOngoing]}>
                      <Text style={[styles.incidentStatusTxt, resolved ? styles.incidentStatusTxtSound : styles.incidentStatusTxtOngoing]}>
                        {resolved ? 'Sound' : 'Ongoing'}
                      </Text>
                    </View>
                  </TouchableOpacity>
                );
              });
            })()}

            {canEdit && (
              <TouchableOpacity style={styles.createIncidentBtn} onPress={openCreateIncident} activeOpacity={0.85}>
                <Text style={styles.createIncidentBtnTxt}>+ Create Incident</Text>
              </TouchableOpacity>
            )}
        </CollapsibleSection>

        {/* ── Provider Notes (grouped by provider) ── */}
        {(() => {
          const uid = auth.currentUser?.uid;
          const isTrainer = isHorseTrainer(horse, uid);
          const isOwner = horse.ownerId === uid || (!horse.ownerId && horse.members?.[0] === uid);
          const allNotes = horse.trainingNotes || [];
          const myCompany = normalizeCompany(businessProfile?.name) || ('u:' + uid);
          const mineNote = (n) => entryCompany(n) === myCompany;
          // Own company's notes always; plus others' non-private notes if owner/viewer.
          const canSeeOthers = isOwner || (horse.ledgerViewers || []).includes(uid) || (!isTrainer);
          const visibleNotes = allNotes.filter((n) => mineNote(n) || (canSeeOthers && !n.private));
          // Only surface this section when it can actually do something: the horse
          // has a provider, or notes already exist. With no provider the general
          // Notes section covers the same ground, so this was just a dead header.
          // The visibleNotes clause keeps history reachable after a provider is
          // removed — gating on horseHasTrainer alone would hide existing notes.
          if (!horseHasTrainer(horse) && visibleNotes.length === 0) return null;
          // Group by company (business name), colleagues merge.
          const gm = {};
          for (const n of visibleNotes) {
            const k = entryCompany(n);
            (gm[k] = gm[k] || { key: k, uid: n.createdByUid, name: (n.providerName || '').trim() || n.createdByName || 'Provider', notes: [] }).notes.push(n);
            if ((n.providerName || '').trim()) gm[k].name = n.providerName.trim();
          }
          const groups = Object.values(gm).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          const grouped = groups.length > 1;
          return (
            <CollapsibleSection sec="trainingNotes" title="Provider Notes" collapsed={collapsedSections.trainingNotes} onToggle={() => toggleSection('trainingNotes')}>
              {visibleNotes.length === 0 ? (
                <Text style={styles.noNotesTxt}>No provider notes yet.</Text>
              ) : grouped ? (
                groups.map((g) => (
                  <NotesProviderGroup key={g.key} name={g.name} notes={g.notes} canEditNote={(n) => canEdit && mineNote(n)}
                    onEdit={(n) => { setEditingTrainingNote(n); setTrainingNoteText(n.text); setTrainingNotePrivate(!!n.private); setShowAddTrainingNoteModal(true); }}
                    onDelete={handleDeleteTrainingNote} defaultCollapsed={groups.length > 2} />
                ))
              ) : (
                visibleNotes.slice().reverse().map((n) => (
                  <View key={n.id} style={styles.notesCard}>
                    <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 6 }}>
                      {n.private && <Text style={{ fontSize: 13, color: MUTED }}>🔒</Text>}
                      <Text style={[styles.noteText, { flex: 1 }]}>{n.text}</Text>
                      {canEdit && mineNote(n) && (
                        <View style={{ flexDirection: 'row', gap: 12 }}>
                          <TouchableOpacity onPress={() => { setEditingTrainingNote(n); setTrainingNoteText(n.text); setTrainingNotePrivate(!!n.private); setShowAddTrainingNoteModal(true); }} activeOpacity={0.7}><Text style={{ fontSize: 13, color: MUTED }}>✏️</Text></TouchableOpacity>
                          <TouchableOpacity onPress={() => handleDeleteTrainingNote(n.id)} activeOpacity={0.7}><Text style={{ fontSize: 13, color: RED }}>🗑</Text></TouchableOpacity>
                        </View>
                      )}
                    </View>
                    <Text style={styles.noteDate}>{n.createdByName} · {formatDate(n.date)}</Text>
                  </View>
                ))
              )}
              {canEdit && (
                <TouchableOpacity style={styles.addMilestoneBtn} onPress={() => { setTrainingNoteText(''); setTrainingNotePrivate(false); setShowAddTrainingNoteModal(true); }} activeOpacity={0.75}>
                  <Text style={styles.addMilestoneBtnTxt}>+ Add Provider Note</Text>
                </TouchableOpacity>
              )}
            </CollapsibleSection>
          );
        })()}

        {/* ── Billing Ledger (grouped by provider) ── */}
        {horseHasTrainer(horse)
          && (isHorseTrainer(horse, auth.currentUser?.uid)
            || horse.ownerId === auth.currentUser?.uid
            || (!horse.ownerId && horse.members?.[0] === auth.currentUser?.uid)
            || (horse.ledgerViewers || []).includes(auth.currentUser?.uid))
          && (() => {
          const uid = auth.currentUser?.uid;
          const isTrainer = isHorseTrainer(horse, uid);
          const isOwner = horse.ownerId === uid || (!horse.ownerId && horse.members?.[0] === uid);
          const isLedgerViewer = isOwner || (horse.ledgerViewers || []).includes(uid);
          const myCompany = normalizeCompany(businessProfile?.name) || ('u:' + uid);
          const mine = (e) => entryCompany(e) === myCompany; // same business name = same company
          const all = ledgerEntries.filter((e) => !e.deleted);
          // A user always sees & controls their OWN entries, even ones created
          // before Provider info existed (which don't match any company name).
          const isMineOrOwn = (e) => mine(e) || e.trainerUid === uid;
          const visible = isLedgerViewer ? all : all.filter(isMineOrOwn);
          const deletedLedger = ledgerEntries.filter((e) => e.deleted && !e.refunded && (isLedgerViewer || isMineOrOwn(e)));
          // Refunded online payments stay visible (crossed out) but never count.
          const refundedShown = ledgerEntries.filter((e) => e.deleted && e.refunded && (isLedgerViewer || isMineOrOwn(e)));
          const byNewest = (a, b) => (b.date || '').localeCompare(a.date || '');
          const withRefunds = (list, extra) => (ledgerShowOutstanding ? list : [...list, ...extra].sort(byNewest));
          const canEditEntry = (e) => isTrainer && isMineOrOwn(e);
          // My own entries that aren't yet linked to my current business identity
          // ("floating" — e.g. charges added before Provider info was a thing).
          const myFloating = (isTrainer && businessProfile?.name) ? all.filter((e) => e.trainerUid === uid && !mine(e)) : [];
          const sumBal = (arr) => arr.filter((e) => e.type === 'charge').reduce((s, e) => s + (e.amount || 0), 0) - arr.filter((e) => e.type === 'payment').reduce((s, e) => s + (e.amount || 0), 0);
          const balance = sumBal(visible);
          const fmt = (n) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const filterEntries = (arr) => ledgerShowOutstanding ? arr.filter((e) => e.type === 'charge' && ((e.amount || 0) - paidTowardCharge(e.id)) > 0.005) : arr;
          // Group visible entries by company (business name), colleagues merge.
          const gm = {};
          for (const e of visible) {
            const k = entryCompany(e);
            (gm[k] = gm[k] || { key: k, uid: e.trainerUid, name: (e.providerName || '').trim(), entries: [] }).entries.push(e);
            if ((e.providerName || '').trim()) gm[k].name = e.providerName.trim();
          }
          const groups = Object.values(gm).map((g) => {
            if (!g.name) { const t = getHorseTrainers(horse).find((x) => x.uid === g.uid); g.name = (t && (t.name || t.email)) || 'Provider'; }
            g.balance = sumBal(g.entries);
            g.displayEntries = withRefunds(filterEntries(g.entries), refundedShown.filter((e) => entryCompany(e) === g.key));
            return g;
          }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          const grouped = isLedgerViewer && groups.length > 1;
          const myOutstanding = all.filter((e) => e.type === 'charge' && mine(e) && ((e.amount || 0) - paidTowardCharge(e.id)) > 0.005);
          const myRecurring = (horse.recurringCharges || []).filter((t) => !t.trainerUid || t.trainerUid === uid || (normalizeCompany(t.providerName) && normalizeCompany(t.providerName) === normalizeCompany(businessProfile?.name)));
          return (
            <CollapsibleSection sec="ledger" title="Billing Ledger" badge={balance > 0 ? `Owed ${fmt(balance)}` : 'Paid up'} collapsed={collapsedSections.ledger} onToggle={() => toggleSection('ledger')}>
              {visible.length > 0 && (
                <View style={{ flexDirection: 'row', gap: 8, marginBottom: 10 }}>
                  {[{ k: false, l: 'All' }, { k: true, l: 'Outstanding' }].map((o) => (
                    <TouchableOpacity key={o.l} onPress={() => setLedgerShowOutstanding(o.k)} activeOpacity={0.75} style={[styles.categoryChip, ledgerShowOutstanding === o.k && styles.categoryChipActive]}>
                      <Text style={[styles.categoryChipTxt, ledgerShowOutstanding === o.k && styles.categoryChipTxtActive]}>{o.l}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}

              {myFloating.length > 0 && (
                <TouchableOpacity onPress={() => handleAssignFloating(myFloating)} activeOpacity={0.8} style={{ backgroundColor: '#FBF3E2', borderRadius: 10, borderWidth: 0.5, borderColor: '#E4CF9C', padding: 12, marginBottom: 10 }}>
                  <Text style={{ fontSize: 13, fontWeight: '700', color: '#8A6D2F' }}>⚠️ {myFloating.length} earlier {myFloating.length === 1 ? 'entry isn’t' : 'entries aren’t'} linked to {businessProfile.name}</Text>
                  <Text style={{ fontSize: 12, color: MUTED, marginTop: 3 }}>These were added before your Provider info existed, so they can’t be grouped or invoiced yet. Tap to link them to {businessProfile.name}.</Text>
                  <Text style={{ fontSize: 13, color: GREEN, fontWeight: '700', marginTop: 6 }}>Link {myFloating.length === 1 ? 'it' : 'them'} →</Text>
                </TouchableOpacity>
              )}

              {grouped ? (
                groups.map((g) => (
                  <LedgerProviderGroup key={g.key} group={g} fmt={fmt} filterEntries={filterEntries} paidTowardCharge={paidTowardCharge} canEditEntry={canEditEntry} onEdit={openEditLedger} onDelete={handleDeleteLedgerEntry} defaultCollapsed={groups.length > 2} horseId={horseId} isProvider={g.key === myCompany} />
                ))
              ) : (() => {
                const displayed = withRefunds(filterEntries(visible), refundedShown);
                // One provider on this ledger: the pay button sits above the list.
                const only = groups.length === 1 ? groups[0] : null;
                const payBtn = only ? <LedgerPayButton horseId={horseId} group={only} isProvider={only.key === myCompany} /> : null;
                if (payBtn) return <>{payBtn}{displayed.length === 0
                  ? <Text style={styles.noNotesTxt}>{ledgerShowOutstanding ? 'Nothing outstanding — all charges are paid.' : 'No entries yet.'}</Text>
                  : <LedgerList count={displayed.length}>{displayed.map((e) => <LedgerRow key={e.id} e={e} fmt={fmt} paidTowardCharge={paidTowardCharge} canEdit={canEditEntry(e)} onEdit={openEditLedger} onDelete={handleDeleteLedgerEntry} />)}</LedgerList>}</>;
                return displayed.length === 0
                  ? <Text style={styles.noNotesTxt}>{ledgerShowOutstanding ? 'Nothing outstanding — all charges are paid.' : 'No entries yet.'}</Text>
                  : <LedgerList count={displayed.length}>{displayed.map((e) => <LedgerRow key={e.id} e={e} fmt={fmt} paidTowardCharge={paidTowardCharge} canEdit={canEditEntry(e)} onEdit={openEditLedger} onDelete={handleDeleteLedgerEntry} />)}</LedgerList>;
              })()}

              {isTrainer && creditAvailable() > 0.005 && (
                <View style={{ marginTop: 10, backgroundColor: '#F0F6EE', borderRadius: 10, borderWidth: 0.5, borderColor: '#CBE0C2', padding: 12 }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ fontSize: 13, fontWeight: '700', color: '#3B6D11' }}>💳 Credit on account</Text>
                    <Text style={{ fontSize: 14, fontWeight: '700', color: '#3B6D11' }}>{fmt(creditAvailable())}</Text>
                  </View>
                  <Text style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>Prepayment not yet applied to a charge. It reduces the balance due until you apply it.</Text>
                  {myOutstanding.length > 0 && (
                    <TouchableOpacity onPress={() => setShowApplyCreditModal(true)} activeOpacity={0.7} style={{ marginTop: 6 }}>
                      <Text style={{ fontSize: 13, color: GREEN, fontWeight: '600' }}>Apply to a charge →</Text>
                    </TouchableOpacity>
                  )}
                </View>
              )}

              {isTrainer && myRecurring.length > 0 && (
                <View style={{ marginTop: 8 }}>
                  {myRecurring.map((t) => (
                    <View key={t.id} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6 }}>
                      <Text style={{ fontSize: 13, marginRight: 6 }}>🔁</Text>
                      <Text style={{ flex: 1, fontSize: 13, color: MUTED }}>{t.description} — {fmt(t.amount)}/mo (day {Math.min(28, Math.max(1, t.dayOfMonth || 1))})</Text>
                      <TouchableOpacity onPress={() => handleRemoveRecurring(t.id)} activeOpacity={0.7}><Text style={{ fontSize: 12, color: RED, fontWeight: '600' }}>Stop</Text></TouchableOpacity>
                    </View>
                  ))}
                </View>
              )}

              {isTrainer && (
                <>
                  <View style={{ flexDirection: 'row', gap: 10, marginTop: 8 }}>
                    <TouchableOpacity style={[styles.addMilestoneBtn, { flex: 1 }]} onPress={() => { setChargeDescription(''); setChargeNote(''); setChargeAmount(''); setChargeDate(new Date()); setShowChargeDatePicker(false); setChargeRecurring(false); setChargeRecurDay('1'); setShowAddChargeModal(true); }} activeOpacity={0.75}>
                      <Text style={styles.addMilestoneBtnTxt}>+ Add Charge</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={[styles.addMilestoneBtn, { flex: 1 }]} onPress={() => { setPaymentAmount(''); setPaymentNote(''); setPaymentDate(new Date()); setShowPaymentDatePicker(false); setPaymentAllocations({}); setShowAddPaymentModal(true); }} activeOpacity={0.75}>
                      <Text style={styles.addMilestoneBtnTxt}>Record Payment</Text>
                    </TouchableOpacity>
                  </View>
                  {isInvoiceAvailable() && (
                    <TouchableOpacity style={[styles.saveBtn, { marginTop: 10 }]} onPress={() => { setInvoiceChooseNew(false); setInvoiceIncludePrevious(true); setInvoiceSelectedIds({}); setInvoiceNotes(businessProfile?.terms || ''); setShowInvoiceModal(true); }} activeOpacity={0.85}>
                      <Text style={styles.saveBtnText}>📄  Create Invoice</Text>
                    </TouchableOpacity>
                  )}
                  {isInvoiceAvailable() && (
                    <TouchableOpacity style={[styles.addMilestoneBtn, { marginTop: 8 }]} onPress={() => { setStatementRange('this_year'); setInvoiceNotes(''); setShowStatementModal(true); }} activeOpacity={0.85}>
                      <Text style={styles.addMilestoneBtnTxt}>📊  Account Statement</Text>
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity onPress={openBusinessModal} activeOpacity={0.7} style={{ marginTop: 8, alignItems: 'center' }}>
                    <Text style={{ fontSize: 12, color: MUTED }}>{businessProfile?.name ? `Invoices from: ${businessProfile.name} · Edit` : 'Set up your business info for invoices'}</Text>
                  </TouchableOpacity>

                  {(() => {
                    // From stamped charges, and from invoice PDFs filed on this
                    // horse. Either alone can be missing — a multi-horse invoice
                    // stamps charges on each horse in a separate write, so one can
                    // fail while the document lands — and an invoice you cannot
                    // see is an invoice you cannot void.
                    const stamped = all.filter((e) => e.type === 'charge' && mine(e)).flatMap((e) => invoiceListOf(e));
                    const filed = (horse.documents || [])
                      .filter((d) => d.docType === 'Invoice')
                      .map((d) => ((d.name || '').match(/INV-\d+/) || [])[0])
                      .filter(Boolean);
                    const nums = [...new Set([...stamped, ...filed])].sort().reverse();
                    if (nums.length === 0) return null;
                    // A long-running horse builds up a lot of these, and they
                    // are rarely acted on — voiding is the exception, not the
                    // routine — so past a handful the list folds itself away.
                    const open = invoiceListOpen === null ? nums.length <= 3 : invoiceListOpen;
                    return (
                      <View style={{ marginTop: 14, borderTopWidth: 0.5, borderTopColor: BORDER, paddingTop: 10 }}>
                        <TouchableOpacity
                          onPress={() => setInvoiceListOpen(!open)}
                          activeOpacity={0.7}
                          style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 2, marginBottom: open ? 6 : 0 }}
                        >
                          <Text style={{ flex: 1, fontSize: 12, fontWeight: '700', color: MUTED }}>Invoices sent ({nums.length})</Text>
                          <Text style={{ fontSize: 18, color: MUTED, marginLeft: 8, lineHeight: 20 }}>{open ? '▾' : '▸'}</Text>
                        </TouchableOpacity>
                        {open && (
                          <>
                            {nums.map((num) => (
                              <View key={num} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6 }}>
                                <Text style={{ flex: 1, fontSize: 13, color: TEXT, fontWeight: '600' }}>{num}</Text>
                                <TouchableOpacity onPress={() => handleVoidInvoice(num)} activeOpacity={0.7}><Text style={{ fontSize: 12, color: RED, fontWeight: '600' }}>Void</Text></TouchableOpacity>
                              </View>
                            ))}
                            <Text style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>Voiding releases an invoice's charges so they can be re-billed, and removes its saved PDF.</Text>
                          </>
                        )}
                      </View>
                    );
                  })()}
                </>
              )}

              {deletedLedger.length > 0 && (
                <View style={{ marginTop: 14, borderTopWidth: 0.5, borderTopColor: BORDER, paddingTop: 10 }}>
                  <TouchableOpacity onPress={() => setShowDeletedLedger((v) => !v)} activeOpacity={0.7} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 2, marginBottom: showDeletedLedger ? 6 : 0 }}>
                    <Text style={{ fontSize: 12, fontWeight: '700', color: MUTED }}>Recently deleted ({deletedLedger.length})</Text>
                    <Text style={{ fontSize: 12, color: MUTED }}>{showDeletedLedger ? '▴' : '▾'}</Text>
                  </TouchableOpacity>
                  {showDeletedLedger && deletedLedger.map((e) => (
                    <View key={e.id} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6 }}>
                      <View style={{ flex: 1 }}>
                        <Text style={{ fontSize: 13, color: MUTED, textDecorationLine: 'line-through' }}>{e.description}</Text>
                        <Text style={{ fontSize: 11, color: MUTED }}>{e.type === 'payment' ? '-' : '+'}{fmt(e.amount)}{e.deletedBy?.email ? ` · deleted by ${e.deletedBy.email}` : ''}</Text>
                      </View>
                      {(canEditEntry(e) || isOwner) && (
                        <TouchableOpacity onPress={() => handleRestoreLedgerEntry(e)} activeOpacity={0.7}><Text style={{ fontSize: 13, color: GREEN, fontWeight: '600' }}>Restore</Text></TouchableOpacity>
                      )}
                    </View>
                  ))}
                </View>
              )}
            </CollapsibleSection>
          );
        })()}

        {/* ── Pictures & Documents ── */}
        {PHOTO_UPLOADS_ENABLED && (
          <CollapsibleSection sec="docs" title="Pictures & Documents" badge={(horse.documents || []).length || ''} collapsed={collapsedSections.docs} onToggle={() => toggleSection('docs')}>
            {(() => {
              const docs = horse.documents || [];
              const typesPresent = [...new Set(docs.map((d) => d.docType || 'Picture'))];
              const q = docSearch.trim().toLowerCase();
              // Upload order is the fallback when a file has no date.
              const when = (d, i) => (d.uploadedAt ? Date.parse(d.uploadedAt) : 0) || i;
              const filtered = docs
                .map((d, i) => ({ d, i }))
                .filter(({ d }) => docFilter === 'All' || (d.docType || 'Picture') === docFilter)
                .filter(({ d }) => !q || [d.name, d.note, d.docType, d.uploadedBy].some((f) => f && String(f).toLowerCase().includes(q)))
                .sort((a, b) => (docSort === 'name'
                  ? (a.d.name || a.d.docType || '').localeCompare(b.d.name || b.d.docType || '')
                  : docSort === 'oldest' ? when(a.d, a.i) - when(b.d, b.i) : when(b.d, b.i) - when(a.d, a.i)))
                .map(({ d }) => d);
              const isImage = (d) => (d.mime || '').startsWith('image/');
              const uploadedOn = (d) => (d.uploadedAt ? formatDate(String(d.uploadedAt).split('T')[0]) : '');
              return (
                <>
                  {typesPresent.length > 1 && (
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                      {['All', ...typesPresent].map((t) => (
                        <TouchableOpacity key={t} style={[styles.categoryChip, docFilter === t && styles.categoryChipActive]} onPress={() => setDocFilter(t)} activeOpacity={0.75}>
                          <Text style={[styles.categoryChipTxt, docFilter === t && styles.categoryChipTxtActive]}>{t}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}
                  {docs.length > 1 && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                      <Text style={{ fontSize: 12, color: MUTED, marginRight: 2 }}>Sort:</Text>
                      {[{ k: 'newest', l: 'Newest' }, { k: 'oldest', l: 'Oldest' }, { k: 'name', l: 'Name' }].map((o) => (
                        <TouchableOpacity key={o.k} onPress={() => chooseDocSort(o.k)} activeOpacity={0.75} style={[styles.categoryChip, docSort === o.k && styles.categoryChipActive]}>
                          <Text style={[styles.categoryChipTxt, docSort === o.k && styles.categoryChipTxtActive]}>{o.l}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}
                  {docs.length > 5 && (
                    <TextInput
                      style={[styles.input, { marginBottom: 10 }]}
                      placeholder="Search file names and notes"
                      placeholderTextColor="#BDBDBD"
                      value={docSearch}
                      onChangeText={setDocSearch}
                    />
                  )}
                  {filtered.length === 0 && (
                    <Text style={styles.healthEmpty}>{docs.length === 0
                      ? 'No pictures or documents yet. Add photos of this horse and files like health certificates or registration papers.'
                      : 'Nothing matches — try a different search or type.'}</Text>
                  )}
                  {(() => {
                    const photos = filtered.filter(isImage);
                    if (photos.length === 0) return null;
                    return (
                      <>
                        <TouchableOpacity onPress={() => setShowDocPhotos((v) => !v)} activeOpacity={0.7} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 6 }}>
                          <Text style={{ fontSize: 12, fontWeight: '700', color: MUTED }}>Photos ({photos.length})</Text>
                          <Text style={{ fontSize: 12, color: MUTED }}>{showDocPhotos ? '▴' : '▾'}</Text>
                        </TouchableOpacity>
                        {showDocPhotos && (
                          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 4 }}>
                            {photos.map((d) => (
                              <TouchableOpacity key={d.path || d.url} onPress={() => setDocViewerDoc({ ...d, editable: true })} onLongPress={canUploadMedia ? () => handleRemoveDocument(d) : undefined} activeOpacity={0.8}>
                                <Image source={{ uri: d.url }} style={{ width: 94, height: 94, borderRadius: 8, backgroundColor: '#EBE7DF' }} />
                                <Text style={{ fontSize: 10, color: MUTED, maxWidth: 94, marginTop: 2 }} numberOfLines={1}>{[d.docType || 'Picture', uploadedOn(d)].filter(Boolean).join(' · ')}</Text>
                                {!!d.note && <Text style={{ fontSize: 11, color: TEXT, maxWidth: 94, lineHeight: 14 }} numberOfLines={2}>{d.note}</Text>}
                              </TouchableOpacity>
                            ))}
                          </View>
                        )}
                      </>
                    );
                  })()}
                  {filtered.filter((d) => !isImage(d)).map((d) => (
                    <View key={d.path || d.url} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: BORDER }}>
                      <Text style={{ fontSize: 18, marginRight: 8 }}>📄</Text>
                      <TouchableOpacity style={{ flex: 1 }} onPress={() => Linking.openURL(d.url)} activeOpacity={0.7}>
                        <Text style={styles.incidentBodyText} numberOfLines={1}>{d.name || 'Document'}</Text>
                        <Text style={{ fontSize: 11, color: MUTED }}>{[d.docType || 'Document', uploadedOn(d), d.uploadedBy].filter(Boolean).join(' · ')}</Text>
                        {!!d.note && <Text style={{ fontSize: 12, color: TEXT, marginTop: 3, lineHeight: 17 }}>{d.note}</Text>}
                      </TouchableOpacity>
                      {canUploadMedia && (
                        <TouchableOpacity onPress={() => openDocNote(d)} activeOpacity={0.7} style={{ paddingHorizontal: 8, paddingVertical: 4 }} hitSlop={{ top: 8, bottom: 8, left: 6, right: 6 }}>
                          <Text style={{ fontSize: 15 }}>📝</Text>
                        </TouchableOpacity>
                      )}
                      <TouchableOpacity onPress={() => handleShareDocument(d)} disabled={sharingDocKey === (d.path || d.url)} activeOpacity={0.7} style={{ paddingHorizontal: 8, paddingVertical: 4 }} hitSlop={{ top: 8, bottom: 8, left: 6, right: 6 }}>
                        {sharingDocKey === (d.path || d.url)
                          ? <ActivityIndicator color={GREEN} />
                          : <Text style={{ fontSize: 16 }}>{Platform.OS === 'web' ? '⬇️' : '⤴️'}</Text>}
                      </TouchableOpacity>
                      {canUploadMedia && (
                        <TouchableOpacity onPress={() => handleRemoveDocument(d)} activeOpacity={0.7} style={{ paddingLeft: 6 }}>
                          <Text style={styles.incDeleteTxt}>✕</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  ))}
                  {docUploading ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 12 }}>
                      <ActivityIndicator color={RED} />
                      <Text style={{ fontSize: 13, color: MUTED }}>Uploading…</Text>
                    </View>
                  ) : canUploadMedia && (
                    <View style={{ flexDirection: 'row', gap: 10, marginTop: 12 }}>
                      {isPhotoPickingAvailable() && (
                        <TouchableOpacity style={[styles.addMilestoneBtn, { flex: 1 }]} onPress={startAddPictures} activeOpacity={0.75}>
                          <Text style={styles.addMilestoneBtnTxt}>+ Add Photos</Text>
                        </TouchableOpacity>
                      )}
                      {isDocPickingAvailable() && (
                        <TouchableOpacity style={[styles.addMilestoneBtn, { flex: 1 }]} onPress={startAddDocument} activeOpacity={0.75}>
                          <Text style={styles.addMilestoneBtnTxt}>+ Add Document</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  )}
                </>
              );
            })()}
          </CollapsibleSection>
        )}

        {/* ── Notes ── */}
        <CollapsibleSection sec="notes" title="Notes" collapsed={collapsedSections.notes} onToggle={() => toggleSection('notes')}>
        {noteEntries.length > 0 && (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            {NOTE_FILTERS.map((f) => (
              <TouchableOpacity
                key={f.key}
                style={[styles.categoryChip, noteFilter === f.key && styles.categoryChipActive]}
                onPress={() => setNoteFilter(f.key)}
                activeOpacity={0.75}
              >
                <Text style={[styles.categoryChipTxt, noteFilter === f.key && styles.categoryChipTxtActive]}>{f.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
        {(() => {
          const filteredNotes = noteEntries.filter((entry) => {
            if (noteFilter === 'health') return !!entry.tag;
            if (noteFilter === 'general') return !entry.tag;
            return true;
          });
          if (filteredNotes.length === 0) {
            return <Text style={styles.noNotesTxt}>No notes yet.</Text>;
          }
          return filteredNotes.map((entry) => {
          const expanded = !!expandedNotes[entry.id];
          return (
            <View key={entry.id} style={styles.notesCard}>
              <TouchableOpacity
                style={styles.collapsibleHeader}
                onPress={() => toggleNoteExpanded(entry.id)}
                activeOpacity={0.75}
              >
                <View style={{ flex: 1, marginRight: 8 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <Text style={styles.noteDate}>{formatNoteDate(entry.createdAt)}</Text>
                    {!!entry.tag && (
                      <View style={styles.categoryBadge}>
                        <Text style={styles.categoryBadgeTxt}>{entry.tag}</Text>
                      </View>
                    )}
                  </View>
                  {!expanded && (
                    <Text style={styles.notePreview} numberOfLines={1}>{entry.text}</Text>
                  )}
                </View>
                <Text style={styles.sectionCardChevron}>{expanded ? '⌄' : '›'}</Text>
              </TouchableOpacity>
              {expanded && (
                <>
                  <Text style={styles.noteText}>{entry.text}</Text>
                  {canEdit && (
                    <View style={{ flexDirection: 'row', gap: 18, marginTop: 4 }}>
                      <TouchableOpacity onPress={() => openEditNoteModal(entry)} activeOpacity={0.75}>
                        <Text style={[styles.noteDeleteTxt, { color: GREEN }]}>Edit</Text>
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => handleDeleteNote(entry.id)} activeOpacity={0.75}>
                        <Text style={styles.noteDeleteTxt}>Delete</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </>
              )}
            </View>
          );
          });
        })()}

        {canEdit && (
          <TouchableOpacity
            style={styles.addMilestoneBtn}
            onPress={openAddNoteModal}
            activeOpacity={0.75}
          >
            <Text style={styles.addMilestoneBtnTxt}>+ Add Note</Text>
          </TouchableOpacity>
        )}
        </CollapsibleSection>
      </ScrollView>

      {/* ── Add Note Modal ── */}
      <Modal visible={showAddNoteModal} animationType="slide" transparent>
        <KeyboardAvoidingView
          style={styles.overlay}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <View style={[styles.sheet, { maxHeight: '85%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
              <Text style={styles.sheetTitle}>{editingNoteId ? 'Edit Note' : 'Add Note'}</Text>
              <TextInput
                style={[styles.notesInput, styles.input, { minHeight: 120 }]}
                placeholder="Write a note..."
                placeholderTextColor="#BDBDBD"
                value={newNoteText}
                onChangeText={setNewNoteText}
                multiline
                textAlignVertical="top"
                autoFocus
              />

              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 18 }}>
                <Text style={styles.fieldLabel}>Tag (optional)</Text>
                <TouchableOpacity
                  onPress={() => {
                    setShowAddNoteModal(false);
                    navigation.navigate('NoteTags');
                  }}
                  activeOpacity={0.75}
                >
                  <Text style={styles.addTagCategoryTxt}>+ Add Tag Category</Text>
                </TouchableOpacity>
              </View>
              <CategoryPicker
                presetOptions={PRESET_NOTE_TAGS}
                hiddenPresets={hiddenNoteTags}
                customCategories={customNoteTags}
                value={newNoteTag}
                onSelect={setNewNoteTag}
                otherText={newNoteTagOther}
                onOtherTextChange={setNewNoteTagOther}
              />

              <TouchableOpacity style={[styles.saveBtn, { marginTop: 18 }]} onPress={handleAddNote} activeOpacity={0.85}>
                <Text style={styles.saveBtnText}>{editingNoteId ? 'Save Changes' : 'Save Note'}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => { setEditingNoteId(null); setShowAddNoteModal(false); }} activeOpacity={0.85}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── Add / Edit Incentive Nomination Modal ── */}
      <Modal visible={showAddIncentiveModal} animationType="slide" transparent>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '90%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
              <Text style={styles.sheetTitle}>{editingIncentiveId ? 'Edit Nomination' : 'Add Nomination'}</Text>

              <Text style={styles.fieldLabel}>Incentive Name</Text>
              <TextInput
                style={styles.input}
                placeholder="e.g. NRCHA Futurity Nomination"
                placeholderTextColor="#BDBDBD"
                value={incentiveName}
                onChangeText={setIncentiveName}
                autoFocus
              />

              <Text style={styles.fieldLabel}>Date Paid In</Text>
              <TouchableOpacity style={styles.dateRow} onPress={() => setShowIncentiveDatePicker((v) => !v)} activeOpacity={0.75}>
                <Text style={styles.dateRowTxt}>{incentiveDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</Text>
              </TouchableOpacity>
              {showIncentiveDatePicker && (
                <DateTimePicker
                  value={incentiveDate}
                  mode="date"
                  display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                  onChange={(e, d) => {
                    if (Platform.OS === 'android') setShowIncentiveDatePicker(false);
                    if (d) setIncentiveDate(d);
                  }}
                />
              )}

              <Text style={[styles.fieldLabel, { marginTop: 14 }]}>Cost ($)</Text>
              <TextInput
                style={styles.input}
                placeholder="0.00"
                placeholderTextColor="#BDBDBD"
                keyboardType="decimal-pad"
                value={incentiveCost}
                onChangeText={setIncentiveCost}
              />

              <Text style={styles.fieldLabel}>Frequency</Text>
              <View style={{ flexDirection: 'row', gap: 8, marginBottom: 4 }}>
                {[['one-time', 'One-time'], ['annual', 'Annual'], ['other', 'Other']].map(([key, label]) => (
                  <TouchableOpacity
                    key={key}
                    style={[styles.typeToggleBtn, incentiveFrequency === key && styles.typeToggleBtnActive]}
                    onPress={() => setIncentiveFrequency(key)}
                    activeOpacity={0.75}
                  >
                    <Text style={[styles.typeToggleBtnTxt, incentiveFrequency === key && styles.typeToggleBtnTxtActive]}>{label}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              <Text style={[styles.fieldLabel, { marginTop: 14 }]}>Notes (optional)</Text>
              <TextInput
                style={[styles.input, { minHeight: 70 }]}
                placeholder="Any details about this nomination..."
                placeholderTextColor="#BDBDBD"
                value={incentiveNotes}
                onChangeText={setIncentiveNotes}
                multiline
                textAlignVertical="top"
              />

              <TouchableOpacity style={[styles.saveBtn, { marginTop: 18 }]} onPress={handleSaveIncentive} activeOpacity={0.85}>
                <Text style={styles.saveBtnText}>{editingIncentiveId ? 'Save Changes' : 'Save Nomination'}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => { setShowAddIncentiveModal(false); setEditingIncentiveId(null); }} activeOpacity={0.85}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── Add Earnings Modal ── */}
      <Modal visible={showAddEarningsModal} animationType="slide" transparent>
        <KeyboardAvoidingView
          style={styles.overlay}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <View style={[styles.sheet, { maxHeight: '90%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
              <Text style={styles.sheetTitle}>Add Event</Text>

              <Text style={styles.fieldLabel}>Type</Text>
              <View style={{ flexDirection: 'row', gap: 10, marginBottom: 16 }}>
                {['horse', 'offspring'].map((t) => (
                  <TouchableOpacity
                    key={t}
                    style={[styles.typeToggleBtn, earningsType === t && styles.typeToggleBtnActive]}
                    onPress={() => setEarningsType(t)}
                    activeOpacity={0.75}
                  >
                    <Text style={[styles.typeToggleBtnTxt, earningsType === t && styles.typeToggleBtnTxtActive]}>
                      {t === 'horse' ? 'This Horse' : 'Offspring'}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              {earningsType === 'offspring' && (
                <>
                  <Text style={styles.fieldLabel}>Offspring Name</Text>
                  {offspringChoices.length > 0 && (
                    <TouchableOpacity
                      style={[styles.datePicker, { marginBottom: 8 }]}
                      onPress={() => { setOffspringPickerSearch(''); setShowOffspringPicker(true); }}
                      activeOpacity={0.75}
                    >
                      <Text style={[styles.datePickerText, !earningsOffspringName.trim() && { color: '#BDBDBD' }]} numberOfLines={1}>
                        {earningsOffspringName.trim() || `Choose from ${offspringChoices.length} known offspring…`}
                      </Text>
                      <Text style={{ color: MUTED }}>▾</Text>
                    </TouchableOpacity>
                  )}
                  <TextInput
                    style={styles.input}
                    placeholder={offspringChoices.length > 0 ? 'Or type a new offspring' : 'e.g. Gingerbread Cookie'}
                    placeholderTextColor="#BDBDBD"
                    value={earningsOffspringName}
                    onChangeText={setEarningsOffspringName}
                  />
                </>
              )}

              <Text style={styles.fieldLabel}>Date</Text>
              <TouchableOpacity
                style={styles.dateRow}
                onPress={() => setShowEarningsDatePicker((v) => !v)}
                activeOpacity={0.75}
              >
                <Text style={styles.dateRowTxt}>{earningsDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</Text>
              </TouchableOpacity>
              {showEarningsDatePicker && (
                <DateTimePicker
                  value={earningsDate}
                  mode="date"
                  display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                  onChange={(e, d) => {
                    if (Platform.OS === 'android') setShowEarningsDatePicker(false);
                    if (d) setEarningsDate(d);
                  }}
                />
              )}

              <Text style={[styles.fieldLabel, { marginTop: 14 }]}>Event Name</Text>
              <TextInput
                style={styles.input}
                placeholder="e.g. NRCHA Snaffle Bit Futurity"
                placeholderTextColor="#BDBDBD"
                value={earningsEventName}
                onChangeText={setEarningsEventName}
              />

              <Text style={styles.fieldLabel}>Earnings ($)</Text>
              <TextInput
                style={styles.input}
                placeholder="0.00"
                placeholderTextColor="#BDBDBD"
                keyboardType="decimal-pad"
                value={earningsAmount}
                onChangeText={setEarningsAmount}
              />

              <Text style={styles.fieldLabel}>Placing / Notes (optional)</Text>
              <TextInput
                style={[styles.input, { minHeight: 70 }]}
                placeholder="e.g. 1st Place Open Division"
                placeholderTextColor="#BDBDBD"
                value={earningsNotes}
                onChangeText={setEarningsNotes}
                multiline
                textAlignVertical="top"
              />

              <TouchableOpacity style={[styles.saveBtn, { marginTop: 18 }]} onPress={handleAddEarnings} activeOpacity={0.85}>
                <Text style={styles.saveBtnText}>Save Event</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => { clearEventDraft(); setShowAddEarningsModal(false); }} activeOpacity={0.85}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── View Earnings Modal ── */}
      <Modal visible={showViewEarningsModal} animationType="slide" transparent>
        <View style={styles.overlay}>
          <View style={[styles.sheet, { maxHeight: '80%' }]}>
            {viewEarningsEntry && (() => {
              const e = viewEarningsEntry;
              const fmt = (n) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              return (
                <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
                  <Text style={styles.sheetTitle}>{e.eventName}</Text>

                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                    <View style={[styles.earningsTypeBadge, e.type === 'offspring' && styles.earningsTypeBadgeOffspring]}>
                      <Text style={[styles.earningsTypeBadgeTxt, e.type === 'offspring' && { color: RED }]}>
                        {e.type === 'offspring' ? (e.offspringName || 'Offspring') : 'Horse'}
                      </Text>
                    </View>
                    <Text style={styles.earningsEventDate}>{formatDate(e.date)}</Text>
                  </View>

                  {e.amount > 0 && (
                    <View style={{ marginBottom: 16 }}>
                      <Text style={styles.earningsTotalLabel}>Earnings</Text>
                      <Text style={[styles.earningsTotalValue, { color: GREEN, fontSize: 22 }]}>{fmt(e.amount)}</Text>
                    </View>
                  )}

                  {!!e.notes && (
                    <View style={{ marginBottom: 16 }}>
                      <Text style={styles.earningsTotalLabel}>Placing / Notes</Text>
                      <Text style={[styles.noteText, { marginTop: 4 }]}>{e.notes}</Text>
                    </View>
                  )}

                  <TouchableOpacity
                    style={[styles.saveBtn, { marginTop: 8 }]}
                    onPress={() => {
                      setShowViewEarningsModal(false);
                      setTimeout(() => openEditEarnings(e), 300);
                    }}
                    activeOpacity={0.85}
                  >
                    <Text style={styles.saveBtnText}>Edit Event</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[styles.cancelBtn, { marginBottom: 8 }]} onPress={() => setShowViewEarningsModal(false)} activeOpacity={0.85}>
                    <Text style={styles.cancelBtnText}>Close</Text>
                  </TouchableOpacity>
                </ScrollView>
              );
            })()}
          </View>
        </View>
      </Modal>

      {/* ── Edit Earnings Modal ── */}
      <Modal visible={showEditEarningsModal} animationType="slide" transparent>
        <KeyboardAvoidingView
          style={styles.overlay}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <View style={[styles.sheet, { maxHeight: '90%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
              <Text style={styles.sheetTitle}>Edit Event</Text>

              <Text style={styles.fieldLabel}>Type</Text>
              <View style={{ flexDirection: 'row', gap: 10, marginBottom: 16 }}>
                {['horse', 'offspring'].map((t) => (
                  <TouchableOpacity
                    key={t}
                    style={[styles.typeToggleBtn, earningsType === t && styles.typeToggleBtnActive]}
                    onPress={() => setEarningsType(t)}
                    activeOpacity={0.75}
                  >
                    <Text style={[styles.typeToggleBtnTxt, earningsType === t && styles.typeToggleBtnTxtActive]}>
                      {t === 'horse' ? 'This Horse' : 'Offspring'}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              {earningsType === 'offspring' && (
                <>
                  <Text style={styles.fieldLabel}>Offspring Name</Text>
                  {offspringChoices.length > 0 && (
                    <TouchableOpacity
                      style={[styles.datePicker, { marginBottom: 8 }]}
                      onPress={() => { setOffspringPickerSearch(''); setShowOffspringPicker(true); }}
                      activeOpacity={0.75}
                    >
                      <Text style={[styles.datePickerText, !earningsOffspringName.trim() && { color: '#BDBDBD' }]} numberOfLines={1}>
                        {earningsOffspringName.trim() || `Choose from ${offspringChoices.length} known offspring…`}
                      </Text>
                      <Text style={{ color: MUTED }}>▾</Text>
                    </TouchableOpacity>
                  )}
                  <TextInput
                    style={styles.input}
                    placeholder={offspringChoices.length > 0 ? 'Or type a new offspring' : 'e.g. Gingerbread Cookie'}
                    placeholderTextColor="#BDBDBD"
                    value={earningsOffspringName}
                    onChangeText={setEarningsOffspringName}
                  />
                </>
              )}

              <Text style={styles.fieldLabel}>Date</Text>
              <TouchableOpacity
                style={styles.dateRow}
                onPress={() => setShowEarningsDatePicker((v) => !v)}
                activeOpacity={0.75}
              >
                <Text style={styles.dateRowTxt}>{earningsDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</Text>
              </TouchableOpacity>
              {showEarningsDatePicker && (
                <DateTimePicker
                  value={earningsDate}
                  mode="date"
                  display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                  onChange={(e, d) => {
                    if (Platform.OS === 'android') setShowEarningsDatePicker(false);
                    if (d) setEarningsDate(d);
                  }}
                />
              )}

              <Text style={[styles.fieldLabel, { marginTop: 14 }]}>Event Name</Text>
              <TextInput
                style={styles.input}
                placeholder="e.g. NRCHA Snaffle Bit Futurity"
                placeholderTextColor="#BDBDBD"
                value={earningsEventName}
                onChangeText={setEarningsEventName}
              />

              <Text style={styles.fieldLabel}>Earnings ($)</Text>
              <TextInput
                style={styles.input}
                placeholder="0.00"
                placeholderTextColor="#BDBDBD"
                keyboardType="decimal-pad"
                value={earningsAmount}
                onChangeText={setEarningsAmount}
              />

              <Text style={styles.fieldLabel}>Placing / Notes (optional)</Text>
              <TextInput
                style={[styles.input, { minHeight: 70 }]}
                placeholder="e.g. 1st Place Open Division"
                placeholderTextColor="#BDBDBD"
                value={earningsNotes}
                onChangeText={setEarningsNotes}
                multiline
                textAlignVertical="top"
              />

              <TouchableOpacity style={[styles.saveBtn, { marginTop: 18 }]} onPress={handleSaveEditEarnings} activeOpacity={0.85}>
                <Text style={styles.saveBtnText}>Save Changes</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => { setShowEditEarningsModal(false); setEditEarningsEntry(null); }} activeOpacity={0.85}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.cancelBtn, { marginTop: 8 }]} onPress={handleDeleteFromEdit} activeOpacity={0.85}>
                <Text style={[styles.cancelBtnText, { color: '#D9534F' }]}>Delete Entry</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── Rename Modal ── */}
      <Modal visible={showRenameModal} animationType="slide" transparent>
        <KeyboardAvoidingView
          style={styles.overlay}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <View style={styles.sheet}>
            <Text style={styles.sheetTitle}>Edit Name</Text>
            <Text style={styles.fieldLabel}>Horse Name</Text>
            <TextInput
              style={styles.input}
              placeholder="Horse name"
              placeholderTextColor="#BDBDBD"
              value={renameInput}
              onChangeText={setRenameInput}
              autoFocus
              returnKeyType="done"
              onSubmitEditing={handleRename}
            />
            <View style={styles.sheetBtns}>
              <TouchableOpacity
                style={styles.cancelBtn}
                onPress={() => setShowRenameModal(false)}
              >
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleRename}>
                <Text style={styles.saveBtnText}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── Edit Details Modal ── */}
      <Modal visible={showDetailsModal} animationType="slide" transparent>
        <KeyboardAvoidingView
          style={styles.overlay}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <View style={[styles.sheet, { maxHeight: '85%' }]}>
            <Text style={styles.sheetTitle}>Edit Details</Text>
            {details && (
              <ScrollView showsVerticalScrollIndicator={false}>
                <Text style={styles.fieldLabel}>
                  {horse.type === 'born' ? 'Date of Birth' : 'Breed Date'}
                </Text>
                <TouchableOpacity
                  style={styles.datePicker}
                  onPress={() => setShowDetailsDatePicker(true)}
                >
                  <Text style={styles.datePickerText}>
                    {detailsDate.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </Text>
                  <Text>📅</Text>
                </TouchableOpacity>

                {showDetailsDatePicker && (
                  <DateTimePicker
                    value={detailsDate}
                    mode="date"
                    display="spinner"
                    onChange={(e, d) => {
                      if (Platform.OS === 'android') setShowDetailsDatePicker(false);
                      if (d) setDetailsDate(d);
                    }}
                  />
                )}

                <Text style={[styles.fieldLabel, { marginTop: 18 }]}>Registered Name</Text>
                <TextInput
                  style={styles.input}
                  placeholder="e.g. Moonin inthe Darkk"
                  placeholderTextColor="#BDBDBD"
                  value={details.registeredName}
                  onChangeText={(v) => setDetails((d) => ({ ...d, registeredName: v }))}
                />

                <Text style={styles.fieldLabel}>Registry Number</Text>
                <TextInput
                  style={styles.input}
                  placeholder="e.g. 6497896"
                  placeholderTextColor="#BDBDBD"
                  value={details.registryNumber}
                  onChangeText={(v) => setDetails((d) => ({ ...d, registryNumber: v }))}
                />

                <Text style={styles.fieldLabel}>Breed</Text>
                <TextInput
                  style={styles.input}
                  placeholder="e.g. Quarter Horse"
                  placeholderTextColor="#BDBDBD"
                  value={details.breed}
                  onChangeText={(v) => setDetails((d) => ({ ...d, breed: v }))}
                />

                <Text style={styles.fieldLabel}>Color</Text>
                <TextInput
                  style={styles.input}
                  placeholder="e.g. Red Roan"
                  placeholderTextColor="#BDBDBD"
                  value={details.color}
                  onChangeText={(v) => setDetails((d) => ({ ...d, color: v }))}
                />

                <Text style={styles.fieldLabel}>Height</Text>
                <TextInput
                  style={styles.input}
                  placeholder="e.g. 15.2 hands"
                  placeholderTextColor="#BDBDBD"
                  value={details.height}
                  onChangeText={(v) => setDetails((d) => ({ ...d, height: v }))}
                />

                <Text style={styles.fieldLabel}>Location</Text>
                <View style={styles.statusChipRow}>
                  {[...new Set([...customLocations, ...allHorses.map((h) => h.location).filter(Boolean)])].map((loc) => {
                    const selected = details.location === loc;
                    return (
                      <TouchableOpacity
                        key={loc}
                        style={[styles.statusChip, selected && styles.statusChipSelected]}
                        onPress={() => setDetails((d) => ({ ...d, location: selected ? '' : loc }))}
                        activeOpacity={0.75}
                      >
                        <Text style={[styles.statusChipTxt, selected && styles.statusChipTxtSelected]}>{loc}</Text>
                      </TouchableOpacity>
                    );
                  })}
                  <TouchableOpacity
                    style={styles.statusChip}
                    onPress={() => { setShowAddLocationEdit(true); setNewLocationTextEdit(''); }}
                    activeOpacity={0.75}
                  >
                    <Text style={styles.statusChipTxt}>+ Add Location</Text>
                  </TouchableOpacity>
                </View>
                {showAddLocationEdit && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 6 }}>
                    <TextInput
                      style={[styles.input, { flex: 1, marginBottom: 0 }]}
                      placeholder="Location name"
                      placeholderTextColor="#BDBDBD"
                      value={newLocationTextEdit}
                      onChangeText={setNewLocationTextEdit}
                      autoFocus
                    />
                    <TouchableOpacity
                      style={[styles.primaryButton, { paddingHorizontal: 14, paddingVertical: 10, marginBottom: 0 }]}
                      onPress={async () => {
                        const loc = newLocationTextEdit.trim();
                        const knownLocs = [...new Set([...customLocations, ...allHorses.map((h) => h.location).filter(Boolean)])];
                        if (loc && !knownLocs.includes(loc)) {
                          await addCustomLocation(auth.currentUser.uid, loc);
                        }
                        if (loc) setDetails((d) => ({ ...d, location: loc }));
                        setShowAddLocationEdit(false);
                        setNewLocationTextEdit('');
                      }}
                    >
                      <Text style={styles.primaryButtonTxt}>Add</Text>
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => setShowAddLocationEdit(false)}>
                      <Text style={{ color: '#999', fontSize: 15 }}>Cancel</Text>
                    </TouchableOpacity>
                  </View>
                )}

                <Text style={[styles.fieldLabel, { marginTop: 14 }]}>Stall # (optional)</Text>
                <TextInput
                  style={styles.input}
                  placeholder="e.g. 12 or B-4"
                  placeholderTextColor="#BDBDBD"
                  value={details.stall || ''}
                  onChangeText={(v) => setDetails((d) => ({ ...d, stall: v }))}
                />

                <Text style={[styles.fieldLabel, { marginTop: 18 }]}>Status (select all that apply)</Text>
                <View style={styles.statusChipRow}>
                  {STATUS_OPTIONS.map((option) => {
                    const selected = details.status.includes(option);
                    return (
                      <TouchableOpacity
                        key={option}
                        style={[styles.statusChip, selected && styles.statusChipSelected]}
                        onPress={() => toggleDetailsStatus(option)}
                        activeOpacity={0.75}
                      >
                        <Text style={[styles.statusChipTxt, selected && styles.statusChipTxtSelected]}>
                          {option}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>

                {details.status.includes('Foal/Yearling') && (
                  <>
                    <Text style={styles.fieldLabel}>Sex</Text>
                    <View style={styles.statusChipRow}>
                      {['Filly', 'Colt'].map((sex) => {
                        const selected = details.status.includes(sex);
                        return (
                          <TouchableOpacity
                            key={sex}
                            style={[styles.statusChip, selected && styles.statusChipSelected]}
                            onPress={() => toggleDetailsFoalSex(sex)}
                            activeOpacity={0.75}
                          >
                            <Text style={[styles.statusChipTxt, selected && styles.statusChipTxtSelected]}>
                              {sex}
                            </Text>
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  </>
                )}

                {details.status.includes('Mare') && (
                  <>
                    <Text style={styles.fieldLabel}>Is she a broodmare?</Text>
                    <TouchableOpacity
                      style={styles.recipToggle}
                      onPress={toggleDetailsBroodmare}
                      activeOpacity={0.75}
                    >
                      <View style={[styles.recipToggleTrack, details.status.includes('Broodmare') && styles.recipToggleTrackOn]}>
                        <View style={[styles.recipToggleThumb, details.status.includes('Broodmare') && styles.recipToggleThumbOn]} />
                      </View>
                      <Text style={styles.recipToggleLabel}>
                        {details.status.includes('Broodmare') ? 'Yes — tagged as Broodmare' : 'No'}
                      </Text>
                    </TouchableOpacity>

                    <Text style={styles.fieldLabel}>Is she a recip?</Text>
                    <TouchableOpacity
                      style={styles.recipToggle}
                      onPress={toggleDetailsRecip}
                      activeOpacity={0.75}
                    >
                      <View style={[styles.recipToggleTrack, details.status.includes('Recip') && styles.recipToggleTrackOn]}>
                        <View style={[styles.recipToggleThumb, details.status.includes('Recip') && styles.recipToggleThumbOn]} />
                      </View>
                      <Text style={styles.recipToggleLabel}>
                        {details.status.includes('Recip') ? 'Yes — tagged as Recip' : 'No'}
                      </Text>
                    </TouchableOpacity>
                  </>
                )}

                {horse.type === 'embryo' && horse.isRecip && (
                  <>
                    <Text style={[styles.fieldLabel, { marginTop: 18 }]}>Recip Mare</Text>
                    <View style={styles.pedigreeInputRow}>
                      <TextInput
                        style={[styles.input, { flex: 1, marginBottom: 0 }]}
                        placeholder="Recip mare name or source"
                        placeholderTextColor="#BDBDBD"
                        value={details.recipMare}
                        onChangeText={(v) => setDetails((d) => ({ ...d, recipMare: v, recipMareId: null }))}
                      />
                      <TouchableOpacity style={styles.fromStableBtn} onPress={() => openPicker('recip')} activeOpacity={0.75}>
                        <Text style={styles.fromStableBtnTxt}>From Stable</Text>
                      </TouchableOpacity>
                    </View>
                    {details.recipMareId && (
                      <Text style={styles.linkedNote}>✓ Linked to {details.recipMare}</Text>
                    )}
                    {(details.recipMare.trim() || details.recipMareId) && (
                      <>
                        <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Is the recip leased?</Text>
                        <TouchableOpacity
                          style={styles.recipToggle}
                          onPress={() => setDetails((d) => ({ ...d, recipLeased: !d.recipLeased, recipLeasedFrom: !d.recipLeased ? d.recipLeasedFrom : '' }))}
                          activeOpacity={0.75}
                        >
                          <View style={[styles.recipToggleTrack, details.recipLeased && styles.recipToggleTrackOn]}>
                            <View style={[styles.recipToggleThumb, details.recipLeased && styles.recipToggleThumbOn]} />
                          </View>
                          <Text style={styles.recipToggleLabel}>{details.recipLeased ? 'Yes — leased' : 'No'}</Text>
                        </TouchableOpacity>
                        {details.recipLeased && (
                          <>
                            <Text style={styles.fieldLabel}>Leased from</Text>
                            <TextInput
                              style={styles.input}
                              placeholder="e.g. Smith Ranch"
                              placeholderTextColor="#BDBDBD"
                              value={details.recipLeasedFrom}
                              onChangeText={(v) => setDetails((d) => ({ ...d, recipLeasedFrom: v }))}
                            />
                          </>
                        )}
                      </>
                    )}
                  </>
                )}

                <Text style={[styles.fieldLabel, { marginTop: 18 }]}>Pedigree</Text>
                <Text style={styles.fieldLabel}>Sire</Text>
                <View style={styles.pedigreeInputRow}>
                  <TextInput
                    style={[styles.input, { flex: 1, marginBottom: 0 }]}
                    placeholder="Sire's name"
                    placeholderTextColor="#BDBDBD"
                    value={details.sire}
                    onChangeText={(v) => setDetails((d) => ({ ...d, sire: v, sireId: null }))}
                  />
                  <TouchableOpacity style={styles.fromStableBtn} onPress={() => openPicker('sire')} activeOpacity={0.75}>
                    <Text style={styles.fromStableBtnTxt}>From Stable</Text>
                  </TouchableOpacity>
                </View>
                {details.sireId && (
                  <Text style={styles.linkedNote}>✓ Linked to {details.sire}</Text>
                )}
                <View style={{ marginBottom: 18 }} />

                <Text style={styles.fieldLabel}>Dam</Text>
                <View style={styles.pedigreeInputRow}>
                  <TextInput
                    style={[styles.input, { flex: 1, marginBottom: 0 }]}
                    placeholder="Dam's name"
                    placeholderTextColor="#BDBDBD"
                    value={details.dam}
                    onChangeText={(v) => setDetails((d) => ({ ...d, dam: v, damId: null }))}
                  />
                  <TouchableOpacity style={styles.fromStableBtn} onPress={() => openPicker('dam')} activeOpacity={0.75}>
                    <Text style={styles.fromStableBtnTxt}>From Stable</Text>
                  </TouchableOpacity>
                </View>
                {details.damId && (
                  <Text style={styles.linkedNote}>✓ Linked to {details.dam}</Text>
                )}
                <View style={{ marginBottom: 18 }} />

                <Text style={[styles.fieldLabel, { marginTop: 6 }]}>Grandparents</Text>
                {GRANDPARENT_FIELDS.map((f) => (
                  <React.Fragment key={f.key}>
                    <Text style={styles.fieldLabel}>{f.label}</Text>
                    <TextInput
                      style={styles.input}
                      placeholder="Enter manually"
                      placeholderTextColor="#BDBDBD"
                      value={details[f.key]}
                      onChangeText={(v) => setDetails((d) => ({ ...d, [f.key]: v }))}
                    />
                  </React.Fragment>
                ))}

                {PHOTO_UPLOADS_ENABLED && canUploadMedia && (
                  <>
                    <Text style={styles.fieldLabel}>Profile Photo</Text>
                    {detailsPhotoPending || details.imageUrl ? (
                      <View style={{ marginBottom: 8 }}>
                        <AdjustableImage
                          uri={detailsPhotoPending ? detailsPhotoPending.uri : details.imageUrl}
                          focus={details.imageFocus}
                          onChange={(fp) => setDetails((d) => ({ ...d, imageFocus: fp }))}
                          maxWidth={520}
                        />
                        <Text style={{ fontSize: 11, color: MUTED, textAlign: 'center', marginTop: 4 }}>
                          Drag to adjust framing · pinch or +/− to zoom
                        </Text>
                        <View style={{ flexDirection: 'row', gap: 20, marginTop: 6, justifyContent: 'center' }}>
                          {isPhotoPickingAvailable() && (
                            <TouchableOpacity onPress={() => choosePhotoSource((items) => { setDetailsPhotoPending(items[0]); setDetails((d) => ({ ...d, imageFocus: { x: 0.5, y: 0.5, zoom: 1 } })); })} activeOpacity={0.7}>
                              <Text style={styles.editNameBtnTxt}>Change Photo</Text>
                            </TouchableOpacity>
                          )}
                          <TouchableOpacity onPress={() => { setDetailsPhotoPending(null); setDetails((d) => ({ ...d, imageUrl: '', imageFocus: null })); }} activeOpacity={0.7}>
                            <Text style={{ color: RED, fontWeight: '600', fontSize: 13 }}>Remove Photo</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    ) : isPhotoPickingAvailable() ? (
                      <TouchableOpacity style={styles.addMilestoneBtn} onPress={() => choosePhotoSource((items) => setDetailsPhotoPending(items[0]))} activeOpacity={0.75}>
                        <Text style={styles.addMilestoneBtnTxt}>+ Add Profile Photo</Text>
                      </TouchableOpacity>
                    ) : (
                      <Text style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>Adding a photo needs the latest app version.</Text>
                    )}
                  </>
                )}

                {isOwner && (
                  <>
                    <View style={styles.divider} />
                    <TouchableOpacity style={styles.recipToggle} onPress={() => setDetailsIsClient((v) => !v)} activeOpacity={0.75}>
                      <View style={[styles.recipToggleTrack, detailsIsClient && styles.recipToggleTrackOn]}>
                        <View style={[styles.recipToggleThumb, detailsIsClient && styles.recipToggleThumbOn]} />
                      </View>
                      <Text style={styles.recipToggleLabel}>
                        {detailsIsClient ? '🎓 Client horse — you train it' : 'This is a client horse'}
                      </Text>
                    </TouchableOpacity>
                    {detailsIsClient && !isHorseTrainer(horse, uid) && (
                      <>
                        <Text style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>
                          Adds this horse to your Client Horses with a billing ledger and provider notes. Enter the owner's contact info.
                        </Text>
                        {(() => {
                          // Clients already on file, so the name is spelled the
                          // same and their horses bill together.
                          const seen = new Map();
                          (allHorses || []).filter((h) => isHorseTrainer(h, uid)).forEach((h) => {
                            const c = getOwnerContact(h) || {};
                            const n = (c.name || h.ownerName || '').trim();
                            if (n && !seen.has(n)) seen.set(n, c);
                          });
                          const list = [...seen.entries()].sort((a, b) => a[0].localeCompare(b[0]));
                          if (!list.length) return null;
                          return (
                            <>
                              <Text style={styles.fieldLabel}>Existing client</Text>
                              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                                {list.map(([n, c]) => (
                                  <TouchableOpacity
                                    key={n}
                                    onPress={() => { setConvOwnerName(n); setConvOwnerEmail(c.email || ''); setConvOwnerPhone(c.phone || ''); }}
                                    style={[styles.cardTag, convOwnerName.trim() === n && { backgroundColor: '#1D9E7518', borderColor: '#1D9E75' }]}
                                    activeOpacity={0.75}
                                  >
                                    <Text style={styles.cardTagTxt}>{n}</Text>
                                  </TouchableOpacity>
                                ))}
                              </View>
                            </>
                          );
                        })()}
                        <Text style={styles.fieldLabel}>Owner Name</Text>
                        <TextInput style={styles.input} placeholder="Owner name" placeholderTextColor="#BDBDBD" value={convOwnerName} onChangeText={setConvOwnerName} />
                        <Text style={styles.fieldLabel}>Owner Email (optional)</Text>
                        <TextInput style={styles.input} placeholder="owner@email.com" placeholderTextColor="#BDBDBD" value={convOwnerEmail} onChangeText={setConvOwnerEmail} keyboardType="email-address" autoCapitalize="none" />
                        <Text style={styles.fieldLabel}>Owner Phone (optional)</Text>
                        <TextInput style={styles.input} placeholder="Phone" placeholderTextColor="#BDBDBD" value={convOwnerPhone} onChangeText={setConvOwnerPhone} keyboardType="phone-pad" />
                      </>
                    )}
                    {!detailsIsClient && isHorseTrainer(horse, uid) && (
                      <Text style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>
                        On save, this horse leaves Client Horses and becomes an ordinary horse in your herd. Its billing records and owner contact are kept.
                      </Text>
                    )}
                  </>
                )}
              </ScrollView>
            )}
            <View style={styles.sheetBtns}>
              <TouchableOpacity
                style={styles.cancelBtn}
                onPress={() => setShowDetailsModal(false)}
              >
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleSaveDetails} disabled={detailsSaving}>
                {detailsSaving ? <ActivityIndicator color="#FFFFFF" /> : <Text style={styles.saveBtnText}>Save</Text>}
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── From Stable Picker Modal ── */}
      <Modal visible={!!pickerFor} animationType="slide" transparent>
        <View style={styles.overlay}>
          <View style={[styles.sheet, { maxHeight: '85%' }]}>
            <Text style={styles.sheetTitle}>
              Select {pickerFor === 'dam' ? 'Dam' : pickerFor === 'recip' ? 'Recip Mare' : 'Sire'} from Your Stable
            </Text>
            <TextInput
              style={styles.input}
              placeholder="Search horses..."
              placeholderTextColor="#BDBDBD"
              value={pickerSearch}
              onChangeText={setPickerSearch}
              autoFocus
            />
            <ScrollView showsVerticalScrollIndicator={false} style={{ maxHeight: 360 }}>
              {(() => {
                const eligible = filterByPedigreeRole(
                  allHorses.filter((h) => h.id !== horseId),
                  pickerFor
                );
                const q = pickerSearch.trim().toLowerCase();
                const filtered = eligible.filter(
                  (h) => stableDisplayName(h).toLowerCase().includes(q) || h.name.toLowerCase().includes(q)
                );
                return (
                  <>
                    {filtered.map((h) => (
                      <TouchableOpacity
                        key={h.id}
                        style={styles.pickerRow}
                        onPress={() => pickFromStable(h)}
                        activeOpacity={0.75}
                      >
                        <Text style={styles.pickerRowName}>{stableDisplayName(h)}</Text>
                        {h.registeredName && h.name !== h.registeredName ? (
                          <Text style={styles.pickerRowSub}>Barn name: {h.name}</Text>
                        ) : null}
                        {h.status && h.status.length > 0 ? (
                          <Text style={styles.pickerRowSub}>{h.status.join(', ')}</Text>
                        ) : null}
                      </TouchableOpacity>
                    ))}
                    {eligible.length === 0 && (
                      <Text style={styles.pickerEmpty}>
                        No {pickerFor === 'sire' ? 'stallions or geldings' : 'mares'} in your stable yet.
                      </Text>
                    )}
                    {pickerFor === 'sire' && (
                      <StudPickerRows studs={studs} search={pickerSearch} onPick={pickStudAsSire} />
                    )}
                  </>
                );
              })()}
            </ScrollView>
            <View style={styles.sheetBtns}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setPickerFor(null)}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Create Incident Modal ── */}
      {/* Pictures & Documents: choose a document type for what was just picked */}
      <Modal visible={!!pendingDocFiles} animationType="slide" transparent onRequestClose={() => setPendingDocFiles(null)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.sheet}>
            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={styles.sheetTitle}>What is this?</Text>
              <Text style={[styles.fieldLabel, { marginBottom: 8 }]}>
                {(pendingDocFiles || []).length > 1 ? `${(pendingDocFiles || []).length} files — pick a type for all of them.` : 'Pick a type so it’s easy to find later.'}
              </Text>
              {(pendingDocFiles || []).length === 1 && (
                <>
                  <Text style={styles.fieldLabel}>Name (optional)</Text>
                  <TextInput
                    style={styles.input}
                    placeholder="e.g. Thunder — 2026 Coggins"
                    placeholderTextColor="#BDBDBD"
                    value={docNameInput}
                    onChangeText={setDocNameInput}
                    returnKeyType="done"
                  />
                </>
              )}
              <Text style={styles.fieldLabel}>Notes (optional)</Text>
              <TextInput
                style={[styles.input, styles.inputMultiline]}
                placeholder="What this is, why it matters, anything to remember about it…"
                placeholderTextColor="#BDBDBD"
                multiline
                value={docNoteInput}
                onChangeText={setDocNoteInput}
              />
              <Text style={{ fontSize: 11, color: MUTED, marginTop: 2, marginBottom: 8 }}>
                {(pendingDocFiles || []).length > 1
                  ? `Saved on all ${(pendingDocFiles || []).length} files.`
                  : 'Shown with the file so you know what it was for later.'}
              </Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
                {[...DOC_TYPE_PRESETS, ...customDocTypes.filter((t) => !DOC_TYPE_PRESETS.includes(t))].map((t) => {
                  const on = docTypeChoice === t && !customDocTypeInput.trim();
                  return (
                    <TouchableOpacity
                      key={t}
                      style={[styles.categoryChip, on && styles.categoryChipActive]}
                      onPress={() => { setDocTypeChoice(t); setCustomDocTypeInput(''); }}
                      activeOpacity={0.75}
                    >
                      <Text style={[styles.categoryChipTxt, on && styles.categoryChipTxtActive]}>{t}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <View style={styles.addTypeRow}>
                <TextInput
                  style={[styles.input, { flex: 1, marginBottom: 0 }]}
                  placeholder="Or add your own type…"
                  placeholderTextColor="#BDBDBD"
                  autoCapitalize="words"
                  returnKeyType="done"
                  value={customDocTypeInput}
                  onChangeText={setCustomDocTypeInput}
                  onSubmitEditing={confirmUploadDocs}
                />
              </View>
              <View style={styles.sheetBtns}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setPendingDocFiles(null)} activeOpacity={0.85}>
                  <Text style={styles.cancelBtnText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={confirmUploadDocs} activeOpacity={0.85}>
                  <Text style={styles.saveBtnText}>Upload</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      <PhotoViewerModal
        url={docViewerDoc?.url}
        note={docViewerDoc?.note}
        onEditNote={canUploadMedia && docViewerDoc?.editable ? () => openDocNote(docViewerDoc) : undefined}
        onClose={() => setDocViewerDoc(null)}
      />

      {/* Change what a picture or document says it is for */}
      <Modal visible={!!editingDocNote} animationType="slide" transparent onRequestClose={() => setEditingDocNote(null)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.sheet}>
            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={styles.sheetTitle}>Notes</Text>
              <Text style={[styles.fieldLabel, { marginBottom: 8 }]} numberOfLines={2}>
                {editingDocNote?.docType === 'Picture' ? 'Picture' : (editingDocNote?.name || 'Document')}
              </Text>
              <TextInput
                style={[styles.input, styles.inputMultiline]}
                placeholder="What this is, why it matters, anything to remember about it…"
                placeholderTextColor="#BDBDBD"
                multiline
                autoFocus
                value={docNoteEdit}
                onChangeText={setDocNoteEdit}
              />
              <Text style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>Clear it to remove the note.</Text>
              <View style={styles.sheetBtns}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setEditingDocNote(null)} activeOpacity={0.85}>
                  <Text style={styles.cancelBtnText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={saveDocNote} disabled={docNoteSaving} activeOpacity={0.85}>
                  {docNoteSaving ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>Save</Text>}
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      <BusinessProfileModal
        visible={showBusinessModal}
        onClose={() => setShowBusinessModal(false)}
        uid={auth.currentUser?.uid}
        initialProfile={businessProfile}
        company={company}
        onSaved={setOwnProfile}
      />

      {/* Edit a ledger entry */}
      <Modal visible={showEditLedgerModal} animationType="slide" transparent onRequestClose={() => setShowEditLedgerModal(false)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.sheet}>
            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={styles.sheetTitle}>Edit {editingLedgerEntry?.type === 'payment' ? 'Payment' : 'Charge'}</Text>

              <Text style={styles.fieldLabel}>Description</Text>
              <TextInput style={styles.input} placeholder="Description" placeholderTextColor="#BDBDBD" value={editLedgerDesc} onChangeText={setEditLedgerDesc} />

              {editingLedgerEntry?.type !== 'payment' && (
                <>
                  <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Note (optional)</Text>
                  <TextInput style={[styles.input, styles.inputMultiline]} placeholder="Any detail the owner should see…" placeholderTextColor="#BDBDBD" multiline value={editLedgerNote} onChangeText={setEditLedgerNote} />
                  <Text style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>Prints on the invoice underneath the description.</Text>
                </>
              )}

              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Amount</Text>
              <TextInput style={styles.input} placeholder="0.00" placeholderTextColor="#BDBDBD" keyboardType="decimal-pad" value={editLedgerAmount} onChangeText={setEditLedgerAmount} />

              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Date</Text>
              <TouchableOpacity style={styles.datePicker} onPress={() => setShowEditLedgerDatePicker(true)}>
                <Text style={styles.datePickerText}>{editLedgerDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Text><Text>📅</Text>
              </TouchableOpacity>
              {showEditLedgerDatePicker && (
                <DateTimePicker value={editLedgerDate} mode="date" display={Platform.OS === 'ios' ? 'spinner' : 'default'} onChange={(ev, d) => { if (Platform.OS === 'android') setShowEditLedgerDatePicker(false); if (d) setEditLedgerDate(d); }} />
              )}

              {editingLedgerEntry?.type === 'charge' && (
                <>
                  <TouchableOpacity style={[styles.recipToggle, { marginTop: 14 }]} onPress={() => setEditLedgerRecurring((v) => !v)} activeOpacity={0.75}>
                    <View style={[styles.recipToggleTrack, editLedgerRecurring && styles.recipToggleTrackOn]}>
                      <View style={[styles.recipToggleThumb, editLedgerRecurring && styles.recipToggleThumbOn]} />
                    </View>
                    <Text style={styles.recipToggleLabel}>{editLedgerRecurring ? '🔁 Repeat this monthly' : 'Make it repeat monthly'}</Text>
                  </TouchableOpacity>
                  {editLedgerRecurring && (
                    <View style={{ marginBottom: 4 }}>
                      <Text style={styles.fieldLabel}>Repeats on day of month</Text>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                        <TouchableOpacity onPress={() => setEditLedgerRecurDay('1')} activeOpacity={0.7} style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, borderWidth: 1, borderColor: editLedgerRecurDay === '1' ? GREEN : BORDER }}>
                          <Text style={{ fontSize: 13, fontWeight: '600', color: editLedgerRecurDay === '1' ? GREEN : MUTED }}>1st</Text>
                        </TouchableOpacity>
                        <Text style={{ fontSize: 13, color: MUTED }}>or day</Text>
                        <TextInput style={[styles.input, { width: 64, marginBottom: 0, textAlign: 'center' }]} keyboardType="number-pad" value={editLedgerRecurDay} onChangeText={(v) => setEditLedgerRecurDay(v.replace(/[^0-9]/g, '').slice(0, 2))} />
                      </View>
                      <Text style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>Adds automatically on day {Math.min(28, Math.max(1, parseInt(editLedgerRecurDay, 10) || 1))} each month going forward. This entry stays as-is.</Text>
                    </View>
                  )}
                </>
              )}

              <View style={styles.sheetBtns}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowEditLedgerModal(false)} activeOpacity={0.85}><Text style={styles.cancelBtnText}>Cancel</Text></TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={handleSaveEditLedger} activeOpacity={0.85}><Text style={styles.saveBtnText}>Save</Text></TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* One outside offspring's earnings, from this horse's own records */}
      <Modal visible={!!breakdownName} animationType="slide" transparent onRequestClose={() => setBreakdownName(null)}>
        <View style={styles.overlay}>
          <View style={[styles.sheet, { maxHeight: '85%' }]}>
            {(() => {
              const rows = allOffspringEntries(horse, allHorses)
                .filter((e) => normalizeHorseName(e.offspringName) === normalizeHorseName(breakdownName))
                .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
              const total = rows.reduce((s, e) => s + (e.amount || 0), 0);
              const money = (n) => '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              return (
                <>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Text style={[styles.sheetTitle, { flex: 1, marginBottom: 0 }]} numberOfLines={2}>{breakdownName}</Text>
                    <TouchableOpacity onPress={() => setBreakdownName(null)} activeOpacity={0.7} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }} style={{ paddingLeft: 12 }}>
                      <Text style={{ fontSize: 20, color: MUTED }}>✕</Text>
                    </TouchableOpacity>
                  </View>
                  <Text style={{ fontSize: 13, color: MUTED, marginBottom: 10, marginTop: 6 }}>
                    {rows.length} earning{rows.length === 1 ? '' : 's'} · {money(total)} · not in your stable, so these are recorded on {displayName(horse)}
                  </Text>
                  <ScrollView showsVerticalScrollIndicator={false} style={{ flexShrink: 1 }}>
                    {rows.map((e) => (
                      <View key={e.id} style={{ paddingVertical: 8, borderBottomWidth: 0.5, borderBottomColor: BORDER }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                          <Text style={[styles.noteText, { flex: 1, marginTop: 0 }]}>{e.eventName || 'Event'}</Text>
                          <Text style={{ fontSize: 15, fontWeight: '700', color: GREEN }}>{money(e.amount)}</Text>
                        </View>
                        <Text style={styles.noteDate}>{formatDate(e.date)}</Text>
                        {!!e.notes && <Text style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{e.notes}</Text>}
                      </View>
                    ))}
                  </ScrollView>
                  <TouchableOpacity style={[styles.cancelBtn, { marginTop: 12 }]} onPress={() => setBreakdownName(null)} activeOpacity={0.85}>
                    <Text style={styles.cancelBtnText}>Close</Text>
                  </TouchableOpacity>
                </>
              );
            })()}
          </View>
        </View>
      </Modal>

      {/* Pick a known offspring for an earnings entry */}
      <Modal visible={showOffspringPicker} animationType="slide" transparent onRequestClose={() => setShowOffspringPicker(false)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '85%' }]}>
            <Text style={styles.sheetTitle}>Choose an Offspring</Text>
            {offspringChoices.length > 8 && (
              <TextInput
                style={styles.input}
                placeholder="Search…"
                placeholderTextColor="#BDBDBD"
                value={offspringPickerSearch}
                onChangeText={setOffspringPickerSearch}
                autoFocus
              />
            )}
            <ScrollView showsVerticalScrollIndicator={false} style={{ maxHeight: 380 }} keyboardShouldPersistTaps="handled">
              {(() => {
                const q = offspringPickerSearch.trim().toLowerCase();
                const inStable = new Set(offspringInStable(horse || {}, allHorses).filter((c) => c.type === 'born').map((c) => normalizeHorseName(displayName(c))));
                const filtered = q ? offspringChoices.filter((n) => n.toLowerCase().includes(q)) : offspringChoices;
                if (filtered.length === 0) return <Text style={styles.pickerEmpty}>No offspring matches that search.</Text>;
                return filtered.map((n) => (
                  <TouchableOpacity
                    key={n}
                    style={styles.pickerRow}
                    onPress={() => { setEarningsOffspringName(n); setShowOffspringPicker(false); }}
                    activeOpacity={0.75}
                  >
                    <Text style={styles.pickerRowName}>{n}</Text>
                    <Text style={styles.pickerRowSub}>{inStable.has(normalizeHorseName(n)) ? 'In your stable — recorded on them, counts here automatically' : 'Entered before'}</Text>
                  </TouchableOpacity>
                ));
              })()}
            </ScrollView>
            <View style={styles.sheetBtns}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowOffspringPicker(false)} activeOpacity={0.85}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* Edit this horse's owner contact */}
      <Modal visible={showOwnerEdit} animationType="slide" transparent onRequestClose={() => setShowOwnerEdit(false)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.sheet}>
            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={styles.sheetTitle}>Owner Contact</Text>

              <Text style={styles.fieldLabel}>Owner Name</Text>
              <TextInput style={styles.input} placeholder="Owner name" placeholderTextColor="#BDBDBD" value={oeName} onChangeText={setOeName} />

              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Email</Text>
              <TextInput style={styles.input} placeholder="owner@email.com" placeholderTextColor="#BDBDBD" keyboardType="email-address" autoCapitalize="none" value={oeEmail} onChangeText={setOeEmail} />

              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Phone</Text>
              <TextInput style={styles.input} placeholder="(555) 555-5555" placeholderTextColor="#BDBDBD" keyboardType="phone-pad" value={oePhone} onChangeText={setOePhone} />

              <Text style={{ fontSize: 11, color: MUTED, marginTop: 6 }}>
                If this owner has other horses with you, you'll be asked whether to update those too — their name is what keeps their horses grouped together.
              </Text>

              <View style={styles.sheetBtns}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowOwnerEdit(false)} activeOpacity={0.85}>
                  <Text style={styles.cancelBtnText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={saveOwnerContact} disabled={oeSaving} activeOpacity={0.85}>
                  {oeSaving ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>Save</Text>}
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* Create Invoice — pick the billing period */}
      <Modal visible={showInvoiceModal} animationType="slide" transparent onRequestClose={() => setShowInvoiceModal(false)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '90%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={styles.sheetTitle}>Create Invoice</Text>
              <Text style={[styles.fieldLabel, { marginBottom: 10 }]}>Bills every outstanding charge. Items already on an invoice are carried forward under “Previously invoiced”; the rest are billed as new.</Text>

              {(() => {
                const { previously, current, credit, newCount, hasPrevious } = getInvoiceParts();
                // The summary is for the whole invoice, so it counts the other
                // horses the user has ticked as well as this one.
                const billable = invoiceBillableHorses();
                const extras = billable.filter((b) => invoiceExtraIds[b.horse.id]);
                const sumAmt = (list) => list.reduce((s, e) => s + e.amount, 0);
                const acc = (pick) => extras.reduce((s, x) => s + pick(x.parts), 0);
                const prevSub = sumAmt(previously) + acc((p) => sumAmt(p.previously));
                const newSub = sumAmt(current) + acc((p) => sumAmt(p.current));
                // This horse's credit only counts if this horse is actually on
                // the invoice — same rule the generated invoice follows.
                const meHasLines = previously.length > 0 || current.length > 0;
                const totalCredit = (meHasLines ? credit : 0) + acc((p) => p.credit);
                const prevCount = previously.length + acc((p) => p.previously.length);
                const newShown = current.length + acc((p) => p.current.length);
                const newAvailable = newCount + acc((p) => p.newCount);
                const bal = prevSub + newSub - totalCredit;
                const money = (n) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                const newAll = ledgerEntries
                  .filter((e) => e.type === 'charge' && !e.deleted && entryCompany(e) === myCompanyKey && invoiceListOf(e).length === 0)
                  .map((c) => ({ ...c, remaining: (c.amount || 0) - paidTowardCharge(c.id) }))
                  .filter((c) => c.remaining > 0.005)
                  .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
                return (
                  <View>
                    <View style={{ backgroundColor: '#FAF8F3', borderRadius: 10, borderWidth: 0.5, borderColor: BORDER, padding: 12, marginBottom: 12 }}>
                      {prevCount > 0 && (
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 }}>
                          <Text style={{ fontSize: 13, color: MUTED }}>Previously invoiced ({prevCount})</Text>
                          <Text style={{ fontSize: 13, color: TEXT }}>{money(prevSub)}</Text>
                        </View>
                      )}
                      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 }}>
                        <Text style={{ fontSize: 13, color: MUTED }}>New charges ({newShown}{invoiceChooseNew ? ` of ${newAvailable}` : ''})</Text>
                        <Text style={{ fontSize: 13, color: TEXT }}>{money(newSub)}</Text>
                      </View>
                      {totalCredit > 0.005 && (
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 }}>
                          <Text style={{ fontSize: 13, color: GREEN }}>Less prepayment credit</Text>
                          <Text style={{ fontSize: 13, color: GREEN }}>-{money(totalCredit)}</Text>
                        </View>
                      )}
                      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 5, paddingTop: 6, borderTopWidth: 0.5, borderTopColor: BORDER }}>
                        <Text style={{ fontSize: 14, fontWeight: '700', color: TEXT }}>Balance due</Text>
                        <Text style={{ fontSize: 14, fontWeight: '700', color: bal > 0.005 ? RED : GREEN }}>{money(bal)}</Text>
                      </View>
                    </View>

                    {hasPrevious && (
                      <TouchableOpacity onPress={() => setInvoiceIncludePrevious((v) => !v)} activeOpacity={0.75} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, paddingVertical: 4, marginBottom: 4 }}>
                        <Text style={{ fontSize: 18 }}>{invoiceIncludePrevious ? '☑️' : '⬜️'}</Text>
                        <View style={{ flex: 1 }}>
                          <Text style={{ fontSize: 14, fontWeight: '600', color: TEXT }}>Include previously-invoiced balance</Text>
                          <Text style={{ fontSize: 12, color: MUTED }}>Off = bill only new charges (a current-charges invoice, no carried-forward balance).</Text>
                        </View>
                      </TouchableOpacity>
                    )}

                    {newAll.length > 0 && (
                      <TouchableOpacity onPress={() => { const nx = !invoiceChooseNew; setInvoiceChooseNew(nx); if (nx) setInvoiceSelectedIds(Object.fromEntries(newAll.map((e) => [e.id, true]))); }} activeOpacity={0.75} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, paddingVertical: 4 }}>
                        <Text style={{ fontSize: 18 }}>{invoiceChooseNew ? '☑️' : '⬜️'}</Text>
                        <View style={{ flex: 1 }}>
                          <Text style={{ fontSize: 14, fontWeight: '600', color: TEXT }}>Choose which new charges to include</Text>
                          <Text style={{ fontSize: 12, color: MUTED }}>Off = bill all {newAll.length} new charge{newAll.length === 1 ? '' : 's'}. Unpicked charges wait for a later invoice.</Text>
                        </View>
                      </TouchableOpacity>
                    )}
                    {invoiceChooseNew && (() => {
                      const sel = newAll.filter((c) => invoiceSelectedIds[c.id]).length;
                      const allOn = sel === newAll.length && newAll.length > 0;
                      return (
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, marginBottom: 2 }}>
                          <Text style={{ fontSize: 12, color: MUTED }}>{sel} of {newAll.length} selected</Text>
                          <TouchableOpacity onPress={() => setInvoiceSelectedIds(allOn ? {} : Object.fromEntries(newAll.map((e) => [e.id, true])))} activeOpacity={0.7}>
                            <Text style={{ fontSize: 12, color: GREEN, fontWeight: '600' }}>{allOn ? 'Clear all' : 'Select all'}</Text>
                          </TouchableOpacity>
                        </View>
                      );
                    })()}
                    {invoiceChooseNew && newAll.map((c) => {
                      const on = !!invoiceSelectedIds[c.id];
                      const partial = c.remaining < (c.amount || 0) - 0.005;
                      return (
                        <TouchableOpacity key={c.id} onPress={() => setInvoiceSelectedIds((prev) => ({ ...prev, [c.id]: !prev[c.id] }))} activeOpacity={0.7} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 0.5, borderBottomColor: BORDER }}>
                          <Text style={{ fontSize: 18, marginRight: 10 }}>{on ? '☑️' : '⬜️'}</Text>
                          <View style={{ flex: 1 }}>
                            <Text style={styles.noteText}>{c.description}{partial ? ' (balance)' : ''}</Text>
                            <Text style={styles.noteDate}>{formatDate(c.date)}</Text>
                          </View>
                          <Text style={{ fontWeight: '700', color: RED, fontSize: 14 }}>{money(c.remaining)}</Text>
                        </TouchableOpacity>
                      );
                    })}

                    {billable.length > 0 && (() => {
                      // Grouped by client, because billing one owner for all of
                      // their horses at once is the point of a shared invoice.
                      const ownerOf = (h) => getOwnerContact(h)?.name || h.ownerName || 'Owner';
                      const owners = [];
                      billable.forEach((b) => {
                        const key = ownerOf(b.horse);
                        const g = owners.find((o) => o.key === key);
                        if (g) g.rows.push(b); else owners.push({ key, rows: [b] });
                      });
                      const totalOf = (p) => sumAmt(p.previously) + sumAmt(p.current);
                      return (
                        <View style={{ marginTop: 16, paddingTop: 12, borderTopWidth: 0.5, borderTopColor: BORDER }}>
                          <Text style={{ fontSize: 14, fontWeight: '700', color: TEXT }}>Also bill other horses</Text>
                          <Text style={{ fontSize: 12, color: MUTED, marginBottom: 2 }}>
                            One invoice, one number, saved to every horse on it.{extras.length > 0 ? ` ${extras.length} added.` : ''}
                          </Text>
                          {owners.map((o) => {
                            const allOn = o.rows.every((b) => invoiceExtraIds[b.horse.id]);
                            return (
                              <View key={o.key}>
                                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                                  <Text style={{ fontSize: 12, fontWeight: '700', color: MUTED }}>{o.key}</Text>
                                  <TouchableOpacity
                                    onPress={() => setInvoiceExtraIds((prev) => {
                                      const nx = { ...prev };
                                      o.rows.forEach((b) => { if (allOn) delete nx[b.horse.id]; else nx[b.horse.id] = true; });
                                      return nx;
                                    })}
                                    activeOpacity={0.7}
                                  >
                                    <Text style={{ fontSize: 12, color: GREEN, fontWeight: '600' }}>{allOn ? 'Clear' : 'Add all'}</Text>
                                  </TouchableOpacity>
                                </View>
                                {o.rows.map((b) => {
                                  const on = !!invoiceExtraIds[b.horse.id];
                                  return (
                                    <TouchableOpacity
                                      key={b.horse.id}
                                      onPress={() => setInvoiceExtraIds((prev) => ({ ...prev, [b.horse.id]: !prev[b.horse.id] }))}
                                      activeOpacity={0.7}
                                      style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 0.5, borderBottomColor: BORDER }}
                                    >
                                      <Text style={{ fontSize: 18, marginRight: 10 }}>{on ? '☑️' : '⬜️'}</Text>
                                      <View style={{ flex: 1 }}>
                                        <Text style={styles.noteText}>{displayName(b.horse)}</Text>
                                        <Text style={styles.noteDate}>
                                          {b.parts.current.length} new{b.parts.previously.length > 0 ? `, ${b.parts.previously.length} carried forward` : ''}
                                        </Text>
                                      </View>
                                      <Text style={{ fontWeight: '700', color: RED, fontSize: 14 }}>{money(totalOf(b.parts))}</Text>
                                    </TouchableOpacity>
                                  );
                                })}
                              </View>
                            );
                          })}
                        </View>
                      );
                    })()}
                  </View>
                );
              })()}

              {businessProfile?.name && (
                <>
                  <Text style={[styles.fieldLabel, { marginTop: 16 }]}>Notes on this invoice (optional)</Text>
                  <TextInput style={[styles.input, styles.inputMultiline]} placeholder="Payment terms, a thank-you, anything specific to this invoice…" placeholderTextColor="#BDBDBD" multiline value={invoiceNotes} onChangeText={setInvoiceNotes} />
                  <Text style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>Pre-filled from your default terms — edit freely for this invoice.</Text>
                </>
              )}

              {INVOICE_EMAIL_ENABLED && businessProfile?.name && (() => {
                const to = (getOwnerContact(horse)?.email || horse.ownerEmail || '').trim();
                if (!to) {
                  return <Text style={{ fontSize: 12, color: MUTED, marginTop: 14, lineHeight: 17 }}>Add the owner's email to this horse's owner contact to email invoices straight from the app.</Text>;
                }
                return (
                  <TouchableOpacity style={[styles.recipToggle, { marginTop: 14 }]} onPress={() => setInvoiceEmail((v) => !v)} activeOpacity={0.75}>
                    <View style={[styles.recipToggleTrack, invoiceEmail && styles.recipToggleTrackOn]}>
                      <View style={[styles.recipToggleThumb, invoiceEmail && styles.recipToggleThumbOn]} />
                    </View>
                    <Text style={[styles.recipToggleLabel, { flex: 1 }]} numberOfLines={2}>
                      {invoiceEmail ? `Email it to ${to}` : 'Email it to the client'}
                    </Text>
                  </TouchableOpacity>
                );
              })()}

              {!businessProfile?.name && (
                <Text style={{ fontSize: 12, color: MUTED, marginTop: 14 }}>You'll set up your business name next — it appears at the top of the invoice.</Text>
              )}

              <View style={styles.sheetBtns}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowInvoiceModal(false)} activeOpacity={0.85}><Text style={styles.cancelBtnText}>Cancel</Text></TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={handleCreateInvoice} disabled={invoiceBusy} activeOpacity={0.85}>
                  {invoiceBusy ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>{!businessProfile?.name ? 'Continue' : (INVOICE_EMAIL_ENABLED && invoiceEmail && (getOwnerContact(horse)?.email || horse.ownerEmail) ? 'Generate & email' : 'Generate')}</Text>}
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* Account Statement — date-range debit/credit report */}
      <Modal visible={showStatementModal} animationType="slide" transparent onRequestClose={() => setShowStatementModal(false)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '90%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={styles.sheetTitle}>Account Statement</Text>
              <Text style={[styles.fieldLabel, { marginBottom: 8 }]}>A full account history for reconciliation — every charge and payment in the range, with a running balance. Unlike an invoice, it lists payments too.</Text>

              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
                {[
                  { key: 'this_month', label: 'This month' },
                  { key: 'last_month', label: 'Last month' },
                  { key: 'this_year', label: 'This year' },
                  { key: 'last_year', label: 'Last year' },
                  { key: 'all', label: 'All time' },
                  { key: 'custom', label: 'Custom range' },
                ].map((o) => (
                  <TouchableOpacity key={o.key} style={[styles.categoryChip, statementRange === o.key && styles.categoryChipActive]} onPress={() => setStatementRange(o.key)} activeOpacity={0.75}>
                    <Text style={[styles.categoryChipTxt, statementRange === o.key && styles.categoryChipTxtActive]}>{o.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              {statementRange === 'custom' && (
                <View style={{ marginTop: 12 }}>
                  <Text style={styles.fieldLabel}>From</Text>
                  <TouchableOpacity style={styles.datePicker} onPress={() => setShowStmtFromPicker(true)}>
                    <Text style={styles.datePickerText}>{statementFrom.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Text><Text>📅</Text>
                  </TouchableOpacity>
                  {showStmtFromPicker && (
                    <DateTimePicker value={statementFrom} mode="date" display={Platform.OS === 'ios' ? 'spinner' : 'default'} onChange={(e, d) => { if (Platform.OS === 'android') setShowStmtFromPicker(false); if (d) setStatementFrom(d); }} />
                  )}
                  <Text style={[styles.fieldLabel, { marginTop: 12 }]}>To</Text>
                  <TouchableOpacity style={styles.datePicker} onPress={() => setShowStmtToPicker(true)}>
                    <Text style={styles.datePickerText}>{statementTo.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Text><Text>📅</Text>
                  </TouchableOpacity>
                  {showStmtToPicker && (
                    <DateTimePicker value={statementTo} mode="date" display={Platform.OS === 'ios' ? 'spinner' : 'default'} onChange={(e, d) => { if (Platform.OS === 'android') setShowStmtToPicker(false); if (d) setStatementTo(d); }} />
                  )}
                </View>
              )}

              {(() => {
                const active = ledgerEntries.filter((e) => !e.deleted && entryCompany(e) === myCompanyKey && (e.type === 'charge' || e.type === 'payment'));
                const r = resolveStatementRange();
                const rows = active.filter((e) => e.date >= r.from && e.date <= r.to);
                const opening = r.from > '0000-01-01' ? active.filter((e) => e.date < r.from).reduce((s, e) => s + (e.type === 'charge' ? (e.amount || 0) : -(e.amount || 0)), 0) : 0;
                const ch = rows.filter((e) => e.type === 'charge').reduce((s, e) => s + (e.amount || 0), 0);
                const pay = rows.filter((e) => e.type === 'payment').reduce((s, e) => s + (e.amount || 0), 0);
                const money = (n) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                return (
                  <View style={{ backgroundColor: '#FAF8F3', borderRadius: 10, borderWidth: 0.5, borderColor: BORDER, padding: 12, marginTop: 12 }}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 }}><Text style={{ fontSize: 13, color: MUTED }}>Opening balance</Text><Text style={{ fontSize: 13, color: TEXT }}>{money(opening)}</Text></View>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 }}><Text style={{ fontSize: 13, color: MUTED }}>Charges ({rows.filter((e) => e.type === 'charge').length})</Text><Text style={{ fontSize: 13, color: TEXT }}>{money(ch)}</Text></View>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 }}><Text style={{ fontSize: 13, color: GREEN }}>Payments ({rows.filter((e) => e.type === 'payment').length})</Text><Text style={{ fontSize: 13, color: GREEN }}>-{money(pay)}</Text></View>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 5, paddingTop: 6, borderTopWidth: 0.5, borderTopColor: BORDER }}><Text style={{ fontSize: 14, fontWeight: '700', color: TEXT }}>Closing balance</Text><Text style={{ fontSize: 14, fontWeight: '700', color: (opening + ch - pay) > 0.005 ? RED : GREEN }}>{money(opening + ch - pay)}</Text></View>
                  </View>
                );
              })()}

              <View style={styles.sheetBtns}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowStatementModal(false)} activeOpacity={0.85}><Text style={styles.cancelBtnText}>Cancel</Text></TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={handleCreateStatement} disabled={statementBusy} activeOpacity={0.85}>
                  {statementBusy ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>{businessProfile?.name ? 'Generate' : 'Continue'}</Text>}
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      <Modal visible={showIncidentModal} animationType="slide" transparent>
        <KeyboardAvoidingView
          style={styles.overlay}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <View style={styles.sheet}>
            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={styles.sheetTitle}>Create Incident</Text>

              <Text style={styles.fieldLabel}>What's going on?</Text>
              <TextInput
                style={styles.input}
                placeholder="e.g. Left front lameness"
                placeholderTextColor="#BDBDBD"
                value={incidentName}
                onChangeText={setIncidentName}
              />

              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Type</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
                {[...INCIDENT_TAG_PRESETS, ...customIncidentTags, ...incidentTags.filter((t) => !INCIDENT_TAG_PRESETS.includes(t) && !customIncidentTags.includes(t))].map((tag) => {
                  const on = incidentTags.includes(tag);
                  return (
                    <TouchableOpacity
                      key={tag}
                      style={[styles.categoryChip, on && styles.categoryChipActive]}
                      onPress={() => toggleIncidentTag(tag)}
                      activeOpacity={0.75}
                    >
                      <Text style={[styles.categoryChipTxt, on && styles.categoryChipTxtActive]}>{tag}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <View style={styles.addTypeRow}>
                <TextInput
                  style={[styles.input, { flex: 1, marginBottom: 0 }]}
                  placeholder="Add your own type…"
                  placeholderTextColor="#BDBDBD"
                  autoCapitalize="words"
                  returnKeyType="done"
                  value={incidentCustomTagInput}
                  onChangeText={setIncidentCustomTagInput}
                  onSubmitEditing={addCustomIncidentType}
                />
                <TouchableOpacity
                  style={[styles.addTypeBtn, !incidentCustomTagInput.trim() && { opacity: 0.5 }]}
                  onPress={addCustomIncidentType}
                  disabled={!incidentCustomTagInput.trim()}
                  activeOpacity={0.8}
                >
                  <Text style={styles.addTypeBtnTxt}>Add</Text>
                </TouchableOpacity>
              </View>

              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Date Occurred</Text>
              <TouchableOpacity style={styles.datePicker} onPress={() => setShowIncidentDatePicker(true)}>
                <Text style={styles.datePickerText}>
                  {incidentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </Text>
                <Text>📅</Text>
              </TouchableOpacity>
              {showIncidentDatePicker && (
                <DateTimePicker
                  value={incidentDate}
                  mode="date"
                  display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                  onChange={(e, d) => {
                    if (Platform.OS === 'android') setShowIncidentDatePicker(false);
                    if (d) setIncidentDate(d);
                  }}
                />
              )}

              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Symptoms</Text>
              <TextInput
                style={[styles.input, styles.inputMultiline]}
                placeholder="What you're seeing…"
                placeholderTextColor="#BDBDBD"
                multiline
                value={incidentSymptoms}
                onChangeText={setIncidentSymptoms}
              />

              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Vet Notes</Text>
              <TextInput
                style={[styles.input, styles.inputMultiline]}
                placeholder="Diagnosis, vet name, findings…"
                placeholderTextColor="#BDBDBD"
                multiline
                value={incidentVetNotes}
                onChangeText={setIncidentVetNotes}
              />

              {PHOTO_UPLOADS_ENABLED && isPhotoPickingAvailable() && canUploadMedia && (
                <>
                  <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Photos</Text>
                  <PhotoStrip
                    photos={incidentPhotosPending}
                    onAdd={() => choosePhotoSource((items) => setIncidentPhotosPending((prev) => [...prev, ...items]))}
                    onRemove={(p, i) => setIncidentPhotosPending((prev) => prev.filter((_, idx) => idx !== i))}
                    onOpen={(uri) => setDocViewerDoc({ url: uri })}
                  />
                </>
              )}

              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Prescribed Treatment</Text>
              <TextInput
                style={[styles.input, styles.inputMultiline]}
                placeholder="Medications, rest, rehab plan…"
                placeholderTextColor="#BDBDBD"
                multiline
                value={incidentTreatment}
                onChangeText={setIncidentTreatment}
              />

              {/* Photo upload — hidden until PHOTO_UPLOADS_ENABLED is turned on */}
              {PHOTO_UPLOADS_ENABLED && (
                <>
                  <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Photos</Text>
                  <TouchableOpacity style={styles.addPhotoBtn} activeOpacity={0.8}>
                    <Text style={styles.addPhotoBtnTxt}>+ Add Photo</Text>
                  </TouchableOpacity>
                </>
              )}

              <View style={styles.sheetBtns}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowIncidentModal(false)} activeOpacity={0.75}>
                  <Text style={styles.cancelBtnText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.saveBtn, incidentSaving && { opacity: 0.6 }]} onPress={handleSaveIncident} disabled={incidentSaving} activeOpacity={0.85}>
                  <Text style={styles.saveBtnText}>{incidentSaving ? 'Saving…' : 'Create'}</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── Add Task Modal ── */}
      <Modal visible={showAddMilestoneModal} animationType="slide" transparent>
        <KeyboardAvoidingView
          style={styles.overlay}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <View style={[styles.sheet, { maxHeight: '90%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
            <Text style={styles.sheetTitle}>{editingMilestoneId ? 'Edit Task' : 'Add Task'}</Text>
            <Text style={styles.fieldLabel}>Task Name</Text>
            <TextInput
              style={styles.input}
              placeholder="e.g. Ultrasound Recheck"
              placeholderTextColor="#BDBDBD"
              value={milestoneName}
              onChangeText={setMilestoneName}
              autoFocus
            />

            {!editingMilestoneId && (
              <TouchableOpacity onPress={() => setMilestoneAddAsComplete((v) => !v)} activeOpacity={0.75} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginTop: 6, marginBottom: 6, paddingVertical: 4 }}>
                <Text style={{ fontSize: 18 }}>{milestoneAddAsComplete ? '☑️' : '⬜️'}</Text>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, fontWeight: '600', color: TEXT }}>Mark as already done</Text>
                  <Text style={{ fontSize: 12, color: MUTED }}>Logs a completed task using the date below as the completed date — no reminders. Handy for something you just finished (e.g. dewormed the herd).</Text>
                </View>
              </TouchableOpacity>
            )}

            <Text style={styles.fieldLabel}>Notes (optional)</Text>
            <TextInput
              style={[styles.input, { minHeight: 70 }]}
              placeholder="Add any details for this task..."
              placeholderTextColor="#BDBDBD"
              value={milestoneNotes}
              onChangeText={setMilestoneNotes}
              multiline
              textAlignVertical="top"
            />

            {getAssignableMembers().length > 1 && (
              <>
                <Text style={styles.fieldLabel}>Assigned To</Text>
                <TouchableOpacity style={styles.sortBar} onPress={() => setShowAssigneeMenu((v) => !v)} activeOpacity={0.75}>
                  <Text style={styles.sortBarLabel}>{milestoneAssignee?.label || 'You'}</Text>
                  <Text style={styles.sortBarChevron}>{showAssigneeMenu ? '▲' : '▼'}</Text>
                </TouchableOpacity>
                {showAssigneeMenu && (
                  <View style={styles.sortMenu}>
                    {getAssignableMembers().map((mem) => (
                      <TouchableOpacity
                        key={mem.uid}
                        style={[styles.sortMenuItem, milestoneAssignee?.uid === mem.uid && styles.sortMenuItemActive]}
                        onPress={() => { setMilestoneAssignee(mem); setShowAssigneeMenu(false); }}
                        activeOpacity={0.75}
                      >
                        <Text style={[styles.sortMenuItemTxt, milestoneAssignee?.uid === mem.uid && styles.sortMenuItemTxtActive]}>{mem.label}</Text>
                        {milestoneAssignee?.uid === mem.uid && <Text style={styles.sortMenuItemCheck}>✓</Text>}
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </>
            )}

            <Text style={styles.fieldLabel}>Due Date</Text>
            <TouchableOpacity
              style={styles.datePicker}
              onPress={() => setShowMilestoneDatePicker(true)}
            >
              <Text style={styles.datePickerText}>
                {milestoneDate.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Text>
              <Text>📅</Text>
            </TouchableOpacity>
            {showMilestoneDatePicker && (
              <DateTimePicker
                value={milestoneDate}
                mode="date"
                display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                onChange={(e, d) => {
                  if (Platform.OS === 'android') setShowMilestoneDatePicker(false);
                  if (d) setMilestoneDate(d);
                }}
              />
            )}

            <Text style={[styles.fieldLabel, { marginTop: 18 }]}>Category (optional)</Text>
            <CategoryPicker
              customCategories={customCategories}
              hiddenPresets={hiddenCategories}
              value={milestoneCategory}
              onSelect={setMilestoneCategory}
              otherText={milestoneCategoryOther}
              onOtherTextChange={setMilestoneCategoryOther}
            />

            {allHorses.length > 1 && !editingMilestoneId && (
              <>
                <Text style={[styles.fieldLabel, { marginTop: 18 }]}>Apply To</Text>
                <View>
                  {allHorses.map((h) => {
                    const checked = milestoneHorseIds.includes(h.id);
                    return (
                      <TouchableOpacity
                        key={h.id}
                        style={styles.recipToggle}
                        onPress={() => toggleMilestoneHorse(h.id)}
                        activeOpacity={0.75}
                      >
                        <View style={[styles.radioOuter, checked && styles.radioOuterSelected, { borderRadius: 4 }]}>
                          {checked && <View style={[styles.radioInner, { width: 14, height: 14, borderRadius: 3 }]} />}
                        </View>
                        <Text style={styles.recipToggleLabel}>{displayName(h)}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </>
            )}

            <Text style={[styles.fieldLabel, { marginTop: 18 }]}>Reminder</Text>
            <TouchableOpacity
              style={styles.recipToggle}
              onPress={() => setReminderEnabled((v) => !v)}
              activeOpacity={0.75}
            >
              <View style={[styles.recipToggleTrack, reminderEnabled && styles.recipToggleTrackOn]}>
                <View style={[styles.recipToggleThumb, reminderEnabled && styles.recipToggleThumbOn]} />
              </View>
              <Text style={styles.recipToggleLabel}>
                {reminderEnabled ? 'Yes — send a reminder' : 'No'}
              </Text>
            </TouchableOpacity>

            {reminderEnabled && (
              <>
                <Text style={styles.fieldLabel}>When</Text>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <TouchableOpacity
                    style={[styles.recipToggle, { flex: 1 }]}
                    onPress={() => setReminderMode('specified')}
                    activeOpacity={0.75}
                  >
                    <View style={[styles.radioOuter, reminderMode === 'specified' && styles.radioOuterSelected]}>
                      {reminderMode === 'specified' && <View style={styles.radioInner} />}
                    </View>
                    <Text style={styles.recipToggleLabel}>On milestone date (9:00 AM)</Text>
                  </TouchableOpacity>
                </View>
                <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
                  <TouchableOpacity
                    style={[styles.recipToggle, { flex: 1 }]}
                    onPress={() => setReminderMode('custom')}
                    activeOpacity={0.75}
                  >
                    <View style={[styles.radioOuter, reminderMode === 'custom' && styles.radioOuterSelected]}>
                      {reminderMode === 'custom' && <View style={styles.radioInner} />}
                    </View>
                    <Text style={styles.recipToggleLabel}>Custom date & time</Text>
                  </TouchableOpacity>
                </View>

                {/* Repeats ride on the server-side due-date push, so they are only
                    offered for the "on milestone date" reminder. */}
                {reminderMode === 'specified' && (
                  <>
                    <Text style={[styles.fieldLabel, { marginTop: 18 }]}>Repeat</Text>
                    <TouchableOpacity
                      style={styles.recipToggle}
                      onPress={() => setReminderRepeat((v) => !v)}
                      activeOpacity={0.75}
                    >
                      <View style={[styles.recipToggleTrack, reminderRepeat && styles.recipToggleTrackOn]}>
                        <View style={[styles.recipToggleThumb, reminderRepeat && styles.recipToggleThumbOn]} />
                      </View>
                      <Text style={styles.recipToggleLabel}>
                        {reminderRepeat ? 'Remind every morning until done' : 'Remind once'}
                      </Text>
                    </TouchableOpacity>
                    {reminderRepeat && (
                      <Text style={{ color: MUTED, fontSize: 12, marginTop: 6, lineHeight: 17 }}>
                        Stops as soon as the task is marked complete, or after 14 days overdue.
                      </Text>
                    )}
                    <Text style={[styles.fieldLabel, { marginTop: 18 }]}>Heads-up</Text>
                    <TouchableOpacity
                      style={styles.recipToggle}
                      onPress={() => setReminderDayBefore((v) => !v)}
                      activeOpacity={0.75}
                    >
                      <View style={[styles.recipToggleTrack, reminderDayBefore && styles.recipToggleTrackOn]}>
                        <View style={[styles.recipToggleThumb, reminderDayBefore && styles.recipToggleThumbOn]} />
                      </View>
                      <Text style={styles.recipToggleLabel}>
                        {reminderDayBefore ? 'Also remind me the day before' : 'No day-before reminder'}
                      </Text>
                    </TouchableOpacity>
                  </>
                )}
              </>
            )}

            {reminderEnabled && reminderMode === 'custom' && (
              <>
                <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Reminder Date & Time</Text>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <TouchableOpacity
                    style={[styles.datePicker, { flex: 1 }]}
                    onPress={() => setShowReminderDatePicker(true)}
                  >
                    <Text style={styles.datePickerText}>
                      {reminderDate.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </Text>
                    <Text>📅</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.datePicker, { flex: 1 }]}
                    onPress={() => setShowReminderTimePicker(true)}
                  >
                    <Text style={styles.datePickerText}>
                      {reminderDate.toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </Text>
                    <Text>⏰</Text>
                  </TouchableOpacity>
                </View>
                {showReminderDatePicker && (
                  <DateTimePicker
                    value={reminderDate}
                    mode="date"
                    display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                    onChange={(e, d) => {
                      if (Platform.OS === 'android') setShowReminderDatePicker(false);
                      if (d) {
                        setReminderDate((prev) => {
                          const next = new Date(prev);
                          next.setFullYear(d.getFullYear(), d.getMonth(), d.getDate());
                          return next;
                        });
                      }
                    }}
                  />
                )}
                {showReminderTimePicker && (
                  <DateTimePicker
                    value={reminderDate}
                    mode="time"
                    display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                    onChange={(e, d) => {
                      if (Platform.OS === 'android') setShowReminderTimePicker(false);
                      if (d) {
                        setReminderDate((prev) => {
                          const next = new Date(prev);
                          next.setHours(d.getHours(), d.getMinutes(), 0, 0);
                          return next;
                        });
                      }
                    }}
                  />
                )}
              </>
            )}

            <View style={styles.sheetBtns}>
              <TouchableOpacity
                style={styles.cancelBtn}
                onPress={() => { clearTaskDraft(); setEditingMilestoneId(null); setShowAddMilestoneModal(false); }}
              >
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleAddMilestone}>
                <Text style={styles.saveBtnText}>{editingMilestoneId ? 'Save' : 'Add'}</Text>
              </TouchableOpacity>
            </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── Share Modal ── */}
      <Modal visible={showShareModal} animationType="slide" transparent>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '90%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
            <Text style={styles.sheetTitle}>{isOwner ? `Share ${displayName(horse)}` : 'Add a Trainer'}</Text>

            <Text style={styles.fieldLabel}>Recipient Email</Text>
            <TextInput
              style={styles.input}
              placeholder="friend@example.com"
              placeholderTextColor="#BDBDBD"
              keyboardType="email-address"
              autoCapitalize="none"
              value={shareEmail}
              onChangeText={setShareEmail}
            />

            {!isOwner ? (
              <Text style={{ fontSize: 12, color: MUTED, marginTop: 12, marginBottom: 8 }}>
                You're a trainer on this horse. This person will be added as another trainer once the horse owner approves your request.
              </Text>
            ) : (
              <>
                <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Permission</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
                  {['view', 'edit', 'trainer', 'transfer'].map((p) => (
                    <TouchableOpacity
                      key={p}
                      style={[styles.categoryChip, sharePermission === p && styles.categoryChipActive]}
                      onPress={() => setSharePermission(p)}
                      activeOpacity={0.75}
                    >
                      <Text style={[styles.categoryChipTxt, sharePermission === p && styles.categoryChipTxtActive]}>
                        {p === 'view' ? 'View Only' : p === 'edit' ? 'Can Edit' : p === 'trainer' ? 'Provider' : 'Transfer'}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
                {sharePermission === 'trainer' && (
                  getOwnerContact(horse) && (getOwnerContact(horse).name || getOwnerContact(horse).email || getOwnerContact(horse).phone) ? (
                    <Text style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>
                      Trainer gets edit access. Using your saved contact info ({getOwnerContact(horse).name || getOwnerContact(horse).email}) so the trainer can reach you.
                    </Text>
                  ) : (
                    <>
                      <Text style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>
                        Trainer gets edit access. Add your contact info so the trainer can reach you.
                      </Text>
                      <View style={{ width: '100%' }}>
                        <Text style={styles.fieldLabel}>Your Name</Text>
                        <TextInput style={styles.input} placeholder="Owner name" placeholderTextColor="#BDBDBD" value={ownerContactName} onChangeText={setOwnerContactName} />
                      </View>
                      <View style={{ width: '100%' }}>
                        <Text style={styles.fieldLabel}>Your Email</Text>
                        <TextInput style={styles.input} placeholder="owner@email.com" placeholderTextColor="#BDBDBD" keyboardType="email-address" autoCapitalize="none" value={ownerContactEmail} onChangeText={setOwnerContactEmail} />
                      </View>
                      <View style={{ width: '100%' }}>
                        <Text style={styles.fieldLabel}>Your Phone</Text>
                        <TextInput style={styles.input} placeholder="(555) 555-5555" placeholderTextColor="#BDBDBD" keyboardType="phone-pad" value={ownerContactPhone} onChangeText={setOwnerContactPhone} />
                      </View>
                    </>
                  )
                )}
                {sharePermission === 'trainer' && (company || businessProfile?.name) && (
                  <TouchableOpacity onPress={() => setShareSameCompany((v) => !v)} activeOpacity={0.75} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginBottom: 8, paddingVertical: 4 }}>
                    <Text style={{ fontSize: 18 }}>{shareSameCompany ? '☑️' : '⬜️'}</Text>
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: 13, fontWeight: '600', color: TEXT }}>Same company as me</Text>
                      <Text style={{ fontSize: 12, color: MUTED }}>Links them to {company?.name || businessProfile?.name} for all horses. Their charges & notes bill under your shared company name.</Text>
                    </View>
                  </TouchableOpacity>
                )}
                {(sharePermission === 'view' || sharePermission === 'edit') && horseHasTrainer(horse) && (
                  <TouchableOpacity onPress={() => setShareCanSeeBilling((v) => !v)} activeOpacity={0.75} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginBottom: 8, paddingVertical: 4 }}>
                    <Text style={{ fontSize: 18 }}>{shareCanSeeBilling ? '☑️' : '⬜️'}</Text>
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: 13, fontWeight: '600', color: TEXT }}>Also let them see billing</Text>
                      <Text style={{ fontSize: 12, color: MUTED }}>Lets this person view the Billing Ledger (all providers). Leave off to hide billing from them.</Text>
                    </View>
                  </TouchableOpacity>
                )}
                {sharePermission === 'transfer' && (
                  <Text style={{ fontSize: 12, color: isHorseTrainer(horse, auth.currentUser?.uid) ? MUTED : '#E53935', marginBottom: 8 }}>
                    {isHorseTrainer(horse, auth.currentUser?.uid)
                      ? 'The recipient becomes the owner and controls the record. You stay on as a trainer with access to billing and notes — but as owner, they can remove your access later.'
                      : 'Transfer permanently moves ownership to the recipient. You will no longer have access.'}
                  </Text>
                )}
              </>
            )}

            <View style={styles.sheetBtns}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowShareModal(false)} activeOpacity={0.75}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.saveBtn, shareLoading && { opacity: 0.6 }]} onPress={handleShare} disabled={shareLoading} activeOpacity={0.85}>
                <Text style={styles.saveBtnText}>{shareLoading ? 'Sending...' : !isOwner ? 'Send Request' : sharePermission === 'transfer' ? 'Transfer' : 'Share'}</Text>
              </TouchableOpacity>
            </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── Add Training Note Modal ── */}
      <Modal visible={showAddTrainingNoteModal} animationType="slide" transparent>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.sheet}>
            <Text style={styles.sheetTitle}>{editingTrainingNote ? 'Edit Provider Note' : 'Add Provider Note'}</Text>
            <TextInput
              style={[styles.input, { minHeight: 100 }]}
              placeholder="Enter training note..."
              placeholderTextColor="#BDBDBD"
              value={trainingNoteText}
              onChangeText={setTrainingNoteText}
              multiline
              textAlignVertical="top"
              autoFocus
            />
            {isHorseTrainer(horse, auth.currentUser?.uid) && (
              <TouchableOpacity
                style={styles.recipToggle}
                onPress={() => setTrainingNotePrivate((v) => !v)}
                activeOpacity={0.75}
              >
                <View style={[styles.recipToggleTrack, trainingNotePrivate && styles.recipToggleTrackOn]}>
                  <View style={[styles.recipToggleThumb, trainingNotePrivate && styles.recipToggleThumbOn]} />
                </View>
                <Text style={styles.recipToggleLabel}>
                  {trainingNotePrivate ? '🔒 Private — trainer only' : '👁 Visible to owner'}
                </Text>
              </TouchableOpacity>
            )}
            <View style={styles.sheetBtns}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => { setShowAddTrainingNoteModal(false); setEditingTrainingNote(null); setTrainingNoteText(''); setTrainingNotePrivate(false); }} activeOpacity={0.75}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={editingTrainingNote ? handleSaveEditTrainingNote : handleAddTrainingNote} activeOpacity={0.85}>
                <Text style={styles.saveBtnText}>{editingTrainingNote ? 'Save Changes' : 'Save'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── Add Charge Modal ── */}
      <Modal visible={showAddChargeModal} animationType="slide" transparent>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '85%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
              <Text style={styles.sheetTitle}>Add Charge</Text>
              <Text style={styles.fieldLabel}>Description</Text>
              <TextInput style={styles.input} placeholder="e.g. Monthly board, Lesson" placeholderTextColor="#BDBDBD" value={chargeDescription} onChangeText={setChargeDescription} autoFocus />
              <Text style={styles.fieldLabel}>Note (optional)</Text>
              <TextInput style={[styles.input, styles.inputMultiline]} placeholder="Any detail the owner should see — what was done, dates covered, who did it…" placeholderTextColor="#BDBDBD" multiline value={chargeNote} onChangeText={setChargeNote} />
              <Text style={{ fontSize: 11, color: MUTED, marginTop: 2, marginBottom: 2 }}>Prints on the invoice underneath the description.</Text>
              <Text style={styles.fieldLabel}>Amount ($)</Text>
              <TextInput style={styles.input} placeholder="0.00" placeholderTextColor="#BDBDBD" keyboardType="decimal-pad" value={chargeAmount} onChangeText={setChargeAmount} />
              <Text style={styles.fieldLabel}>Date of expense</Text>
              <Text style={{ fontSize: 12, color: MUTED, marginBottom: 4 }}>When it actually occurred — back-date it if you're entering it later (e.g. set July 1 even if you're adding it on the 10th).</Text>
              <TouchableOpacity style={styles.datePicker} onPress={() => setShowChargeDatePicker(true)} activeOpacity={0.75}>
                <Text style={styles.datePickerText}>{chargeDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</Text>
                <Text>📅</Text>
              </TouchableOpacity>
              {showChargeDatePicker && (
                <DateTimePicker value={chargeDate} mode="date" display={Platform.OS === 'ios' ? 'spinner' : 'default'} onChange={(e, d) => { if (Platform.OS === 'android') setShowChargeDatePicker(false); if (d) setChargeDate(d); }} />
              )}
              <TouchableOpacity
                style={styles.recipToggle}
                onPress={() => setChargeRecurring((v) => !v)}
                activeOpacity={0.75}
              >
                <View style={[styles.recipToggleTrack, chargeRecurring && styles.recipToggleTrackOn]}>
                  <View style={[styles.recipToggleThumb, chargeRecurring && styles.recipToggleThumbOn]} />
                </View>
                <Text style={styles.recipToggleLabel}>
                  {chargeRecurring ? '🔁 Repeats monthly' : 'Repeat monthly'}
                </Text>
              </TouchableOpacity>
              {chargeRecurring && (
                <View style={{ marginBottom: 4 }}>
                  <Text style={styles.fieldLabel}>Repeats on day of month</Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <TouchableOpacity onPress={() => setChargeRecurDay('1')} activeOpacity={0.7} style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, borderWidth: 1, borderColor: chargeRecurDay === '1' ? GREEN : BORDER }}>
                      <Text style={{ fontSize: 13, fontWeight: '600', color: chargeRecurDay === '1' ? GREEN : MUTED }}>1st</Text>
                    </TouchableOpacity>
                    <Text style={{ fontSize: 13, color: MUTED }}>or day</Text>
                    <TextInput
                      style={[styles.input, { width: 64, marginBottom: 0, textAlign: 'center' }]}
                      keyboardType="number-pad"
                      value={chargeRecurDay}
                      onChangeText={(v) => setChargeRecurDay(v.replace(/[^0-9]/g, '').slice(0, 2))}
                    />
                  </View>
                  <Text style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>
                    Added automatically on day {Math.min(28, Math.max(1, parseInt(chargeRecurDay, 10) || 1))} of each month until you stop it. (Capped at 28 so it lands every month.)
                  </Text>
                </View>
              )}
              <TouchableOpacity style={[styles.saveBtn, { marginTop: 18 }]} onPress={handleAddCharge} activeOpacity={0.85}>
                <Text style={styles.saveBtnText}>Add Charge</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowAddChargeModal(false)} activeOpacity={0.85}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── Record Payment Modal ── */}
      <Modal visible={showAddPaymentModal} animationType="slide" transparent>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '85%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
              <Text style={styles.sheetTitle}>Record Payment</Text>
              <Text style={styles.fieldLabel}>Amount Received ($)</Text>
              <TextInput style={styles.input} placeholder="0.00" placeholderTextColor="#BDBDBD" keyboardType="decimal-pad" value={paymentAmount} onChangeText={setPaymentAmount} autoFocus />
              <Text style={styles.fieldLabel}>Note (optional)</Text>
              <TextInput style={styles.input} placeholder="e.g. Check, Venmo, Cash" placeholderTextColor="#BDBDBD" value={paymentNote} onChangeText={setPaymentNote} />
              <Text style={styles.fieldLabel}>Date received</Text>
              <TouchableOpacity style={styles.datePicker} onPress={() => setShowPaymentDatePicker(true)} activeOpacity={0.75}>
                <Text style={styles.datePickerText}>{paymentDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</Text>
                <Text>📅</Text>
              </TouchableOpacity>
              {showPaymentDatePicker && (
                <DateTimePicker value={paymentDate} mode="date" display={Platform.OS === 'ios' ? 'spinner' : 'default'} onChange={(e, d) => { if (Platform.OS === 'android') setShowPaymentDatePicker(false); if (d) setPaymentDate(d); }} />
              )}

              {/* Apply this payment to specific charges (optional) */}
              {(() => {
                const myCompany = normalizeCompany(businessProfile?.name) || ('u:' + auth.currentUser?.uid);
                const outstanding = ledgerEntries
                  .filter((e) => e.type === 'charge' && !e.deleted && entryCompany(e) === myCompany)
                  .map((c) => ({ ...c, remaining: (c.amount || 0) - paidTowardCharge(c.id) }))
                  .filter((c) => c.remaining > 0.005)
                  .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
                if (outstanding.length === 0) return null;
                const amt = parseAmount(paymentAmount);
                const applied = Object.values(paymentAllocations).reduce((s, v) => s + (parseAmount(v) || 0), 0);
                const autoFill = () => {
                  let left = isNaN(amt) ? 0 : amt;
                  const next = {};
                  for (const c of outstanding) {
                    const give = Math.min(c.remaining, left);
                    if (give > 0.005) { next[c.id] = give.toFixed(2); left -= give; }
                  }
                  setPaymentAllocations(next);
                };
                return (
                  <View style={{ marginTop: 14 }}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <Text style={styles.fieldLabel}>Apply to charges (optional)</Text>
                      <TouchableOpacity onPress={autoFill} activeOpacity={0.7}><Text style={{ fontSize: 12, color: GREEN, fontWeight: '600' }}>Auto-apply oldest first</Text></TouchableOpacity>
                    </View>
                    <Text style={{ fontSize: 11, color: MUTED, marginBottom: 6 }}>Mark which charges this payment covers. Leave a smaller amount for a partial payment. Anything left over is held as a prepayment credit.</Text>
                    {outstanding.map((c) => (
                      <View key={c.id} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6, borderBottomWidth: 0.5, borderBottomColor: BORDER }}>
                        <View style={{ flex: 1 }}>
                          <Text style={styles.noteText}>{c.description}</Text>
                          <Text style={styles.noteDate}>{formatDate(c.date)} · ${c.remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} left</Text>
                        </View>
                        <TouchableOpacity
                          onPress={() => setPaymentAllocations((prev) => ({ ...prev, [c.id]: (prev[c.id] && parseAmount(prev[c.id]) >= c.remaining - 0.005) ? '' : c.remaining.toFixed(2) }))}
                          activeOpacity={0.7}
                          style={{ paddingHorizontal: 8, paddingVertical: 6, marginRight: 6, borderRadius: 6, borderWidth: 1, borderColor: (paymentAllocations[c.id] && parseAmount(paymentAllocations[c.id]) >= c.remaining - 0.005) ? GREEN : BORDER }}
                        >
                          <Text style={{ fontSize: 12, fontWeight: '700', color: (paymentAllocations[c.id] && parseAmount(paymentAllocations[c.id]) >= c.remaining - 0.005) ? GREEN : MUTED }}>Full</Text>
                        </TouchableOpacity>
                        <TextInput
                          style={[styles.input, { width: 84, marginBottom: 0, textAlign: 'right' }]}
                          placeholder="$0.00"
                          placeholderTextColor="#BDBDBD"
                          keyboardType="decimal-pad"
                          value={paymentAllocations[c.id] || ''}
                          onChangeText={(v) => setPaymentAllocations((prev) => ({ ...prev, [c.id]: v }))}
                        />
                      </View>
                    ))}
                    {(() => {
                      const over = applied > (isNaN(amt) ? 0 : amt) + 0.005;
                      const leftover = !isNaN(amt) ? amt - applied : 0;
                      return (
                        <Text style={{ fontSize: 12, color: over ? RED : MUTED, marginTop: 6, textAlign: 'right' }}>
                          Applied {fmtMoney(applied)}{!isNaN(amt) ? ` of ${fmtMoney(amt)}` : ''}
                          {!over && leftover > 0.005 ? ` · ${fmtMoney(leftover)} → credit` : ''}
                        </Text>
                      );
                    })()}
                  </View>
                );
              })()}

              <TouchableOpacity style={[styles.saveBtn, { marginTop: 18 }]} onPress={handleAddPayment} activeOpacity={0.85}>
                <Text style={styles.saveBtnText}>Record Payment</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowAddPaymentModal(false)} activeOpacity={0.85}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── Apply Credit Modal ── */}
      <Modal visible={showApplyCreditModal} animationType="slide" transparent onRequestClose={() => setShowApplyCreditModal(false)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '85%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={styles.sheetTitle}>Apply Credit</Text>
              {(() => {
                const credit = creditAvailable();
                const outstanding = ledgerEntries
                  .filter((e) => e.type === 'charge' && !e.deleted && entryCompany(e) === myCompanyKey)
                  .map((c) => ({ ...c, remaining: (c.amount || 0) - paidTowardCharge(c.id) }))
                  .filter((c) => c.remaining > 0.005)
                  .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
                return (
                  <>
                    <Text style={{ fontSize: 13, color: MUTED, marginBottom: 12 }}>Credit available: <Text style={{ color: '#3B6D11', fontWeight: '700' }}>{fmtMoney(credit)}</Text>. Tap a charge to apply credit to it (oldest prepayment used first).</Text>
                    {outstanding.length === 0 ? (
                      <Text style={styles.noNotesTxt}>No outstanding charges to apply credit to.</Text>
                    ) : outstanding.map((c) => {
                      const willApply = Math.min(credit, c.remaining);
                      const can = credit > 0.005;
                      return (
                        <View key={c.id} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 0.5, borderBottomColor: BORDER }}>
                          <View style={{ flex: 1 }}>
                            <Text style={styles.noteText}>{c.description}</Text>
                            <Text style={styles.noteDate}>{formatDate(c.date)} · {fmtMoney(c.remaining)} left</Text>
                          </View>
                          <TouchableOpacity disabled={!can} onPress={() => applyCreditToCharge(c)} activeOpacity={0.7} style={{ paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6, borderWidth: 1, borderColor: can ? GREEN : BORDER }}>
                            <Text style={{ fontSize: 12, fontWeight: '700', color: can ? GREEN : MUTED }}>Apply {fmtMoney(willApply)}</Text>
                          </TouchableOpacity>
                        </View>
                      );
                    })}
                  </>
                );
              })()}
              <TouchableOpacity style={[styles.cancelBtn, { marginTop: 16 }]} onPress={() => setShowApplyCreditModal(false)} activeOpacity={0.85}>
                <Text style={styles.cancelBtnText}>Done</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── Mark as Born Modal ── */}
      <Modal visible={showMarkBornModal} animationType="slide" transparent>
        <KeyboardAvoidingView
          style={styles.overlay}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <View style={styles.sheet}>
            <Text style={styles.sheetTitle}>Mark as Born</Text>
            <Text style={styles.fieldLabel}>Date of Birth</Text>
            <TouchableOpacity
              style={styles.datePicker}
              onPress={() => setShowBornDatePicker(true)}
            >
              <Text style={styles.datePickerText}>
                {bornDate.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Text>
              <Text>📅</Text>
            </TouchableOpacity>
            {showBornDatePicker && (
              <DateTimePicker
                value={bornDate}
                mode="date"
                display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                onChange={(e, d) => {
                  if (Platform.OS === 'android') setShowBornDatePicker(false);
                  if (d) setBornDate(d);
                }}
              />
            )}
            <View style={styles.sheetBtns}>
              <TouchableOpacity
                style={styles.cancelBtn}
                onPress={() => setShowMarkBornModal(false)}
              >
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleMarkBorn}>
                <Text style={styles.saveBtnText}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── Mark as Sold Modal ── */}
      <Modal visible={showSoldModal} animationType="slide" transparent>
        <KeyboardAvoidingView
          style={styles.overlay}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <View style={styles.sheet}>
            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={styles.sheetTitle}>Mark as Sold</Text>
              <Text style={{ fontSize: 13, color: MUTED, marginBottom: 12, lineHeight: 18 }}>
                The horse leaves your herd and is kept under View Sold Horses at the bottom of the herd list. Its records, earnings, health log and billing stay readable; its tasks and reminders stop. To hand the horse to a buyer who uses Horse Math, use Share → Transfer instead.
              </Text>
              <Text style={styles.fieldLabel}>Sale Date</Text>
              <TouchableOpacity
                style={styles.datePicker}
                onPress={() => {
                  // The iOS spinner only reports a date once it's scrolled,
                  // so opening it counts as choosing the date it shows.
                  if (!soldDate && Platform.OS === 'ios') setSoldDate(new Date());
                  setShowSoldDatePicker(true);
                }}
              >
                {soldDate ? (
                  <Text style={styles.datePickerText}>
                    {soldDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </Text>
                ) : (
                  <Text style={[styles.datePickerText, { color: '#BDBDBD' }]}>Select sale date *</Text>
                )}
                <Text>📅</Text>
              </TouchableOpacity>
              {showSoldDatePicker && (
                <DateTimePicker
                  value={Platform.OS === 'web' ? soldDate : (soldDate || new Date())}
                  mode="date"
                  display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                  onChange={(e, d) => {
                    if (Platform.OS === 'android') setShowSoldDatePicker(false);
                    if (d) setSoldDate(d);
                  }}
                />
              )}
              <Text style={styles.fieldLabel}>Sold To (optional)</Text>
              <TextInput style={styles.input} placeholder="Buyer's name" placeholderTextColor="#BDBDBD" value={soldTo} onChangeText={setSoldTo} />
              <Text style={styles.fieldLabel}>Sale Price (optional)</Text>
              <TextInput style={styles.input} placeholder="e.g. 25000" placeholderTextColor="#BDBDBD" value={salePrice} onChangeText={setSalePrice} keyboardType="decimal-pad" />
              <Text style={styles.fieldLabel}>Notes (optional)</Text>
              <TextInput style={[styles.input, { minHeight: 60, textAlignVertical: 'top' }]} placeholder="Terms, contact, etc." placeholderTextColor="#BDBDBD" value={soldNotes} onChangeText={setSoldNotes} multiline />
            </ScrollView>
            <View style={styles.sheetBtns}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowSoldModal(false)}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.saveBtn, soldSaving && { opacity: 0.6 }]} onPress={handleMarkSold} disabled={soldSaving}>
                <Text style={styles.saveBtnText}>{soldSaving ? 'Saving...' : 'Mark Sold'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </>
  );
}

// ─── MILESTONES & TASKS SCREEN ────────────────────────────────────────────────

const DATE_FILTERS = [
  { key: 'all',      label: 'All' },
  { key: 'overdue',  label: 'Overdue' },
  { key: '7',        label: 'Next 7 Days' },
  { key: '30',       label: 'Next 30 Days' },
];

// ─── CLIENT HORSES SCREEN ────────────────────────────────────────────────────

// ─── SHARING SCREEN ───────────────────────────────────────────────────────────
// Two ways to share beyond one horse at a time:
//   Herd partners — a standing, two-way arrangement. Everything either of you
//     owns, now and later, is in one herd. Needs the other side to accept.
//   Share several horses — a one-off: pick horses, pick a person, done.
// Client horses stay out of a partnership; they can still be picked for a
// one-off share, the same as sharing them individually.
function SharingScreen({ navigation }) {
  const uid = auth.currentUser?.uid;
  const [horses, setHorses] = useState([]);
  const [partnerships, setPartnerships] = useState([]);
  useEffect(() => subscribeToHorses(uid, setHorses), [uid]);
  useEffect(() => subscribeToHerdPartnerships(uid, setPartnerships), [uid]);

  // Partner invite
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePerm, setInvitePerm] = useState('view');
  const [inviteClients, setInviteClients] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(false);

  // One-off share of several horses
  const [bulkEmail, setBulkEmail] = useState('');
  const [bulkPerm, setBulkPerm] = useState('view');
  const [bulkSel, setBulkSel] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);

  const mineOwned = horses.filter((h) => h.ownerId === uid);
  const herdOwned = mineOwned.filter((h) => isOwnHerdHorse(h, uid));

  // Everyone with access to any horse I own, and which horses. A provider's
  // team is one entry; a herd partner is marked as such, since the way to
  // change their access is the partnership above, not horse by horse.
  const partnerIds = new Set(activePartners(partnerships, uid).map((p) => p.uid));
  const access = (() => {
    const groups = new Map();
    mineOwned.forEach((h) => (h.shares || []).forEach((s) => {
      if (!s?.uid || s.uid === uid) return;
      const key = s.viaCompany ? 'team:' + s.viaCompany : 'person:' + s.uid;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          team: !!s.viaCompany,
          label: s.viaCompany ? (s.viaCompanyName || 'Provider team') : (s.name || s.email || 'Someone'),
          email: s.viaCompany ? '' : (s.email || ''),
          partner: !s.viaCompany && partnerIds.has(s.uid),
          uid: s.uid,
          people: new Set(),
          horses: new Map(),
        });
      }
      const g = groups.get(key);
      if (s.viaCompany) g.people.add(s.name || s.email);
      if (!g.horses.has(h.id)) g.horses.set(h.id, { horse: h, permission: s.permission });
    }));
    return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label));
  })();
  const accessLevel = (p) => (p === 'trainer' ? 'Provider' : p === 'edit' ? 'Can Edit' : 'View Only');

  // View/Edit access can be taken back right here. A Provider or a team has
  // balances and notes to think about, so those go through the horse itself.
  const handleRemoveAccess = (g, h) => {
    Alert.alert(
      'Remove access',
      `Remove ${g.label}'s access to ${displayName(h)}?${g.partner ? ' They are your herd partner — the partnership will not add it back, but ending the partnership is the usual way to change what they see.' : ''}`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Remove', style: 'destructive', onPress: () => revokeShare(h.id, h.shares || [], g.uid).catch(() => Alert.alert('Error', 'Could not remove access. Please try again.')) },
      ]
    );
  };
  const active = partnerships.filter((p) => p.status === 'active');
  const waitingForMe = partnerships.filter((p) => p.status === 'pending' && p.invitedBy !== uid);
  const sentByMe = partnerships.filter((p) => p.status === 'pending' && p.invitedBy === uid);
  const permLabel = (p) => (p === 'edit' ? 'Can Edit' : 'View Only');

  // Look someone up by email, with the checks both flows share.
  const findPerson = async (raw) => {
    const email = raw.trim().toLowerCase();
    if (!email) { Alert.alert('Email needed', 'Enter the email they use for Horse Math.'); return null; }
    if (email === (auth.currentUser?.email || '').toLowerCase()) { Alert.alert('That is you', 'Enter someone else\'s email.'); return null; }
    const found = await findUserByEmail(email);
    if (!found) { Alert.alert('User not found', USER_NOT_FOUND_MSG); return null; }
    if (!(await confirmSharingMatch(found, email))) return null;
    return found;
  };

  // Nothing is shared at invite time. Sharing happens on BOTH sides only once
  // they accept — so a declined invite leaves nothing behind to undo.
  const handleInvite = async () => {
    if (inviteBusy) return;
    setInviteBusy(true);
    try {
      const found = await findPerson(inviteEmail);
      if (!found) return;
      const existing = partnerships.find((p) => (p.uids || []).includes(found.uid) && p.status !== 'ended');
      if (existing) {
        Alert.alert('Already set up', existing.status === 'active' ? 'You already share a herd with them.' : 'There is already an invite between you — check below.');
        return;
      }
      const me = auth.currentUser;
      await inviteHerdPartner(
        { uid: me.uid, email: me.email || '', name: me.displayName || me.email || '' },
        { uid: found.uid, email: found.shareEmail || found.email || '', name: found.displayName || found.shareEmail || found.email || '' },
        invitePerm,
        inviteClients
      );
      setInviteEmail('');
      setInviteClients(false);
      Alert.alert('Invite sent', `Once ${found.displayName || found.email} accepts, every horse you each own will be shared with the other.`);
    } catch (e) {
      Alert.alert('Could not send', e?.message || 'Please try again.');
    } finally {
      setInviteBusy(false);
    }
  };

  const handleAccept = (p) => {
    const other = partnerOf(p, uid);
    Alert.alert(
      'Share herds?',
      p.includeClients
        ? `Every horse you own, client horses included — ${mineOwned.length} now, and any you add later — will be shared with ${other?.name || other?.email} as ${permLabel(p.permission)}, and theirs with you.`
        : `Every horse you own — ${herdOwned.length} now, and any you add later — will be shared with ${other?.name || other?.email} as ${permLabel(p.permission)}, and theirs with you. Client horses are not included.`,
      [
        { text: 'Not now', style: 'cancel' },
        { text: 'Accept', onPress: () => acceptHerdPartnership(p.id).catch(() => Alert.alert('Error', 'Could not accept. Please try again.')) },
      ]
    );
  };

  const handleDecline = (p) =>
    declineHerdPartnership(p.id).catch(() => Alert.alert('Error', 'Could not decline. Please try again.'));

  const handleEnd = (p) => {
    const other = partnerOf(p, uid);
    Alert.alert(
      'End herd sharing?',
      `${other?.name || other?.email} will lose access to every horse you own, and you to theirs. Horses each of you added stay with whoever owns them.`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'End', style: 'destructive', onPress: () => endHerdPartnership(p.id, uid).catch(() => Alert.alert('Error', 'Could not end it. Please try again.')) },
      ]
    );
  };

  // One-off: the same share as doing each horse by hand, in one go.
  const handleBulkShare = async () => {
    if (bulkBusy) return;
    const chosen = mineOwned.filter((h) => bulkSel[h.id]);
    if (chosen.length === 0) { Alert.alert('Pick horses', 'Tick at least one horse to share.'); return; }
    setBulkBusy(true);
    try {
      const found = await findPerson(bulkEmail);
      if (!found) return;
      const results = await Promise.allSettled(chosen.map((h) =>
        shareHorse(h.id, h.shares || [], found.uid, found.shareEmail || found.email || '', found.displayName || '', bulkPerm)
      ));
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (chosen.length - failed > 0) {
        notifyAccess(found.uid, '🐴 Horses Shared With You',
          `${myDisplayName()} shared ${plural(chosen.length - failed, 'horse')} with you (${permLabel(bulkPerm)}) in Horse Math.`);
      }
      if (failed === 0) {
        Alert.alert('Shared', `${chosen.length} horse${chosen.length === 1 ? '' : 's'} shared with ${found.displayName || found.email} as ${permLabel(bulkPerm)}.`);
        setBulkSel({});
        setBulkEmail('');
      } else {
        Alert.alert('Partly shared', `${chosen.length - failed} of ${chosen.length} shared. ${failed} could not be — try again for those.`);
      }
    } catch (e) {
      Alert.alert('Could not share', e?.message || 'Please try again.');
    } finally {
      setBulkBusy(false);
    }
  };

  const PermChips = ({ value, onChange }) => (
    <View style={{ flexDirection: 'row', gap: 8, marginBottom: 6 }}>
      {['view', 'edit'].map((p) => (
        <TouchableOpacity key={p} style={[styles.categoryChip, value === p && styles.categoryChipActive]} onPress={() => onChange(p)} activeOpacity={0.75}>
          <Text style={[styles.categoryChipTxt, value === p && styles.categoryChipTxtActive]}>{permLabel(p)}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  return (
    <SafeAreaView style={styles.screen} edges={['bottom']}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }} keyboardShouldPersistTaps="handled">

          {/* ── Herd partners ── */}
          <Text style={styles.sectionHeading}>Herd Partners</Text>
          <Text style={{ fontSize: 13, color: MUTED, marginBottom: 12, lineHeight: 19 }}>
            One herd between two accounts. Every horse either of you owns — now and whenever you add one — is shared with the other. Client horses stay separate unless you choose to include them.
          </Text>

          {waitingForMe.map((p) => {
            const other = partnerOf(p, uid);
            return (
              <View key={p.id} style={[styles.formCard, { borderColor: GREEN }]}>
                <Text style={styles.horseName}>{other?.name || other?.email}</Text>
                <Text style={{ fontSize: 13, color: MUTED, marginTop: 2 }}>wants to share herds with you · {permLabel(p.permission)}{p.includeClients ? ' · incl. client horses' : ''}</Text>
                <View style={{ flexDirection: 'row', gap: 10, marginTop: 10 }}>
                  <TouchableOpacity style={[styles.cancelBtn, { flex: 1 }]} onPress={() => handleDecline(p)} activeOpacity={0.85}>
                    <Text style={styles.cancelBtnText}>Decline</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[styles.saveBtn, { flex: 1 }]} onPress={() => handleAccept(p)} activeOpacity={0.85}>
                    <Text style={styles.saveBtnText}>Accept</Text>
                  </TouchableOpacity>
                </View>
              </View>
            );
          })}

          {active.map((p) => {
            const other = partnerOf(p, uid);
            const applied = (p.appliedBy || []).includes(uid) && (p.appliedBy || []).includes(other?.uid);
            return (
              <View key={p.id} style={styles.formCard}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.horseName}>{other?.name || other?.email}</Text>
                    <Text style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                      {other?.email}{other?.email ? ' · ' : ''}{permLabel(p.permission)}{p.includeClients ? ' · incl. client horses' : ''}{applied ? '' : ' · syncing…'}
                    </Text>
                  </View>
                  <TouchableOpacity onPress={() => handleEnd(p)} activeOpacity={0.7} style={{ paddingVertical: 6, paddingLeft: 10 }}>
                    <Text style={{ fontSize: 13, color: RED, fontWeight: '600' }}>End</Text>
                  </TouchableOpacity>
                </View>
              </View>
            );
          })}

          {sentByMe.map((p) => {
            const other = partnerOf(p, uid);
            return (
              <View key={p.id} style={styles.formCard}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.horseName}>{other?.name || other?.email}</Text>
                    <Text style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>Waiting for them to accept · {permLabel(p.permission)}</Text>
                  </View>
                  <TouchableOpacity onPress={() => handleDecline(p)} activeOpacity={0.7} style={{ paddingVertical: 6, paddingLeft: 10 }}>
                    <Text style={{ fontSize: 13, color: MUTED, fontWeight: '600' }}>Cancel</Text>
                  </TouchableOpacity>
                </View>
              </View>
            );
          })}

          <View style={styles.formCard}>
            <Text style={[styles.fieldLabel, { marginBottom: 6 }]}>Invite someone to share your herd</Text>
            <TextInput
              style={styles.input}
              placeholder="Their Horse Math email"
              placeholderTextColor="#BDBDBD"
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              value={inviteEmail}
              onChangeText={setInviteEmail}
            />
            <Text style={styles.fieldLabel}>They can</Text>
            <PermChips value={invitePerm} onChange={setInvitePerm} />
            <TouchableOpacity onPress={() => setInviteClients((v) => !v)} activeOpacity={0.75} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, paddingVertical: 6 }}>
              <Text style={{ fontSize: 18 }}>{inviteClients ? '☑️' : '⬜️'}</Text>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 14, fontWeight: '600', color: TEXT }}>Include client horses</Text>
                <Text style={{ fontSize: 12, color: MUTED }}>For a partner in everything. Off = your own herd only; client horses stay separate.</Text>
              </View>
            </TouchableOpacity>
            <Text style={{ fontSize: 11, color: MUTED, marginBottom: 10 }}>
              The same level applies both ways. Nothing is shared until they accept.
            </Text>
            <TouchableOpacity style={styles.saveBtn} onPress={handleInvite} disabled={inviteBusy} activeOpacity={0.85}>
              {inviteBusy ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>Send Invite</Text>}
            </TouchableOpacity>
          </View>

          {/* ── Share several horses at once ── */}
          <Text style={[styles.sectionHeading, { marginTop: 22 }]}>Share Several Horses</Text>
          <Text style={{ fontSize: 13, color: MUTED, marginBottom: 12, lineHeight: 19 }}>
            A one-off. Pick the horses, pick the person — the same as sharing each one from its profile, in a single step.
          </Text>
          <View style={styles.formCard}>
            <TextInput
              style={styles.input}
              placeholder="Their Horse Math email"
              placeholderTextColor="#BDBDBD"
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              value={bulkEmail}
              onChangeText={setBulkEmail}
            />
            <Text style={styles.fieldLabel}>They can</Text>
            <PermChips value={bulkPerm} onChange={setBulkPerm} />

            {mineOwned.length === 0 ? (
              <Text style={styles.pickerEmpty}>You don't own any horses to share yet.</Text>
            ) : (
              <>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, marginBottom: 2 }}>
                  <Text style={styles.fieldLabel}>Horses ({Object.values(bulkSel).filter(Boolean).length} of {mineOwned.length})</Text>
                  <TouchableOpacity
                    onPress={() => {
                      const allOn = mineOwned.every((h) => bulkSel[h.id]);
                      setBulkSel(allOn ? {} : Object.fromEntries(mineOwned.map((h) => [h.id, true])));
                    }}
                    activeOpacity={0.7}
                  >
                    <Text style={{ fontSize: 12, color: GREEN, fontWeight: '600' }}>
                      {mineOwned.every((h) => bulkSel[h.id]) ? 'Clear all' : 'Select all'}
                    </Text>
                  </TouchableOpacity>
                </View>
                {mineOwned.map((h) => {
                  const on = !!bulkSel[h.id];
                  const client = !isOwnHerdHorse(h, uid);
                  return (
                    <TouchableOpacity
                      key={h.id}
                      onPress={() => setBulkSel((prev) => ({ ...prev, [h.id]: !prev[h.id] }))}
                      activeOpacity={0.7}
                      style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 0.5, borderBottomColor: BORDER }}
                    >
                      <Text style={{ fontSize: 18, marginRight: 10 }}>{on ? '☑️' : '⬜️'}</Text>
                      <View style={{ flex: 1 }}>
                        <Text style={{ fontSize: 15, color: TEXT }}>{displayName(h)}</Text>
                        {client && <Text style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>Client horse</Text>}
                      </View>
                    </TouchableOpacity>
                  );
                })}
                <TouchableOpacity style={[styles.saveBtn, { marginTop: 12 }]} onPress={handleBulkShare} disabled={bulkBusy} activeOpacity={0.85}>
                  {bulkBusy ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>Share Selected</Text>}
                </TouchableOpacity>
              </>
            )}
          </View>

          {/* ── Who has access to what ── */}
          <Text style={[styles.sectionHeading, { marginTop: 22 }]}>Who Has Access</Text>
          <Text style={{ fontSize: 13, color: MUTED, marginBottom: 12, lineHeight: 19 }}>
            Everyone your horses are shared with, and which ones. Tap a horse to manage it there.
          </Text>
          {access.length === 0 ? (
            <View style={styles.formCard}>
              <Text style={styles.pickerEmpty}>None of your horses are shared with anyone yet.</Text>
            </View>
          ) : access.map((g) => (
            <View key={g.key} style={styles.formCard}>
              <Text style={styles.horseName}>{g.team ? '🏢 ' : ''}{g.label}{g.partner ? '  🤝' : ''}</Text>
              <Text style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                {g.team
                  ? `Provider team · ${[...g.people].join(', ')}`
                  : `${g.email}${g.partner ? (g.email ? ' · ' : '') + 'Herd partner' : ''}`}
              </Text>
              {[...g.horses.values()].sort((a, b) => displayName(a.horse).localeCompare(displayName(b.horse))).map(({ horse: h, permission }) => (
                <View key={h.id} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 0.5, borderBottomColor: BORDER }}>
                  <TouchableOpacity style={{ flex: 1 }} onPress={() => navigation.navigate('Horse', { horseId: h.id })} activeOpacity={0.7}>
                    <Text style={{ fontSize: 15, color: TEXT }}>{displayName(h)}</Text>
                    <Text style={{ fontSize: 12, color: MUTED, marginTop: 1 }}>{accessLevel(permission)}{!isOwnHerdHorse(h, uid) ? ' · client horse' : ''}</Text>
                  </TouchableOpacity>
                  {!g.team && permission !== 'trainer' && (
                    <TouchableOpacity onPress={() => handleRemoveAccess(g, h)} activeOpacity={0.7} style={{ paddingVertical: 6, paddingLeft: 12 }}>
                      <Text style={{ fontSize: 13, color: RED, fontWeight: '600' }}>Remove</Text>
                    </TouchableOpacity>
                  )}
                </View>
              ))}
            </View>
          ))}
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

// What a client could be billed for on one horse, for my company: charges not
// yet on an invoice (current), invoiced but unpaid (previously), and unapplied
// credit. Same rules as the single-horse invoice sheet.
const invoicePartsFor = (entries, myCompanyKey) => {
  const paidBy = new Map();
  let credit = 0;
  entries.forEach((e) => {
    if (e.type !== 'payment' || e.deleted) return;
    let alloc = 0;
    (e.allocations || []).forEach((a) => { const amt = a.amount || 0; alloc += amt; paidBy.set(a.chargeId, (paidBy.get(a.chargeId) || 0) + amt); });
    if (entryCompany(e) === myCompanyKey) credit += Math.max(0, (e.amount || 0) - alloc);
  });
  const outstanding = entries
    .filter((e) => !e.deleted && e.type === 'charge' && entryCompany(e) === myCompanyKey)
    .map((c) => ({ ...c, remaining: (c.amount || 0) - (paidBy.get(c.id) || 0) }))
    .filter((c) => c.remaining > 0.005);
  const label = (c) => (c.remaining < (c.amount || 0) - 0.005 ? `${c.description} (balance)` : c.description);
  return {
    previously: outstanding.filter((c) => invoiceListOf(c).length > 0)
      .map((c) => ({ id: c.id, date: c.date, description: label(c), note: c.note || '', amount: c.remaining, invoiceNumber: firstInvoiceOf(c) })),
    current: outstanding.filter((c) => invoiceListOf(c).length === 0)
      .map((c) => ({ id: c.id, date: c.date, description: label(c), note: c.note || '', amount: c.remaining })),
    credit,
  };
};

// Send invoices (or payment reminders) to several clients at once. Each client
// gets ONE email covering all their horses. Nothing goes out until the provider
// has reviewed the list and confirmed.
function SendInvoicesModal({ visible, onClose, ownerGroups, businessProfile, company }) {
  const [tab, setTab] = useState('new'); // 'new' | 'remind'
  const [ledger, setLedger] = useState([]);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [previewKey, setPreviewKey] = useState(null);
  const uid = auth.currentUser?.uid;
  const idKey = ownerGroups.flatMap((g) => g.rows.map((h) => h.id)).filter(Boolean).sort().join(',');
  useEffect(() => {
    if (!visible || !idKey) { setLedger([]); return undefined; }
    return subscribeToLedgerForHorses(idKey.split(','), setLedger);
  }, [visible, idKey]);
  useEffect(() => { if (visible) { setPicked({}); setTab('new'); setProgress(''); setPreviewKey(null); } }, [visible]);
  const fmt = (n) => '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const myKey = normalizeCompany(businessProfile?.name) || ('u:' + uid);
  const byHorse = {};
  ledger.forEach((e) => { (byHorse[e.horseId] = byHorse[e.horseId] || []).push(e); });
  const sum = (arr) => arr.reduce((s, c) => s + (c.amount || 0), 0);
  const today = localYmd(new Date());
  const daysSince = (ymd) => (ymd ? Math.max(0, Math.round((new Date(today + 'T12:00:00') - new Date(ymd + 'T12:00:00')) / 86400000)) : 0);
  const clients = ownerGroups.map((g) => {
    const filled = (x) => (x?.name ? 1 : 0) + (x?.email ? 1 : 0) + (x?.phone ? 1 : 0);
    const contact = g.rows.map((h) => getOwnerContact(h) || { name: h.ownerName, email: h.ownerEmail })
      .reduce((best, cur) => (filled(cur) > filled(best) ? cur : best), {});
    const parts = g.rows.map((h) => ({ horse: h, ...invoicePartsFor(byHorse[h.id] || [], myKey) }));
    const newAmt = parts.reduce((s, p) => s + sum(p.current), 0);
    const prevAmt = parts.reduce((s, p) => s + sum(p.previously), 0);
    const credit = parts.reduce((s, p) => s + p.credit, 0);
    const prevCharges = parts.flatMap((p) => p.previously);
    return {
      key: g.key, contact, email: (contact.email || '').trim(), parts, newAmt, prevAmt, credit,
      total: Math.max(0, newAmt + prevAmt - credit),
      due: Math.max(0, prevAmt - credit),
      invoices: [...new Set(prevCharges.map((c) => c.invoiceNumber).filter(Boolean))],
      oldest: prevCharges.map((c) => c.date).filter(Boolean).sort()[0],
      horseNames: g.rows.map((h) => displayName(h)),
      emailProblem: g.rows.map((h) => h.ownerEmailProblem).find((x) => x && x.email && x.email.toLowerCase() === (contact.email || '').trim().toLowerCase()) || null,
    };
  });
  const list = tab === 'new'
    ? clients.filter((c) => c.newAmt > 0.005 && c.total > 0.005)
    : clients.filter((c) => c.due > 0.005);
  const isOn = (c) => !!c.email && (picked[c.key] ?? true);
  const chosen = list.filter(isOn);
  const chosenTotal = chosen.reduce((s, c) => s + (tab === 'new' ? c.total : c.due), 0);

  const sendAll = async () => {
    setBusy(true);
    const sent = [];
    const failed = [];
    for (let i = 0; i < chosen.length; i += 1) {
      const c = chosen[i];
      setProgress(`${tab === 'new' ? 'Sending invoice' : 'Sending reminder'} ${i + 1} of ${chosen.length}…`);
      try {
        if (tab === 'new') {
          const groups = c.parts.filter((p) => p.current.length || p.previously.length)
            .map((p) => ({ horse: p.horse, previously: p.previously, current: p.current, credit: p.credit }));
          const num = await getNextInvoiceNumber(uid, company?.id || null);
          const primary = groups[0].horse;
          const horseLabel = (primary.name || primary.registeredName || 'Horse').replace(/[^\w -]+/g, '').trim() || 'Horse';
          const result = await generateAndShareInvoice({
            horseId: primary.id,
            horseIds: groups.map((g) => g.horse.id),
            fileName: groups.length > 1 ? `Invoice ${num}.pdf` : `${horseLabel} Invoice ${num}.pdf`,
            groups,
            credit: c.credit,
            business: businessProfile,
            ownerContact: c.contact,
            horse: primary,
            invoiceNumber: num,
            notes: businessProfile?.terms || '',
            emailTo: c.email,
            emailTotal: Math.round(c.total * 100) / 100,
          });
          // The invoice exists either way, so its charges are stamped either way.
          for (const g of groups) {
            for (const ch of [...g.current, ...g.previously]) {
              try {
                await addChargeToInvoice(ch.id, num);
                if (result === 'emailed') await updateLedgerEntry(ch.id, { invoiceEmailedAt: today, emailProblem: null });
              } catch { /* shows as new next time; rare */ }
            }
          }
          if (result === 'emailed') sent.push(c.key);
          else failed.push(`${c.key}: ${num} was created but not emailed${String(result).slice(13) ? ` (${String(result).slice(13)})` : ''}`);
        } else {
          await callSendPaymentReminder({
            horseIds: c.parts.filter((p) => p.previously.length).map((p) => p.horse.id),
            emailTo: c.email,
            clientName: c.contact.name || '',
            amount: Math.round(c.due * 100) / 100,
            invoices: c.invoices,
            chargeIds: c.parts.flatMap((p) => p.previously.map((ch) => ch.id)),
          });
          sent.push(c.key);
        }
      } catch (e) {
        failed.push(`${c.key}: ${e?.message || 'not sent'}`);
      }
    }
    setBusy(false);
    setProgress('');
    const what = tab === 'new' ? 'invoice' : 'reminder';
    const skipped = list.length - chosen.length;
    Alert.alert(
      failed.length ? 'Some didn’t send' : `${sent.length} ${what}${sent.length === 1 ? '' : 's'} sent`,
      [
        sent.length ? `Sent to ${sent.join(', ')}.` : '',
        failed.length ? `Not sent:\n${failed.join('\n')}` : '',
        skipped ? `${skipped} skipped (not selected or no email on file).` : '',
      ].filter(Boolean).join('\n\n'),
    );
    if (!failed.length) onClose();
  };

  const confirmSend = () => {
    if (!chosen.length) return;
    const n = chosen.length;
    const msg = tab === 'new'
      ? `Each client gets one email with their invoice PDF${''} and a Pay online button. The charges are marked as invoiced. Total ${fmt(chosenTotal)}.`
      : `Each client gets a friendly reminder of what's still unpaid, with a Pay online button for their balance.`;
    const title = `Send ${n} ${tab === 'new' ? 'invoice' : 'reminder'}${n === 1 ? '' : 's'}?`;
    if (Platform.OS === 'web') { if (window.confirm(`${title}\n\n${msg}`)) sendAll(); }
    else Alert.alert(title, msg, [{ text: 'Back', style: 'cancel' }, { text: 'Send', onPress: sendAll }]);
  };

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={() => !busy && onClose()}>
      <View style={styles.overlay}>
        <View style={[styles.sheet, { maxHeight: '92%' }]}>
          <Text style={styles.sheetTitle}>Send to clients</Text>
          <View style={{ flexDirection: 'row', gap: 8, marginBottom: 10 }}>
            {[['new', 'New invoices'], ['remind', 'Payment reminders']].map(([k, l]) => (
              <TouchableOpacity key={k} onPress={() => { if (!busy) { setTab(k); setPicked({}); } }} activeOpacity={0.75}
                style={[styles.categoryChip, tab === k && styles.categoryChipActive]}>
                <Text style={[styles.categoryChipTxt, tab === k && styles.categoryChipTxtActive]}>{l}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <Text style={{ fontSize: 12.5, color: MUTED, lineHeight: 18, marginBottom: 8 }}>
            {tab === 'new'
              ? 'One invoice per client, covering all their horses: new charges plus any earlier unpaid balance. Untick anyone you’re not ready to bill.'
              : 'Clients with invoices still unpaid. Each gets a friendly reminder with a Pay online button.'}
          </Text>
          <ScrollView style={{ maxHeight: 420 }}>
            {list.length === 0 && (
              <Text style={styles.noNotesTxt}>{tab === 'new' ? 'No new charges to invoice.' : 'Nobody has an unpaid invoice.'}</Text>
            )}
            {list.map((c) => {
              const on = isOn(c);
              return (
                <TouchableOpacity key={c.key} disabled={!c.email || busy} activeOpacity={0.8}
                  onPress={() => setPicked((p) => ({ ...p, [c.key]: !on }))}
                  style={{ flexDirection: 'row', gap: 10, alignItems: 'flex-start', backgroundColor: CARD, borderWidth: 0.5, borderColor: BORDER, borderRadius: 10, padding: 10, marginBottom: 8, opacity: c.email ? 1 : 0.85 }}>
                  <View style={{ width: 20, height: 20, borderRadius: 5, marginTop: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: on ? RED : CARD, borderWidth: on ? 0 : 1, borderColor: '#B4B2A9' }}>
                    {on && <Text style={{ color: '#FFF', fontSize: 12, fontWeight: '700' }}>✓</Text>}
                  </View>
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                      <Text style={{ fontSize: 14.5, fontWeight: '700', color: TEXT, flex: 1 }} numberOfLines={1}>{c.key}</Text>
                      <Text style={{ fontSize: 14.5, fontWeight: '700', color: TEXT }}>{fmt(tab === 'new' ? c.total : c.due)}</Text>
                    </View>
                    <Text style={{ fontSize: 12, color: MUTED }} numberOfLines={1}>{c.horseNames.join(', ')}</Text>
                    {tab === 'remind' && (
                      <Text style={{ fontSize: 12, color: MUTED }} numberOfLines={2}>
                        {c.invoices.join(', ')}{c.oldest ? ` · oldest ${daysSince(c.oldest)} day${daysSince(c.oldest) === 1 ? '' : 's'}` : ''}
                      </Text>
                    )}
                    <TouchableOpacity onPress={() => setPreviewKey(c.key)} disabled={busy} activeOpacity={0.7} style={{ alignSelf: 'flex-start', paddingVertical: 2 }}>
                      <Text style={{ fontSize: 12.5, color: RED, fontWeight: '700' }}>Preview</Text>
                    </TouchableOpacity>
                    {!!c.emailProblem && (
                      <Text style={{ fontSize: 12, color: '#791F1F', backgroundColor: '#FCEBEB', borderRadius: 6, paddingHorizontal: 6, paddingVertical: 3, marginTop: 4, alignSelf: 'flex-start' }}>
                        {c.emailProblem.kind === 'complained' ? 'Last email was marked as spam — contact them another way' : 'Last email bounced — check this address in the client’s details'}
                      </Text>
                    )}
                    {c.email
                      ? <Text style={{ fontSize: 12, color: MUTED }} numberOfLines={1}>{c.email}</Text>
                      : <Text style={{ fontSize: 12, color: '#633806', backgroundColor: '#FAEEDA', borderRadius: 6, paddingHorizontal: 6, paddingVertical: 3, marginTop: 4, alignSelf: 'flex-start' }}>No email on file — add it in this client’s details on Client Horses</Text>}
                  </View>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
          {!!progress && <Text style={{ fontSize: 13, color: MUTED, textAlign: 'center', marginTop: 8 }}>{progress}</Text>}
          {(() => {
            const c = previewKey ? list.find((x) => x.key === previewKey) : null;
            if (!c) return null;
            const biz = businessProfile?.name || 'Your business';
            const isNew = tab === 'new';
            const amt = isNew ? c.total : c.due;
            const subject = isNew ? `Invoice INV-#### from ${biz} · ${fmt(amt)}` : `Reminder: ${fmt(amt)} due to ${biz}`;
            const groups = c.parts.filter((p) => (isNew ? (p.current.length || p.previously.length) : p.previously.length));
            const line = (key, left, right, sub) => (
              <View key={key} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 3 }}>
                <View style={{ flex: 1, paddingRight: 8 }}>
                  <Text style={{ fontSize: 13, color: TEXT }}>{left}</Text>
                  {!!sub && <Text style={{ fontSize: 11.5, color: MUTED }}>{sub}</Text>}
                </View>
                <Text style={{ fontSize: 13, color: TEXT }}>{right}</Text>
              </View>
            );
            const openLayout = () => {
              const html = buildInvoiceHtml({
                groups: groups.map((p) => ({ horse: p.horse, previously: p.previously, current: p.current, credit: p.credit })),
                credit: c.credit, business: businessProfile, ownerContact: c.contact, horse: groups[0]?.horse || {},
                invoiceNumber: 'PREVIEW', notes: businessProfile?.terms || '',
              }).replace('<!--HM_PAY_LINK-->', '<div class="paybox"><a class="paybtn" href="#">Pay online &rarr;</a><div class="paytext">Shown when you take online payments.</div></div>');
              const w = window.open('', '_blank');
              if (w) { w.document.write(html); w.document.close(); }
            };
            return (
              <Modal visible transparent animationType="fade" onRequestClose={() => setPreviewKey(null)}>
                <View style={[styles.overlay, { justifyContent: 'center', padding: 16 }]}>
                  <View style={{ backgroundColor: CARD, borderRadius: 14, padding: 16, maxHeight: '88%' }}>
                    <Text style={styles.sheetTitle}>{isNew ? 'Invoice preview' : 'Reminder preview'}</Text>
                    <ScrollView>
                      <Text style={{ fontSize: 12, color: MUTED }}>To: {c.email || 'no email on file'}</Text>
                      <Text style={{ fontSize: 12, color: MUTED }}>From: {biz} via Horse Math</Text>
                      <Text style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>Subject: {subject}</Text>
                      {groups.map((p) => (
                        <View key={p.horse.id} style={{ borderTopWidth: 0.5, borderTopColor: BORDER, paddingTop: 8, marginBottom: 8 }}>
                          <Text style={{ fontSize: 14, fontWeight: '700', color: TEXT, marginBottom: 2 }}>{displayName(p.horse)}</Text>
                          {p.previously.map((ch) => line(ch.id, ch.description, fmt(ch.amount), `${ch.invoiceNumber ? `${ch.invoiceNumber} · ` : ''}${formatDate(ch.date)}`))}
                          {isNew && p.current.map((ch) => line(ch.id, ch.description, fmt(ch.amount), `New · ${formatDate(ch.date)}`))}
                        </View>
                      ))}
                      {c.credit > 0.005 && line('credit', 'Credit on account', `−${fmt(c.credit)}`)}
                      <View style={{ flexDirection: 'row', justifyContent: 'space-between', borderTopWidth: 1, borderTopColor: TEXT, paddingTop: 8, marginTop: 4 }}>
                        <Text style={{ fontSize: 15, fontWeight: '700', color: TEXT }}>{isNew ? 'Balance due' : 'Still owed'}</Text>
                        <Text style={{ fontSize: 15, fontWeight: '700', color: TEXT }}>{fmt(amt)}</Text>
                      </View>
                      <Text style={{ fontSize: 12, color: MUTED, lineHeight: 17, marginTop: 10 }}>
                        {isNew
                          ? 'The email includes the invoice PDF (your logo, details and notes), a View invoice button, and a Pay online button if you take online payments. The invoice number is assigned when it’s sent.'
                          : `The email lists ${c.invoices.join(', ') || 'the unpaid invoices'}, attaches ${c.invoices.length === 1 ? 'that invoice' : 'those invoices'} as PDF${c.invoices.length === 1 ? '' : 's'}, and has a Pay online button if you take online payments.`}
                      </Text>
                    </ScrollView>
                    {isNew && Platform.OS === 'web' && (
                      <TouchableOpacity onPress={openLayout} activeOpacity={0.8} style={{ marginTop: 12, borderWidth: 1.5, borderColor: RED, borderRadius: 10, paddingVertical: 10, alignItems: 'center' }}>
                        <Text style={{ color: RED, fontWeight: '700' }}>Open the invoice layout</Text>
                      </TouchableOpacity>
                    )}
                    <TouchableOpacity onPress={() => setPreviewKey(null)} activeOpacity={0.7} style={{ alignItems: 'center', marginTop: 12 }}>
                      <Text style={{ color: MUTED, fontSize: 15 }}>Close</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </Modal>
            );
          })()}
          <View style={styles.sheetBtns}>
            <TouchableOpacity style={styles.cancelBtn} onPress={onClose} disabled={busy} activeOpacity={0.85}><Text style={styles.cancelBtnText}>Close</Text></TouchableOpacity>
            <TouchableOpacity style={[styles.saveBtn, (!chosen.length || busy) && { opacity: 0.5 }]} onPress={confirmSend} disabled={!chosen.length || busy} activeOpacity={0.85}>
              {busy ? <ActivityIndicator color="#FFF" /> : (
                <Text style={styles.saveBtnText}>
                  {chosen.length ? `Send ${chosen.length} · ${fmt(chosenTotal)}` : 'Send'}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function ClientHorsesScreen({ navigation, route }) {
  const guide = useGuide();
  const [showSendInvoices, setShowSendInvoices] = useState(false);
  const guidePay = guide.stage === 'payments';
  const [horses, setHorses] = useState([]);
  const [soldClientHorses, setSoldClientHorses] = useState([]);
  const [balances, setBalances] = useState({}); // horseId -> balance
  const [ownProfile, setOwnProfile] = useState(null);
  const [company, setCompany] = useState(null);
  const businessProfile = effectiveBusinessProfile(ownProfile, company);
  const [showBusinessModal, setShowBusinessModal] = useState(false);
  // "Get paid online" banner: shown to providers until they set up or dismiss.
  const [payBanner, setPayBanner] = useState(false);
  const payBannerKey = 'paymentsBannerDismissed:' + (auth.currentUser?.uid || 'anon');
  useEffect(() => {
    if (!canSeeOnlinePayments()) return;
    let live = true;
    AsyncStorage.getItem(payBannerKey).then((v) => {
      if (v) return;
      callStripeStatus().then((st) => { if (live && !(st && st.ready)) setPayBanner(true); }).catch(() => {});
    }).catch(() => {});
    return () => { live = false; };
  }, []);
  const dismissPayBanner = () => { setPayBanner(false); AsyncStorage.setItem(payBannerKey, '1').catch(() => {}); };
  // Tapping the "get paid online" push opens Business Info at the setup.
  useEffect(() => {
    if (route?.params?.openPayments) {
      setShowBusinessModal(true);
      navigation.setParams({ openPayments: undefined });
    }
  }, [route?.params?.openPayments]);
  // Bulk charge to multiple horses
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkDesc, setBulkDesc] = useState('');
  const [bulkNote, setBulkNote] = useState('');
  const [bulkAmount, setBulkAmount] = useState('');
  const [bulkDate, setBulkDate] = useState(new Date());
  const [showBulkDatePicker, setShowBulkDatePicker] = useState(false);
  const [bulkRecurring, setBulkRecurring] = useState(false);
  const [bulkRecurDay, setBulkRecurDay] = useState('1');
  const [bulkSelected, setBulkSelected] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);
  // Unique per-horse notes/costs on a bulk charge.
  const [bulkUnique, setBulkUnique] = useState(false);
  const [bulkPer, setBulkPer] = useState({}); // horseId → { amount, note }
  // A task (an appointment) for several client horses at once.
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskName, setTaskName] = useState('');
  const [taskNotes, setTaskNotes] = useState('');
  const [taskDate, setTaskDate] = useState(new Date());
  const [showTaskDatePicker, setShowTaskDatePicker] = useState(false);
  const [taskRemindDue, setTaskRemindDue] = useState(true);
  const [taskRemindBefore, setTaskRemindBefore] = useState(true);
  const [taskRemindOwner, setTaskRemindOwner] = useState(true); // the owner gets the same reminders
  const [taskSelected, setTaskSelected] = useState({});
  const [taskSaving, setTaskSaving] = useState(false);
  const [taskInWeeks, setTaskInWeeks] = useState('');     // this visit: N weeks from today…
  const [taskInMonths, setTaskInMonths] = useState('');   // …or N months
  const [taskNext, setTaskNext] = useState(false);       // also book the next one
  const [taskNextWeeks, setTaskNextWeeks] = useState('6'); // N weeks after this visit…
  const [taskNextMonths, setTaskNextMonths] = useState(''); // …or N months
  const [taskNextDate, setTaskNextDate] = useState(null); // an explicit date instead
  const [showTaskNextDatePicker, setShowTaskNextDatePicker] = useState(false);
  // Providers think in clients, not horses — so the list arrives grouped by owner.
  const [groupByOwner, setGroupByOwner] = useState(true);
  // The client detail sheet, addressed by name so it follows a live rename.
  const [clientSheetKey, setClientSheetKey] = useState(null);
  const [editingClient, setEditingClient] = useState(false);
  const [ecName, setEcName] = useState('');
  const [ecEmail, setEcEmail] = useState('');
  const [ecPhone, setEcPhone] = useState('');
  const [ecSaving, setEcSaving] = useState(false);

  const openBulk = () => {
    setBulkDesc(''); setBulkNote(''); setBulkAmount(''); setBulkDate(new Date()); setShowBulkDatePicker(false);
    setBulkRecurring(false); setBulkRecurDay('1'); setBulkSelected({}); setBulkUnique(false); setBulkPer({}); setShowBulkModal(true);
  };

  const openTask = () => {
    setTaskName(''); setTaskNotes(''); setTaskDate(new Date()); setShowTaskDatePicker(false);
    setTaskRemindDue(true); setTaskRemindBefore(true); setTaskRemindOwner(true); setTaskSelected({});
    setTaskInWeeks(''); setTaskInMonths('');
    setTaskNext(false); setTaskNextWeeks('6'); setTaskNextMonths(''); setTaskNextDate(null); setShowTaskNextDatePicker(false);
    setShowTaskModal(true);
  };

  const addWeeks = (d, w) => { const x = new Date(d); x.setDate(x.getDate() + w * 7); return x; };
  // The next visit, from whichever number box has a value (weeks wins if both).
  const taskNextOffsetDate = () => {
    const w = parseInt(taskNextWeeks, 10), m = parseInt(taskNextMonths, 10);
    if (w > 0) return addWeeks(taskDate, w);
    if (m > 0) return addMonthsClamped(taskDate, m);
    return null;
  };
  // This visit, counted from today.
  const setTaskOffset = (weeks, months) => {
    setTaskInWeeks(weeks); setTaskInMonths(months);
    const w = parseInt(weeks, 10), m = parseInt(months, 10);
    if (w > 0) setTaskDate(addWeeks(new Date(), w));
    else if (m > 0) setTaskDate(addMonthsClamped(new Date(), m));
  };
  const ymd = (d) => { const x = new Date(d); x.setHours(12, 0, 0, 0); return x.toISOString().split('T')[0]; };

  // One task on each chosen horse, and optionally the next one as well —
  // `weeks` out from this date, or an exact date. Each task is an ordinary
  // custom task on the horse: it shows on the horse, on Milestones & Tasks,
  // and can be reminded, moved or completed like any other.
  const handleBulkTask = async () => {
    const name = taskName.trim();
    if (!name) { Alert.alert('Task name required', 'Give the appointment a name, like "Shoeing".'); return; }
    const chosen = horses.filter((h) => taskSelected[h.id]);
    if (chosen.length === 0) { Alert.alert('No horses selected', 'Pick at least one horse.'); return; }
    const dateStr = ymd(taskDate);
    const nextAuto = taskNextOffsetDate();
    if (taskNext && !taskNextDate && !nextAuto) { Alert.alert('When is the next one?', 'Enter a number of weeks or months, or pick a date.'); return; }
    const nextStr = taskNext ? ymd(taskNextDate || nextAuto) : null;
    if (nextStr && nextStr <= dateStr) { Alert.alert('Check the next date', 'The next appointment has to be after this one.'); return; }
    const notes = taskNotes.trim();
    const me = auth.currentUser?.uid || '';
    const myName = businessProfile?.name || auth.currentUser?.displayName || auth.currentUser?.email || 'Me';
    setTaskSaving(true);
    let failed = 0;
    for (const h of chosen) {
      const stamp = Date.now();
      const mk = (date, suffix) => ({
        id: `m_custom_${stamp}_${suffix}_${h.id}`,
        name,
        date,
        completed: false,
        custom: true,
        category: 'Farrier',
        isAppointment: true,
        ...(notes && { notes }),
        ...(taskRemindDue && { remindOnDue: true }),
        ...(taskRemindBefore && { remindDayBefore: true }),
        ...(taskRemindOwner && (taskRemindDue || taskRemindBefore) && { notifyOwnerToo: true }),
        assignedToUid: me,
        assignedToName: myName,
        assignedBy: me,
        assignedByName: myName,
        createdByUid: me,
      });
      const add = [mk(dateStr, 'a')];
      if (nextStr) add.push(mk(nextStr, 'b'));
      const milestones = [...(h.milestones || []), ...add]
        .sort((a, b) => getMilestoneDate(h, a).localeCompare(getMilestoneDate(h, b)));
      try { await updateHorse(h.id, { milestones }); } catch (e) { failed += 1; }
    }
    setTaskSaving(false);
    if (failed > 0) {
      Alert.alert('Partly saved', `${chosen.length - failed} of ${chosen.length} horses got the task. Try again for the rest.`);
    } else {
      setShowTaskModal(false);
      Alert.alert('Appointment scheduled', `${name} on ${formatDate(dateStr)} for ${chosen.length} horse${chosen.length === 1 ? '' : 's'}${nextStr ? `, and again on ${formatDate(nextStr)}` : ''}.`);
    }
  };

  const handleBulkCharge = async () => {
    const amt = parseAmount(bulkAmount);
    if (!bulkDesc.trim() || (!bulkUnique && (isNaN(amt) || amt <= 0))) { Alert.alert('Missing info', bulkUnique ? 'Enter a description.' : 'Enter a description and a valid amount.'); return; }
    const chosen = horses.filter((h) => bulkSelected[h.id]);
    if (chosen.length === 0) { Alert.alert('No horses selected', 'Pick at least one horse to charge.'); return; }
    // With unique notes/costs on, every chosen horse needs its own amount.
    if (bulkUnique) {
      const missing = chosen.filter((h) => { const a = parseAmount(bulkPer[h.id]?.amount ?? ''); return isNaN(a) || a <= 0; });
      if (missing.length > 0) { Alert.alert('Amount missing', `Enter an amount for ${missing.map(displayName).join(', ')}.`); return; }
    }
    const uid = auth.currentUser?.uid;
    const myProvider = { name: businessProfile?.name || auth.currentUser?.displayName || auth.currentUser?.email || 'Provider', type: businessProfile?.providerType || 'Trainer' };
    setBulkSaving(true);
    try {
      const dateStr = localYmd(bulkDate);
      const day = Math.min(28, Math.max(1, parseInt(bulkRecurDay, 10) || 1));
      for (const h of chosen) {
        // Unique mode: this horse's own amount and note; otherwise the shared ones.
        const per = bulkUnique ? (bulkPer[h.id] || {}) : null;
        const thisAmt = per ? parseAmount(per.amount ?? '') : amt;
        const thisNote = per ? (per.note ?? '') : bulkNote;
        await addCharge(h.id, uid, h.ownerId, bulkDesc.trim(), thisAmt, dateStr, myProvider, thisNote);
        if (bulkRecurring) {
          const template = { id: `rc_${Date.now()}_${h.id.slice(-4)}`, description: bulkDesc.trim(), amount: thisAmt, lastGenerated: monthKey(new Date()), dayOfMonth: day, trainerUid: uid, providerName: myProvider.name, providerType: myProvider.type };
          await updateHorse(h.id, { recurringCharges: [...(h.recurringCharges || []), template] });
        }
      }
      setShowBulkModal(false);
      const total = bulkUnique ? chosen.reduce((s, h) => s + parseAmount(bulkPer[h.id]?.amount ?? ''), 0) : amt * chosen.length;
      Alert.alert('Charges added', `${chosen.length} charge${chosen.length === 1 ? '' : 's'}, ${'$' + total.toFixed(2)} in total${bulkRecurring ? ', repeating monthly' : ''}.`);
    } catch {
      Alert.alert('Error', 'Some charges may not have been added. Please check the ledgers.');
    } finally {
      setBulkSaving(false);
    }
  };

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    // A sold horse leaves the client list, but money still owed on it must not
    // vanish from the provider's books — it is kept apart and shown while owed.
    return subscribeToHorses(uid, (all) => {
      const mine = all.filter((h) => isHorseTrainer(h, uid));
      setHorses(mine.filter((h) => !h.sold));
      setSoldClientHorses(mine.filter((h) => h.sold));
    }, { includeSold: true });
  }, []);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    return subscribeToUserSettings(uid, (s) => setOwnProfile(s.businessProfile || null));
  }, []);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    // Catch the company's invoice sequence up to this member's own, so joining
    // can never cause a number to be issued twice.
    return subscribeToMyCompany(uid, (c) => {
      setCompany(c);
      if (c?.id) reconcileInvoiceCounter(uid, c.id);
    });
  }, []);

  // Every client horse's balance, in ONE chunked subscription rather than a
  // listener per horse. A farrier or boarding barn can easily have a hundred
  // client horses; a hundred concurrent Firestore listeners is not something
  // that scales, and each one re-rendered the whole screen on every change.
  // Ten horses per query is the Firestore limit, so a hundred horses is ten
  // listeners. Balances are rebuilt whole each time, so a horse that leaves the
  // list cannot leave a stale balance behind in the totals.
  useEffect(() => {
    const ids = [...horses, ...soldClientHorses].map((h) => h.id).filter(Boolean);
    const myCompany = normalizeCompany(businessProfile?.name) || ('u:' + auth.currentUser?.uid);
    return subscribeToLedgerForHorses(ids, (entries) => {
      const totals = {};
      ids.forEach((id) => { totals[id] = 0; });
      entries.forEach((e) => {
        // A provider's Client Horses balance is what's owed to their company.
        if (e.deleted || entryCompany(e) !== myCompany) return;
        if (!(e.horseId in totals)) return;
        if (e.type === 'charge') totals[e.horseId] += (e.amount || 0);
        else if (e.type === 'payment') totals[e.horseId] -= (e.amount || 0);
      });
      setBalances(totals);
    });
  }, [[...horses, ...soldClientHorses].map((h) => h.id).join(','), businessProfile?.name]);

  const getBalance = (horseId) => balances[horseId] || 0;
  const soldOwing = soldClientHorses.filter((h) => getBalance(h.id) > 0);

  // A client's horses can carry different amounts of contact detail — only one
  // of them may have the phone number. Take the fullest record, so opening the
  // sheet from a sparse horse neither shows blanks nor, on save, wipes details
  // the other horses already had.
  const clientContact = (rows) => {
    const filled = (x) => (x?.name ? 1 : 0) + (x?.email ? 1 : 0) + (x?.phone ? 1 : 0);
    return rows
      .map((h) => getOwnerContact(h) || {})
      .reduce((best, cur) => (filled(cur) > filled(best) ? cur : best), {});
  };

  const openClient = (g) => {
    const c = clientContact(g.rows);
    setEcName(c.name || g.key);
    setEcEmail(c.email || '');
    setEcPhone(c.phone || '');
    setEditingClient(false);
    setClientSheetKey(g.key);
  };

  // A client's details live on each of their horses — there is no separate
  // client record — so editing one writes the same contact to every horse of
  // theirs. That is also what keeps them grouped as one client: the name is the
  // grouping key, so a rename has to reach all of them or the client splits in
  // two. Their trainer/owner account fields are left alone; only the contact
  // card changes.
  const saveClient = async (group) => {
    const name = ecName.trim();
    if (!name) { Alert.alert('Name required', "Enter the client's name."); return; }
    const contact = { name, email: ecEmail.trim(), phone: ecPhone.trim() };
    setEcSaving(true);
    const results = await Promise.allSettled(
      group.rows.map((h) => updateHorse(h.id, { ownerContact: contact }))
    );
    setEcSaving(false);
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      Alert.alert(
        'Partly saved',
        `Updated ${results.length - failed} of ${results.length} horses. ${failed} could not be changed — you may not have edit access to ${failed === 1 ? 'it' : 'them'}. Try again, or update ${failed === 1 ? 'that horse' : 'those horses'} individually.`
      );
      return;
    }
    setEditingClient(false);
    setClientSheetKey(name); // follow the rename
  };

  const totalOutstanding = [...horses, ...soldOwing].reduce((s, h) => s + Math.max(0, getBalance(h.id)), 0);
  const fmt = (n) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // One entry per client, in the order their horses were loaded, with what that
  // client owes across all of them.
  const ownerNameOf = (h) => getOwnerContact(h)?.name || h.ownerName || 'Owner not set';
  const ownerGroups = (() => {
    const out = [];
    horses.forEach((h) => {
      const key = ownerNameOf(h);
      const g = out.find((x) => x.key === key);
      if (g) g.rows.push(h); else out.push({ key, rows: [h] });
    });
    out.forEach((g) => { g.owed = g.rows.reduce((s, h) => s + Math.max(0, getBalance(h.id)), 0); });
    return out.sort((a, b) => a.key.localeCompare(b.key));
  })();

  return (
    <SafeAreaView style={styles.screen} edges={['bottom']}>
      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
        <View style={{ flexDirection: 'row', gap: 10, marginBottom: 16 }}>
          <View style={[styles.earningsTotalCell, { flex: 1, backgroundColor: CARD, borderRadius: 10, borderWidth: 0.5, borderColor: BORDER, padding: 14 }]}>
            <Text style={styles.earningsTotalLabel}>Client Horses</Text>
            <Text style={[styles.earningsTotalValue, { color: TEXT }]}>{horses.length}</Text>
          </View>
          <View style={[styles.earningsTotalCell, { flex: 1, backgroundColor: CARD, borderRadius: 10, borderWidth: 0.5, borderColor: BORDER, padding: 14 }]}>
            <Text style={styles.earningsTotalLabel}>Total Outstanding</Text>
            <Text style={[styles.earningsTotalValue, { color: RED }]}>{fmt(totalOutstanding)}</Text>
          </View>
        </View>

        {payBanner && !guidePay && horses.length > 0 && (
          <View style={{ backgroundColor: '#F3ECE2', borderRadius: 10, borderWidth: 0.5, borderColor: '#D9C7AE', padding: 12, marginBottom: 16 }}>
            <Text style={{ fontSize: 14, fontWeight: '700', color: TEXT }}>💳 Get paid online</Text>
            <Text style={{ fontSize: 12.5, color: MUTED, marginTop: 3, lineHeight: 18 }}>Let clients pay your invoices by card or bank transfer, straight to your bank. Set up takes about 10 minutes.</Text>
            <View style={{ flexDirection: 'row', gap: 16, marginTop: 8, alignItems: 'center' }}>
              <TouchableOpacity onPress={() => { setPayBanner(false); setShowBusinessModal(true); }} activeOpacity={0.8} style={{ backgroundColor: RED, borderRadius: 8, paddingVertical: 7, paddingHorizontal: 14 }}>
                <Text style={{ color: '#FFF', fontWeight: '700', fontSize: 13 }}>Set up</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={dismissPayBanner} activeOpacity={0.7}>
                <Text style={{ color: MUTED, fontWeight: '600', fontSize: 13 }}>Not now</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        <TouchableOpacity
          style={[{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: CARD, borderRadius: 10, borderWidth: 0.5, borderColor: BORDER, paddingVertical: 12, marginBottom: 16 }, guidePay && { ...GUIDE_HIGHLIGHT, marginBottom: 4 }]}
          onPress={() => { if (guidePay) setGuideStage('done'); setShowBusinessModal(true); }}
          activeOpacity={0.75}
        >
          <Text style={{ fontSize: 15 }}>🧾</Text>
          <Text style={{ fontSize: 14, fontWeight: '600', color: TEXT }}>
            {businessProfile?.name ? `Business Info & Payments: ${businessProfile.name}` : 'Set Up Business Info & Payments'}
          </Text>
        </TouchableOpacity>

        {guidePay && (
          <GuideTip arrow="up" text="Add your business name and logo for invoices, and turn on online payments so clients can pay you by card or bank.">
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 18, marginTop: 8 }}>
              <TouchableOpacity onPress={() => { setGuideStage('done'); setShowBusinessModal(true); }} activeOpacity={0.7}>
                <Text style={{ color: GOLD, fontWeight: '700', fontSize: 13.5 }}>Set up now</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setGuideStage('done')} activeOpacity={0.7}>
                <Text style={{ color: CREAM, fontSize: 13.5 }}>Later</Text>
              </TouchableOpacity>
            </View>
          </GuideTip>
        )}

        {/* Adding a client horse belongs here as much as in the herd — this is
            where a provider is already thinking about their clients. */}
        <TouchableOpacity
          style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: CARD, borderRadius: 10, borderWidth: 0.5, borderColor: BORDER, paddingVertical: 12, marginBottom: 16 }}
          onPress={() => navigation.navigate('Herd', { openAdd: true, clientHorse: true })}
          activeOpacity={0.75}
        >
          <Text style={{ fontSize: 15 }}>🎓</Text>
          <Text style={{ fontSize: 14, fontWeight: '600', color: TEXT }}>Add Client Horse</Text>
        </TouchableOpacity>

        {INVOICE_EMAIL_ENABLED && horses.length > 0 && !!businessProfile?.name && (
          <TouchableOpacity
            style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: RED, borderRadius: 10, paddingVertical: 12, marginBottom: 16 }}
            onPress={() => setShowSendInvoices(true)}
            activeOpacity={0.85}
          >
            <Text style={{ fontSize: 15 }}>📨</Text>
            <Text style={{ fontSize: 14, fontWeight: '700', color: CREAM }}>Send invoices & reminders</Text>
          </TouchableOpacity>
        )}
        <SendInvoicesModal
          visible={showSendInvoices}
          onClose={() => setShowSendInvoices(false)}
          ownerGroups={ownerGroups}
          businessProfile={businessProfile}
          company={company}
        />

        {horses.length > 0 && (
          <TouchableOpacity
            style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: CARD, borderRadius: 10, borderWidth: 0.5, borderColor: BORDER, paddingVertical: 12, marginBottom: 16 }}
            onPress={openBulk}
            activeOpacity={0.75}
          >
            <Text style={{ fontSize: 15 }}>➕</Text>
            <Text style={{ fontSize: 14, fontWeight: '600', color: TEXT }}>Add Charge to Multiple Horses</Text>
          </TouchableOpacity>
        )}

        {horses.length > 0 && (
          <TouchableOpacity
            style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: CARD, borderRadius: 10, borderWidth: 0.5, borderColor: BORDER, paddingVertical: 12, marginBottom: 16 }}
            onPress={openTask}
            activeOpacity={0.75}
          >
            <Text style={{ fontSize: 15 }}>📅</Text>
            <Text style={{ fontSize: 14, fontWeight: '600', color: TEXT }}>Schedule Appointment</Text>
          </TouchableOpacity>
        )}

        {horses.length > 0 && (
          <TouchableOpacity
            style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: CARD, borderRadius: 10, borderWidth: 0.5, borderColor: BORDER, paddingVertical: 12, marginBottom: 16 }}
            onPress={() => navigation.navigate('Milestones', { calScope: 'today', clientOnly: true })}
            activeOpacity={0.75}
          >
            <Text style={{ fontSize: 15 }}>☀️</Text>
            <Text style={{ fontSize: 14, fontWeight: '600', color: TEXT }}>Today's Day Sheet</Text>
          </TouchableOpacity>
        )}

        {horses.length === 0 && <Text style={styles.noNotesTxt}>No client horses yet. Ask an owner to share a horse with you using the Trainer permission.</Text>}

        {horses.length > 1 && (
          <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 8 }}>
            <TouchableOpacity onPress={() => setGroupByOwner((v) => !v)} activeOpacity={0.7} style={{ paddingVertical: 4, paddingHorizontal: 2 }}>
              <Text style={{ fontSize: 12, color: GREEN, fontWeight: '600' }}>
                {groupByOwner ? 'Show as one list' : 'Group by owner'}
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {(() => {
          const horseCard = (h, showOwner) => {
            const balance = getBalance(h.id);
            const next = nextAppointment(h);
            const sub = [
              h.stall ? `Stall ${h.stall}` : null,
              showOwner ? getOwnerContact(h)?.name : null,
              next ? `Next ${formatDate(next.date)}` : null,
            ].filter(Boolean).join('  ·  ');
            return (
              <TouchableOpacity
                key={h.id}
                style={styles.denseRow}
                onPress={() => navigation.navigate('Horse', { horseId: h.id })}
                activeOpacity={0.7}
              >
                <View style={{ flex: 1 }}>
                  <Text style={styles.denseName} numberOfLines={1}>{displayName(h)}</Text>
                  {!!sub && <Text style={styles.denseSub} numberOfLines={1}>{sub}</Text>}
                </View>
                <Text style={[styles.denseRight, { color: balance > 0 ? RED : GREEN }]} numberOfLines={1}>
                  {balance > 0 ? fmt(balance) : 'Paid up'}
                </Text>
                <Text style={styles.denseChevron}>›</Text>
              </TouchableOpacity>
            );
          };
          // Grouped, the owner's name is the heading — repeating it on every card
          // underneath would just be noise.
          if (!groupByOwner) return horses.map((h) => horseCard(h, true));
          return ownerGroups.map((g) => (
            <View key={g.key} style={{ marginBottom: 4 }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, marginBottom: 6 }}>
                <TouchableOpacity
                  style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}
                  onPress={() => openClient(g)}
                  activeOpacity={0.7}
                >
                  <Text style={{ fontSize: 13, fontWeight: '700', color: TEXT }} numberOfLines={1}>{g.key}</Text>
                  <Text style={{ fontSize: 12, color: MUTED, marginLeft: 5 }}>ⓘ</Text>
                </TouchableOpacity>
                <Text style={{ fontSize: 12, color: g.owed > 0.005 ? RED : MUTED, fontWeight: '600' }}>
                  {g.rows.length} horse{g.rows.length === 1 ? '' : 's'}{g.owed > 0.005 ? ` · ${fmt(g.owed)} owed` : ''}
                </Text>
                {/* Bills this client for all of their horses at once. It opens the
                    invoice sheet on their first horse with the rest already ticked,
                    so there is one invoice flow rather than two to keep in step. */}
                {g.owed > 0.005 && canUseMultiHorseInvoice() && (
                  <TouchableOpacity
                    onPress={() => navigation.navigate('Horse', { horseId: g.rows[0].id, invoiceOwner: g.key })}
                    activeOpacity={0.75}
                    style={{ marginLeft: 10, backgroundColor: CARD, borderWidth: 0.5, borderColor: BORDER, borderRadius: 6, paddingHorizontal: 9, paddingVertical: 4 }}
                  >
                    <Text style={{ fontSize: 12, fontWeight: '700', color: GREEN }}>Invoice</Text>
                  </TouchableOpacity>
                )}
              </View>
              {g.rows.map((h) => horseCard(h, false))}
            </View>
          ));
        })()}

        {soldOwing.length > 0 && (
          <View style={{ marginTop: 18 }}>
            <Text style={{ fontSize: 14, fontWeight: '700', color: TEXT, marginBottom: 2 }}>🏷️ Sold — balance still owed</Text>
            <Text style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>These horses were sold but still have unpaid charges. They drop off this list once paid.</Text>
            {soldOwing.map((h) => (
              <TouchableOpacity
                key={h.id}
                style={styles.denseRow}
                onPress={() => navigation.navigate('Horse', { horseId: h.id })}
                activeOpacity={0.7}
              >
                <View style={{ flex: 1 }}>
                  <Text style={styles.denseName} numberOfLines={1}>{displayName(h)}</Text>
                  <Text style={styles.denseSub} numberOfLines={1}>{[ownerNameOf(h), `Sold ${formatDate(h.soldDate)}`].join('  ·  ')}</Text>
                </View>
                <Text style={[styles.denseRight, { color: RED }]} numberOfLines={1}>{fmt(getBalance(h.id))}</Text>
                <Text style={styles.denseChevron}>›</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </ScrollView>
      {/* Client details — view, edit, and see everything of theirs in one place */}
      <Modal visible={!!clientSheetKey} animationType="slide" transparent onRequestClose={() => setClientSheetKey(null)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '88%' }]}>
            {(() => {
              const g = ownerGroups.find((o) => o.key === clientSheetKey);
              if (!g) return <Text style={styles.pickerEmpty}>This client no longer has any horses with you.</Text>;
              const c = clientContact(g.rows);
              return (
                <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
                  <Text style={styles.sheetTitle}>{g.key}</Text>

                  {!editingClient ? (
                    <>
                      {!!c.email && (
                        <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }} onPress={() => Linking.openURL(`mailto:${c.email}`)} activeOpacity={0.7}>
                          <Text style={{ fontSize: 15, marginRight: 8 }}>✉️</Text>
                          <Text style={[styles.noteText, { flex: 1, color: GREEN }]}>{c.email}</Text>
                        </TouchableOpacity>
                      )}
                      {!!c.phone && (
                        <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }} onPress={() => Linking.openURL(`tel:${c.phone}`)} activeOpacity={0.7}>
                          <Text style={{ fontSize: 15, marginRight: 8 }}>📞</Text>
                          <Text style={[styles.noteText, { flex: 1, color: GREEN }]}>{c.phone}</Text>
                        </TouchableOpacity>
                      )}
                      {!c.email && !c.phone && (
                        <Text style={{ fontSize: 13, color: MUTED, marginBottom: 8 }}>No contact details saved yet.</Text>
                      )}
                      <TouchableOpacity onPress={() => setEditingClient(true)} activeOpacity={0.7} style={{ paddingVertical: 6 }}>
                        <Text style={{ fontSize: 13, color: GREEN, fontWeight: '700' }}>Edit client details</Text>
                      </TouchableOpacity>
                    </>
                  ) : (
                    <>
                      <Text style={styles.fieldLabel}>Client Name</Text>
                      <TextInput style={styles.input} placeholder="Client name" placeholderTextColor="#BDBDBD" value={ecName} onChangeText={setEcName} />
                      <Text style={styles.fieldLabel}>Email</Text>
                      <TextInput style={styles.input} placeholder="owner@email.com" placeholderTextColor="#BDBDBD" keyboardType="email-address" autoCapitalize="none" value={ecEmail} onChangeText={setEcEmail} />
                      <Text style={styles.fieldLabel}>Phone</Text>
                      <TextInput style={styles.input} placeholder="(555) 555-5555" placeholderTextColor="#BDBDBD" keyboardType="phone-pad" value={ecPhone} onChangeText={setEcPhone} />
                      <Text style={{ fontSize: 11, color: MUTED, marginTop: 2, marginBottom: 8 }}>
                        Saves to all {g.rows.length} of their horse{g.rows.length === 1 ? '' : 's'} — that is what keeps them together as one client.
                      </Text>
                      <View style={{ flexDirection: 'row', gap: 10, marginBottom: 4 }}>
                        <TouchableOpacity style={[styles.cancelBtn, { flex: 1 }]} onPress={() => setEditingClient(false)} activeOpacity={0.85}>
                          <Text style={styles.cancelBtnText}>Cancel</Text>
                        </TouchableOpacity>
                        <TouchableOpacity style={[styles.saveBtn, { flex: 1 }]} onPress={() => saveClient(g)} disabled={ecSaving} activeOpacity={0.85}>
                          {ecSaving ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>Save</Text>}
                        </TouchableOpacity>
                      </View>
                    </>
                  )}

                  <View style={styles.divider} />
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <Text style={{ fontSize: 13, fontWeight: '700', color: TEXT }}>
                      {g.rows.length} horse{g.rows.length === 1 ? '' : 's'}
                    </Text>
                    <Text style={{ fontSize: 13, fontWeight: '700', color: g.owed > 0.005 ? RED : GREEN }}>
                      {g.owed > 0.005 ? fmt(g.owed) + ' owed' : 'Paid up'}
                    </Text>
                  </View>
                  {g.rows.map((h) => {
                    const balance = getBalance(h.id);
                    return (
                      <TouchableOpacity
                        key={h.id}
                        style={styles.pickerRow}
                        onPress={() => { setClientSheetKey(null); navigation.navigate('Horse', { horseId: h.id }); }}
                        activeOpacity={0.75}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                          <Text style={[styles.pickerRowName, { flex: 1 }]}>{displayName(h)}</Text>
                          <Text style={{ fontSize: 14, fontWeight: '700', color: balance > 0 ? RED : GREEN }}>
                            {balance > 0 ? fmt(balance) + ' owed' : 'Paid up'}
                          </Text>
                        </View>
                      </TouchableOpacity>
                    );
                  })}

                  {g.owed > 0.005 && canUseMultiHorseInvoice() && (
                    <TouchableOpacity
                      style={[styles.saveBtn, { marginTop: 14 }]}
                      onPress={() => { setClientSheetKey(null); navigation.navigate('Horse', { horseId: g.rows[0].id, invoiceOwner: g.key }); }}
                      activeOpacity={0.85}
                    >
                      <Text style={styles.saveBtnText}>📄  Invoice {g.key}</Text>
                    </TouchableOpacity>
                  )}

                  <TouchableOpacity style={[styles.cancelBtn, { marginTop: 10, marginBottom: 4 }]} onPress={() => setClientSheetKey(null)} activeOpacity={0.85}>
                    <Text style={styles.cancelBtnText}>Close</Text>
                  </TouchableOpacity>
                </ScrollView>
              );
            })()}
          </View>
        </KeyboardAvoidingView>
      </Modal>

      <BusinessProfileModal
        visible={showBusinessModal}
        onClose={() => setShowBusinessModal(false)}
        uid={auth.currentUser?.uid}
        initialProfile={businessProfile}
        company={company}
        onSaved={setOwnProfile}
      />

      {/* Schedule a task (an appointment) on several client horses */}
      <Modal visible={showTaskModal} animationType="slide" transparent onRequestClose={() => setShowTaskModal(false)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '92%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
              <Text style={styles.sheetTitle}>Schedule Appointment</Text>
              <Text style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>Books one visit across any number of your clients' horses. It shows on each horse and on your calendar.</Text>

              <Text style={styles.fieldLabel}>What</Text>
              <TextInput style={styles.input} placeholder="e.g. Shoeing, Trim, Teeth float" placeholderTextColor="#BDBDBD" value={taskName} onChangeText={setTaskName} autoFocus />

              <Text style={styles.fieldLabel}>When</Text>
              <TouchableOpacity style={styles.datePicker} onPress={() => setShowTaskDatePicker(true)} activeOpacity={0.75}>
                <Text style={styles.datePickerText}>{taskDate.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })}</Text>
                <Text>📅</Text>
              </TouchableOpacity>
              {showTaskDatePicker && (
                <DateTimePicker value={taskDate} mode="date" display={Platform.OS === 'ios' ? 'spinner' : 'default'} onChange={(e, d) => { if (Platform.OS === 'android') setShowTaskDatePicker(false); if (d) { setTaskDate(d); setTaskInWeeks(''); setTaskInMonths(''); } }} />
              )}
              <View style={{ flexDirection: 'row', gap: 8, marginTop: 6 }}>
                {[['Today', 0], ['Tomorrow', 1]].map(([label, days]) => (
                  <TouchableOpacity key={label} style={styles.categoryChip} onPress={() => { const d = new Date(); d.setDate(d.getDate() + days); setTaskDate(d); setTaskInWeeks(''); setTaskInMonths(''); }} activeOpacity={0.75}>
                    <Text style={styles.categoryChipTxt}>{label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              <OffsetInputs
                caption="Or from today, in…"
                weeks={taskInWeeks} months={taskInMonths}
                onWeeks={(v) => setTaskOffset(v, '')}
                onMonths={(v) => setTaskOffset('', v)}
              />

              <Text style={[styles.fieldLabel, { marginTop: 10 }]}>Notes (optional)</Text>
              <TextInput style={[styles.input, styles.inputMultiline]} placeholder="Anything to remember for this visit…" placeholderTextColor="#BDBDBD" multiline value={taskNotes} onChangeText={setTaskNotes} />

              <TouchableOpacity onPress={() => setTaskRemindBefore((v) => !v)} activeOpacity={0.75} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 6 }}>
                <Text style={{ fontSize: 18 }}>{taskRemindBefore ? '☑️' : '⬜️'}</Text>
                <Text style={{ flex: 1, fontSize: 14, color: TEXT }}>Remind me the day before</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setTaskRemindDue((v) => !v)} activeOpacity={0.75} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 6 }}>
                <Text style={{ fontSize: 18 }}>{taskRemindDue ? '☑️' : '⬜️'}</Text>
                <Text style={{ flex: 1, fontSize: 14, color: TEXT }}>Remind me on the day</Text>
              </TouchableOpacity>
              {(taskRemindDue || taskRemindBefore) && (
                <TouchableOpacity onPress={() => setTaskRemindOwner((v) => !v)} activeOpacity={0.75} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, paddingVertical: 6 }}>
                  <Text style={{ fontSize: 18 }}>{taskRemindOwner ? '☑️' : '⬜️'}</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 14, color: TEXT }}>Also remind the owner</Text>
                    <Text style={{ fontSize: 12, color: MUTED }}>Owners who share their horse with you get the same reminders, in their own morning, so they're ready too.</Text>
                  </View>
                </TouchableOpacity>
              )}

              <View style={[styles.divider, { marginVertical: 10 }]} />
              <TouchableOpacity onPress={() => setTaskNext((v) => !v)} activeOpacity={0.75} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, paddingVertical: 6 }}>
                <Text style={{ fontSize: 18 }}>{taskNext ? '☑️' : '⬜️'}</Text>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, fontWeight: '600', color: TEXT }}>Also book the next one</Text>
                  <Text style={{ fontSize: 12, color: MUTED }}>Creates a second task on the same horses, so the next visit is already on the calendar.</Text>
                </View>
              </TouchableOpacity>
              {taskNext && (
                <View style={{ paddingLeft: 28 }}>
                  <OffsetInputs
                    caption="After this visit, in…"
                    weeks={taskNextWeeks} months={taskNextMonths}
                    onWeeks={(v) => { setTaskNextWeeks(v); setTaskNextMonths(''); setTaskNextDate(null); }}
                    onMonths={(v) => { setTaskNextMonths(v); setTaskNextWeeks(''); setTaskNextDate(null); }}
                  />
                  <TouchableOpacity style={[styles.datePicker, { marginTop: 10 }]} onPress={() => setShowTaskNextDatePicker(true)} activeOpacity={0.75}>
                    <Text style={styles.datePickerText}>
                      {taskNextDate
                        ? taskNextDate.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })
                        : taskNextOffsetDate()
                          ? `${taskNextOffsetDate().toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })} · or pick a date`
                          : 'Or pick a date'}
                    </Text>
                    <Text>📅</Text>
                  </TouchableOpacity>
                  {showTaskNextDatePicker && (
                    <DateTimePicker value={taskNextDate || taskNextOffsetDate() || taskDate} mode="date" display={Platform.OS === 'ios' ? 'spinner' : 'default'} onChange={(e, d) => { if (Platform.OS === 'android') setShowTaskNextDatePicker(false); if (d) { setTaskNextDate(d); setTaskNextWeeks(''); setTaskNextMonths(''); } }} />
                  )}
                </View>
              )}

              <OwnerGroupedPicker horses={horses} selected={taskSelected} onChange={setTaskSelected} ownerNameOf={ownerNameOf} />

              <View style={styles.sheetBtns}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowTaskModal(false)} activeOpacity={0.85}><Text style={styles.cancelBtnText}>Cancel</Text></TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={handleBulkTask} disabled={taskSaving} activeOpacity={0.85}>
                  {taskSaving ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>Schedule</Text>}
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* Bulk charge to multiple client horses */}
      <Modal visible={showBulkModal} animationType="slide" transparent onRequestClose={() => setShowBulkModal(false)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '90%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={styles.sheetTitle}>Add Charge to Multiple Horses</Text>

              <Text style={styles.fieldLabel}>Description</Text>
              <TextInput style={styles.input} placeholder="e.g. Monthly board, Farrier" placeholderTextColor="#BDBDBD" value={bulkDesc} onChangeText={setBulkDesc} />

              <TouchableOpacity onPress={() => setBulkUnique((v) => !v)} activeOpacity={0.75} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, paddingVertical: 8, marginTop: 6 }}>
                <Text style={{ fontSize: 18 }}>{bulkUnique ? '☑️' : '⬜️'}</Text>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, fontWeight: '600', color: TEXT }}>Unique notes and costs per horse</Text>
                  <Text style={{ fontSize: 12, color: MUTED }}>Off = the same note and amount on every horse. On = each chosen horse gets its own amount and note below.</Text>
                </View>
              </TouchableOpacity>

              {!bulkUnique && (
                <>
                  <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Note (optional)</Text>
                  <TextInput style={[styles.input, styles.inputMultiline]} placeholder="Any detail the owner should see — what was done, dates covered…" placeholderTextColor="#BDBDBD" multiline value={bulkNote} onChangeText={setBulkNote} />
                  <Text style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
                    The description and note are the same on every horse you pick.
                  </Text>

                  <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Amount ($) — each horse</Text>
                  <TextInput style={styles.input} placeholder="0.00" placeholderTextColor="#BDBDBD" keyboardType="decimal-pad" value={bulkAmount} onChangeText={setBulkAmount} />
                </>
              )}

              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Date of expense</Text>
              <TouchableOpacity style={styles.datePicker} onPress={() => setShowBulkDatePicker(true)} activeOpacity={0.75}>
                <Text style={styles.datePickerText}>{bulkDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</Text><Text>📅</Text>
              </TouchableOpacity>
              {showBulkDatePicker && (
                <DateTimePicker value={bulkDate} mode="date" display={Platform.OS === 'ios' ? 'spinner' : 'default'} onChange={(e, d) => { if (Platform.OS === 'android') setShowBulkDatePicker(false); if (d) setBulkDate(d); }} />
              )}

              <TouchableOpacity style={[styles.recipToggle, { marginTop: 12 }]} onPress={() => setBulkRecurring((v) => !v)} activeOpacity={0.75}>
                <View style={[styles.recipToggleTrack, bulkRecurring && styles.recipToggleTrackOn]}>
                  <View style={[styles.recipToggleThumb, bulkRecurring && styles.recipToggleThumbOn]} />
                </View>
                <Text style={styles.recipToggleLabel}>{bulkRecurring ? '🔁 Repeats monthly' : 'Repeat monthly'}</Text>
              </TouchableOpacity>
              {bulkRecurring && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <Text style={{ fontSize: 13, color: MUTED }}>Day of month</Text>
                  <TouchableOpacity onPress={() => setBulkRecurDay('1')} activeOpacity={0.7} style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, borderWidth: 1, borderColor: bulkRecurDay === '1' ? GREEN : BORDER }}>
                    <Text style={{ fontSize: 13, fontWeight: '600', color: bulkRecurDay === '1' ? GREEN : MUTED }}>1st</Text>
                  </TouchableOpacity>
                  <TextInput style={[styles.input, { width: 64, marginBottom: 0, textAlign: 'center' }]} keyboardType="number-pad" value={bulkRecurDay} onChangeText={(v) => setBulkRecurDay(v.replace(/[^0-9]/g, '').slice(0, 2))} />
                </View>
              )}

              <OwnerGroupedPicker horses={horses} selected={bulkSelected} onChange={setBulkSelected} ownerNameOf={ownerNameOf} />

              {bulkUnique && horses.some((h) => bulkSelected[h.id]) && (
                <View style={{ marginTop: 14 }}>
                  <Text style={styles.fieldLabel}>Each horse's charge</Text>
                  {horses.filter((h) => bulkSelected[h.id]).map((h) => {
                    const per = bulkPer[h.id] || {};
                    return (
                      <View key={h.id} style={{ paddingVertical: 8, borderBottomWidth: 0.5, borderBottomColor: BORDER }}>
                        <Text style={{ fontSize: 14, fontWeight: '700', color: TEXT, marginBottom: 6 }}>{displayName(h)} <Text style={{ fontWeight: '400', color: MUTED }}>· {ownerNameOf(h)}</Text></Text>
                        <TextInput
                          style={[styles.input, { marginBottom: 6 }]}
                          placeholder="Amount ($)"
                          placeholderTextColor="#BDBDBD"
                          keyboardType="decimal-pad"
                          value={per.amount ?? ''}
                          onChangeText={(v) => setBulkPer((prev) => ({ ...prev, [h.id]: { ...(prev[h.id] || {}), amount: v } }))}
                        />
                        <TextInput
                          style={[styles.input, { marginBottom: 0 }]}
                          placeholder="Note for this horse (optional)"
                          placeholderTextColor="#BDBDBD"
                          value={per.note ?? ''}
                          onChangeText={(v) => setBulkPer((prev) => ({ ...prev, [h.id]: { ...(prev[h.id] || {}), note: v } }))}
                        />
                      </View>
                    );
                  })}
                </View>
              )}

              <View style={styles.sheetBtns}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowBulkModal(false)} activeOpacity={0.85}><Text style={styles.cancelBtnText}>Cancel</Text></TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={handleBulkCharge} disabled={bulkSaving} activeOpacity={0.85}>
                  {bulkSaving ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>Add Charge</Text>}
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </SafeAreaView>
  );
}

// ─── FROZEN EMBRYOS ──────────────────────────────────────────────────────────

function FrozenEmbryoScreen({ route, navigation }) {
  const { sire = '', dam = '' } = route.params || {};
  const targetKey = frozenKey({ sire, dam });
  const [horses, setHorses] = useState([]);
  const [showImplant, setShowImplant] = useState(false);
  const [implantDate, setImplantDate] = useState(new Date());
  const [showImplantDate, setShowImplantDate] = useState(false);
  const [implantRecip, setImplantRecip] = useState('');
  const [implantRecipId, setImplantRecipId] = useState(null);
  const [implantBatchId, setImplantBatchId] = useState(null);
  const [showRecipPicker, setShowRecipPicker] = useState(false);
  const [recipSearch, setRecipSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [editBatch, setEditBatch] = useState(null);
  const [ed, setEd] = useState(null);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    return subscribeToHorses(uid, setHorses);
  }, []);

  const batches = horses.filter((x) => x.type === 'frozen_embryo' && frozenKey(x) === targetKey)
    .sort((a, b) => (a.firstFrozen || '').localeCompare(b.firstFrozen || '') || (a.id || '').localeCompare(b.id || ''));
  const label = frozenLabel({ sire, dam });
  const remainingTotal = batches.reduce((s, x) => s + (x.frozenCount || 0), 0);
  const implantedTotal = batches.reduce((s, x) => s + Math.max(0, (x.frozenTotal || x.frozenCount || 0) - (x.frozenCount || 0)), 0);
  const availableBatches = batches.filter((x) => (x.frozenCount || 0) > 0);

  // In-utero embryos, to flag a recip mare that's already carrying one.
  const activeEmbryos = horses.filter((x) => x.type === 'embryo');
  const normName = (s) => (s || '').trim().toLowerCase();
  const carryingByMare = (mare) => activeEmbryos.find((e) => (e.recipMareId && e.recipMareId === mare.id) || (normName(e.recipMare) && normName(e.recipMare) === normName(displayName(mare))));
  const recipCarry = (() => {
    const nm = normName(implantRecip);
    if (!nm) return null;
    return activeEmbryos.find((e) => (implantRecipId && e.recipMareId === implantRecipId) || (normName(e.recipMare) === nm));
  })();

  // If every batch in this pairing was deleted, leave the (now-empty) screen.
  useEffect(() => { if (horses.length > 0 && batches.length === 0) navigation.goBack(); }, [horses.length, batches.length]);

  const openImplant = () => {
    if (availableBatches.length === 0) { Alert.alert('None left', 'There are no embryos left to implant in this pairing.'); return; }
    setImplantDate(new Date());
    setImplantRecip('');
    setImplantRecipId(null);
    setShowImplantDate(false);
    setImplantBatchId(availableBatches[0].id);
    setShowImplant(true);
  };

  const doImplant = async () => {
    if (busy) return;
    const batch = batches.find((b) => b.id === implantBatchId) || availableBatches[0];
    if (!batch || (batch.frozenCount || 0) < 1) { Alert.alert('None left', 'That batch has no embryos left.'); return; }
    setBusy(true);
    try {
      const uid = auth.currentUser?.uid;
      const bd = new Date(implantDate); bd.setDate(bd.getDate() - 7); // breed date = 7 days before implant
      const breedDate = bd.toISOString().split('T')[0];
      const recip = implantRecip.trim();
      const base = createEmbryoHorse(label, breedDate, !!recip);
      // The embryo is already bred, so its Breed Date milestone is done, and it's
      // tagged with the Embryo status automatically.
      const milestones = (base.milestones || []).map((m) => (m.name === 'Breed Date' ? { ...m, completed: true, completedDate: breedDate } : m));
      const embryo = {
        ...base,
        milestones,
        status: ['Embryo'],
        sire: batch.sire || '', sireSire: batch.sireSire || '', sireDam: batch.sireDam || '',
        dam: batch.dam || '', damSire: batch.damSire || '', damDam: batch.damDam || '',
        registeredName: batch.registeredName || '', breed: batch.breed || '', color: batch.color || '',
        implantedDate: localYmd(implantDate),
        fromFrozenId: batch.id, embryoMethod: batch.method || 'ICSI',
        ...(recip && { recipMare: recip }),
        ...(implantRecipId && { recipMareId: implantRecipId }),
        ownerEmail: auth.currentUser?.email || '', ownerName: auth.currentUser?.displayName || '',
      };
      const newId = await addHorse(uid, embryo);
      const newCount = (batch.frozenCount || 0) - 1;
      await updateHorse(batch.id, { frozenCount: newCount });
      setShowImplant(false);
      if (newCount === 0) {
        Alert.alert('Batch used up', `That was the last embryo in this ${batch.method || 'ICSI'} batch. Keep it for your records, or delete it?`, [
          { text: 'Keep', onPress: async () => { try { await updateHorse(batch.id, { depleted: true }); } catch {} navigation.navigate('Horse', { horseId: newId }); } },
          { text: 'Delete', style: 'destructive', onPress: async () => { try { await softDeleteHorse(batch.id, uid, auth.currentUser?.email); } catch {} navigation.navigate('Horse', { horseId: newId }); } },
        ]);
      } else {
        navigation.navigate('Horse', { horseId: newId });
      }
    } catch (e) {
      Alert.alert('Error', 'Could not implant. Please try again.');
    } finally { setBusy(false); }
  };

  const openEdit = (b) => {
    setEditBatch(b);
    setEd({ method: b.method || 'ICSI', frozenLocation: b.frozenLocation || '', frozenCount: String(b.frozenCount ?? ''), sire: b.sire || '', dam: b.dam || '', sireSire: b.sireSire || '', sireDam: b.sireDam || '', damSire: b.damSire || '', damDam: b.damDam || '' });
  };
  const saveEdit = async () => {
    const c = parseInt(ed.frozenCount, 10);
    if (!(c >= 0)) { Alert.alert('Invalid count', 'Enter a valid number of embryos.'); return; }
    try {
      await updateHorse(editBatch.id, {
        method: ed.method === 'Flush' ? 'Flush' : 'ICSI', frozenLocation: ed.frozenLocation.trim(), frozenCount: c,
        sire: ed.sire.trim(), dam: ed.dam.trim(), sireSire: ed.sireSire.trim(), sireDam: ed.sireDam.trim(), damSire: ed.damSire.trim(), damDam: ed.damDam.trim(),
        name: frozenLabel({ sire: ed.sire, dam: ed.dam }), ...(c > 0 ? { depleted: false } : {}),
      });
      setEditBatch(null); setEd(null);
    } catch { Alert.alert('Error', 'Could not save. Please try again.'); }
  };
  const delBatch = (b) => {
    Alert.alert('Delete batch', "Move this batch to Recently Deleted? Embryos already implanted from it aren't affected.", [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => { try { await softDeleteHorse(b.id, auth.currentUser?.uid, auth.currentUser?.email); } catch {} } },
    ]);
  };

  const Row = ({ label: l, value }) => value ? (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 5 }}>
      <Text style={{ fontSize: 12.5, color: MUTED }}>{l}</Text>
      <Text style={{ fontSize: 13.5, color: TEXT, fontWeight: '600', flexShrink: 1, textAlign: 'right', marginLeft: 10 }}>{value}</Text>
    </View>
  ) : null;

  if (horses.length === 0) return <SafeAreaView style={styles.screen}><Text style={{ padding: 20, color: MUTED }}>Loading…</Text></SafeAreaView>;

  return (
    <SafeAreaView style={styles.screen} edges={['bottom']}>
      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
        <Text style={{ fontSize: 22, fontWeight: '700', color: TEXT }}>{label}</Text>
        <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8, marginTop: 4, marginBottom: 16 }}>
          <Text style={{ fontSize: 32, fontWeight: '800', color: remainingTotal > 0 ? '#2E6DA4' : MUTED }}>{remainingTotal}</Text>
          <Text style={{ fontSize: 14, color: MUTED }}>frozen{implantedTotal > 0 ? ` · ${implantedTotal} implanted` : ''}{batches.length > 1 ? ` · ${batches.length} batches` : ''}</Text>
        </View>

        {batches.map((b, i) => {
          const rem = b.frozenCount || 0;
          const tot = b.frozenTotal || rem;
          return (
            <View key={b.id} style={{ backgroundColor: CARD, borderRadius: 12, borderWidth: 0.5, borderColor: BORDER, padding: 14, marginBottom: 12, opacity: rem > 0 ? 1 : 0.6 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
                <Text style={{ fontSize: 15, fontWeight: '700', color: TEXT, flex: 1 }}>{batches.length > 1 ? `Batch ${i + 1} · ` : ''}{b.method || 'ICSI'}</Text>
                <Text style={{ fontSize: 16, fontWeight: '800', color: rem > 0 ? '#2E6DA4' : MUTED }}>{rem}<Text style={{ fontSize: 12, color: MUTED, fontWeight: '400' }}> / {tot}</Text></Text>
              </View>
              <Row label="Location" value={b.frozenLocation} />
              <Row label="First frozen" value={b.firstFrozen ? formatDate(b.firstFrozen) : ''} />
              <Row label="Last frozen" value={b.lastFrozen ? formatDate(b.lastFrozen) : ''} />
              <View style={{ flexDirection: 'row', gap: 18, marginTop: 8 }}>
                <TouchableOpacity onPress={() => openEdit(b)} activeOpacity={0.7}><Text style={{ fontSize: 13, color: GREEN, fontWeight: '600' }}>Edit</Text></TouchableOpacity>
                <TouchableOpacity onPress={() => delBatch(b)} activeOpacity={0.7}><Text style={{ fontSize: 13, color: RED, fontWeight: '600' }}>Delete</Text></TouchableOpacity>
              </View>
            </View>
          );
        })}

        {(sire || dam) && batches[0] && (
          <View style={{ backgroundColor: CARD, borderRadius: 12, borderWidth: 0.5, borderColor: BORDER, padding: 14, marginBottom: 16 }}>
            <Text style={{ fontSize: 13, fontWeight: '700', color: MUTED, marginBottom: 4 }}>Pedigree</Text>
            <Row label="Sire" value={batches[0].sire} />
            <Row label="Dam" value={batches[0].dam} />
            <Row label="Sire's sire" value={batches[0].sireSire} />
            <Row label="Sire's dam" value={batches[0].sireDam} />
            <Row label="Dam's sire" value={batches[0].damSire} />
            <Row label="Dam's dam" value={batches[0].damDam} />
          </View>
        )}

        {remainingTotal > 0 && (
          <TouchableOpacity style={styles.saveBtn} onPress={openImplant} activeOpacity={0.85}>
            <Text style={styles.saveBtnText}>🐴  Embryo Implanted</Text>
          </TouchableOpacity>
        )}
      </ScrollView>

      <Modal visible={showImplant} animationType="slide" transparent onRequestClose={() => setShowImplant(false)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '90%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={styles.sheetTitle}>Embryo Implanted</Text>
              <Text style={[styles.fieldLabel, { marginBottom: 8 }]}>Creates a new in-utero embryo and reduces the chosen batch by one. Breed date is set 7 days before the implant date.</Text>
              {availableBatches.length > 1 && (
                <>
                  <Text style={styles.fieldLabel}>From which batch?</Text>
                  {availableBatches.map((b) => {
                    const sel = implantBatchId === b.id;
                    return (
                      <TouchableOpacity key={b.id} onPress={() => setImplantBatchId(b.id)} activeOpacity={0.7} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 8, borderBottomWidth: 0.5, borderBottomColor: BORDER }}>
                        <View style={[styles.radioOuter, sel && styles.radioOuterSelected]}>{sel && <View style={styles.radioInner} />}</View>
                        <Text style={{ flex: 1, fontSize: 14, color: TEXT }}>Batch {batches.indexOf(b) + 1} · {b.method || 'ICSI'}{b.frozenLocation ? ` · ${b.frozenLocation}` : ''}</Text>
                        <Text style={{ fontSize: 13, fontWeight: '700', color: '#2E6DA4' }}>{b.frozenCount} left</Text>
                      </TouchableOpacity>
                    );
                  })}
                </>
              )}
              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Implant date</Text>
              <TouchableOpacity style={styles.datePicker} onPress={() => setShowImplantDate(true)}>
                <Text style={styles.datePickerText}>{implantDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Text><Text>📅</Text>
              </TouchableOpacity>
              {showImplantDate && (
                <DateTimePicker value={implantDate} mode="date" display={Platform.OS === 'ios' ? 'spinner' : 'default'} onChange={(e, d) => { if (Platform.OS === 'android') setShowImplantDate(false); if (d) setImplantDate(d); }} />
              )}
              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Recipient mare (optional)</Text>
              <View style={styles.pedigreeInputRow}>
                <TextInput style={[styles.input, { flex: 1, marginBottom: 0 }]} placeholder="Recip mare name or source" placeholderTextColor="#BDBDBD" value={implantRecip} onChangeText={(v) => { setImplantRecip(v); setImplantRecipId(null); }} />
                <TouchableOpacity style={styles.fromStableBtn} onPress={() => { setRecipSearch(''); setShowRecipPicker(true); }} activeOpacity={0.75}>
                  <Text style={styles.fromStableBtnTxt}>From Stable</Text>
                </TouchableOpacity>
              </View>
              {recipCarry && (
                <Text style={{ fontSize: 12.5, color: '#B00020', fontWeight: '600', marginTop: 6 }}>⚠️ {implantRecip} may already be carrying {displayName(recipCarry)}. Double-check before implanting.</Text>
              )}
              <View style={styles.sheetBtns}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowImplant(false)} activeOpacity={0.85}><Text style={styles.cancelBtnText}>Cancel</Text></TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={doImplant} disabled={busy} activeOpacity={0.85}>{busy ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>Create embryo</Text>}</TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      <Modal visible={showRecipPicker} animationType="slide" transparent onRequestClose={() => setShowRecipPicker(false)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '80%' }]}>
            <Text style={styles.sheetTitle}>Pick a recipient mare</Text>
            <TextInput style={styles.input} placeholder="Search your stable…" placeholderTextColor="#BDBDBD" value={recipSearch} onChangeText={setRecipSearch} autoFocus />
            <ScrollView keyboardShouldPersistTaps="handled" style={{ maxHeight: 340 }}>
              {(() => {
                const q = recipSearch.trim().toLowerCase();
                const cands = horses
                  .filter((x) => x.type === 'born' && (x.status || []).includes('Recip'))
                  .filter((x) => !q || displayName(x).toLowerCase().includes(q))
                  .sort((a, b) => displayName(a).localeCompare(displayName(b)));
                if (cands.length === 0) return <Text style={styles.noNotesTxt}>No recips in your stable. Tag a mare as “Recip,” or type the name above instead.</Text>;
                return cands.map((x) => {
                  const carrying = carryingByMare(x);
                  return (
                    <TouchableOpacity key={x.id} onPress={() => { setImplantRecip(displayName(x)); setImplantRecipId(x.id); setShowRecipPicker(false); }} activeOpacity={0.7} style={{ paddingVertical: 12, borderBottomWidth: 0.5, borderBottomColor: BORDER }}>
                      <Text style={{ fontSize: 15, color: TEXT, fontWeight: '600' }}>{displayName(x)}</Text>
                      {carrying
                        ? <Text style={{ fontSize: 12, color: '#B00020', fontWeight: '600' }}>⚠️ Already carrying {displayName(carrying)}</Text>
                        : <Text style={{ fontSize: 12, color: MUTED }}>Available</Text>}
                    </TouchableOpacity>
                  );
                });
              })()}
            </ScrollView>
            <TouchableOpacity style={[styles.cancelBtn, { marginTop: 12 }]} onPress={() => setShowRecipPicker(false)} activeOpacity={0.85}><Text style={styles.cancelBtnText}>Cancel</Text></TouchableOpacity>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      <Modal visible={!!editBatch} animationType="slide" transparent onRequestClose={() => { setEditBatch(null); setEd(null); }}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '90%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={styles.sheetTitle}>Edit Batch</Text>
              {ed && (
                <>
                  <Text style={styles.fieldLabel}>Made via</Text>
                  <View style={styles.typeToggle}>
                    {['ICSI', 'Flush'].map((m) => (
                      <TouchableOpacity key={m} style={[styles.typeBtn, ed.method === m && styles.typeBtnActive]} onPress={() => setEd((s) => ({ ...s, method: m }))} activeOpacity={0.75}>
                        <Text style={[styles.typeBtnTxt, ed.method === m && styles.typeBtnTxtActive]}>{m}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                  <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Location</Text>
                  <TextInput style={styles.input} value={ed.frozenLocation} onChangeText={(v) => setEd((s) => ({ ...s, frozenLocation: v }))} placeholder="Location" placeholderTextColor="#BDBDBD" />
                  <Text style={styles.fieldLabel}>Number frozen</Text>
                  <TextInput style={styles.input} keyboardType="number-pad" value={ed.frozenCount} onChangeText={(v) => setEd((s) => ({ ...s, frozenCount: v }))} placeholder="0" placeholderTextColor="#BDBDBD" />
                  {[['sire', 'Sire'], ['dam', 'Dam'], ['sireSire', "Sire's sire"], ['sireDam', "Sire's dam"], ['damSire', "Dam's sire"], ['damDam', "Dam's dam"]].map(([k, l]) => (
                    <View key={k}>
                      <Text style={styles.fieldLabel}>{l}</Text>
                      <TextInput style={styles.input} value={ed[k]} onChangeText={(v) => setEd((s) => ({ ...s, [k]: v }))} placeholder={l} placeholderTextColor="#BDBDBD" />
                    </View>
                  ))}
                  <View style={styles.sheetBtns}>
                    <TouchableOpacity style={styles.cancelBtn} onPress={() => { setEditBatch(null); setEd(null); }} activeOpacity={0.85}><Text style={styles.cancelBtnText}>Cancel</Text></TouchableOpacity>
                    <TouchableOpacity style={styles.saveBtn} onPress={saveEdit} activeOpacity={0.85}><Text style={styles.saveBtnText}>Save</Text></TouchableOpacity>
                  </View>
                </>
              )}
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </SafeAreaView>
  );
}

function MilestonesScreen({ navigation, route }) {
  const [horses, setHorses] = useState([]);
  const [horseFilter, setHorseFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [showCompleted, setShowCompleted] = useState(false);
  const [showDateMenu, setShowDateMenu] = useState(false);
  const [showHorseMenu, setShowHorseMenu] = useState(false);
  // 'list' is the task list; 'timeline' is the same tasks grouped by when they
  // fall due, with foal dates and breeding checks folded in.
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'timeline' | 'calendar'
  const [clientOnly, setClientOnly] = useState(false); // only tasks on horses I'm a provider for
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [calDay, setCalDay] = useState(() => { const d = new Date(); d.setHours(12, 0, 0, 0); return d.toISOString().split('T')[0]; });
  const [calScope, setCalScope] = useState('today'); // 'today' | 'week' planner | 'month' grid
  // Multi-select: tick tasks anywhere on this screen, then move or complete
  // them together — five horses shod today, three of them bumped a week.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState({}); // 'horseId|milestoneId' → true
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveDate, setMoveDate] = useState(new Date());
  const [showMoveDatePicker, setShowMoveDatePicker] = useState(false);
  const [moveInWeeks, setMoveInWeeks] = useState('');
  const [moveInMonths, setMoveInMonths] = useState('');
  const [moving, setMoving] = useState(false);
  const [calWeekStart, setCalWeekStart] = useState(() => { const d = new Date(); d.setHours(12, 0, 0, 0); d.setDate(d.getDate() - d.getDay()); return d; }); // Sunday
  const [plans, setPlans] = useState([]);
  // Provider-only: the ledger behind the chips, and who I bill as.
  const [ledger, setLedger] = useState([]);
  const [ownProfile, setOwnProfile] = useState(null);
  const [company, setCompany] = useState(null);
  const [pinnedChips, setPinnedChips] = useState([]);
  const [hiddenChips, setHiddenChips] = useState([]);
  const [charging, setCharging] = useState(null);   // horseId being billed
  const [chargeLines, setChargeLines] = useState([]); // [{ amount, description, note }]
  const [chargeBusy, setChargeBusy] = useState(false);
  const [showChipEditor, setShowChipEditor] = useState(false);
  const [chipDraft, setChipDraft] = useState([]);
  const [chipOriginals, setChipOriginals] = useState([]); // what the editor opened on
  const [showRebook, setShowRebook] = useState(false);
  const [rebookWeeks, setRebookWeeks] = useState('6');
  const [rebookMonths, setRebookMonths] = useState('');
  const [rebookBusy, setRebookBusy] = useState(false);
  const [rebookEach, setRebookEach] = useState(false);      // a cycle per horse
  const [rebookPer, setRebookPer] = useState({});           // horseId → { weeks, months }
  const [showAddMilestoneModal, setShowAddMilestoneModal] = useState(false);
  const [milestoneName, setMilestoneName] = useState('');
  const [milestoneDate, setMilestoneDate] = useState(new Date());
  const [showMilestoneDatePicker, setShowMilestoneDatePicker] = useState(false);
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderMode, setReminderMode] = useState('specified'); // 'specified' | 'custom'
  const [reminderDayBefore, setReminderDayBefore] = useState(false); // a push the day before, at 8am local
  const [reminderRepeat, setReminderRepeat] = useState(false); // repeat daily until completed
  const [reminderDate, setReminderDate] = useState(new Date());
  const [showReminderDatePicker, setShowReminderDatePicker] = useState(false);
  const [showReminderTimePicker, setShowReminderTimePicker] = useState(false);
  const [milestoneHorseIds, setMilestoneHorseIds] = useState([]);
  const [milestoneCategory, setMilestoneCategory] = useState('');
  const [milestoneCategoryOther, setMilestoneCategoryOther] = useState('');
  const [milestoneNotes, setMilestoneNotes] = useState('');
  const [milestoneAddAsComplete, setMilestoneAddAsComplete] = useState(false); // log a task that's already done
  const [editingMilestone, setEditingMilestone] = useState(null); // { horseId, milestoneId }
  const [customCategories, setCustomCategories] = useState([]);
  const [hiddenCategories, setHiddenCategories] = useState([]);
  const [showLastCompletedModal, setShowLastCompletedModal] = useState(false);
  const [lastCompletedCategory, setLastCompletedCategory] = useState(null);

  // Load the last-used view and filters once, then persist each change.
  // Nothing is written until the load has finished: the save effects also run
  // on mount, and writing the defaults then would wipe what was stored.
  // A jump from the dashboard or a push (calendar · today · client horses only)
  // is a one-off view: it must not be saved as the user's preference, or every
  // later visit opens on today's client work and the rest of the herd's tasks
  // look gone. Client-only is never remembered for the same reason.
  const prefsLoaded = useRef(false);
  const jumpView = useRef(null); // the scope a jump opened on, until the user changes view
  useEffect(() => {
    Promise.all([
      AsyncStorage.getItem('milestonesViewMode'),
      AsyncStorage.getItem('milestonesDateFilter'),
      AsyncStorage.getItem('milestonesHorseFilter'),
      AsyncStorage.getItem('milestonesCalScope'),
      AsyncStorage.getItem('milestonesPrefsV2'),
    ]).then(([view, date, horse, scope, v2]) => {
      // Before V2, a jump's calendar/today view was saved as the preference —
      // drop that once so affected users land back on the full list.
      if (!v2) {
        AsyncStorage.multiRemove(['milestonesViewMode', 'milestonesCalScope', 'milestonesClientOnly']).catch(() => {});
        AsyncStorage.setItem('milestonesPrefsV2', '1').catch(() => {});
        view = null; scope = null;
      }
      const jumped = !!route?.params?.calScope; // a push said where to open
      if (!jumped && (view === 'list' || view === 'timeline' || view === 'calendar')) setViewMode(view);
      if (date) setDateFilter(date);
      if (horse) setHorseFilter(horse);
      if (!jumped && (scope === 'week' || scope === 'month' || scope === 'today')) setCalScope(scope);
    }).catch(() => {}).finally(() => { prefsLoaded.current = true; });
  }, []);
  useEffect(() => {
    const scope = route?.params?.calScope;
    if (scope !== 'today' && scope !== 'week' && scope !== 'month') return;
    jumpView.current = scope;
    setViewMode('calendar');
    setCalScope(scope);
    setClientOnly(!!route?.params?.clientOnly);
    if (scope === 'week') { const d = new Date(); d.setHours(12, 0, 0, 0); d.setDate(d.getDate() - d.getDay()); setCalWeekStart(d); }
    navigation.setParams({ calScope: undefined, clientOnly: undefined });
  }, [route?.params?.calScope]);
  const persist = (key, value) => { if (prefsLoaded.current) AsyncStorage.setItem(key, value).catch(() => {}); };
  // A saved horse filter for a horse that has since been deleted or unshared
  // would leave the list empty with no visible reason.
  useEffect(() => {
    if (horseFilter !== 'all' && horses.length && !horses.some((h) => h.id === horseFilter)) setHorseFilter('all');
  }, [horses, horseFilter]);
  useEffect(() => {
    if (jumpView.current) {
      if (viewMode === 'calendar' && calScope === jumpView.current) return; // still the jump's view
      jumpView.current = null; // the user picked a view of their own
    }
    persist('milestonesViewMode', viewMode);
    persist('milestonesCalScope', calScope);
  }, [viewMode, calScope]);
  useEffect(() => { persist('milestonesDateFilter', dateFilter); }, [dateFilter]);
  useEffect(() => { persist('milestonesHorseFilter', horseFilter); }, [horseFilter]);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const unsub = subscribeToHorses(uid, setHorses);
    return unsub;
  }, []);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const unsub = subscribeToUserSettings(uid, (s) => {
      setCustomCategories(s.customCategories || []);
      setHiddenCategories(s.hiddenCategories || []);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    return subscribeToBreedingPlans(uid, setPlans);
  }, []);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    return subscribeToUserSettings(uid, (s) => { setOwnProfile(s.businessProfile || null); setPinnedChips(s.serviceChips || []); setHiddenChips(s.hiddenServiceChips || []); });
  }, []);
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    return subscribeToMyCompany(uid, setCompany);
  }, []);
  const myProfile = effectiveBusinessProfile(ownProfile, company);
  // Only the horses I am a provider for have a ledger I can bill to.
  const clientIdKey = horses.filter((h) => isHorseTrainer(h, auth.currentUser?.uid)).map((h) => h.id).sort().join(',');
  useEffect(() => {
    if (!clientIdKey) { setLedger([]); return; }
    return subscribeToLedgerForHorses(clientIdKey.split(','), setLedger);
  }, [clientIdKey]);

  const myUid = auth.currentUser?.uid;
  const hasClientHorses = horses.some((h) => isHorseTrainer(h, myUid));
  const inScope = (h) => !clientOnly || isHorseTrainer(h, myUid);
  const visibleMilestones = (h) => (h.milestones || []).filter((m) => taskVisibleTo(h, m, myUid));
  const horsesWithMilestones = horses.filter((h) => visibleMilestones(h).length && inScope(h));
  const horsesWithPending = horsesWithMilestones.filter((h) => visibleMilestones(h).some((m) => !m.completed));

  // Distinct category/name keys across all horses' completed milestones
  const lastCompletedOptions = Array.from(
    new Set(
      horsesWithMilestones.flatMap((h) =>
        h.milestones.filter((m) => m.completed).map((m) => m.category || m.name)
      )
    )
  ).sort();

  // For the selected key, find each horse's most recently completed milestone matching it
  const lastCompletedResults = lastCompletedCategory
    ? horsesWithMilestones
        .map((h) => {
          const matches = h.milestones.filter((m) => m.completed && (m.category || m.name) === lastCompletedCategory);
          if (matches.length === 0) return null;
          const latest = matches.reduce((best, m) =>
            !best || getMilestoneDate(h, m) > getMilestoneDate(h, best) ? m : best
          , null);
          return { horse: h, mDate: getMilestoneDate(h, latest) };
        })
        .filter(Boolean)
        .sort((a, b) => displayName(a.horse).localeCompare(displayName(b.horse)))
    : [];

  const toggleMilestone = (horse, milestoneId) => {
    const today = new Date().toISOString().split('T')[0];
    const milestones = (horse.milestones || []).map((m) => {
      if (m.id !== milestoneId) return m;
      const nowComplete = !m.completed;
      const updated = { ...m, completed: nowComplete };
      if (nowComplete) updated.completedDate = today; else delete updated.completedDate;
      return updated;
    });
    updateHorse(horse.id, { milestones });
  };

  const openAddMilestoneModal = () => {
    setEditingMilestone(null);
    setMilestoneName('');
    setMilestoneDate(new Date());
    setReminderEnabled(true); // reminders on by default; user can turn off
    setReminderMode('specified');
    setReminderDayBefore(false);
    setReminderRepeat(false); // one reminder unless the user opts into repeats
    setReminderDate(new Date());
    setShowReminderDatePicker(false);
    setShowReminderTimePicker(false);
    setMilestoneHorseIds([]);
    setMilestoneCategory('');
    setMilestoneCategoryOther('');
    setMilestoneNotes('');
    setMilestoneAddAsComplete(false);
    setShowAddMilestoneModal(true);
  };

  const openEditMilestone = (horse, m) => {
    setEditingMilestone({ horseId: horse.id, milestoneId: m.id });
    setMilestoneAddAsComplete(false);
    setMilestoneName(m.name || '');
    setMilestoneDate(new Date(getMilestoneDate(horse, m) + 'T12:00:00'));
    setReminderEnabled(!!m.remindOnDue || !!m.reminderAt);
    setReminderMode(m.reminderAt && !m.remindOnDue ? 'custom' : 'specified');
    setReminderDayBefore(!!m.remindDayBefore);
    setReminderRepeat(!!m.repeatUntilDone);
    setReminderDate(m.reminderAt ? new Date(m.reminderAt) : new Date());
    setShowReminderDatePicker(false);
    setShowReminderTimePicker(false);
    setMilestoneHorseIds([horse.id]);
    setMilestoneCategory(m.category || '');
    setMilestoneCategoryOther('');
    setMilestoneNotes(m.notes || '');
    setShowAddMilestoneModal(true);
  };

  const toggleMilestoneHorse = (id) => {
    setMilestoneHorseIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAddMilestone = async () => {
    if (!milestoneName.trim()) {
      Alert.alert('Name required', 'Please enter a task name.');
      return;
    }
    if (milestoneHorseIds.length === 0) {
      Alert.alert('Select a horse', 'Please select at least one horse for this task.');
      return;
    }
    const picked = new Date(milestoneDate);
    picked.setHours(12, 0, 0, 0);
    const dateStr = picked.toISOString().split('T')[0];

    // A "due date" reminder is delivered server-side (push to the assignee on
    // the due date). A "custom" reminder still schedules a local notification
    // on this device. This avoids double-notifying and reaches the assignee.
    const remindOnDue = reminderEnabled && reminderMode === 'specified';
    let fireDate = null;
    if (reminderEnabled && reminderMode === 'custom') {
      fireDate = reminderDate;
    }
    // Logging a task that's already done — no reminders, and stamp the date it
    // was completed (the date field doubles as "completed on").
    const completingNow = milestoneAddAsComplete && !editingMilestone;
    if (completingNow) fireDate = null;
    const effRemindOnDue = completingNow ? false : remindOnDue;
    const assignerName = auth.currentUser?.displayName || auth.currentUser?.email || '';

    const category = milestoneCategory === 'Other' ? milestoneCategoryOther.trim() : milestoneCategory;
    const notes = milestoneNotes.trim();

    // ── Edit existing task ──
    if (editingMilestone) {
      const target = horses.find((h) => h.id === editingMilestone.horseId);
      if (target) {
        const existing = (target.milestones || []).find((m) => m.id === editingMilestone.milestoneId);
        if (existing?.notificationId) await cancelReminder(existing.notificationId);
        let newNotifId = null;
        if (fireDate) newNotifId = await scheduleReminder(milestoneName.trim(), `${displayName(target)} — reminder`, fireDate);
        const milestones = (target.milestones || []).map((m) => {
          if (m.id !== editingMilestone.milestoneId) return m;
          const updated = { ...m, name: milestoneName.trim(), date: dateStr };
          if (category) updated.category = category; else delete updated.category;
          if (notes) updated.notes = notes; else delete updated.notes;
          if (remindOnDue) updated.remindOnDue = true; else delete updated.remindOnDue;
        // Repeats are server-driven, so they only apply to the due-date reminder.
        if (remindOnDue && reminderRepeat) updated.repeatUntilDone = true; else delete updated.repeatUntilDone;
        if (remindOnDue && reminderDayBefore) updated.remindDayBefore = true; else delete updated.remindDayBefore;
        delete updated.dayBeforeNotifiedOn; // edited, so it may be due again
        delete updated.ownerDueNotifiedOn; delete updated.ownerDayBeforeNotifiedOn;
          delete updated.dueNotifiedOn;
          if (newNotifId) { updated.notificationId = newNotifId; updated.reminderAt = fireDate.toISOString(); }
          else { delete updated.notificationId; delete updated.reminderAt; }
          return updated;
        }).sort((a, b) => getMilestoneDate(target, a).localeCompare(getMilestoneDate(target, b)));
        updateHorse(target.id, { milestones });
      }
      setEditingMilestone(null);
      setShowAddMilestoneModal(false);
      return;
    }

    for (const id of milestoneHorseIds) {
      const target = horses.find((h) => h.id === id);
      if (!target) continue;
      const newMilestone = {
        id: `m_custom_${Date.now()}_${id}`,
        name: milestoneName.trim(),
        date: dateStr,
        completed: completingNow,
        ...(completingNow && { completedDate: dateStr }),
        custom: true,
        ...(category && { category }),
        ...(notes && { notes }),
        ...(effRemindOnDue && { remindOnDue: true }),
        ...(effRemindOnDue && reminderRepeat && { repeatUntilDone: true }),
        ...(effRemindOnDue && reminderDayBefore && { remindDayBefore: true }),
        createdByUid: auth.currentUser?.uid || '',
      };
      if (fireDate) {
        const notificationId = await scheduleReminder(
          newMilestone.name,
          `${displayName(target)} — reminder`,
          fireDate
        );
        if (notificationId) {
          newMilestone.notificationId = notificationId;
          newMilestone.reminderAt = fireDate.toISOString();
        }
      }
      const milestones = [...(target.milestones || []), newMilestone]
        .sort((a, b) => getMilestoneDate(target, a).localeCompare(getMilestoneDate(target, b)));
      updateHorse(id, { milestones });
    }
    setShowAddMilestoneModal(false);
  };

  // Flatten all milestones across all horses into a single list
  let items = horsesWithMilestones.flatMap((h) =>
    visibleMilestones(h).map((m) => ({
      horse: h,
      milestone: m,
      mDate: getMilestoneDate(h, m),
    }))
  );

  if (horseFilter !== 'all') {
    items = items.filter((i) => i.horse.id === horseFilter);
  }
  if (!showCompleted) {
    items = items.filter((i) => !i.milestone.completed);
  }
  if (dateFilter !== 'all') {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    items = items.filter((i) => {
      const target = new Date(i.mDate + 'T12:00:00');
      target.setHours(0, 0, 0, 0);
      const diff = Math.round((target - today) / 86400000);
      if (dateFilter === 'overdue') return diff < 0 && !i.milestone.completed;
      if (dateFilter === '7') return diff >= 0 && diff <= 7;
      if (dateFilter === '30') return diff >= 0 && diff <= 30;
      return true;
    });
  }

  items.sort((a, b) => new Date(a.mDate + 'T12:00:00') - new Date(b.mDate + 'T12:00:00'));

  // Everything with a date that has not happened yet: open tasks, the foal date
  // for each embryo, and the breeding checks on a plan that has been bred but is
  // not yet in the herd. The horse filter still applies; the date filter does
  // not, because the grouping below is itself a date view.
  const timelineItems = (() => {
    const out = [];
    horses.forEach((h) => {
      if (horseFilter !== 'all' && h.id !== horseFilter) return;
      if (!inScope(h)) return;
      visibleMilestones(h)
        .filter((m) => !m.completed)
        .forEach((m) => {
          out.push({
            key: h.id + '_' + m.id,
            horse: h,
            m,
            title: m.name,
            who: displayName(h),
            category: m.category,
            date: getMilestoneDate(h, m),
            onPress: () => navigation.navigate('Horse', { horseId: h.id }),
          });
        });
      if (h.type === 'embryo' && h.breedDate) {
        out.push({
          key: h.id + '_foal',
          title: 'Estimated Foal Date',
          who: displayName(h),
          date: addDays(h.breedDate, 340),
          onPress: () => navigation.navigate('Horse', { horseId: h.id }),
        });
      }
    });
    // A plan's checks matter before there is any horse to hang them on.
    if (horseFilter === 'all' && !clientOnly) {
      plans.forEach((p) => {
        if (!p.bredDate || p.stage === 'bred' || p.archived) return;
        (p.checks || [])
          .filter((c) => !c.completed)
          .forEach((c) => {
            out.push({
              key: p.id + '_' + c.id,
              title: c.name,
              who: planLabel(p),
              plan: true,
              date: addDays(p.bredDate, c.daysOffset),
              onPress: () => navigation.navigate('BreedingContract', { planId: p.id }),
            });
          });
      });
    }
    return out.sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
  })();

  const daysAway = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateStr + 'T12:00:00');
    target.setHours(0, 0, 0, 0);
    return Math.round((target - today) / 86400000);
  };

  const timelineGroups = (() => {
    const g = [
      { label: 'Overdue', items: timelineItems.filter((i) => daysAway(i.date) < 0) },
      { label: 'This Week', items: timelineItems.filter((i) => { const d = daysAway(i.date); return d >= 0 && d <= 7; }) },
      { label: 'Next 30 Days', items: timelineItems.filter((i) => { const d = daysAway(i.date); return d > 7 && d <= 30; }) },
    ].filter((x) => x.items.length > 0);
    timelineItems.filter((i) => daysAway(i.date) > 30).forEach((i) => {
      const label = new Date(i.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      let grp = g.find((x) => x.label === label);
      if (!grp) { grp = { label, items: [] }; g.push(grp); }
      grp.items.push(i);
    });
    return g;
  })();

  const calendarByDay = (() => {
    const map = {};
    const put = (i) => { if (!i.date) return; (map[i.date] = map[i.date] || []).push(i); };
    timelineItems.forEach(put);
    if (showCompleted) {
      horses.forEach((h) => {
        if (horseFilter !== 'all' && h.id !== horseFilter) return;
        if (!inScope(h)) return;
        visibleMilestones(h).filter((m) => m.completed).forEach((m) => put({
          key: h.id + '_' + m.id + '_done', horse: h, m, title: m.name, who: displayName(h), done: true,
          date: m.completedDate || getMilestoneDate(h, m),
          onPress: () => navigation.navigate('Horse', { horseId: h.id }),
        }));
      });
    }
    return map;
  })();
  const calCells = (() => {
    const y = calMonth.getFullYear(), mo = calMonth.getMonth();
    const first = new Date(y, mo, 1).getDay();
    const days = new Date(y, mo + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(`${y}-${String(mo + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
    while (cells.length % 7) cells.push(null);
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
  })();
  const todayStr = (() => { const d = new Date(); d.setHours(12, 0, 0, 0); return d.toISOString().split('T')[0]; })();
  const calMonthCount = calCells.flat().reduce((n, d) => n + (d ? (calendarByDay[d] || []).length : 0), 0);
  const dayKey = (d) => { const x = new Date(d); x.setHours(12, 0, 0, 0); return x.toISOString().split('T')[0]; };
  const calWeekDays = Array.from({ length: 7 }, (_, i) => { const d = new Date(calWeekStart); d.setDate(d.getDate() + i); return dayKey(d); });
  const calWeekCount = calWeekDays.reduce((n, d) => n + (calendarByDay[d] || []).length, 0);
  const calWeekLabel = (() => {
    const a = new Date(calWeekDays[0] + 'T12:00:00'), b = new Date(calWeekDays[6] + 'T12:00:00');
    const sameMonth = a.getMonth() === b.getMonth();
    return `${a.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${b.toLocaleDateString('en-US', sameMonth ? { day: 'numeric' } : { month: 'short', day: 'numeric' })}`;
  })();
  const isThisWeek = calWeekDays.includes(todayStr);
  const overdueItems = timelineItems.filter((i) => i.date && i.date < todayStr);
  const todayItems = calendarByDay[todayStr] || [];

  const money = (n) => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const myCompanyKey = normalizeCompany(myProfile?.name) || ('u:' + (myUid || 'unknown'));
  const myLedger = ledger.filter((e) => entryCompany(e) === myCompanyKey);
  const chips = serviceChips(myLedger, pinnedChips, todayStr, hiddenChips);
  // Today's work for a provider, grouped by client: one stop per barn, the
  // horses under it, and what has already been billed there today.
  const dayItems = (() => {
    const out = [];
    horses.forEach((h) => {
      if (!isHorseTrainer(h, myUid)) return;
      if (horseFilter !== 'all' && h.id !== horseFilter) return;
      (h.milestones || []).forEach((m) => {
        if (getMilestoneDate(h, m) !== todayStr) return;
        if (!taskVisibleTo(h, m, myUid)) return;
        out.push({ key: h.id + '_' + m.id, horse: h, m, who: displayName(h), title: m.name });
      });
    });
    return out;
  })();
  const dayKeys = new Set(dayItems.map((i) => i.key));
  const dayGroups = (() => {
    const out = [];
    dayItems.forEach((i) => {
      const key = getOwnerContact(i.horse)?.name || i.horse.ownerName || 'Owner not set';
      const g = out.find((x) => x.key === key) || (out.push({ key, contact: getOwnerContact(i.horse), rows: [] }), out[out.length - 1]);
      g.rows.push(i);
    });
    out.sort((a, b) => a.key.localeCompare(b.key));
    return out;
  })();
  const dayRows = dayGroups.flatMap((g) => g.rows);
  const dayDone = dayRows.filter((i) => i.m.completed).length;
  const billedToday = myLedger
    .filter((e) => e.type === 'charge' && !e.deleted && e.date === todayStr && dayRows.some((i) => i.horse.id === e.horseId))
    .reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const isDaySheet = calScope === 'today' && dayRows.length > 0;

  const openCharge = (horseId, taskName) => {
    setCharging(horseId);
    const want = String(taskName || '').trim().toLowerCase();
    const pick = chips.find((c) => c.description.trim().toLowerCase() === want) || chips[0];
    setChargeLines([{ amount: pick ? String(pick.amount) : '', description: pick?.description || taskName || '', note: '' }]);
  };
  const setLine = (idx, patch) => setChargeLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const chargeTotal = chargeLines.reduce((s, l) => s + (parseAmount(l.amount) || 0), 0);

  // Tick the horse off and bill it in one go. Either both happen or the task
  // stays open, so a charge can never be left attached to work marked undone.
  const finishHorse = async (item, withCharge) => {
    const h = item.horse;
    if (withCharge) {
      const lines = chargeLines.filter((l) => (parseAmount(l.amount) || 0) > 0);
      if (lines.length === 0) { Alert.alert('Amount needed', 'Enter what to charge, or choose No charge.'); return; }
      if (lines.some((l) => !l.description.trim())) { Alert.alert('What for?', 'Every line needs a description — it is what prints on the invoice.'); return; }
      setChargeBusy(true);
      try {
        for (const l of lines) {
          await addCharge(h.id, myUid, h.ownerId, l.description.trim(), parseAmount(l.amount), todayStr, myProfile, l.note.trim());
        }
      } catch {
        setChargeBusy(false);
        Alert.alert('Not billed', 'The charge did not save, so the task has been left open. Please try again.');
        return;
      }
      setChargeBusy(false);
    }
    toggleMilestone(h, item.m.id);
    setCharging(null);
  };

  const openRebook = () => {
    setRebookWeeks('6'); setRebookMonths('');
    // Each horse starts on the shared cycle, so switching to per-horse only
    // means changing the ones that differ.
    setRebookEach(false);
    setRebookPer(Object.fromEntries(dayRows.filter((i) => i.m.completed).map((i) => [i.horse.id, { weeks: '6', months: '' }])));
    setShowRebook(true);
  };
  const offsetDate = (weeks, months) => {
    const w = parseInt(weeks, 10), mo = parseInt(months, 10);
    if (w > 0) { const d = new Date(); d.setDate(d.getDate() + w * 7); return d; }
    if (mo > 0) return addMonthsClamped(new Date(), mo);
    return null;
  };
  // Book the same visit again for everyone finished today, on one date.
  const rebookAll = async () => {
    const done = dayRows.filter((i) => i.m.completed);
    if (done.length === 0) { Alert.alert('Nothing finished yet', 'Tick the horses you have done first.'); return; }
    // One date for everyone, or one per horse when their cycles differ.
    const dateFor = (horseId) => {
      const p = rebookEach ? (rebookPer[horseId] || {}) : { weeks: rebookWeeks, months: rebookMonths };
      const d = offsetDate(p.weeks, p.months);
      return d ? localYmd(d) : null;
    };
    const missing = done.filter((i) => !dateFor(i.horse.id));
    if (missing.length > 0) {
      Alert.alert('How far out?', rebookEach
        ? `Enter weeks or months for ${missing.map((i) => i.who).join(', ')}.`
        : 'Enter a number of weeks or months.');
      return;
    }
    setRebookBusy(true);
    let n = 0;
    const dates = new Set();
    for (const i of done) {
      const h = i.horse;
      const m = i.m;
      const dateStr = dateFor(h.id);
      dates.add(dateStr);
      const next = {
        id: `m_custom_${Date.now()}_r_${h.id}`,
        name: m.name,
        date: dateStr,
        completed: false,
        custom: true,
        category: m.category || 'Farrier',
        isAppointment: true,
        ...(m.remindOnDue && { remindOnDue: true }),
        ...(m.remindDayBefore && { remindDayBefore: true }),
        ...(m.notifyOwnerToo && { notifyOwnerToo: true }),
        assignedToUid: myUid,
        assignedToName: myProfile?.name || auth.currentUser?.displayName || '',
        assignedBy: myUid,
        assignedByName: myProfile?.name || auth.currentUser?.displayName || '',
        createdByUid: myUid,
      };
      const milestones = [...(h.milestones || []), next].sort((a, b) => getMilestoneDate(h, a).localeCompare(getMilestoneDate(h, b)));
      try { await updateHorse(h.id, { milestones }); n += 1; } catch {}
    }
    setRebookBusy(false);
    setShowRebook(false);
    const only = dates.size === 1 ? [...dates][0] : null;
    Alert.alert('Rebooked', only
      ? `${n} horse${n === 1 ? '' : 's'} booked again for ${formatDate(only)}.`
      : `${n} horses booked again, on ${dates.size} different dates.`);
  };

  // Only what they actually changed becomes theirs. A row they left alone
  // goes back in the pot and keeps following their charges, so editing one
  // price does not quietly freeze the other two.
  const saveChips = async () => {
    const rows = chipDraft.filter((c) => c.description.trim() && (parseAmount(c.amount) || 0) > 0);
    const touched = rows.filter((c) => {
      if (c.pinned) return true;                 // already theirs, stays theirs
      if (!c.orig) return true;                  // typed in by hand
      return c.description.trim() !== c.orig.description || parseAmount(c.amount) !== c.orig.amount;
    });
    const pinned = touched.map((c) => ({ description: c.description.trim(), amount: parseAmount(c.amount) })).slice(0, CHIP_COUNT);
    // A learned row they deleted (or blanked) is remembered as unwanted, so it
    // does not walk back in on the next charge.
    const kept = new Set(rows.filter((c) => c.orig).map((c) => chipKey(c.orig.description, c.orig.amount)));
    const dropped = chipOriginals
      .filter((o) => !o.pinned && !kept.has(chipKey(o.description, o.amount)))
      .map((o) => chipKey(o.description, o.amount));
    const hidden = [...new Set([...hiddenChips, ...dropped])]
      .filter((k) => !pinned.some((p) => chipKey(p.description, p.amount) === k));
    try { await saveServiceChips(myUid, pinned, hidden); setShowChipEditor(false); }
    catch { Alert.alert('Not saved', 'Could not save those. Please try again.'); }
  };

  const tkey = (hId, mId) => hId + '|' + mId;
  const toggleSelected = (key) => setSelectedTasks((prev) => { const n = { ...prev }; if (n[key]) delete n[key]; else n[key] = true; return n; });
  const selectedCount = Object.keys(selectedTasks).length;
  const finishSelection = () => { setSelectMode(false); setSelectedTasks({}); };
  // The ticked tasks, grouped by horse so each horse is written once.
  const selectedByHorse = () => {
    const out = [];
    horses.forEach((h) => {
      const ids = (h.milestones || []).filter((m) => selectedTasks[tkey(h.id, m.id)]).map((m) => m.id);
      if (ids.length) out.push({ horse: h, ids });
    });
    return out;
  };

  const bulkComplete = async () => {
    const today = dayKey(new Date());
    let n = 0;
    for (const { horse, ids } of selectedByHorse()) {
      const milestones = (horse.milestones || []).map((m) => {
        if (!ids.includes(m.id) || m.completed) return m;
        if (m.notificationId) cancelReminder(m.notificationId);
        const u = { ...m, completed: true, completedDate: today };
        delete u.notificationId; delete u.reminderAt;
        n += 1;
        return u;
      });
      await updateHorse(horse.id, { milestones });
    }
    finishSelection();
    if (n > 0) Alert.alert('Done', `${n} task${n === 1 ? '' : 's'} marked complete.`);
  };

  const openMove = () => { setMoveDate(new Date()); setMoveInWeeks(''); setMoveInMonths(''); setShowMoveDatePicker(false); setShowMoveModal(true); };
  const setMoveOffset = (weeks, months) => {
    setMoveInWeeks(weeks); setMoveInMonths(months);
    const w = parseInt(weeks, 10), m = parseInt(months, 10);
    if (w > 0) { const d = new Date(); d.setDate(d.getDate() + w * 7); setMoveDate(d); }
    else if (m > 0) setMoveDate(addMonthsClamped(new Date(), m));
  };
  const ymdDiff = (a, b) => Math.round((Date.parse(b + 'T12:00:00') - Date.parse(a + 'T12:00:00')) / 86400000);
  // Move every ticked task to one date. Server reminders follow m.date on
  // their own (the notified stamps are cleared so they fire again); a custom
  // local reminder is shifted by the same number of days, keeping its time.
  const bulkMove = async () => {
    const newStr = dayKey(moveDate);
    setMoving(true);
    let n = 0;
    try {
      for (const { horse, ids } of selectedByHorse()) {
        const updated = [];
        for (const m of horse.milestones || []) {
          if (!ids.includes(m.id)) { updated.push(m); continue; }
          const oldStr = getMilestoneDate(horse, m);
          const u = { ...m, date: newStr };
          delete u.dueNotifiedOn; delete u.dayBeforeNotifiedOn; delete u.ownerDueNotifiedOn; delete u.ownerDayBeforeNotifiedOn;
          if (m.notificationId) { await cancelReminder(m.notificationId); delete u.notificationId; delete u.reminderAt; }
          if (m.reminderAt && oldStr) {
            const fire = new Date(m.reminderAt);
            fire.setDate(fire.getDate() + ymdDiff(oldStr, newStr));
            if (fire > new Date()) {
              const id = await scheduleReminder(m.name, `${displayName(horse)} — reminder`, fire);
              if (id) { u.notificationId = id; u.reminderAt = fire.toISOString(); }
            }
          }
          updated.push(u);
          n += 1;
        }
        updated.sort((a, b) => getMilestoneDate(horse, a).localeCompare(getMilestoneDate(horse, b)));
        await updateHorse(horse.id, { milestones: updated });
      }
      setShowMoveModal(false);
      finishSelection();
      Alert.alert('Moved', `${n} task${n === 1 ? '' : 's'} now on ${formatDate(newStr)}.`);
    } catch (e) {
      Alert.alert('Could not move', 'Some tasks may not have moved. Please check and try again.');
    }
    setMoving(false);
  };

  // One task on the calendar: check it off, or tick it to select. Foal dates
  // and breeding checks show but cannot be ticked (they belong to the plan).
  const renderTaskRow = (it) => {
    const canPick = !!it.m;
    const key = canPick ? tkey(it.horse.id, it.m.id) : it.key;
    const sel = !!selectedTasks[key];
    return (
      <View key={it.key} style={[styles.timelineItem, { alignItems: 'center' }, selectMode && sel && styles.taskRowSel]}>
        {selectMode ? (
          <TouchableOpacity onPress={() => canPick && toggleSelected(key)} activeOpacity={0.7} disabled={!canPick} style={{ marginRight: 12 }}>
            <Text style={{ fontSize: 20, opacity: canPick ? 1 : 0.25 }}>{sel ? '☑️' : '⬜️'}</Text>
          </TouchableOpacity>
        ) : canPick ? (
          <TouchableOpacity onPress={() => toggleMilestone(it.horse, it.m.id)} activeOpacity={0.7} style={{ marginRight: 12 }} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <View style={[styles.circle, it.done && styles.circleDone]}>
              {it.done && <Text style={styles.tick}>✓</Text>}
            </View>
          </TouchableOpacity>
        ) : null}
        <TouchableOpacity style={{ flex: 1 }} onPress={selectMode && canPick ? () => toggleSelected(key) : it.onPress} activeOpacity={0.75}>
          <Text style={styles.tlWho} numberOfLines={1}>{it.who}</Text>
          <Text style={[styles.tlWhat, it.done && { color: MUTED, textDecorationLine: 'line-through' }]}>
            {it.title}{it.plan ? '  ·  breeding plan' : it.m?.isAppointment ? '  ·  appointment' : ''}
          </Text>
          {!!it.m?.notes && <Text style={styles.mNotes} numberOfLines={2}>{it.m.notes}</Text>}
        </TouchableOpacity>
        <View style={{ alignItems: 'flex-end', marginLeft: 8 }}>
          <Text style={styles.tlDate}>{formatDate(it.date)}</Text>
          <Text style={[styles.tlAway, !it.done && it.date < todayStr && { color: RED, fontWeight: '700' }]}>{it.done ? '✓ done' : getDaysLabel(it.date)}</Text>
        </View>
      </View>
    );
  };

  const currentDateLabel = DATE_FILTERS.find((f) => f.key === dateFilter)?.label ?? 'All';
  const currentHorseLabel = horseFilter === 'all'
    ? 'All Horses'
    : displayName(horsesWithPending.find((h) => h.id === horseFilter) || {});

  return (
    <SafeAreaView style={styles.screen} edges={['bottom']}>
      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: selectMode ? 120 : 40 }}>
        <View style={styles.viewToggle}>
          <TouchableOpacity
            style={[styles.viewToggleBtn, viewMode === 'list' && styles.viewToggleBtnOn]}
            onPress={() => setViewMode('list')}
            activeOpacity={0.75}
          >
            <Text style={[styles.viewToggleTxt, viewMode === 'list' && styles.viewToggleTxtOn]}>Tasks</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.viewToggleBtn, viewMode === 'timeline' && styles.viewToggleBtnOn]}
            onPress={() => setViewMode('timeline')}
            activeOpacity={0.75}
          >
            <Text style={[styles.viewToggleTxt, viewMode === 'timeline' && styles.viewToggleTxtOn]}>Timeline</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.viewToggleBtn, viewMode === 'calendar' && styles.viewToggleBtnOn]}
            onPress={() => setViewMode('calendar')}
            activeOpacity={0.75}
          >
            <Text style={[styles.viewToggleTxt, viewMode === 'calendar' && styles.viewToggleTxtOn]}>Calendar</Text>
          </TouchableOpacity>
        </View>

        {viewMode === 'list' && (
          <TouchableOpacity
            style={styles.addMilestoneBtn}
            onPress={openAddMilestoneModal}
            activeOpacity={0.75}
          >
            <Text style={styles.addMilestoneBtnTxt}>+ Add Task</Text>
          </TouchableOpacity>
        )}

        {viewMode === 'list' && (
          <TouchableOpacity
            style={[styles.addMilestoneBtn, { borderStyle: 'solid' }]}
            onPress={() => { setLastCompletedCategory(null); setShowLastCompletedModal(true); }}
            activeOpacity={0.75}
          >
            <Text style={[styles.addMilestoneBtnTxt, { color: TEXT }]}>Show Last Completed</Text>
          </TouchableOpacity>
        )}

        {/* The timeline groups by date, so a date filter on top of it would
            fight the grouping. The horse filter still applies to both. */}
        {viewMode === 'list' && (
        <>
        <Text style={[styles.sectionHeading, { marginTop: 14 }]}>Filter by Date</Text>
        <TouchableOpacity
          style={styles.sortBar}
          onPress={() => { setShowDateMenu((v) => !v); setShowHorseMenu(false); }}
          activeOpacity={0.75}
        >
          <Text style={styles.sortBarLabel}>Date: <Text style={styles.sortBarValue}>{currentDateLabel}</Text></Text>
          <Text style={styles.sortBarChevron}>{showDateMenu ? '▲' : '▼'}</Text>
        </TouchableOpacity>
        {showDateMenu && (
          <View style={styles.sortMenu}>
            {DATE_FILTERS.map((f) => (
              <TouchableOpacity
                key={f.key}
                style={[styles.sortMenuItem, dateFilter === f.key && styles.sortMenuItemActive]}
                onPress={() => { setDateFilter(f.key); setShowDateMenu(false); }}
                activeOpacity={0.75}
              >
                <Text style={[styles.sortMenuItemTxt, dateFilter === f.key && styles.sortMenuItemTxtActive]}>
                  {f.label}
                </Text>
                {dateFilter === f.key && <Text style={styles.sortMenuItemCheck}>✓</Text>}
              </TouchableOpacity>
            ))}
          </View>
        )}

        </>
        )}

        <Text style={[styles.sectionHeading, { marginTop: 14 }]}>Filter by Horse</Text>
        <TouchableOpacity
          style={styles.sortBar}
          onPress={() => { setShowHorseMenu((v) => !v); setShowDateMenu(false); }}
          activeOpacity={0.75}
        >
          <Text style={styles.sortBarLabel}>Horse: <Text style={styles.sortBarValue}>{currentHorseLabel}</Text></Text>
          <Text style={styles.sortBarChevron}>{showHorseMenu ? '▲' : '▼'}</Text>
        </TouchableOpacity>
        {showHorseMenu && (
          <View style={styles.sortMenu}>
            <TouchableOpacity
              style={[styles.sortMenuItem, horseFilter === 'all' && styles.sortMenuItemActive]}
              onPress={() => { setHorseFilter('all'); setShowHorseMenu(false); }}
              activeOpacity={0.75}
            >
              <Text style={[styles.sortMenuItemTxt, horseFilter === 'all' && styles.sortMenuItemTxtActive]}>
                All Horses
              </Text>
              {horseFilter === 'all' && <Text style={styles.sortMenuItemCheck}>✓</Text>}
            </TouchableOpacity>
            {horsesWithPending.map((h) => (
              <TouchableOpacity
                key={h.id}
                style={[styles.sortMenuItem, horseFilter === h.id && styles.sortMenuItemActive]}
                onPress={() => { setHorseFilter(h.id); setShowHorseMenu(false); }}
                activeOpacity={0.75}
              >
                <Text style={[styles.sortMenuItemTxt, horseFilter === h.id && styles.sortMenuItemTxtActive]}>
                  {displayName(h)}
                </Text>
                {horseFilter === h.id && <Text style={styles.sortMenuItemCheck}>✓</Text>}
              </TouchableOpacity>
            ))}
          </View>
        )}

        <TouchableOpacity
          style={styles.recipToggle}
          onPress={() => setShowCompleted((v) => !v)}
          activeOpacity={0.75}
        >
          <View style={[styles.recipToggleTrack, showCompleted && styles.recipToggleTrackOn]}>
            <View style={[styles.recipToggleThumb, showCompleted && styles.recipToggleThumbOn]} />
          </View>
          <Text style={styles.recipToggleLabel}>Show completed</Text>
        </TouchableOpacity>
        {hasClientHorses && (
          <TouchableOpacity
            style={styles.recipToggle}
            onPress={() => setClientOnly((v) => !v)}
            activeOpacity={0.75}
          >
            <View style={[styles.recipToggleTrack, clientOnly && styles.recipToggleTrackOn]}>
              <View style={[styles.recipToggleThumb, clientOnly && styles.recipToggleThumbOn]} />
            </View>
            <Text style={styles.recipToggleLabel}>Client horses only</Text>
          </TouchableOpacity>
        )}

        <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 18, marginBottom: 6 }}>
          <Text style={[styles.sectionHeading, { marginTop: 0, marginBottom: 0, flex: 1 }]}>
            {viewMode === 'calendar'
              ? (calScope === 'today' ? `${todayItems.length} today${overdueItems.length ? ` · ${overdueItems.length} overdue` : ''}` : calScope === 'week' ? `${calWeekCount} this week` : `${calMonthCount} in ${calMonth.toLocaleDateString('en-US', { month: 'long' })}`)
              : `${items.length} Milestone${items.length === 1 ? '' : 's'}`}
          </Text>
          <TouchableOpacity onPress={() => { setSelectMode((v) => !v); setSelectedTasks({}); }} activeOpacity={0.7} hitSlop={{ top: 8, bottom: 8, left: 8 }}>
            <Text style={{ fontSize: 13, color: RED, fontWeight: '700' }}>{selectMode ? 'Cancel' : 'Select multiple tasks'}</Text>
          </TouchableOpacity>
        </View>
        {selectMode && (
          <Text style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>Tick tasks, then move or complete them with the buttons at the bottom.</Text>
        )}

        {viewMode === 'calendar' ? (
          <View>
            <View style={[styles.viewToggle, { marginBottom: 10 }]}>
              {[['today', 'Today'], ['week', 'Week'], ['month', 'Month']].map(([k, label]) => (
                <TouchableOpacity key={k} style={[styles.viewToggleBtn, calScope === k && styles.viewToggleBtnOn]} onPress={() => setCalScope(k)} activeOpacity={0.75}>
                  <Text style={[styles.viewToggleTxt, calScope === k && styles.viewToggleTxtOn]}>{label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {calScope === 'today' ? (
              <View>
                <Text style={[styles.calTitle, { marginBottom: 4 }]}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</Text>

                {isDaySheet && (
                  <View>
                    <View style={{ flexDirection: 'row', gap: 8, marginTop: 10 }}>
                      <View style={{ flex: 1, backgroundColor: CARD, borderRadius: 10, borderWidth: 0.5, borderColor: BORDER, padding: 10 }}>
                        <Text style={{ fontSize: 11, color: MUTED }}>Done</Text>
                        <Text style={{ fontSize: 17, fontWeight: '700', color: TEXT }}>{dayDone} of {dayRows.length}</Text>
                      </View>
                      <View style={{ flex: 1, backgroundColor: CARD, borderRadius: 10, borderWidth: 0.5, borderColor: BORDER, padding: 10 }}>
                        <Text style={{ fontSize: 11, color: MUTED }}>Billed today</Text>
                        <Text style={{ fontSize: 17, fontWeight: '700', color: GREEN }}>{money(billedToday)}</Text>
                      </View>
                    </View>

                    {dayGroups.map((g) => (
                      <View key={g.key} style={{ marginTop: 14 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
                          <Text style={{ flex: 1, fontSize: 13, fontWeight: '700', color: TEXT }} numberOfLines={1}>
                            {g.key} <Text style={{ fontWeight: '400', color: MUTED }}>· {g.rows.length} horse{g.rows.length === 1 ? '' : 's'}</Text>
                          </Text>
                          {!!g.contact?.phone && (
                            <>
                              <TouchableOpacity onPress={() => Linking.openURL(`tel:${g.contact.phone}`)} activeOpacity={0.7} style={{ paddingHorizontal: 8 }}>
                                <Text style={{ fontSize: 15 }}>📞</Text>
                              </TouchableOpacity>
                              <TouchableOpacity onPress={() => Linking.openURL(`sms:${g.contact.phone}`)} activeOpacity={0.7} style={{ paddingHorizontal: 4 }}>
                                <Text style={{ fontSize: 15 }}>💬</Text>
                              </TouchableOpacity>
                            </>
                          )}
                          {!!g.contact?.address && (
                            <TouchableOpacity onPress={() => Linking.openURL(`https://maps.google.com/?q=${encodeURIComponent(g.contact.address)}`)} activeOpacity={0.7} style={{ paddingHorizontal: 6 }}>
                              <Text style={{ fontSize: 15 }}>📍</Text>
                            </TouchableOpacity>
                          )}
                        </View>

                        {g.rows.map((i) => {
                          const open = charging === i.horse.id;
                          const isDone = i.m.completed;
                          return (
                            <View key={i.key} style={[styles.dayRow, open && styles.dayRowOpen]}>
                              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <TouchableOpacity
                                  onPress={() => (isDone ? toggleMilestone(i.horse, i.m.id) : open ? setCharging(null) : openCharge(i.horse.id, i.m.name))}
                                  activeOpacity={0.7}
                                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                                  style={{ marginRight: 10 }}
                                >
                                  <View style={[styles.circle, isDone && styles.circleDone]}>
                                    {isDone && <Text style={styles.tick}>✓</Text>}
                                  </View>
                                </TouchableOpacity>
                                <TouchableOpacity
                                  style={{ flex: 1 }}
                                  onPress={() => (isDone
                                    ? navigation.navigate('Horse', { horseId: i.horse.id })
                                    : open ? setCharging(null) : openCharge(i.horse.id, i.m.name))}
                                  activeOpacity={0.7}
                                >
                                  <Text style={[styles.dayName, isDone && { color: MUTED, textDecorationLine: 'line-through' }]} numberOfLines={1}>{i.who}</Text>
                                  <Text style={styles.denseSub} numberOfLines={1}>{i.title}{i.m.notes ? `  ·  ${i.m.notes}` : ''}</Text>
                                </TouchableOpacity>
                                {open && !isDone && (
                                  <TouchableOpacity
                                    onPress={() => navigation.navigate('Horse', { horseId: i.horse.id })}
                                    activeOpacity={0.7}
                                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                                    style={{ marginLeft: 8 }}
                                  >
                                    <Text style={{ fontSize: 12, color: GREEN, fontWeight: '700' }}>View horse ›</Text>
                                  </TouchableOpacity>
                                )}
                                {isDone && (() => {
                                  const billed = myLedger
                                    .filter((e) => e.type === 'charge' && !e.deleted && e.date === todayStr && e.horseId === i.horse.id)
                                    .reduce((s, e) => s + (Number(e.amount) || 0), 0);
                                  return (
                                    <Text style={{ fontSize: 13, fontWeight: '700', color: billed > 0 ? GREEN : MUTED, marginLeft: 8 }}>
                                      {billed > 0 ? money(billed) : 'No charge'}
                                    </Text>
                                  );
                                })()}
                              </View>

                              {open && !isDone && (
                                <View style={{ marginTop: 10, paddingLeft: 32 }}>
                                  {chips.length > 0 && (
                                    <>
                                      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
                                        <Text style={{ flex: 1, fontSize: 11, color: MUTED }}>You usually charge</Text>
                                        <TouchableOpacity
                                          onPress={() => {
                                            setChipOriginals(chips.map((c) => ({ description: c.description, amount: c.amount, pinned: !!c.pinned })));
                                            setChipDraft(chips.map((c) => ({ description: c.description, amount: String(c.amount), pinned: !!c.pinned, orig: { description: c.description, amount: c.amount, pinned: !!c.pinned } })));
                                            setShowChipEditor(true);
                                          }}
                                          activeOpacity={0.7}
                                          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                                        >
                                          <Text style={{ fontSize: 11, color: GREEN, fontWeight: '700' }}>Edit</Text>
                                        </TouchableOpacity>
                                      </View>
                                      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                                        {chips.map((c) => (
                                          <TouchableOpacity
                                            key={c.description + c.amount}
                                            style={styles.categoryChip}
                                            onPress={() => setLine(0, { description: c.description, amount: String(c.amount) })}
                                            activeOpacity={0.75}
                                          >
                                            <Text style={[styles.categoryChipTxt, { maxWidth: 150 }]} numberOfLines={1}>{c.description} {money(c.amount)}</Text>
                                          </TouchableOpacity>
                                        ))}
                                      </View>
                                    </>
                                  )}

                                  {chargeLines.map((l, idx) => (
                                    <View key={idx} style={{ marginBottom: 8 }}>
                                      <View style={{ flexDirection: 'row', gap: 8 }}>
                                        <TextInput
                                          style={[styles.input, { width: 96, marginBottom: 0 }]}
                                          placeholder="Amount" placeholderTextColor="#BDBDBD" keyboardType="decimal-pad"
                                          value={l.amount} onChangeText={(v) => setLine(idx, { amount: v })}
                                        />
                                        <TextInput
                                          style={[styles.input, { flex: 1, marginBottom: 0 }]}
                                          placeholder="What for" placeholderTextColor="#BDBDBD"
                                          value={l.description} onChangeText={(v) => setLine(idx, { description: v })}
                                        />
                                      </View>
                                      <TextInput
                                        style={[styles.input, { marginTop: 6, marginBottom: 0 }]}
                                        placeholder="Note for the owner (optional)" placeholderTextColor="#BDBDBD"
                                        value={l.note} onChangeText={(v) => setLine(idx, { note: v })}
                                      />
                                      {chargeLines.length > 1 && (
                                        <TouchableOpacity onPress={() => setChargeLines((prev) => prev.filter((_, x) => x !== idx))} activeOpacity={0.7} style={{ paddingVertical: 4 }}>
                                          <Text style={{ fontSize: 11, color: RED }}>Remove this line</Text>
                                        </TouchableOpacity>
                                      )}
                                    </View>
                                  ))}
                                  <TouchableOpacity onPress={() => setChargeLines((prev) => [...prev, { amount: '', description: '', note: '' }])} activeOpacity={0.7} style={{ paddingVertical: 4, marginBottom: 6 }}>
                                    <Text style={{ fontSize: 12, color: GREEN, fontWeight: '600' }}>+ Add another line</Text>
                                  </TouchableOpacity>

                                  <View style={{ flexDirection: 'row', gap: 8 }}>
                                    <TouchableOpacity style={[styles.saveBtn, { flex: 1 }]} onPress={() => finishHorse(i, true)} disabled={chargeBusy} activeOpacity={0.85}>
                                      {chargeBusy ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>Done · add {money(chargeTotal)}</Text>}
                                    </TouchableOpacity>
                                    <TouchableOpacity style={styles.cancelBtn} onPress={() => finishHorse(i, false)} disabled={chargeBusy} activeOpacity={0.85}>
                                      <Text style={styles.cancelBtnText}>No charge</Text>
                                    </TouchableOpacity>
                                  </View>
                                </View>
                              )}
                            </View>
                          );
                        })}
                      </View>
                    ))}

                    <View style={styles.dayFooter}>
                      <Text style={{ fontSize: 13, color: TEXT, fontWeight: '600', marginBottom: 8 }}>
                        {dayDone === dayRows.length
                          ? `Day finished · ${money(billedToday)} billed`
                          : `${dayRows.length - dayDone} still to do · ${money(billedToday)} billed`}
                      </Text>
                      <TouchableOpacity
                        style={[styles.saveBtn, dayDone === 0 && { opacity: 0.4 }]}
                        onPress={openRebook}
                        disabled={dayDone === 0}
                        activeOpacity={0.85}
                      >
                        <Text style={styles.saveBtnText}>
                          {dayDone === 0 ? 'Rebook — tick some off first' : `Rebook the ${dayDone} finished`}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                )}

                {overdueItems.length > 0 && (
                  <>
                    <Text style={[styles.sectionHeading, { marginTop: 12, color: RED }]}>Overdue ({overdueItems.length})</Text>
                    {overdueItems.map(renderTaskRow)}
                  </>
                )}
                {(() => {
                  const rest = isDaySheet ? todayItems.filter((i) => !dayKeys.has(i.key)) : todayItems;
                  return (
                    <>
                      <Text style={[styles.sectionHeading, { marginTop: 12 }]}>
                        {isDaySheet ? `Also today (${rest.length})` : `Today (${rest.length})`}
                      </Text>
                      {rest.length === 0 ? (
                        <Text style={{ color: MUTED, fontSize: 13, paddingVertical: 10 }}>{isDaySheet ? 'Nothing else today.' : 'Nothing due today.'}</Text>
                      ) : rest.map(renderTaskRow)}
                    </>
                  );
                })()}
              </View>
            ) : calScope === 'week' ? (
              <View>
                <View style={styles.calHeader}>
                  <TouchableOpacity onPress={() => setCalWeekStart((d) => { const x = new Date(d); x.setDate(x.getDate() - 7); return x; })} activeOpacity={0.7} style={styles.calNav}>
                    <Text style={styles.calNavTxt}>‹</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => { const d = new Date(); d.setHours(12, 0, 0, 0); d.setDate(d.getDate() - d.getDay()); setCalWeekStart(d); }} activeOpacity={0.7}>
                    <Text style={styles.calTitle}>{calWeekLabel}{isThisWeek ? '' : '  ·  today'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setCalWeekStart((d) => { const x = new Date(d); x.setDate(x.getDate() + 7); return x; })} activeOpacity={0.7} style={styles.calNav}>
                    <Text style={styles.calNavTxt}>›</Text>
                  </TouchableOpacity>
                </View>
                {/* A planner: each day is a row, its tasks stacked under it. */}
                <View style={styles.calGrid}>
                  {calWeekDays.map((d, i) => {
                    const list = calendarByDay[d] || [];
                    const day = new Date(d + 'T12:00:00');
                    const today = d === todayStr;
                    const past = d < todayStr;
                    return (
                      <View key={d} style={[styles.wkRow, i > 0 && styles.calRowLine, today && styles.wkRowToday]}>
                        <View style={styles.wkDay}>
                          <Text style={[styles.wkDow, today && { color: RED }]}>{day.toLocaleDateString('en-US', { weekday: 'short' })}</Text>
                          <View style={[styles.calDayWrap, today && styles.calDaySel]}>
                            <Text style={[styles.calDayTxt, today && styles.calDayTxtSel]}>{day.getDate()}</Text>
                          </View>
                        </View>
                        <View style={{ flex: 1, paddingVertical: 4 }}>
                          {list.length === 0 ? (
                            <Text style={styles.wkEmpty}>—</Text>
                          ) : list.map((it) => (
                            <TouchableOpacity key={it.key} style={styles.wkItem} onPress={selectMode && it.m ? () => toggleSelected(tkey(it.horse.id, it.m.id)) : it.onPress} activeOpacity={0.75}>
                              {selectMode && it.m ? (
                                <Text style={{ fontSize: 16, marginTop: -1 }}>{selectedTasks[tkey(it.horse.id, it.m.id)] ? '☑️' : '⬜️'}</Text>
                              ) : (
                                <View style={[styles.wkDot, it.done ? { backgroundColor: MUTED } : past ? { backgroundColor: RED } : null]} />
                              )}
                              <View style={{ flex: 1 }}>
                                <Text style={[styles.wkItemTitle, it.done && { color: MUTED, textDecorationLine: 'line-through' }]} numberOfLines={2}>{it.title}</Text>
                                <Text style={styles.wkItemWho} numberOfLines={1}>{it.who}{it.plan ? '  ·  breeding plan' : ''}</Text>
                              </View>
                            </TouchableOpacity>
                          ))}
                        </View>
                      </View>
                    );
                  })}
                </View>
              </View>
            ) : (
            <>
            <View style={styles.calHeader}>
              <TouchableOpacity onPress={() => setCalMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))} activeOpacity={0.7} style={styles.calNav}>
                <Text style={styles.calNavTxt}>‹</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => { const d = new Date(); setCalMonth(new Date(d.getFullYear(), d.getMonth(), 1)); setCalDay(todayStr); }} activeOpacity={0.7}>
                <Text style={styles.calTitle}>{calMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setCalMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))} activeOpacity={0.7} style={styles.calNav}>
                <Text style={styles.calNavTxt}>›</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.calGrid}>
              <View style={styles.calRow}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                  <View key={d} style={styles.calCell}><Text style={styles.calDow}>{d}</Text></View>
                ))}
              </View>
              {calCells.map((week, w) => (
                <View key={w} style={[styles.calRow, styles.calRowLine]}>
                  {week.map((d, i) => {
                    if (!d) return <View key={'e' + i} style={styles.calCell} />;
                    const list = calendarByDay[d] || [];
                    const open = list.filter((x) => !x.done).length;
                    const overdue = open > 0 && d < todayStr;
                    const sel = d === calDay;
                    return (
                      <TouchableOpacity key={d} style={styles.calCell} onPress={() => setCalDay(d)} activeOpacity={0.7}>
                        <View style={[styles.calDayWrap, d === todayStr && styles.calDayToday, sel && styles.calDaySel]}>
                          <Text style={[styles.calDayTxt, sel && styles.calDayTxtSel]}>{parseInt(d.slice(8), 10)}</Text>
                        </View>
                        {list.length > 0 ? (
                          <View style={[styles.calBadge, overdue && styles.calBadgeOverdue, open === 0 && styles.calBadgeDone]}>
                            <Text style={styles.calBadgeTxt}>{list.length}</Text>
                          </View>
                        ) : <View style={{ height: 18 }} />}
                      </TouchableOpacity>
                    );
                  })}
                </View>
              ))}
            </View>

            <Text style={[styles.sectionHeading, { marginTop: 14 }]}>
              {formatDate(calDay)}{calDay === todayStr ? ' · Today' : ''}
            </Text>
            {(calendarByDay[calDay] || []).length === 0 ? (
              <Text style={{ color: MUTED, fontSize: 13, paddingVertical: 10 }}>Nothing on this day.</Text>
            ) : (calendarByDay[calDay] || []).map(renderTaskRow)}
            </>
            )}
          </View>
        ) : viewMode === 'timeline' ? (
          timelineGroups.length === 0 ? (
            <View style={[styles.emptyWrap, { paddingTop: 40 }]}>
              <Text style={styles.emptyIcon}>📅</Text>
              <Text style={styles.emptyTitle}>Nothing upcoming</Text>
              <Text style={styles.emptySub}>Tasks, foal dates and breeding checks will appear here.</Text>
            </View>
          ) : (
            timelineGroups.map((g) => (
              <View key={g.label}>
                <Text style={[styles.sectionHeading, { marginTop: 14 }]}>{g.label}</Text>
                {g.items.map((i) => (
                  <TouchableOpacity
                    key={i.key}
                    style={styles.timelineItem}
                    onPress={i.onPress}
                    activeOpacity={0.75}
                  >
                    <View style={{ flex: 1 }}>
                      <Text style={styles.tlWho} numberOfLines={1}>{i.who}</Text>
                      <Text style={styles.tlWhat}>
                        {i.title}
                        {i.plan ? '  ·  breeding plan' : ''}
                      </Text>
                    </View>
                    <View style={{ alignItems: 'flex-end' }}>
                      <Text style={styles.tlDate}>{formatDate(i.date)}</Text>
                      <Text style={styles.tlAway}>{getDaysLabel(i.date)}</Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            ))
          )
        ) : items.length === 0 ? (
          <View style={[styles.emptyWrap, { paddingTop: 40 }]}>
            <Text style={styles.emptyIcon}>✅</Text>
            <Text style={styles.emptyTitle}>Nothing here</Text>
            <Text style={styles.emptySub}>No milestones match these filters.</Text>
          </View>
        ) : (
          items.map(({ horse, milestone: m, mDate }) => (
            <View
              key={`${horse.id}_${m.id}`}
              style={[styles.milestone, m.completed && styles.milestoneDone]}
            >
              {selectMode ? (
                <TouchableOpacity onPress={() => toggleSelected(tkey(horse.id, m.id))} activeOpacity={0.7}>
                  <Text style={{ fontSize: 20 }}>{selectedTasks[tkey(horse.id, m.id)] ? '☑️' : '⬜️'}</Text>
                </TouchableOpacity>
              ) : (
                <View style={[styles.circle, m.completed && styles.circleDone]}>
                  {m.completed && <Text style={styles.tick}>✓</Text>}
                </View>
              )}
              <TouchableOpacity
                style={{ flex: 1, marginHorizontal: 12 }}
                onPress={() => (selectMode ? toggleSelected(tkey(horse.id, m.id)) : navigation.navigate('Horse', { horseId: horse.id }))}
                activeOpacity={0.75}
              >
                <Text style={styles.mHorseName}>{displayName(horse)}</Text>
                {!!m.category && (
                  <View style={styles.categoryBadge}>
                    <Text style={styles.categoryBadgeTxt}>{m.category}</Text>
                  </View>
                )}
                <Text style={[styles.mName, m.completed && styles.mNameDone]}>
                  {m.name}{m.isAppointment ? '  ·  appointment' : ''}
                </Text>
                <Text style={styles.mDate}>{m.completed ? `✓ Completed ${formatDate(m.completedDate || mDate)}` : formatDate(mDate)}</Text>
                {!m.completed && (
                  <Text style={styles.mDays}>{getDaysLabel(mDate)}</Text>
                )}
                {!!m.notes && (
                  <Text style={styles.mNotes}>{m.notes}</Text>
                )}
              </TouchableOpacity>
              <View style={{ alignItems: 'center', gap: 8 }}>
                {m.custom && (
                  <TouchableOpacity onPress={() => openEditMilestone(horse, m)} activeOpacity={0.7}>
                    <Text style={{ fontSize: 15 }}>✏️</Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity
                  style={[styles.markBtn, m.completed && styles.markBtnDone]}
                  onPress={() => toggleMilestone(horse, m.id)}
                  activeOpacity={0.75}
                >
                  <Text style={[styles.markBtnTxt, m.completed && styles.markBtnTxtDone]}>
                    {m.completed ? 'Undo' : 'Mark\nComplete'}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          ))
        )}
      </ScrollView>

      {selectMode && (
        <View style={styles.bulkBar}>
          <Text style={styles.bulkBarTxt}>{selectedCount} selected</Text>
          <TouchableOpacity style={[styles.bulkBtn, selectedCount === 0 && { opacity: 0.4 }]} disabled={selectedCount === 0} onPress={openMove} activeOpacity={0.8}>
            <Text style={styles.bulkBtnTxt}>📅 Move date</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.bulkBtn, styles.bulkBtnGreen, selectedCount === 0 && { opacity: 0.4 }]} disabled={selectedCount === 0} onPress={bulkComplete} activeOpacity={0.8}>
            <Text style={styles.bulkBtnTxt}>✓ Complete</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Book today's finished horses in again */}
      <Modal visible={showRebook} animationType="slide" transparent onRequestClose={() => setShowRebook(false)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '85%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false} style={{ flexShrink: 1 }}>
            <Text style={styles.sheetTitle}>Rebook {dayDone} horse{dayDone === 1 ? '' : 's'}</Text>
            <Text style={{ fontSize: 12, color: MUTED, marginBottom: 6 }}>Books the same visit again for everyone you finished today. Their reminders come along.</Text>

            {dayDone > 1 && (
              <TouchableOpacity onPress={() => setRebookEach((v) => !v)} activeOpacity={0.75} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, paddingVertical: 8 }}>
                <Text style={{ fontSize: 18 }}>{rebookEach ? '☑️' : '⬜️'}</Text>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, fontWeight: '600', color: TEXT }}>A different cycle for each horse</Text>
                  <Text style={{ fontSize: 12, color: MUTED }}>Off = they all come back on the same day. On = set each one, for horses on different schedules.</Text>
                </View>
              </TouchableOpacity>
            )}

            {rebookEach ? (
              dayRows.filter((i) => i.m.completed).map((i) => {
                const p = rebookPer[i.horse.id] || {};
                const when = offsetDate(p.weeks, p.months);
                return (
                  <View key={i.horse.id} style={{ paddingVertical: 8, borderBottomWidth: 0.5, borderBottomColor: BORDER }}>
                    <Text style={{ fontSize: 14, fontWeight: '700', color: TEXT }}>{i.who}</Text>
                    <OffsetInputs
                      caption={when ? formatDate(localYmd(when)) : 'In…'}
                      weeks={p.weeks || ''} months={p.months || ''}
                      onWeeks={(v) => setRebookPer((prev) => ({ ...prev, [i.horse.id]: { weeks: v, months: '' } }))}
                      onMonths={(v) => setRebookPer((prev) => ({ ...prev, [i.horse.id]: { weeks: '', months: v } }))}
                    />
                  </View>
                );
              })
            ) : (
              <OffsetInputs
                caption={(() => { const d = offsetDate(rebookWeeks, rebookMonths); return d ? `From today · ${formatDate(localYmd(d))}` : 'From today, in…'; })()}
                weeks={rebookWeeks} months={rebookMonths}
                onWeeks={(v) => { setRebookWeeks(v); setRebookMonths(''); }}
                onMonths={(v) => { setRebookMonths(v); setRebookWeeks(''); }}
              />
            )}
            <View style={[styles.sheetBtns, { marginTop: 16 }]}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowRebook(false)} activeOpacity={0.85}><Text style={styles.cancelBtnText}>Cancel</Text></TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={rebookAll} disabled={rebookBusy} activeOpacity={0.85}>
                {rebookBusy ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>Rebook</Text>}
              </TouchableOpacity>
            </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* The service chips, once someone wants them their way */}
      <Modal visible={showChipEditor} animationType="slide" transparent onRequestClose={() => setShowChipEditor(false)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '85%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
              <Text style={styles.sheetTitle}>Your services</Text>
              <Text style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>
                Up to {CHIP_COUNT} shortcuts, following what you have been charging. Change one and it stays as you set it — the ones you leave alone keep following your charges.
              </Text>
              {chipDraft.map((c, idx) => (
                <View key={idx} style={{ flexDirection: 'row', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <TextInput
                    style={[styles.input, { flex: 1, marginBottom: 0 }]}
                    placeholder="What for" placeholderTextColor="#BDBDBD"
                    value={c.description} onChangeText={(v) => setChipDraft((prev) => prev.map((x, i) => (i === idx ? { ...x, description: v } : x)))}
                  />
                  <TextInput
                    style={[styles.input, { width: 92, marginBottom: 0 }]}
                    placeholder="Amount" placeholderTextColor="#BDBDBD" keyboardType="decimal-pad"
                    value={c.amount} onChangeText={(v) => setChipDraft((prev) => prev.map((x, i) => (i === idx ? { ...x, amount: v } : x)))}
                  />
                  <TouchableOpacity onPress={() => setChipDraft((prev) => prev.filter((_, i) => i !== idx))} activeOpacity={0.7} hitSlop={{ top: 8, bottom: 8 }}>
                    <Text style={{ fontSize: 16, color: MUTED }}>✕</Text>
                  </TouchableOpacity>
                </View>
              ))}
              {chipDraft.length < CHIP_COUNT ? (
                <TouchableOpacity onPress={() => setChipDraft((prev) => [...prev, { description: '', amount: '', pinned: true, orig: null }])} activeOpacity={0.7} style={{ paddingVertical: 6 }}>
                  <Text style={{ fontSize: 13, color: GREEN, fontWeight: '600' }}>+ Add a service</Text>
                </TouchableOpacity>
              ) : (
                <Text style={{ fontSize: 12, color: MUTED, paddingVertical: 6 }}>
                  {CHIP_COUNT} is the most that fits on one row. Remove one to add another — anything else you charge is still a quick type.
                </Text>
              )}
              <View style={[styles.sheetBtns, { marginTop: 14 }]}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowChipEditor(false)} activeOpacity={0.85}><Text style={styles.cancelBtnText}>Cancel</Text></TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={saveChips} activeOpacity={0.85}><Text style={styles.saveBtnText}>Save</Text></TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* Move the ticked tasks to one new date */}
      <Modal visible={showMoveModal} animationType="slide" transparent onRequestClose={() => setShowMoveModal(false)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.sheet}>
            <Text style={styles.sheetTitle}>Move {selectedCount} task{selectedCount === 1 ? '' : 's'}</Text>
            <Text style={styles.fieldLabel}>New date</Text>
            <TouchableOpacity style={styles.datePicker} onPress={() => setShowMoveDatePicker(true)} activeOpacity={0.75}>
              <Text style={styles.datePickerText}>{moveDate.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })}</Text>
              <Text>📅</Text>
            </TouchableOpacity>
            {showMoveDatePicker && (
              <DateTimePicker value={moveDate} mode="date" display={Platform.OS === 'ios' ? 'spinner' : 'default'} onChange={(e, d) => { if (Platform.OS === 'android') setShowMoveDatePicker(false); if (d) { setMoveDate(d); setMoveInWeeks(''); setMoveInMonths(''); } }} />
            )}
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 6 }}>
              {[['Today', 0], ['Tomorrow', 1]].map(([label, days]) => (
                <TouchableOpacity key={label} style={styles.categoryChip} onPress={() => { const d = new Date(); d.setDate(d.getDate() + days); setMoveDate(d); setMoveInWeeks(''); setMoveInMonths(''); }} activeOpacity={0.75}>
                  <Text style={styles.categoryChipTxt}>{label}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <OffsetInputs
              caption="Or from today, in…"
              weeks={moveInWeeks} months={moveInMonths}
              onWeeks={(v) => setMoveOffset(v, '')}
              onMonths={(v) => setMoveOffset('', v)}
            />
            <Text style={{ fontSize: 12, color: MUTED, marginTop: 10, lineHeight: 17 }}>
              Reminders follow the new date. A reminder set for a specific time keeps that time of day.
            </Text>
            <View style={[styles.sheetBtns, { marginTop: 16 }]}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowMoveModal(false)} activeOpacity={0.85}><Text style={styles.cancelBtnText}>Cancel</Text></TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={bulkMove} disabled={moving} activeOpacity={0.85}>
                {moving ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>Move</Text>}
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── Add Milestone Modal ── */}
      <Modal visible={showAddMilestoneModal} animationType="slide" transparent>
        <KeyboardAvoidingView
          style={styles.overlay}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <View style={[styles.sheet, { maxHeight: '90%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
            <Text style={styles.sheetTitle}>{editingMilestone ? 'Edit Task' : 'Add Task'}</Text>
            <Text style={styles.fieldLabel}>Name</Text>
            <TextInput
              style={styles.input}
              placeholder="e.g. Farrier visit"
              placeholderTextColor="#BDBDBD"
              value={milestoneName}
              onChangeText={setMilestoneName}
              autoFocus
            />

            {!editingMilestone && (
              <TouchableOpacity onPress={() => setMilestoneAddAsComplete((v) => !v)} activeOpacity={0.75} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginTop: 6, marginBottom: 6, paddingVertical: 4 }}>
                <Text style={{ fontSize: 18 }}>{milestoneAddAsComplete ? '☑️' : '⬜️'}</Text>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, fontWeight: '600', color: TEXT }}>Mark as already done</Text>
                  <Text style={{ fontSize: 12, color: MUTED }}>Logs a completed task using the date below as the completed date — no reminders. Handy for something you just finished (e.g. dewormed the herd).</Text>
                </View>
              </TouchableOpacity>
            )}

            <Text style={styles.fieldLabel}>Notes (optional)</Text>
            <TextInput
              style={[styles.input, { minHeight: 70 }]}
              placeholder="Add any details for this task..."
              placeholderTextColor="#BDBDBD"
              value={milestoneNotes}
              onChangeText={setMilestoneNotes}
              multiline
              textAlignVertical="top"
            />
            <Text style={styles.fieldLabel}>Due Date</Text>
            <TouchableOpacity
              style={styles.datePicker}
              onPress={() => setShowMilestoneDatePicker(true)}
            >
              <Text style={styles.datePickerText}>
                {milestoneDate.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Text>
              <Text>📅</Text>
            </TouchableOpacity>
            {showMilestoneDatePicker && (
              <DateTimePicker
                value={milestoneDate}
                mode="date"
                display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                onChange={(e, d) => {
                  if (Platform.OS === 'android') setShowMilestoneDatePicker(false);
                  if (d) setMilestoneDate(d);
                }}
              />
            )}

            <Text style={[styles.fieldLabel, { marginTop: 18 }]}>Category (optional)</Text>
            <CategoryPicker
              customCategories={customCategories}
              hiddenPresets={hiddenCategories}
              value={milestoneCategory}
              onSelect={setMilestoneCategory}
              otherText={milestoneCategoryOther}
              onOtherTextChange={setMilestoneCategoryOther}
            />

            {!editingMilestone && (
              <>
                <Text style={[styles.fieldLabel, { marginTop: 18 }]}>Apply To</Text>
                {horses.length === 0 ? (
                  <Text style={styles.emptySub}>No horses yet.</Text>
                ) : (
                  <View>
                    {horses.map((h) => {
                      const checked = milestoneHorseIds.includes(h.id);
                      return (
                        <TouchableOpacity
                          key={h.id}
                          style={styles.recipToggle}
                          onPress={() => toggleMilestoneHorse(h.id)}
                          activeOpacity={0.75}
                        >
                          <View style={[styles.radioOuter, checked && styles.radioOuterSelected, { borderRadius: 4 }]}>
                            {checked && <View style={[styles.radioInner, { width: 14, height: 14, borderRadius: 3 }]} />}
                          </View>
                          <Text style={styles.recipToggleLabel}>{displayName(h)}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                )}
              </>
            )}

            <Text style={[styles.fieldLabel, { marginTop: 18 }]}>Reminder</Text>
            <TouchableOpacity
              style={styles.recipToggle}
              onPress={() => setReminderEnabled((v) => !v)}
              activeOpacity={0.75}
            >
              <View style={[styles.recipToggleTrack, reminderEnabled && styles.recipToggleTrackOn]}>
                <View style={[styles.recipToggleThumb, reminderEnabled && styles.recipToggleThumbOn]} />
              </View>
              <Text style={styles.recipToggleLabel}>
                {reminderEnabled ? 'Yes — send a reminder' : 'No'}
              </Text>
            </TouchableOpacity>

            {reminderEnabled && (
              <>
                <Text style={styles.fieldLabel}>When</Text>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <TouchableOpacity
                    style={[styles.recipToggle, { flex: 1 }]}
                    onPress={() => setReminderMode('specified')}
                    activeOpacity={0.75}
                  >
                    <View style={[styles.radioOuter, reminderMode === 'specified' && styles.radioOuterSelected]}>
                      {reminderMode === 'specified' && <View style={styles.radioInner} />}
                    </View>
                    <Text style={styles.recipToggleLabel}>On milestone date (9:00 AM)</Text>
                  </TouchableOpacity>
                </View>
                <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
                  <TouchableOpacity
                    style={[styles.recipToggle, { flex: 1 }]}
                    onPress={() => setReminderMode('custom')}
                    activeOpacity={0.75}
                  >
                    <View style={[styles.radioOuter, reminderMode === 'custom' && styles.radioOuterSelected]}>
                      {reminderMode === 'custom' && <View style={styles.radioInner} />}
                    </View>
                    <Text style={styles.recipToggleLabel}>Custom date & time</Text>
                  </TouchableOpacity>
                </View>

                {/* Repeats ride on the server-side due-date push, so they are only
                    offered for the "on milestone date" reminder. */}
                {reminderMode === 'specified' && (
                  <>
                    <Text style={[styles.fieldLabel, { marginTop: 18 }]}>Repeat</Text>
                    <TouchableOpacity
                      style={styles.recipToggle}
                      onPress={() => setReminderRepeat((v) => !v)}
                      activeOpacity={0.75}
                    >
                      <View style={[styles.recipToggleTrack, reminderRepeat && styles.recipToggleTrackOn]}>
                        <View style={[styles.recipToggleThumb, reminderRepeat && styles.recipToggleThumbOn]} />
                      </View>
                      <Text style={styles.recipToggleLabel}>
                        {reminderRepeat ? 'Remind every morning until done' : 'Remind once'}
                      </Text>
                    </TouchableOpacity>
                    {reminderRepeat && (
                      <Text style={{ color: MUTED, fontSize: 12, marginTop: 6, lineHeight: 17 }}>
                        Stops as soon as the task is marked complete, or after 14 days overdue.
                      </Text>
                    )}
                    <Text style={[styles.fieldLabel, { marginTop: 18 }]}>Heads-up</Text>
                    <TouchableOpacity
                      style={styles.recipToggle}
                      onPress={() => setReminderDayBefore((v) => !v)}
                      activeOpacity={0.75}
                    >
                      <View style={[styles.recipToggleTrack, reminderDayBefore && styles.recipToggleTrackOn]}>
                        <View style={[styles.recipToggleThumb, reminderDayBefore && styles.recipToggleThumbOn]} />
                      </View>
                      <Text style={styles.recipToggleLabel}>
                        {reminderDayBefore ? 'Also remind me the day before' : 'No day-before reminder'}
                      </Text>
                    </TouchableOpacity>
                  </>
                )}
              </>
            )}

            {reminderEnabled && reminderMode === 'custom' && (
              <>
                <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Reminder Date & Time</Text>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <TouchableOpacity
                    style={[styles.datePicker, { flex: 1 }]}
                    onPress={() => setShowReminderDatePicker(true)}
                  >
                    <Text style={styles.datePickerText}>
                      {reminderDate.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </Text>
                    <Text>📅</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.datePicker, { flex: 1 }]}
                    onPress={() => setShowReminderTimePicker(true)}
                  >
                    <Text style={styles.datePickerText}>
                      {reminderDate.toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </Text>
                    <Text>⏰</Text>
                  </TouchableOpacity>
                </View>
                {showReminderDatePicker && (
                  <DateTimePicker
                    value={reminderDate}
                    mode="date"
                    display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                    onChange={(e, d) => {
                      if (Platform.OS === 'android') setShowReminderDatePicker(false);
                      if (d) {
                        setReminderDate((prev) => {
                          const next = new Date(prev);
                          next.setFullYear(d.getFullYear(), d.getMonth(), d.getDate());
                          return next;
                        });
                      }
                    }}
                  />
                )}
                {showReminderTimePicker && (
                  <DateTimePicker
                    value={reminderDate}
                    mode="time"
                    display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                    onChange={(e, d) => {
                      if (Platform.OS === 'android') setShowReminderTimePicker(false);
                      if (d) {
                        setReminderDate((prev) => {
                          const next = new Date(prev);
                          next.setHours(d.getHours(), d.getMinutes(), 0, 0);
                          return next;
                        });
                      }
                    }}
                  />
                )}
              </>
            )}

            <View style={styles.sheetBtns}>
              <TouchableOpacity
                style={styles.cancelBtn}
                onPress={() => { setEditingMilestone(null); setShowAddMilestoneModal(false); }}
              >
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleAddMilestone}>
                <Text style={styles.saveBtnText}>{editingMilestone ? 'Save' : 'Add'}</Text>
              </TouchableOpacity>
            </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── Show Last Completed Modal ── */}
      <Modal visible={showLastCompletedModal} animationType="slide" transparent>
        <KeyboardAvoidingView
          style={styles.overlay}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <View style={styles.sheet}>
            <Text style={styles.sheetTitle}>Show Last Completed</Text>

            {lastCompletedOptions.length === 0 ? (
              <Text style={styles.emptySub}>No completed milestones yet.</Text>
            ) : (
              <>
                <Text style={styles.fieldLabel}>Category</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                  {lastCompletedOptions.map((key) => (
                    <TouchableOpacity
                      key={key}
                      style={[styles.categoryChip, lastCompletedCategory === key && styles.categoryChipActive]}
                      onPress={() => setLastCompletedCategory(lastCompletedCategory === key ? null : key)}
                      activeOpacity={0.75}
                    >
                      <Text style={[styles.categoryChipTxt, lastCompletedCategory === key && styles.categoryChipTxtActive]}>
                        {key}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>

                {lastCompletedCategory && (
                  <ScrollView style={{ maxHeight: 300, marginTop: 14 }}>
                    {lastCompletedResults.length === 0 ? (
                      <Text style={styles.emptySub}>No horse has completed "{lastCompletedCategory}" yet.</Text>
                    ) : (
                      lastCompletedResults.map(({ horse, mDate }) => (
                        <View key={horse.id} style={[styles.milestone, styles.milestoneDone]}>
                          <View style={[styles.circle, styles.circleDone]}>
                            <Text style={styles.tick}>✓</Text>
                          </View>
                          <View style={{ flex: 1, marginHorizontal: 12 }}>
                            <Text style={styles.mName}>{displayName(horse)}</Text>
                            <Text style={styles.mDate}>Last: {formatDate(mDate)}</Text>
                          </View>
                        </View>
                      ))
                    )}
                  </ScrollView>
                )}
              </>
            )}

            <View style={styles.sheetBtns}>
              <TouchableOpacity
                style={styles.saveBtn}
                onPress={() => setShowLastCompletedModal(false)}
              >
                <Text style={styles.saveBtnText}>Close</Text>
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </SafeAreaView>
  );
}

// ─── TIMELINE SCREEN ──────────────────────────────────────────────────────────

// Month calendar of health incidents — each incident is a colored bar (by type)
// running from occurred → resolved; ongoing ones run dashed to today. Lanes are
// packed so non-overlapping incidents share a row; overflow past 4 shows "+N".
// `incidents` should already be tag-filtered by the caller.
function IncidentCalendar({ incidents, horseNames, onIncidentPress }) {
  const [monthOffset, setMonthOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const MAX_LANES = 4;

  const now = new Date();
  const view = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const year = view.getFullYear();
  const month = view.getMonth();
  const todayStr = new Date().toLocaleDateString('en-CA');
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const iso = (dd) => `${year}-${String(month + 1).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
  const endOf = (inc) => (inc.status === 'resolved' ? (inc.dateResolved || inc.dateOccurred) : todayStr);

  // Greedy lane packing (fewest lanes; non-overlapping incidents reuse a lane).
  const sorted = [...incidents].filter((i) => i.dateOccurred).sort((a, b) => a.dateOccurred.localeCompare(b.dateOccurred));
  const laneEnds = [];
  sorted.forEach((inc) => {
    const e = endOf(inc);
    let placed = false;
    for (let l = 0; l < laneEnds.length; l++) {
      if (laneEnds[l] < inc.dateOccurred) { inc._lane = l; laneEnds[l] = e; placed = true; break; }
    }
    if (!placed) { inc._lane = laneEnds.length; laneEnds.push(e); }
  });
  const activeOn = (dateStr) => sorted.filter((i) => dateStr >= i.dateOccurred && dateStr <= endOf(i));

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let dd = 1; dd <= daysInMonth; dd++) cells.push(dd);
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  const legendTypes = Array.from(new Set(sorted.map((i) => (i.tags || [])[0]).filter(Boolean)));
  const selected = selectedDate ? activeOn(selectedDate) : [];

  return (
    <View>
      <View style={styles.calHeader}>
        <TouchableOpacity onPress={() => { setMonthOffset((m) => m - 1); setSelectedDate(null); }} style={styles.calNavBtn} activeOpacity={0.7}><Text style={styles.calNavTxt}>‹</Text></TouchableOpacity>
        <Text style={styles.calMonth}>{view.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</Text>
        <TouchableOpacity onPress={() => { setMonthOffset((m) => m + 1); setSelectedDate(null); }} style={styles.calNavBtn} activeOpacity={0.7}><Text style={styles.calNavTxt}>›</Text></TouchableOpacity>
      </View>
      <View style={styles.calRow}>
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (<Text key={i} style={styles.calDow}>{d}</Text>))}
      </View>
      {weeks.map((wk, wi) => (
        <View key={wi} style={styles.calRow}>
          {wk.map((dd, di) => {
            if (dd === null) return <View key={di} style={styles.calCellBlank} />;
            const dateStr = iso(dd);
            const active = activeOn(dateStr);
            const overflow = active.filter((i) => i._lane >= MAX_LANES).length;
            return (
              <TouchableOpacity key={di} style={[styles.calCell, dateStr === todayStr && styles.calCellToday]} onPress={() => setSelectedDate(dateStr)} activeOpacity={0.7}>
                <Text style={styles.calDay}>{dd}</Text>
                {Array.from({ length: MAX_LANES }).map((_, l) => {
                  const inc = active.find((i) => i._lane === l);
                  if (!inc) return <View key={l} style={styles.calLaneEmpty} />;
                  const c = incidentColor(inc);
                  const isStart = dateStr === inc.dateOccurred;
                  const isEnd = dateStr === endOf(inc);
                  const ongoingEnd = inc.status !== 'resolved' && isEnd;
                  return (
                    <View key={l} style={[
                      styles.calLane,
                      { backgroundColor: ongoingEnd ? c.bg : c.fill },
                      ongoingEnd && { borderWidth: 1, borderColor: c.fill, borderStyle: 'dashed' },
                      isStart && { borderTopLeftRadius: 3, borderBottomLeftRadius: 3 },
                      isEnd && { borderTopRightRadius: 3, borderBottomRightRadius: 3 },
                    ]} />
                  );
                })}
                {overflow > 0 && <Text style={styles.calOverflow}>+{overflow}</Text>}
              </TouchableOpacity>
            );
          })}
        </View>
      ))}
      {legendTypes.length > 0 && (
        <View style={styles.calLegend}>
          {legendTypes.map((t) => {
            const c = incidentTypeColor(t);
            return (<View key={t} style={styles.calLegendItem}><View style={[styles.calDot, { backgroundColor: c.fill }]} /><Text style={styles.calLegendTxt}>{t}</Text></View>);
          })}
        </View>
      )}
      {selectedDate && (
        <View style={styles.calDetail}>
          <Text style={styles.calDetailDate}>{formatDate(selectedDate)}</Text>
          {selected.length === 0 ? (
            <Text style={styles.healthEmpty}>No active incidents.</Text>
          ) : selected.map((i) => {
            const c = incidentColor(i);
            return (
              <TouchableOpacity
                key={i.id}
                style={styles.calDetailRow}
                onPress={onIncidentPress ? () => onIncidentPress(i) : undefined}
                activeOpacity={onIncidentPress ? 0.6 : 1}
                disabled={!onIncidentPress}
              >
                <View style={[styles.calDot, { backgroundColor: c.fill }]} />
                <Text style={styles.calDetailName}>{i.name}</Text>
                <Text style={styles.calDetailMeta}>
                  {horseNames && horseNames[i.horseId] ? ` · ${horseNames[i.horseId]}` : ''}
                  {` · ${(i.tags || [])[0] || 'Other'}${i.status !== 'resolved' ? ' · ongoing' : ''}`}
                </Text>
                {onIncidentPress && <Text style={styles.calDetailChevron}>›</Text>}
              </TouchableOpacity>
            );
          })}
        </View>
      )}
    </View>
  );
}

function HerdHealthScreen({ navigation }) {
  const [herdIncidents, setHerdIncidents] = useState([]);
  const [horseNames, setHorseNames] = useState({});
  const [tagFilter, setTagFilter] = useState(null);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    let unsubIncidents = () => {};
    const unsubHorses = subscribeToHorses(uid, (horses) => {
      const names = {};
      horses.forEach((h) => { names[h.id] = displayName(h); });
      setHorseNames(names);
      unsubIncidents();
      unsubIncidents = subscribeToHerdIncidents(horses.map((h) => h.id), setHerdIncidents);
    });
    return () => { unsubHorses(); unsubIncidents(); };
  }, []);

  const presentTags = Array.from(new Set(herdIncidents.flatMap((i) => i.tags || []))).sort();
  const filtered = herdIncidents.filter((i) => !tagFilter || (i.tags || []).includes(tagFilter));
  const ongoing = herdIncidents.filter((i) => i.status !== 'resolved');

  return (
    <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 60 }}>
      <Text style={styles.dashSub}>Every horse's health incidents on one calendar — color-coded by type so patterns stand out across the herd.</Text>
      {ongoing.length > 0 && (
        <View style={[styles.healthBadge, { alignSelf: 'flex-start', marginBottom: 12 }]}>
          <Text style={styles.healthBadgeTxt}>🩺 {ongoing.length} ongoing across the herd</Text>
        </View>
      )}
      {presentTags.length > 0 && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, paddingBottom: 12 }}>
          <TouchableOpacity style={[styles.categoryChip, !tagFilter && styles.categoryChipActive]} onPress={() => setTagFilter(null)} activeOpacity={0.75}>
            <Text style={[styles.categoryChipTxt, !tagFilter && styles.categoryChipTxtActive]}>All types</Text>
          </TouchableOpacity>
          {presentTags.map((tag) => (
            <TouchableOpacity key={tag} style={[styles.categoryChip, tagFilter === tag && styles.categoryChipActive]} onPress={() => setTagFilter((p) => (p === tag ? null : tag))} activeOpacity={0.75}>
              <Text style={[styles.categoryChipTxt, tagFilter === tag && styles.categoryChipTxtActive]}>{tag}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}
      <View style={styles.detailsCard}>
        {herdIncidents.length === 0 ? (
          <Text style={styles.healthEmpty}>No health incidents recorded across your herd yet.</Text>
        ) : (
          <IncidentCalendar
            incidents={filtered}
            horseNames={horseNames}
            onIncidentPress={(i) => navigation.navigate('Incident', { horseId: i.horseId, incidentId: i.id })}
          />
        )}
      </View>
    </ScrollView>
  );
}

function IncidentScreen({ route, navigation }) {
  const { horseId, incidentId } = route.params;
  const [incident, setIncident] = useState(null);
  const [horse, setHorse] = useState(null);

  // Treatment-log modal
  const [showLogModal, setShowLogModal] = useState(false);
  const [logType, setLogType] = useState('Note');
  const [logText, setLogText] = useState('');
  const [logAction, setLogAction] = useState('');
  const [editingLogId, setEditingLogId] = useState(null);

  // Edit-incident modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [eName, setEName] = useState('');
  const [eTags, setETags] = useState([]);
  const [eDate, setEDate] = useState(new Date());
  const [eShowDate, setEShowDate] = useState(false);
  const [eSymptoms, setESymptoms] = useState('');
  const [eVet, setEVet] = useState('');
  const [eTreat, setETreat] = useState('');
  const [eCustomTagInput, setECustomTagInput] = useState('');

  // Add-task modal (writes into horse.milestones with an incidentId link)
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskName, setTaskName] = useState('');
  const [taskNotes, setTaskNotes] = useState('');
  const [taskDate, setTaskDate] = useState(new Date());
  const [taskShowDate, setTaskShowDate] = useState(false);
  const [taskReminder, setTaskReminder] = useState(true);
  const [taskRepeat, setTaskRepeat] = useState(false);
  const [taskAssignee, setTaskAssignee] = useState(null);
  const [showTaskAssignee, setShowTaskAssignee] = useState(false);

  const [customIncidentTags, setCustomIncidentTags] = useState([]);

  // Incident/log photos
  const [logPhotosPending, setLogPhotosPending] = useState([]);
  const [incPhotoUploading, setIncPhotoUploading] = useState(false);
  const [incViewerUrl, setIncViewerUrl] = useState(null);

  useEffect(() => subscribeToIncident(incidentId, setIncident), [incidentId]);
  useEffect(() => subscribeToHorse(horseId, setHorse), [horseId]);
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    return subscribeToUserSettings(uid, (s) => setCustomIncidentTags(s.customIncidentTags || []));
  }, []);
  useEffect(() => { if (incident?.name) navigation.setOptions({ title: incident.name }); }, [incident?.name]);

  const me = auth.currentUser;
  const canUploadMedia = canUploadForHorse(horse, me?.uid);
  // View-only shares can read the incident but not change anything.
  const canEdit = canUploadMedia;

  const assignableMembers = (() => {
    const list = [{ uid: me?.uid, label: 'You' }];
    (horse?.shares || []).forEach((s) => {
      if (s.uid && s.uid !== me?.uid && !list.some((x) => x.uid === s.uid)) list.push({ uid: s.uid, label: s.name || s.email });
    });
    if (horse?.ownerId && horse.ownerId !== me?.uid && !list.some((x) => x.uid === horse.ownerId)) {
      list.push({ uid: horse.ownerId, label: horse.ownerName || horse.ownerEmail || 'Owner' });
    }
    return list;
  })();

  const incidentTasks = (horse?.milestones || []).filter((m) => m.incidentId === incidentId);
  const logs = [...(incident?.logs || [])].sort((a, b) => (a.at || '').localeCompare(b.at || ''));
  const resolved = incident?.status === 'resolved';

  const handleMarkSound = () => {
    const today = new Date().toISOString().split('T')[0];
    markIncidentSound(incidentId, today).catch(() => {});
  };
  const handleReopen = () => reopenIncident(incidentId).catch(() => {});

  const openAddLog = () => { setEditingLogId(null); setLogType('Note'); setLogText(''); setLogAction(''); setLogPhotosPending([]); setShowLogModal(true); };
  const openEditLog = (l) => { setEditingLogId(l.id); setLogType(l.type || 'Note'); setLogText(l.text || ''); setLogAction(l.action || ''); setLogPhotosPending([]); setShowLogModal(true); };
  const handleSaveLog = async () => {
    if (!logText.trim() && !logAction.trim()) { Alert.alert('Detail required', 'Please enter some detail or a treatment/action.'); return; }
    try {
      // Upload any photos attached in the modal first, then save the entry.
      const uploaded = [];
      if (logPhotosPending.length) {
        setIncPhotoUploading(true);
        for (const f of logPhotosPending) {
          uploaded.push(await uploadHorseFileAsync(horseId, f.payload, { kind: 'incidents', name: f.name, mime: f.mime }));
        }
      }
      if (editingLogId) {
        const next = (incident.logs || []).map((l) => (l.id === editingLogId ? { ...l, type: logType, text: logText.trim(), action: logAction.trim(), photos: [...(l.photos || []), ...uploaded] } : l));
        await setIncidentLogs(incidentId, next);
      } else {
        await addIncidentLog(incidentId, { type: logType, text: logText, action: logAction, authorName: me?.displayName || me?.email || '', photos: uploaded });
      }
      setLogPhotosPending([]);
      setShowLogModal(false);
    } catch (e) { Alert.alert('Error', logPhotosPending.length ? uploadErrMsg(e) : 'Could not save. Please try again.'); }
    finally { setIncPhotoUploading(false); }
  };

  const handleAddIncidentPhotos = () => {
    choosePhotoSource(async (items) => {
      setIncPhotoUploading(true);
      try {
        for (const f of items) {
          const meta = await uploadHorseFileAsync(horseId, f.payload, { kind: 'incidents', name: f.name, mime: f.mime });
          await addIncidentPhoto(incidentId, meta);
        }
      } catch (e) {
        Alert.alert('Upload failed', uploadErrMsg(e));
      } finally {
        setIncPhotoUploading(false);
      }
    });
  };

  const handleRemoveIncidentPhoto = (p) => {
    Alert.alert('Remove photo', 'Remove this photo from the incident?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Remove',
        style: 'destructive',
        onPress: async () => {
          try { await removeIncidentPhoto(incidentId, p); deleteFileByUrl(p.path || p.url); } catch {}
        },
      },
    ]);
  };
  const deleteLog = (lId) => {
    Alert.alert('Delete entry', 'Remove this log entry?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => setIncidentLogs(incidentId, (incident.logs || []).filter((l) => l.id !== lId)).catch(() => {}) },
    ]);
  };

  const openEdit = () => {
    setEName(incident.name || '');
    setETags(incident.tags || []);
    setECustomTagInput('');
    setEDate(incident.dateOccurred ? new Date(incident.dateOccurred + 'T12:00:00') : new Date());
    setEShowDate(false);
    setESymptoms(incident.symptoms || '');
    setEVet(incident.vetNotes || '');
    setETreat(incident.prescribedTreatment || '');
    setShowEditModal(true);
  };
  const toggleETag = (tag) => setETags((p) => (p.includes(tag) ? p.filter((t) => t !== tag) : [...p, tag]));
  const addECustomType = () => {
    const t = eCustomTagInput.trim();
    if (!t) return;
    if (!eTags.includes(t)) setETags((p) => [...p, t]);
    const uid = auth.currentUser?.uid;
    if (uid && !INCIDENT_TAG_PRESETS.includes(t) && !customIncidentTags.includes(t)) addCustomIncidentTag(uid, t).catch(() => {});
    setECustomTagInput('');
  };
  const handleSaveEdit = async () => {
    if (!eName.trim()) { Alert.alert('Name required', 'Enter what is going on.'); return; }
    const picked = new Date(eDate); picked.setHours(12, 0, 0, 0);
    try {
      await updateIncident(incidentId, {
        name: eName.trim(), tags: eTags, dateOccurred: picked.toISOString().split('T')[0],
        symptoms: eSymptoms.trim(), vetNotes: eVet.trim(), prescribedTreatment: eTreat.trim(),
      });
      setShowEditModal(false);
    } catch { Alert.alert('Error', 'Could not save. Please try again.'); }
  };

  const handleDeleteIncident = () => {
    Alert.alert('Delete incident', 'This removes the incident and its treatment log. Any tasks created for it stay on the horse. Continue?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => deleteIncident(incidentId).then(() => navigation.goBack()).catch(() => {}) },
    ]);
  };

  const openAddTask = () => {
    setTaskName('');
    setTaskNotes('');
    setTaskDate(new Date());
    setTaskShowDate(false);
    setTaskReminder(true);
    setTaskRepeat(false);
    setTaskAssignee({ uid: me?.uid, label: 'You' });
    setShowTaskAssignee(false);
    setShowTaskModal(true);
  };
  const handleAddTask = async () => {
    if (!taskName.trim()) { Alert.alert('Name required', 'Enter a task name.'); return; }
    const picked = new Date(taskDate); picked.setHours(12, 0, 0, 0);
    const dateStr = picked.toISOString().split('T')[0];
    const assignee = taskAssignee || { uid: me?.uid, label: 'You' };
    const newM = {
      id: `m_inc_${Date.now()}`,
      name: taskName.trim(),
      date: dateStr,
      completed: false,
      custom: true,
      incidentId,
      ...(assignee.uid && {
        assignedToUid: assignee.uid, assignedToName: assignee.label,
        assignedBy: me?.uid || '', assignedByName: me?.displayName || me?.email || '',
      }),
      ...(taskNotes.trim() && { notes: taskNotes.trim() }),
      ...(taskReminder && { remindOnDue: true }),
      ...(taskReminder && taskRepeat && { repeatUntilDone: true }),
      createdByUid: me?.uid || '',
    };
    const milestones = [...(horse?.milestones || []), newM].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    try { await updateHorse(horseId, { milestones }); setShowTaskModal(false); }
    catch { Alert.alert('Error', 'Could not add task. Please try again.'); }
  };
  const toggleTask = (mId) => {
    const milestones = (horse?.milestones || []).map((m) => (m.id === mId ? { ...m, completed: !m.completed } : m));
    updateHorse(horseId, { milestones }).catch(() => {});
  };
  const deleteTask = (mId) => {
    Alert.alert('Delete task', 'Remove this task?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => updateHorse(horseId, { milestones: (horse?.milestones || []).filter((m) => m.id !== mId) }).catch(() => {}) },
    ]);
  };

  if (!incident) {
    return <View style={{ flex: 1, padding: 24 }}><Text style={styles.healthEmpty}>Loading…</Text></View>;
  }

  return (
    <>
      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 60 }}>
        {/* Status + dates */}
        <View style={styles.detailsCard}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <View style={[styles.incidentStatus, resolved ? styles.incidentStatusSound : styles.incidentStatusOngoing]}>
              <Text style={[styles.incidentStatusTxt, resolved ? styles.incidentStatusTxtSound : styles.incidentStatusTxtOngoing]}>
                {resolved ? 'Sound / Healthy' : 'Ongoing'}
              </Text>
            </View>
            {canEdit && (
              <TouchableOpacity onPress={openEdit} activeOpacity={0.7}>
                <Text style={styles.editNameBtnTxt}>Edit</Text>
              </TouchableOpacity>
            )}
          </View>
          {(incident.tags || []).length > 0 && (
            <View style={[styles.incidentTagWrap, { marginTop: 10 }]}>
              {incident.tags.map((t) => (
                <View key={t} style={styles.incidentTagChip}><Text style={styles.incidentTagChipTxt}>{t}</Text></View>
              ))}
            </View>
          )}
          <View style={[styles.detailRow, { marginTop: 12 }]}>
            <Text style={styles.detailLabel}>Date Occurred</Text>
            <Text style={styles.detailValue}>{formatDate(incident.dateOccurred)}</Text>
          </View>
          {resolved && (
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Marked Sound</Text>
              <Text style={styles.detailValue}>{formatDate(incident.dateResolved)}</Text>
            </View>
          )}
          {!!incident.symptoms && (
            <View style={{ marginTop: 12 }}>
              <Text style={styles.detailLabel}>Symptoms</Text>
              <Text style={styles.incidentBodyText}>{incident.symptoms}</Text>
            </View>
          )}
          {!!incident.vetNotes && (
            <View style={{ marginTop: 12 }}>
              <Text style={styles.detailLabel}>Vet Notes</Text>
              <Text style={styles.incidentBodyText}>{incident.vetNotes}</Text>
            </View>
          )}
          {!!incident.prescribedTreatment && (
            <View style={{ marginTop: 12 }}>
              <Text style={styles.detailLabel}>Prescribed Treatment</Text>
              <Text style={styles.incidentBodyText}>{incident.prescribedTreatment}</Text>
            </View>
          )}

          {PHOTO_UPLOADS_ENABLED && ((incident.photos || []).length > 0 || (isPhotoPickingAvailable() && canUploadMedia)) && (
            <View style={{ marginTop: 12 }}>
              <Text style={styles.detailLabel}>Photos</Text>
              {incPhotoUploading ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <ActivityIndicator color={RED} />
                  <Text style={{ fontSize: 13, color: MUTED }}>Uploading…</Text>
                </View>
              ) : (
                <PhotoStrip
                  photos={incident.photos || []}
                  onOpen={setIncViewerUrl}
                  onAdd={isPhotoPickingAvailable() && canUploadMedia ? handleAddIncidentPhotos : null}
                  onRemove={canUploadMedia ? handleRemoveIncidentPhoto : null}
                />
              )}
            </View>
          )}

          {canEdit && (
            <TouchableOpacity
              style={[styles.saveBtn, { marginTop: 16 }, resolved && { backgroundColor: '#78909C' }]}
              onPress={resolved ? handleReopen : handleMarkSound}
              activeOpacity={0.85}
            >
              <Text style={styles.saveBtnText}>{resolved ? 'Re-open (issue returned)' : '✓ Mark Sound / Healthy'}</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Tasks */}
        <Text style={styles.sectionHeading}>Tasks</Text>
        <View style={styles.detailsCard}>
          {incidentTasks.length === 0 ? (
            <Text style={styles.healthEmpty}>No tasks yet.</Text>
          ) : (
            incidentTasks.map((m) => (
              <View key={m.id} style={styles.incLogRow}>
                <TouchableOpacity onPress={() => canEdit && toggleTask(m.id)} activeOpacity={canEdit ? 0.7 : 1} style={{ flexDirection: 'row', alignItems: 'flex-start', flex: 1 }}>
                  <Text style={{ fontSize: 16, marginRight: 8 }}>{m.completed ? '☑️' : '⬜️'}</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.incidentBodyText, m.completed && { textDecorationLine: 'line-through', color: MUTED }]}>{m.name}</Text>
                    {!!m.notes && <Text style={[styles.incidentBodyText, { fontSize: 13, color: MUTED }]}>{m.notes}</Text>}
                    <Text style={styles.incLogMeta}>
                      Due {formatDate(m.date)}{m.assignedToName ? ` · 👤 ${m.assignedToUid === me?.uid ? 'You' : m.assignedToName}` : ''}
                    </Text>
                  </View>
                </TouchableOpacity>
                {canEdit && (
                  <TouchableOpacity onPress={() => deleteTask(m.id)} activeOpacity={0.7}><Text style={styles.incDeleteTxt}>✕</Text></TouchableOpacity>
                )}
              </View>
            ))
          )}
          {canEdit && (
            <TouchableOpacity style={styles.createIncidentBtn} onPress={openAddTask} activeOpacity={0.85}>
              <Text style={styles.createIncidentBtnTxt}>+ Add Task</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Treatment log */}
        <Text style={styles.sectionHeading}>Treatment Log</Text>
        <View style={styles.detailsCard}>
          {logs.length === 0 ? (
            <Text style={styles.healthEmpty}>No entries yet. Add notes, vet visits, and treatments here to track progress.</Text>
          ) : (
            logs.map((l) => (
              <View key={l.id} style={styles.incLogRow}>
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <View style={styles.incLogTypeChip}><Text style={styles.incLogTypeChipTxt}>{l.type || 'Note'}</Text></View>
                    <Text style={styles.incLogMeta}>{formatDate((l.at || '').split('T')[0])}{l.authorName ? ` · ${l.authorName}` : ''}</Text>
                  </View>
                  {!!l.text && <Text style={[styles.incidentBodyText, { marginTop: 4 }]}>{l.text}</Text>}
                  {!!l.action && (
                    <View style={styles.incLogAction}>
                      <Text style={styles.incLogActionLabel}>Treatment / Action</Text>
                      <Text style={styles.incidentBodyText}>{l.action}</Text>
                    </View>
                  )}
                  {(l.photos || []).length > 0 && (
                    <PhotoStrip photos={l.photos} onOpen={setIncViewerUrl} size={58} />
                  )}
                </View>
                {canEdit && (
                  <View style={{ alignItems: 'flex-end' }}>
                    <TouchableOpacity onPress={() => openEditLog(l)} activeOpacity={0.7}><Text style={styles.editNameBtnTxt}>Edit</Text></TouchableOpacity>
                    <TouchableOpacity onPress={() => deleteLog(l.id)} activeOpacity={0.7}><Text style={styles.incDeleteTxt}>✕</Text></TouchableOpacity>
                  </View>
                )}
              </View>
            ))
          )}
          {canEdit && (
            <TouchableOpacity style={styles.createIncidentBtn} onPress={openAddLog} activeOpacity={0.85}>
              <Text style={styles.createIncidentBtnTxt}>+ Add Log Entry</Text>
            </TouchableOpacity>
          )}
        </View>

        {canEdit && (
        <TouchableOpacity style={styles.deleteBtn} onPress={handleDeleteIncident} activeOpacity={0.8}>
          <Text style={styles.deleteBtnTxt}>Delete Incident</Text>
        </TouchableOpacity>
        )}
      </ScrollView>

      {/* Add / Edit Log Modal */}
      <PhotoViewerModal url={incViewerUrl} onClose={() => setIncViewerUrl(null)} />

      <Modal visible={showLogModal} animationType="slide" transparent>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.sheet}>
            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={styles.sheetTitle}>{editingLogId ? 'Edit Entry' : 'Add Log Entry'}</Text>
              <Text style={styles.fieldLabel}>Type</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
                {INCIDENT_LOG_TYPES.map((t) => (
                  <TouchableOpacity key={t} style={[styles.categoryChip, logType === t && styles.categoryChipActive]} onPress={() => setLogType(t)} activeOpacity={0.75}>
                    <Text style={[styles.categoryChipTxt, logType === t && styles.categoryChipTxtActive]}>{t}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Details</Text>
              <TextInput style={[styles.input, styles.inputMultiline]} placeholder="What happened, findings, observations…" placeholderTextColor="#BDBDBD" multiline value={logText} onChangeText={setLogText} />
              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Treatment / Action</Text>
              <TextInput style={[styles.input, styles.inputMultiline]} placeholder="What was done or prescribed (optional)…" placeholderTextColor="#BDBDBD" multiline value={logAction} onChangeText={setLogAction} />
              {PHOTO_UPLOADS_ENABLED && isPhotoPickingAvailable() && canUploadMedia && (
                <>
                  <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Photos</Text>
                  <PhotoStrip
                    photos={logPhotosPending}
                    onAdd={() => choosePhotoSource((items) => setLogPhotosPending((prev) => [...prev, ...items]))}
                    onRemove={(p, i) => setLogPhotosPending((prev) => prev.filter((_, idx) => idx !== i))}
                    onOpen={setIncViewerUrl}
                  />
                </>
              )}
              <View style={styles.sheetBtns}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => { setLogPhotosPending([]); setShowLogModal(false); }} activeOpacity={0.75}><Text style={styles.cancelBtnText}>Cancel</Text></TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={handleSaveLog} disabled={incPhotoUploading} activeOpacity={0.85}>
                  {incPhotoUploading ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>Save</Text>}
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* Edit Incident Modal */}
      <Modal visible={showEditModal} animationType="slide" transparent>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.sheet}>
            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={styles.sheetTitle}>Edit Incident</Text>
              <Text style={styles.fieldLabel}>What's going on?</Text>
              <TextInput style={styles.input} placeholder="e.g. Left front lameness" placeholderTextColor="#BDBDBD" value={eName} onChangeText={setEName} />
              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Type</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
                {[...INCIDENT_TAG_PRESETS, ...customIncidentTags, ...eTags.filter((t) => !INCIDENT_TAG_PRESETS.includes(t) && !customIncidentTags.includes(t))].map((tag) => {
                  const on = eTags.includes(tag);
                  return (
                    <TouchableOpacity key={tag} style={[styles.categoryChip, on && styles.categoryChipActive]} onPress={() => toggleETag(tag)} activeOpacity={0.75}>
                      <Text style={[styles.categoryChipTxt, on && styles.categoryChipTxtActive]}>{tag}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <View style={styles.addTypeRow}>
                <TextInput style={[styles.input, { flex: 1, marginBottom: 0 }]} placeholder="Add your own type…" placeholderTextColor="#BDBDBD" autoCapitalize="words" returnKeyType="done"
                  value={eCustomTagInput} onChangeText={setECustomTagInput} onSubmitEditing={addECustomType} />
                <TouchableOpacity style={[styles.addTypeBtn, !eCustomTagInput.trim() && { opacity: 0.5 }]} onPress={addECustomType} disabled={!eCustomTagInput.trim()} activeOpacity={0.8}>
                  <Text style={styles.addTypeBtnTxt}>Add</Text>
                </TouchableOpacity>
              </View>
              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Date Occurred</Text>
              <TouchableOpacity style={styles.datePicker} onPress={() => setEShowDate(true)}>
                <Text style={styles.datePickerText}>{eDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Text>
                <Text>📅</Text>
              </TouchableOpacity>
              {eShowDate && (
                <DateTimePicker value={eDate} mode="date" display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                  onChange={(ev, d) => { if (Platform.OS === 'android') setEShowDate(false); if (d) setEDate(d); }} />
              )}
              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Symptoms</Text>
              <TextInput style={[styles.input, styles.inputMultiline]} placeholderTextColor="#BDBDBD" multiline value={eSymptoms} onChangeText={setESymptoms} />
              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Vet Notes</Text>
              <TextInput style={[styles.input, styles.inputMultiline]} placeholderTextColor="#BDBDBD" multiline value={eVet} onChangeText={setEVet} />
              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Prescribed Treatment</Text>
              <TextInput style={[styles.input, styles.inputMultiline]} placeholderTextColor="#BDBDBD" multiline value={eTreat} onChangeText={setETreat} />
              <View style={styles.sheetBtns}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowEditModal(false)} activeOpacity={0.75}><Text style={styles.cancelBtnText}>Cancel</Text></TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={handleSaveEdit} activeOpacity={0.85}><Text style={styles.saveBtnText}>Save</Text></TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* Add Task Modal */}
      <Modal visible={showTaskModal} animationType="slide" transparent>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.sheet}>
            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={styles.sheetTitle}>Add Task</Text>
              <Text style={styles.fieldLabel}>Task</Text>
              <TextInput style={styles.input} placeholder="e.g. Re-wrap leg" placeholderTextColor="#BDBDBD" value={taskName} onChangeText={setTaskName} />
              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Notes (optional)</Text>
              <TextInput style={[styles.input, styles.inputMultiline]} placeholder="Any extra detail to follow with this task…" placeholderTextColor="#BDBDBD" multiline value={taskNotes} onChangeText={setTaskNotes} />
              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Due Date</Text>
              <TouchableOpacity style={styles.datePicker} onPress={() => setTaskShowDate(true)}>
                <Text style={styles.datePickerText}>{taskDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Text>
                <Text>📅</Text>
              </TouchableOpacity>
              {taskShowDate && (
                <DateTimePicker value={taskDate} mode="date" display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                  onChange={(ev, d) => { if (Platform.OS === 'android') setTaskShowDate(false); if (d) setTaskDate(d); }} />
              )}
              {assignableMembers.length > 1 && (
                <>
                  <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Assigned To</Text>
                  <TouchableOpacity style={styles.sortBar} onPress={() => setShowTaskAssignee((v) => !v)} activeOpacity={0.75}>
                    <Text style={styles.sortBarLabel}>{taskAssignee?.label || 'You'}</Text>
                    <Text>▾</Text>
                  </TouchableOpacity>
                  {showTaskAssignee && (
                    <View style={styles.sortMenu}>
                      {assignableMembers.map((mem) => (
                        <TouchableOpacity key={mem.uid} style={[styles.sortMenuItem, taskAssignee?.uid === mem.uid && styles.sortMenuItemActive]}
                          onPress={() => { setTaskAssignee(mem); setShowTaskAssignee(false); }} activeOpacity={0.75}>
                          <Text style={[styles.sortMenuItemTxt, taskAssignee?.uid === mem.uid && styles.sortMenuItemTxtActive]}>{mem.label}</Text>
                          {taskAssignee?.uid === mem.uid && <Text style={styles.sortMenuItemCheck}>✓</Text>}
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}
                </>
              )}
              <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Reminder</Text>
              <TouchableOpacity style={styles.recipToggle} onPress={() => setTaskReminder((v) => !v)} activeOpacity={0.75}>
                <View style={[styles.recipToggleTrack, taskReminder && styles.recipToggleTrackOn]}>
                  <View style={[styles.recipToggleThumb, taskReminder && styles.recipToggleThumbOn]} />
                </View>
                <Text style={styles.recipToggleLabel}>{taskReminder ? 'Yes — remind on due date' : 'No'}</Text>
              </TouchableOpacity>
              {taskReminder && (
                <>
                  <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Repeat</Text>
                  <TouchableOpacity style={styles.recipToggle} onPress={() => setTaskRepeat((v) => !v)} activeOpacity={0.75}>
                    <View style={[styles.recipToggleTrack, taskRepeat && styles.recipToggleTrackOn]}>
                      <View style={[styles.recipToggleThumb, taskRepeat && styles.recipToggleThumbOn]} />
                    </View>
                    <Text style={styles.recipToggleLabel}>{taskRepeat ? 'Remind every morning until done' : 'Remind once'}</Text>
                  </TouchableOpacity>
                  {taskRepeat && (
                    <Text style={{ color: MUTED, fontSize: 12, marginTop: 6, lineHeight: 17 }}>
                      Stops as soon as the task is marked complete, or after 14 days overdue.
                    </Text>
                  )}
                </>
              )}
              <View style={styles.sheetBtns}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowTaskModal(false)} activeOpacity={0.75}><Text style={styles.cancelBtnText}>Cancel</Text></TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={handleAddTask} activeOpacity={0.85}><Text style={styles.saveBtnText}>Add</Text></TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </>
  );
}

function TimelineScreen({ navigation }) {
  // The timeline is a mode of Milestones & Tasks now. This route survives only
  // so an old link does not dead-end.
  useEffect(() => {
    AsyncStorage.setItem('milestonesViewMode', 'timeline').catch(() => {});
    navigation.replace('Milestones');
  }, []);
  return (
    <SafeAreaView style={styles.screen} edges={['bottom']}>
      <Text style={[styles.noNotesTxt, { marginTop: 30 }]}>Opening Milestones &amp; Tasks…</Text>
    </SafeAreaView>
  );
}

// ─── STYLES ───────────────────────────────────────────────────────────────────

// Android draws the app edge-to-edge, so anything pinned to the bottom of the
// screen — a sheet, a fixed button bar — sits under the system navigation bar
// unless it is pushed up by that bar's height. iOS already has its own number.
const NAV_INSET = Platform.OS === 'android' ? (initialWindowMetrics?.insets?.bottom || 0) : 0;

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: BG },

  // Empty state
  emptyWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  emptyIcon: { fontSize: 72, marginBottom: 16 },
  emptyTitle: { fontSize: 22, fontWeight: '700', color: TEXT, marginBottom: 8 },
  emptySub: {
    fontSize: 15,
    color: MUTED,
    textAlign: 'center',
    lineHeight: 22,
  },

  // Horse list card
  horseCard: {
    backgroundColor: CARD,
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.07,
    shadowRadius: 4,
    elevation: 2,
  },
  horseCardTop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  horseName: { fontSize: 18, fontWeight: '700', color: TEXT, marginBottom: 2 },
  horseSharedTag: { fontSize: 11, color: '#8A6D2F', fontWeight: '600', marginTop: 2 },
  horseRegisteredName: { fontSize: 12, color: MUTED, marginBottom: 2 },
  horseSubtext: { fontSize: 13, color: MUTED, marginBottom: 4 },
  horseNext: { fontSize: 13, color: RED, fontWeight: '500' },
  horseAllDone: { fontSize: 13, color: GREEN, fontWeight: '600' },
  badge: {
    backgroundColor: RED + '18',
    borderRadius: 20,
    paddingHorizontal: 10,
    paddingVertical: 4,
    marginLeft: 12,
  },
  badgeText: { fontSize: 13, fontWeight: '700', color: RED },
  cardTagRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 8,
  },
  cardTagRowBadge: {
    marginBottom: 0,
    marginLeft: 12,
    justifyContent: 'flex-end',
    maxWidth: '45%',
  },
  cardTag: {
    backgroundColor: BG,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: BORDER,
    paddingHorizontal: 10,
    paddingVertical: 3,
  },
  cardTagTxt: { fontSize: 12, fontWeight: '600', color: MUTED },
  barBg: { height: 5, backgroundColor: BORDER, borderRadius: 3 },
  barFill: { height: 5, backgroundColor: RED, borderRadius: 3 },

  // Bottom bar (fixed at bottom)
  bottomBar: {
    position: 'absolute',
    bottom: 24 + NAV_INSET,
    left: 24,
    right: 24,
    gap: 10,
  },
  addBtn: {
    backgroundColor: RED,
    borderRadius: 14,
    paddingVertical: 16,
    alignItems: 'center',
    shadowColor: RED,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  addBtnText: { fontSize: 17, fontWeight: '700', color: '#FFF' },

  // Modal sheet
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: CARD,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 24,
    paddingBottom: Platform.OS === 'ios' ? 40 : 24 + NAV_INSET,
  },
  sheetTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: TEXT,
    marginBottom: 22,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: 6,
  },
  addTagCategoryTxt: {
    fontSize: 12,
    fontWeight: '700',
    color: RED,
  },
  input: {
    backgroundColor: BG,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: BORDER,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    color: TEXT,
    marginBottom: 18,
  },

  // Type toggle (segmented control)
  typeToggle: {
    flexDirection: 'row',
    backgroundColor: BG,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: BORDER,
    marginBottom: 18,
    overflow: 'hidden',
  },
  typeBtn: {
    flex: 1,
    paddingVertical: 11,
    alignItems: 'center',
  },
  typeBtnActive: {
    backgroundColor: RED,
  },
  typeBtnTxt: {
    fontSize: 14,
    fontWeight: '600',
    color: MUTED,
  },
  typeBtnTxtActive: {
    color: '#FFF',
  },

  datePicker: {
    backgroundColor: BG,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: BORDER,
    paddingHorizontal: 14,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  // Used by the incentive and earnings date rows.
  dateRow: {
    backgroundColor: BG,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: BORDER,
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginBottom: 12,
  },
  dateRowTxt: { fontSize: 16, color: TEXT },
  // Small inline "Add" button in the category and tag pickers.
  primaryButton: {
    backgroundColor: GREEN,
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  primaryButtonTxt: { fontSize: 14, fontWeight: '700', color: '#FFF' },
  // Owner line under a client horse's name.
  datePickerText: { fontSize: 16, color: TEXT },
  sheetBtns: { flexDirection: 'row', gap: 12 },

  // ── Breeding Planning ──
  seasonBar: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: CARD,
    borderRadius: 12, borderWidth: 1, borderColor: BORDER,
    paddingHorizontal: 14, paddingVertical: 11, marginBottom: 8,
  },
  seasonLabel: { fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: '700' },
  seasonPick: { marginLeft: 'auto', flexDirection: 'row', alignItems: 'center', gap: 14 },
  seasonArrow: { fontSize: 22, color: MUTED, lineHeight: 26 },
  seasonYear: { fontSize: 18, fontWeight: '700', color: RED, minWidth: 54, textAlign: 'center' },
  seasonStrip: { flexDirection: 'row', gap: 6, marginBottom: 12 },
  seasonChip: {
    flex: 1, alignItems: 'center', paddingVertical: 7, borderRadius: 9,
    borderWidth: 1, borderColor: BORDER, backgroundColor: CARD,
  },
  seasonChipOn: { backgroundColor: RED, borderColor: RED },
  seasonChipTxt: { fontSize: 13, color: MUTED, fontWeight: '600' },
  seasonChipTxtOn: { color: CREAM, fontWeight: '700' },

  planSummary: {
    flexDirection: 'row', backgroundColor: CARD, borderRadius: 12,
    borderWidth: 1, borderColor: BORDER, overflow: 'hidden', marginBottom: 14,
  },
  planSummaryCell: { flex: 1, alignItems: 'center', paddingVertical: 11 },
  planSummaryNum: { fontSize: 19, fontWeight: '700', color: TEXT },
  planSummaryKey: { fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 2 },

  planCard: {
    backgroundColor: BG, borderRadius: 11, borderWidth: 1, borderColor: BORDER,
    padding: 12, marginBottom: 10,
  },
  planTopRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  planMare: { fontSize: 15, fontWeight: '700', color: TEXT, flexShrink: 1 },
  planOutside: {
    fontSize: 9, fontWeight: '700', color: '#55697A', borderWidth: 1, borderColor: '#55697A',
    borderRadius: 4, paddingHorizontal: 5, paddingVertical: 1, letterSpacing: 0.5,
  },
  planDelete: { marginLeft: 'auto', fontSize: 15, color: MUTED, paddingHorizontal: 4 },
  sharedOwner: {
    fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: '700',
    color: MUTED, marginBottom: 8,
  },
  sharedPerm: {
    fontSize: 10, fontWeight: '700', color: '#37444F', backgroundColor: '#EDEFF2',
    borderRadius: 5, paddingHorizontal: 8, paddingVertical: 3, overflow: 'hidden',
  },
  sharedPermFull: { color: '#2E4A37', backgroundColor: '#E4EDE7' },
  sharedCard: {
    flexDirection: 'row', alignItems: 'center', gap: 11, backgroundColor: CARD,
    borderRadius: 12, borderWidth: 1, borderColor: '#8FA8BD',
    paddingHorizontal: 13, paddingVertical: 12, marginBottom: 12,
  },
  sharedCardIcon: { fontSize: 19 },
  sharedCardTitle: { fontSize: 14, fontWeight: '700', color: TEXT },
  sharedCardSub: { fontSize: 11.5, color: MUTED, marginTop: 2 },
  sharedCardChevron: { fontSize: 17, color: MUTED },
  sharedBadge: {
    alignSelf: 'flex-start', fontSize: 10, fontWeight: '700', color: '#2B4257',
    backgroundColor: '#EAF1F6', borderRadius: 5, paddingHorizontal: 8, paddingVertical: 3,
    marginTop: 6, overflow: 'hidden',
  },
  sharePlanBtn: {
    marginTop: 12, alignItems: 'center', paddingVertical: 10, borderRadius: 9,
    borderWidth: 1, borderStyle: 'dashed', borderColor: '#55697A',
  },
  sharePlanBtnTxt: { fontSize: 13, fontWeight: '700', color: '#37444F' },
  shareRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10,
    borderBottomWidth: 1, borderBottomColor: BORDER,
  },
  shareName: { fontSize: 14, fontWeight: '600', color: TEXT },
  shareMeta: { fontSize: 11.5, color: MUTED, marginTop: 2 },
  frozenBanner: {
    marginTop: 10, borderRadius: 10, borderWidth: 1, borderColor: '#8FA8BD',
    backgroundColor: '#EAF1F6', paddingHorizontal: 12, paddingVertical: 10,
  },
  frozenBannerTxt: { fontSize: 12.5, fontWeight: '700', color: '#2B4257' },
  frozenFlag: {
    fontSize: 9.5, fontWeight: '700', color: '#2B4257', backgroundColor: '#EAF1F6',
    borderRadius: 4, paddingHorizontal: 5, paddingVertical: 2, overflow: 'hidden',
  },
  frozenBannerAction: { fontSize: 11.5, color: '#3E6B8A', fontWeight: '700', marginTop: 3 },
  batchRow: {
    flexDirection: 'row', alignItems: 'center', gap: 11, paddingVertical: 11,
    paddingHorizontal: 12, borderRadius: 10, borderWidth: 1, borderColor: BORDER,
    backgroundColor: CARD, marginBottom: 8,
  },
  batchRowOn: { borderColor: GREEN, backgroundColor: '#F2F7F3' },
  radio: {
    width: 18, height: 18, borderRadius: 9, borderWidth: 2, borderColor: BORDER,
  },
  radioOn: { borderColor: GREEN, backgroundColor: GREEN },
  batchName: { fontSize: 14, fontWeight: '600', color: TEXT },
  batchMeta: { fontSize: 11.5, color: MUTED, marginTop: 2 },
  planRebreed: {
    fontSize: 9, fontWeight: '700', color: '#FFF', backgroundColor: '#9A7B2E',
    borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2, letterSpacing: 0.5,
    overflow: 'hidden',
  },
  closeOutBtn: {
    alignItems: 'center', paddingVertical: 12, borderRadius: 11,
    borderWidth: 1, borderColor: '#9A7B2E', backgroundColor: '#F4EEDA', marginTop: 4,
  },
  closeOutBtnTxt: { fontSize: 14, fontWeight: '700', color: '#5E4A16' },
  archiveRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingVertical: 11, borderBottomWidth: 1, borderBottomColor: BORDER,
  },
  archiveName: { flex: 1, fontSize: 13.5, color: TEXT, fontWeight: '600' },
  archiveStage: { fontSize: 11.5, color: MUTED },
  carryRow: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 11,
    paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: BORDER,
  },
  carryLabel: { fontSize: 14, fontWeight: '700', color: TEXT },
  carrySub: { fontSize: 11.5, color: MUTED, lineHeight: 16, marginTop: 3 },
  planMeta: { fontSize: 12, color: MUTED, marginTop: 3 },
  planRange: { fontSize: 12, color: TEXT, fontWeight: '600', marginTop: 8 },
  planAddStud: {
    marginTop: 9, alignItems: 'center', paddingVertical: 8, borderRadius: 9,
    borderWidth: 1, borderStyle: 'dashed', borderColor: GREEN,
  },
  planAddStudTxt: { fontSize: 13, fontWeight: '700', color: GREEN },
  planMoveBtn: {
    marginTop: 10, alignItems: 'center', paddingVertical: 9, borderRadius: 9,
    borderWidth: 1, borderStyle: 'dashed', borderColor: GREEN,
  },
  planMoveBtnTxt: { fontSize: 13, fontWeight: '700', color: GREEN },
  planPrimaryBtn: {
    backgroundColor: GREEN, borderRadius: 12, paddingVertical: 14,
    alignItems: 'center', marginTop: 6, marginBottom: 10,
  },
  planPrimaryBtnTxt: { fontSize: 15, fontWeight: '700', color: '#FFF' },

  studRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 7,
    backgroundColor: CARD, borderRadius: 8, borderWidth: 1, borderColor: BORDER,
    paddingHorizontal: 10, paddingVertical: 8,
  },
  studRowChosen: { borderColor: GREEN, backgroundColor: '#F2F7F3' },
  studName: { fontSize: 13, color: TEXT, flex: 1 },
  studFee: { fontSize: 13, color: MUTED },
  studChoose: { fontSize: 12, fontWeight: '700', color: GREEN },
  studRemove: { fontSize: 14, color: MUTED, paddingHorizontal: 4 },
  studPickRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },

  studLibRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: CARD,
    borderRadius: 12, borderWidth: 1, borderColor: BORDER,
    paddingHorizontal: 13, paddingVertical: 12, marginBottom: 9,
  },
  studLibName: { fontSize: 15, fontWeight: '700', color: TEXT },
  studLibMeta: { fontSize: 11.5, color: MUTED, marginTop: 2 },
  studLibFee: { fontSize: 13, color: TEXT, fontWeight: '600' },
  studLibNote: { fontSize: 12, color: MUTED, lineHeight: 17, marginTop: 6, paddingHorizontal: 4 },

  segRow: { flexDirection: 'row', gap: 8, marginBottom: 12 },
  segBtn: {
    flex: 1, alignItems: 'center', paddingVertical: 10, borderRadius: 10,
    borderWidth: 1, borderColor: BORDER, backgroundColor: CARD,
  },
  segBtnOn: { backgroundColor: RED, borderColor: RED },
  segBtnTxt: { fontSize: 13, color: MUTED, fontWeight: '600' },
  segBtnTxtOn: { color: CREAM, fontWeight: '700' },

  pickerBtn: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: BG,
    borderRadius: 10, borderWidth: 1, borderColor: BORDER,
    paddingHorizontal: 14, paddingVertical: 13, marginBottom: 18,
  },
  pickerBtnTxt: { fontSize: 16, color: TEXT, flex: 1 },
  pickerBtnCaret: { fontSize: 13, color: MUTED },

  saveLibRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 16 },
  saveLibBox: {
    width: 22, height: 22, borderRadius: 6, borderWidth: 1.5,
    borderColor: BORDER, alignItems: 'center', justifyContent: 'center',
  },
  saveLibBoxOn: { backgroundColor: GREEN, borderColor: GREEN },
  saveLibTick: { color: '#FFF', fontSize: 13, fontWeight: '700' },
  saveLibTxt: { fontSize: 14, color: TEXT },
  pedToggle: { paddingVertical: 10, marginBottom: 6 },
  pedToggleTxt: { fontSize: 13, fontWeight: '700', color: GREEN },
  pedBox: {
    backgroundColor: CARD, borderRadius: 10, borderWidth: 1, borderColor: BORDER,
    padding: 12, marginBottom: 14,
  },
  pedGen: {
    fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: '700',
    color: MUTED, marginBottom: 6,
  },
  studLibPed: { fontSize: 11.5, color: '#6E5773', marginTop: 2 },
  studLibInc: { fontSize: 11.5, color: '#6E5210', marginTop: 2 },
  incText: {
    fontSize: 12, color: '#6E5210', backgroundColor: '#F7EDD5',
    borderRadius: 7, paddingHorizontal: 9, paddingVertical: 7,
    marginTop: 8, lineHeight: 17,
  },
  cmpNotes: { fontSize: 12.5, color: MUTED, lineHeight: 17, marginTop: 8, fontStyle: 'italic' },

  viewToggle: { flexDirection: 'row', gap: 6, marginBottom: 14 },
  viewToggleBtn: {
    flex: 1, alignItems: 'center', paddingVertical: 8, borderRadius: 9,
    borderWidth: 1, borderColor: BORDER, backgroundColor: CARD,
  },
  viewToggleBtnOn: { backgroundColor: RED, borderColor: RED },
  viewToggleTxt: { fontSize: 13, color: MUTED, fontWeight: '600' },
  viewToggleTxtOn: { color: CREAM, fontWeight: '700' },

  // Cards / List
  densityBtn: {
    flex: 1, alignItems: 'center', paddingVertical: 7, borderRadius: 9,
    borderWidth: 1, borderColor: BORDER, backgroundColor: CARD,
  },
  densityBtnOn: { backgroundColor: RED, borderColor: RED },
  densityTxt: { fontSize: 12, color: MUTED, fontWeight: '600' },
  densityTxtOn: { color: CREAM, fontWeight: '700' },
  denseRow: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: CARD, paddingVertical: 10, paddingHorizontal: 12,
    borderBottomWidth: 0.5, borderBottomColor: BORDER,
  },
  denseName: { fontSize: 15, fontWeight: '700', color: TEXT },
  denseSub: { fontSize: 12, color: MUTED, marginTop: 1 },
  denseRight: { fontSize: 13, fontWeight: '600', color: TEXT, maxWidth: 110, textAlign: 'right' },
  denseChevron: { fontSize: 14, color: MUTED },
  denseTag: {
    backgroundColor: RED + '14', borderRadius: 6, paddingHorizontal: 6, paddingVertical: 2, maxWidth: 96,
  },
  denseTagTxt: { fontSize: 10, color: RED, fontWeight: '700' },
  // Day sheet
  dayRow: {
    backgroundColor: CARD, borderRadius: 10, borderWidth: 0.5, borderColor: BORDER,
    padding: 12, marginBottom: 8,
  },
  dayRowOpen: { borderColor: GREEN, borderWidth: 1.5 },
  dayName: { fontSize: 15, fontWeight: '700', color: TEXT },
  dayFooter: {
    marginTop: 14, padding: 12, backgroundColor: CARD,
    borderRadius: 10, borderWidth: 0.5, borderColor: BORDER,
  },

  // Calendar view on Milestones & Tasks
  calHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 },
  calNav: { paddingHorizontal: 14, paddingVertical: 4 },
  calNavTxt: { fontSize: 26, color: TEXT, lineHeight: 30 },
  calTitle: { fontSize: 16, fontWeight: '700', color: TEXT },
  calGrid: { backgroundColor: CARD, borderRadius: 12, borderWidth: 1, borderColor: BORDER, paddingVertical: 4, overflow: 'hidden' },
  calRow: { flexDirection: 'row' },
  calRowLine: { borderTopWidth: 0.5, borderTopColor: BORDER },
  calCell: { flex: 1, alignItems: 'center', justifyContent: 'flex-start', paddingVertical: 6, minHeight: 58 },
  calDow: { fontSize: 12, color: MUTED, fontWeight: '700', paddingVertical: 2 },
  calDayWrap: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  calDayToday: { borderWidth: 2, borderColor: RED },
  calDaySel: { backgroundColor: RED },
  calDayTxt: { fontSize: 16, color: TEXT, fontWeight: '600' },
  calDayTxtSel: { color: CREAM, fontWeight: '800' },
  calBadge: { minWidth: 22, height: 18, borderRadius: 9, paddingHorizontal: 6, backgroundColor: GREEN, alignItems: 'center', justifyContent: 'center', marginTop: 3 },
  calBadgeOverdue: { backgroundColor: RED },
  calBadgeDone: { backgroundColor: MUTED },
  calBadgeTxt: { fontSize: 12, color: '#FFF', fontWeight: '800' },
  // Week planner
  wkRow: { flexDirection: 'row', paddingHorizontal: 10, paddingVertical: 6, minHeight: 56 },
  wkRowToday: { backgroundColor: RED + '0D' },
  wkDay: { width: 48, alignItems: 'center', paddingTop: 2 },
  wkDow: { fontSize: 11, color: MUTED, fontWeight: '700', marginBottom: 2, textTransform: 'uppercase' },
  wkEmpty: { fontSize: 14, color: BORDER, paddingTop: 8, paddingLeft: 10 },
  wkItem: { flexDirection: 'row', alignItems: 'flex-start', gap: 8, paddingVertical: 5, paddingLeft: 6 },
  wkDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: GREEN, marginTop: 6 },
  wkItemTitle: { fontSize: 15, color: TEXT, fontWeight: '700' },
  wkItemWho: { fontSize: 12, color: MUTED, marginTop: 1 },
  // Multi-select on Milestones & Tasks
  taskRowSel: { borderWidth: 1.5, borderColor: RED },
  bulkBar: {
    position: 'absolute', left: 0, right: 0, bottom: 0,
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingHorizontal: 16, paddingTop: 12, paddingBottom: (Platform.OS === 'ios' ? 28 : 14) + NAV_INSET,
    backgroundColor: CARD, borderTopWidth: 1, borderTopColor: BORDER,
  },
  bulkBarTxt: { flex: 1, fontSize: 14, fontWeight: '700', color: TEXT },
  bulkBtn: { backgroundColor: RED, borderRadius: 10, paddingVertical: 10, paddingHorizontal: 14 },
  bulkBtnGreen: { backgroundColor: GREEN },
  bulkBtnTxt: { color: '#FFF', fontSize: 14, fontWeight: '700' },

  listGroup: {
    backgroundColor: CARD, borderRadius: 12, borderWidth: 1,
    borderColor: BORDER, overflow: 'hidden', marginBottom: 14,
  },
  listHead: {
    fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: '700',
    color: MUTED, paddingHorizontal: 12, paddingTop: 11, paddingBottom: 5, backgroundColor: BG,
  },
  listRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingHorizontal: 12, paddingVertical: 11,
    borderBottomWidth: 1, borderBottomColor: BORDER, borderLeftWidth: 3,
  },
  listMare: { flex: 1, fontSize: 13.5, fontWeight: '700', color: TEXT },
  listStud: { fontSize: 12, color: MUTED, maxWidth: '38%' },
  listFee: { fontSize: 12.5, color: TEXT, fontWeight: '600' },

  planCompareBtn: {
    marginTop: 9, alignItems: 'center', paddingVertical: 8, borderRadius: 9,
    borderWidth: 1, borderColor: '#55697A', backgroundColor: '#E9EDF1',
  },
  planCompareBtnTxt: { fontSize: 13, fontWeight: '700', color: '#37444F' },

  cmpCard: {
    backgroundColor: BG, borderRadius: 11, borderWidth: 1, borderColor: BORDER,
    padding: 12, marginBottom: 10,
  },
  cmpHead: { flexDirection: 'row', alignItems: 'baseline', gap: 10, marginBottom: 6 },
  cmpName: { flex: 1, fontSize: 15, fontWeight: '700', color: TEXT },
  cmpFee: { fontSize: 14, fontWeight: '700', color: TEXT },
  cmpPed: { marginTop: 2 },
  cmpPedLine: { flexDirection: 'row', gap: 8, paddingVertical: 2 },
  cmpPedRole: { width: 34, fontSize: 11, color: MUTED },
  cmpPedName: { fontSize: 13, fontWeight: '600', color: TEXT, flexShrink: 1 },
  cmpPedSub: { fontSize: 11.5, color: '#55697A', paddingLeft: 42, paddingBottom: 3 },
  cmpNoPed: { fontSize: 11.5, color: MUTED, fontStyle: 'italic', marginTop: 2 },
  cmpChoose: {
    marginTop: 10, alignItems: 'center', paddingVertical: 9,
    borderRadius: 9, backgroundColor: GREEN,
  },
  cmpChooseTxt: { fontSize: 13, fontWeight: '700', color: '#FFF' },

  planMareTap: { flexDirection: 'row', alignItems: 'center', gap: 4, flexShrink: 1 },
  planMareChevron: { fontSize: 17, color: MUTED, lineHeight: 20 },
  planOpenHint: { fontSize: 11.5, color: GREEN, fontWeight: '700', marginTop: 4 },

  detailHead: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 14 },
  detailStage: {
    fontSize: 11, fontWeight: '700', color: CREAM, backgroundColor: RED,
    borderRadius: 5, paddingHorizontal: 8, paddingVertical: 3, letterSpacing: 0.4,
  },
  detailSeason: { fontSize: 12.5, color: MUTED },
  detailLabel: {
    fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: '700',
    color: MUTED, marginTop: 12, marginBottom: 5,
  },
  detailValue: { fontSize: 14.5, color: TEXT, fontWeight: '600' },
  detailSmall: { fontSize: 12, color: MUTED, marginTop: 4 },
  detailLinkRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  detailLinkTxt: { fontSize: 14.5, color: GREEN, fontWeight: '700' },
  detailLinkChevron: { fontSize: 16, color: GREEN },
  detailStudBox: {
    backgroundColor: BG, borderRadius: 11, borderWidth: 1, borderColor: BORDER, padding: 12,
  },
  methodWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 7 },
  methodChip: {
    paddingHorizontal: 12, paddingVertical: 8, borderRadius: 20,
    borderWidth: 1, borderColor: BORDER, backgroundColor: CARD,
  },
  methodChipOn: { backgroundColor: '#E9F0EA', borderColor: GREEN },
  methodChipTxt: { fontSize: 13, color: MUTED },
  methodChipTxtOn: { color: '#2E4A37', fontWeight: '700' },

  contractTitle: { fontSize: 21, fontWeight: '700', color: TEXT },
  contractSub: { fontSize: 13, color: MUTED, marginTop: 3, marginBottom: 6 },
  contractHead: {
    fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: '700',
    color: MUTED, marginTop: 18, marginBottom: 6,
  },
  contractCard: {
    backgroundColor: CARD, borderRadius: 12, borderWidth: 1,
    borderColor: BORDER, paddingHorizontal: 13, paddingVertical: 4,
  },
  contractRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    gap: 12, paddingVertical: 9, borderBottomWidth: 1, borderBottomColor: BORDER,
  },
  contractKey: { fontSize: 13, color: MUTED },
  contractVal: { fontSize: 13.5, fontWeight: '600', color: TEXT, textAlign: 'right', flexShrink: 1 },
  contractBtn: {
    marginVertical: 10, alignItems: 'center', paddingVertical: 10, borderRadius: 9,
    borderWidth: 1, borderStyle: 'dashed', borderColor: GREEN,
  },
  contractBtnTxt: { fontSize: 13, fontWeight: '700', color: GREEN },
  contractHint: { fontSize: 12, color: MUTED, lineHeight: 17, paddingVertical: 10 },
  contractDone: {
    backgroundColor: '#E9F0EA', borderRadius: 11, borderWidth: 1, borderColor: GREEN,
    padding: 12, marginTop: 12,
  },
  contractDoneTxt: { fontSize: 13.5, fontWeight: '700', color: '#2E4A37' },
  contractDoneLink: { fontSize: 13, fontWeight: '700', color: GREEN, marginTop: 6 },

  payChip: {
    fontSize: 10, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5,
    borderRadius: 5, paddingHorizontal: 8, paddingVertical: 3, overflow: 'hidden',
  },
  payChip_none: { backgroundColor: '#EDEFF2', color: '#37444F' },
  payChip_unpaid: { backgroundColor: '#F2E5E3', color: '#5C2020' },
  payChip_partial: { backgroundColor: '#F7EDD5', color: '#6E5210' },
  payChip_paid: { backgroundColor: '#E4EDE7', color: '#2E4A37' },

  docRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10 },
  docIcon: { fontSize: 20 },
  docName: { fontSize: 13.5, fontWeight: '600', color: TEXT },
  docMeta: { fontSize: 11, color: MUTED, marginTop: 2 },
  docOpen: { fontSize: 12.5, fontWeight: '700', color: GREEN },

  toggleRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 11 },
  toggleLabel: { fontSize: 13.5, color: TEXT, flex: 1 },
  switchTrack: { width: 44, height: 25, borderRadius: 20, backgroundColor: BORDER, padding: 2 },
  switchTrackOn: { backgroundColor: GREEN },
  switchThumb: { width: 21, height: 21, borderRadius: 11, backgroundColor: '#FFF' },
  switchThumbOn: { marginLeft: 19 },

  checkRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10,
    borderBottomWidth: 1, borderBottomColor: BORDER,
  },
  checkBox: {
    width: 21, height: 21, borderRadius: 6, borderWidth: 1.5, borderColor: BORDER,
    alignItems: 'center', justifyContent: 'center',
  },
  checkBoxOn: { backgroundColor: GREEN, borderColor: GREEN },
  checkTick: { color: '#FFF', fontSize: 12, fontWeight: '700' },
  checkName: { flex: 1, fontSize: 13.5, color: TEXT },
  checkNameDone: { color: MUTED },
  checkDate: { fontSize: 11.5, color: MUTED },

  inFoalBtn: {
    backgroundColor: RED, borderRadius: 12, paddingVertical: 14, paddingHorizontal: 16,
    alignItems: 'center', marginTop: 22,
  },
  inFoalBtnOff: { opacity: 0.45 },
  inFoalBtnTxt: { fontSize: 15, fontWeight: '700', color: CREAM },
  inFoalBtnSub: { fontSize: 11, color: CREAM, opacity: 0.8, marginTop: 4, textAlign: 'center', lineHeight: 15 },
  didntTakeBtn: {
    backgroundColor: '#F2E5E3', borderRadius: 11, borderWidth: 1, borderColor: '#E0C9C5',
    paddingVertical: 12, paddingHorizontal: 14, alignItems: 'center', marginTop: 12,
  },
  didntTakeTxt: { fontSize: 13.5, fontWeight: '700', color: '#5C2020' },
  didntTakeSub: { fontSize: 11, color: '#5C2020', opacity: 0.8, marginTop: 3, textAlign: 'center' },

  detailComing: {
    fontSize: 12, color: MUTED, fontStyle: 'italic', lineHeight: 17, marginTop: 8,
  },
  cancelBtn: {
    flex: 1,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: BORDER,
    paddingVertical: 14,
    alignItems: 'center',
  },
  cancelBtnText: { fontSize: 16, fontWeight: '600', color: MUTED },
  saveBtn: {
    flex: 2,
    borderRadius: 12,
    backgroundColor: RED,
    paddingVertical: 14,
    alignItems: 'center',
  },
  saveBtnText: { fontSize: 16, fontWeight: '700', color: '#FFF' },

  // Summary card (horse detail)
  summaryCard: {
    backgroundColor: CARD,
    borderRadius: 14,
    padding: 16,
    marginBottom: 22,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.07,
    shadowRadius: 4,
    elevation: 2,
  },
  summaryRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  summaryLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: 3,
  },
  summaryValue: { fontSize: 20, fontWeight: '700', color: TEXT },
  summaryNote: { fontSize: 12, color: MUTED, marginTop: 3 },
  divider: { height: 1, backgroundColor: BORDER, marginVertical: 12 },
  soldBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    backgroundColor: '#FFF4E0', borderColor: '#E0A84A', borderWidth: 1, borderRadius: 12,
    padding: 12, marginBottom: 12,
  },
  soldBannerTitle: { fontSize: 15, fontWeight: '700', color: '#7A4A00' },
  soldBannerText: { fontSize: 13, color: '#5C3A00', marginTop: 2 },
  soldBannerHint: { fontSize: 11.5, color: MUTED, marginTop: 4 },
  soldUndoBtn: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8, borderWidth: 1, borderColor: '#E0A84A', backgroundColor: '#FFFFFF' },
  soldUndoTxt: { fontSize: 13, fontWeight: '700', color: '#7A4A00' },
  deleteBtn: {
    backgroundColor: '#FFEBEE',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
  },
  deleteBtnTxt: { fontSize: 13, fontWeight: '600', color: '#E53935' },

  // Age breakdown grid (born horses)
  ageGrid: {
    flexDirection: 'row',
    backgroundColor: BG,
    borderRadius: 10,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: BORDER,
  },
  ageCell: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 12,
  },
  ageCellValue: {
    fontSize: 26,
    fontWeight: '700',
    color: GREEN,
    marginBottom: 2,
  },
  ageCellLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  ageGridDivider: {
    width: 1,
    backgroundColor: BORDER,
    marginVertical: 8,
  },

  // Milestones section
  sectionHeading: {
    fontSize: 12,
    fontWeight: '700',
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: 10,
  },
  loggedInAsTxt: {
    fontSize: 13,
    color: MUTED,
    marginBottom: 10,
  },
  collapsibleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  sectionWrap: {
    backgroundColor: CARD,
    borderRadius: 12,
    borderWidth: 0.5,
    borderColor: BORDER,
    borderLeftWidth: 4,
    overflow: 'hidden',
    marginBottom: 14,
  },
  sectionHeaderBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 11,
    paddingHorizontal: 12,
  },
  sectionEmoji: { fontSize: 16, marginRight: 8 },
  sectionTitle: { fontSize: 15, fontWeight: '700', flex: 1 },
  sectionBadge: { borderRadius: 10, paddingHorizontal: 8, paddingVertical: 2, marginRight: 8 },
  sectionBadgeTxt: { color: '#FFF', fontSize: 12, fontWeight: '700' },
  sectionChevron: { fontSize: 15, fontWeight: '700' },
  sectionBody: { paddingHorizontal: 14, paddingTop: 4, paddingBottom: 14 },

  // Details / Pedigree cards
  detailsCard: {
    backgroundColor: CARD,
    borderRadius: 14,
    padding: 16,
    marginBottom: 22,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.07,
    shadowRadius: 4,
    elevation: 2,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
  },
  detailLabel: { fontSize: 13, color: MUTED, fontWeight: '600' },
  detailValue: { fontSize: 14, color: TEXT, fontWeight: '600', flexShrink: 1, textAlign: 'right' },
  statusChipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 6,
    marginBottom: 18,
  },
  statusChip: {
    backgroundColor: BG,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: BORDER,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  statusChipSelected: {
    backgroundColor: RED,
    borderColor: RED,
  },
  statusChipTxt: { fontSize: 13, fontWeight: '600', color: TEXT },
  statusChipTxtSelected: { color: '#FFF' },
  pedigreeRow: {
    flexDirection: 'row',
    gap: 16,
  },
  pedigreeCell: { flex: 1 },
  pedigreeInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  fromStableBtn: {
    backgroundColor: CARD,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  fromStableBtnTxt: { fontSize: 13, fontWeight: '700', color: GREEN },
  linkedNote: {
    fontSize: 12,
    fontWeight: '600',
    color: GREEN,
    marginTop: 6,
  },
  leaseBtn: {
    alignSelf: 'flex-start',
    backgroundColor: CARD,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginTop: 8,
  },
  leaseBtnTxt: { fontSize: 13, fontWeight: '700', color: TEXT },
  familyRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
  },
  familyRowLabel: { fontSize: 13, color: MUTED, fontWeight: '600' },
  familyRowName: { fontSize: 14, color: TEXT, fontWeight: '600' },
  familyRowNameLinked: { color: GREEN },
  pickerRow: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
  },
  pickerRowName: { fontSize: 15, fontWeight: '700', color: TEXT },
  pickerRowSub: { fontSize: 12, color: MUTED, marginTop: 2 },
  pickerGroupHead: {
    fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: '700',
    color: MUTED, marginTop: 16, marginBottom: 4,
  },
  pickerEmpty: {
    fontSize: 14,
    color: MUTED,
    textAlign: 'center',
    paddingVertical: 24,
  },
  milestone: {
    backgroundColor: CARD,
    borderRadius: 14,
    padding: 14,
    marginBottom: 10,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: 'transparent',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 3,
    elevation: 1,
  },
  milestoneDone: {
    backgroundColor: '#F0F8F3',
    borderColor: GREEN + '50',
  },
  circle: {
    width: 30,
    height: 30,
    borderRadius: 15,
    borderWidth: 2,
    borderColor: BORDER,
    alignItems: 'center',
    justifyContent: 'center',
  },
  circleDone: { backgroundColor: GREEN, borderColor: GREEN },
  tick: { color: '#FFF', fontSize: 15, fontWeight: '700' },
  mHorseName: { fontSize: 12, fontWeight: '700', color: GREEN, marginBottom: 2, textTransform: 'uppercase', letterSpacing: 0.4 },
  mName: { fontSize: 16, fontWeight: '600', color: TEXT, marginBottom: 2 },
  mNameDone: { color: MUTED, textDecorationLine: 'line-through' },
  mDate: { fontSize: 13, color: MUTED },
  mDays: { fontSize: 12, color: RED, fontWeight: '500', marginTop: 2 },
  mNotes: { fontSize: 13, color: TEXT, marginTop: 6, fontStyle: 'italic' },
  mAssignee: { fontSize: 12, color: GREEN, fontWeight: '600', marginTop: 4 },
  markBtn: {
    backgroundColor: RED,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 9,
    alignItems: 'center',
    minWidth: 74,
  },
  markBtnDone: { backgroundColor: '#E0E0E0' },
  markBtnTxt: {
    fontSize: 12,
    fontWeight: '700',
    color: '#FFF',
    textAlign: 'center',
    lineHeight: 16,
  },
  markBtnTxtDone: { color: MUTED },
  deleteMilestoneBtn: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: '#F5F5F5',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  deleteMilestoneBtnTxt: { fontSize: 13, fontWeight: '700', color: MUTED },
  addMilestoneBtn: {
    backgroundColor: CARD,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: BORDER,
    borderStyle: 'dashed',
    paddingVertical: 14,
    alignItems: 'center',
    marginBottom: 10,
  },
  addMilestoneBtnTxt: { fontSize: 15, fontWeight: '700', color: GREEN },

  // Sort bar
  sortBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: CARD,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 11,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: BORDER,
  },
  sortBarLabel: { fontSize: 14, color: MUTED, fontWeight: '600' },
  sortBarValue: { color: RED, fontWeight: '700' },
  sortBarChevron: { fontSize: 11, color: MUTED },
  sortMenu: {
    backgroundColor: CARD,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: BORDER,
    marginBottom: 12,
    overflow: 'hidden',
  },
  sortMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 13,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
  },
  sortMenuItemActive: { backgroundColor: RED + '0D' },
  sortMenuItemTxt: { fontSize: 15, color: TEXT },
  sortMenuItemTxtActive: { color: RED, fontWeight: '700' },
  sortMenuItemCheck: { fontSize: 14, color: RED, fontWeight: '700' },

  // Sub-sort within status groups
  subSortRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 10,
    marginBottom: 4,
  },
  subSortLabel: { fontSize: 13, color: MUTED, fontWeight: '600', marginRight: 2 },
  subSortChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: BORDER,
    backgroundColor: CARD,
  },
  subSortChipActive: { borderColor: RED, backgroundColor: RED + '15' },
  subSortChipTxt: { fontSize: 13, fontWeight: '600', color: MUTED },
  subSortChipTxtActive: { color: RED },

  // Group headers
  groupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    gap: 8,
  },
  groupHeaderTxt: {
    fontSize: 13,
    fontWeight: '700',
    color: RED,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  groupCount: {
    backgroundColor: RED + '18',
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  groupCountTxt: { fontSize: 12, fontWeight: '700', color: RED },

  // Card action buttons row (Edit Name + Delete)
  cardActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    flexBasis: '100%',
    gap: 8,
    marginTop: 12,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  editNameBtn: {
    backgroundColor: '#E3F2FD',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
  },
  editNameBtnTxt: { fontSize: 13, fontWeight: '600', color: '#1565C0' },
  bornBtn: {
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
  },
  bornBtnTxt: { fontSize: 13, fontWeight: '600', color: GREEN },

  shareBtn: {
    backgroundColor: '#F3E5F5',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
  },
  shareBtnTxt: { fontSize: 13, fontWeight: '600', color: '#6A1B9A' },

  sharesCard: {
    backgroundColor: CARD,
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: BORDER,
  },
  sharedByTxt: { fontSize: 13, color: MUTED, fontStyle: 'italic' },
  shareRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    gap: 8,
  },
  shareRowName: { fontSize: 14, fontWeight: '600', color: TEXT },
  shareRowEmail: { fontSize: 12, color: MUTED },
  permBadge: {
    backgroundColor: '#E3F2FD',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  permBadgeEdit: { backgroundColor: '#E8F5E9' },
  permBadgeTxt: { fontSize: 11, fontWeight: '600', color: '#1565C0' },
  permBadgeTxtEdit: { color: GREEN },
  revokeBtn: {
    backgroundColor: '#FFEBEE',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  revokeBtnTxt: { fontSize: 12, fontWeight: '600', color: '#E53935' },
  sharesEmpty: { fontSize: 13, color: MUTED, fontStyle: 'italic', textAlign: 'center', paddingVertical: 8 },

  sharedBanner: {
    backgroundColor: '#FDF6E9',
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: GOLD,
  },
  sharedBannerTitle: { fontSize: 15, fontWeight: '700', color: RED, marginBottom: 8 },
  sharedBannerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: GOLD,
  },
  sharedBannerHorseName: { fontSize: 14, fontWeight: '600', color: RED },
  sharedBannerPerm: { fontSize: 12, color: '#8B6914' },
  sharedBannerDismiss: {
    marginTop: 10,
    alignSelf: 'flex-end',
    backgroundColor: RED,
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 8,
  },
  sharedBannerDismissTxt: { fontSize: 13, fontWeight: '600', color: GOLD },

  requestBanner: {
    backgroundColor: '#FFF3E0',
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
    borderWidth: 1.5,
    borderColor: '#FB8C00',
  },
  requestBannerTitle: { fontSize: 15, fontWeight: '700', color: '#E65100', marginBottom: 6 },
  requestBannerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: '#FFCC80',
    gap: 8,
  },
  requestBannerText: { fontSize: 13, color: '#5D4037', flex: 1 },
  requestBannerChevron: { fontSize: 13, fontWeight: '700', color: '#E65100' },

  dueBanner: {
    backgroundColor: '#E8F1FB',
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
    borderWidth: 1.5,
    borderColor: '#1E88E5',
  },
  dueBannerTitle: { fontSize: 15, fontWeight: '700', color: '#0D47A1', marginBottom: 6 },
  dueBannerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: '#BBDEFB',
    gap: 8,
  },
  dueBannerText: { fontSize: 13, color: '#263238', flex: 1 },
  dueBannerDate: { fontSize: 12, color: '#607D8B', fontWeight: '600' },
  dueBannerTag: { fontSize: 13, fontWeight: '700', color: '#1565C0' },
  dueBannerTagOverdue: { color: '#C62828' },
  dueBannerMore: { fontSize: 13, color: '#0D47A1', marginTop: 8, fontWeight: '700' },

  // Health & Lameness
  inputMultiline: { minHeight: 80, textAlignVertical: 'top' },
  healthHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 },
  healthBadge: { backgroundColor: '#FDECEA', borderRadius: 12, paddingHorizontal: 10, paddingVertical: 4, borderWidth: 1, borderColor: '#E57373' },
  healthBadgeTxt: { fontSize: 12, fontWeight: '700', color: '#C62828' },
  horseHealthBadge: { fontSize: 12, fontWeight: '700', color: '#C62828', marginTop: 2 },
  healthToggleRow: { flexDirection: 'row', gap: 8, marginBottom: 12 },
  healthToggleBtn: { flex: 1, paddingVertical: 8, borderRadius: 8, backgroundColor: '#F1F1F1', alignItems: 'center' },
  healthToggleBtnActive: { backgroundColor: GREEN },
  healthToggleTxt: { fontSize: 13, fontWeight: '600', color: MUTED },
  healthToggleTxtActive: { color: '#FFF' },
  healthEmpty: { fontSize: 13, color: MUTED, fontStyle: 'italic', textAlign: 'center', paddingVertical: 10 },
  incidentRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderTopWidth: 1, borderTopColor: '#EEE', gap: 8 },
  incidentName: { fontSize: 15, fontWeight: '700', color: TEXT },
  incidentMeta: { fontSize: 12, color: MUTED, marginTop: 2 },
  addTypeRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  addTypeBtn: { backgroundColor: '#5E35B1', borderRadius: 8, paddingHorizontal: 16, paddingVertical: 12 },
  addTypeBtnTxt: { color: '#FFF', fontWeight: '700', fontSize: 14 },
  incidentTagWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 6 },
  incidentTagChip: { backgroundColor: '#EDE7F6', borderRadius: 10, paddingHorizontal: 8, paddingVertical: 3 },
  incidentTagChipTxt: { fontSize: 11, fontWeight: '600', color: '#5E35B1' },
  incidentStatus: { borderRadius: 12, paddingHorizontal: 10, paddingVertical: 4, alignSelf: 'flex-start' },
  incidentStatusOngoing: { backgroundColor: '#FFF3E0', borderWidth: 1, borderColor: '#FB8C00' },
  incidentStatusSound: { backgroundColor: '#E8F5E9', borderWidth: 1, borderColor: '#43A047' },
  incidentStatusTxt: { fontSize: 12, fontWeight: '700' },
  incidentStatusTxtOngoing: { color: '#E65100' },
  incidentStatusTxtSound: { color: '#2E7D32' },
  incidentBodyText: { fontSize: 14, color: TEXT, lineHeight: 20 },
  createIncidentBtn: { marginTop: 12, backgroundColor: '#EDE7F6', borderRadius: 8, paddingVertical: 10, alignItems: 'center' },
  createIncidentBtnTxt: { fontSize: 14, fontWeight: '700', color: '#5E35B1' },
  addPhotoBtn: { marginTop: 8, backgroundColor: '#E3F2FD', borderRadius: 8, paddingVertical: 10, alignItems: 'center' },
  addPhotoBtnTxt: { fontSize: 14, fontWeight: '700', color: '#1565C0' },
  incLogRow: { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 10, borderTopWidth: 1, borderTopColor: '#EEE', gap: 8 },
  incLogMeta: { fontSize: 11, color: MUTED, marginTop: 2 },
  incLogTypeChip: { backgroundColor: '#E0F2F1', borderRadius: 8, paddingHorizontal: 8, paddingVertical: 2 },
  incLogTypeChipTxt: { fontSize: 11, fontWeight: '700', color: '#00695C' },
  incLogAction: { marginTop: 6, paddingLeft: 10, borderLeftWidth: 2, borderLeftColor: '#4DB6AC' },
  incLogActionLabel: { fontSize: 11, fontWeight: '700', color: '#00695C', marginBottom: 2 },

  calHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  calNavBtn: { width: 36, height: 36, borderRadius: 8, backgroundColor: '#F1F1F1', alignItems: 'center', justifyContent: 'center' },
  calNavTxt: { fontSize: 20, fontWeight: '700', color: TEXT },
  calMonth: { fontSize: 15, fontWeight: '700', color: TEXT },
  calRow: { flexDirection: 'row', gap: 2, marginBottom: 2 },
  calDow: { flex: 1, textAlign: 'center', fontSize: 11, color: MUTED, paddingVertical: 2 },
  calCell: { flex: 1, minHeight: 52, borderWidth: 0.5, borderColor: '#EEE', borderRadius: 5, paddingTop: 2, paddingBottom: 3 },
  calCellBlank: { flex: 1, minHeight: 52 },
  calCellToday: { borderColor: GREEN, borderWidth: 1.5 },
  calDay: { fontSize: 10, color: MUTED, textAlign: 'center', marginBottom: 2 },
  calLane: { height: 6, marginBottom: 2 },
  calLaneEmpty: { height: 6, marginBottom: 2 },
  calOverflow: { fontSize: 9, color: MUTED, textAlign: 'center', fontWeight: '700' },
  calLegend: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 10 },
  calLegendItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  calLegendTxt: { fontSize: 12, color: TEXT },
  calDot: { width: 9, height: 9, borderRadius: 2 },
  calDetail: { marginTop: 12, borderTopWidth: 1, borderTopColor: '#EEE', paddingTop: 10 },
  calDetailDate: { fontSize: 13, fontWeight: '700', color: TEXT, marginBottom: 6 },
  calDetailRow: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 3 },
  calDetailName: { fontSize: 14, color: TEXT, fontWeight: '600' },
  calDetailMeta: { fontSize: 12, color: MUTED, flexShrink: 1 },
  calDetailChevron: { marginLeft: 'auto', fontSize: 18, fontWeight: '700', color: MUTED, paddingLeft: 6 },
  incDeleteTxt: { fontSize: 15, color: '#E53935', fontWeight: '700', paddingHorizontal: 6, paddingVertical: 2 },

  // Recipient mare badge (embryo detail)
  recipBadgeWrap: { marginTop: 10 },
  recipBadge: {
    alignSelf: 'flex-start',
    backgroundColor: '#FFF3E0',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: '#FFB300',
  },
  recipBadgeTxt: { fontSize: 12, fontWeight: '700', color: '#E65100' },

  // Recip toggle (add modal)
  recipToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 18,
    gap: 12,
  },
  recipToggleTrack: {
    width: 44,
    height: 26,
    borderRadius: 13,
    backgroundColor: BORDER,
    justifyContent: 'center',
    padding: 2,
  },
  recipToggleTrackOn: { backgroundColor: RED },
  recipToggleThumb: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#FFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 2,
    elevation: 2,
  },
  recipToggleThumbOn: { alignSelf: 'flex-end' },
  recipToggleLabel: { fontSize: 15, color: TEXT },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: BORDER,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  radioOuterSelected: { borderColor: RED },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: RED,
  },

  // Category chips
  categoryChip: {
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: BORDER,
    backgroundColor: CARD,
  },
  categoryChipActive: { borderColor: RED, backgroundColor: RED + '15' },
  categoryChipTxt: { fontSize: 13, fontWeight: '600', color: MUTED },
  categoryChipTxtActive: { color: RED },
  categoryBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 8,
    backgroundColor: GREEN + '18',
    marginBottom: 2,
  },
  categoryBadgeTxt: { fontSize: 11, fontWeight: '700', color: GREEN, textTransform: 'uppercase', letterSpacing: 0.4 },

  // Notes section
  notesCard: {
    backgroundColor: CARD,
    borderRadius: 14,
    padding: 14,
    marginBottom: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.07,
    shadowRadius: 4,
    elevation: 2,
  },
  notesInput: {
    fontSize: 15,
    color: TEXT,
    minHeight: 100,
    lineHeight: 22,
  },
  tlWho: { fontSize: 12, color: MUTED, fontWeight: '600' },
  tlWhat: { fontSize: 15, color: TEXT, fontWeight: '700', marginTop: 2 },
  tlDate: { fontSize: 13, color: TEXT, fontWeight: '600' },
  tlAway: { fontSize: 11, color: MUTED, marginTop: 2 },
  timelineItem: {
    backgroundColor: CARD,
    borderRadius: 14,
    padding: 14,
    marginBottom: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.07,
    shadowRadius: 4,
    elevation: 2,
  },
  noNotesTxt: { fontSize: 14, color: MUTED, marginBottom: 10 },
  earningsTotalsRow: { flexDirection: 'row', backgroundColor: CARD, borderRadius: 10, borderWidth: 1, borderColor: BORDER, marginBottom: 12, overflow: 'hidden' },
  earningsTotalCell: { flex: 1, paddingVertical: 12, paddingHorizontal: 14, alignItems: 'center' },
  earningsTotalDivider: { width: 1, backgroundColor: BORDER },
  earningsTotalLabel: { fontSize: 11, color: MUTED, fontWeight: '600', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 },
  earningsTotalValue: { fontSize: 18, fontWeight: '800' },
  earningsCard: { backgroundColor: CARD, borderRadius: 10, borderWidth: 1, borderColor: BORDER, padding: 12, marginBottom: 10 },
  earningsTypeBadge: { backgroundColor: GREEN + '22', borderRadius: 4, paddingHorizontal: 8, paddingVertical: 2, marginRight: 8 },
  earningsTypeBadgeOffspring: { backgroundColor: RED + '1A' },
  earningsTypeBadgeTxt: { fontSize: 11, fontWeight: '700', color: GREEN, letterSpacing: 0.3 },
  earningsEventDate: { fontSize: 12, color: MUTED },
  earningsEventName: { fontSize: 15, fontWeight: '700', color: TEXT, marginBottom: 2 },
  earningsEventAmount: { fontSize: 16, fontWeight: '800', color: GREEN, marginBottom: 2 },
  earningsEventNotes: { fontSize: 13, color: MUTED, marginTop: 4 },
  herdEarningsNote: { fontSize: 12, color: MUTED, lineHeight: 17, marginTop: 14, paddingHorizontal: 4 },
  dupWarnBox: {
    marginTop: 8,
    padding: 8,
    borderRadius: 8,
    backgroundColor: '#FDF3E3',
    borderLeftWidth: 3,
    borderLeftColor: '#E8A23B',
  },
  dupWarnTxt: { fontSize: 12, color: '#7A5A1E', lineHeight: 17 },
  dupWarnDelete: { fontSize: 12, color: RED, fontWeight: '700', marginTop: 6 },
  offspringBreakdownCard: { backgroundColor: CARD, borderRadius: 10, borderWidth: 1, borderColor: BORDER, padding: 12, marginBottom: 12 },
  offspringBreakdownTitle: { fontSize: 11, fontWeight: '700', color: MUTED, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 },
  offspringBreakdownRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 4 },
  offspringBreakdownName: { fontSize: 14, color: TEXT, fontWeight: '600' },
  offspringBreakdownAmt: { fontSize: 14, fontWeight: '700', color: RED },
  typeToggleBtn: { flex: 1, paddingVertical: 10, borderRadius: 8, borderWidth: 1, borderColor: BORDER, alignItems: 'center', backgroundColor: CARD },
  typeToggleBtnActive: { backgroundColor: RED, borderColor: RED },
  typeToggleBtnTxt: { fontSize: 14, fontWeight: '600', color: MUTED },
  typeToggleBtnTxtActive: { color: CREAM },
  noteDate: { fontSize: 12, fontWeight: '700', color: MUTED, marginBottom: 2 },
  notePreview: { fontSize: 14, color: TEXT },
  noteText: { fontSize: 15, color: TEXT, lineHeight: 22, marginTop: 10 },
  noteDeleteTxt: { fontSize: 13, fontWeight: '700', color: '#D9534F', marginTop: 12 },
  rootLoading: {
    flex: 1,
    backgroundColor: BG,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Dashboard
  dashTitle: { fontSize: 24, fontWeight: '800', color: TEXT },
  dashSub: { fontSize: 14, color: MUTED, marginTop: 4, marginBottom: 16 },
  dashHero: {
    backgroundColor: RED,
    paddingTop: 0,
    paddingBottom: 14,
    paddingHorizontal: 24,
    alignItems: 'center',
    overflow: 'hidden',
  },
  dashCorner: { position: 'absolute', width: 12, height: 12 },
  dashCornerTL: { top: 8, left: 8, borderTopWidth: 1.5, borderLeftWidth: 1.5, borderColor: '#E8A23B', opacity: 0.6 },
  dashCornerTR: { top: 8, right: 8, borderTopWidth: 1.5, borderRightWidth: 1.5, borderColor: '#E8A23B', opacity: 0.6 },
  dashCornerBL: { bottom: 8, left: 8, borderBottomWidth: 1.5, borderLeftWidth: 1.5, borderColor: '#E8A23B', opacity: 0.6 },
  dashCornerBR: { bottom: 8, right: 8, borderBottomWidth: 1.5, borderRightWidth: 1.5, borderColor: '#E8A23B', opacity: 0.6 },
  dashHorseSilhouette: { position: 'absolute', right: -8, bottom: -6, width: 110, height: 110, opacity: 0.4 },
  dashHeroStar: { fontSize: 22, color: '#E8A23B', lineHeight: 28, marginBottom: 2 },
  dashHeroAppName: { fontSize: 26, color: '#fff', letterSpacing: -0.3, marginBottom: 2, fontFamily: 'Arvo_700Bold' },
  dashHeroAppNameAccent: { color: '#E8A23B', fontFamily: 'Arvo_700Bold' },
  dashHeroTitle: { fontSize: 11, fontWeight: '400', fontStyle: 'italic', color: CREAM, textAlign: 'center', letterSpacing: 0.4, marginBottom: 10, opacity: 0.85, fontFamily: 'Georgia', lineHeight: 16 },
  dashHeroPoweredRow: { flexDirection: 'row', alignItems: 'center', width: '100%', gap: 8 },
  dashHeroPoweredLine: { flex: 1, height: 0.5, backgroundColor: '#E8A23B', opacity: 0.45 },
  dashHeroPoweredText: { fontSize: 8, color: '#E8A23B', letterSpacing: 2, fontWeight: '600', opacity: 0.85 },
  statsRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 16,
  },
  statCard: {
    flex: 1,
    backgroundColor: CARD,
    borderRadius: 14,
    paddingVertical: 16,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: BORDER,
  },
  statNumber: { fontSize: 24, fontWeight: '800', color: TEXT },
  statLabel: { fontSize: 12, color: MUTED, marginTop: 4, textAlign: 'center' },
  upcomingBanner: {
    backgroundColor: RED + '14',
    borderRadius: 12,
    padding: 12,
    marginBottom: 16,
  },
  upcomingBannerTxt: { fontSize: 13, fontWeight: '600', color: RED },
  formCard: {
    backgroundColor: CARD,
    borderRadius: 14,
    padding: 14,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: BORDER,
  },
  sectionCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: CARD,
    borderRadius: 14,
    padding: 14,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: BORDER,
    gap: 12,
  },
  sectionCardIcon: { fontSize: 26 },
  sectionCardTitle: { fontSize: 15, fontWeight: '700', color: TEXT },
  sectionCardSub: { fontSize: 12, color: MUTED, marginTop: 2 },
  sectionCardChevron: { fontSize: 22, color: MUTED },
});

// Incentives are free text — a running list the owner types. A handful of
// records were written while it was an array, so accept either shape.
const incentiveText = (v) => (Array.isArray(v) ? v.join('\n') : (v || '')).trim();

// Saved stallions offered alongside your own horses when picking a sire. Their
// pedigree was typed once in the stud library, so choosing one fills the sire
// and both of his parents — the same shortcut a herd horse already gives you.
function StudPickerRows({ studs, search, onPick }) {
  const q = (search || '').trim().toLowerCase();
  const shown = (studs || []).filter((st) => !q || (st.name || '').toLowerCase().includes(q));
  if (shown.length === 0) return null;
  return (
    <>
      <Text style={styles.pickerGroupHead}>From your stud library</Text>
      {shown.map((st) => (
        <TouchableOpacity key={st.id} style={styles.pickerRow} onPress={() => onPick(st)} activeOpacity={0.75}>
          <Text style={styles.pickerRowName}>{st.name}</Text>
          <Text style={styles.pickerRowSub}>
            {st.sire || st.dam ? (st.sire || '?') + ' × ' + (st.dam || '?') : 'No pedigree saved'}
            {st.farm ? ' · ' + st.farm : ''}
          </Text>
        </TouchableOpacity>
      ))}
    </>
  );
}

// ─── BREEDING PLANNING ────────────────────────────────────────────────────────

// Money for plan fees. Whole dollars — stud fees aren't quoted in cents.
const planMoney = (n) =>
  n == null || n === '' ? '—' : '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });

// Seasons offered in the picker: everything already planned, plus this year and
// the next two, so a new user always has somewhere to start.
const planYearOptions = (plans) => {
  const now = new Date().getFullYear();
  const set = new Set([now, now + 1, now + 2]);
  (plans || []).forEach((p) => { if (p.year) set.add(p.year); });
  return Array.from(set).sort((a, b) => a - b);
};

// A plan's name. Once a stud is chosen the cross IS the identity, so a
// confirmed or signed plan reads "Stud × Mare" — matching how frozen embryo
// batches are labelled. A short list has no single stud yet, so it stays the
// mare's name.
const planLabel = (plan) => {
  if (!plan) return '';
  const stage = plan.stage || 'shortlist';
  const list = plan.studs || [];
  const chosen = list.find((x) => x.id === plan.chosenStudId) || (stage !== 'shortlist' ? list[0] : null);
  return chosen && chosen.name ? chosen.name + ' × ' + plan.mareName : plan.mareName;
};

// A signed cross is mirrored onto the horses it involves as a small line on the
// HORSE document — plans themselves are owner-only, so a trainer's app cannot
// read one. This is the single fact worth sharing; the fee, the contract and the
// attempts stay private on the plan.
//
// Stored as an array: a mare can hold more than one signed cross in a season.
// Each entry names the OTHER side, so the mare's page reads toward the stud and
// the stallion's page reads toward the mare.
// ~340 day gestation, the same figure the rest of the app uses. Only claimed
// once there is a bred date — before that the foaling year is a guess.
const expectedFoalYear = (bredDate) =>
  bredDate ? Number(addDays(bredDate, 340).slice(0, 4)) : null;

const comingSoonEntry = (planId, label, year, ownerId, foalYear) =>
  ({ planId, label, year, ownerId, foalYear: foalYear || null });

// "Shiners Gold Card × SR Bella Rose - 2028" once she is bred, or the season
// while the foaling year is still unknown.
const comingSoonText = (c) =>
  (c.label || c.withName || 'Breeding') + ' - ' + (c.foalYear || c.year);

const withComingSoon = (horse, entry) => [
  ...((horse?.comingSoon || []).filter((c) => c.planId !== entry.planId)),
  entry,
];
const withoutComingSoon = (horse, planId) =>
  (horse?.comingSoon || []).filter((c) => c.planId !== planId);

// Both sides of a signed cross, for whichever of them is a horse we may write to.
const syncComingSoon = async (plan, horses, uid, updateFn) => {
  const list = plan.studs || [];
  const chosen = list.find((x) => x.id === plan.chosenStudId) || list[0] || null;
  const signed = plan.stage === 'signed';
  // Both horses show the same cross name — it is what the foal will be called.
  const label = planLabel(plan);
  const foalYear = expectedFoalYear(plan.bredDate);
  const targets = [{ id: plan.mareId }, { id: chosen?.horseId }].filter((t) => t.id);
  const writes = [];
  for (const t of targets) {
    const horse = horses.find((h) => h.id === t.id);
    if (!horse || !canWriteHorse(horse, uid)) continue;
    const next = signed
      ? withComingSoon(horse, comingSoonEntry(plan.id, label, plan.year, uid, foalYear))
      : withoutComingSoon(horse, plan.id);
    // Don't write when nothing changes — this runs on every stage move.
    if (JSON.stringify(next) === JSON.stringify(horse.comingSoon || [])) continue;
    writes.push(updateFn(t.id, { comingSoon: next }));
  }
  return writes;
};

// Closing out a season. Nothing is deleted: a plan either moves forward to the
// next year or is archived where it is, so an old season stays readable.
// Returns the update for one plan, or null to leave it alone.
const closeOutUpdate = (plan, opts) => {
  const stage = plan.stage || 'shortlist';
  if (plan.archived) return null;
  // A plan that produced a foal belongs to the year it happened in.
  if (stage === 'bred') return { archived: true };
  const carry =
    (stage === 'shortlist' && opts.shortlists)
    || (stage === 'confirmed' && opts.confirmed)
    || (stage === 'signed' && opts.contracts);
  if (!carry) return { archived: true };
  // A signed contract you paid for but never used comes forward as a rebreed.
  return {
    year: (plan.year || 0) + 1,
    ...(stage === 'signed' && { rebreed: true, carriedFrom: plan.year }),
    ...(stage !== 'signed' && { carriedFrom: plan.year }),
  };
};

// How many plans reference a saved stud — shown in the library, and checked
// before deleting one.
const studPlanCount = (studId, plans) =>
  (plans || []).filter((p) => (p.studs || []).some((s) => s.studId === studId)).length;

// ── Frozen embryos in a breeding plan ────────────────────────────────────────
// A frozen batch is a cross that already exists, so once a plan has a chosen
// stud we can look for batches whose sire × dam is the same pairing. If there
// are any, the plan can be fulfilled by thawing one instead of breeding.

// Available batches matching this plan's chosen cross. Depleted batches and
// batches with nothing left are excluded.
// Does a frozen batch hold THIS cross? The dam is matched as a horse, not a
// string: a batch made by picking the mare from the stable stores her
// registered name, while a plan made the same way stores her barn name — so
// the same mare must match under either, or by id where the batch has one.
// The sire is matched on name, with the same case/space tolerance.
const frozenBatchIsCross = (batch, studName, mareName, mare) => {
  if (!batch || batch.type !== 'frozen_embryo' || batch.depleted || (batch.frozenCount || 0) <= 0) return false;
  if (normalizeHorseName(batch.sire) !== normalizeHorseName(studName)) return false;
  if (mare && batch.damId && batch.damId === mare.id) return true;
  const damKeys = new Set([mareName, mare?.name, mare?.registeredName].map(normalizeHorseName).filter(Boolean));
  return damKeys.has(normalizeHorseName(batch.dam));
};

// Available batches matching this plan's chosen cross.
const frozenMatchesFor = (plan, horses) => {
  if (!plan) return [];
  const list = plan.studs || [];
  const chosen = list.find((x) => x.id === plan.chosenStudId) || (plan.stage !== 'shortlist' ? list[0] : null);
  if (!chosen?.name || !plan.mareName) return [];
  const mare = plan.mareId ? (horses || []).find((h) => h.id === plan.mareId) : null;
  return (horses || []).filter((h) => frozenBatchIsCross(h, chosen.name, plan.mareName, mare));
};

// Batches for one candidate cross. Used to flag a short list: knowing you
// already hold embryos from a pairing is worth seeing while you decide, even
// though nothing is used at that point.
const frozenMatchesForStud = (studName, mareName, horses, mareId = null) => {
  if (!studName || !mareName) return [];
  const mare = mareId ? (horses || []).find((h) => h.id === mareId) : null;
  return (horses || []).filter((h) => frozenBatchIsCross(h, studName, mareName, mare));
};

const frozenAvailableCount = (batches) => batches.reduce((s, b) => s + (b.frozenCount || 0), 0);

// The breed date convention for a thawed embryo: 7 days before the implant, the
// same figure the frozen batch screen uses, so her checks and foaling date line
// up with a naturally bred mare.
const breedDateFromImplant = (implantDate) => {
  const bd = new Date(implantDate);
  bd.setDate(bd.getDate() - 7);
  return bd.toISOString().split('T')[0];
};

// Recording an implant does NOT create a horse — she joins the herd at Confirmed
// in Foal like any other pregnancy. What it does do is consume the embryo: the
// batch is decremented now, and a later "didn't take" never puts it back, because
// a thawed embryo is gone whether or not it held.
const recordFrozenImplant = async ({ batch, implantDate }) => {
  const newCount = Math.max(0, (batch.frozenCount || 0) - 1);
  await updateHorse(batch.id, { frozenCount: newCount });
  return { breedDate: breedDateFromImplant(implantDate), newCount };
};

// Sharing a plan. Full Access can edit it; View Only can read it. Only the
// owner can share or unshare, so this sheet is never shown to anyone else.
function PlanShareSheet({ visible, plan, onClose, onChanged }) {
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState('view');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (visible) { setEmail(''); setPermission('view'); } }, [visible]);

  const shares = plan?.shares || [];

  const handleShare = async () => {
    const addr = email.trim().toLowerCase();
    if (!addr) { Alert.alert('Email required', 'Enter the email address to share with.'); return; }
    if (addr === auth.currentUser?.email?.toLowerCase()) {
      Alert.alert('That is you', 'You already have this plan.');
      return;
    }
    setBusy(true);
    try {
      const found = await findUserByEmail(addr);
      if (!found) {
        Alert.alert('User not found', USER_NOT_FOUND_MSG);
        return;
      }
      if (!(await confirmSharingMatch(found, addr))) return;
      await shareBreedingPlan(plan.id, shares, found.uid, found.email, found.displayName || '', permission);
      setEmail('');
      onChanged && onChanged();
    } catch (e) {
      Alert.alert('Could not share', 'Please check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = (sh) => {
    Alert.alert('Remove access', 'Remove ' + (sh.name || sh.email) + " from this plan?", [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Remove',
        style: 'destructive',
        onPress: () => revokeBreedingPlanShare(plan.id, shares, sh.uid).catch(() =>
          Alert.alert('Could not remove', 'Please try again.')),
      },
    ]);
  };

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <View style={[styles.sheet, { maxHeight: '85%' }]}>
          <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
            <Text style={styles.sheetTitle}>Share This Plan</Text>

            {shares.length > 0 && (
              <>
                <Text style={styles.fieldLabel}>Shared with</Text>
                {shares.map((sh) => (
                  <View key={sh.uid} style={styles.shareRow}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.shareName}>{sh.name || sh.email}</Text>
                      <Text style={styles.shareMeta}>
                        {sh.permission === 'full' ? 'Full Access' : 'View Only'}
                      </Text>
                    </View>
                    <TouchableOpacity onPress={() => handleRevoke(sh)} activeOpacity={0.7}>
                      <Text style={styles.studRemove}>✕</Text>
                    </TouchableOpacity>
                  </View>
                ))}
              </>
            )}

            <Text style={[styles.fieldLabel, { marginTop: 14 }]}>Email</Text>
            <TextInput
              style={styles.input}
              placeholder="their@email.com"
              placeholderTextColor="#BDBDBD"
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              keyboardType="email-address"
            />

            <Text style={styles.fieldLabel}>Access</Text>
            <View style={styles.segRow}>
              <TouchableOpacity
                style={[styles.segBtn, permission === 'full' && styles.segBtnOn]}
                onPress={() => setPermission('full')}
                activeOpacity={0.75}
              >
                <Text style={[styles.segBtnTxt, permission === 'full' && styles.segBtnTxtOn]}>Full Access</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.segBtn, permission === 'view' && styles.segBtnOn]}
                onPress={() => setPermission('view')}
                activeOpacity={0.75}
              >
                <Text style={[styles.segBtnTxt, permission === 'view' && styles.segBtnTxtOn]}>View Only</Text>
              </TouchableOpacity>
            </View>
            <Text style={styles.contractHint}>
              {permission === 'full'
                ? 'They can change this plan — studs, dates, payments and checks. They cannot delete it or change who it is shared with.'
                : 'They can see this plan, including the fee and the contract, but cannot change anything.'}
            </Text>

            <View style={styles.sheetBtns}>
              <TouchableOpacity style={styles.cancelBtn} onPress={onClose} activeOpacity={0.75}>
                <Text style={styles.cancelBtnText}>Done</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleShare} activeOpacity={0.8} disabled={busy}>
                <Text style={styles.saveBtnText}>{busy ? 'Sharing…' : 'Share'}</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

// Shared sheet: pick a batch, an implant date, and optionally a recip.
function FrozenImplantSheet({ visible, batches, recips, onClose, onImplant, busy }) {
  const [batchId, setBatchId] = useState(null);
  const [date, setDate] = useState(new Date());
  const [showPicker, setShowPicker] = useState(false);
  const [recip, setRecip] = useState('');
  const [recipId, setRecipId] = useState(null);

  useEffect(() => {
    if (visible) {
      setBatchId(batches[0]?.id || null);
      setDate(new Date());
      setShowPicker(false);
      setRecip('');
      setRecipId(null);
    }
  }, [visible]);

  const chosenBatch = batches.find((b) => b.id === batchId) || batches[0] || null;

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <View style={[styles.sheet, { maxHeight: '90%' }]}>
          <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
            <Text style={styles.sheetTitle}>Use a Frozen Embryo</Text>

            {batches.length > 1 && (
              <>
                <Text style={styles.fieldLabel}>Which batch</Text>
                {batches.map((b) => (
                  <TouchableOpacity
                    key={b.id}
                    style={[styles.batchRow, b.id === batchId && styles.batchRowOn]}
                    onPress={() => setBatchId(b.id)}
                    activeOpacity={0.75}
                  >
                    <View style={[styles.radio, b.id === batchId && styles.radioOn]} />
                    <View style={{ flex: 1 }}>
                      <Text style={styles.batchName}>{b.method || 'ICSI'}{b.frozenLocation ? ' · ' + b.frozenLocation : ''}</Text>
                      <Text style={styles.batchMeta}>{b.frozenCount} left of {b.frozenTotal || b.frozenCount}</Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </>
            )}

            <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Implant date</Text>
            <TouchableOpacity style={styles.datePicker} onPress={() => setShowPicker((v) => !v)} activeOpacity={0.75}>
              <Text style={styles.datePickerText}>
                {date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </Text>
              <Text>📅</Text>
            </TouchableOpacity>
            {showPicker && (
              <DateTimePicker
                value={date}
                mode="date"
                display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                onChange={(e, d) => {
                  if (Platform.OS === 'android') setShowPicker(false);
                  if (d) setDate(d);
                }}
              />
            )}
            <Text style={styles.contractHint}>
              The breed date is set 7 days before the implant, so her checks and foaling date line up
              with the rest of the herd.
            </Text>

            <Text style={styles.fieldLabel}>Recip mare — optional</Text>
            {recips.map((h) => (
              <TouchableOpacity
                key={h.id}
                style={[styles.batchRow, recipId === h.id && styles.batchRowOn]}
                onPress={() => {
                  const on = recipId === h.id;
                  setRecipId(on ? null : h.id);
                  setRecip(on ? '' : displayName(h));
                }}
                activeOpacity={0.75}
              >
                <View style={[styles.radio, recipId === h.id && styles.radioOn]} />
                <Text style={styles.batchName}>{displayName(h)}</Text>
              </TouchableOpacity>
            ))}
            <TextInput
              style={[styles.input, { marginTop: 8 }]}
              placeholder="Or a leased recip's name"
              placeholderTextColor="#BDBDBD"
              value={recipId ? '' : recip}
              onChangeText={(v) => { setRecip(v); setRecipId(null); }}
            />

            <View style={styles.sheetBtns}>
              <TouchableOpacity style={styles.cancelBtn} onPress={onClose} activeOpacity={0.75}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.saveBtn}
                onPress={() => chosenBatch && onImplant({ batch: chosenBatch, date, recip: recip.trim(), recipId })}
                activeOpacity={0.8}
                disabled={busy || !chosenBatch}
              >
                <Text style={styles.saveBtnText}>{busy ? 'Working…' : 'Implant'}</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

function BreedingPlanningScreen({ navigation, route }) {
  // Viewing another owner's shared plans uses THIS screen, in a view mode:
  // same seasons, same three stages, same cards. A separate "shared plans"
  // layout would drift from the real one and never read the same.
  const viewingUid = route?.params?.sharedFrom || null;
  const viewingName = route?.params?.sharedName || 'Shared';
  const viewing = !!viewingUid;
  const [plans, setPlans] = useState([]);
  const [studs, setStuds] = useState([]);
  const [horses, setHorses] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear() + 1);
  const [collapsed, setCollapsed] = useState({});
  // 'cards' or 'list'. Remembered like the Milestones filters — someone with
  // forty mares sets List once and never thinks about it again.
  const [viewMode, setViewMode] = useState('cards');
  // Modals hold an id, not the plan object: the subscription rewrites plans on
  // every edit, and a captured object would go stale behind the modal.
  const [comparePlanId, setComparePlanId] = useState(null);
  const [detailPlanId, setDetailPlanId] = useState(null);
  // Signing is where farm, contact and method finally become required — they
  // are optional right up until there is a contract to honour.
  const [signPlanId, setSignPlanId] = useState(null);
  const [signMethod, setSignMethod] = useState('');
  const [signFarm, setSignFarm] = useState('');
  const [signContact, setSignContact] = useState('');
  const [signFee, setSignFee] = useState('');
  const [showCloseOut, setShowCloseOut] = useState(false);
  const [carryShortlists, setCarryShortlists] = useState(true);
  const [carryContracts, setCarryContracts] = useState(true);
  const [carryConfirmed, setCarryConfirmed] = useState(false);
  const [frozenPlanId, setFrozenPlanId] = useState(null);
  const [frozenBusy, setFrozenBusy] = useState(false);
  const [sharePlanId, setSharePlanId] = useState(null);
  // Share every plan I own at once — this season, or all of them.
  const [seasonShares, setSeasonShares] = useState([]);
  const [showShareAll, setShowShareAll] = useState(false);
  const [shareAllEmail, setShareAllEmail] = useState('');
  const [shareAllPerm, setShareAllPerm] = useState('view');
  const [shareAllScope, setShareAllScope] = useState('season'); // 'season' | 'all'
  const [shareAllBusy, setShareAllBusy] = useState(false);

  // New-plan sheet
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [mareSource, setMareSource] = useState('herd');
  const [mareName, setMareName] = useState('');
  const [mareId, setMareId] = useState(null);
  const [showMarePicker, setShowMarePicker] = useState(false);
  const [draftStuds, setDraftStuds] = useState([]);

  // Add-stud sheet. `studTarget` is the plan being added to, or 'draft' while
  // building a brand-new plan that hasn't been saved yet.
  const [showStudModal, setShowStudModal] = useState(false);
  const [studTarget, setStudTarget] = useState(null);
  const [studSource, setStudSource] = useState('new');
  const [studName, setStudName] = useState('');
  const [studFee, setStudFee] = useState('');
  const [saveToLibrary, setSaveToLibrary] = useState(true);
  // Two generations, all optional. Hidden behind a toggle so the fast path —
  // jotting three names after a conversation — stays one field and a fee.
  const [showPedigree, setShowPedigree] = useState(false);
  const [ped, setPed] = useState({ sire: '', sireSire: '', sireDam: '', dam: '', damSire: '', damDam: '' });
  const setPedField = (k, v) => setPed((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const unsubPlans = subscribeToBreedingPlans(uid, setPlans);
    const unsubStuds = subscribeToStuds(uid, setStuds);
    const unsubHorses = subscribeToHorses(uid, setHorses);
    const unsubShares = subscribeToSeasonShares(uid, setSeasonShares);
    return () => { unsubPlans(); unsubStuds(); unsubHorses(); unsubShares(); };
  }, []);

  useEffect(() => {
    AsyncStorage.getItem('breedingViewMode').then((v) => { if (v) setViewMode(v); }).catch(() => {});
  }, []);
  useEffect(() => {
    AsyncStorage.setItem('breedingViewMode', viewMode).catch(() => {});
  }, [viewMode]);

  const myUid = auth.currentUser?.uid;
  // Plans shared with you live behind their own card so they never mix in with
  // your own season.
  const myPlans = viewing
    ? plans.filter((p) => p.ownerId === viewingUid)
    : plans.filter((p) => !p.ownerId || p.ownerId === myUid);
  // What I may change on a given plan: everything on my own, what Full Access
  // allows on someone else's, nothing on View Only.
  const mayEdit = (plan) => !viewing || canEditPlan(plan, myUid);
  useEffect(() => {
    if (viewing) navigation.setOptions({ title: `${viewingName}'s Breeding Plans` });
  }, [viewing, viewingName]);
  const sharedPlans = plans.filter((p) => p.ownerId && p.ownerId !== myUid);
  const years = planYearOptions(myPlans);
  const seasonPlans = myPlans.filter((p) => p.year === year);
  const byStage = (stage) => seasonPlans.filter((p) => (p.stage || 'shortlist') === stage && !p.archived);
  const archivedPlans = seasonPlans.filter((p) => p.archived);
  const openPlans = seasonPlans.filter((p) => !p.archived);
  const mares = filterByPedigreeRole(horses, 'dam');
  const comparePlan = plans.find((p) => p.id === comparePlanId) || null;
  const detailPlan = plans.find((p) => p.id === detailPlanId) || null;
  // Committed counts SIGNED contracts only — a confirmed pick can still be
  // walked away from, so it has no business inflating what the season costs.
  const committed = byStage('signed').reduce((sum, p) => {
    const list = p.studs || [];
    const chosen = list.find((x) => x.id === p.chosenStudId) || list[0];
    return sum + (typeof chosen?.fee === 'number' ? chosen.fee : 0);
  }, 0);

  const toggle = (k) => setCollapsed((prev) => ({ ...prev, [k]: !prev[k] }));

  const resetPlanForm = () => {
    setMareSource('herd');
    setMareName('');
    setMareId(null);
    setDraftStuds([]);
  };

  const openStudSheet = (target) => {
    setStudTarget(target);
    setStudSource(studs.length > 0 ? 'saved' : 'new');
    setStudName('');
    setStudFee('');
    setSaveToLibrary(true);
    setShowPedigree(false);
    setPed({ sire: '', sireSire: '', sireDam: '', dam: '', damSire: '', damDam: '' });
    setShowStudModal(true);
  };

  // Attach a stud to a draft plan or straight onto a saved one.
  const attachStud = async (entry) => {
    if (studTarget === 'draft') {
      setDraftStuds((prev) => [...prev, entry]);
    } else if (studTarget) {
      await updateBreedingPlan(studTarget.id, { studs: [...(studTarget.studs || []), entry] });
    }
    setShowStudModal(false);
  };

  const handleSaveStud = async () => {
    if (!studName.trim()) {
      Alert.alert('Name required', 'Enter the stallion’s name. Everything else is optional.');
      return;
    }
    const uid = auth.currentUser?.uid;
    const feeNum = studFee.trim() === '' ? null : parseFloat(studFee.replace(/[^0-9.]/g, ''));
    let studId = null;
    if (saveToLibrary) {
      try {
        const ref = await addStud(uid, { name: studName.trim(), fee: feeNum, ...ped });
        studId = ref.id;
      } catch (e) { /* fall through — the plan still gets the stud */ }
    }
    await attachStud({
      id: 'st_' + Date.now(),
      studId,
      name: studName.trim(),
      fee: Number.isFinite(feeNum) ? feeNum : null,
      // Copied onto the plan as well as the library so the Compare view can read
      // it without a lookup, and so an unsaved outside stud still has a pedigree.
      ...ped,
    });
  };

  const handlePickSavedStud = async (s) => {
    await attachStud({
      id: 'st_' + Date.now(),
      studId: s.id,
      name: s.name,
      fee: s.fee == null ? null : s.fee,
      farm: s.farm || '', contact: s.contact || '',
      incentives: incentiveText(s.incentives), notes: s.notes || '',
      sire: s.sire || '', sireSire: s.sireSire || '', sireDam: s.sireDam || '',
      dam: s.dam || '', damSire: s.damSire || '', damDam: s.damDam || '',
    });
  };

  const handleCreatePlan = async () => {
    if (!mareName.trim()) {
      Alert.alert('Mare required', 'Pick a mare from your herd, or type an outside mare’s name.');
      return;
    }
    const uid = auth.currentUser?.uid;
    try {
      await addBreedingPlan(uid, {
        year,
        stage: 'shortlist',
        mareName: mareName.trim(),
        mareId: mareSource === 'herd' ? mareId : null,
        studs: draftStuds,
      });
      setShowPlanModal(false);
      resetPlanForm();
    } catch (e) {
      Alert.alert('Could not save', 'Please check your connection and try again.');
    }
  };

  // Choosing a stud spins off a Confirmed plan for that cross and leaves the
  // short list standing with the remaining candidates, so a mare can be
  // committed to one stallion while still weighing another.
  const handleChooseStud = (plan, stud) => {
    Alert.alert(
      'Confirm this cross',
      'Confirm ' + plan.mareName + ' × ' + stud.name + ' for ' + plan.year + '?\n\nHer short list stays put with the other candidates.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Confirm',
          onPress: async () => {
            const uid = auth.currentUser?.uid;
            try {
              await addBreedingPlan(uid, {
                year: plan.year,
                stage: 'confirmed',
                mareName: plan.mareName,
                mareId: plan.mareId,
                studs: [stud],
                chosenStudId: stud.id,
              });
              await updateBreedingPlan(plan.id, {
                studs: (plan.studs || []).filter((s) => s.id !== stud.id),
              });
            } catch (e) {
              Alert.alert('Could not confirm', 'Please check your connection and try again.');
            }
          },
        },
      ]
    );
  };

  const openSignSheet = (plan) => {
    const list = plan.studs || [];
    const chosen = list.find((x) => x.id === plan.chosenStudId) || list[0] || {};
    setSignPlanId(plan.id);
    setSignMethod(plan.method || '');
    setSignFarm(plan.farm || chosen.farm || '');
    setSignContact(plan.contact || chosen.contact || '');
    setSignFee(typeof chosen.fee === 'number' ? String(chosen.fee) : '');
  };

  const handleSignContract = async () => {
    const plan = plans.find((p) => p.id === signPlanId);
    if (!plan) return;
    if (!signMethod) {
      Alert.alert('Method required', 'Choose how she is being bred.');
      return;
    }
    const feeNum = signFee.trim() === '' ? null : parseFloat(signFee.replace(/[^0-9.]/g, ''));
    try {
      await updateBreedingPlan(plan.id, {
        stage: 'signed',
        signedDate: new Date().toISOString().split('T')[0],
        method: signMethod,
        farm: signFarm.trim(),
        contact: signContact.trim(),
        fee: Number.isFinite(feeNum) ? feeNum : null,
        paid: typeof plan.paid === 'number' ? plan.paid : 0,
      });
      // Mirror the signed cross onto the mare (and the stallion if he's yours).
      // Best-effort: a failed mirror must never undo a signed contract.
      try {
        await Promise.allSettled(
          await syncComingSoon(
            { ...plan, stage: 'signed', method: signMethod },
            horses, auth.currentUser?.uid, updateHorse
          )
        );
      } catch (e) { /* the contract is signed either way */ }
      setSignPlanId(null);
      navigation.navigate('BreedingContract', { planId: plan.id });
    } catch (e) {
      Alert.alert('Could not save', 'Please check your connection and try again.');
    }
  };

  const frozenPlan = plans.find((p) => p.id === frozenPlanId) || null;
  const sharePlan = plans.find((p) => p.id === sharePlanId) || null;

  // The plans a season share covers right now — shown for context. The share
  // itself is a standing record, so plans added later are covered too.
  const shareAllTargets = () => plans.filter((p) => p.ownerId === myUid && (shareAllScope === 'all' || p.year === year));

  const givenShares = seasonShares.filter((s) => s.ownerId === myUid && s.status !== 'ended');
  const invitesForMe = seasonShares.filter((s) => s.toUid === myUid && s.status === 'pending');
  const activeToMe = seasonShares.filter((s) => s.toUid === myUid && s.status === 'active');
  const otherPerson = (s) => s.people?.[s.ownerId === myUid ? s.toUid : s.ownerId] || {};

  // Invite someone to a whole season (or every season). Nothing is shared
  // until they accept; then the owner's device shares what exists and every
  // plan created after that starts out shared.
  const handleShareAll = async () => {
    if (shareAllBusy) return;
    const email = shareAllEmail.trim().toLowerCase();
    if (!email) { Alert.alert('Email needed', 'Enter the email they use for Horse Math.'); return; }
    if (email === (auth.currentUser?.email || '').toLowerCase()) { Alert.alert('That is you', "Enter someone else's email."); return; }
    setShareAllBusy(true);
    try {
      const found = await findUserByEmail(email);
      if (!found) { Alert.alert('User not found', USER_NOT_FOUND_MSG); return; }
      if (!(await confirmSharingMatch(found, email))) return;
      const scopeYear = shareAllScope === 'all' ? 'all' : year;
      const dup = givenShares.find((s) => s.toUid === found.uid && (s.year === 'all' || scopeYear === 'all' || Number(s.year) === Number(scopeYear)));
      if (dup) {
        Alert.alert('Already set up', dup.status === 'pending'
          ? `${found.displayName || found.email} already has an invite for ${seasonLabel(dup)} waiting.`
          : `You already share ${seasonLabel(dup)} with ${found.displayName || found.email}.`);
        return;
      }
      const me = auth.currentUser;
      await inviteSeasonShare(
        { uid: me.uid, email: me.email || '', name: me.displayName || me.email || '' },
        { uid: found.uid, email: found.shareEmail || found.email || '', name: found.displayName || found.shareEmail || found.email || '' },
        scopeYear,
        shareAllPerm
      );
      notifyAccess(found.uid, '🐎 Breeding Plans Invite',
        `${myDisplayName()} wants to share ${scopeYear === 'all' ? 'all of their breeding plans' : 'their ' + scopeYear + ' breeding plans'} with you. Open Breeding Planning to accept.`);
      Alert.alert('Invite sent', `Once ${found.displayName || found.email} accepts, every plan you own ${scopeYear === 'all' ? 'in any season' : 'in ' + scopeYear} — now and later — is shared with them.`);
      setShareAllEmail('');
    } catch (e) {
      Alert.alert('Could not send', e?.message || 'Please try again.');
    } finally {
      setShareAllBusy(false);
    }
  };

  const handleEndSeasonShare = (s) => {
    const who = otherPerson(s).name || otherPerson(s).email || 'them';
    const iOwn = s.ownerId === myUid;
    Alert.alert(
      s.status === 'pending' ? 'Cancel invite?' : 'Stop sharing?',
      s.status === 'pending'
        ? `Withdraw the ${seasonLabel(s)} invite to ${who}?`
        : iOwn
          ? `${who} will lose access to your ${seasonLabel(s)} plans. Any plan you shared with them individually stays shared.`
          : `You will lose access to ${who}'s ${seasonLabel(s)} plans.`,
      [
        { text: 'Keep', style: 'cancel' },
        { text: s.status === 'pending' ? 'Cancel invite' : 'Stop', style: 'destructive', onPress: () => {
          const op = s.status === 'pending' ? declineSeasonShare(s.id) : endSeasonShare(s.id, myUid);
          op.catch(() => Alert.alert('Error', 'Could not do that. Please try again.'));
        } },
      ]
    );
  };

  const handleAcceptSeasonShare = (s) => {
    const who = otherPerson(s).name || otherPerson(s).email || 'They';
    Alert.alert(
      'Accept shared plans?',
      `${who}'s ${seasonLabel(s) === 'every season' ? 'breeding plans, every season,' : seasonLabel(s) + ' breeding plans'} will appear under Shared with you as ${s.permission === 'full' ? 'Full Access' : 'View Only'} — including plans they add later.`,
      [
        { text: 'Not now', style: 'cancel' },
        { text: 'Accept', onPress: async () => {
          try {
            await acceptSeasonShare(s.id);
            notifyAccess(s.ownerId, '🐎 Breeding Plans Accepted', `${myDisplayName()} accepted your ${seasonLabel(s)} breeding plans.`);
          } catch (e) { Alert.alert('Error', 'Could not accept. Please try again.'); }
        } },
      ]
    );
  };
  const recipHorses = horses.filter((h) => (h.status || []).includes('Recip'));

  // Recording the implant sets her breed date and starts the checks. She does
  // not join the herd until Confirmed in Foal, exactly like a bred mare.
  const handleImplantForPlan = async ({ batch, date, recip, recipId }) => {
    if (!frozenPlan || frozenBusy) return;
    setFrozenBusy(true);
    try {
      const { breedDate, newCount } = await recordFrozenImplant({ batch, implantDate: date });
      const fresh = buildChecks(!!recip || !!frozenPlan.isRecip);
      const withReminders = [];
      for (const c of fresh) {
        let notificationId = null;
        const when = checkReminderAt(breedDate, c.daysOffset);
        if (when > new Date()) {
          try {
            notificationId = await scheduleReminder(
              c.name, planLabel(frozenPlan) + ' — ' + c.name + ' due today', when
            );
          } catch (e) { /* a missing reminder must not lose the implant */ }
        }
        withReminders.push({ ...c, notificationId: notificationId || null });
      }
      await updateBreedingPlan(frozenPlan.id, {
        stage: frozenPlan.stage === 'shortlist' ? 'confirmed' : frozenPlan.stage,
        bredDate: breedDate,
        checks: withReminders,
        fromFrozenId: batch.id,
        implantedDate: date.toISOString().split('T')[0],
        embryoMethod: batch.method || 'ICSI',
        ...(recip && { isRecip: true, recipMare: recip }),
        ...(recipId && { recipHorseId: recipId }),
      });
      const planId = frozenPlan.id;
      setFrozenPlanId(null);
      if (newCount === 0) {
        Alert.alert(
          'Batch used up',
          'That was the last embryo in this ' + (batch.method || 'ICSI') + ' batch. Keep it for your records, or delete it?',
          [
            { text: 'Keep', onPress: async () => { try { await updateHorse(batch.id, { depleted: true }); } catch {} navigation.navigate('BreedingContract', { planId }); } },
            { text: 'Delete', style: 'destructive', onPress: async () => { try { await softDeleteHorse(batch.id, auth.currentUser?.uid, auth.currentUser?.email); } catch {} navigation.navigate('BreedingContract', { planId }); } },
          ]
        );
      } else {
        navigation.navigate('BreedingContract', { planId });
      }
    } catch (e) {
      Alert.alert('Could not record the implant', 'Please check your connection and try again.');
    } finally {
      setFrozenBusy(false);
    }
  };

  const handleCloseOut = async () => {
    const opts = { shortlists: carryShortlists, contracts: carryContracts, confirmed: carryConfirmed };
    const moving = openPlans.filter((p) => {
      const u = closeOutUpdate(p, opts);
      return u && u.year;
    }).length;
    try {
      await Promise.all(
        openPlans.map((p) => {
          const update = closeOutUpdate(p, opts);
          return update ? updateBreedingPlan(p.id, update) : Promise.resolve();
        })
      );
      // An archived contract is no longer a foal on the way.
      const archivedNow = openPlans.filter((p) => closeOutUpdate(p, opts)?.archived);
      try {
        for (const p of archivedNow) {
          await Promise.allSettled(
            await syncComingSoon({ ...p, stage: 'archived' }, horses, auth.currentUser?.uid, updateHorse)
          );
        }
      } catch (e) { /* cosmetic */ }
      setShowCloseOut(false);
      if (moving > 0) setYear(year + 1);
    } catch (e) {
      Alert.alert('Could not close out', 'Please check your connection and try again.');
    }
  };

  // A signed contract has its own screen; anything earlier opens in place.
  const openPlan = (plan) => {
    if (plan.stage === 'signed' || plan.stage === 'bred') {
      navigation.navigate('BreedingContract', { planId: plan.id });
    } else {
      setDetailPlanId(plan.id);
    }
  };

  const handleDeletePlan = (plan) => {
    Alert.alert('Delete plan', 'Remove ' + planLabel(plan) + ' from ' + plan.year + '? This cannot be undone.', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          try {
            await Promise.allSettled(
              await syncComingSoon({ ...plan, stage: 'deleted' }, horses, auth.currentUser?.uid, updateHorse)
            );
          } catch (e) { /* clearing the mirror must not block the delete */ }
          deleteBreedingPlan(plan.id).catch(() => {});
        },
      },
    ]);
  };

  // What a plan is worth at a glance: one fee once chosen, a range while
  // shortlisting, and a dash when no fee has been asked about yet.
  const planFeeLabel = (plan) => {
    const list = plan.studs || [];
    const chosen = list.find((x) => x.id === plan.chosenStudId);
    if (chosen) return planMoney(chosen.fee);
    const fees = list.map((x) => x.fee).filter((f) => typeof f === 'number');
    if (fees.length === 0) return '—';
    const lo = Math.min.apply(null, fees);
    const hi = Math.max.apply(null, fees);
    return lo === hi ? planMoney(lo) : planMoney(lo) + ' – ' + planMoney(hi);
  };

  const handleRemoveStud = (plan, stud) =>
    updateBreedingPlan(plan.id, { studs: (plan.studs || []).filter((s) => s.id !== stud.id) }).catch(() => {});

  // Two generations, rendered only for the names that were actually filled in.
  const StudPedigree = ({ stud }) => {
    if (!stud.sire && !stud.dam) return null;
    return (
      <View style={styles.cmpPed}>
        {!!stud.sire && (
          <>
            <View style={styles.cmpPedLine}>
              <Text style={styles.cmpPedRole}>Sire</Text>
              <Text style={styles.cmpPedName}>{stud.sire}</Text>
            </View>
            {(!!stud.sireSire || !!stud.sireDam) && (
              <Text style={styles.cmpPedSub}>{(stud.sireSire || '?') + ' × ' + (stud.sireDam || '?')}</Text>
            )}
          </>
        )}
        {!!stud.dam && (
          <>
            <View style={styles.cmpPedLine}>
              <Text style={styles.cmpPedRole}>Dam</Text>
              <Text style={styles.cmpPedName}>{stud.dam}</Text>
            </View>
            {(!!stud.damSire || !!stud.damDam) && (
              <Text style={styles.cmpPedSub}>{(stud.damSire || '?') + ' × ' + (stud.damDam || '?')}</Text>
            )}
          </>
        )}
      </View>
    );
  };

  const PlanCard = ({ plan }) => {
    const stage = plan.stage || 'shortlist';
    const list = plan.studs || [];
    const chosen = list.find((s) => s.id === plan.chosenStudId) || list[0];
    const fees = list.map((s) => s.fee).filter((f) => typeof f === 'number');
    return (
      <View style={styles.planCard}>
        <View style={styles.planTopRow}>
          <TouchableOpacity
            style={styles.planMareTap}
            onPress={() => openPlan(plan)}
            activeOpacity={0.7}
          >
            <Text style={styles.planMare}>{planLabel(plan)}</Text>
            <Text style={styles.planMareChevron}>›</Text>
          </TouchableOpacity>
          {!!plan.rebreed && <Text style={styles.planRebreed}>Rebreed</Text>}
          {!plan.mareId && <Text style={styles.planOutside}>Outside</Text>}
          {plan.ownerId === myUid && (
            <TouchableOpacity onPress={() => handleDeletePlan(plan)} activeOpacity={0.7}>
              <Text style={styles.planDelete}>✕</Text>
            </TouchableOpacity>
          )}
        </View>

        {stage === 'shortlist' ? (
          <>
            <Text style={styles.planMeta}>
              {list.length === 0
                ? 'No studs yet'
                : list.length + ' stud' + (list.length === 1 ? '' : 's') + ' under consideration'}
            </Text>
            {list.map((s) => (
              <View key={s.id} style={styles.studRow}>
                <Text style={styles.studName}>{s.name}</Text>
                {frozenMatchesForStud(s.name, plan.mareName, horses, plan.mareId).length > 0 && (
                  <Text style={styles.frozenFlag}>🧊 frozen</Text>
                )}
                <Text style={styles.studFee}>{planMoney(s.fee)}</Text>
                <TouchableOpacity onPress={() => handleChooseStud(plan, s)} activeOpacity={0.7}>
                  <Text style={styles.studChoose}>Choose</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => handleRemoveStud(plan, s)} activeOpacity={0.7}>
                  <Text style={styles.studRemove}>✕</Text>
                </TouchableOpacity>
              </View>
            ))}
            {fees.length > 1 && (
              <Text style={styles.planRange}>
                Range {planMoney(Math.min.apply(null, fees))} – {planMoney(Math.max.apply(null, fees))}
              </Text>
            )}
            {list.length > 1 && (
              <TouchableOpacity style={styles.planCompareBtn} onPress={() => setComparePlanId(plan.id)} activeOpacity={0.75}>
                <Text style={styles.planCompareBtnTxt}>Compare {list.length} crosses</Text>
              </TouchableOpacity>
            )}
            {mayEdit(plan) && <TouchableOpacity style={styles.planAddStud} onPress={() => openStudSheet(plan)} activeOpacity={0.75}>
              <Text style={styles.planAddStudTxt}>+ Add a stud</Text>
            </TouchableOpacity>}
          </>
        ) : (
          <>
            {chosen && (
              <View style={[styles.studRow, styles.studRowChosen]}>
                <Text style={[styles.studName, { fontWeight: '700' }]}>✓ {chosen.name}</Text>
                <Text style={styles.studFee}>{planMoney(chosen.fee)}</Text>
              </View>
            )}
            {stage !== 'bred' && !plan.fromFrozenId && (() => {
              const matches = viewing ? [] : frozenMatchesFor(plan, horses);
              if (matches.length === 0) return null;
              const n = frozenAvailableCount(matches);
              return (
                <TouchableOpacity style={styles.frozenBanner} onPress={() => setFrozenPlanId(plan.id)} activeOpacity={0.75}>
                  <Text style={styles.frozenBannerTxt}>
                    🧊 You have {n} frozen embryo{n === 1 ? '' : 's'} from this cross
                  </Text>
                  <Text style={styles.frozenBannerAction}>Record an implant instead of breeding ›</Text>
                </TouchableOpacity>
              );
            })()}
            {stage === 'confirmed' && mayEdit(plan) && (
              <TouchableOpacity style={styles.planMoveBtn} onPress={() => openSignSheet(plan)} activeOpacity={0.75}>
                <Text style={styles.planMoveBtnTxt}>Move to Signed Contract</Text>
              </TouchableOpacity>
            )}
            {stage === 'bred' && (
              <TouchableOpacity onPress={() => openPlan(plan)} activeOpacity={0.7}>
                <Text style={styles.planMeta}>Confirmed in foal</Text>
                <Text style={styles.planOpenHint}>Tap to open ›</Text>
              </TouchableOpacity>
            )}
            {stage === 'signed' && (
              <TouchableOpacity onPress={() => openPlan(plan)} activeOpacity={0.7}>
                <Text style={styles.planMeta}>
                  Signed {plan.signedDate ? formatDate(plan.signedDate) : '—'}
                  {plan.carriedFrom ? ' · carried from ' + plan.carriedFrom : ''}
                </Text>
                <Text style={styles.planOpenHint}>Tap to open this contract ›</Text>
              </TouchableOpacity>
            )}
          </>
        )}
      </View>
    );
  };

  // Everything known about one plan. The signed stage has no other actions, so
  // without this a signed contract was a card you could not open.
  const PlanDetail = ({ plan }) => {
    const stage = plan.stage || 'shortlist';
    const list = plan.studs || [];
    const chosen = list.find((x) => x.id === plan.chosenStudId) || (stage !== 'shortlist' ? list[0] : null);
    const stageLabel = stage === 'shortlist' ? 'Short List' : stage === 'confirmed' ? 'Confirmed' : 'Signed Contract';
    const others = list.filter((x) => x.id !== chosen?.id);
    return (
      <View>
        <View style={styles.detailHead}>
          <Text style={styles.detailStage}>{stageLabel}</Text>
          <Text style={styles.detailSeason}>{plan.year}</Text>
        </View>

        <Text style={styles.detailLabel}>Mare</Text>
        {plan.mareId ? (
          <TouchableOpacity
            style={styles.detailLinkRow}
            onPress={() => { setDetailPlanId(null); navigation.navigate('Horse', { horseId: plan.mareId }); }}
            activeOpacity={0.7}
          >
            <Text style={styles.detailLinkTxt}>{plan.mareName}</Text>
            <Text style={styles.detailLinkChevron}>›</Text>
          </TouchableOpacity>
        ) : (
          <Text style={styles.detailValue}>{plan.mareName} · outside mare</Text>
        )}

        {!!chosen && (
          <>
            <Text style={styles.detailLabel}>{stage === 'shortlist' ? 'Stud' : 'Chosen stud'}</Text>
            <View style={styles.detailStudBox}>
              <View style={styles.cmpHead}>
                <Text style={styles.cmpName}>{chosen.name}</Text>
                <Text style={styles.cmpFee}>{planMoney(chosen.fee)}</Text>
              </View>
              <StudPedigree stud={chosen} />
              {!chosen.sire && !chosen.dam && (
                <Text style={styles.cmpNoPed}>No pedigree entered — add it in the stud library.</Text>
              )}
              {!!incentiveText(chosen.incentives) && (
                <Text style={styles.incText}>⭐ {incentiveText(chosen.incentives)}</Text>
              )}
              {!!chosen.notes && <Text style={styles.cmpNotes}>{chosen.notes}</Text>}
              {!!chosen.farm && <Text style={styles.detailSmall}>{chosen.farm}</Text>}
              {!!chosen.contact && <Text style={styles.detailSmall}>{chosen.contact}</Text>}
            </View>
          </>
        )}

        {others.length > 0 && (
          <>
            <Text style={styles.detailLabel}>
              {stage === 'shortlist' ? 'Under consideration' : 'Also considered'}
            </Text>
            {others.map((st) => (
              <View key={st.id} style={styles.studRow}>
                <Text style={styles.studName}>{st.name}</Text>
                <Text style={styles.studFee}>{planMoney(st.fee)}</Text>
                {stage === 'shortlist' && (
                  <TouchableOpacity
                    onPress={() => { setDetailPlanId(null); handleChooseStud(plan, st); }}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.studChoose}>Choose</Text>
                  </TouchableOpacity>
                )}
              </View>
            ))}
            {stage === 'shortlist' && others.length > 1 && (
              <TouchableOpacity
                style={styles.planCompareBtn}
                onPress={() => { setDetailPlanId(null); setComparePlanId(plan.id); }}
                activeOpacity={0.75}
              >
                <Text style={styles.planCompareBtnTxt}>Compare {others.length} crosses</Text>
              </TouchableOpacity>
            )}
          </>
        )}

        {stage !== 'bred' && (() => {
          const matches = viewing ? [] : frozenMatchesFor(plan, horses);
          if (matches.length === 0) return null;
          const n = frozenAvailableCount(matches);
          return (
            <TouchableOpacity
              style={styles.frozenBanner}
              onPress={() => { setDetailPlanId(null); setFrozenPlanId(plan.id); }}
              activeOpacity={0.75}
            >
              <Text style={styles.frozenBannerTxt}>
                🧊 You have {n} frozen embryo{n === 1 ? '' : 's'} from this cross
              </Text>
              <Text style={styles.frozenBannerAction}>Use one instead ›</Text>
            </TouchableOpacity>
          );
        })()}

        {stage === 'shortlist' && mayEdit(plan) && (
          <TouchableOpacity
            style={styles.planAddStud}
            onPress={() => { setDetailPlanId(null); openStudSheet(plan); }}
            activeOpacity={0.75}
          >
            <Text style={styles.planAddStudTxt}>+ Add a stud</Text>
          </TouchableOpacity>
        )}

        {stage === 'signed' && (
          <>
            <Text style={styles.detailLabel}>Contract</Text>
            <Text style={styles.detailValue}>
              Signed {plan.signedDate ? formatDate(plan.signedDate) : '—'}
            </Text>
            <Text style={styles.detailComing}>
              Open the contract for payments, the signed PDF, the breeding checks and Confirmed in Foal.
            </Text>
          </>
        )}


        {stage === 'confirmed' && (
          <TouchableOpacity
            style={styles.planMoveBtn}
            onPress={() => { setDetailPlanId(null); openSignSheet(plan); }}
            activeOpacity={0.75}
          >
            <Text style={styles.planMoveBtnTxt}>Move to Signed Contract</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  const Stage = ({ stage, sec, title }) => {
    const items = byStage(stage);
    return (
      <CollapsibleSection
        sec={sec}
        title={title}
        badge={items.length || ''}
        collapsed={!!collapsed[stage]}
        onToggle={() => toggle(stage)}
      >
        {items.length === 0 ? (
          <Text style={styles.noNotesTxt}>Nothing here yet.</Text>
        ) : (
          items.map((p) => <PlanCard key={p.id} plan={p} />)
        )}
      </CollapsibleSection>
    );
  };

  return (
    <SafeAreaView style={styles.screen} edges={['bottom']}>
      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
        {viewing && (
          <View style={[styles.formCard, { marginBottom: 12 }]}>
            <Text style={{ fontSize: 13, color: MUTED, lineHeight: 19 }}>
              🤝 {viewingName}'s plans, as they see them. {myPlans.some((p) => canEditPlan(p, myUid)) ? 'You have Full Access on some or all of these.' : 'View Only.'}
            </Text>
          </View>
        )}
        {!viewing && invitesForMe.map((s) => {
          const who = otherPerson(s);
          return (
            <View key={s.id} style={[styles.formCard, { borderColor: GREEN }]}>
              <Text style={styles.horseName}>{who.name || who.email}</Text>
              <Text style={{ fontSize: 13, color: MUTED, marginTop: 2 }}>
                wants to share their {seasonLabel(s) === 'every season' ? 'breeding plans, every season' : seasonLabel(s) + ' breeding plans'} · {s.permission === 'full' ? 'Full Access' : 'View Only'}
              </Text>
              <View style={{ flexDirection: 'row', gap: 10, marginTop: 10 }}>
                <TouchableOpacity style={[styles.cancelBtn, { flex: 1 }]} onPress={() => handleEndSeasonShare(s)} activeOpacity={0.85}>
                  <Text style={styles.cancelBtnText}>Decline</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.saveBtn, { flex: 1 }]} onPress={() => handleAcceptSeasonShare(s)} activeOpacity={0.85}>
                  <Text style={styles.saveBtnText}>Accept</Text>
                </TouchableOpacity>
              </View>
            </View>
          );
        })}

        {!viewing && (sharedPlans.length > 0 || activeToMe.length > 0) && (
          <TouchableOpacity
            style={styles.sharedCard}
            onPress={() => navigation.navigate('SharedBreedingPlans')}
            activeOpacity={0.75}
          >
            <Text style={styles.sharedCardIcon}>🤝</Text>
            <View style={{ flex: 1 }}>
              <Text style={styles.sharedCardTitle}>Shared with you</Text>
              <Text style={styles.sharedCardSub}>
                {sharedPlans.length > 0 ? `${plural(sharedPlans.length, 'plan')} from ${[...new Set(sharedPlans.map((p) => p.ownerId))].length === 1 ? 'one person' : 'other people'}` : 'Plans are on their way'}
                {activeToMe.length > 0 ? ` · ${activeToMe.map((s) => `${otherPerson(s).name || otherPerson(s).email} (${seasonLabel(s)})`).join(', ')}` : ''}
              </Text>
            </View>
            <Text style={styles.sharedCardChevron}>›</Text>
          </TouchableOpacity>
        )}

        {/* Season picker */}
        <View style={styles.seasonBar}>
          <Text style={styles.seasonLabel}>Season</Text>
          <View style={styles.seasonPick}>
            <TouchableOpacity
              onPress={() => setYear((y) => y - 1)}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              activeOpacity={0.7}
            >
              <Text style={styles.seasonArrow}>‹</Text>
            </TouchableOpacity>
            <Text style={styles.seasonYear}>{year}</Text>
            <TouchableOpacity
              onPress={() => setYear((y) => y + 1)}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              activeOpacity={0.7}
            >
              <Text style={styles.seasonArrow}>›</Text>
            </TouchableOpacity>
          </View>
        </View>
        <View style={styles.seasonStrip}>
          {years.map((y) => (
            <TouchableOpacity
              key={y}
              style={[styles.seasonChip, y === year && styles.seasonChipOn]}
              onPress={() => setYear(y)}
              activeOpacity={0.75}
            >
              <Text style={[styles.seasonChipTxt, y === year && styles.seasonChipTxtOn]}>{y}</Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Season summary */}
        <View style={styles.planSummary}>
          <View style={styles.planSummaryCell}>
            <Text style={styles.planSummaryNum}>{seasonPlans.length}</Text>
            <Text style={styles.planSummaryKey}>Plans</Text>
          </View>
          <View style={styles.planSummaryCell}>
            <Text style={styles.planSummaryNum}>{byStage('confirmed').length}</Text>
            <Text style={styles.planSummaryKey}>Confirmed</Text>
          </View>
          <View style={styles.planSummaryCell}>
            <Text style={styles.planSummaryNum}>{byStage('signed').length}</Text>
            <Text style={styles.planSummaryKey}>Signed</Text>
          </View>
          <View style={styles.planSummaryCell}>
            <Text style={[styles.planSummaryNum, { color: '#9A7B2E' }]}>{planMoney(committed)}</Text>
            <Text style={styles.planSummaryKey}>Committed</Text>
          </View>
        </View>

        <View style={styles.viewToggle}>
          <TouchableOpacity
            style={[styles.viewToggleBtn, viewMode === 'cards' && styles.viewToggleBtnOn]}
            onPress={() => setViewMode('cards')}
            activeOpacity={0.75}
          >
            <Text style={[styles.viewToggleTxt, viewMode === 'cards' && styles.viewToggleTxtOn]}>Cards</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.viewToggleBtn, viewMode === 'list' && styles.viewToggleBtnOn]}
            onPress={() => setViewMode('list')}
            activeOpacity={0.75}
          >
            <Text style={[styles.viewToggleTxt, viewMode === 'list' && styles.viewToggleTxtOn]}>List</Text>
          </TouchableOpacity>
        </View>

        {viewMode === 'cards' ? (
          <>
            <Stage stage="shortlist" sec="planShort" title="Short List" />
            <Stage stage="confirmed" sec="planConf" title="Confirmed" />
            <Stage stage="signed" sec="planSigned" title="Signed Contract" />
          </>
        ) : (
          <View style={styles.listGroup}>
            {seasonPlans.length === 0 ? (
              <Text style={[styles.noNotesTxt, { padding: 16 }]}>Nothing planned for {year} yet.</Text>
            ) : (
              [
                { stage: 'shortlist', label: 'Short List', color: '#55697A' },
                { stage: 'confirmed', label: 'Confirmed', color: '#4A7C59' },
                { stage: 'signed', label: 'Signed Contract', color: '#9A7B2E' },
              ].map((g) => {
                const items = byStage(g.stage);
                if (items.length === 0) return null;
                return (
                  <View key={g.stage}>
                    <Text style={styles.listHead}>{g.label} · {items.length}</Text>
                    {items.map((p) => {
                      const list = p.studs || [];
                      const chosen = list.find((x) => x.id === p.chosenStudId);
                      return (
                        <TouchableOpacity
                          key={p.id}
                          style={[styles.listRow, { borderLeftColor: g.color }]}
                          onPress={() => openPlan(p)}
                          activeOpacity={0.7}
                        >
                          <Text style={styles.listMare} numberOfLines={1}>{planLabel(p)}</Text>
                          {!chosen && (
                            <Text style={styles.listStud} numberOfLines={1}>
                              {list.length === 0
                                ? 'no studs yet'
                                : list.length + ' stud' + (list.length === 1 ? '' : 's')}
                            </Text>
                          )}
                          <Text style={styles.listFee}>{planFeeLabel(p)}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                );
              })
            )}
          </View>
        )}

        {archivedPlans.length > 0 && (
          <CollapsibleSection
            sec="planArchive"
            title="Archived"
            badge={archivedPlans.length}
            collapsed={collapsed.archive !== false}
            onToggle={() => toggle('archive')}
          >
            {archivedPlans.map((p) => (
              <TouchableOpacity key={p.id} style={styles.archiveRow} onPress={() => openPlan(p)} activeOpacity={0.7}>
                <Text style={styles.archiveName} numberOfLines={1}>{planLabel(p)}</Text>
                <Text style={styles.archiveStage}>
                  {p.stage === 'bred' ? 'In foal' : p.stage === 'signed' ? 'Signed' : p.stage === 'confirmed' ? 'Confirmed' : 'Short list'}
                </Text>
              </TouchableOpacity>
            ))}
          </CollapsibleSection>
        )}

        {!viewing && (
        <TouchableOpacity
          style={styles.planPrimaryBtn}
          onPress={() => { resetPlanForm(); setShowPlanModal(true); }}
          activeOpacity={0.8}
        >
          <Text style={styles.planPrimaryBtnTxt}>+ Plan a Breeding</Text>
        </TouchableOpacity>
        )}

        {!viewing && (plans.some((p) => p.ownerId === myUid) || givenShares.length > 0) && (
          <TouchableOpacity style={styles.closeOutBtn} onPress={() => { setShareAllEmail(''); setShareAllScope('season'); setShowShareAll(true); }} activeOpacity={0.75}>
            <Text style={styles.closeOutBtnTxt}>
              {givenShares.length > 0 ? `🤝 Sharing with ${givenShares.length} — manage` : '🤝 Share All Plans'}
            </Text>
          </TouchableOpacity>
        )}

        {!viewing && openPlans.length > 0 && (
          <TouchableOpacity style={styles.closeOutBtn} onPress={() => setShowCloseOut(true)} activeOpacity={0.75}>
            <Text style={styles.closeOutBtnTxt}>Close Out {year}</Text>
          </TouchableOpacity>
        )}

        <TouchableOpacity
          style={styles.addMilestoneBtn}
          onPress={() => navigation.navigate('StudLibrary')}
          activeOpacity={0.75}
        >
          <Text style={styles.addMilestoneBtnTxt}>Stud Library ({studs.length})</Text>
        </TouchableOpacity>
      </ScrollView>

      <PlanShareSheet
        visible={!!sharePlan}
        plan={sharePlan}
        onClose={() => setSharePlanId(null)}
      />

      {/* Share every plan I own with one person, in one go */}
      <Modal visible={showShareAll} animationType="slide" transparent onRequestClose={() => setShowShareAll(false)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.sheet}>
            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={styles.sheetTitle}>Share All Plans</Text>
              <Text style={{ fontSize: 13, color: MUTED, lineHeight: 19, marginBottom: 12 }}>
                Every plan you own in a season goes to one person — including plans you add later, until you stop it. They have to accept first. To share just one plan, open that plan instead.
              </Text>

              {givenShares.length > 0 && (
                <View style={{ marginBottom: 14 }}>
                  <Text style={styles.fieldLabel}>Already sharing</Text>
                  {givenShares.map((s) => {
                    const who = otherPerson(s);
                    return (
                      <View key={s.id} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 0.5, borderBottomColor: BORDER }}>
                        <View style={{ flex: 1 }}>
                          <Text style={{ fontSize: 15, color: TEXT }}>{who.name || who.email}</Text>
                          <Text style={{ fontSize: 12, color: MUTED, marginTop: 1 }}>
                            {seasonLabel(s)} · {s.permission === 'full' ? 'Full Access' : 'View Only'}{s.status === 'pending' ? ' · waiting for them to accept' : s.applied ? '' : ' · syncing…'}
                          </Text>
                        </View>
                        <TouchableOpacity onPress={() => handleEndSeasonShare(s)} activeOpacity={0.7} style={{ paddingVertical: 6, paddingLeft: 12 }}>
                          <Text style={{ fontSize: 13, color: s.status === 'pending' ? MUTED : RED, fontWeight: '600' }}>{s.status === 'pending' ? 'Cancel' : 'Stop'}</Text>
                        </TouchableOpacity>
                      </View>
                    );
                  })}
                </View>
              )}
              <Text style={styles.fieldLabel}>Share with someone new</Text>

              <TextInput
                style={styles.input}
                placeholder="Their Horse Math email"
                placeholderTextColor="#BDBDBD"
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                value={shareAllEmail}
                onChangeText={setShareAllEmail}
              />

              <Text style={styles.fieldLabel}>Which plans</Text>
              <View style={{ flexDirection: 'row', gap: 8, marginBottom: 12 }}>
                {[['season', `${year} season`], ['all', 'Every season']].map(([k, label]) => (
                  <TouchableOpacity key={k} style={[styles.categoryChip, shareAllScope === k && styles.categoryChipActive]} onPress={() => setShareAllScope(k)} activeOpacity={0.75}>
                    <Text style={[styles.categoryChipTxt, shareAllScope === k && styles.categoryChipTxtActive]}>{label}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              <Text style={styles.fieldLabel}>They can</Text>
              <View style={{ flexDirection: 'row', gap: 8, marginBottom: 6 }}>
                {[['view', 'View Only'], ['full', 'Full Access']].map(([k, label]) => (
                  <TouchableOpacity key={k} style={[styles.categoryChip, shareAllPerm === k && styles.categoryChipActive]} onPress={() => setShareAllPerm(k)} activeOpacity={0.75}>
                    <Text style={[styles.categoryChipTxt, shareAllPerm === k && styles.categoryChipTxtActive]}>{label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              <Text style={{ fontSize: 11, color: MUTED, marginBottom: 10 }}>
                {(() => { const n = shareAllTargets().length; return `${plural(n, 'plan')} of yours ${shareAllScope === 'all' ? 'across every season' : `in ${year}`} right now, plus any you add.`; })()}
              </Text>

              <View style={styles.sheetBtns}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowShareAll(false)} activeOpacity={0.85}>
                  <Text style={styles.cancelBtnText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={handleShareAll} disabled={shareAllBusy} activeOpacity={0.85}>
                  {shareAllBusy ? <ActivityIndicator color="#FFF" /> : <Text style={styles.saveBtnText}>Send Invite</Text>}
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      <FrozenImplantSheet
        visible={!!frozenPlan}
        batches={frozenPlan ? frozenMatchesFor(frozenPlan, horses) : []}
        recips={recipHorses}
        busy={frozenBusy}
        onClose={() => setFrozenPlanId(null)}
        onImplant={handleImplantForPlan}
      />

      {/* ── Close out the season ── */}
      <Modal visible={showCloseOut} animationType="slide" transparent onRequestClose={() => setShowCloseOut(false)}>
        <View style={styles.overlay}>
          <View style={[styles.sheet, { maxHeight: '85%' }]}>
            <ScrollView showsVerticalScrollIndicator={false}>
              <Text style={styles.sheetTitle}>Close Out {year}</Text>
              <Text style={styles.contractHint}>
                {openPlans.length} plan{openPlans.length === 1 ? '' : 's'} in this season.
                Choose what carries into {year + 1} — everything else is archived where it is.
                Nothing is deleted.
              </Text>

              {[
                { on: carryShortlists, set: setCarryShortlists, label: 'Carry over short lists',
                  n: byStage('shortlist').length,
                  sub: 'Mares you never chose a stud for. Candidates and pedigrees come with them.' },
                { on: carryConfirmed, set: setCarryConfirmed, label: 'Carry over confirmed picks',
                  n: byStage('confirmed').length,
                  sub: 'Crosses you decided on but never contracted.' },
                { on: carryContracts, set: setCarryContracts, label: 'Carry over unused contracts',
                  n: byStage('signed').length,
                  sub: 'Signed contracts that never produced a pregnancy. Fee, payments and the PDF stay attached, tagged Rebreed.' },
              ].map((o) => (
                <TouchableOpacity key={o.label} style={styles.carryRow} onPress={() => o.set(!o.on)} activeOpacity={0.75}>
                  <View style={[styles.checkBox, o.on && styles.checkBoxOn]}>
                    {o.on && <Text style={styles.checkTick}>✓</Text>}
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.carryLabel}>{o.label} ({o.n})</Text>
                    <Text style={styles.carrySub}>{o.sub}</Text>
                  </View>
                </TouchableOpacity>
              ))}

              <Text style={styles.contractHint}>
                {year} stays readable from the year picker, with everything archived under it.
              </Text>

              <View style={styles.sheetBtns}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowCloseOut(false)} activeOpacity={0.75}>
                  <Text style={styles.cancelBtnText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={handleCloseOut} activeOpacity={0.8}>
                  <Text style={styles.saveBtnText}>Close out season</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* ── Sign the contract ── */}
      <Modal visible={!!signPlanId} animationType="slide" transparent onRequestClose={() => setSignPlanId(null)}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '90%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
              <Text style={styles.sheetTitle}>Sign the Contract</Text>

              <Text style={styles.fieldLabel}>Method</Text>
              <View style={styles.methodWrap}>
                {['Live cover', 'Cooled AI', 'Frozen AI', 'ICSI', 'Embryo transfer'].map((m) => (
                  <TouchableOpacity
                    key={m}
                    style={[styles.methodChip, signMethod === m && styles.methodChipOn]}
                    onPress={() => setSignMethod(m)}
                    activeOpacity={0.75}
                  >
                    <Text style={[styles.methodChipTxt, signMethod === m && styles.methodChipTxtOn]}>{m}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              <Text style={[styles.fieldLabel, { marginTop: 14 }]}>Stud fee</Text>
              <TextInput
                style={styles.input}
                placeholder="e.g. 6000"
                placeholderTextColor="#BDBDBD"
                value={signFee}
                onChangeText={setSignFee}
                keyboardType="decimal-pad"
              />

              <Text style={styles.fieldLabel}>Stud farm</Text>
              <TextInput
                style={styles.input}
                placeholder="Where he stands"
                placeholderTextColor="#BDBDBD"
                value={signFarm}
                onChangeText={setSignFarm}
              />

              <Text style={styles.fieldLabel}>Contact</Text>
              <TextInput
                style={styles.input}
                placeholder="Phone or email"
                placeholderTextColor="#BDBDBD"
                value={signContact}
                onChangeText={setSignContact}
              />

              <Text style={styles.contractHint}>
                Signing opens the contract, where you record payments, upload the signed PDF, and follow the breeding through to in foal.
              </Text>

              <View style={styles.sheetBtns}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setSignPlanId(null)} activeOpacity={0.75}>
                  <Text style={styles.cancelBtnText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={handleSignContract} activeOpacity={0.8}>
                  <Text style={styles.saveBtnText}>Sign</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── Compare crosses ── */}
      <Modal visible={!!comparePlan} animationType="slide" transparent onRequestClose={() => setComparePlanId(null)}>
        <View style={styles.overlay}>
          <View style={[styles.sheet, { maxHeight: '90%' }]}>
            <Text style={styles.sheetTitle}>{comparePlan ? 'Compare · ' + comparePlan.mareName : 'Compare'}</Text>
            <ScrollView style={{ flexShrink: 1 }} showsVerticalScrollIndicator={false}>
              {(comparePlan?.studs || []).map((st) => (
                <View key={st.id} style={styles.cmpCard}>
                  <View style={styles.cmpHead}>
                    <Text style={styles.cmpName}>{st.name}</Text>
                    <Text style={styles.cmpFee}>{planMoney(st.fee)}</Text>
                  </View>
                  <StudPedigree stud={st} />
                  {!st.sire && !st.dam && (
                    <Text style={styles.cmpNoPed}>No pedigree entered — add it in the stud library to compare crosses.</Text>
                  )}
                  {!!incentiveText(st.incentives) && (
                    <Text style={styles.incText}>⭐ {incentiveText(st.incentives)}</Text>
                  )}
                  {!!st.notes && <Text style={styles.cmpNotes}>{st.notes}</Text>}
                  <TouchableOpacity
                    style={styles.cmpChoose}
                    onPress={() => { setComparePlanId(null); handleChooseStud(comparePlan, st); }}
                    activeOpacity={0.8}
                  >
                    <Text style={styles.cmpChooseTxt}>Choose this cross</Text>
                  </TouchableOpacity>
                </View>
              ))}
            </ScrollView>
            <TouchableOpacity style={styles.cancelBtn} onPress={() => setComparePlanId(null)} activeOpacity={0.75}>
              <Text style={styles.cancelBtnText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* ── One plan, opened from the list view ── */}
      <Modal visible={!!detailPlan} animationType="slide" transparent onRequestClose={() => setDetailPlanId(null)}>
        <View style={styles.overlay}>
          <View style={[styles.sheet, { maxHeight: '85%' }]}>
            <Text style={styles.sheetTitle}>{planLabel(detailPlan)}</Text>
            {/* Sharing sits under the title, not at the foot of a long scroll
                where nobody found it. Owner only — recipients cannot re-share. */}
            {detailPlan && detailPlan.ownerId === myUid && (
              <TouchableOpacity
                style={[styles.sharePlanBtn, { marginBottom: 10 }]}
                onPress={() => { setDetailPlanId(null); setSharePlanId(detailPlan.id); }}
                activeOpacity={0.75}
              >
                <Text style={styles.sharePlanBtnTxt}>
                  {(detailPlan.shares || []).length > 0
                    ? '🤝 Shared with ' + (detailPlan.shares || []).length + ' — manage'
                    : '🤝 Share this plan'}
                </Text>
              </TouchableOpacity>
            )}
            {detailPlan && detailPlan.ownerId === myUid && <PlanSharesList plan={detailPlan} />}
            <ScrollView style={{ flexShrink: 1 }} showsVerticalScrollIndicator={false}>
              {detailPlan && <PlanDetail plan={detailPlan} />}
            </ScrollView>
            <TouchableOpacity style={styles.cancelBtn} onPress={() => setDetailPlanId(null)} activeOpacity={0.75}>
              <Text style={styles.cancelBtnText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* ── New plan sheet ── */}
      <Modal visible={showPlanModal} animationType="slide" transparent onRequestClose={() => { setShowPlanModal(false); resetPlanForm(); }}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '90%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
              <Text style={styles.sheetTitle}>Plan a Breeding</Text>

              <Text style={styles.fieldLabel}>Season</Text>
              <View style={styles.seasonStrip}>
                {years.map((y) => (
                  <TouchableOpacity
                    key={y}
                    style={[styles.seasonChip, y === year && styles.seasonChipOn]}
                    onPress={() => setYear(y)}
                    activeOpacity={0.75}
                  >
                    <Text style={[styles.seasonChipTxt, y === year && styles.seasonChipTxtOn]}>{y}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              <Text style={[styles.fieldLabel, { marginTop: 14 }]}>Mare</Text>
              <View style={styles.segRow}>
                <TouchableOpacity
                  style={[styles.segBtn, mareSource === 'herd' && styles.segBtnOn]}
                  onPress={() => { setMareSource('herd'); setMareName(''); setMareId(null); }}
                  activeOpacity={0.75}
                >
                  <Text style={[styles.segBtnTxt, mareSource === 'herd' && styles.segBtnTxtOn]}>From my herd</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.segBtn, mareSource === 'outside' && styles.segBtnOn]}
                  onPress={() => { setMareSource('outside'); setMareName(''); setMareId(null); }}
                  activeOpacity={0.75}
                >
                  <Text style={[styles.segBtnTxt, mareSource === 'outside' && styles.segBtnTxtOn]}>Outside mare</Text>
                </TouchableOpacity>
              </View>

              {mareSource === 'herd' ? (
                <TouchableOpacity style={styles.pickerBtn} onPress={() => setShowMarePicker(true)} activeOpacity={0.75}>
                  <Text style={[styles.pickerBtnTxt, !mareName && { color: '#BDBDBD' }]}>
                    {mareName || 'Choose a mare…'}
                  </Text>
                  <Text style={styles.pickerBtnCaret}>▾</Text>
                </TouchableOpacity>
              ) : (
                <TextInput
                  style={styles.input}
                  placeholder="Mare's name"
                  placeholderTextColor="#BDBDBD"
                  value={mareName}
                  onChangeText={setMareName}
                />
              )}

              <Text style={styles.fieldLabel}>Studs under consideration</Text>
              {draftStuds.length === 0 ? (
                <Text style={styles.noNotesTxt}>None yet — you can add them later too.</Text>
              ) : (
                draftStuds.map((s) => (
                  <View key={s.id} style={styles.studRow}>
                    <Text style={styles.studName}>{s.name}</Text>
                    <Text style={styles.studFee}>{planMoney(s.fee)}</Text>
                    <TouchableOpacity
                      onPress={() => setDraftStuds((prev) => prev.filter((x) => x.id !== s.id))}
                      activeOpacity={0.7}
                    >
                      <Text style={styles.studRemove}>✕</Text>
                    </TouchableOpacity>
                  </View>
                ))
              )}
              <TouchableOpacity style={styles.planAddStud} onPress={() => openStudSheet('draft')} activeOpacity={0.75}>
                <Text style={styles.planAddStudTxt}>+ Add a stud</Text>
              </TouchableOpacity>

              <View style={styles.sheetBtns}>
                <TouchableOpacity
                  style={styles.cancelBtn}
                  onPress={() => { setShowPlanModal(false); resetPlanForm(); }}
                  activeOpacity={0.75}
                >
                  <Text style={styles.cancelBtnText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={handleCreatePlan} activeOpacity={0.8}>
                  <Text style={styles.saveBtnText}>Add to Short List</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── Mare picker ── */}
      <Modal visible={showMarePicker} animationType="slide" transparent onRequestClose={() => { setShowMarePicker(false) }}>
        <View style={styles.overlay}>
          <View style={[styles.sheet, { maxHeight: '70%' }]}>
            <Text style={styles.sheetTitle}>Choose a Mare</Text>
            <ScrollView style={{ flexShrink: 1 }}>
              {mares.length === 0 ? (
                <Text style={styles.noNotesTxt}>No mares in your herd yet.</Text>
              ) : (
                mares.map((h) => (
                  <TouchableOpacity
                    key={h.id}
                    style={styles.pickerRow}
                    onPress={() => { setMareName(displayName(h)); setMareId(h.id); setShowMarePicker(false); }}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.pickerRowName}>{displayName(h)}</Text>
                  </TouchableOpacity>
                ))
              )}
            </ScrollView>
            <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowMarePicker(false)} activeOpacity={0.75}>
              <Text style={styles.cancelBtnText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* ── Add stud sheet ── */}
      <Modal visible={showStudModal} animationType="slide" transparent onRequestClose={() => { setShowStudModal(false) }}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '85%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
              <Text style={styles.sheetTitle}>Add a Stud</Text>

              {studs.length > 0 && (
                <View style={styles.segRow}>
                  <TouchableOpacity
                    style={[styles.segBtn, studSource === 'saved' && styles.segBtnOn]}
                    onPress={() => setStudSource('saved')}
                    activeOpacity={0.75}
                  >
                    <Text style={[styles.segBtnTxt, studSource === 'saved' && styles.segBtnTxtOn]}>Saved</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.segBtn, studSource === 'new' && styles.segBtnOn]}
                    onPress={() => setStudSource('new')}
                    activeOpacity={0.75}
                  >
                    <Text style={[styles.segBtnTxt, studSource === 'new' && styles.segBtnTxtOn]}>New stud</Text>
                  </TouchableOpacity>
                </View>
              )}

              {studSource === 'saved' && studs.length > 0 ? (
                studs.map((s) => (
                  <TouchableOpacity
                    key={s.id}
                    style={[styles.pickerRow, styles.studPickRow]}
                    onPress={() => handlePickSavedStud(s)}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.pickerRowName}>{s.name}</Text>
                    <Text style={styles.studFee}>{planMoney(s.fee)}</Text>
                  </TouchableOpacity>
                ))
              ) : (
                <>
                  <Text style={styles.fieldLabel}>Stallion</Text>
                  <TextInput
                    style={styles.input}
                    placeholder="Stallion's name"
                    placeholderTextColor="#BDBDBD"
                    value={studName}
                    onChangeText={setStudName}
                    autoFocus
                  />
                  <Text style={styles.fieldLabel}>Stud fee — optional</Text>
                  <TextInput
                    style={styles.input}
                    placeholder="e.g. 4500"
                    placeholderTextColor="#BDBDBD"
                    value={studFee}
                    onChangeText={setStudFee}
                    keyboardType="decimal-pad"
                  />
                  <TouchableOpacity
                    style={styles.pedToggle}
                    onPress={() => setShowPedigree((v) => !v)}
                    activeOpacity={0.75}
                  >
                    <Text style={styles.pedToggleTxt}>
                      {showPedigree ? '− Hide pedigree' : '+ Add pedigree — optional, for comparing'}
                    </Text>
                  </TouchableOpacity>

                  {showPedigree && (
                    <View style={styles.pedBox}>
                      <Text style={styles.pedGen}>Sire side</Text>
                      <TextInput style={styles.input} placeholder="Sire" placeholderTextColor="#BDBDBD"
                        value={ped.sire} onChangeText={(v) => setPedField('sire', v)} />
                      <TextInput style={styles.input} placeholder="Sire's sire" placeholderTextColor="#BDBDBD"
                        value={ped.sireSire} onChangeText={(v) => setPedField('sireSire', v)} />
                      <TextInput style={styles.input} placeholder="Sire's dam" placeholderTextColor="#BDBDBD"
                        value={ped.sireDam} onChangeText={(v) => setPedField('sireDam', v)} />
                      <Text style={styles.pedGen}>Dam side</Text>
                      <TextInput style={styles.input} placeholder="Dam" placeholderTextColor="#BDBDBD"
                        value={ped.dam} onChangeText={(v) => setPedField('dam', v)} />
                      <TextInput style={styles.input} placeholder="Dam's sire" placeholderTextColor="#BDBDBD"
                        value={ped.damSire} onChangeText={(v) => setPedField('damSire', v)} />
                      <TextInput style={styles.input} placeholder="Dam's dam" placeholderTextColor="#BDBDBD"
                        value={ped.damDam} onChangeText={(v) => setPedField('damDam', v)} />
                    </View>
                  )}

                  <TouchableOpacity
                    style={styles.saveLibRow}
                    onPress={() => setSaveToLibrary((v) => !v)}
                    activeOpacity={0.75}
                  >
                    <View style={[styles.saveLibBox, saveToLibrary && styles.saveLibBoxOn]}>
                      {saveToLibrary && <Text style={styles.saveLibTick}>✓</Text>}
                    </View>
                    <Text style={styles.saveLibTxt}>Save to the stud library for reuse</Text>
                  </TouchableOpacity>
                </>
              )}

              <View style={styles.sheetBtns}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowStudModal(false)} activeOpacity={0.75}>
                  <Text style={styles.cancelBtnText}>Cancel</Text>
                </TouchableOpacity>
                {studSource !== 'saved' && (
                  <TouchableOpacity style={styles.saveBtn} onPress={handleSaveStud} activeOpacity={0.8}>
                    <Text style={styles.saveBtnText}>Add stud</Text>
                  </TouchableOpacity>
                )}
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </SafeAreaView>
  );
}

// ─── BREEDING CONTRACT ────────────────────────────────────────────────────────

// Check reminders fire at 9am local — a vet appointment you need to have booked
// that morning, not a midnight buzz.
const CHECK_REMINDER_HOUR = 9;

const checkDueDate = (bredDate, daysOffset) => addDays(bredDate, daysOffset);

const checkReminderAt = (bredDate, daysOffset) => {
  const d = new Date(addDays(bredDate, daysOffset) + 'T12:00:00');
  d.setHours(CHECK_REMINDER_HOUR, 0, 0, 0);
  return d;
};

// Fee, paid and what's left. Balance and the chip are derived, never stored, so
// they cannot drift from the numbers they came from.
const contractBalance = (plan) => {
  const fee = typeof plan?.fee === 'number' ? plan.fee : 0;
  const paid = typeof plan?.paid === 'number' ? plan.paid : 0;
  return Math.max(0, fee - paid);
};
const paymentState = (plan) => {
  const fee = typeof plan?.fee === 'number' ? plan.fee : 0;
  const paid = typeof plan?.paid === 'number' ? plan.paid : 0;
  if (fee <= 0) return { key: 'none', label: 'No fee' };
  if (paid <= 0) return { key: 'unpaid', label: 'Unpaid' };
  if (paid >= fee) return { key: 'paid', label: 'Paid' };
  return { key: 'partial', label: 'Part paid' };
};

function BreedingContractScreen({ route, navigation }) {
  const planId = route.params?.planId;
  const [plan, setPlan] = useState(null);
  const [horses, setHorses] = useState([]);
  const [busy, setBusy] = useState(false);

  const [showPayModal, setShowPayModal] = useState(false);
  const [payAmount, setPayAmount] = useState('');

  const [bredDate, setBredDate] = useState(new Date());
  const [showBredModal, setShowBredModal] = useState(false);
  const [showBredPicker, setShowBredPicker] = useState(false);

  const [showRecipPicker, setShowRecipPicker] = useState(false);
  const [leasedName, setLeasedName] = useState('');
  const [showFrozen, setShowFrozen] = useState(false);
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    const unsub = subscribeToBreedingPlan(planId, setPlan);
    return unsub;
  }, [planId]);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    return subscribeToHorses(uid, setHorses);
  }, []);

  if (!plan) {
    return (
      <SafeAreaView style={styles.screen} edges={['bottom']}>
        <Text style={[styles.noNotesTxt, { marginTop: 30 }]}>Loading…</Text>
      </SafeAreaView>
    );
  }

  const list = plan.studs || [];
  const chosen = list.find((x) => x.id === plan.chosenStudId) || list[0] || null;
  const checks = plan.checks || [];
  const balance = contractBalance(plan);
  const pay = paymentState(plan);
  const recips = horses.filter((h) => (h.status || []).includes('Recip'));
  const mareHorse = plan.mareId ? horses.find((h) => h.id === plan.mareId) : null;

  const save = (data) => updateBreedingPlan(plan.id, data).catch(() =>
    Alert.alert('Could not save', 'Please check your connection and try again.'));

  const frozenMatches = frozenMatchesFor(plan, horses);
  const myUid = auth.currentUser?.uid;
  const isPlanOwner = plan.ownerId === myUid;
  // View Only sees everything and changes nothing.
  const readOnly = !canEditPlan(plan, myUid);

  // Records the implant and starts her checks. She joins the herd later, at
  // Confirmed in Foal, like any other pregnancy.
  const handleImplantHere = async ({ batch, date, recip: r, recipId }) => {
    if (busy) return;
    setBusy(true);
    try {
      const { breedDate, newCount } = await recordFrozenImplant({ batch, implantDate: date });
      checks.forEach((c) => { if (c.notificationId) cancelReminder(c.notificationId); });
      const fresh = buildChecks(!!r || !!plan.isRecip);
      const withReminders = [];
      for (const c of fresh) {
        let notificationId = null;
        const when = checkReminderAt(breedDate, c.daysOffset);
        if (when > new Date()) {
          try {
            notificationId = await scheduleReminder(
              c.name, planLabel(plan) + ' — ' + c.name + ' due today', when
            );
          } catch (e) { /* a missing reminder must not lose the implant */ }
        }
        withReminders.push({ ...c, notificationId: notificationId || null });
      }
      await save({
        bredDate: breedDate,
        checks: withReminders,
        fromFrozenId: batch.id,
        implantedDate: date.toISOString().split('T')[0],
        embryoMethod: batch.method || 'ICSI',
        ...(r && { isRecip: true, recipMare: r }),
        ...(recipId && { recipHorseId: recipId }),
      });
      if (newCount === 0) {
        try { await updateHorse(batch.id, { depleted: true }); } catch (e) {}
      }
      setShowFrozen(false);
    } catch (e) {
      Alert.alert('Could not record the implant', 'Please check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  // ── Payments ───────────────────────────────────────────────────────────────
  const handleRecordPayment = async () => {
    const amt = parseFloat(String(payAmount).replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(amt) || amt <= 0) {
      Alert.alert('Amount required', 'Enter how much was paid.');
      return;
    }
    const paid = (typeof plan.paid === 'number' ? plan.paid : 0) + amt;
    await save({ paid });
    setShowPayModal(false);
    setPayAmount('');
  };

  // ── Contract file ──────────────────────────────────────────────────────────
  const handleUploadContract = async () => {
    if (!isDocPickingAvailable()) {
      Alert.alert('Not available', 'File picking is not available on this device.');
      return;
    }
    try {
      const picked = await pickDocumentAsync();
      if (!picked) return;
      setBusy(true);
      const ts = Date.now();
      const safeName = (picked.name || 'contract.pdf').replace(/[^\w.\-]+/g, '_').slice(-80);
      const path = 'breedingPlans/' + plan.id + '/contract/' + ts + '_' + safeName;
      const url = await uploadFileAsync(path, picked.file || picked.uri);
      await save({
        contractDoc: {
          url, path, name: safeName,
          mime: picked.mime || 'application/pdf',
          uploadedAt: new Date().toISOString(),
        },
      });
    } catch (e) {
      Alert.alert('Upload failed', 'The contract could not be uploaded. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // ── Recip ──────────────────────────────────────────────────────────────────
  // Turning the toggle off clears the recip and drops the 120 day check; turning
  // it on adds that check back if the checks already exist.
  const handleToggleRecip = async () => {
    const next = !plan.isRecip;
    const data = { isRecip: next };
    if (!next) {
      data.recipMare = '';
      data.recipHorseId = null;
      data.recipLeasedFrom = '';
    }
    if (checks.length > 0) {
      const has120 = checks.some((c) => c.daysOffset === 120);
      if (next && !has120) {
        data.checks = [...checks, { ...RECIP_CHECK, completed: false }];
      } else if (!next && has120) {
        const c120 = checks.find((c) => c.daysOffset === 120);
        if (c120?.notificationId) cancelReminder(c120.notificationId);
        data.checks = checks.filter((c) => c.daysOffset !== 120);
      }
    }
    await save(data);
  };

  const pickRecip = async (h) => {
    await save({ isRecip: true, recipMare: displayName(h), recipHorseId: h.id, recipLeasedFrom: '' });
    setShowRecipPicker(false);
  };

  const saveLeasedRecip = async () => {
    if (!leasedName.trim()) return;
    await save({ isRecip: true, recipMare: leasedName.trim(), recipHorseId: null, recipLeasedFrom: leasedName.trim() });
    setLeasedName('');
    setShowRecipPicker(false);
  };

  // ── Bred date generates the checks ─────────────────────────────────────────
  const handleSetBredDate = async () => {
    const ymd = localYmd(bredDate);
    const fresh = buildChecks(!!plan.isRecip);
    const withReminders = [];
    for (const c of fresh) {
      let notificationId = null;
      const when = checkReminderAt(ymd, c.daysOffset);
      if (when > new Date()) {
        try {
          notificationId = await scheduleReminder(
            c.name,
            planLabel(plan) + ' — ' + c.name + ' due today',
            when
          );
        } catch (e) { /* a missing reminder must not stop the date being saved */ }
      }
      withReminders.push({ ...c, notificationId: notificationId || null });
    }
    // Any earlier attempt's reminders belong to a cycle that is over.
    checks.forEach((c) => { if (c.notificationId) cancelReminder(c.notificationId); });
    await save({ bredDate: ymd, checks: withReminders });
    // The expected foaling year is only knowable now, so refresh the Coming Soon
    // line on both horses in the cross.
    try {
      await Promise.allSettled(
        await syncComingSoon({ ...plan, bredDate: ymd }, horses, auth.currentUser?.uid, updateHorse)
      );
    } catch (e) { /* cosmetic */ }
    setShowBredModal(false);
  };

  const toggleCheck = async (check) => {
    const done = !check.completed;
    if (done && check.notificationId) cancelReminder(check.notificationId);
    let notificationId = check.notificationId || null;
    if (!done && plan.bredDate) {
      const when = checkReminderAt(plan.bredDate, check.daysOffset);
      notificationId = when > new Date()
        ? await scheduleReminder(check.name, planLabel(plan) + ' — ' + check.name + ' due today', when).catch(() => null)
        : null;
    }
    await save({
      checks: checks.map((c) => c.id === check.id
        ? { ...c, completed: done, completedDate: done ? new Date().toISOString().split('T')[0] : null, notificationId: done ? null : notificationId }
        : c),
    });
  };

  // ── Didn't take ────────────────────────────────────────────────────────────
  // Clears the cycle's dates and keeps the booking. The attempt is recorded so a
  // mare who took three tries shows it instead of looking like a first-time hit.
  const handleDidntTake = () => {
    Alert.alert(
      'Didn’t take',
      plan.fromFrozenId
        ? 'Clear the implant date and checks so you can try again?\n\nThe thawed embryo is not returned to the batch — it is used either way. The contract and everything you have paid stay as they are.'
        : 'Clear the bred date and checks so you can breed her again?\n\nThe contract, the fee and everything you have paid stay exactly as they are.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Reset dates',
          style: 'destructive',
          onPress: async () => {
            checks.forEach((c) => { if (c.notificationId) cancelReminder(c.notificationId); });
            await save({
              bredDate: null,
              checks: [],
              // The embryo is NOT returned to the batch — thawing consumes it.
              fromFrozenId: null,
              implantedDate: null,
              attempts: [
                ...(plan.attempts || []),
                {
                  bredDate: plan.bredDate || null,
                  method: plan.fromFrozenId ? 'Frozen embryo' : (plan.method || ''),
                  outcome: 'Didn’t take',
                },
              ],
            });
          },
        },
      ]
    );
  };

  // ── Confirmed in foal ──────────────────────────────────────────────────────
  const handleConfirmedInFoal = () => {
    if (!plan.bredDate) {
      Alert.alert('Bred date needed', 'Record the bred date first — the foal’s dates are counted from it.');
      return;
    }
    const label = planLabel(plan);
    Alert.alert(
      'Confirmed in foal',
      'Add ' + label + ' to My Herd as an embryo?\n\nShe carries the bred date, the checks you have already ticked, and the signed contract.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Add to herd',
          onPress: async () => {
            setBusy(true);
            try {
              const uid = auth.currentUser?.uid;
              const doneAt = (offset) => checks.find((c) => c.daysOffset === offset && c.completed)?.completedDate || null;
              const isDone = (offset) => !!checks.find((c) => c.daysOffset === offset && c.completed);
              const milestone = (id, name, offset) => ({
                id, name, daysOffset: offset,
                completed: isDone(offset),
                ...(isDone(offset) && { completedDate: doneAt(offset) }),
              });
              const horseId = await addHorse(uid, {
                name: label,
                type: 'embryo',
                // Without this she has no status, so she falls outside the herd's
                // status grouping and the Embryo filter.
                status: ['Embryo'],
                isRecip: !!plan.isRecip,
                ...(plan.recipMare && { recipMare: plan.recipMare }),
                ...(plan.recipLeasedFrom && { recipLeased: true, recipLeasedFrom: plan.recipLeasedFrom }),
                // The date she was actually bred, not the day this button was pressed.
                breedDate: plan.bredDate,
                notes: '',
                // A thawed embryo keeps its provenance, so the foal's record
                // still points back to the batch it came from.
                ...(plan.fromFrozenId && {
                  fromFrozenId: plan.fromFrozenId,
                  implantedDate: plan.implantedDate || null,
                  embryoMethod: plan.embryoMethod || 'ICSI',
                }),
                // Pedigree flows through: the stud and mare become the foal's
                // parents, and their own parents become its grandparents.
                sire: chosen?.name || '',
                sireSire: chosen?.sire || '',
                sireDam: chosen?.dam || '',
                ...(chosen?.horseId && { sireId: chosen.horseId }),
                dam: plan.mareName || '',
                damSire: mareHorse?.sire || '',
                damDam: mareHorse?.dam || '',
                ...(plan.mareId && { damId: plan.mareId }),
                milestones: [
                  { id: 'm0', name: 'Breed Date', daysOffset: 0, completed: true, completedDate: plan.bredDate },
                  milestone('m1', '14 Day Check', 14),
                  milestone('m2', '30 Day Check', 30),
                  milestone('m3', '45 Day Check', 45),
                  ...(plan.isRecip ? [milestone('m4', '120 Day Check', 120)] : []),
                ],
              });
              // The contract follows the horse for life — marking born only
              // rewrites milestones, so it stays on her record after foaling.
              if (plan.contractDoc?.url) {
                try {
                  await addHorseDocument(horseId, { ...plan.contractDoc, docType: 'Breeding Contract' });
                } catch (e) { /* the horse still exists; the file stays on the plan */ }
              }
              checks.forEach((c) => { if (c.notificationId) cancelReminder(c.notificationId); });
              // The Coming Soon row goes as the real embryo arrives — one row
              // replaced by the thing it was promising.
              try {
                await Promise.allSettled(
                  await syncComingSoon({ ...plan, stage: 'bred' }, horses, uid, updateHorse)
                );
              } catch (e) { /* the foal exists; a stale row is cosmetic */ }
              await save({ stage: 'bred', bredHorseId: horseId });
              navigation.replace('Horse', { horseId });
            } catch (e) {
              Alert.alert('Could not add', 'Please check your connection and try again.');
            } finally {
              setBusy(false);
            }
          },
        },
      ]
    );
  };

  const Row = ({ k, v, color }) => (
    <View style={styles.contractRow}>
      <Text style={styles.contractKey}>{k}</Text>
      <Text style={[styles.contractVal, color && { color }]}>{v}</Text>
    </View>
  );

  return (
    <SafeAreaView style={styles.screen} edges={['bottom']}>
      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
        <Text style={styles.contractTitle}>{planLabel(plan)}</Text>
        <Text style={styles.contractSub}>
          {plan.year}{plan.signedDate ? ' · signed ' + formatDate(plan.signedDate) : ''}
        </Text>
        {!isPlanOwner && (
          <Text style={styles.sharedBadge}>
            {readOnly ? 'Shared with you · View Only' : 'Shared with you · Full Access'}
          </Text>
        )}
        {isPlanOwner && (
          <TouchableOpacity style={styles.sharePlanBtn} onPress={() => setShowShare(true)} activeOpacity={0.75}>
            <Text style={styles.sharePlanBtnTxt}>
              {(plan.shares || []).length > 0
                ? 'Shared with ' + (plan.shares || []).length + ' — manage'
                : 'Share this plan'}
            </Text>
          </TouchableOpacity>
        )}
        {isPlanOwner && <PlanSharesList plan={plan} />}

        {plan.stage === 'bred' && (
          <View style={styles.contractDone}>
            <Text style={styles.contractDoneTxt}>
              Confirmed in foal — she is in My Herd.
            </Text>
            {!!plan.bredHorseId && (
              <TouchableOpacity onPress={() => navigation.navigate('Horse', { horseId: plan.bredHorseId })} activeOpacity={0.7}>
                <Text style={styles.contractDoneLink}>Open her record ›</Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {!readOnly && plan.stage !== 'bred' && !plan.fromFrozenId && frozenMatches.length > 0 && (
          <TouchableOpacity style={styles.frozenBanner} onPress={() => setShowFrozen(true)} activeOpacity={0.75}>
            <Text style={styles.frozenBannerTxt}>
              🧊 You have {frozenAvailableCount(frozenMatches)} frozen embryo
              {frozenAvailableCount(frozenMatches) === 1 ? '' : 's'} from this cross
            </Text>
            <Text style={styles.frozenBannerAction}>Record an implant instead of breeding ›</Text>
          </TouchableOpacity>
        )}

        {/* Stud fee */}
        <Text style={styles.contractHead}>Stud fee</Text>
        <View style={styles.contractCard}>
          <Row k="Fee" v={planMoney(plan.fee)} />
          <Row k="Paid" v={planMoney(plan.paid || 0)} />
          <Row k="Balance" v={planMoney(balance)} color={balance > 0 ? RED : GREEN} />
          <View style={styles.contractRow}>
            <Text style={styles.contractKey}>Status</Text>
            <Text style={[styles.payChip, styles['payChip_' + pay.key]]}>{pay.label}</Text>
          </View>
          {!readOnly && plan.stage !== 'bred' && (
            <TouchableOpacity style={styles.contractBtn} onPress={() => setShowPayModal(true)} activeOpacity={0.75}>
              <Text style={styles.contractBtnTxt}>Record a payment</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Contract file */}
        <Text style={styles.contractHead}>Contract</Text>
        <View style={styles.contractCard}>
          {plan.contractDoc?.url ? (
            <>
              <TouchableOpacity
                style={styles.docRow}
                onPress={() => Linking.openURL(plan.contractDoc.url)}
                activeOpacity={0.7}
              >
                <Text style={styles.docIcon}>📄</Text>
                <View style={{ flex: 1 }}>
                  <Text style={styles.docName}>{plan.contractDoc.name}</Text>
                  <Text style={styles.docMeta}>Uploaded {formatDate((plan.contractDoc.uploadedAt || '').split('T')[0])}</Text>
                </View>
                <Text style={styles.docOpen}>Open</Text>
              </TouchableOpacity>
              {!readOnly && plan.stage !== 'bred' && (
                <TouchableOpacity style={styles.contractBtn} onPress={handleUploadContract} activeOpacity={0.75} disabled={busy}>
                  <Text style={styles.contractBtnTxt}>{busy ? 'Uploading…' : 'Replace'}</Text>
                </TouchableOpacity>
              )}
            </>
          ) : readOnly ? (
            <Text style={styles.contractHint}>No contract uploaded.</Text>
          ) : (
            <TouchableOpacity style={styles.contractBtn} onPress={handleUploadContract} activeOpacity={0.75} disabled={busy}>
              <Text style={styles.contractBtnTxt}>{busy ? 'Uploading…' : 'Upload the signed contract'}</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Breeding */}
        <Text style={styles.contractHead}>Breeding</Text>
        <View style={styles.contractCard}>
          <Row k="Method" v={plan.method || '—'} />
          <Row k="Stud farm" v={plan.farm || chosen?.farm || '—'} />
          <Row k="Contact" v={plan.contact || chosen?.contact || '—'} />
        </View>

        {/* Recip */}
        <Text style={styles.contractHead}>Carried by a recip</Text>
        <View style={styles.contractCard}>
          <TouchableOpacity style={styles.toggleRow} onPress={handleToggleRecip} activeOpacity={0.75} disabled={readOnly || plan.stage === 'bred'}>
            <Text style={styles.toggleLabel}>In recip</Text>
            <View style={[styles.switchTrack, plan.isRecip && styles.switchTrackOn]}>
              <View style={[styles.switchThumb, plan.isRecip && styles.switchThumbOn]} />
            </View>
          </TouchableOpacity>
          {plan.isRecip && (
            <TouchableOpacity
              style={styles.contractBtn}
              onPress={() => setShowRecipPicker(true)}
              activeOpacity={0.75}
              disabled={readOnly || plan.stage === 'bred'}
            >
              <Text style={styles.contractBtnTxt}>{plan.recipMare || 'Choose the recip mare'}</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Progress */}
        <Text style={styles.contractHead}>Progress</Text>
        <View style={styles.contractCard}>
          {!!plan.fromFrozenId && (
            <View style={styles.contractRow}>
              <Text style={styles.contractKey}>Implanted</Text>
              <Text style={styles.contractVal}>
                {plan.implantedDate ? formatDate(plan.implantedDate) : '—'}
                {plan.embryoMethod ? ' · ' + plan.embryoMethod : ''}
              </Text>
            </View>
          )}
          <View style={styles.contractRow}>
            <Text style={styles.contractKey}>{plan.fromFrozenId ? 'Breed date' : 'Bred'}</Text>
            <Text style={styles.contractVal}>{plan.bredDate ? formatDate(plan.bredDate) : '—'}</Text>
          </View>
          {!readOnly && plan.stage !== 'bred' && (
            <TouchableOpacity
              style={styles.contractBtn}
              onPress={() => {
                setBredDate(plan.bredDate ? new Date(plan.bredDate + 'T12:00:00') : new Date());
                setShowBredModal(true);
              }}
              activeOpacity={0.75}
            >
              <Text style={styles.contractBtnTxt}>{plan.bredDate ? 'Change the bred date' : 'Record the bred date'}</Text>
            </TouchableOpacity>
          )}

          {checks.length === 0 ? (
            <Text style={styles.contractHint}>
              The 14, 30 and 45 day checks appear once the bred date is recorded, with a reminder on each.
            </Text>
          ) : (
            checks.map((c) => (
              <TouchableOpacity
                key={c.id}
                style={styles.checkRow}
                onPress={() => !readOnly && plan.stage !== 'bred' && toggleCheck(c)}
                activeOpacity={0.7}
              >
                <View style={[styles.checkBox, c.completed && styles.checkBoxOn]}>
                  {c.completed && <Text style={styles.checkTick}>✓</Text>}
                </View>
                <Text style={[styles.checkName, c.completed && styles.checkNameDone]}>{c.name}</Text>
                <Text style={styles.checkDate}>
                  {c.completed
                    ? formatDate(c.completedDate)
                    : 'due ' + formatDate(checkDueDate(plan.bredDate, c.daysOffset))}
                </Text>
              </TouchableOpacity>
            ))
          )}
        </View>

        {/* Attempts */}
        {(plan.attempts || []).length > 0 && (
          <>
            <Text style={styles.contractHead}>Attempts</Text>
            <View style={styles.contractCard}>
              {(plan.attempts || []).map((a, i) => (
                <View key={i} style={styles.contractRow}>
                  <Text style={styles.contractKey}>{a.bredDate ? formatDate(a.bredDate) : '—'}</Text>
                  <Text style={styles.contractVal}>{a.outcome}</Text>
                </View>
              ))}
              {!!plan.bredDate && (
                <View style={styles.contractRow}>
                  <Text style={styles.contractKey}>{formatDate(plan.bredDate)}</Text>
                  <Text style={[styles.contractVal, { color: GREEN }]}>In progress</Text>
                </View>
              )}
            </View>
          </>
        )}

        {!readOnly && plan.stage !== 'bred' && (
          <>
            <TouchableOpacity
              style={[styles.inFoalBtn, !plan.bredDate && styles.inFoalBtnOff]}
              onPress={handleConfirmedInFoal}
              activeOpacity={0.8}
              disabled={busy}
            >
              <Text style={styles.inFoalBtnTxt}>Confirmed in Foal</Text>
              <Text style={styles.inFoalBtnSub}>
                Adds her to My Herd as an embryo with the bred date, the checks so far, and the contract
              </Text>
            </TouchableOpacity>

            {!!plan.bredDate && (
              <TouchableOpacity style={styles.didntTakeBtn} onPress={handleDidntTake} activeOpacity={0.75}>
                <Text style={styles.didntTakeTxt}>Didn’t take — reset dates</Text>
                <Text style={styles.didntTakeSub}>Clears the bred date and checks. Contract and fee stay.</Text>
              </TouchableOpacity>
            )}
          </>
        )}
      </ScrollView>

      <PlanShareSheet
        visible={showShare}
        plan={plan}
        onClose={() => setShowShare(false)}
      />

      <FrozenImplantSheet
        visible={showFrozen}
        batches={frozenMatches}
        recips={recips}
        busy={busy}
        onClose={() => setShowFrozen(false)}
        onImplant={handleImplantHere}
      />

      {/* Record a payment */}
      <Modal visible={showPayModal} animationType="slide" transparent onRequestClose={() => { setShowPayModal(false) }}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.sheet}>
            <Text style={styles.sheetTitle}>Record a Payment</Text>
            <Text style={styles.fieldLabel}>Amount</Text>
            <TextInput
              style={styles.input}
              placeholder={'Balance is ' + planMoney(balance)}
              placeholderTextColor="#BDBDBD"
              value={payAmount}
              onChangeText={setPayAmount}
              keyboardType="decimal-pad"
              autoFocus
            />
            <View style={styles.sheetBtns}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => { setShowPayModal(false); setPayAmount(''); }} activeOpacity={0.75}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleRecordPayment} activeOpacity={0.8}>
                <Text style={styles.saveBtnText}>Record</Text>
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* Bred date */}
      <Modal visible={showBredModal} animationType="slide" transparent onRequestClose={() => { setShowBredModal(false) }}>
        <View style={styles.overlay}>
          <View style={styles.sheet}>
            <Text style={styles.sheetTitle}>Bred Date</Text>
            <Text style={styles.fieldLabel}>Date she was bred</Text>
            <TouchableOpacity style={styles.datePicker} onPress={() => setShowBredPicker((v) => !v)} activeOpacity={0.75}>
              <Text style={styles.datePickerText}>
                {bredDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </Text>
              <Text>📅</Text>
            </TouchableOpacity>
            <Text style={styles.contractHint}>
              Defaults to today. Tap the date to pick the day she was actually bred — everything counts from it.
            </Text>
            {showBredPicker && (
              <DateTimePicker
                value={bredDate}
                mode="date"
                display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                onChange={(e, d) => {
                  if (Platform.OS === 'android') setShowBredPicker(false);
                  if (d) setBredDate(d);
                }}
              />
            )}
            <Text style={styles.contractHint}>
              The 14, 30 and 45 day checks are generated from this date{plan.isRecip ? ', plus the 120 day check for the recip' : ''}, each with a reminder at 9am.
            </Text>
            <View style={styles.sheetBtns}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowBredModal(false)} activeOpacity={0.75}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleSetBredDate} activeOpacity={0.8}>
                <Text style={styles.saveBtnText}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Recip picker */}
      <Modal visible={showRecipPicker} animationType="slide" transparent onRequestClose={() => { setShowRecipPicker(false) }}>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '80%' }]}>
            <Text style={styles.sheetTitle}>Recip Mare</Text>
            <ScrollView style={{ flexShrink: 1 }} keyboardShouldPersistTaps="handled">
              {recips.length === 0 ? (
                <Text style={styles.noNotesTxt}>No horses with a Recip status in your herd.</Text>
              ) : (
                recips.map((h) => (
                  <TouchableOpacity key={h.id} style={styles.pickerRow} onPress={() => pickRecip(h)} activeOpacity={0.7}>
                    <Text style={styles.pickerRowName}>{displayName(h)}</Text>
                  </TouchableOpacity>
                ))
              )}
              <Text style={[styles.fieldLabel, { marginTop: 16 }]}>Or a leased recip</Text>
              <TextInput
                style={styles.input}
                placeholder="Name of the leased mare"
                placeholderTextColor="#BDBDBD"
                value={leasedName}
                onChangeText={setLeasedName}
              />
              <TouchableOpacity style={styles.contractBtn} onPress={saveLeasedRecip} activeOpacity={0.75}>
                <Text style={styles.contractBtnTxt}>Use this leased mare</Text>
              </TouchableOpacity>
            </ScrollView>
            <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowRecipPicker(false)} activeOpacity={0.75}>
              <Text style={styles.cancelBtnText}>Close</Text>
            </TouchableOpacity>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </SafeAreaView>
  );
}


// ─── PLANS SHARED WITH ME ─────────────────────────────────────────────────────

// Kept deliberately separate from your own board: these are other people's
// seasons, and mixing them into yours would make the totals meaningless.
function SharedBreedingPlansScreen({ navigation }) {
  // Who shares breeding plans with me — from plans already here and from
  // standing season shares still on their way. Tapping a person opens the
  // Breeding Planning board in view mode, so their plans read exactly as they
  // do to them.
  const myUid = auth.currentUser?.uid;
  const [plans, setPlans] = useState([]);
  const [owners, setOwners] = useState({});
  const [seasonShares, setSeasonShares] = useState([]);
  useEffect(() => subscribeToSeasonShares(auth.currentUser?.uid, setSeasonShares), []);
  const activeToMe = seasonShares.filter((s) => s.toUid === myUid && s.status === 'active');

  useEffect(() => {
    if (!myUid) return;
    return subscribeToBreedingPlans(myUid, (all) => setPlans(all.filter((p) => p.ownerId && p.ownerId !== myUid)));
  }, []);

  // Resolve names for owners we only know by id.
  useEffect(() => {
    const missing = [...new Set(plans.map((p) => p.ownerId))].filter((id) => id && !owners[id]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const found = {};
      for (const id of missing) {
        try { const u = await fetchUserById(id); found[id] = u?.displayName || u?.email || 'Another owner'; }
        catch (e) { found[id] = 'Another owner'; }
      }
      if (!cancelled) setOwners((prev) => ({ ...prev, ...found }));
    })();
    return () => { cancelled = true; };
  }, [plans]);

  const people = (() => {
    const byOwner = new Map();
    plans.forEach((p) => {
      if (!byOwner.has(p.ownerId)) byOwner.set(p.ownerId, { ownerId: p.ownerId, name: owners[p.ownerId] || '', count: 0, full: 0, standing: [] });
      const g = byOwner.get(p.ownerId);
      g.count += 1;
      if (canEditPlan(p, myUid)) g.full += 1;
    });
    activeToMe.forEach((s) => {
      const who = s.people?.[s.ownerId] || {};
      if (!byOwner.has(s.ownerId)) byOwner.set(s.ownerId, { ownerId: s.ownerId, name: who.name || who.email || '', count: 0, full: 0, standing: [] });
      const g = byOwner.get(s.ownerId);
      if (!g.name) g.name = who.name || who.email || '';
      g.standing.push(s);
    });
    return [...byOwner.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  })();

  return (
    <SafeAreaView style={styles.screen} edges={['bottom']}>
      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
        {people.length === 0 ? (
          <Text style={[styles.noNotesTxt, { marginTop: 20 }]}>No one has shared a breeding plan with you.</Text>
        ) : people.map((g) => (
          <View key={g.ownerId} style={styles.formCard}>
            <TouchableOpacity
              onPress={() => navigation.navigate('BreedingPlanning', { sharedFrom: g.ownerId, sharedName: g.name || 'Shared' })}
              activeOpacity={0.75}
              style={{ flexDirection: 'row', alignItems: 'center' }}
            >
              <View style={{ flex: 1 }}>
                <Text style={styles.horseName}>{g.name || 'Loading…'}</Text>
                <Text style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                  {g.count > 0 ? `${plural(g.count, 'plan')}${g.full > 0 ? ` · Full Access on ${g.full === g.count ? 'all' : g.full}` : ' · View Only'}` : 'Plans on their way — they appear once the owner opens the app'}
                </Text>
              </View>
              <Text style={styles.sharedCardChevron}>›</Text>
            </TouchableOpacity>
            {g.standing.map((s) => (
              <View key={s.id} style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8, paddingTop: 8, borderTopWidth: 0.5, borderTopColor: BORDER }}>
                <Text style={{ flex: 1, fontSize: 12, color: MUTED }}>
                  Shares {seasonLabel(s)} with you · {s.permission === 'full' ? 'Full Access' : 'View Only'} · new plans arrive automatically
                </Text>
                <TouchableOpacity
                  onPress={() => Alert.alert('Stop receiving?', `You will lose access to ${g.name || 'their'} ${seasonLabel(s)} plans.`, [
                    { text: 'Keep', style: 'cancel' },
                    { text: 'Stop', style: 'destructive', onPress: () => endSeasonShare(s.id, myUid).catch(() => Alert.alert('Error', 'Could not do that. Please try again.')) },
                  ])}
                  activeOpacity={0.7}
                  style={{ paddingVertical: 4, paddingLeft: 12 }}
                >
                  <Text style={{ fontSize: 12, color: RED, fontWeight: '600' }}>Stop</Text>
                </TouchableOpacity>
              </View>
            ))}
          </View>
        ))}
      </ScrollView>
    </SafeAreaView>
  );
}

function StudLibraryScreen() {
  const [studs, setStuds] = useState([]);
  const [plans, setPlans] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [fee, setFee] = useState('');
  const [farm, setFarm] = useState('');
  const [contact, setContact] = useState('');
  const [showPedigree, setShowPedigree] = useState(false);
  const [ped, setPed] = useState({ sire: '', sireSire: '', sireDam: '', dam: '', damSire: '', damDam: '' });
  const setPedField = (k, v) => setPed((prev) => ({ ...prev, [k]: v }));
  const [incentives, setIncentives] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const unsubStuds = subscribeToStuds(uid, setStuds);
    const unsubPlans = subscribeToBreedingPlans(uid, setPlans);
    return () => { unsubStuds(); unsubPlans(); };
  }, []);

  const openNew = () => {
    setEditing(null);
    setName(''); setFee(''); setFarm(''); setContact('');
    setIncentives(''); setNotes('');
    setShowPedigree(false);
    setPed({ sire: '', sireSire: '', sireDam: '', dam: '', damSire: '', damDam: '' });
    setShowModal(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setName(s.name || '');
    setFee(s.fee == null ? '' : String(s.fee));
    setFarm(s.farm || '');
    setContact(s.contact || '');
    setIncentives(incentiveText(s.incentives));
    setNotes(s.notes || '');
    const p = {
      sire: s.sire || '', sireSire: s.sireSire || '', sireDam: s.sireDam || '',
      dam: s.dam || '', damSire: s.damSire || '', damDam: s.damDam || '',
    };
    setPed(p);
    setShowPedigree(Object.values(p).some(Boolean));
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      Alert.alert('Name required', 'A name is all that’s needed — the rest can wait.');
      return;
    }
    const feeNum = fee.trim() === '' ? null : parseFloat(fee.replace(/[^0-9.]/g, ''));
    const data = {
      name: name.trim(),
      fee: Number.isFinite(feeNum) ? feeNum : null,
      farm: farm.trim(),
      contact: contact.trim(),
      incentives: incentives.trim(),
      notes: notes.trim(),
      ...ped,
    };
    try {
      if (editing) await updateStud(editing.id, data);
      else await addStud(auth.currentUser?.uid, data);
      setShowModal(false);
    } catch (e) {
      Alert.alert('Could not save', 'Please check your connection and try again.');
    }
  };

  // A stud still attached to a plan is worth a warning — deleting him here does
  // not touch the plans that already copied his name and fee.
  const handleDelete = (s) => {
    const used = studPlanCount(s.id, plans);
    Alert.alert(
      'Delete stud',
      used > 0
        ? s.name + ' is on ' + used + ' plan' + (used === 1 ? '' : 's') + '. Removing him from the library leaves those plans as they are.'
        : 'Remove ' + s.name + ' from the library?',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: () => deleteStud(s.id).catch(() => {}) },
      ]
    );
  };

  return (
    <SafeAreaView style={styles.screen} edges={['bottom']}>
      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
        {studs.length === 0 ? (
          <Text style={[styles.noNotesTxt, { marginTop: 20 }]}>
            Nothing in the library yet. Add a stallion and he&rsquo;s ready to pick for any mare.
          </Text>
        ) : (
          studs.map((s) => {
            const used = studPlanCount(s.id, plans);
            return (
              <TouchableOpacity key={s.id} style={styles.studLibRow} onPress={() => openEdit(s)} activeOpacity={0.75}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.studLibName}>{s.name}</Text>
                  <Text style={styles.studLibMeta}>
                    {used > 0 ? used + ' plan' + (used === 1 ? '' : 's') : 'Not used yet'}
                    {s.farm ? ' · ' + s.farm : ''}
                  </Text>
                  {(s.sire || s.dam) && (
                    <Text style={styles.studLibPed}>
                      {(s.sire || '?') + ' × ' + (s.dam || '?')}
                    </Text>
                  )}
                  {!!incentiveText(s.incentives) && (
                    <Text style={styles.studLibInc} numberOfLines={1}>
                      ⭐ {incentiveText(s.incentives).split('\n').join(' · ')}
                    </Text>
                  )}
                </View>
                <Text style={styles.studLibFee}>{s.fee == null ? 'no fee yet' : planMoney(s.fee)}</Text>
                <TouchableOpacity onPress={() => handleDelete(s)} activeOpacity={0.7}>
                  <Text style={styles.studRemove}>✕</Text>
                </TouchableOpacity>
              </TouchableOpacity>
            );
          })
        )}

        <TouchableOpacity style={styles.addMilestoneBtn} onPress={openNew} activeOpacity={0.75}>
          <Text style={styles.addMilestoneBtnTxt}>+ Add a stud</Text>
        </TouchableOpacity>

        <Text style={styles.studLibNote}>
          Changing a fee here updates what new plans quote. Plans already made keep the fee they were created with.
        </Text>
      </ScrollView>

      <Modal visible={showModal} animationType="slide" transparent>
        <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheet, { maxHeight: '85%' }]}>
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
              <Text style={styles.sheetTitle}>{editing ? 'Edit Stud' : 'Add a Stud'}</Text>
              <Text style={styles.fieldLabel}>Stallion</Text>
              <TextInput
                style={styles.input}
                placeholder="Stallion's name"
                placeholderTextColor="#BDBDBD"
                value={name}
                onChangeText={setName}
                autoFocus
              />
              <Text style={styles.fieldLabel}>Stud fee — optional</Text>
              <TextInput
                style={styles.input}
                placeholder="e.g. 4500"
                placeholderTextColor="#BDBDBD"
                value={fee}
                onChangeText={setFee}
                keyboardType="decimal-pad"
              />
              <Text style={styles.fieldLabel}>Stud farm — optional</Text>
              <TextInput
                style={styles.input}
                placeholder="e.g. Willow Bend Ranch"
                placeholderTextColor="#BDBDBD"
                value={farm}
                onChangeText={setFarm}
              />
              <Text style={styles.fieldLabel}>Contact — optional</Text>
              <TextInput
                style={styles.input}
                placeholder="Phone or email"
                placeholderTextColor="#BDBDBD"
                value={contact}
                onChangeText={setContact}
              />

              <Text style={styles.fieldLabel}>Incentive programs — optional</Text>
              <TextInput
                style={[styles.input, { minHeight: 88, textAlignVertical: 'top' }]}
                placeholder={'NRHA Sire & Dam\nPink Buckle\nRuby Buckle'}
                placeholderTextColor="#BDBDBD"
                value={incentives}
                onChangeText={setIncentives}
                multiline
              />
              <Text style={styles.contractHint}>
                What a foal by him would be eligible for. Keep a running list — one per line.
              </Text>

              <Text style={styles.fieldLabel}>Notes — optional</Text>
              <TextInput
                style={[styles.input, { minHeight: 88, textAlignVertical: 'top' }]}
                placeholder="Anything worth remembering when you compare him"
                placeholderTextColor="#BDBDBD"
                value={notes}
                onChangeText={setNotes}
                multiline
              />

              <TouchableOpacity
                style={styles.pedToggle}
                onPress={() => setShowPedigree((v) => !v)}
                activeOpacity={0.75}
              >
                <Text style={styles.pedToggleTxt}>
                  {showPedigree ? '− Hide pedigree' : '+ Add pedigree — optional, for comparing'}
                </Text>
              </TouchableOpacity>

              {showPedigree && (
                <View style={styles.pedBox}>
                  <Text style={styles.pedGen}>Sire side</Text>
                  <TextInput style={styles.input} placeholder="Sire" placeholderTextColor="#BDBDBD"
                    value={ped.sire} onChangeText={(v) => setPedField('sire', v)} />
                  <TextInput style={styles.input} placeholder="Sire's sire" placeholderTextColor="#BDBDBD"
                    value={ped.sireSire} onChangeText={(v) => setPedField('sireSire', v)} />
                  <TextInput style={styles.input} placeholder="Sire's dam" placeholderTextColor="#BDBDBD"
                    value={ped.sireDam} onChangeText={(v) => setPedField('sireDam', v)} />
                  <Text style={styles.pedGen}>Dam side</Text>
                  <TextInput style={styles.input} placeholder="Dam" placeholderTextColor="#BDBDBD"
                    value={ped.dam} onChangeText={(v) => setPedField('dam', v)} />
                  <TextInput style={styles.input} placeholder="Dam's sire" placeholderTextColor="#BDBDBD"
                    value={ped.damSire} onChangeText={(v) => setPedField('damSire', v)} />
                  <TextInput style={styles.input} placeholder="Dam's dam" placeholderTextColor="#BDBDBD"
                    value={ped.damDam} onChangeText={(v) => setPedField('damDam', v)} />
                </View>
              )}
              <View style={styles.sheetBtns}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowModal(false)} activeOpacity={0.75}>
                  <Text style={styles.cancelBtnText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={handleSave} activeOpacity={0.8}>
                  <Text style={styles.saveBtnText}>Save</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </SafeAreaView>
  );
}


// ─── ROOT ─────────────────────────────────────────────────────────────────────


const linking = {
  prefixes: [],
  config: {
    screens: {
      Home: '',
      Herd: 'herd',
      Horse: 'horse/:horseId',
      Milestones: 'milestones',
      Timeline: 'timeline',
      RecentlyDeleted: 'recently-deleted',
      TaskCategories: 'task-categories',
      NoteTags: 'note-tags',
      Admin: 'admin',
      AdminUserHorses: 'admin/user-horses',
      HerdEarnings: 'earnings',
      BreedingPlanning: 'breeding-planning',
      StudLibrary: 'studs',
      BreedingContract: 'breeding-contract',
      SharedBreedingPlans: 'shared-breeding-plans',
    },
  },
};

function WebLandingScreen({ onGetStarted }) {
  return (
    <ScrollView style={{ flex: 1, backgroundColor: BG }} contentContainerStyle={{ flexGrow: 1 }}>
      {/* Hero — matches LoginScreen exactly */}
      <View style={{ backgroundColor: RED, paddingTop: 48, paddingBottom: 32, paddingHorizontal: 32, alignItems: 'center', overflow: 'hidden' }}>
        <View style={{ position: 'absolute', top: 18, left: 18, width: 16, height: 16, borderTopWidth: 1.5, borderLeftWidth: 1.5, borderColor: GOLD, opacity: 0.6 }} />
        <View style={{ position: 'absolute', top: 18, right: 18, width: 16, height: 16, borderTopWidth: 1.5, borderRightWidth: 1.5, borderColor: GOLD, opacity: 0.6 }} />
        <View style={{ position: 'absolute', bottom: 18, left: 18, width: 16, height: 16, borderBottomWidth: 1.5, borderLeftWidth: 1.5, borderColor: GOLD, opacity: 0.6 }} />
        <View style={{ position: 'absolute', bottom: 18, right: 18, width: 16, height: 16, borderBottomWidth: 1.5, borderRightWidth: 1.5, borderColor: GOLD, opacity: 0.6 }} />
        <Image source={require('./assets/horse-silhouette.png')} style={{ position: 'absolute', right: -10, bottom: -8, width: 140, height: 140, opacity: 0.45 }} />
        <Text style={{ fontSize: 52, color: GOLD, marginBottom: 8, lineHeight: 60 }}>★</Text>
        <Text style={{ fontSize: 32, color: '#fff', fontWeight: '900', letterSpacing: -0.3 }}>Horse <Text style={{ color: GOLD }}>Math</Text></Text>
        <Text style={{ fontSize: 15, color: CREAM, fontStyle: 'italic', marginTop: 4, marginBottom: 10, textAlign: 'center' }}>Herd & Breeding{'\n'}Management</Text>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 24 }}>
          <View style={{ flex: 1, height: 1, backgroundColor: GOLD, opacity: 0.4 }} />
          <Text style={{ color: GOLD, fontSize: 10, fontWeight: '700', letterSpacing: 1.5 }}>POWERED BY GINGERBREAD RANCH</Text>
          <View style={{ flex: 1, height: 1, backgroundColor: GOLD, opacity: 0.4 }} />
        </View>
        <TouchableOpacity style={{ backgroundColor: GOLD, borderRadius: 10, paddingVertical: 14, alignItems: 'center', width: '100%', maxWidth: 320, marginBottom: 10 }} onPress={onGetStarted} activeOpacity={0.85}>
          <Text style={{ color: '#3a1a00', fontSize: 15, fontWeight: '700' }}>Get started free</Text>
        </TouchableOpacity>
        <TouchableOpacity style={{ borderWidth: 1.5, borderColor: CREAM, borderRadius: 10, paddingVertical: 12, alignItems: 'center', width: '100%', maxWidth: 320 }} onPress={onGetStarted} activeOpacity={0.85}>
          <Text style={{ color: CREAM, fontSize: 14 }}>Sign in</Text>
        </TouchableOpacity>
      </View>

      {/* Platform tiles */}
      <View style={{ flexDirection: 'row', gap: 10, padding: 20 }}>
        <View style={{ flex: 1, backgroundColor: CARD, borderRadius: 10, borderWidth: 0.5, borderColor: BORDER, padding: 16, alignItems: 'center' }}>
          <Text style={{ fontSize: 26, marginBottom: 6 }}>📱</Text>
          <Text style={{ fontSize: 13, fontWeight: '700', color: TEXT, marginBottom: 4 }}>iOS & Android</Text>
          <View style={{ backgroundColor: '#FAC775', borderRadius: 20, paddingHorizontal: 8, paddingVertical: 2 }}>
            <Text style={{ fontSize: 10, fontWeight: '700', color: '#412402' }}>Coming soon</Text>
          </View>
        </View>
        <View style={{ flex: 1, backgroundColor: CARD, borderRadius: 10, borderWidth: 0.5, borderColor: BORDER, padding: 16, alignItems: 'center' }}>
          <Text style={{ fontSize: 26, marginBottom: 6 }}>🌐</Text>
          <Text style={{ fontSize: 13, fontWeight: '700', color: TEXT, marginBottom: 2 }}>Web</Text>
          <Text style={{ fontSize: 11, color: MUTED, marginBottom: 4 }}>horsemath.app</Text>
          <View style={{ backgroundColor: '#C0DD97', borderRadius: 20, paddingHorizontal: 8, paddingVertical: 2 }}>
            <Text style={{ fontSize: 10, fontWeight: '700', color: '#173404' }}>Available now</Text>
          </View>
        </View>
      </View>

      <View style={{ height: 1, backgroundColor: BORDER, marginHorizontal: 20 }} />

      {/* Feature cards */}
      <View style={{ padding: 20 }}>
        <Text style={{ fontSize: 17, fontWeight: '700', color: RED, textAlign: 'center', marginBottom: 16 }}>Everything your herd needs</Text>
        {[
          { icon: '📋', title: 'Herd management', body: 'Track born horses and embryos with full pedigree, color, breed date, and location details.' },
          { icon: '💰', title: 'Earnings tracking', body: 'Log competition winnings and offspring earnings. See your total herd earnings at a glance.' },
          { icon: '✅', title: 'Tasks & reminders', body: 'Schedule vet visits, farrier appointments, and breeding milestones with push notifications.' },
          { icon: '🩺', title: 'Health log', body: 'Keep detailed notes and health history for each horse with custom tags.' },
          { icon: '📅', title: 'Breeding timeline', body: 'Track embryo progress with estimated foal dates based on 340-day gestation.' },
          { icon: '🔗', title: 'Share access', body: 'Share horses with vets, trainers, or ranch hands with view or edit permissions.' },
        ].map((f) => (
          <View key={f.title} style={{ backgroundColor: CARD, borderRadius: 12, borderWidth: 0.5, borderColor: BORDER, padding: 14, marginBottom: 10, flexDirection: 'row', gap: 14 }}>
            <Text style={{ fontSize: 22, marginTop: 1 }}>{f.icon}</Text>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 14, fontWeight: '700', color: TEXT, marginBottom: 3 }}>{f.title}</Text>
              <Text style={{ fontSize: 13, color: MUTED, lineHeight: 19 }}>{f.body}</Text>
            </View>
          </View>
        ))}
      </View>

      <View style={{ height: 1, backgroundColor: BORDER, marginHorizontal: 20 }} />

      {/* Bottom CTA */}
      <View style={{ padding: 24, alignItems: 'center' }}>
        <Text style={{ fontSize: 17, fontWeight: '700', color: RED, marginBottom: 8 }}>Ready to get started?</Text>
        <Text style={{ fontSize: 13, color: MUTED, marginBottom: 16 }}>No credit card required.</Text>
        <TouchableOpacity style={{ backgroundColor: RED, borderRadius: 10, paddingVertical: 14, paddingHorizontal: 40, alignItems: 'center' }} onPress={onGetStarted} activeOpacity={0.85}>
          <Text style={{ color: CREAM, fontSize: 15, fontWeight: '700' }}>Create your free account</Text>
        </TouchableOpacity>
      </View>

      {/* Footer */}
      <View style={{ backgroundColor: RED, padding: 20, alignItems: 'center' }}>
        <Text style={{ color: '#e8d5b0', fontSize: 12, marginBottom: 8 }}>Horse Math by Gingerbread Ranch</Text>
        <View style={{ flexDirection: 'row', gap: 16 }}>
          <TouchableOpacity onPress={() => Linking.openURL('https://horsemath.app/privacy')}><Text style={{ color: GOLD, fontSize: 12 }}>Privacy Policy</Text></TouchableOpacity>
          <TouchableOpacity onPress={() => Linking.openURL('https://horsemath.app/terms')}><Text style={{ color: GOLD, fontSize: 12 }}>Terms of Service</Text></TouchableOpacity>
          <TouchableOpacity onPress={() => Linking.openURL('mailto:gingerbreadranch.life@gmail.com')}><Text style={{ color: GOLD, fontSize: 12 }}>Support</Text></TouchableOpacity>
        </View>
      </View>
    </ScrollView>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32, backgroundColor: BG }}>
          <Text style={{ fontSize: 40, marginBottom: 16 }}>🐴</Text>
          <Text style={{ fontSize: 20, fontWeight: '700', color: TEXT, marginBottom: 10, textAlign: 'center' }}>Something went wrong</Text>
          <Text style={{ fontSize: 15, color: MUTED, textAlign: 'center', marginBottom: 28, lineHeight: 22 }}>Please restart the app or refresh the page. If the problem persists, contact support.</Text>
          <TouchableOpacity
            style={{ backgroundColor: RED, borderRadius: 10, paddingVertical: 14, paddingHorizontal: 28 }}
            onPress={() => {
              if (Platform.OS === 'web' && typeof window !== 'undefined') {
                window.location.reload();
              } else {
                this.setState({ hasError: false });
              }
            }}
            activeOpacity={0.85}
          >
            <Text style={{ color: CREAM, fontWeight: '700', fontSize: 15 }}>Try Again</Text>
          </TouchableOpacity>
        </View>
      );
    }
    return this.props.children;
  }
}

const ONBOARDING_COMPLETE_KEY = 'onboardingComplete';
const UPCOMING_NOTIFICATIONS_KEY = 'scheduledUpcomingNotifications';

const ONBOARDING_STEPS = [
  {
    icon: '🐴',
    title: 'Welcome to Horse Math',
    body: 'Your whole herd in one place. Foaling due dates and reminders are worked out for you, and every horse keeps its own health history, photos and papers.',
    sub: 'Train, board or shoe for others? Bill clients and get paid here too.',
  },
  {
    icon: '📋',
    title: 'Everything about every horse',
    bullets: [
      ['📅', 'Breed dates, foaling due dates and milestones, worked out for you'],
      ['🩺', 'Vet, farrier and health history, with reminders'],
      ['📁', 'Photos, Coggins and registration papers on each horse'],
      ['🤝', 'Share with partners, trainers and vets'],
    ],
  },
  {
    icon: '🧾',
    title: "Care for other people's horses?",
    bullets: [
      ['🎓', 'Keep client horses separate from your own'],
      ['🧾', 'Log charges and send invoices in a tap'],
      ['💳', 'Get paid online by card or bank transfer — new'],
    ],
    sub: 'Trainers, boarding barns, farriers, vets',
  },
  {
    icon: '🐎',
    title: 'How will you use Horse Math?',
    choice: [
      ['own', '🏠', 'My own horses'],
      ['clients', '🎓', "I care for clients' horses"],
      ['both', '🔁', 'Both'],
    ],
    sub: 'This just tailors a few tips.',
  },
];

function OnboardingScreen({ onComplete }) {
  const [step, setStep] = useState(0);
  const [use, setUse] = useState(null);
  const current = ONBOARDING_STEPS[step];
  const isLast = step === ONBOARDING_STEPS.length - 1;

  const next = () => {
    if (isLast) onComplete(use);
    else setStep((s) => s + 1);
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: BG }}>
      <StatusBar style="light" />
      <View style={{ backgroundColor: RED, paddingTop: 60, paddingBottom: 36, alignItems: 'center', paddingHorizontal: 24 }}>
        <Text style={{ fontSize: 52, marginBottom: 14 }}>{current.icon}</Text>
        <Text style={{ color: CREAM, fontSize: 22, fontWeight: '700', textAlign: 'center', lineHeight: 30 }}>{current.title}</Text>
      </View>
      <View style={{ flex: 1, padding: 28, justifyContent: 'space-between' }}>
        <ScrollView style={{ marginTop: 8 }} contentContainerStyle={{ flexGrow: 1 }} showsVerticalScrollIndicator={false}>
          {!!current.body && (
            <Text style={{ fontSize: 16, color: TEXT, lineHeight: 26, textAlign: 'center' }}>{current.body}</Text>
          )}
          {!!current.bullets && current.bullets.map(([icon, text]) => (
            <View key={text} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
              <Text style={{ fontSize: 20, width: 28 }}>{icon}</Text>
              <Text style={{ flex: 1, fontSize: 15.5, color: TEXT, lineHeight: 23 }}>{text}</Text>
            </View>
          ))}
          {!!current.choice && current.choice.map(([key, icon, label]) => (
            <TouchableOpacity
              key={key}
              onPress={() => setUse(key)}
              activeOpacity={0.8}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: CARD, borderRadius: 12, borderWidth: use === key ? 2 : 1, borderColor: use === key ? RED : BORDER, paddingVertical: 14, paddingHorizontal: 16, marginBottom: 10 }}
            >
              <Text style={{ fontSize: 20 }}>{icon}</Text>
              <Text style={{ fontSize: 15.5, color: TEXT, fontWeight: use === key ? '700' : '500' }}>{label}</Text>
            </TouchableOpacity>
          ))}
          {current.sub && (
            <Text style={{ fontSize: 13, color: MUTED, textAlign: 'center', marginTop: 12 }}>{current.sub}</Text>
          )}
        </ScrollView>
        <View style={{ marginTop: 12 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'center', marginBottom: 28, gap: 8 }}>
            {ONBOARDING_STEPS.map((_, i) => (
              <View key={i} style={{ width: i === step ? 20 : 8, height: 8, borderRadius: 4, backgroundColor: i === step ? RED : BORDER }} />
            ))}
          </View>
          <TouchableOpacity style={{ backgroundColor: RED, borderRadius: 12, paddingVertical: 16, alignItems: 'center' }} onPress={next} activeOpacity={0.85}>
            <Text style={{ color: CREAM, fontSize: 16, fontWeight: '700' }}>{isLast ? "Let's go" : 'Next'}</Text>
          </TouchableOpacity>
          {!isLast && (
            <TouchableOpacity onPress={() => onComplete(null)} style={{ marginTop: 16, alignItems: 'center' }} activeOpacity={0.7}>
              <Text style={{ color: MUTED, fontSize: 14 }}>Skip</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </SafeAreaView>
  );
}

// ── First-horse walkthrough ──
// One-time tips on the real screens for a new user: Home's Add Horse card →
// the client-horse switch in Add Horse → back on Home, where the horse went
// (Client Horses or My Herd) → for a client horse, billing setup on Client
// Horses. Stage per user (AsyncStorage): add → switch → placed → payments → done.
// Home marks existing users (they already have horses) done the first time.
const GUIDE_KEY = 'firstHorseGuide_';
const GUIDE_HIGHLIGHT = { borderColor: GOLD, borderWidth: 2.5 };
let guideState = { uid: null, loaded: false, stage: null, use: null, kind: null, horseName: '' };
const guideListeners = new Set();
const emitGuide = () => guideListeners.forEach((f) => f(guideState));
const loadGuide = (uid) => {
  if (!uid || (guideState.uid === uid && guideState.loaded)) return;
  if (guideState.uid !== uid) guideState = { uid, loaded: false, stage: null, use: null, kind: null, horseName: '' };
  emitGuide();
  AsyncStorage.getItem(GUIDE_KEY + uid)
    .then((v) => {
      if (guideState.uid !== uid) return;
      let saved = {};
      try { saved = v ? JSON.parse(v) : {}; } catch { /* ignore */ }
      guideState = { ...guideState, ...saved, loaded: true };
      emitGuide();
    })
    .catch(() => {
      if (guideState.uid !== uid) return;
      guideState = { ...guideState, stage: 'done', loaded: true }; // never nag if storage fails
      emitGuide();
    });
};
const setGuideStage = (stage, extra = {}) => {
  const uid = auth.currentUser?.uid || guideState.uid;
  if (!uid) return;
  guideState = { ...guideState, ...extra, uid, loaded: true, stage };
  emitGuide();
  const { use, kind, horseName } = guideState;
  AsyncStorage.setItem(GUIDE_KEY + uid, JSON.stringify({ stage, use, kind, horseName })).catch(() => {});
};
function useGuide() {
  const [g, setG] = useState(guideState);
  useEffect(() => {
    guideListeners.add(setG);
    setG(guideState);
    return () => { guideListeners.delete(setG); };
  }, []);
  return g;
}

// A coach-mark bubble that points at the real control next to it.
function GuideTip({ step, text, arrow, onSkip, children }) {
  const tri = (dir) => (
    <View style={{
      alignSelf: 'flex-start', marginLeft: 28, width: 0, height: 0,
      borderLeftWidth: 9, borderRightWidth: 9, borderLeftColor: 'transparent', borderRightColor: 'transparent',
      ...(dir === 'up' ? { borderBottomWidth: 9, borderBottomColor: RED } : { borderTopWidth: 9, borderTopColor: RED }),
    }} />
  );
  return (
    <View style={{ marginBottom: arrow === 'down' ? 2 : 12, marginTop: arrow === 'up' ? -4 : 0 }}>
      {arrow === 'up' && tri('up')}
      <View style={{ backgroundColor: RED, borderRadius: 12, padding: 12 }}>
        <Text style={{ color: CREAM, fontSize: 14, lineHeight: 20 }}>
          {step ? <Text style={{ color: GOLD, fontWeight: '700' }}>{step} </Text> : null}{text}
        </Text>
        {children}
        {!!onSkip && (
          <TouchableOpacity onPress={onSkip} style={{ alignSelf: 'flex-end', marginTop: 8 }} activeOpacity={0.7} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Text style={{ color: CREAM, opacity: 0.75, fontSize: 12.5 }}>Skip tips</Text>
          </TouchableOpacity>
        )}
      </View>
      {arrow === 'down' && tri('down')}
    </View>
  );
}

async function scheduleUpcomingNotifications(uid) {
  if (Platform.OS === 'web') return;
  let Notifications;
  try { Notifications = require('expo-notifications'); } catch { return; }
  try {
    const raw = await AsyncStorage.getItem(UPCOMING_NOTIFICATIONS_KEY);
    const prevIds = raw ? JSON.parse(raw) : [];
    for (const id of prevIds) {
      try { await Notifications.cancelScheduledNotificationAsync(id); } catch {}
    }
    const horses = await fetchUserHorses(uid);
    if (!horses || !horses.length) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const newIds = [];
    for (const horse of horses) {
      const name = horse.registeredName || horse.name || 'A horse';
      for (const m of (horse.milestones || [])) {
        if (m.completed) continue;
        // remindOnDue tasks are pushed from the server at 8am local (and repeated
        // daily if repeatUntilDone). Scheduling a local one too would land a
        // second notification at the same minute on the due date.
        if (m.remindOnDue) continue;
        if (m.reminderAt && new Date(m.reminderAt) > new Date()) continue;
        if (!m.date && !horse.breedDate) continue;
        const mDate = getMilestoneDate(horse, m);
        const due = new Date(mDate + 'T12:00:00');
        if (isNaN(due.getTime())) continue;
        const daysAway = Math.round((due - today) / 86400000);
        if (daysAway < 0 || daysAway > 7) continue;
        const fireAt = new Date(due);
        fireAt.setHours(8, 0, 0, 0);
        if (fireAt <= new Date()) continue;
        const label = daysAway === 0 ? 'today' : daysAway === 1 ? 'tomorrow' : `in ${daysAway} days`;
        try {
          const notifId = await Notifications.scheduleNotificationAsync({
            content: {
              title: `Upcoming task — ${name}`,
              body: `${m.name} is due ${label}`,
              data: { screen: 'Horse', horseId: horse.id },
              sound: 'default',
            },
            trigger: { type: Notifications.SchedulableTriggerInputTypes.DATE, date: fireAt, channelId: 'reminders' },
          });
          newIds.push(notifId);
        } catch {}
      }
      if (horse.type === 'embryo' && horse.breedDate) {
        const foalDate = new Date(addDays(horse.breedDate, 340) + 'T12:00:00');
        const daysAway = Math.round((foalDate - today) / 86400000);
        if (daysAway >= 0 && daysAway <= 30) {
          const fireAt = new Date(foalDate);
          fireAt.setHours(8, 0, 0, 0);
          if (fireAt > new Date()) {
            try {
              const notifId = await Notifications.scheduleNotificationAsync({
                content: {
                  title: `Foal date approaching — ${name}`,
                  body: `Estimated foal date is ${daysAway === 0 ? 'today' : `in ${daysAway} days`}`,
                  data: { screen: 'Horse', horseId: horse.id },
                  sound: 'default',
                },
                trigger: { type: Notifications.SchedulableTriggerInputTypes.DATE, date: fireAt, channelId: 'reminders' },
              });
              newIds.push(notifId);
            } catch {}
          }
        }
      }
    }
    await AsyncStorage.setItem(UPCOMING_NOTIFICATIONS_KEY, JSON.stringify(newIds));
  } catch {}
}

export default function App() {
  const [fontsLoaded] = useFonts({ Arvo_700Bold });
  const [user, setUser] = useState(undefined); // undefined = loading, null = signed out
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [onboardingComplete, setOnboardingComplete] = useState(null);
  const [showLanding, setShowLanding] = useState(Platform.OS === 'web');

  useEffect(() => {
    if (!user) {
      if (Platform.OS === 'web') setShowLanding(true);
      return;
    }
    setOnboardingComplete(null);
    loadGuide(user.uid);
    AsyncStorage.getItem(`${ONBOARDING_COMPLETE_KEY}_${user.uid}`)
      .then((v) => setOnboardingComplete(v === 'true'))
      .catch(() => setOnboardingComplete(false)); // never trap a new user on the spinner
  }, [user]);

  const handleOnboardingComplete = async (use) => {
    if (user) await AsyncStorage.setItem(`${ONBOARDING_COMPLETE_KEY}_${user.uid}`, 'true');
    // Home ends the walkthrough straight away if this person already has horses.
    if (user && guideState.stage !== 'done') setGuideStage(guideState.stage || 'add', { use: use || null });
    setOnboardingComplete(true);
  };

  useEffect(() => {
    if (firebaseInitError) {
      Alert.alert('Startup Error', firebaseInitError);
      return;
    }
    // Complete any pending Google redirect sign-in (web popup fallback)
    if (Platform.OS === 'web') handleWebRedirectResult();
    const unsub = subscribeToAuthChanges(setUser);
    return unsub;
  }, []);

  useEffect(() => {
    AsyncStorage.getItem(BIOMETRIC_LOCK_KEY).then((v) => {
      const enabled = v === 'true';
      setBiometricEnabled(enabled);
      setUnlocked(!enabled);
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    ensureNotificationPermission().then(() => {
      scheduleUpcomingNotifications(user.uid);
    });
  }, [user]);

  // Navigate to the correct screen when a push notification is tapped
  useEffect(() => {
    if (Platform.OS === 'web') return;
    let Notifications;
    try { Notifications = require('expo-notifications'); } catch { return; }
    const sub = Notifications.addNotificationResponseReceivedListener((response) => {
      const { screen, ...params } = response.notification.request.content.data || {};
      if (screen && navigationRef.isReady()) {
        navigationRef.navigate(screen, Object.keys(params).length ? params : undefined);
      }
    });
    return () => sub.remove();
  }, []);

  useEffect(() => {
    if (!user) return;
    const initUser = async () => {
      const isNew = await checkIsNewUser(user.uid);
      const creationMs = user.metadata?.creationTime ? new Date(user.metadata.creationTime).getTime() : 0;
      const lastSignInMs = user.metadata?.lastSignInTime ? new Date(user.metadata.lastSignInTime).getTime() : 0;
      const diffSec = Math.abs(creationMs - lastSignInMs) / 1000;
      const justCreated = diffSec < 60;
      const treatAsNew = isNew || justCreated;
      await upsertUserProfile(user, treatAsNew);
      markActive(user.uid);
      if (treatAsNew) {
        try {
          await writeAdminAlert({
            type: 'new_user',
            userId: user.uid,
            email: user.email || '',
            displayName: user.displayName || '',
          });
        } catch {
          // non-critical, admin alert failure doesn't affect the user
        }
      }
    };
    initUser().catch(() => {});
    getPushToken().then((token) => {
      if (token) savePushToken(user.uid, token).catch(() => {});
    });
    // Record the device timezone so due-date reminders fire at 8am local time.
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) saveUserTimezone(user.uid, tz).catch(() => {});
    } catch {}
  }, [user]);

  // Admin-only: listen for unread adminAlerts and fire a local notification on native.
  useEffect(() => {
    if (!user || user.email !== ADMIN_EMAIL || Platform.OS === 'web') return;
    let NotificationsLocal = null;
    try { NotificationsLocal = require('expo-notifications'); } catch { return; }
    const unsub = subscribeToAdminAlerts((alerts) => {
      alerts.forEach(async (alert) => {
        try {
          await markAdminAlertRead(alert.id);
          await NotificationsLocal.scheduleNotificationAsync({
            content: {
              title: 'New User Signed Up 🐴',
              body: `${alert.displayName || alert.email} just created an account`,
              data: { screen: 'Admin' },
              sound: 'default',
            },
            trigger: null,
          });
        } catch {}
      });
    });
    return unsub;
  }, [user]);

  useEffect(() => {
    let backgroundedAt = null;
    const TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'background' || state === 'inactive') {
        backgroundedAt = Date.now();
      } else if (state === 'active') {
        markActive(auth.currentUser?.uid);
        // Biometric check
        AsyncStorage.getItem(BIOMETRIC_LOCK_KEY).then((v) => {
          const enabled = v === 'true';
          setBiometricEnabled(enabled);
          if (enabled) setUnlocked(false);
        });
        // Return to dashboard after 5 minutes away (mobile only — on web this would
        // disrupt users who switched tabs briefly)
        if (Platform.OS !== 'web' && backgroundedAt && Date.now() - backgroundedAt >= TIMEOUT_MS) {
          if (navigationRef.isReady()) {
            navigationRef.reset({ index: 0, routes: [{ name: 'Home' }] });
          }
        }
        backgroundedAt = null;
        // Refresh upcoming notifications when app comes back to foreground
        const uid = auth.currentUser?.uid;
        if (uid) scheduleUpcomingNotifications(uid);
      }
    });
    return () => sub.remove();
  }, []);

  return (
    <ErrorBoundary>
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaProvider>
        {user === undefined ? (
          <View style={[StyleSheet.absoluteFill, styles.rootLoading]}>
            <StatusBar style="dark" />
            <ActivityIndicator size="large" color={RED} />
          </View>
        ) : !user ? (
          showLanding ? <WebLandingScreen onGetStarted={() => setShowLanding(false)} /> : <LoginScreen />
        ) : biometricEnabled && !unlocked ? (
          <BiometricLockScreen onUnlock={() => setUnlocked(true)} />
        ) : Platform.OS !== 'web' && onboardingComplete === null ? (
          <View style={[StyleSheet.absoluteFill, styles.rootLoading]}>
            <StatusBar style="dark" />
            <ActivityIndicator size="large" color={RED} />
          </View>
        ) : Platform.OS !== 'web' && onboardingComplete === false ? (
          <OnboardingScreen onComplete={handleOnboardingComplete} />
        ) : (
          <>
            <StatusBar style="light" />
            <NavigationContainer
              ref={navigationRef}
              linking={linking}
            >
              <Stack.Navigator
                screenOptions={({ navigation, route }) => ({
                  headerStyle: { backgroundColor: RED, elevation: 0, shadowOpacity: 0, borderBottomWidth: 0 },
                  headerBackground: () => <View style={{ flex: 1, backgroundColor: RED }} pointerEvents="none" />,
                  headerTintColor: CREAM,
                  headerTitleStyle: { fontWeight: '700', fontSize: 18, color: CREAM },
                  cardStyle: { backgroundColor: BG, flex: 1 },
                  headerLeft: ({ onPress }) => {
                    if (route.name === 'Home') return null;
                    return (
                      <TouchableOpacity
                        onPress={() => {
                          if (navigation.canGoBack()) {
                            onPress?.();
                          } else {
                            navigation.navigate('Home');
                          }
                        }}
                        style={{ paddingLeft: 16, paddingRight: 8, paddingVertical: 4 }}
                        activeOpacity={0.7}
                      >
                        <Text style={{ color: CREAM, fontSize: 32, lineHeight: 36, fontWeight: '200' }}>‹</Text>
                      </TouchableOpacity>
                    );
                  },
                })}
              >
                <Stack.Screen
                  name="Home"
                  component={DashboardScreen}
                  options={{ title: 'Dashboard' }}
                />
                <Stack.Screen
                  name="Herd"
                  component={HerdScreen}
                  options={{ title: 'My Herd' }}
                />
                <Stack.Screen
                  name="Horse"
                  component={HorseScreen}
                  options={{ title: 'Horse' }}
                />
                <Stack.Screen
                  name="Milestones"
                  component={MilestonesScreen}
                  options={{ title: 'Milestones & Tasks' }}
                />
                <Stack.Screen
                  name="Incident"
                  component={IncidentScreen}
                  options={{ title: 'Incident' }}
                />
                <Stack.Screen
                  name="HerdHealth"
                  component={HerdHealthScreen}
                  options={{ title: 'Herd Health Calendar' }}
                />
                <Stack.Screen
                  name="Timeline"
                  component={TimelineScreen}
                  options={{ title: 'Timeline' }}
                />
                <Stack.Screen
                  name="RecentlyDeleted"
                  component={RecentlyDeletedScreen}
                  options={{ title: 'Recently Deleted' }}
                />
                <Stack.Screen
                  name="TaskCategories"
                  component={TaskCategoriesScreen}
                  options={{ title: 'Task Categories' }}
                />
                <Stack.Screen
                  name="NoteTags"
                  component={NoteTagsScreen}
                  options={{ title: 'Note Tags' }}
                />
                <Stack.Screen
                  name="Admin"
                  component={AdminScreen}
                  options={{ title: 'All Users' }}
                />
                <Stack.Screen
                  name="AdminUserHorses"
                  component={AdminUserHorsesScreen}
                  options={{ title: 'User Horses' }}
                />
                <Stack.Screen
                  name="HerdEarnings"
                  component={HerdEarningsScreen}
                  options={{ title: 'Herd Earnings' }}
                />
                <Stack.Screen
                  name="ClientHorses"
                  component={ClientHorsesScreen}
                  options={{ title: 'Client Horses' }}
                />
                <Stack.Screen
                  name="Sharing"
                  component={SharingScreen}
                  options={{ title: 'Sharing' }}
                />
                <Stack.Screen
                  name="BreedingPlanning"
                  component={BreedingPlanningScreen}
                  options={{ title: 'Breeding Planning' }}
                />
                <Stack.Screen
                  name="BreedingContract"
                  component={BreedingContractScreen}
                  options={{ title: 'Contract' }}
                />
                <Stack.Screen
                  name="SharedBreedingPlans"
                  component={SharedBreedingPlansScreen}
                  options={{ title: 'Shared with Me' }}
                />
                <Stack.Screen
                  name="StudLibrary"
                  component={StudLibraryScreen}
                  options={{ title: 'Stud Library' }}
                />
                <Stack.Screen
                  name="FrozenEmbryo"
                  component={FrozenEmbryoScreen}
                  options={{ title: 'Frozen Embryo' }}
                />
              </Stack.Navigator>
            </NavigationContainer>
          </>
        )}
      </SafeAreaProvider>
    </GestureHandlerRootView>
    </ErrorBoundary>
  );
}
